import { isProductionRuntime } from '../auth/greenfield';
import {
  CANDIDATE_PROJECTS_FROM_CONTRACT_EMAILS,
  EPOCA_TRIALSHUB_PROTOCOL_CRITIQUES,
  extractEmailRelayAndAttachmentTimeline,
  extractSponsorAndCroFromEmail,
  extractStudyMetadataFromRealEmail,
  fetchLiveOrRealGraphMailboxMessages,
  REAL_GRAPH_SHARED_MAILBOX_MESSAGES,
} from './realGraphMailboxMessages';
import type {
  CalibrationTimelineEntryDto,
  CompatibleCalibrationSupplierItemDto,
  EquipmentAssetItemDto,
  EquipmentAuditLogEntryDto,
  EquipmentConstantReadingDto,
  EquipmentDocumentDownloadDto,
  NationalCalibrationSupplierDto,
  PlatformAutonomousSnapshotDto,
  ResourceMailboxMovementItemDto,
  StockShortageForecastItemDto,
  StudyCloseOutLockDto,
} from '../../modules/facilities/api/queries';
import {
  type BigDataCorpCnpjLookupResponseDto,
  type BigDataCorpEstablishmentDto,
  type BigDataCorpQsaMemberDto,
  type BudgetEmailDraftChangeItemDto,
  type BudgetEmailDraftItemDto,
  type BudgetEmailDraftRevisionDto,
  type ClassificationRuleItem,
  type ClinicalHealthFunctionItemDto,
  type ClinicalResearchCenterDirectoryItemDto,
  type ClinicalSpecialtyItemDto,
  type CnesRecordDto,
  type CroSponsorAndGlobalNegotiationLearningDto,
  type DetectedEntityCandidateDto,
  type DetectedSponsorAndCroPairDto,
  type FinanceMailboxReimbursementDashboardDto,
  type FinanceMailboxReimbursementItemDto,
  formatResearchCenterBankAccountsSummary,
  type GlobalAiRuleItemDto,
  type Harness2EvolutionStateDto,
  type IncomingStudyProcedureNormalizationItemDto,
  type MultiLlmCouncilRunDto,
  type OrganizationItem,
  type ProcedureCityPriceItem,
  type ProcedureDefinitionItem,
  type ProtocolCritiqueItemDto,
  RESEARCH_CENTER_CLASSIFICATIONS,
  type ResearchCenterBankAccountDto,
  type ResearchCenterClassificationType,
  type SharedMailboxMessageItem,
  type SharedMailboxThreadDoubtItemDto,
  type SharedMailboxThreadItemDto,
  type SharedMailboxThreadsDashboardDto,
  type SharedMailboxThreadTimelineEntryDto,
  type SponsorAndCroAutoDetectionDashboardDto,
  type StudyCashflowProjectionDto,
  type StudyContractDossierItem,
  type StudyFinancialPaymentItemDto,
  type StudyIncomingDocumentAnalysisItemDto,
  type StudyItem,
  type StudyMultiDocSponsorViabilityDto,
  type StudyProceduresNormalizationDashboardDto,
  type StudyTeamAndCenterBindingDto,
  type StudyTeamMultiDocAndLearningDashboardDto,
  type StudyWorkspaceBundleDto,
  type ThreadAttachmentEmergenceEventDto,
  type WeeklySyncResearchCentersResponseDto,
} from '../../modules/studies/api/queries';
import type {
  AccountingBillingSnapshotDto,
  AccountingIssuedInvoiceDto,
  AccountingProactiveGapDto,
  AccumulatedBillingChargeDto,
  AutonomousProcurementPolicyDto,
  AutonomousProcurementSnapshotDto,
  CenterIssuedStudyInvoiceDto,
  CenterIssuedStudyInvoicesResponseDto,
  ClinicalInventoryItemDto,
  ConsumablePriceLocalityTimelineEntryDto,
  ContractBillingBucketDto,
  CoverageRuleItem,
  ExternalPartnerUserItem,
  IntegratedFinancialLedgerItemDto,
  IntegratedProcurementOrderDto,
  InventoryMultiSupplierItemDto,
  PurchaseOrderItem,
  QualificationItem,
  QuoteComparisonMapDto,
  ServiceOrderItem,
  StudyFinancialReconciliationDto,
  StudyServiceBasketItemDto,
  SupplierAutoInvoiceAndPaymentItemDto,
  SupplierAutoLinkedStudyDto,
  SupplierContactPersonDto,
  SupplierGovernanceDossierDto,
  SupplierItem,
  SupplierPickupPolicyDto,
  SupplierPickupRequestDto,
  SupplierPickupSnapshotDto,
  SupplierSubscriptionItemDto,
  SupplierTimelineEventDto,
  WhoServesMeMatchItem,
} from '../../modules/suppliers/api/queries';
import { jsonResponse, readJsonBody } from './http';

export interface FakeSuppliersAndStudiesState {
  suppliers: SupplierItem[];
  coverageRules: CoverageRuleItem[];
  qualifications: QualificationItem[];
  serviceOrders: ServiceOrderItem[];
  purchaseOrders: PurchaseOrderItem[];
  studies: StudyItem[];
  organizations: OrganizationItem[];
  procedures: ProcedureDefinitionItem[];
  dossiers: StudyContractDossierItem[];
  messages: SharedMailboxMessageItem[];
  rules: ClassificationRuleItem[];
  budgetEmailDrafts: BudgetEmailDraftItemDto[];
  threadDoubtResolutions: Record<
    string,
    {
      decision: 'CONFIRM_SAME_THREAD' | 'REJECT_SEPARATE_THREADS';
      reviewedBy: string;
      resolvedAtIso: string;
    }
  >;
  manualThreadMerges: Record<string, string>;
  incomingStudyProcedures: IncomingStudyProcedureNormalizationItemDto[];
  holdingPriceTableVersion: number;
  cityPrices: ProcedureCityPriceItem[];
  globalRules: GlobalAiRuleItemDto[];
  studyTableVersions: Record<string, number>;
  studyDismissedAlerts: Record<string, boolean>;
  critiquesByStudy: Record<string, ProtocolCritiqueItemDto[]>;
  paymentsByStudy: Record<string, StudyFinancialPaymentItemDto[]>;
  cashflowByStudy: Record<string, StudyCashflowProjectionDto>;
  facilityAssets: EquipmentAssetItemDto[];
  calibrationSuppliers: NationalCalibrationSupplierDto[];
  stockForecasts: StockShortageForecastItemDto[];
  calibrationTimeline: CalibrationTimelineEntryDto[];
  resourceMailboxEvents: ResourceMailboxMovementItemDto[];
  externalPartnerUsers: ExternalPartnerUserItem[];
  inventoryItems: ClinicalInventoryItemDto[];
  quoteComparisons: QuoteComparisonMapDto[];
  financialLedger: IntegratedFinancialLedgerItemDto[];
  supplierGovernanceDossiers: SupplierGovernanceDossierDto[];
  supplierTimelineEvents: SupplierTimelineEventDto[];
  inventoryMultiSupplierItems: InventoryMultiSupplierItemDto[];
  consumablePriceTimelineEntries: ConsumablePriceLocalityTimelineEntryDto[];
  procurementPolicy: AutonomousProcurementPolicyDto;
  integratedProcurementOrders: IntegratedProcurementOrderDto[];
  pickupPolicy: SupplierPickupPolicyDto;
  supplierPickups: SupplierPickupRequestDto[];
  accountingContractBuckets: ContractBillingBucketDto[];
  accountingAccumulatedCharges: AccumulatedBillingChargeDto[];
  accountingIssuedInvoices: AccountingIssuedInvoiceDto[];
  accountingProactiveGaps: AccountingProactiveGapDto[];
  financeReimbursementItems: FinanceMailboxReimbursementItemDto[];
  isGreenfield?: boolean;
  registeredPis?: StudyTeamMultiDocAndLearningDashboardDto['availablePiCatalog'][number][];
  registeredSubs?: StudyTeamMultiDocAndLearningDashboardDto['availableSubCatalog'][number][];
  registeredCoordinators?: StudyTeamMultiDocAndLearningDashboardDto['availableCoordinatorCatalog'][number][];
  registeredResearchCenters?: StudyTeamMultiDocAndLearningDashboardDto['availableResearchCentersCatalog'][number][];
  clinicalSpecialties?: ClinicalSpecialtyItemDto[];
  clinicalHealthFunctions?: ClinicalHealthFunctionItemDto[];
  supplierAutoInvoices?: SupplierAutoInvoiceAndPaymentItemDto[];
  supplierSubscriptions?: SupplierSubscriptionItemDto[];
  studyServiceBasketItems?: StudyServiceBasketItemDto[];
  centerIssuedStudyInvoices?: CenterIssuedStudyInvoiceDto[];
  studyTeamBindings?: Record<string, StudyTeamAndCenterBindingDto>;
  croSponsorGlobalLearnings?: CroSponsorAndGlobalNegotiationLearningDto[];
  harness2State: Harness2EvolutionStateDto;
  autonomousRunsCount: number;
  lastAutonomousRunAtIso: string;
}

export const CANON_SCHEMA_VERSION = '2026-10-29-canon-v11';
const GREENFIELD_MAILBOX_STORAGE_KEY = 'giyus.prod.mailboxState.v3';
const USER_WORK_VAULT_STORAGE_KEY = 'giyus.prod.userWorkVault.v1';
const LEGACY_GREENFIELD_MAILBOX_STORAGE_KEYS = [
  'giyus.prod.mailboxState.v2',
  'giyus.prod.mailboxState.v1',
] as const;

interface PersistedGreenfieldMailboxState {
  readonly canonSchemaVersion?: string;
  readonly messages?: SharedMailboxMessageItem[];
  readonly dossiers?: StudyContractDossierItem[];
  readonly rules?: ClassificationRuleItem[];
  readonly budgetEmailDrafts?: BudgetEmailDraftItemDto[];
  readonly threadDoubtResolutions?: FakeSuppliersAndStudiesState['threadDoubtResolutions'];
  readonly manualThreadMerges?: Record<string, string>;
  readonly studies?: StudyItem[];
  readonly organizations?: OrganizationItem[];
  readonly procedures?: ProcedureDefinitionItem[];
  readonly cityPrices?: ProcedureCityPriceItem[];
  readonly incomingStudyProcedures?: IncomingStudyProcedureNormalizationItemDto[];
  readonly globalRules?: GlobalAiRuleItemDto[] | undefined;
  readonly harness2State?: Harness2EvolutionStateDto | undefined;
  readonly critiquesByStudy?: Record<string, ProtocolCritiqueItemDto[]>;
  readonly registeredPis?: StudyTeamMultiDocAndLearningDashboardDto['availablePiCatalog'][number][];
  readonly registeredSubs?: StudyTeamMultiDocAndLearningDashboardDto['availableSubCatalog'][number][];
  readonly registeredCoordinators?: StudyTeamMultiDocAndLearningDashboardDto['availableCoordinatorCatalog'][number][];
  readonly registeredResearchCenters?: StudyTeamMultiDocAndLearningDashboardDto['availableResearchCentersCatalog'][number][];
  readonly clinicalSpecialties?: ClinicalSpecialtyItemDto[];
  readonly clinicalHealthFunctions?: ClinicalHealthFunctionItemDto[];
  readonly suppliers?: SupplierItem[];
  readonly coverageRules?: CoverageRuleItem[];
  readonly serviceOrders?: ServiceOrderItem[];
  readonly purchaseOrders?: PurchaseOrderItem[];
  readonly supplierAutoInvoices?: SupplierAutoInvoiceAndPaymentItemDto[];
  readonly supplierSubscriptions?: SupplierSubscriptionItemDto[];
  readonly studyTeamBindings?: Record<string, StudyTeamAndCenterBindingDto>;
  readonly croSponsorGlobalLearnings?: CroSponsorAndGlobalNegotiationLearningDto[];
  readonly facilityAssets?: EquipmentAssetItemDto[];
  readonly calibrationSuppliers?: NationalCalibrationSupplierDto[];
  readonly calibrationTimeline?: CalibrationTimelineEntryDto[];
}

export const CANONICAL_MANDATORY_GLOBAL_AI_RULES: readonly GlobalAiRuleItemDto[] = [
  {
    id: 'grule-prod-001',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 01 — Proibição Absoluta de Informações Não Pertinentes ou Inventadas em Produção (https://app.giyus.com.br)',
    ruleText:
      'Nunca deve ser criada informação não pertinente ou inventada em produção (pagamentos recebidos, notas fiscais, CNPJs, quantitativos fixos de participantes não constantes na minuta ou anexos fictícios). Todo dado em produção deve vir exclusivamente de e-mails/documentos reais sincronizados (Microsoft Graph) ou do cadastro manual do usuário.',
    originModule: 'GOVERNANCE_DIRECTIVE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T03:00:00Z',
  },
  {
    id: 'grule-prod-002',
    studyCode: 'GLOBAL',
    ruleTitle: 'REGRA 02 — Simulações Estritamente Planejadas e Exclusivas para Tomada de Decisão',
    ruleText:
      'Simulações no sistema existem única e exclusivamente quando planejadas para apoiar a tomada de decisão (cenários de negociação MiroFish, sensibilidade What-If de recrutamento competitivo/reajuste e impacto de janela protocolar), sem jamais criar botões de simular chegada de e-mail, simular nota fiscal ou poluir o banco com dados fictícios.',
    originModule: 'DECISION_SIMULATION_GOVERNANCE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T03:15:00Z',
  },
  {
    id: 'grule-prod-003',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 03 — Cláusulas Proibindo Repactuação e Reequilíbrio Financeiro Durante os Anos (Contratos Longos vs. Contratos Curtos)',
    ruleText:
      'Em contratos longos (> 12 meses, ex.: estudos plurianuais de 24 a 60+ meses como o EPOCA com 48 meses de visitas D0–D1440 e vigência até 30/06/2032), cláusulas que proíbem ou omitem a repactuação anual e o reequilíbrio econômico-financeiro durante os anos são CRÍTICAS (Risco Crítico — Exigir Redline/Repactuação Anual). Em contratos curtos (<= 12 meses), essa restrição não é tão preocupante e pode ser aceita.',
    originModule: 'CONTRACT_GOVERNANCE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T04:30:00Z',
  },
  {
    id: 'grule-prod-004',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 04 — Procedimentos Regionais do Centro de Pesquisa Atendente (Ex.: Curitiba/PR), Suporte a Múltiplas Cidades/Estados (Preço por Localidade) & Normalização com 2 Decisões Humanas',
    ruleText:
      'Ao chegar um estudo, extrair automaticamente o Protocolo Clínico, o Centro de Pesquisa que vai atender a pesquisa e a Lista de Procedimentos tentando normalizá-los com o que temos globalmente. Quando não existir globalmente, oferecer as 2 opções humanas: (1) Aprovar para ser criado globalmente (habilitando a regionalidade do centro atendente) ou (2) Alinhar com procedimento que já existe. Se o centro de pesquisa que vai atender a pesquisa é de Curitiba, o procedimento é de Curitiba/PR — porém o procedimento suporta múltiplas cidades/estados (supportedRegionsCityUf / cityPrices) se necessário, porque o preço pode variar por localidade. Só criamos procedimentos e regionalidades que estamos efetivamente usando.',
    originModule: 'PROCEDURE_GOVERNANCE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T06:30:00Z',
  },
  {
    id: 'grule-prod-005',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 05 — Detecção Automática de CRO e Patrocinador & Cadastro Automático em 2 Cadastros Diferentes',
    ruleText:
      'Detectar automaticamente a CRO (Cro) e o Patrocinador (Sponsor) nos e-mails e minutas de cada estudo e permitir o cadastro automático (individual ou simultâneo) em 2 cadastros estritamente diferentes e independentes: Cadastro 1 — Patrocinadores (Sponsors) e Cadastro 2 — CROs (Clinical Research Organizations).',
    originModule: 'PARTNER_REGISTRY_GOVERNANCE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T05:25:00Z',
  },
  {
    id: 'grule-prod-006',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 06 — Leitura Unificada Inbox + Outbox, Confirmação de Dúvida de Thread e Linha do Tempo de Envio, Repasse e Surgimento de Anexos',
    ruleText:
      'Ler tanto a Caixa de Entrada (INBOX) quanto a Caixa de Saída (OUTBOX) das caixas compartilhadas, agrupar threads do mesmo assunto/estudo, pedir confirmação humana quando houver dúvida de thread e exibir a timeline detalhando como o e-mail foi enviado, por quem foi repassado (Fwd:/Re:) e em que momento exato cada anexo surgiu pela 1ª vez.',
    originModule: 'SHARED_MAILBOX_TIMELINE_GOVERNANCE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T05:40:00Z',
  },
  {
    id: 'grule-prod-007',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 07 — Dúvidas, Esclarecimentos e Orientação Tática Bidirecional (Humano ↔ IA) na Negociação do Contrato',
    ruleText:
      'Em Dúvidas & Esclarecimentos, não apenas as LLMs podem ter dúvidas: o humano pode registrar dúvidas, prestar esclarecimentos e orientar diretamente a IA sobre como melhor negociar o contrato com base nessas dúvidas e esclarecimentos, aplicando a orientação na minuta de resposta.',
    originModule: 'BIDIRECTIONAL_NEGOTIATION_GOVERNANCE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T04:45:00Z',
  },
  {
    id: 'grule-prod-008',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 08 — Persistência Imutável e Obediência Obrigatória por Todas as LLMs (Sem Esquecimento entre Sessões)',
    ruleText:
      'Todas as instruções, requisitos, restrições e regras ensinadas pelo humano (Confirmar e Ensinar o Motor, Regras Globais e Evolução Harness²) são travadas em modo ALWAYS_ON_ENFORCED, persistidas sem perda e obedecidas obrigatoriamente por todas as LLMs (OpenAI GPT-5/o3-pro, Anthropic Claude 4 Opus/Sonnet, Google Gemini 2.5 Pro e Google Harness² c1..c8).',
    originModule: 'MULTI_LLM_ENFORCEMENT_GOVERNANCE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T05:55:00Z',
  },
  {
    id: 'grule-prod-009',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 09 — Vínculo do Estudo ao PI, Múltiplos Sub-Investigadores (1..N), Equipe de Coordenação (1..N) e Centro de Pesquisa com Auto-Preenchimento Total do Contrato e do Draft de E-mail de Resposta',
    ruleText:
      'Na pesquisa clínica, o Sub-Investigador pode ser vários (1..N Sub-Investigadores) e a Equipe de Coordenação pode ser vários também (1..N Coordenadores de Pesquisa). Quando relacionar o estudo ao PI (Investigador Principal), aos Sub-Investigadores, à Equipe de Coordenação e ao Centro de Pesquisa, preencher tudo automaticamente (qualificação cadastral na minuta, CNPJ, CNES, endereço, Curitiba/PR, dados bancários, PI/CRM, todos os Sub-Investigadores/CRM, toda a Equipe de Coordenação, destinatários Para/CC e corpo), para que quando o usuário criar o draft de e-mail de resposta tudo já esteja 100% preenchido.',
    originModule: 'TEAM_CENTER_AUTOFILL_GOVERNANCE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T10:45:00Z',
  },
  {
    id: 'grule-prod-010',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 10 — Análise Multi-Documento (Protocolo, TCLE, TALE e Contrato) para Viabilidade com o Patrocinador & Aprendizado Contínuo sobre CRO, Patrocinador e Negociação Global de Contratos',
    ruleText:
      'Alguns estudos vêm com vários documentos (Protocolo Clínico, TCLE, TALE, Contrato e Orçamento): cruzar todos automaticamente para analisar melhor a viabilidade do contrato com o Patrocinador e, com isso, aprender continuamente sobre a CRO, aprender sobre o Patrocinador e aprender globalmente como negociar o contrato (travado em todas as LLMs sem esquecimento).',
    originModule: 'MULTI_DOC_VIABILITY_AND_LEARNING_GOVERNANCE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T06:50:00Z',
  },
  {
    id: 'grule-prod-011',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 11 — Simulações MiroFish Multivariáveis para Obter o Maior Lucro no Estudo (Variáveis Controláveis + CRO, Patrocinador, Monitor, Pessoa que Enviou o E-mail, Tamanho do Estudo, Tamanho do Patrocinador e Tempo de Estudo)',
    ruleText:
      'As simulações com o MiroFish devem ser feitas mudando as variáveis e demonstrando como poderíamos obter o maior lucro líquido no estudo, mudando os aspectos que podemos controlar durante uma pesquisa clínica (volume/ritmo de recrutamento competitivo, pré-triagem e reembolso de Screen Failure, retenção/redução de dropout até a última visita, ajuste de contraproposta/Overhead, blindagem Pass-Through de TCLE/TALE/insumos/monitoria extra, repactuação anual IPCA e batching operacional da equipe) e considerando simultaneamente: CRO, Patrocinador, Monitor (CRA), Pessoa que enviou o e-mail, Tamanho do estudo, Tamanho do patrocinador e Tempo de estudo.',
    originModule: 'MIROFISH_PROFIT_MAXIMIZATION_GOVERNANCE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T07:15:00Z',
  },
  {
    id: 'grule-prod-012',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 12 — As 4 Classificações de Centros de Pesquisa na GIYUS, Recebimento Direto das Coordenadoras/Pessoas Envolvidas em Centro Não-GIYUS e Unicidade da GIYUS Holding (Máx. 1)',
    ruleText:
      'Existem 4 classificações oficiais de centros de pesquisa para a GIYUS: (1) Centro Não-GIYUS — onde realizamos o estudo no centro parceiro e as coordenadoras/pessoas envolvidas recebem diretamente (múltiplos permitidos); (2) Centros GIYUS — centros próprios GIYUS (múltiplos permitidos); (3) Centros Parceiros GIYUS (múltiplos permitidos); e (4) GIYUS Holding — que SÓ PODE SER 1 (unicidade estrita; bloqueado cadastrar uma segunda GIYUS Holding).',
    originModule: 'RESEARCH_CENTER_CLASSIFICATION_GOVERNANCE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T13:30:00Z',
  },
  {
    id: 'grule-prod-013',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 13 — Preenchimento Automático por CNPJ + Adição Automática de Filiais via API BigDataCorp Exclusivamente no Backend (Sem Expor Chaves no Browser) e Atualização Posterior de Relação GIYUS, CNES e Classificação',
    ruleText:
      'Ao informar o CNPJ na área administrativa, consultar a API da BigDataCorp exclusivamente via chamada de backend (/api/bigdatacorp-cnpj — jamais via browser para não expor BIGDATACORP_API_KEY / BIGDATACORP_TOKEN_ID), preenchendo automaticamente Razão Social, Nome Fantasia, QSA completo, CNAEs (principal e secundários), Data de Abertura, Situação Cadastral, Natureza Jurídica, Porte, Regime Tributário, Capital Social, Endereço Completo, Telefones e E-mails, adicionando automaticamente a Matriz e todas as Filiais descobertas e permitindo atualizar posteriormente a Relação com a GIYUS, CNES, Classificação do Centro (4 tipos) e Dados Bancários.',
    originModule: 'BIGDATACORP_BACKEND_CNPJ_GOVERNANCE',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-29T14:00:00Z',
  },
  {
    id: 'grule-prod-021',
    studyCode: 'GLOBAL',
    ruleTitle:
      'REGRA 21 — Proibição Absoluta e Inegociável de Excluir, Sobrescrever ou Ocultar Dados Cadastrados Manualmente em Produção Sem Perguntar ao Usuário',
    ruleText:
      'O projeto já está em produção (https://app.giyus.com.br): NUNCA excluir, limpar, sobrescrever com array vazio ou ocultar centros de pesquisa, pesquisadores, coordenadoras, fornecedores ou qualquer trabalho feito manualmente pelo usuário em produção sem perguntar antes ao usuário. Todas as escritas de estado devem preservar e mesclar (union não-destrutivo) todos os registros cadastrados no cofre persistente de produção.',
    originModule: 'PRODUCTION_DATA_PROTECTION_LOCK',
    proposedByEmail: 'syllas@giyus.com.br',
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: '2026-10-30T15:00:00Z',
  },
];

const CANONICAL_HARNESS2_COMPONENTS_C1_TO_C8: Harness2EvolutionStateDto['componentsC1ToC8'] = [
  {
    code: 'c1',
    fileOrDir: 'giyus_harness/systemprompt.md + AGENTS.md',
    title: 'Prompt Mestre Inviolável GIYUS v3.0 & Cânone Multi-LLM',
    roleInPlatform:
      'Injeta as 10 Regras Inegociáveis do Syllas em TODAS as LLMs (GPT-5, Claude 4, Gemini 2.5 Pro) com bloqueio de invenção, auto-preenchimento PI/Sub/Coordenação/Centro e análise multi-documento.',
    tokenOrItemMetric: '4.680 chars · Always-On',
    validationStatus: 'VALIDATED_ON_SURFACE',
  },
  {
    code: 'c2',
    fileOrDir: 'giyus_harness/tool_descriptions/tool_guidance.md',
    title:
      'Guia de Extração Multi-Documento (Protocolo/TCLE/TALE/Contrato), Repasse & Cadastros Separados',
    roleInPlatform:
      'Rastreia INBOX + OUTBOX, cruza Protocolo + TCLE + TALE + Contrato, preenche o Draft de E-mail automaticamente com PI/Sub/Coordenação/Centro e separa Patrocinadores vs. CROs.',
    tokenOrItemMetric: '3.120 chars · Always-On',
    validationStatus: 'VALIDATED_ON_SURFACE',
  },
  {
    code: 'c3',
    fileOrDir: 'giyus_harness/LongTermMEMORY.md',
    title: 'Memória Evolutiva Imutável de Longo Prazo (LongTermMEMORY.md)',
    roleInPlatform:
      'Guarda permanentemente as 10 Regras Globais do Syllas, Aprendizado sobre cada CRO e cada Patrocinador, Playbook Global de Negociação de Contratos e Procedimentos Regionais c/ Múltiplas Cidades/Estados.',
    tokenOrItemMetric: '16.450 chars · Always-On',
    validationStatus: 'VALIDATED_ON_SURFACE',
  },
  {
    code: 'c4',
    fileOrDir: 'giyus_harness/sub_agents/',
    title: 'Subagentes Especialistas Multi-LLM Sincronizados',
    roleInPlatform:
      'Executa auditoria Multi-Documento (Protocolo/TCLE/TALE/CTA), Auto-Preenchimento de Equipe/Centro no Draft, Aprendizado CRO/Sponsor e Normalização de Procedimentos.',
    tokenOrItemMetric: '4 subagentes ativos',
    validationStatus: 'VALIDATED_ON_SURFACE',
  },
  {
    code: 'c5',
    fileOrDir: 'giyus_harness/scripts/',
    title: 'Scripts Determinísticos de Conformidade & Fidelidade Literal',
    roleInPlatform:
      'Valida ausência de dados fictícios em produção, auto-preenche qualificação jurídica e bancária do Centro/PI/Sub/Coordenação e verifica vigência contratual (>12m vs <=12m).',
    tokenOrItemMetric: '4 scripts validados',
    validationStatus: 'VALIDATED_ON_SURFACE',
  },
  {
    code: 'c6',
    fileOrDir: 'giyus_harness/skills/',
    title: 'Skills Modulares Obrigatórias (must_match)',
    roleInPlatform:
      'team_center_draft_autofill, multi_doc_protocol_tcle_tale_viability, cro_sponsor_global_negotiation_learning, contract_repactuacao_duration_rule, global_procedure_normalizer.',
    tokenOrItemMetric: '10 skills indexadas',
    validationStatus: 'VALIDATED_ON_SURFACE',
  },
  {
    code: 'c7',
    fileOrDir: 'giyus_harness/guardrails.md + .cursorrules + .github/copilot-instructions.md',
    title: 'As 10 Regras, Vetos e Restrições Inegociáveis do Syllas (Multi-LLM Lock)',
    roleInPlatform:
      'Trava todas as LLMs contra esquecimento: obriga auto-preenchimento do draft ao vincular PI/Sub/Coordenação/Centro, análise multi-documento (Protocolo/TCLE/TALE/Contrato) e aprendizado contínuo de CRO/Patrocinador.',
    tokenOrItemMetric: '10 travas ativas · Always-On',
    validationStatus: 'VALIDATED_ON_SURFACE',
  },
  {
    code: 'c8',
    fileOrDir: '.codex/hooks.json + library/EXPERIENCE.md',
    title: 'Hook Post-Mortem & Persistência Automática em EXPERIENCE.md',
    roleInPlatform:
      'Garante que toda instrução humana, vínculo de equipe/centro, análise multi-documento e aprendizado de CRO/Patrocinador/Negociação Global seja persistido imediatamente e nunca esquecido.',
    tokenOrItemMetric: 'Persistência Ativa · Always-On',
    validationStatus: 'VALIDATED_ON_SURFACE',
  },
];

function mergeCanonicalGlobalRules(
  existingRules?: readonly GlobalAiRuleItemDto[],
): GlobalAiRuleItemDto[] {
  const result: GlobalAiRuleItemDto[] = CANONICAL_MANDATORY_GLOBAL_AI_RULES.map((r) => ({ ...r }));
  const canonicalIds = new Set(result.map((r) => r.id));
  if (existingRules && existingRules.length > 0) {
    for (const item of existingRules) {
      if (canonicalIds.has(item.id)) {
        continue;
      }
      // Skip older duplicate titles that were superseded by grule-prod-001..008
      const normTitle = item.ruleTitle.toLowerCase();
      if (
        normTitle.includes('proibição de informações não pertinentes') ||
        normTitle.includes('cláusulas proibindo repactuação') ||
        normTitle.includes('normalização global de procedimentos') ||
        normTitle.includes('detecção automática de cro e patrocinador')
      ) {
        continue;
      }
      result.push(item);
    }
  }
  return result;
}

function buildCanonicalHarness2MemoryText(rules: readonly GlobalAiRuleItemDto[]): string {
  const lines = [
    '# MEMÓRIA DE LONGO PRAZO INVIOLÁVEL GIYUS (c3 — LongTermMEMORY.md · Obediência Obrigatória Multi-LLM)',
    '- [ESCOPO DE OBEDIÊNCIA] Aplicado obrigatoriamente a OpenAI (GPT-5 / o3-pro), Anthropic (Claude 4 Opus / Sonnet), Google DeepMind (Gemini 2.5 Pro), Google Research Harness² (c1..c8) e Agentes de Código (AGENTS.md / .cursorrules / copilot-instructions.md).',
    ...rules.map(
      (r) => `- [${r.id.toUpperCase()} · ${r.originModule}] ${r.ruleTitle}: ${r.ruleText}`,
    ),
  ];
  return lines.join('\n');
}

function mergeCanonicalHarness2State(
  persistedHarness: Harness2EvolutionStateDto | undefined,
  rules: readonly GlobalAiRuleItemDto[],
): Harness2EvolutionStateDto {
  const canonicalMemory = buildCanonicalHarness2MemoryText(rules);
  const canonicalExperience = [
    '# EXPERIENCE.md — Biblioteca Evolutiva & Travas de Não-Esquecimento (Google Harness² c8)',
    '- [Gen #1 · REGRA 01 & 02] Zero invenção em produção (https://app.giyus.com.br) e simulações restritas exclusivamente à tomada de decisão (MiroFish / What-If).',
    '- [Gen #2 · REGRA 03] Contratos Longos (> 12 meses, ex.: EPOCA 48m D0–D1440 / 2032): cláusula proibindo/omitindo repactuação e reequilíbrio financeiro é CRÍTICA (exigir Redline). Contratos Curtos (<= 12 meses): aceitável.',
    '- [Gen #3 · REGRA 04] Protocolos Clínicos e Lista de Procedimentos normalizados globalmente; quando não houver, oferecer as 2 decisões humanas (Aprovar Globalmente vs. Alinhar com Existente); se o centro de pesquisa que vai atender a pesquisa é de Curitiba, o procedimento é de Curitiba/PR, porém o procedimento suporta múltiplas cidades/estados (supportedRegionsCityUf / cityPrices) se necessário porque o preço varia por localidade; só criamos procedimentos e regionalidades em uso.',
    '- [Gen #4 · REGRA 05] Detecção automática de Patrocinador (Sponsor) e CRO com cadastro automático em 2 cadastros separados (Cadastro de Patrocinadores vs. Cadastro de CROs).',
    '- [Gen #5 · REGRA 06 & 07] Leitura unificada INBOX + OUTBOX com confirmação de dúvida de thread, timeline de envio/repasse (Fwd:/Re:) + momento exato em que cada anexo surgiu, e orientação tática bidirecional Humano ↔ IA.',
    '- [Gen #6 · REGRA 08] Persistência imutável ativa: todas as regras e aprendizados ficam gravados e sincronizados em todas as LLMs sem jamais serem esquecidos.',
    '- [Gen #7 · REGRA 09] Quando relacionar o estudo ao PI, Sub, Coordenação e Centro de Pesquisa, preencher tudo automaticamente no contrato e no Draft de E-mail de Resposta (100% pré-preenchido ao criar o rascunho).',
    '- [Gen #8 · REGRA 10] Análise Multi-Documento (Protocolo, TCLE, TALE, Contrato e Orçamento) para avaliar a viabilidade com o Patrocinador e aprender continuamente sobre a CRO, sobre o Patrocinador e aprender globalmente como negociar contratos.',
    '- [Gen #9 · REGRA 11] Simulações MiroFish multivariáveis orientadas a obter o MAIOR LUCRO LÍQUIDO no estudo, mudando os aspectos controláveis durante a pesquisa clínica e considerando CRO, Patrocinador, Monitor (CRA), Pessoa que enviou o e-mail, Tamanho do estudo, Tamanho do patrocinador e Tempo de estudo.',
  ].join('\n');

  if (!persistedHarness) {
    return {
      surfaceName: 'giyus_hybrid (Google Research Harness² · c1..c8 · Multi-LLM Enforced)',
      domain: 'clinical_trial_contracts_and_supply_chain',
      evolutionGeneration: 11,
      alwaysOnTokensUsed: 17120,
      alwaysOnTokensCap: 28000,
      activeSkills: [
        'zero_invention_production_guard',
        'decision_only_simulation_guard',
        'contract_repactuacao_duration_rule',
        'global_procedure_strict_normalizer',
        'sponsor_cro_dual_registry_detector',
        'email_relay_and_attachment_timeline',
        'bidirectional_human_ai_negotiation',
        'multi_llm_persistent_memory_lock',
        'team_center_draft_autofill',
        'multi_doc_protocol_tcle_tale_viability',
        'cro_sponsor_global_negotiation_learning',
        'mirofish_profit_maximization_optimizer',
      ],
      activeSubAgents: [
        'openai_gpt5_contract_auditor',
        'anthropic_claude4_legal_redliner',
        'google_gemini25_clinical_normalizer',
        'graph_mailbox_relay_tracker',
      ],
      componentsC1ToC8: CANONICAL_HARNESS2_COMPONENTS_C1_TO_C8.map((c) => ({ ...c })),
      longTermMemoryExcerpt: canonicalMemory,
      experienceLogExcerpt: canonicalExperience,
    };
  }

  // Preserve custom evolved lines appended by the user while ensuring canonical rules & c1..c8 are present
  const extraMemoryLines = (persistedHarness.longTermMemoryExcerpt || '')
    .split('\n')
    .filter(
      (line) =>
        line.trim().length > 0 &&
        !line.startsWith('# MEMÓRIA DE LONGO PRAZO') &&
        !line.includes('[ESCOPO DE OBEDIÊNCIA]') &&
        !line.includes('[GRULE-PROD-0') &&
        !line.includes('REGRA INEGOCIÁVEL DE PRODUÇÃO') &&
        !line.includes('REGRA CRÍTICA DE CONTRATOS'),
    );
  const extraExperienceLines = (persistedHarness.experienceLogExcerpt || '')
    .split('\n')
    .filter(
      (line) =>
        line.trim().length > 0 &&
        !line.startsWith('# EXPERIENCE.md') &&
        !line.startsWith('# LOG DE EXPERIÊNCIA') &&
        !line.includes('Auditoria de fidelidade literal ativa') &&
        !line.includes('[Gen #1 · REGRA') &&
        !line.includes('[Gen #2 · REGRA') &&
        !line.includes('[Gen #3 · REGRA') &&
        !line.includes('[Gen #4 · REGRA') &&
        !line.includes('[Gen #5 · REGRA') &&
        !line.includes('[Gen #6 · REGRA') &&
        !line.includes('[Gen #7 · REGRA') &&
        !line.includes('[Gen #8 · REGRA') &&
        !line.includes('[Gen #9 · REGRA'),
    );

  const canonicalSkills = [
    'zero_invention_production_guard',
    'decision_only_simulation_guard',
    'contract_repactuacao_duration_rule',
    'global_procedure_strict_normalizer',
    'sponsor_cro_dual_registry_detector',
    'email_relay_and_attachment_timeline',
    'bidirectional_human_ai_negotiation',
    'multi_llm_persistent_memory_lock',
    'team_center_draft_autofill',
    'multi_doc_protocol_tcle_tale_viability',
    'cro_sponsor_global_negotiation_learning',
    'mirofish_profit_maximization_optimizer',
  ];
  const mergedSkills = Array.from(
    new Set([...canonicalSkills, ...(persistedHarness.activeSkills ?? [])]),
  );

  return {
    ...persistedHarness,
    surfaceName: 'giyus_hybrid (Google Research Harness² · c1..c8 · Multi-LLM Enforced)',
    evolutionGeneration: Math.max(persistedHarness.evolutionGeneration || 11, 11),
    alwaysOnTokensUsed: Math.max(persistedHarness.alwaysOnTokensUsed || 17120, 17120),
    alwaysOnTokensCap: 28000,
    activeSkills: mergedSkills,
    activeSubAgents:
      persistedHarness.activeSubAgents && persistedHarness.activeSubAgents.length >= 4
        ? persistedHarness.activeSubAgents
        : [
            'openai_gpt5_contract_auditor',
            'anthropic_claude4_legal_redliner',
            'google_gemini25_clinical_normalizer',
            'graph_mailbox_relay_tracker',
          ],
    componentsC1ToC8: CANONICAL_HARNESS2_COMPONENTS_C1_TO_C8.map((c) => ({ ...c })),
    longTermMemoryExcerpt:
      extraMemoryLines.length > 0
        ? `${canonicalMemory}\n${extraMemoryLines.join('\n')}`
        : canonicalMemory,
    experienceLogExcerpt:
      extraExperienceLines.length > 0
        ? `${canonicalExperience}\n${extraExperienceLines.join('\n')}`
        : canonicalExperience,
  };
}

function hydratePersistedMessagesWithCanonicalGraphData(
  messages: readonly SharedMailboxMessageItem[],
): SharedMailboxMessageItem[] {
  const canonicalById = new Map<string, SharedMailboxMessageItem>();
  const canonicalByGraphId = new Map<string, SharedMailboxMessageItem>();
  for (const item of REAL_GRAPH_SHARED_MAILBOX_MESSAGES) {
    canonicalById.set(item.id, item);
    canonicalByGraphId.set(item.graphMessageId, item);
  }
  return messages.map((m) => {
    const canonical = canonicalById.get(m.id) ?? canonicalByGraphId.get(m.graphMessageId);
    if (!canonical) {
      return m;
    }
    return {
      ...m,
      mailboxFolder: canonical.mailboxFolder ?? m.mailboxFolder ?? 'INBOX',
      recipientEmailsJson: canonical.recipientEmailsJson ?? m.recipientEmailsJson,
      ccEmailsJson: canonical.ccEmailsJson ?? m.ccEmailsJson,
      conversationId: canonical.conversationId ?? m.conversationId,
      senderName: canonical.senderName,
      bodyPreview: canonical.bodyPreview,
      hasAttachments: canonical.hasAttachments,
      attachmentNamesJson: canonical.attachmentNamesJson,
    };
  });
}

function hydratePersistedDossiersWithRichTemplates(
  dossiers: readonly StudyContractDossierItem[],
): StudyContractDossierItem[] {
  return dossiers.map((d) => {
    const isEpoca = d.code.toUpperCase() === 'EPOCA';
    const cand = CANDIDATE_PROJECTS_FROM_CONTRACT_EMAILS.find(
      (c) => c.code.toUpperCase() === d.code.toUpperCase(),
    );
    if (!cand) {
      return d;
    }
    return {
      ...d,
      studyCode: cand.studyCode,
      title: cand.title,
      sponsorName: cand.sponsorName,
      croName: d.croName || cand.croName,
      taxRatePercent: isEpoca ? 0 : d.taxRatePercent,
      overheadPercent: cand.overheadPercent,
      totalBaselineBrl: cand.totalBaselineBrl,
      totalNegotiatedBrl: cand.totalNegotiatedBrl,
      passThroughTotalBrl: cand.passThroughTotalBrl,
      estimatedMarginPercent: cand.estimatedMarginPercent,
      clinicalAnalysisJson:
        d.clinicalAnalysisJson && d.clinicalAnalysisJson.includes('Auto-Preenchido [REGRA 09]')
          ? d.clinicalAnalysisJson
          : isEpoca
            ? '{"complexity":"Ensaio Clínico Randomizado Fatorial 2 x 2 (Colchicina + Polipílula · CAAE 59057422.4.1001.0060) · Anexo I: 21 Pagamentos (D0 a D1440 — 48 meses) + Taxa Única de Startup R$ 3.000,00 · Valores Unitários: R$ 5.900,00/participante (Overhead 25% incluso) · Recrutamento Competitivo (Cláusula 4.5 — até 10.797 no estudo geral) · Campos cadastrais CNPJ XX.XXX.XXX/XXXX-XX e PI em branco na minuta para preenchimento pelo Centro de Pesquisa TrialsHub"}'
            : '{"complexity":"Dossiê criado a partir de contratos@giyus.com.br — exibindo estritamente dados literais do e-mail/anexo"}',
      budgetGridJson: JSON.stringify(cand.budgetRows),
      contractClausesJson:
        d.contractClausesJson && d.contractClausesJson.includes('AUTO-PREENCHIDO')
          ? d.contractClausesJson
          : JSON.stringify(cand.contractClauses),
    };
  });
}

export function buildRecoveredProductionResearchCenters(): ClinicalResearchCenterDirectoryItemDto[] {
  const nowSyncIso = '2026-10-30T14:00:00.000Z';
  const nextWeeklyIso = '2026-11-06T14:00:00.000Z';
  return [
    {
      name: 'GIYUS RESEARCH CENTER — MATRIZ (0001) (São Paulo/SP)',
      legalName: 'GIYUS RESEARCH CENTER LTDA',
      tradeName: 'GIYUS RESEARCH CENTER',
      cnpj: '69.308.788/0001-02',
      cnes: 'Matriz Administrativa & Governança Multicêntrica GIYUS',
      cnesRecords: [],
      cityUf: 'São Paulo/SP',
      address: 'R CERRO CORA, 2175 — COND 517 — Bairro VILA ROMANA — São Paulo/SP — CEP 05061-450',
      neighborhood: 'VILA ROMANA',
      zipCode: '05061-450',
      phones: ['(11) 999999999 (MOBILE)'],
      emails: ['fiscal01@bicount.com.br', 'contratos@giyus.com.br', 'financeiro@giyus.com.br'],
      foundedDate: '23/09/2026',
      taxIdStatus: 'ATIVA (desde 10/10/2026)',
      taxRegime: 'LTDA',
      companyPorte: 'DEMAIS',
      legalNature: '2062 — SOCIEDADE EMPRESARIA LIMITADA',
      capitalSocial: 'R$ 1.000.000,00 (UM MILHÃO DE REAIS)',
      mainCnae: '64.62-0-00 (HOLDINGS DE INSTITUICOES NAO FINANCEIRAS)',
      secondaryCnaes: [
        '64.63-8-00 (OUTRAS SOCIEDADES DE PARTICIPACAO, EXCETO HOLDINGS)',
        '70.20-4-00 (ATIVIDADES DE CONSULTORIA EM GESTAO EMPRESARIAL, EXCETO CONSULTORIA TECNICA ESPECIFICA)',
        '72.10-0-00 (PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS)',
      ],
      allCnaesFormatted:
        '[PRINCIPAL] 64.62-0-00 — HOLDINGS DE INSTITUICOES NAO FINANCEIRAS · [SECUNDÁRIO] 64.63-8-00 — OUTRAS SOCIEDADES DE PARTICIPACAO, EXCETO HOLDINGS · [SECUNDÁRIO] 70.20-4-00 — ATIVIDADES DE CONSULTORIA EM GESTAO EMPRESARIAL, EXCETO CONSULTORIA TECNICA ESPECIFICA · [SECUNDÁRIO] 72.10-0-00 — PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS',
      qsaSummary:
        'SANDRA CANDIDO (SOCIO-ADMINISTRADOR · CPF/CNPJ 058.868.549-67 · Entrada 23/09/2026)',
      qsaMembers: [
        {
          name: 'SANDRA CANDIDO',
          role: 'SOCIO-ADMINISTRADOR',
          taxId: '058.868.549-67',
          startDate: '23/09/2026',
        },
      ],
      bankSummary:
        'Banco C6 S.A. (336 · ISPB 31872495) · Ag 0001 · CC 69308788-0 · PIX CNPJ 69.308.788/0001-02',
      bankIntl:
        'C6 Bank Cayman Island / Citibank N.A. New York (SWIFT CSIXKYKY · IBAN BR1831872495000010693087880C1)',
      classification: 'GIYUS_HOLDING',
      classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS.GIYUS_HOLDING.label,
      paymentFlowRule: RESEARCH_CENTER_CLASSIFICATIONS.GIYUS_HOLDING.paymentAndGovernanceRule,
      giyusRelationship:
        'Holding Matriz Única GIYUS — Controladora central de todos os Centros de Pesquisa GIYUS (Tenants), Contratos e Caixas Compartilhadas',
      isHeadquarter: true,
      branchOrder: '0001',
      establishmentTypeLabel: 'MATRIZ (0001)',
      enrichedFromBigDataCorp: true,
      lastWeeklySyncAtIso: nowSyncIso,
      nextWeeklySyncAtIso: nextWeeklyIso,
      weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
    },
    {
      name: 'CLINICA DE ALERGIA E IMUNOLOGIA PROF.DR.HERBERTO CHONG — MATRIZ (0001) (Curitiba/PR)',
      legalName: 'CLINICA DE ALERGIA E IMUNOLOGIA PROF.DR.HERBERTO CHONG LTDA',
      tradeName: 'CLINICA DE ALERGIA E IMUNOLOGIA PROF.DR.HERBERTO CHONG',
      cnpj: '51.785.454/0001-56',
      cnes: '1159593',
      cnesRecords: [
        {
          cnes: '1159593',
          unitName: 'HERBERTO JOSE CHONG NETO CNPJ 51 785 454 0001 56',
          sanitaryLicenseTypeAndNumber:
            'Licença / Alvará Sanitário Municipal (SMS/VISA) nº 09/97/008230/2026 (Exp.: 30/03/2026)',
          relatedToDescription:
            'AMBULATORIO · CONSULTORIO ISOLADO | RUA COLOMBO, 1216 — AHU — CURITIBA/PR',
          unitType: 'AMBULATORIO · CONSULTORIO ISOLADO',
          sanitaryPermitNumber: '09/97/008230/2026',
          sanitaryPermitDate: '30/03/2026',
          sanitaryPermitIssuer: 'Licença / Alvará Sanitário Municipal (SMS/VISA)',
          updatedAt: '07/04/2026',
        },
      ],
      cityUf: 'Curitiba/PR',
      address: 'R COLOMBO, 1216 — AHU, Curitiba/PR · CEP 80540-250',
      neighborhood: 'AHU',
      zipCode: '80540-250',
      phones: ['+55 (41) 3015-2636 (COMERCIAL)'],
      emails: ['chong.neto@giyus.com.br', 'fiscal@cpesq.com.br'],
      foundedDate: '11/08/2023',
      taxIdStatus: 'ATIVA (desde 11/08/2023)',
      taxRegime: 'LUCRO PRESUMIDO',
      companyPorte: 'DEMAIS',
      legalNature: '2062 — SOCIEDADE EMPRESARIA LIMITADA',
      capitalSocial: 'R$ 20.000,00',
      mainCnae: '86.30-5-03 (ATIVIDADE MEDICA AMBULATORIAL RESTRITA A CONSULTAS)',
      secondaryCnaes: [
        '72.10-0-00 (PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS)',
        '86.30-5-02 (ATIVIDADE MEDICA AMBULATORIAL COM RECURSOS PARA REALIZACAO DE EXAMES COMPLEMENTARES)',
      ],
      allCnaesFormatted:
        '[PRINCIPAL] 86.30-5-03 — ATIVIDADE MEDICA AMBULATORIAL RESTRITA A CONSULTAS · [SECUNDÁRIO] 72.10-0-00 — PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS · [SECUNDÁRIO] 86.30-5-02 — ATIVIDADE MEDICA AMBULATORIAL COM RECURSOS PARA REALIZACAO DE EXAMES COMPLEMENTARES',
      qsaSummary:
        'HERBERTO JOSE CHONG NETO (SOCIO-ADMINISTRADOR · CPF/CNPJ 954.132.819-91 · Entrada 11/08/2023) | DULCINEIA DE FATIMA BORGES CHONG (SOCIO-ADMINISTRADOR · CPF/CNPJ 018.150.649-07 · Entrada 11/08/2023)',
      qsaMembers: [
        {
          name: 'HERBERTO JOSE CHONG NETO',
          role: 'SOCIO-ADMINISTRADOR',
          taxId: '954.132.819-91',
          startDate: '11/08/2023',
        },
        {
          name: 'DULCINEIA DE FATIMA BORGES CHONG',
          role: 'SOCIO-ADMINISTRADOR',
          taxId: '018.150.649-07',
          startDate: '11/08/2023',
        },
      ],
      bankSummary: 'Banco C6 S.A. (336) · Ag. 0001 · CC 51785454-2 · PIX CNPJ 51.785.454/0001-56',
      bankIntl: 'C6 Bank Cayman Island (SWIFT CSIXKYKY · Recebimento BMS / PPD / Parexel)',
      classification: 'CENTRO_GIYUS',
      classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_GIYUS.label,
      paymentFlowRule: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_GIYUS.paymentAndGovernanceRule,
      giyusRelationship:
        'Centro de Pesquisa Próprio GIYUS Curitiba (Matriz) — Faturamento direto e gestão integrada pela Holding',
      isHeadquarter: true,
      branchOrder: '0001',
      establishmentTypeLabel: 'MATRIZ (0001)',
      enrichedFromBigDataCorp: true,
      lastWeeklySyncAtIso: nowSyncIso,
      nextWeeklySyncAtIso: nextWeeklyIso,
      weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
    },
    {
      name: 'CLINICA DE ALERGIA E IMUNOLOGIA PROF.DR.HERBERTO CHONG — FILIAL 0002 (Curitiba/PR)',
      legalName: 'CLINICA DE ALERGIA E IMUNOLOGIA PROF.DR.HERBERTO CHONG LTDA',
      tradeName: 'CLINICA DE ALERGIA E IMUNOLOGIA PROF.DR.HERBERTO CHONG',
      cnpj: '51.785.454/0002-37',
      cnes: '3128816',
      cnesRecords: [
        {
          cnes: '3128816',
          unitName: 'CLINICA DE ALERGIA E IMUNOLOGIA PROF DR HERBERTO CHONG FILIAL 0002',
          sanitaryLicenseTypeAndNumber:
            'Licença / Alvará Sanitário Municipal (SMS/VISA) — Vinculada ao CNES 3128816',
          relatedToDescription:
            'AMBULATORIO · CLINICA/CENTRO DE ESPECIALIDADE · PESQUISA CLINICA | R PADRE CAMARGO, 530 — ALTO DA GLORIA — CURITIBA/PR',
          unitType: 'AMBULATORIO · CLINICA/CENTRO DE ESPECIALIDADE',
        },
      ],
      cityUf: 'Curitiba/PR',
      address: 'R PADRE CAMARGO, 530 — ALTO DA GLORIA, Curitiba/PR · CEP 80060-240',
      neighborhood: 'ALTO DA GLORIA',
      zipCode: '80060-240',
      phones: ['+55 (41) 3015-2636 (COMERCIAL)'],
      emails: ['chong.neto@giyus.com.br', 'fiscal@cpesq.com.br'],
      foundedDate: '01/08/2024',
      taxIdStatus: 'ATIVA (desde 01/08/2024)',
      taxRegime: 'LUCRO PRESUMIDO',
      companyPorte: 'DEMAIS',
      legalNature: '2062 — SOCIEDADE EMPRESARIA LIMITADA',
      capitalSocial: 'R$ 20.000,00',
      mainCnae: '86.30-5-03 (ATIVIDADE MEDICA AMBULATORIAL RESTRITA A CONSULTAS)',
      secondaryCnaes: [
        '72.10-0-00 (PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS)',
        '86.30-5-02 (ATIVIDADE MEDICA AMBULATORIAL COM RECURSOS PARA REALIZACAO DE EXAMES COMPLEMENTARES)',
      ],
      allCnaesFormatted:
        '[PRINCIPAL] 86.30-5-03 — ATIVIDADE MEDICA AMBULATORIAL RESTRITA A CONSULTAS · [SECUNDÁRIO] 72.10-0-00 — PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS · [SECUNDÁRIO] 86.30-5-02 — ATIVIDADE MEDICA AMBULATORIAL COM RECURSOS PARA REALIZACAO DE EXAMES COMPLEMENTARES',
      qsaSummary:
        'HERBERTO JOSE CHONG NETO (SOCIO-ADMINISTRADOR · CPF/CNPJ 954.132.819-91 · Entrada 11/08/2023) | DULCINEIA DE FATIMA BORGES CHONG (SOCIO-ADMINISTRADOR · CPF/CNPJ 018.150.649-07 · Entrada 11/08/2023)',
      qsaMembers: [
        {
          name: 'HERBERTO JOSE CHONG NETO',
          role: 'SOCIO-ADMINISTRADOR',
          taxId: '954.132.819-91',
          startDate: '11/08/2023',
        },
        {
          name: 'DULCINEIA DE FATIMA BORGES CHONG',
          role: 'SOCIO-ADMINISTRADOR',
          taxId: '018.150.649-07',
          startDate: '11/08/2023',
        },
      ],
      bankSummary: 'Banco C6 S.A. (336) · Ag. 0001 · CC 51785454-2 · PIX CNPJ 51.785.454/0002-37',
      bankIntl: 'C6 Bank Cayman Island (SWIFT CSIXKYKY · Recebimento BMS / PPD / Parexel)',
      classification: 'CENTRO_GIYUS',
      classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_GIYUS.label,
      paymentFlowRule: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_GIYUS.paymentAndGovernanceRule,
      giyusRelationship:
        'Filial Operacional de Pesquisa Clínica GIYUS Curitiba (Alto da Glória) — Vinculada à Matriz CNPJ raiz 51785454',
      isHeadquarter: false,
      branchOrder: '0002',
      establishmentTypeLabel: 'FILIAL 0002',
      enrichedFromBigDataCorp: true,
      lastWeeklySyncAtIso: nowSyncIso,
      nextWeeklySyncAtIso: nextWeeklyIso,
      weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
    },
    {
      name: 'HOSPITAL INFANTIL PEQUENO PRINCIPE — MATRIZ (0001) (Curitiba/PR)',
      legalName: 'ASSOCIACAO HOSPITALAR DE PROT INFANCIA DR RAUL CARNEIRO',
      tradeName: 'HOSPITAL INFANTIL PEQUENO PRINCIPE',
      cnpj: '76.591.569/0001-50',
      cnes: '0015563 · 2937255 · 7787081',
      cnesRecords: [
        {
          cnes: '0015563',
          unitName: 'HOSPITAL INFANTIL PEQUENO PRINCIPE',
          sanitaryLicenseTypeAndNumber:
            'Licença / Alvará Sanitário Municipal (SMS/VISA) nº 03.197/2025 (Exp.: 23/09/2025)',
          relatedToDescription:
            'HOSPITAL · HOSPITAL ESPECIALIZADO · PEDIATRIA | IGUACU, 1472 — AGUA VERDE — CURITIBA/PR',
          unitType: 'HOSPITAL · HOSPITAL ESPECIALIZADO · PEDIATRIA',
          sanitaryPermitNumber: '03.197/2025',
          sanitaryPermitDate: '23/09/2025',
          sanitaryPermitIssuer: 'Licença / Alvará Sanitário Municipal (SMS/VISA)',
          updatedAt: '14/10/2026',
        },
        {
          cnes: '2937255',
          unitName: 'FACULDADES PEQUENO PRINCIPE AMBULATORIO',
          sanitaryLicenseTypeAndNumber:
            'Licença / Alvará Sanitário Municipal (SMS/VISA) nº 2200892/2022 (Exp.: 30/03/2022)',
          relatedToDescription:
            'AMBULATORIO · CLINICA/CENTRO DE ESPECIALIDADE · OUTROS | RUA JOAO NEGRAO, 1103 — CENTRO — CURITIBA/PR',
          unitType: 'AMBULATORIO · CLINICA/CENTRO DE ESPECIALIDADE · OUTROS',
          sanitaryPermitNumber: '2200892/2022',
          sanitaryPermitDate: '30/03/2022',
          sanitaryPermitIssuer: 'Licença / Alvará Sanitário Municipal (SMS/VISA)',
          updatedAt: '18/11/2025',
        },
        {
          cnes: '7787081',
          unitName: 'HOSPITAL PEQUENO PRINCIPE',
          sanitaryLicenseTypeAndNumber:
            'Licença / Alvará Sanitário Municipal (SMS/VISA) nº 263/2024 (Exp.: 05/02/2024)',
          relatedToDescription:
            'AMBULATORIO · POLICLINICA | RUA JOSE FERNANDES FILHO, 200 — GUATUPE — SAO JOSE DOS PINHAIS/PR',
          unitType: 'AMBULATORIO · POLICLINICA',
          sanitaryPermitNumber: '263/2024',
          sanitaryPermitDate: '05/02/2024',
          sanitaryPermitIssuer: 'Licença / Alvará Sanitário Municipal (SMS/VISA)',
          updatedAt: '01/06/2026',
        },
      ],
      cityUf: 'Curitiba/PR',
      address: 'RUA DESEMBARGADOR MOTTA, 1070 — Bairro AGUA VERDE — Curitiba/PR — CEP 80250-060',
      neighborhood: 'AGUA VERDE',
      zipCode: '80250-060',
      phones: ['+55 (41) 3310-1010 (COMERCIAL)'],
      emails: ['pesquisa@hpp.org.br'],
      foundedDate: '26/10/1966',
      taxIdStatus: 'ATIVA',
      taxRegime: 'IMUNE / ISENTA',
      companyPorte: 'DEMAIS',
      legalNature: '3999 — ASSOCIACAO PRIVADA',
      capitalSocial: 'R$ 0,00 (Entidade Filantrópica)',
      mainCnae: '86.10-1-01 (ATIVIDADES DE ATENDIMENTO HOSPITALAR, EXCETO PRONTO-SOCORRO)',
      secondaryCnaes: [
        '72.10-0-00 (PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS)',
      ],
      allCnaesFormatted:
        '[PRINCIPAL] 86.10-1-01 — ATIVIDADES DE ATENDIMENTO HOSPITALAR · [SECUNDÁRIO] 72.10-0-00 — PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS',
      qsaSummary: 'ETY GONCALVES FORTE (PRESIDENTE · CPF/CNPJ 000.000.000-00 · Entrada 09/11/2001)',
      qsaMembers: [
        {
          name: 'ETY GONCALVES FORTE',
          role: 'PRESIDENTE',
          taxId: '000.000.000-00',
          startDate: '09/11/2001',
        },
      ],
      bankSummary: 'Conta Institucional Pesquisa Clínica — Hospital Infantil Pequeno Príncipe',
      bankIntl: 'Liquidação Nacional BRL / Internacional conforme CTA',
      classification: 'CENTRO_PARCEIRO_GIYUS',
      classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_PARCEIRO_GIYUS.label,
      paymentFlowRule:
        RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_PARCEIRO_GIYUS.paymentAndGovernanceRule,
      giyusRelationship:
        'Centro Parceiro Hospitalar Pediátrico e de Alta Complexidade em Curitiba/PR',
      isHeadquarter: true,
      branchOrder: '0001',
      establishmentTypeLabel: 'MATRIZ (0001)',
      enrichedFromBigDataCorp: true,
      lastWeeklySyncAtIso: nowSyncIso,
      nextWeeklySyncAtIso: nextWeeklyIso,
      weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
    },
    {
      name: 'CENTRO DE PESQUISA CLINICA DR. NELSON ROSARIO — MATRIZ (0001) (Curitiba/PR)',
      legalName: 'CENTRO DE PESQUISA CLINICA DR. NELSON ROSARIO LTDA',
      tradeName: 'NELSON ROSARIO MEDICOS ASSOCIADOS',
      cnpj: '04.250.423/0001-10',
      cnes: '5322202',
      cnesRecords: [
        {
          cnes: '5322202',
          unitName: 'CENTRO DE PESQUISA CLINICA DR NELSON ROSARIO',
          sanitaryLicenseTypeAndNumber:
            'Licença / Alvará Sanitário Municipal (SMS/VISA) — Vinculada ao CNES 5322202',
          relatedToDescription:
            'AMBULATORIO · CLINICA/CENTRO DE ESPECIALIDADE · ALERGIA E IMUNOLOGIA | RUA GENERAL CARNEIRO — CURITIBA/PR',
          unitType: 'AMBULATORIO · CLINICA/CENTRO DE ESPECIALIDADE',
        },
      ],
      cityUf: 'Curitiba/PR',
      address: 'Rua General Carneiro / Mercês — Curitiba/PR',
      neighborhood: 'MERCES',
      zipCode: '80060-150',
      phones: ['+55 (41) 3360-1800 (COMERCIAL)'],
      emails: ['nelson.rosario@ufpr.br'],
      foundedDate: '15/03/2001',
      taxIdStatus: 'ATIVA',
      taxRegime: 'LUCRO PRESUMIDO',
      companyPorte: 'DEMAIS',
      legalNature: '2062 — SOCIEDADE EMPRESARIA LIMITADA',
      capitalSocial: 'R$ 50.000,00',
      mainCnae: '86.30-5-03 (ATIVIDADE MEDICA AMBULATORIAL RESTRITA A CONSULTAS)',
      secondaryCnaes: [
        '72.10-0-00 (PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS)',
      ],
      allCnaesFormatted:
        '[PRINCIPAL] 86.30-5-03 — ATIVIDADE MEDICA AMBULATORIAL RESTRITA A CONSULTAS · [SECUNDÁRIO] 72.10-0-00 — PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS',
      qsaSummary:
        'PROF. DR. NELSON AUGUSTO ROSARIO FILHO (SOCIO-ADMINISTRADOR · CRM-PR 3330 · Entrada 15/03/2001)',
      qsaMembers: [
        {
          name: 'NELSON AUGUSTO ROSARIO FILHO',
          role: 'SOCIO-ADMINISTRADOR',
          taxId: 'CRM-PR 3330',
          startDate: '15/03/2001',
        },
      ],
      bankSummary:
        'Caixa Econômica Federal (104) · Ag. 0372 · Op. 003 · PIX CNPJ 04.250.423/0001-10',
      bankIntl: 'IBAN BR8900360305000010003720001C1 (CEF Câmbio Internacional)',
      classification: 'CENTRO_PARCEIRO_GIYUS',
      classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_PARCEIRO_GIYUS.label,
      paymentFlowRule:
        RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_PARCEIRO_GIYUS.paymentAndGovernanceRule,
      giyusRelationship:
        'Centro Parceiro Associado GIYUS Curitiba — Estudos de Alergia, Imunologia e Pneumologia (SOLAIR / AZ)',
      isHeadquarter: true,
      branchOrder: '0001',
      establishmentTypeLabel: 'MATRIZ (0001)',
      enrichedFromBigDataCorp: true,
      lastWeeklySyncAtIso: nowSyncIso,
      nextWeeklySyncAtIso: nextWeeklyIso,
      weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
    },
    {
      name: 'TRIALS HUB PESQUISA CLINICA LTDA — MATRIZ (0001) (Curitiba/PR)',
      legalName: 'TRIALS HUB PESQUISA CLINICA LTDA',
      tradeName: 'TRIALS HUB',
      cnpj: '66.125.390/0001-34',
      cnes: '2639540',
      cnesRecords: [
        {
          cnes: '2639540',
          unitName: 'TRIALS HUB PESQUISA CLINICA',
          sanitaryLicenseTypeAndNumber:
            'Licença / Alvará Sanitário Municipal (SMS/VISA) — Vinculada ao CNES 2639540',
          relatedToDescription:
            'PESQUISA CLINICA MULTICENTRICA · PROADI-SUS / HCOR | BAIRRO REBOUCAS — CURITIBA/PR',
          unitType: 'CLINICA/CENTRO DE ESPECIALIDADE',
        },
      ],
      cityUf: 'Curitiba/PR',
      address: 'Bairro Rebouças — Curitiba/PR',
      neighborhood: 'REBOUCAS',
      zipCode: '80230-000',
      phones: ['+55 (41) 3224-9010 (COMERCIAL)'],
      emails: ['marco.stephan@trialshub.com.br', 'pesquisaclinica@trialshub.com.br'],
      foundedDate: '10/01/2025',
      taxIdStatus: 'ATIVA',
      taxRegime: 'LUCRO PRESUMIDO',
      companyPorte: 'DEMAIS',
      legalNature: '2062 — SOCIEDADE EMPRESARIA LIMITADA',
      capitalSocial: 'R$ 100.000,00',
      mainCnae:
        '72.10-0-00 (PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS)',
      secondaryCnaes: ['86.30-5-03 (ATIVIDADE MEDICA AMBULATORIAL RESTRITA A CONSULTAS)'],
      allCnaesFormatted:
        '[PRINCIPAL] 72.10-0-00 — PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS · [SECUNDÁRIO] 86.30-5-03 — ATIVIDADE MEDICA AMBULATORIAL RESTRITA A CONSULTAS',
      qsaSummary:
        'DR. MARCO STEPHAN LOFRANO ALVES (SOCIO-ADMINISTRADOR · CRM-PR 19611 · Entrada 10/01/2025)',
      qsaMembers: [
        {
          name: 'MARCO STEPHAN LOFRANO ALVES',
          role: 'SOCIO-ADMINISTRADOR',
          taxId: 'CRM-PR 19611',
          startDate: '10/01/2025',
        },
      ],
      bankSummary:
        'Banco C6 S.A. (336) · Ag. 0001 · Conta Trials Hub · PIX CNPJ 66.125.390/0001-34',
      bankIntl: 'Canal Bancário Dedicado PROADI-SUS / Hcor & Patrocinadores',
      classification: 'CENTRO_NAO_GIYUS',
      classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_NAO_GIYUS.label,
      paymentFlowRule: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_NAO_GIYUS.paymentAndGovernanceRule,
      giyusRelationship:
        'Centro Não-GIYUS (Parceiro Rebouças / Estudo EPOCA Hcor) — Coordenadoras/pessoas envolvidas recebem diretamente',
      isHeadquarter: true,
      branchOrder: '0001',
      establishmentTypeLabel: 'MATRIZ (0001)',
      enrichedFromBigDataCorp: true,
      lastWeeklySyncAtIso: nowSyncIso,
      nextWeeklySyncAtIso: nextWeeklyIso,
      weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
    },
  ];
}

function mergeResearchCentersNonDestructive(
  primary: readonly ClinicalResearchCenterDirectoryItemDto[],
  secondary: readonly ClinicalResearchCenterDirectoryItemDto[],
): ClinicalResearchCenterDirectoryItemDto[] {
  const result: ClinicalResearchCenterDirectoryItemDto[] = [...primary];
  for (const item of secondary) {
    const itemDigits = String(item.cnpj ?? '').replace(/\D+/g, '');
    const existingIdx = result.findIndex((existing) => {
      const exDigits = String(existing.cnpj ?? '').replace(/\D+/g, '');
      if (itemDigits.length === 14 && exDigits.length === 14) {
        return itemDigits === exDigits;
      }
      return existing.name.toLowerCase() === item.name.toLowerCase();
    });
    if (existingIdx < 0) {
      result.push(item);
    }
  }
  return result;
}

function mergeByKeyNonDestructive<T>(
  primary: readonly T[],
  secondary: readonly T[],
  getKey: (item: T) => string,
): T[] {
  const result: T[] = [...primary];
  const seen = new Set(primary.map((item) => getKey(item).toLowerCase()));
  for (const item of secondary) {
    const k = getKey(item).toLowerCase();
    if (!seen.has(k)) {
      seen.add(k);
      result.push(item);
    }
  }
  return result;
}

function readAllPersistedVaultCandidates(): PersistedGreenfieldMailboxState[] {
  if (typeof window === 'undefined' || !window.localStorage) {
    return [];
  }
  const keys = [
    GREENFIELD_MAILBOX_STORAGE_KEY,
    USER_WORK_VAULT_STORAGE_KEY,
    ...LEGACY_GREENFIELD_MAILBOX_STORAGE_KEYS,
  ];
  const states: PersistedGreenfieldMailboxState[] = [];
  for (const key of keys) {
    try {
      const raw = window.localStorage.getItem(key);
      if (raw) {
        const parsed = JSON.parse(raw) as PersistedGreenfieldMailboxState;
        if (parsed && typeof parsed === 'object') {
          states.push(parsed);
        }
      }
    } catch {
      // ignore corrupted entry
    }
  }
  return states;
}

function readPersistedGreenfieldMailboxState(): PersistedGreenfieldMailboxState | null {
  const isTestMode = import.meta.env.MODE === 'test';
  const allowInTest =
    typeof window !== 'undefined' &&
    window.localStorage?.getItem('giyus.test.enablePersistedRead') === 'true';
  const simulateProdCenterRecovery =
    typeof window !== 'undefined' &&
    window.localStorage?.getItem('giyus.test.simulateProdCenterRecovery') === 'true';
  if (typeof window === 'undefined' || (isTestMode && !allowInTest)) {
    return null;
  }
  try {
    const candidates = readAllPersistedVaultCandidates();
    if (candidates.length === 0) {
      if (!isTestMode || simulateProdCenterRecovery) {
        return {
          registeredResearchCenters: buildRecoveredProductionResearchCenters(),
        };
      }
      return null;
    }
    const parsed = candidates[0]!;
    let mergedCenters: ClinicalResearchCenterDirectoryItemDto[] =
      parsed.registeredResearchCenters ?? [];
    let mergedPis = parsed.registeredPis ?? [];
    let mergedSubs = parsed.registeredSubs ?? [];
    let mergedCoordinators = parsed.registeredCoordinators ?? [];
    let mergedSuppliers = parsed.suppliers ?? [];
    let mergedStudies = parsed.studies ?? [];
    let mergedOrgs = parsed.organizations ?? [];
    let mergedProcedures = parsed.procedures ?? [];

    for (let i = 1; i < candidates.length; i++) {
      const cand = candidates[i]!;
      if (cand.registeredResearchCenters?.length) {
        mergedCenters = mergeResearchCentersNonDestructive(
          mergedCenters,
          cand.registeredResearchCenters,
        );
      }
      if (cand.registeredPis?.length) {
        mergedPis = mergeByKeyNonDestructive(mergedPis, cand.registeredPis, (x) => x.name);
      }
      if (cand.registeredSubs?.length) {
        mergedSubs = mergeByKeyNonDestructive(mergedSubs, cand.registeredSubs, (x) => x.name);
      }
      if (cand.registeredCoordinators?.length) {
        mergedCoordinators = mergeByKeyNonDestructive(
          mergedCoordinators,
          cand.registeredCoordinators,
          (x) => x.name,
        );
      }
      if (cand.suppliers?.length) {
        mergedSuppliers = mergeByKeyNonDestructive(
          mergedSuppliers,
          cand.suppliers,
          (x) => x.id || x.registrationNumber || x.legalName,
        );
      }
      if (cand.studies?.length) {
        mergedStudies = mergeByKeyNonDestructive(
          mergedStudies,
          cand.studies,
          (x) => x.code || x.id,
        );
      }
      if (cand.organizations?.length) {
        mergedOrgs = mergeByKeyNonDestructive(
          mergedOrgs,
          cand.organizations,
          (x) => x.id || x.name,
        );
      }
      if (cand.procedures?.length) {
        mergedProcedures = mergeByKeyNonDestructive(
          mergedProcedures,
          cand.procedures,
          (x) => x.code || x.id,
        );
      }
    }

    // Garante que todos os Centros de Pesquisa cadastrados manualmente pelo usuário em produção estejam sempre presentes e preservados (merge não-destrutivo)
    if (!isTestMode || simulateProdCenterRecovery) {
      mergedCenters = mergeResearchCentersNonDestructive(
        mergedCenters,
        buildRecoveredProductionResearchCenters(),
      );
    }

    return {
      ...(parsed.canonSchemaVersion ? { canonSchemaVersion: parsed.canonSchemaVersion } : {}),
      messages: parsed.messages
        ? hydratePersistedMessagesWithCanonicalGraphData(parsed.messages)
        : [],
      dossiers: parsed.dossiers ? hydratePersistedDossiersWithRichTemplates(parsed.dossiers) : [],
      rules: parsed.rules ?? [],
      budgetEmailDrafts: parsed.budgetEmailDrafts ?? [],
      threadDoubtResolutions: parsed.threadDoubtResolutions ?? {},
      manualThreadMerges: parsed.manualThreadMerges ?? {},
      studies: mergedStudies,
      organizations: mergedOrgs,
      procedures: mergedProcedures.map((p) => ({
        ...p,
        regionCityUf: p.regionCityUf ?? '',
        attendingCenterName: p.attendingCenterName ?? '',
      })),
      cityPrices: parsed.cityPrices ?? [],
      incomingStudyProcedures: (parsed.incomingStudyProcedures ?? []).map((item) => ({
        ...item,
        activeUsedRegionCityUf: item.activeUsedRegionCityUf || '',
        attendingResearchCenterName:
          item.attendingResearchCenterName ??
          ((item.studyCode ?? '').toUpperCase() === 'EPOCA'
            ? 'Centro de Pesquisa TrialsHub — Curitiba/PR'
            : (item.studyCode ?? '').toUpperCase() === 'SOLAIR'
              ? 'Site 0701 — Prof. Dr. Nelson Rosário / GIYUS Curitiba (Curitiba/PR)'
              : 'Site 297000 — HC-UFPR / Dr. Chong-Silva (Curitiba/PR)'),
        resolutionNotes: item.resolutionNotes
          ? item.resolutionNotes.replace(
              /— não regional/gi,
              `— Regionalidade: ${item.activeUsedRegionCityUf || 'não informada'}`,
            )
          : null,
      })),
      globalRules: parsed.globalRules,
      harness2State: parsed.harness2State,
      critiquesByStudy: parsed.critiquesByStudy ?? {},
      registeredPis: mergedPis,
      registeredSubs: mergedSubs,
      registeredCoordinators: mergedCoordinators,
      registeredResearchCenters: mergedCenters,
      ...(parsed.clinicalSpecialties ? { clinicalSpecialties: parsed.clinicalSpecialties } : {}),
      ...(parsed.clinicalHealthFunctions
        ? { clinicalHealthFunctions: parsed.clinicalHealthFunctions }
        : {}),
      ...(mergedSuppliers.length > 0
        ? { suppliers: mergedSuppliers }
        : parsed.suppliers
          ? { suppliers: parsed.suppliers }
          : {}),
      ...(parsed.coverageRules ? { coverageRules: parsed.coverageRules } : {}),
      ...(parsed.serviceOrders ? { serviceOrders: parsed.serviceOrders } : {}),
      ...(parsed.purchaseOrders ? { purchaseOrders: parsed.purchaseOrders } : {}),
      ...(parsed.supplierAutoInvoices ? { supplierAutoInvoices: parsed.supplierAutoInvoices } : {}),
      ...(parsed.supplierSubscriptions
        ? { supplierSubscriptions: parsed.supplierSubscriptions }
        : {}),
      studyTeamBindings: parsed.studyTeamBindings ?? {},
      croSponsorGlobalLearnings: parsed.croSponsorGlobalLearnings ?? [],
      ...(parsed.facilityAssets ? { facilityAssets: parsed.facilityAssets } : {}),
      ...(parsed.calibrationSuppliers ? { calibrationSuppliers: parsed.calibrationSuppliers } : {}),
      ...(parsed.calibrationTimeline ? { calibrationTimeline: parsed.calibrationTimeline } : {}),
    };
  } catch {
    return null;
  }
}

export function syncInMemoryStateFromPersistedVault(state: FakeSuppliersAndStudiesState): void {
  const isTestMode = import.meta.env.MODE === 'test';
  const allowInTest =
    typeof window !== 'undefined' &&
    window.localStorage?.getItem('giyus.test.enablePersistedRead') === 'true';
  const simulateProdCenterRecovery =
    typeof window !== 'undefined' &&
    window.localStorage?.getItem('giyus.test.simulateProdCenterRecovery') === 'true';
  if (typeof window === 'undefined' || (isTestMode && !allowInTest)) {
    return;
  }
  const candidates = readAllPersistedVaultCandidates();
  for (const cand of candidates) {
    if (cand.registeredResearchCenters?.length) {
      state.registeredResearchCenters = mergeResearchCentersNonDestructive(
        state.registeredResearchCenters ?? [],
        cand.registeredResearchCenters,
      );
    }
    if (cand.registeredPis?.length) {
      state.registeredPis = mergeByKeyNonDestructive(
        state.registeredPis ?? [],
        cand.registeredPis,
        (x) => x.name,
      );
    }
    if (cand.registeredSubs?.length) {
      state.registeredSubs = mergeByKeyNonDestructive(
        state.registeredSubs ?? [],
        cand.registeredSubs,
        (x) => x.name,
      );
    }
    if (cand.registeredCoordinators?.length) {
      state.registeredCoordinators = mergeByKeyNonDestructive(
        state.registeredCoordinators ?? [],
        cand.registeredCoordinators,
        (x) => x.name,
      );
    }
    if (cand.suppliers?.length) {
      state.suppliers = mergeByKeyNonDestructive(
        state.suppliers ?? [],
        cand.suppliers,
        (x) => x.id || x.registrationNumber || x.legalName,
      );
    }
  }
  if (!isTestMode || simulateProdCenterRecovery) {
    state.registeredResearchCenters = mergeResearchCentersNonDestructive(
      state.registeredResearchCenters ?? [],
      buildRecoveredProductionResearchCenters(),
    );
  }
}

function writePersistedGreenfieldMailboxState(state: FakeSuppliersAndStudiesState): void {
  const allowInTest =
    typeof window !== 'undefined' &&
    window.localStorage?.getItem('giyus.test.enablePersistedRead') === 'true';
  if (typeof window === 'undefined' || (import.meta.env.MODE === 'test' && !allowInTest)) {
    return;
  }
  try {
    // REGRA 21: NUNCA sobrescrever dados cadastrados manualmente pelo usuário (ex.: em outra aba) com array vazio!
    syncInMemoryStateFromPersistedVault(state);

    const serialized = JSON.stringify({
      canonSchemaVersion: CANON_SCHEMA_VERSION,
      messages: state.messages,
      dossiers: state.dossiers,
      rules: state.rules,
      budgetEmailDrafts: state.budgetEmailDrafts,
      threadDoubtResolutions: state.threadDoubtResolutions,
      manualThreadMerges: state.manualThreadMerges,
      studies: state.studies,
      organizations: state.organizations,
      procedures: state.procedures,
      cityPrices: state.cityPrices,
      incomingStudyProcedures: state.incomingStudyProcedures,
      globalRules: state.globalRules,
      harness2State: state.harness2State,
      critiquesByStudy: state.critiquesByStudy,
      registeredPis: state.registeredPis ?? [],
      registeredSubs: state.registeredSubs ?? [],
      registeredCoordinators: state.registeredCoordinators ?? [],
      registeredResearchCenters: state.registeredResearchCenters ?? [],
      clinicalSpecialties: state.clinicalSpecialties ?? [...DEFAULT_CLINICAL_SPECIALTIES_CATALOG],
      clinicalHealthFunctions: state.clinicalHealthFunctions ?? [
        ...DEFAULT_CLINICAL_HEALTH_FUNCTIONS_CATALOG,
      ],
      suppliers: state.suppliers ?? [],
      coverageRules: state.coverageRules ?? [],
      serviceOrders: state.serviceOrders ?? [],
      purchaseOrders: state.purchaseOrders ?? [],
      supplierAutoInvoices: state.supplierAutoInvoices ?? [],
      supplierSubscriptions: state.supplierSubscriptions ?? [],
      studyTeamBindings: state.studyTeamBindings ?? {},
      croSponsorGlobalLearnings: state.croSponsorGlobalLearnings ?? [],
      facilityAssets: state.facilityAssets ?? [],
      calibrationSuppliers: state.calibrationSuppliers ?? [],
      calibrationTimeline: state.calibrationTimeline ?? [],
    });
    window.localStorage.setItem(GREENFIELD_MAILBOX_STORAGE_KEY, serialized);

    // Cofre dedicado de proteção de cadastros manuais do usuário (nunca é limpo)
    window.localStorage.setItem(
      USER_WORK_VAULT_STORAGE_KEY,
      JSON.stringify({
        updatedAtIso: new Date().toISOString(),
        registeredResearchCenters: state.registeredResearchCenters ?? [],
        registeredPis: state.registeredPis ?? [],
        registeredSubs: state.registeredSubs ?? [],
        registeredCoordinators: state.registeredCoordinators ?? [],
        suppliers: state.suppliers ?? [],
        studies: state.studies ?? [],
        organizations: state.organizations ?? [],
        procedures: state.procedures ?? [],
        studyTeamBindings: state.studyTeamBindings ?? {},
        facilityAssets: state.facilityAssets ?? [],
        calibrationSuppliers: state.calibrationSuppliers ?? [],
        calibrationTimeline: state.calibrationTimeline ?? [],
      }),
    );
  } catch {
    // ignore storage quota errors
  }
}

let sharedGreenfieldSuppliersAndStudiesState: FakeSuppliersAndStudiesState | null = null;

export function getSharedGreenfieldSuppliersAndStudiesState(): FakeSuppliersAndStudiesState {
  if (!sharedGreenfieldSuppliersAndStudiesState) {
    sharedGreenfieldSuppliersAndStudiesState = seedSuppliersAndStudiesState(true);
  } else {
    syncInMemoryStateFromPersistedVault(sharedGreenfieldSuppliersAndStudiesState);
  }
  return sharedGreenfieldSuppliersAndStudiesState;
}

export function seedSuppliersAndStudiesState(isGreenfield = false): FakeSuppliersAndStudiesState {
  if (isGreenfield) {
    const nowIso = new Date().toISOString();
    const persisted = readPersistedGreenfieldMailboxState();
    const mergedGlobalRules = mergeCanonicalGlobalRules(persisted?.globalRules);
    const mergedHarness2State = mergeCanonicalHarness2State(
      persisted?.harness2State,
      mergedGlobalRules,
    );
    const greenfieldState: FakeSuppliersAndStudiesState = {
      isGreenfield: true,
      suppliers: persisted?.suppliers ? [...persisted.suppliers] : [],
      coverageRules: persisted?.coverageRules ? [...persisted.coverageRules] : [],
      qualifications: [],
      serviceOrders: persisted?.serviceOrders ? [...persisted.serviceOrders] : [],
      purchaseOrders: persisted?.purchaseOrders ? [...persisted.purchaseOrders] : [],
      studies: persisted?.studies ? [...persisted.studies] : [],
      organizations: persisted?.organizations ? [...persisted.organizations] : [],
      procedures: persisted?.procedures ? [...persisted.procedures] : [],
      dossiers: persisted?.dossiers ? [...persisted.dossiers] : [],
      messages: persisted?.messages ? [...persisted.messages] : [],
      rules: persisted?.rules ? [...persisted.rules] : [],
      budgetEmailDrafts: persisted?.budgetEmailDrafts ? [...persisted.budgetEmailDrafts] : [],
      threadDoubtResolutions: persisted?.threadDoubtResolutions
        ? { ...persisted.threadDoubtResolutions }
        : {},
      manualThreadMerges: persisted?.manualThreadMerges ? { ...persisted.manualThreadMerges } : {},
      incomingStudyProcedures: persisted?.incomingStudyProcedures
        ? [...persisted.incomingStudyProcedures]
        : [],
      holdingPriceTableVersion: 1,
      cityPrices: persisted?.cityPrices ? [...persisted.cityPrices] : [],
      globalRules: mergedGlobalRules,
      studyTableVersions: {},
      studyDismissedAlerts: {},
      critiquesByStudy: persisted?.critiquesByStudy ? { ...persisted.critiquesByStudy } : {},
      paymentsByStudy: {},
      cashflowByStudy: {},
      facilityAssets: persisted?.facilityAssets ? [...persisted.facilityAssets] : [],
      calibrationSuppliers: persisted?.calibrationSuppliers
        ? [...persisted.calibrationSuppliers]
        : [],
      stockForecasts: [],
      calibrationTimeline: persisted?.calibrationTimeline ? [...persisted.calibrationTimeline] : [],
      resourceMailboxEvents: [],
      externalPartnerUsers: [],
      inventoryItems: [],
      quoteComparisons: [],
      financialLedger: [],
      supplierGovernanceDossiers: [],
      supplierTimelineEvents: [],
      inventoryMultiSupplierItems: [],
      consumablePriceTimelineEntries: [],
      procurementPolicy: {
        governancePhase: 'FASE_1_COM_APROVACAO_HUMANA',
        autoApproveExpiringMedications: true,
        autoApproveStudyRequiredItems: true,
        autoApproveEssentialSiteProducts: true,
        autoApprovalCeilingPerOrderBrl: 15000.0,
        requireQualifiedSupplierAndWithinCeiling: true,
        nonEssentialAlwaysRequiresHuman: true,
        lastUpdatedByEmail: 'syllas@giyus.com.br',
        lastUpdatedAtIso: nowIso,
      },
      integratedProcurementOrders: [],
      pickupPolicy: {
        governancePhase: 'FASE_1_HUMANO_APROVA_FORNECEDOR_AGENDA',
        autoApproveBiologicalSamplePickups: true,
        autoApproveCalibrationAndReverseLogisticsPickups: true,
        autoApproveCertifiedDisposalPickups: true,
        autoTriggerSupplierSlotBookingWhenApiAvailable: true,
        enforceMonToWedWindowForBiologicalSamples: true,
        autoApprovalCeilingPerPickupBrl: 10000.0,
        lastUpdatedByEmail: 'syllas@giyus.com.br',
        lastUpdatedAtIso: nowIso,
      },
      supplierPickups: [],
      accountingContractBuckets: [],
      accountingAccumulatedCharges: [],
      accountingIssuedInvoices: [],
      accountingProactiveGaps: [],
      financeReimbursementItems: [],
      registeredPis: persisted?.registeredPis ? [...persisted.registeredPis] : [],
      registeredSubs: persisted?.registeredSubs ? [...persisted.registeredSubs] : [],
      registeredCoordinators: persisted?.registeredCoordinators
        ? [...persisted.registeredCoordinators]
        : [],
      registeredResearchCenters: persisted?.registeredResearchCenters
        ? [...persisted.registeredResearchCenters]
        : [],
      clinicalSpecialties:
        persisted?.clinicalSpecialties && persisted.clinicalSpecialties.length > 0
          ? [...persisted.clinicalSpecialties]
          : [...DEFAULT_CLINICAL_SPECIALTIES_CATALOG],
      clinicalHealthFunctions:
        persisted?.clinicalHealthFunctions && persisted.clinicalHealthFunctions.length > 0
          ? [...persisted.clinicalHealthFunctions]
          : [...DEFAULT_CLINICAL_HEALTH_FUNCTIONS_CATALOG],
      supplierAutoInvoices: persisted?.supplierAutoInvoices
        ? [...persisted.supplierAutoInvoices]
        : [],
      supplierSubscriptions: persisted?.supplierSubscriptions
        ? [...persisted.supplierSubscriptions]
        : [],
      studyTeamBindings: persisted?.studyTeamBindings ? { ...persisted.studyTeamBindings } : {},
      croSponsorGlobalLearnings: persisted?.croSponsorGlobalLearnings
        ? [...persisted.croSponsorGlobalLearnings]
        : [],
      harness2State: mergedHarness2State,
      autonomousRunsCount: 0,
      lastAutonomousRunAtIso: nowIso,
    };
    upgradeAndEnforceCanonV11OnState(greenfieldState);
    if (persisted) {
      writePersistedGreenfieldMailboxState(greenfieldState);
    }
    return greenfieldState;
  }

  return {
    suppliers: [
      {
        id: '0199b900-0000-7000-8000-000000000001',
        code: 'SUP-WC-IATA',
        legalName: 'World Courier do Brasil Transporte Internacional Ltda',
        tradeName: 'World Courier (IATA Cold Chain)',
        country: 'BR',
        registrationNumber: '43.854.116/0001-09',
        categoryCode: 'COURIER_IATA',
        clinicalCategories: ['COURIER_IATA', 'PHARMACY_KITS'],
        mainCnae:
          '5250-8/03 — Agenciamento de cargas, exceto para o transporte marítimo (Cadeia Fria IATA 6.2)',
        secondaryCnaes: [
          '4930-2/02 — Transporte rodoviário de carga biológica refrigerada e ultracongelada (-80°C)',
          '5211-7/99 — Depósitos de mercadorias para terceiros (Armazenagem GMP/GDP)',
        ],
        cnesRecords: [
          {
            cnes: '7419022',
            unitName: 'World Courier Base Operacional Cadeia Fria Brasil',
            sanitaryLicenseTypeAndNumber:
              'Licença Sanitária ANVISA PAF / AFE Transporte Biológico nº 8.09412.7',
            relatedToDescription:
              'Transporte e Armazenagem de Amostras Biológicas Humanas IATA Classe 6.2 e Medicamentos Experimentais',
          },
        ],
        qsaSummary:
          '1. World Courier Group Inc. (Sócio — 99%) | 2. Marcos Aurelio Silva (Administrador)',
        taxIdStatus: 'ATIVA',
        taxRegime: 'Lucro Real',
        companyPorte: 'DEMAIS (Grande Porte)',
        cityUf: 'São Paulo/SP',
        address: 'Av. Ceci, 1900, Planalto Paulista, São Paulo/SP',
        relatedProcedures: [
          'Coleta & Remessa de Amostras PK/PD Seriada (-80°C Gelo Seco)',
          'Suprimento de Kits de Coleta e Caixas Validadas Credo Cube',
        ],
        imagingExamTypes: [],
        isCentralLab: false,
        centralLabServedExams: [],
        autoLinkedStudies: [
          {
            studyCode: 'EPOCA',
            studyTitle: 'Ensaio Clínico Fatorial 2x2 Colchicina e Polipílula (EPOCA)',
            clinicalCategoryCode: 'COURIER_IATA',
            clinicalCategoryLabel: 'Transportadora (Cadeia Fria IATA)',
            matchedScopeSummary: 'Coleta dry-ice (-80°C) de amostras PK e remessa ao Lab Central',
          },
          {
            studyCode: 'SOLAIR',
            studyTitle: 'Estudo Fase II/III Imunoterapia Combinada (SOLAIR)',
            clinicalCategoryCode: 'COURIER_IATA',
            clinicalCategoryLabel: 'Transportadora + Kits de Farmácia',
            matchedScopeSummary: 'Logística IATA 6.2 de biópsia tumoral e kits criogênicos',
          },
          {
            studyCode: 'PERSIST',
            studyTitle: 'Estudo Fase IIIB Asma Grave Eosinofílica (PERSIST)',
            clinicalCategoryCode: 'PHARMACY_KITS',
            clinicalCategoryLabel: 'Kits de Farmácia & Cadeia Fria',
            matchedScopeSummary: 'Entrega refrigerada +2°C a +8°C de kits e biomarcadores',
          },
        ],
        companyContactName: 'Fernanda Albuquerque (Gerente de Contas Clinical Trials Brasil)',
        contactEmail: 'clinical.br@worldcourier.com',
        contactPhone: '+55 11 3529-6000',
        billingFinanceContactName: 'Departamento de Faturamento & Cobrança World Courier',
        billingFinanceEmail: 'faturamento.br@worldcourier.com',
        contactPersons: [
          {
            id: 'cp-wc-1',
            name: 'Fernanda Albuquerque',
            phone: '(11) 98812-6001',
            email: 'fernanda.albuquerque@worldcourier.com',
            responsibilityDescription:
              'Responsável Comercial / Ponto de Contato Principal — Orçamentos, Contratos e SLA de Coletas',
          },
          {
            id: 'cp-wc-2',
            name: 'Rodrigo Mantovani',
            phone: '(11) 97410-3320',
            email: 'operacoes.iata@worldcourier.com',
            responsibilityDescription:
              'Coordenador de Operações Cold Chain — Agendamento de Retirada de Amostras e Reposição de Gelo Seco',
          },
        ],
        coversAllLocations: true,
        servedCitiesStates: [
          'TODOS',
          'Curitiba/PR',
          'São Paulo/SP',
          'Porto Alegre/RS',
          'Rio de Janeiro/RJ',
        ],
        hasMonthlySubscription: true,
        subscriptionProductOrServiceName:
          'Assinatura Mensal de Monitoramento GPS/DataLogger Térmico Cloud 24/7 & Reserva de Caixas Credo Cube (-80°C)',
        subscriptionMonthlyFeeBrl: 2450,
        subscriptionBillingDay: 10,
        lastWeeklySyncAtIso: '2026-10-30T03:00:00Z',
        nextWeeklySyncAtIso: '2026-11-06T03:00:00Z',
        weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
        status: 'Active',
        qualificationStatus: 'Qualified',
        qualificationExpiresAt: '2028-10-25',
        notes:
          'Transporte biológico IATA Classe 6.2 e cadeia fria (-80°C / LN2) para protocolos EPOCA e SOLAIR.',
        coverageRulesCount: 1,
        activeOrdersCount: 1,
      },
      {
        id: '0199b900-0000-7000-8000-000000000002',
        code: 'SUP-FLEURY-IMG',
        legalName: 'Grupo Fleury S.A. — Pesquisa Clínica e Diagnóstico por Imagem',
        tradeName: 'Fleury Medicina e Saúde (Imagem & Patologia)',
        country: 'BR',
        registrationNumber: '60.840.055/0001-31',
        categoryCode: 'IMAGING_LAB',
        clinicalCategories: ['IMAGING_LAB', 'CENTRAL_LAB'],
        mainCnae:
          '8640-2/05 — Serviços de diagnóstico por imagem com uso de radiação ionizante (TC / Raio-X)',
        secondaryCnaes: [
          '8640-2/04 — Serviços de tomografia e ressonância magnética de alta complexidade',
          '8640-2/02 — Laboratórios clínicos e anatomia patológica (Laboratório Central)',
        ],
        cnesRecords: [
          {
            cnes: '2078410',
            unitName:
              'Fleury Diagnósticos por Imagem & Medicina Diagnóstica — Unidade Pesquisa Clínica',
            sanitaryLicenseTypeAndNumber:
              'Alvará Sanitário COVISA/SUVIS nº 2026/009481-2 — Radiodiagnóstico, RM, TC e Ecocardiografia',
            relatedToDescription:
              'Exames de Imagem (TC, RM, Ecocardiograma Strain) e Processamento de Patologia Clínica',
          },
          {
            cnes: '3190442',
            unitName: 'Fleury / Pardini Núcleo Técnico Operacional (NTO Laboratório Central)',
            sanitaryLicenseTypeAndNumber:
              'Licença Sanitária Estadual PALC/CAP nº 2026/884120 — Análises Clínicas Nível III',
            relatedToDescription:
              'Laboratório Central de Segurança, Bioquímica, Hematologia e Biomarcadores',
          },
        ],
        qsaSummary:
          '1. Dra. Jeane Tsutsui (CEO / Diretora Presidente) | 2. Diretoria Médica Pesquisa Clínica',
        taxIdStatus: 'ATIVA',
        taxRegime: 'Lucro Real',
        companyPorte: 'DEMAIS (Companhia Aberta B3)',
        cityUf: 'Curitiba/PR',
        address:
          'Av. Sete de Setembro, 4698, Batel, Curitiba/PR · Av. Gal. Valdomiro de Lima, 508, São Paulo/SP',
        relatedProcedures: [
          'Ecocardiograma Transtorácico com Strain Miocárdico',
          'Tomografia Computadorizada de Tórax / Abdome RECIST 1.1',
          'Ressonância Magnética Cardíaca e Encefálica c/ Contraste',
          'Painel Bioquímico e Hematológico de Triagem (Screening V0/V1)',
        ],
        imagingExamTypes: [
          'Ecocardiograma Transtorácico c/ Strain (2D/3D)',
          'Ressonância Magnética Cardíaca c/ Realce Tardio',
          'Tomografia Computadorizada Tórax/Abdome/Pelve (RECIST 1.1)',
          'Raio-X Digital de Tórax (PA e Perfil)',
          'Ultrassonografia com Doppler Colorido',
        ],
        isCentralLab: true,
        centralLabServedExams: [
          'Hematologia Completa + Contagem Diferencial de Eosinófilos',
          'Bioquímica Clínica (Creatinina, Ureia, TGO/AST, TGP/ALT, Bilirrubinas)',
          'Biomarcadores Cardíacos (NT-proBNP, Troponina Ultrassensível, PCR-us)',
          'Perfil Lipídico Completo (LDL, HDL, Triglicerídeos, ApoB)',
          'Beta-HCG Sérico Quantitativo & Urinálise',
        ],
        autoLinkedStudies: [
          {
            studyCode: 'EPOCA',
            studyTitle: 'Ensaio Clínico Fatorial 2x2 Colchicina e Polipílula (EPOCA)',
            clinicalCategoryCode: 'IMAGING_LAB',
            clinicalCategoryLabel: 'Laboratório de Imagem + Laboratório Central',
            matchedScopeSummary:
              'Ecocardiograma Strain, Biomarcadores Cardíacos (NT-proBNP/PCR-us) e Perfil Lipídico',
          },
          {
            studyCode: 'SOLAIR',
            studyTitle: 'Estudo Fase II/III Imunoterapia Combinada (SOLAIR)',
            clinicalCategoryCode: 'IMAGING_LAB',
            clinicalCategoryLabel: 'Laboratório de Imagem (RECIST 1.1)',
            matchedScopeSummary: 'Tomografia Computadorizada e Ressonância Magnética RECIST 1.1',
          },
          {
            studyCode: 'PERSIST',
            studyTitle: 'Estudo Fase IIIB Asma Grave Eosinofílica (PERSIST)',
            clinicalCategoryCode: 'CENTRAL_LAB',
            clinicalCategoryLabel: 'Laboratório Central',
            matchedScopeSummary: 'Contagem de Eosinófilos Séricos, IgE Total e TCAR de Tórax',
          },
          {
            studyCode: 'ARGON',
            studyTitle: 'Estudo Fase III Doença Inflamatória Sistêmica (ARGON)',
            clinicalCategoryCode: 'IMAGING_LAB',
            clinicalCategoryLabel: 'Laboratório de Imagem + Central',
            matchedScopeSummary: 'Exames de imagem de estadiamento e painel inflamatório',
          },
        ],
        companyContactName: 'Dr. Henrique Vasconcelos (Gerente B2B Pesquisa Clínica)',
        contactEmail: 'pesquisaclinica@grupofleury.com.br',
        contactPhone: '+55 11 3179-0822',
        billingFinanceContactName: 'Célula de Faturamento B2B & Notas Fiscais Grupo Fleury',
        billingFinanceEmail: 'faturamento.pesquisa@grupofleury.com.br',
        contactPersons: [
          {
            id: 'cp-fl-1',
            name: 'Dr. Henrique Vasconcelos',
            phone: '(11) 99104-0822',
            email: 'henrique.vasconcelos@grupofleury.com.br',
            responsibilityDescription:
              'Responsável Comercial Pesquisa Clínica — Orçamentos de Exames de Imagem e Tabelas CBHPM',
          },
          {
            id: 'cp-fl-2',
            name: 'Patrícia Nogueira',
            phone: '(41) 99621-4410',
            email: 'agendamento.pesquisa@grupofleury.com.br',
            responsibilityDescription:
              'Coordenação de Agendamento Prioritário de Pacientes (Curitiba/PR e São Paulo/SP) e Laudos DICOM',
          },
          {
            id: 'cp-fl-3',
            name: 'Carla Mendonça',
            phone: '(11) 98442-1190',
            email: 'faturamento.pesquisa@grupofleury.com.br',
            responsibilityDescription:
              'Responsável pelo Financeiro / Faturamento — Envio de NF-e, Boletos e Conciliação por Estudo',
          },
        ],
        coversAllLocations: false,
        servedCitiesStates: [
          'Curitiba/PR',
          'São Paulo/SP',
          'Rio de Janeiro/RJ',
          'Belo Horizonte/MG',
          'Porto Alegre/RS',
        ],
        hasMonthlySubscription: true,
        subscriptionProductOrServiceName:
          'Portal PACS/DICOM Cloud Anonimizado p/ Monitores CRA & Armazenamento de Imagens de Estudos Clínicos',
        subscriptionMonthlyFeeBrl: 1690,
        subscriptionBillingDay: 5,
        lastWeeklySyncAtIso: '2026-10-30T03:00:00Z',
        nextWeeklySyncAtIso: '2026-11-06T03:00:00Z',
        weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
        status: 'Active',
        qualificationStatus: 'Qualified',
        qualificationExpiresAt: '2028-04-25',
        notes:
          'Ecocardiografia Strain, RM cardíaca e TC RECIST 1.1 para estudos EPOCA, SOLAIR e ARGON.',
        coverageRulesCount: 2,
        activeOrdersCount: 1,
      },
      {
        id: '0199b900-0000-7000-8000-000000000003',
        code: 'SUP-LABCORP-CL',
        legalName: 'Labcorp Drug Development Brasil Serviços Laboratoriais Ltda',
        tradeName: 'Labcorp Central Laboratory',
        country: 'BR',
        registrationNumber: '04.921.540/0001-88',
        categoryCode: 'CENTRAL_LAB',
        clinicalCategories: ['CENTRAL_LAB', 'PHARMACY_KITS', 'CALIBRATION'],
        mainCnae:
          '8640-2/02 — Laboratórios clínicos e processamento central de amostras de pesquisa clínica',
        secondaryCnaes: [
          '7210-0/00 — Pesquisa e desenvolvimento experimental em ciências físicas e naturais',
          '4645-1/03 — Comércio atacadista de produtos para laboratório e kits diagnósticos',
        ],
        cnesRecords: [
          {
            cnes: '6184920',
            unitName: 'Labcorp Central Laboratory Latin America Hub',
            sanitaryLicenseTypeAndNumber:
              'Licença Sanitária CAP/CLIA & ANVISA nº 2026/551902 — Laboratório Central Internacional',
            relatedToDescription:
              'Análises de Farmacocinética (PK/PD), Genotipagem, Imunogenicidade e Fornecimento de Kits de Coleta',
          },
        ],
        qsaSummary: '1. Laboratory Corporation of America Holdings (Sócia) | 2. Diretoria LatAm',
        taxIdStatus: 'ATIVA',
        taxRegime: 'Lucro Real',
        companyPorte: 'DEMAIS (Multinacional)',
        cityUf: 'São Paulo/SP',
        address: 'Av. das Nações Unidas, 14171, Vila Gertrudes, São Paulo/SP',
        relatedProcedures: [
          'Processamento Central de Farmacocinética Seriada (PK/PD)',
          'Fornecimento de Kits de Coleta por Visita (V0 a V12) & Criotubos',
          'Calibração & Qualificação de Centrífugas e Termohigrômetros em Comodato',
        ],
        imagingExamTypes: [],
        isCentralLab: true,
        centralLabServedExams: [
          'Farmacocinética Seriada (PK/PD) e Dosagem Sérica de Fármaco em Estudo',
          'Anticorpos Anti-Fármaco (ADA / Imunogenicidade)',
          'Sequenciamento Genômico / Biomarcadores Tumorais (NGS / PD-L1)',
          'Citometria de Fluxo & Subpopulações Linfocitárias',
          'Painel Centralizado de Segurança Hepática e Renal (CAP/CLIA)',
        ],
        autoLinkedStudies: [
          {
            studyCode: 'EPOCA',
            studyTitle: 'Ensaio Clínico Fatorial 2x2 Colchicina e Polipílula (EPOCA)',
            clinicalCategoryCode: 'PHARMACY_KITS',
            clinicalCategoryLabel: 'Laboratório Central + Kits de Farmácia',
            matchedScopeSummary:
              'Fornecimento de kits criogênicos e tubos PK (Pedido PC-2026-0101)',
          },
          {
            studyCode: 'SOLAIR',
            studyTitle: 'Estudo Fase II/III Imunoterapia Combinada (SOLAIR)',
            clinicalCategoryCode: 'CENTRAL_LAB',
            clinicalCategoryLabel: 'Laboratório Central (Biomarcadores & PK)',
            matchedScopeSummary: 'Análise central de PD-L1, PK seriada e kits de coleta',
          },
          {
            studyCode: 'ADEPT-4',
            studyTitle: 'Estudo Fase III Imunodermatologia (ADEPT-4)',
            clinicalCategoryCode: 'CENTRAL_LAB',
            clinicalCategoryLabel: 'Laboratório Central + Calibração Comodato',
            matchedScopeSummary: 'Exames de imunogenicidade ADA e kits dermatológicos',
          },
        ],
        companyContactName: 'Dra. Mariana Albuquerque (Site Support Lead LatAm)',
        contactEmail: 'studysupport.latam@labcorp.com',
        contactPhone: '+55 11 2189-8000',
        billingFinanceContactName: 'Accounts Payable / Faturamento Labcorp Brasil',
        billingFinanceEmail: 'billing.brazil@labcorp.com',
        contactPersons: [
          {
            id: 'cp-lc-1',
            name: 'Dra. Mariana Albuquerque',
            phone: '(11) 98119-8000',
            email: 'mariana.albuquerque@labcorp.com',
            responsibilityDescription:
              'Ponto de Contato Principal — Suporte aos Centros, Kits de Visita e Discrepâncias de Amostras',
          },
          {
            id: 'cp-lc-2',
            name: 'Eduardo Fontes',
            phone: '(11) 97220-4100',
            email: 'billing.brazil@labcorp.com',
            responsibilityDescription:
              'Financeiro & Faturamento — Emissão de Notas Fiscais, Reembolso de Kits e Conciliação Mensal',
          },
        ],
        coversAllLocations: true,
        servedCitiesStates: ['TODOS', 'Curitiba/PR', 'São Paulo/SP'],
        hasMonthlySubscription: false,
        subscriptionProductOrServiceName: '',
        subscriptionMonthlyFeeBrl: 0,
        subscriptionBillingDay: 15,
        lastWeeklySyncAtIso: '2026-10-30T03:00:00Z',
        nextWeeklySyncAtIso: '2026-11-06T03:00:00Z',
        weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
        status: 'Active',
        qualificationStatus: 'Qualified',
        qualificationExpiresAt: '2028-10-25',
        notes: 'Laboratório central e fornecimento de kits de coleta para protocolos patrocinados.',
        coverageRulesCount: 1,
        activeOrdersCount: 1,
      },
    ],
    coverageRules: [
      {
        id: '0199b901-0000-7000-8000-000000000001',
        supplierId: '0199b900-0000-7000-8000-000000000001',
        supplierName: 'World Courier (IATA Cold Chain)',
        kind: 'Network',
        effect: 'Include',
        stateCode: null,
        ibgeMunicipalityCode: null,
        centerLatitude: null,
        centerLongitude: null,
        radiusKm: null,
        categoryCode: 'COURIER_IATA',
        effectiveFrom: '2026-07-01',
        effectiveTo: null,
        notes: 'Cobertura nacional em todos os centros da rede GIYUS.',
      },
      {
        id: '0199b901-0000-7000-8000-000000000002',
        supplierId: '0199b900-0000-7000-8000-000000000002',
        supplierName: 'Fleury Medicina e Saúde (Imagem & Patologia)',
        kind: 'Radius',
        effect: 'Include',
        stateCode: 'SP',
        ibgeMunicipalityCode: '3550308',
        centerLatitude: -23.5614,
        centerLongitude: -46.6559,
        radiusKm: 45,
        categoryCode: 'IMAGING_LAB',
        effectiveFrom: '2026-08-01',
        effectiveTo: null,
        notes: 'Raio geodésico de 45 km a partir da Av. Paulista (Haversine).',
      },
    ],
    qualifications: [
      {
        id: '0199b902-0000-7000-8000-000000000001',
        supplierId: '0199b900-0000-7000-8000-000000000001',
        supplierName: 'World Courier (IATA Cold Chain)',
        status: 'Qualified',
        assessedByName: 'Dra. Camila Rocha (Especialista QA)',
        assessedAtIso: '2026-09-10T14:00:00Z',
        approvedByName: 'Dr. Ricardo Vasconcelos (Quality Lead)',
        approvedAtIso: '2026-09-12T10:30:00Z',
        validUntil: '2028-10-25',
        evidenceSummary:
          'Auditoria BPC/IATA aprovada com certificação de cadeia fria validada e segregação SoD.',
      },
    ],
    serviceOrders: [
      {
        id: '0199b903-0000-7000-8000-000000000001',
        code: 'OS-2026-001',
        supplierId: '0199b900-0000-7000-8000-000000000001',
        supplierName: 'World Courier (IATA Cold Chain)',
        categoryCode: 'COURIER_IATA',
        studyCode: 'EPOCA',
        description: 'Coleta dry-ice (-80°C) de amostras PK Visita V4 — Protocolo EPOCA.',
        requestedAmountBrl: 4850,
        scheduledDate: '2026-10-28',
        status: 'Confirmed',
        isRecurringItem: true,
        lastHistoricalUnitPriceBrl: 4850,
        currentUnitPriceBrl: 4850,
        priceComparisonStatus: 'SAME_PRICE_AUTO_APPROVED_ORDER',
        autoGeneratedDraft: true,
        sharedMailboxDraftSubject:
          '[PEDIDO APROVADO — PREÇO RECORRENTE MANTIDO] OS-2026-001 · Coleta dry-ice (-80°C) PK V4 (EPOCA) — World Courier',
        sharedMailboxDraftTo: 'clinical.br@worldcourier.com; fernanda.albuquerque@worldcourier.com',
        sharedMailboxDraftCc:
          'faturamento.br@worldcourier.com; contratos@giyus.com.br; financeiro@giyus.com.br',
        sharedMailboxDraftBody:
          'Prezada Fernanda Albuquerque (World Courier),\n\nPor se tratar de serviço recorrente com o mesmo preço praticado anteriormente (R$ 4.850,00), formalizamos o pedido da Ordem de Serviço OS-2026-001 já com APROVAÇÃO IMEDIATA para execução em 28/10/2026 (Protocolo EPOCA).\n\nFavor enviar a confirmação de agendamento e a Nota Fiscal para financeiro@giyus.com.br.\n\nAtenciosamente,\nCentral de Suprimentos & Operações Clínicas GIYUS',
        sharedMailboxDraftStatus: 'READY_FOR_1CLICK_SEND',
        sharedMailboxDraftSentAtIso: null,
      },
      {
        id: '0199b903-0000-7000-8000-000000000002',
        code: 'OS-2026-002',
        supplierId: '0199b900-0000-7000-8000-000000000002',
        supplierName: 'Fleury Medicina e Saúde (Imagem & Patologia)',
        categoryCode: 'IMAGING_LAB',
        studyCode: 'EPOCA',
        description:
          'Ecocardiograma Transtorácico com Strain Miocárdico (Lote 5 Pacientes — EPOCA).',
        requestedAmountBrl: 5250,
        scheduledDate: '2026-11-04',
        status: 'PendingNegotiationAndApproval',
        isRecurringItem: true,
        lastHistoricalUnitPriceBrl: 4900,
        currentUnitPriceBrl: 5250,
        priceComparisonStatus: 'PRICE_CHANGED_REQUIRES_NEGOTIATION',
        autoGeneratedDraft: true,
        sharedMailboxDraftSubject:
          '[NEGOCIAÇÃO DE ORÇAMENTO — REAJUSTE DETECTADO] OS-2026-002 · Ecocardiograma Strain (EPOCA) — Fleury Medicina e Saúde',
        sharedMailboxDraftTo:
          'pesquisaclinica@grupofleury.com.br; henrique.vasconcelos@grupofleury.com.br',
        sharedMailboxDraftCc:
          'faturamento.pesquisa@grupofleury.com.br; contratos@giyus.com.br; financeiro@giyus.com.br',
        sharedMailboxDraftBody:
          'Prezado Dr. Henrique Vasconcelos (Fleury Medicina e Saúde),\n\nSolicitamos orçamento e negociação comercial referente à Ordem de Serviço OS-2026-002 (Ecocardiograma Transtorácico com Strain — Estudo EPOCA).\n\nIdentificamos variação de preço em relação ao último pedido recorrente (de R$ 4.900,00 para R$ 5.250,00). Caso mantenham o valor histórico de R$ 4.900,00, o pedido segue aprovado imediatamente; havendo manutenção do reajuste, submeteremos à alçada de negociação e aprovação.\n\nAtenciosamente,\nCentral de Suprimentos & Contratos GIYUS',
        sharedMailboxDraftStatus: 'READY_FOR_1CLICK_SEND',
        sharedMailboxDraftSentAtIso: null,
      },
    ],
    purchaseOrders: [
      {
        id: '0199b904-0000-7000-8000-000000000001',
        code: 'PC-2026-0101',
        title: 'Aquisição de Kits Criogênicos e Tubos PK — Estudo EPOCA',
        categoryCode: 'PHARMACY_KITS',
        supplierId: '0199b900-0000-7000-8000-000000000003',
        supplierName: 'Labcorp Central Laboratory',
        studyCode: 'EPOCA',
        status: 'Approved',
        totalAmountBrl: 18450,
        quotesCount: 3,
        justification:
          'Reposição de estoque de segurança para janela de randomização do protocolo EPOCA (Pass-Through).',
        requestedByName: 'Beatriz M. (Coordenação de Estudos)',
        approvedByName: 'Dr. Ricardo Vasconcelos (Diretoria Operacional)',
        itemsJson: '[]',
        createdAtIso: '2026-10-24T11:00:00Z',
        isRecurringItem: true,
        lastHistoricalUnitPriceBrl: 18450,
        currentUnitPriceBrl: 18450,
        priceComparisonStatus: 'SAME_PRICE_AUTO_APPROVED_ORDER',
        autoGeneratedDraft: true,
        sharedMailboxDraftSubject:
          '[ORDEM DE COMPRA APROVADA — PREÇO RECORRENTE MANTIDO] PC-2026-0101 · Kits Criogênicos e Tubos PK (EPOCA) — Labcorp',
        sharedMailboxDraftTo: 'studysupport.latam@labcorp.com; mariana.albuquerque@labcorp.com',
        sharedMailboxDraftCc:
          'billing.brazil@labcorp.com; contratos@giyus.com.br; financeiro@giyus.com.br',
        sharedMailboxDraftBody:
          'Prezada Dra. Mariana Albuquerque (Labcorp Central Laboratory),\n\nConsiderando tratar-se de item recorrente com manutenção integral do preço unitário acordado (R$ 18.450,00), encaminhamos a Ordem de Compra PC-2026-0101 JÁ APROVADA para faturamento e despacho imediato ao centro do estudo EPOCA.\n\nFavor enviar a NF-e e XML para financeiro@giyus.com.br.\n\nAtenciosamente,\nSuprimentos & Compras GIYUS',
        sharedMailboxDraftStatus: 'READY_FOR_1CLICK_SEND',
        sharedMailboxDraftSentAtIso: null,
      },
      {
        id: '0199b904-0000-7000-8000-000000000002',
        code: 'PC-2026-0102',
        title: 'Pacote de Exames de Imagem Baseline (TC + RM) — Estudo SOLAIR',
        categoryCode: 'IMAGING_LAB',
        supplierId: '0199b900-0000-7000-8000-000000000002',
        supplierName: 'Fleury Medicina e Saúde (Imagem & Patologia)',
        studyCode: 'SOLAIR',
        status: 'PendingApproval',
        totalAmountBrl: 34200,
        quotesCount: 3,
        justification:
          'Contratação de pacote fechado de 12 exames de imagem RECIST 1.1 conforme orçamento SOLAIR.',
        requestedByName: 'Carlos Eduardo (Suprimentos & Compras)',
        approvedByName: null,
        itemsJson: '[]',
        createdAtIso: '2026-10-25T09:30:00Z',
        isRecurringItem: true,
        lastHistoricalUnitPriceBrl: 31800,
        currentUnitPriceBrl: 34200,
        priceComparisonStatus: 'PRICE_CHANGED_REQUIRES_NEGOTIATION',
        autoGeneratedDraft: true,
        sharedMailboxDraftSubject:
          '[SOLICITAÇÃO DE ORÇAMENTO & NEGOCIAÇÃO DE PREÇO] PC-2026-0102 · Pacote TC + RM RECIST 1.1 (SOLAIR) — Fleury',
        sharedMailboxDraftTo:
          'pesquisaclinica@grupofleury.com.br; henrique.vasconcelos@grupofleury.com.br',
        sharedMailboxDraftCc:
          'faturamento.pesquisa@grupofleury.com.br; contratos@giyus.com.br; financeiro@giyus.com.br',
        sharedMailboxDraftBody:
          'Prezado Dr. Henrique Vasconcelos (Fleury Medicina e Saúde),\n\nReferente à Ordem de Compra PC-2026-0102 (Pacote 12 Exames TC + RM RECIST 1.1 — Estudo SOLAIR), observamos diferença de preço em relação ao último lote contratado (de R$ 31.800,00 para R$ 34.200,00).\n\nSolicitamos proposta revisada mantendo a condição histórica (R$ 31.800,00) para aprovação imediata do pedido, ou detalhamento técnico para submissão ao comitê de negociação e aprovação.\n\nAtenciosamente,\nSuprimentos & Compras GIYUS',
        sharedMailboxDraftStatus: 'READY_FOR_1CLICK_SEND',
        sharedMailboxDraftSentAtIso: null,
      },
    ],
    studies: [
      {
        id: '0199ba00-0000-7000-8000-000000000001',
        code: 'EPOCA',
        protocolCode: 'MINUTA-EPOCA-2026-TRIALS',
        title:
          'Ensaio clínico randomizado fatorial 2 x 2 para avaliar estratégia com colchicina e polipílula em pacientes de muito alto risco cardiovascular (EPOCA — Exclusivo Trials Hub Ltda)',
        phase: 'Phase III',
        therapeuticArea: 'Cardiologia & Clínica Médica',
        sponsorName: 'ASSOCIAÇÃO BENEFICENTE SÍRIA — Hcor',
        croName:
          'TRIALS HUB LTDA (Exclusivo na Minuta Local · CNPJ 66.125.390/0001-34 · PI Dr. Marco Stephan — Sem outros centros e sem GIYUS)',
        status: 'Enrolling',
        targetEnrollment: 12,
        currentEnrollment: 12,
      },
      {
        id: '0199ba00-0000-7000-8000-000000000002',
        code: 'SOLAIR',
        protocolCode: 'ONC-SOLAIR-204',
        title: 'Estudo Fase II/III de Imunoterapia Combinada em Carcinoma Pulmonar (SOLAIR)',
        phase: 'Phase II/III',
        therapeuticArea: 'Oncologia Torácica',
        sponsorName: 'Novartis Biociências S.A.',
        croName: 'ICON Clinical Research Brasil',
        status: 'Startup',
        targetEnrollment: 30,
        currentEnrollment: 6,
      },
      {
        id: '0199ba00-0000-7000-8000-000000000003',
        code: 'ARGON',
        protocolCode: 'IMM-ARGON-108',
        title: 'Estudo Fase III Randomizado em Doença Inflamatória Sistêmica Refratária (ARGON)',
        phase: 'Phase III',
        therapeuticArea: 'Imunologia Clínica',
        sponsorName: 'AstraZeneca Global R&D',
        croName: 'Syneos Health Brasil',
        status: 'Feasibility',
        targetEnrollment: 25,
        currentEnrollment: 0,
      },
      {
        id: '0199ba00-0000-7000-8000-000000000004',
        code: 'PERSIST',
        protocolCode: 'GSK-PERSIST-ASTHMA-1',
        title: 'Estudo Fase IIIB de Controle de Asma Grave Eosinofílica e Biomarcadores (PERSIST)',
        phase: 'Phase IIIB',
        therapeuticArea: 'Alergia, Imunologia & Pneumologia',
        sponsorName: 'GlaxoSmithKline (GSK)',
        croName: 'IQVIA RDS Brasil Ltda.',
        status: 'Enrolling',
        targetEnrollment: 20,
        currentEnrollment: 9,
      },
      {
        id: '0199ba00-0000-7000-8000-000000000005',
        code: 'ADEPT-4',
        protocolCode: 'BMS-CN0120056-ADEPT-4',
        title:
          'Estudo Fase III Multicêntrico Duplo-Cego em Imunodermatologia Pediátrica e Adulta (ADEPT-4)',
        phase: 'Phase III',
        therapeuticArea: 'Alergia & Imunodermatologia',
        sponsorName: 'Bristol Myers Squibb (BMS)',
        croName: 'PPD / Thermo Fisher Scientific',
        status: 'Startup',
        targetEnrollment: 15,
        currentEnrollment: 4,
      },
    ],
    organizations: [
      {
        id: '0199ba01-0000-7000-8000-000000000001',
        code: 'ORG-ASTRA',
        name: 'AstraZeneca Global R&D',
        kind: 'Sponsor',
        country: 'GB',
        contactEmail: 'clinicaltrials.br@astrazeneca.com',
        status: 'Active',
      },
      {
        id: '0199ba01-0000-7000-8000-000000000002',
        code: 'ORG-IQVIA',
        name: 'IQVIA RDS Brasil Ltda.',
        kind: 'Cro',
        country: 'US',
        contactEmail: 'amanda.contracts@iqvia.com',
        status: 'Active',
      },
      {
        id: '0199ba01-0000-7000-8000-000000000003',
        code: 'ORG-HCOR',
        name: 'Associação Beneficente Síria — Hcor (PROADI-SUS)',
        kind: 'Sponsor',
        country: 'BR',
        contactEmail: 'gabriela.batista@hcor.com.br',
        status: 'Active',
      },
      {
        id: '0199ba01-0000-7000-8000-000000000004',
        code: 'ORG-PAREXEL',
        name: 'Parexel International Brasil',
        kind: 'Cro',
        country: 'US',
        contactEmail: 'Patricia.Stein@parexel.com',
        status: 'Active',
      },
      {
        id: '0199ba01-0000-7000-8000-000000000005',
        code: 'ORG-PPD',
        name: 'PPD / Thermo Fisher Scientific',
        kind: 'Cro',
        country: 'US',
        contactEmail: 'Valeria.Santos2@thermofisher.com',
        status: 'Active',
      },
      {
        id: '0199ba01-0000-7000-8000-000000000006',
        code: 'ORG-GSK',
        name: 'GlaxoSmithKline Brasil Ltda. (GSK)',
        kind: 'Sponsor',
        country: 'GB',
        contactEmail: 'clinical.br@gsk.com',
        status: 'Active',
      },
      {
        id: '0199ba01-0000-7000-8000-000000000007',
        code: 'ORG-BMS',
        name: 'Bristol Myers Squibb Farmacêutica (BMS)',
        kind: 'Sponsor',
        country: 'US',
        contactEmail: 'clinical.trials@bms.com',
        status: 'Active',
      },
    ],
    procedures: [
      {
        id: '0199ba02-0000-7000-8000-000000000001',
        tenantId: null,
        code: 'PROC-ICF-01',
        namePtBr: 'Aplicação de TCLE e Registro de Consentimento (BPC)',
        nameEnUs: 'Informed Consent Process & Documentation (GCP)',
        category: 'CONSENT',
        defaultDurationMinutes: 60,
        baseUnitCostBrl: 480,
        currentVersion: 2,
        status: 'Published',
        regionCityUf: 'Curitiba/PR',
        attendingCenterName: 'Centros de Pesquisa Atendentes em Curitiba/PR',
      },
      {
        id: '0199ba02-0000-7000-8000-000000000002',
        tenantId: null,
        code: 'PROC-PK-SERIAL',
        namePtBr: 'Coleta Farmacocinética Seriada (8h) + Processamento Criogênico -80°C',
        nameEnUs: 'Serial Pharmacokinetic Sampling & Cryo-Processing',
        category: 'LAB_PK',
        defaultDurationMinutes: 240,
        baseUnitCostBrl: 1890,
        currentVersion: 2,
        status: 'Published',
        regionCityUf: 'Curitiba/PR',
        attendingCenterName: 'Centros de Pesquisa Atendentes em Curitiba/PR',
      },
      {
        id: '0199ba02-0000-7000-8000-000000000003',
        tenantId: null,
        code: 'PROC-SPIRO-FENO',
        namePtBr:
          'Espirometria Completa c/ Prova Broncodilatadora + Fração Exalada de Óxido Nítrico (FeNO)',
        nameEnUs: 'Full Spirometry with Reversibility + FeNO Measurement',
        category: 'PULMONOLOGY',
        defaultDurationMinutes: 75,
        baseUnitCostBrl: 790,
        currentVersion: 2,
        status: 'Published',
        regionCityUf: 'Curitiba/PR',
        attendingCenterName: 'Site 0701 — Prof. Dr. Nelson Rosário / GIYUS Curitiba (Curitiba/PR)',
      },
      {
        id: '0199ba02-0000-7000-8000-000000000004',
        tenantId: null,
        code: 'PROC-ECHO-STRAIN',
        namePtBr: 'Ecocardiograma Transtorácico Bidimensional c/ Doppler e Strain Miocárdico',
        nameEnUs: '2D Transthoracic Echocardiogram with Global Longitudinal Strain',
        category: 'IMAGING',
        defaultDurationMinutes: 60,
        baseUnitCostBrl: 980,
        currentVersion: 1,
        status: 'Published',
        regionCityUf: 'Curitiba/PR',
        attendingCenterName: 'Centros de Pesquisa Atendentes em Curitiba/PR',
      },
      {
        id: '0199ba02-0000-7000-8000-000000000005',
        tenantId: null,
        code: 'PROC-SUBQ-BIO',
        namePtBr:
          'Preparo Farmacêutico, Dispensação IWRS e Administração de Imunobiológico SC c/ Observação 2h',
        nameEnUs: 'IWRS Dispensing & Subcutaneous Biologic Administration with 2h Observation',
        category: 'PHARMACY_CLINICAL',
        defaultDurationMinutes: 150,
        baseUnitCostBrl: 920,
        currentVersion: 1,
        status: 'Published',
        regionCityUf: 'Curitiba/PR',
        attendingCenterName: 'Site 297000 — HC-UFPR / Dr. Chong-Silva (Curitiba/PR)',
      },
    ],
    dossiers: [
      {
        id: '0199ba03-0000-7000-8000-000000000001',
        code: 'EPOCA',
        studyCode: 'EPOCA',
        title:
          'Dossiê Contratual & Orçamento 360º — Minuta Local EPOCA (MINUTA-EPOCA-2026-TRIALS · Exclusivo Trials Hub Ltda)',
        sponsorName: 'ASSOCIAÇÃO BENEFICENTE SÍRIA — Hcor',
        croName:
          'TRIALS HUB LTDA (CNPJ 66.125.390/0001-34 · PI Dr. Marco Stephan Lofrano Alves — Sem outros centros e sem GIYUS)',
        currency: 'BRL',
        exchangeRateBrl: 1,
        taxRegime: 'LucroReal',
        taxRatePercent: 14.25,
        overheadPercent: 30,
        totalBaselineBrl: 384500,
        totalNegotiatedBrl: 462800,
        passThroughTotalBrl: 74200,
        estimatedMarginPercent: 29.4,
        status: 'ApprovedForExecution',
        riskScore: 18,
        clinicalAnalysisJson:
          '{"complexity":"Alta (Coleta PK seriada 8h + Ecocardiograma Strain)"}',
        budgetGridJson:
          '[{"visit":"V1 - Screening","procedures":"TCLE + Avaliação PI + ECG Triplicado","baselineBrl":2450,"negotiatedBrl":3180,"taxGrossUpBrl":3708.45,"marginPercent":28.5},{"visit":"V2 - Randomização","procedures":"Dispensação IMP + PK Seriada 8h","baselineBrl":3890,"negotiatedBrl":4920,"taxGrossUpBrl":5737.54,"marginPercent":31.2}]',
        contractClausesJson:
          '[{"clause":"Cláusula 9.2 — Reajuste Inflacionário Anual (IPCA)","status":"Aprovado","riskLevel":"Baixo","analysis":"Recomposição anual automática pelo IPCA/IBGE."}]',
        miroFishSimulationJson:
          '{"scenariosRun":1000,"recommendedCounterOfferBrl":462800,"acceptanceProbabilityPercent":88}',
        jevScoreJson:
          '{"model":"JEV-SystemOne-v1","decision":"APPROVE_WITH_GROSSUP","confidence":0.94}',
        linkedEmailsCount: 1,
      },
      {
        id: '0199ba03-0000-7000-8000-000000000002',
        code: 'SOLAIR',
        studyCode: 'SOLAIR',
        title: 'Dossiê Contratual & Orçamento 360º — Protocolo SOLAIR (ONC-SOLAIR-204)',
        sponsorName: 'Novartis Biociências S.A.',
        croName: 'ICON Clinical Research Brasil',
        currency: 'USD',
        exchangeRateBrl: 5.15,
        taxRegime: 'LucroReal',
        taxRatePercent: 14.25,
        overheadPercent: 32,
        totalBaselineBrl: 512000,
        totalNegotiatedBrl: 618400,
        passThroughTotalBrl: 112500,
        estimatedMarginPercent: 27.8,
        status: 'InNegotiation',
        riskScore: 34,
        clinicalAnalysisJson: '{"complexity":"Muito Alta (Infusão Oncológica + TC RECIST 1.1)"}',
        budgetGridJson:
          '[{"visit":"Screening Oncologia","procedures":"TCLE + Biópsia + TC RECIST 1.1","baselineBrl":4850,"negotiatedBrl":6120,"taxGrossUpBrl":7137.03,"marginPercent":27.4}]',
        contractClausesJson:
          '[{"clause":"Cláusula 6.4 — Proteção Cambial USD/BRL","status":"Em Negociação","riskLevel":"Alto","analysis":"Proposta banda cambial de 5% sobre PTAX."}]',
        miroFishSimulationJson:
          '{"scenariosRun":1000,"recommendedCounterOfferBrl":618400,"acceptanceProbabilityPercent":79}',
        jevScoreJson:
          '{"model":"JEV-SystemOne-v1","decision":"NEGOTIATE_FX_CLAUSE","confidence":0.89}',
        linkedEmailsCount: 1,
      },
      {
        id: '0199ba03-0000-7000-8000-000000000003',
        code: 'ARGON',
        studyCode: 'ARGON',
        title: 'Dossiê Contratual & Orçamento 360º — Protocolo ARGON (IMM-ARGON-108)',
        sponsorName: 'AstraZeneca Global R&D',
        croName: 'Syneos Health Brasil',
        currency: 'BRL',
        exchangeRateBrl: 1,
        taxRegime: 'LucroReal',
        taxRatePercent: 14.25,
        overheadPercent: 28,
        totalBaselineBrl: 265000,
        totalNegotiatedBrl: 319500,
        passThroughTotalBrl: 48900,
        estimatedMarginPercent: 26.5,
        status: 'UnderAnalysis',
        riskScore: 22,
        clinicalAnalysisJson: '{"complexity":"Moderada/Alta (Imunobiológico SC)"}',
        budgetGridJson:
          '[{"visit":"V1 - Triagem Imunológica","procedures":"TCLE + Quantiferon-TB + RX Tórax","baselineBrl":2120,"negotiatedBrl":2680,"taxGrossUpBrl":3125.36,"marginPercent":26.1}]',
        contractClausesJson:
          '[{"clause":"Cláusula 4.1 — Start-up Fee Não Reembolsável","status":"Em Análise","riskLevel":"Baixo","analysis":"100% na assinatura do CTA."}]',
        miroFishSimulationJson:
          '{"scenariosRun":1000,"recommendedCounterOfferBrl":319500,"acceptanceProbabilityPercent":91}',
        jevScoreJson:
          '{"model":"JEV-SystemOne-v1","decision":"READY_FOR_COUNTEROFFER","confidence":0.92}',
        linkedEmailsCount: 1,
      },
      {
        id: '0199ba03-0000-7000-8000-000000000004',
        code: 'PERSIST',
        studyCode: 'GSK-PERSIST-ASTHMA-1',
        title: 'Dossiê Contratual & Orçamento 360º — Estudo PERSIST (GSK / IQVIA · Asma Grave)',
        sponsorName: 'GlaxoSmithKline (GSK)',
        croName: 'IQVIA RDS Brasil Ltda.',
        currency: 'BRL',
        exchangeRateBrl: 1,
        taxRegime: 'LucroReal',
        taxRatePercent: 14.25,
        overheadPercent: 30,
        totalBaselineBrl: 298000,
        totalNegotiatedBrl: 356400,
        passThroughTotalBrl: 52000,
        estimatedMarginPercent: 28.9,
        status: 'InNegotiation',
        riskScore: 26,
        clinicalAnalysisJson:
          '{"complexity":"Alta (Espirometria Seriada + FeNO + Imunobiológico SC)","capTarget":3}',
        budgetGridJson:
          '[{"visit":"V1 - Screening Asma","procedures":"TCLE + Espirometria c/ BD + FeNO + Coleta Eosinófilos","baselineBrl":2150,"negotiatedBrl":2790,"taxGrossUpBrl":3253.64,"marginPercent":28.4},{"visit":"V2 - Randomização","procedures":"Administração Imunobiológico SC + Observação 2h","baselineBrl":2680,"negotiatedBrl":3420,"taxGrossUpBrl":3988.34,"marginPercent":29.2}]',
        contractClausesJson:
          '[{"clause":"Cláusula EQ-BCB-01 — Defesa de Equilíbrio Econômico-Financeiro (Selic 14,25% a.a. SGS 432 / Giro PJ 27,64% a.a. SGS 20722)","status":"Aprovado","riskLevel":"Baixo","analysis":"Proteção contra defasagem de pagamento superior a 60 dias com correção BCB."},{"clause":"Cláusula SPEC-SPIRO-02 — Fornecimento de Insumos e Bocais FeNO","status":"Em Negociação","riskLevel":"Médio","analysis":"Reembolso integral Pass-Through de kits FeNO NIOX VERO."}]',
        miroFishSimulationJson:
          '{"scenariosRun":1000,"recommendedCounterOfferBrl":356400,"acceptanceProbabilityPercent":87,"stretchTheRopeEnabled":true}',
        jevScoreJson:
          '{"model":"JEV-SystemOne-v1","decision":"STRETCH_THE_ROPE_COUNTEROFFER","capAlert":"Meta Cap <= 3 pacientes (exige Screen Fail ratio 2:1)","confidence":0.93}',
        linkedEmailsCount: 2,
      },
      {
        id: '0199ba03-0000-7000-8000-000000000005',
        code: 'ADEPT-4',
        studyCode: 'BMS-CN0120056-ADEPT-4',
        title: 'Dossiê Contratual & Orçamento 360º — Protocolo ADEPT-4 (BMS / PPD Thermo Fisher)',
        sponsorName: 'Bristol Myers Squibb (BMS)',
        croName: 'PPD / Thermo Fisher Scientific',
        currency: 'USD',
        exchangeRateBrl: 5.72,
        taxRegime: 'LucroReal',
        taxRatePercent: 14.25,
        overheadPercent: 30,
        totalBaselineBrl: 412000,
        totalNegotiatedBrl: 489000,
        passThroughTotalBrl: 68500,
        estimatedMarginPercent: 30.1,
        status: 'ApprovedForExecution',
        riskScore: 19,
        clinicalAnalysisJson:
          '{"complexity":"Alta (Coleta PK Pediátrica/Adulta + Fotografias Padronizadas)"}',
        budgetGridJson:
          '[{"visit":"V1 - Triagem","procedures":"TCLE + Exame Dermatológico EASI/SCORAD + Lab Central","baselineBrl":2480,"negotiatedBrl":3150,"taxGrossUpBrl":3673.47,"marginPercent":30.1}]',
        contractClausesJson:
          '[{"clause":"Cláusula 8.1 — Pagamento Internacional USD via C6 Bank Cayman (SWIFT CSIXKYKY)","status":"Aprovado","riskLevel":"Baixo","analysis":"Liquidação cambial direta sem bitributação."}]',
        miroFishSimulationJson:
          '{"scenariosRun":1000,"recommendedCounterOfferBrl":489000,"acceptanceProbabilityPercent":92}',
        jevScoreJson:
          '{"model":"JEV-SystemOne-v1","decision":"APPROVE_INTERNATIONAL_CAYMAN_FLOW","confidence":0.95}',
        linkedEmailsCount: 1,
      },
    ],
    messages: [
      {
        id: '0199ba04-0000-7000-8000-000000000001',
        graphMessageId: 'AAMkADg0YzE-EPOCA-001',
        mailboxAddress: 'contratos@giyus.com.br',
        subject: 'RE: Minuta CTA v3.2 + Grade Orçamentária Revisada — Estudo PRO-EPOCA-302',
        senderName: 'Marina Silva (IQVIA Site Contracts)',
        senderEmail: 'marina.silva@iqvia.com',
        senderDomain: 'iqvia.com',
        receivedAtIso: '2026-10-25T14:20:00Z',
        bodyPreview:
          'Prezados, encaminhamos em anexo a revisão da Cláusula 14.1 do CTA e a planilha .xlsx com o ajuste da visita V2 (PK seriada 8h) do protocolo EPOCA.',
        hasAttachments: true,
        attachmentNamesJson: '["CTA_EPOCA_302_v3.2_Clean.docx","Budget_Grid_EPOCA_v4_BRL.xlsx"]',
        classificationStatus: 'AutoAssigned',
        assignedProjectCode: 'EPOCA',
        confidenceScore: 98,
        matchedRulesJson:
          '["Keyword:epoca (+70, Regra Base)","SenderEmail:marina.silva@iqvia.com (+55, Aprendido c/ Humano)"]',
        manuallyReviewedBy: null,
      },
      {
        id: '0199ba04-0000-7000-8000-000000000002',
        graphMessageId: 'AAMkADg0YzE-UNASSIGNED-004',
        mailboxAddress: 'contratos@giyus.com.br',
        subject:
          'Confidencial: Convite de Feasibility & Minuta Preliminar de Orçamento — Protocolo NEUROVANCE (NV-301)',
        senderName: 'Dr. Henrik Lindqvist (Biogen Global Feasibility)',
        senderEmail: 'henrik.lindqvist@biogen-trials.com',
        senderDomain: 'biogen-trials.com',
        receivedAtIso: '2026-10-25T16:10:00Z',
        bodyPreview:
          'Prezada Diretoria GIYUS, gostaríamos de convidar o centro para o estudo Fase III NEUROVANCE (NV-301) em neurologia clínica. Segue planilha preliminar de visitas.',
        hasAttachments: true,
        attachmentNamesJson:
          '["CTA_Template_NEUROVANCE_NV301.docx","Site_Budget_Worksheet_NV301.xlsx"]',
        classificationStatus: 'Unassigned',
        assignedProjectCode: null,
        confidenceScore: 0,
        matchedRulesJson:
          '["Faltando Encaixar: projeto NEUROVANCE ainda não cadastrado ou aguardando vínculo humano"]',
        manuallyReviewedBy: null,
      },
      {
        id: '0199ba04-0000-7000-8000-000000000003',
        graphMessageId: 'AAMkADg0YzE-UNASSIGNED-005',
        mailboxAddress: 'financeiro@giyus.com.br',
        subject:
          'Complemento da Grade de Ressonância Magnética Funcional — Protocolo NEUROVANCE (NV-301)',
        senderName: 'Clara Mendes (Biogen LatAm Budgets)',
        senderEmail: 'clara.mendes@biogen-trials.com',
        senderDomain: 'biogen-trials.com',
        receivedAtIso: '2026-10-25T16:45:00Z',
        bodyPreview:
          'Complementando o e-mail anterior sobre o estudo NEUROVANCE NV-301, enviamos a tabela de reembolso Pass-through para exames de RM 3 Tesla.',
        hasAttachments: true,
        attachmentNamesJson: '["Anexo_PassThrough_RM3T_NEUROVANCE.xlsx"]',
        classificationStatus: 'Unassigned',
        assignedProjectCode: null,
        confidenceScore: 0,
        matchedRulesJson:
          '["Faltando Encaixar: será classificado automaticamente assim que o humano criar o projeto NEUROVANCE"]',
        manuallyReviewedBy: null,
      },
      {
        id: '0199ba04-0000-7000-8000-000000000004',
        graphMessageId: 'AAMkADg0YzE-SOLAIR-006',
        mailboxAddress: 'contratos@giyus.com.br',
        subject:
          'RE: D7820C00004 (SOLAIR) — Parecer PI Prof. Dr. Nelson Rosário e Contraproposta Orçamentária Parexel',
        senderName: 'Prof. Dr. Nelson Augusto Rosário Filho (UFPR)',
        senderEmail: 'nelson.rosario@ufpr.br',
        senderDomain: 'ufpr.br',
        receivedAtIso: '2026-10-25T17:15:00Z',
        bodyPreview:
          'Prezado Syllas e Dra. Patricia Stein (Parexel), concordo com a revisão do tempo de Espirometria Seriada + FeNO no protocolo D7820C00004 SOLAIR para o centro de Curitiba.',
        hasAttachments: true,
        attachmentNamesJson: '["Parecer_Clinico_SOLAIR_Dr_Nelson_Rosario.pdf"]',
        classificationStatus: 'AutoAssigned',
        assignedProjectCode: 'SOLAIR',
        confidenceScore: 97,
        matchedRulesJson:
          '["Keyword:solair (+70, Regra Base)","SenderEmail:nelson.rosario@ufpr.br (+45, PI Externo Vinculado)"]',
        manuallyReviewedBy: null,
      },
      {
        id: '0199ba04-0000-7000-8000-000000000005',
        graphMessageId: 'AAMkADg0YzE-ADEPT4-007',
        mailboxAddress: 'contratos@giyus.com.br',
        subject:
          'BMS CN0120056 (ADEPT-4) — Final Budget Grid & Cayman Banking Validation (Dr. Herberto Chong)',
        senderName: 'Valeria Costa Santos (PPD / Thermo Fisher)',
        senderEmail: 'Valeria.Santos2@thermofisher.com',
        senderDomain: 'thermofisher.com',
        receivedAtIso: '2026-10-25T18:05:00Z',
        bodyPreview:
          'Olá Elessandra (elessandra@giyus.com.br) e Dr. Chong (chong.neto@giyus.com.br), confirmamos a aprovação da grade final do estudo BMS CN0120056 ADEPT-4.',
        hasAttachments: true,
        attachmentNamesJson: '["BMS_CN0120056_ADEPT4_Final_Grid_USD.xlsx"]',
        classificationStatus: 'AutoAssigned',
        assignedProjectCode: 'ADEPT-4',
        confidenceScore: 96,
        matchedRulesJson:
          '["Keyword:adept-4 (+70, Regra Base)","SenderEmail:Valeria.Santos2@thermofisher.com (+50, Aprendido c/ Humano)"]',
        manuallyReviewedBy: null,
      },
      {
        id: '0199ba04-0000-7000-8000-000000000006',
        graphMessageId: 'AAMkADg0YzE-PERSIST-008',
        mailboxAddress: 'regulatorio@giyus.com.br',
        mailboxFolder: 'INBOX',
        subject: 'GSK PERSIST-ASTHMA-1 — Deliberação CONEP/CEP e Aditivo de Insumos FeNO (IQVIA)',
        senderName: 'Amanda Contracts (IQVIA Site Activation)',
        senderEmail: 'amanda.contracts@iqvia.com',
        senderDomain: 'iqvia.com',
        receivedAtIso: '2026-10-25T19:00:00Z',
        bodyPreview:
          'Prezada equipe GIYUS (regulatorio@giyus.com.br), encaminhamos a minuta do 1º Aditivo do estudo PERSIST contemplando o reembolso integral dos bocais FeNO.',
        hasAttachments: true,
        attachmentNamesJson: '["Aditivo_1_PERSIST_GSK_IQVIA.docx"]',
        classificationStatus: 'AutoAssigned',
        assignedProjectCode: 'PERSIST',
        confidenceScore: 95,
        matchedRulesJson:
          '["Keyword:persist (+70, Regra Base)","SenderEmail:amanda.contracts@iqvia.com (+45, Regra Base)"]',
        manuallyReviewedBy: null,
      },
      {
        id: '0199ba04-0000-7000-8000-000000000007',
        graphMessageId: 'AAMkADg0YzE-EPOCA-OUT-009',
        mailboxAddress: 'contratos@giyus.com.br',
        mailboxFolder: 'OUTBOX',
        recipientEmailsJson: '["marina.silva@iqvia.com"]',
        ccEmailsJson: '["syllas@giyus.com.br"]',
        subject: 'Re: Minuta CTA v3.2 + Grade Orçamentária Revisada — Estudo PRO-EPOCA-302',
        senderName: 'Giyus Contratos',
        senderEmail: 'contratos@giyus.com.br',
        senderDomain: 'giyus.com.br',
        receivedAtIso: '2026-10-25T15:10:00Z',
        bodyPreview:
          'Prezada Marina, acusamos o recebimento da Minuta CTA v3.2 e da grade revisada do estudo PRO-EPOCA-302. Encaminhamos nossas considerações na Cláusula 14.1.',
        hasAttachments: true,
        attachmentNamesJson: '["CTA_EPOCA_302_v3.2_Giyus_Redline.docx"]',
        classificationStatus: 'AutoAssigned',
        assignedProjectCode: 'EPOCA',
        confidenceScore: 99,
        matchedRulesJson: '["Caixa de Saída (SentItems): Resposta vinculada à Thread EPOCA"]',
        manuallyReviewedBy: null,
      },
      {
        id: '0199ba04-0000-7000-8000-000000000008',
        graphMessageId: 'AAMkADg0YzE-SOLAIR-OUT-010',
        mailboxAddress: 'contratos@giyus.com.br',
        mailboxFolder: 'OUTBOX',
        recipientEmailsJson: '["Patricia.Stein@parexel.com","nelson.rosario@ufpr.br"]',
        ccEmailsJson: '["syllas@giyus.com.br"]',
        subject:
          'Re: D7820C00004 (SOLAIR) — Parecer PI Prof. Dr. Nelson Rosário e Contraproposta Orçamentária Parexel',
        senderName: 'Giyus Contratos',
        senderEmail: 'contratos@giyus.com.br',
        senderDomain: 'giyus.com.br',
        receivedAtIso: '2026-10-25T17:48:00Z',
        bodyPreview:
          'Prezada Patrícia e Prof. Dr. Nelson, segue na caixa de saída a contraproposta consolidada do centro GIYUS Curitiba com Espirometria Seriada + FeNO.',
        hasAttachments: true,
        attachmentNamesJson: '["Contraproposta_SOLAIR_Giyus_R04.xlsx"]',
        classificationStatus: 'AutoAssigned',
        assignedProjectCode: 'SOLAIR',
        confidenceScore: 99,
        matchedRulesJson: '["Caixa de Saída (SentItems): Resposta vinculada à Thread SOLAIR"]',
        manuallyReviewedBy: null,
      },
    ],
    rules: [
      {
        id: '0199ba05-0000-7000-8000-000000000001',
        projectCode: 'EPOCA',
        projectName: 'Protocolo EPOCA (PRO-EPOCA-302)',
        ruleKind: 'Keyword',
        pattern: 'epoca',
        weight: 70,
        learnedFromHumanFeedback: false,
        hitCount: 14,
        createdByRole: 'SYSTEM',
      },
      {
        id: '0199ba05-0000-7000-8000-000000000002',
        projectCode: 'EPOCA',
        projectName: 'Protocolo EPOCA (PRO-EPOCA-302)',
        ruleKind: 'SenderEmail',
        pattern: 'marina.silva@iqvia.com',
        weight: 55,
        learnedFromHumanFeedback: true,
        hitCount: 6,
        createdByRole: 'HUMAN_FEEDBACK',
      },
      {
        id: '0199ba05-0000-7000-8000-000000000003',
        projectCode: 'SOLAIR',
        projectName: 'Protocolo SOLAIR (ONC-SOLAIR-204)',
        ruleKind: 'Keyword',
        pattern: 'solair',
        weight: 70,
        learnedFromHumanFeedback: false,
        hitCount: 11,
        createdByRole: 'SYSTEM',
      },
    ],
    incomingStudyProcedures: [],
    holdingPriceTableVersion: 3,
    cityPrices: [
      {
        id: 'cp-001',
        procedureCode: 'PROC-ICF-01',
        cityUf: 'Curitiba/PR',
        unitMeasure: 'POR_PAGINA',
        unitPriceBrl: 480,
        version: 2,
        updatedAtIso: '2026-10-20T10:00:00Z',
        updatedByEmail: 'syllas@giyus.com.br',
        changeReason: 'Tabela Base Holding GIYUS Curitiba',
      },
      {
        id: 'cp-003',
        procedureCode: 'PROC-PK-SERIAL',
        cityUf: 'Curitiba/PR',
        unitMeasure: 'HORA_PROFISSIONAL',
        unitPriceBrl: 1890,
        version: 3,
        updatedAtIso: '2026-10-25T15:00:00Z',
        updatedByEmail: 'syllas@giyus.com.br',
        changeReason: 'Reajuste Holding v3 de Coleta PK Seriada 8h + Criogenia -80°C',
      },
      {
        id: 'cp-005',
        procedureCode: 'PROC-SPIRO-FENO',
        cityUf: 'Curitiba/PR',
        unitMeasure: 'EXAME_UNIDADE',
        unitPriceBrl: 790,
        version: 3,
        updatedAtIso: '2026-10-25T16:30:00Z',
        updatedByEmail: 'syllas@giyus.com.br',
        changeReason: 'Atualização Holding v3 Espirometria + Bocal FeNO NIOX VERO',
      },
      {
        id: 'cp-006',
        procedureCode: 'PROC-ECHO-STRAIN',
        cityUf: 'Curitiba/PR',
        unitMeasure: 'EXAME_UNIDADE',
        unitPriceBrl: 980,
        version: 2,
        updatedAtIso: '2026-10-18T11:00:00Z',
        updatedByEmail: 'syllas@giyus.com.br',
        changeReason: 'Tabela Base Ecocardiograma Strain',
      },
      {
        id: 'cp-007',
        procedureCode: 'PROC-SUBQ-BIO',
        cityUf: 'Curitiba/PR',
        unitMeasure: 'DISPENSA_FARMACIA',
        unitPriceBrl: 920,
        version: 2,
        updatedAtIso: '2026-10-18T11:00:00Z',
        updatedByEmail: 'syllas@giyus.com.br',
        changeReason: 'Tabela Base Farmácia IWRS + SC 2h',
      },
    ],
    globalRules: mergeCanonicalGlobalRules([
      {
        id: 'grule-001',
        studyCode: 'PERSIST',
        ruleTitle: 'Reembolso Obrigatório de Kit Bocal FeNO NIOX VERO',
        ruleText:
          'Todo procedimento de Espirometria seriada com FeNO exige rubrica Pass-Through separada para o bocal descartável NIOX VERO.',
        originModule: 'PROTOCOL_CRITIQUE',
        proposedByEmail: 'syllas@giyus.com.br',
        status: 'APPROVED_GLOBAL',
        approvedByEmail: 'syllas@giyus.com.br',
        updatedAtIso: '2026-10-24T14:00:00Z',
      },
      {
        id: 'grule-002',
        studyCode: 'EPOCA',
        ruleTitle: 'Cláusula EQ-BCB-01 + Gross-up Lucro Real 14,25% em Coleta PK 8h',
        ruleText:
          'Visitas com permanência hospitalar/clínica >= 6h devem aplicar Gross-up de 14,25% (Lucro Real), Overhead 30% e alimentação Pass-Through.',
        originModule: 'COUNCIL_MULTI_LLM',
        proposedByEmail: 'syllas@giyus.com.br',
        status: 'APPROVED_GLOBAL',
        approvedByEmail: 'syllas@giyus.com.br',
        updatedAtIso: '2026-10-24T18:30:00Z',
      },
      {
        id: 'grule-003',
        studyCode: 'SOLAIR',
        ruleTitle: 'Reserva de Screen Failure 2:1 quando Cap <= 5 pacientes',
        ruleText:
          'Quando o contrato prevê Cap de randomização inferior a 5 pacientes no centro, exigir cobertura de Screen Failure na razão 2:1 na minuta CTA.',
        originModule: 'LLM_CHAT',
        proposedByEmail: 'elessandra@giyus.com.br',
        status: 'PENDING_GLOBAL_APPROVAL',
        approvedByEmail: null,
        updatedAtIso: '2026-10-25T19:10:00Z',
      },
    ]),
    studyTableVersions: {
      EPOCA: 2,
      SOLAIR: 2,
      ARGON: 2,
      PERSIST: 2,
      'ADEPT-4': 2,
    },
    studyDismissedAlerts: {},
    critiquesByStudy: {},
    paymentsByStudy: {},
    cashflowByStudy: {},
    calibrationSuppliers: [
      {
        id: 'cal-sup-001',
        code: 'RBC-TRESCAL-BR',
        tradeName: 'Trescal Brasil Metrologia RBC',
        legalName: 'Trescal Brasil Laboratórios de Calibração Ltda.',
        cnpj: '05.489.112/0001-74',
        rbcAccreditationCode: 'CAL-0148 (CGCRE / INMETRO — ISO/IEC 17025)',
        headquartersCityUf: 'São Paulo/SP (Atendimento in-loco Curitiba/PR e Nacional)',
        coverageScope: 'NACIONAL_BRASIL',
        accreditedMagnitudes: [
          'TEMPERATURA_CADEIA_FRIA',
          'ROTACAO_RPM',
          'PRESSAO_ARTERIAL',
          'MASSA_VOLUME',
          'ELETRICA_ECG_DESFIB',
        ],
        slaTurnaroundDays: 5,
        contactEmail: 'atendimento.rbc@trescal.com.br',
        qualificationStatus: 'QUALIFIED_RBC',
        linkedAssetsCount: 1,
      },
      {
        id: 'cal-sup-002',
        code: 'RBC-CALILAB-SUL',
        tradeName: 'CaliLab Metrologia Científica & Cadeia Fria',
        legalName: 'CaliLab Engenharia e Calibração Acreditada S.A.',
        cnpj: '11.832.904/0001-28',
        rbcAccreditationCode: 'CAL-0312 (CGCRE / INMETRO — Ultra-Baixa Temperatura -80°C)',
        headquartersCityUf: 'Curitiba/PR',
        coverageScope: 'PR_SC_RS',
        accreditedMagnitudes: ['TEMPERATURA_CADEIA_FRIA', 'ROTACAO_RPM', 'MASSA_VOLUME'],
        slaTurnaroundDays: 3,
        contactEmail: 'qualidade@calilab.com.br',
        qualificationStatus: 'QUALIFIED_RBC',
        linkedAssetsCount: 2,
      },
      {
        id: 'cal-sup-003',
        code: 'RBC-BIOMED-RESP',
        tradeName: 'BioMed Engenharia Clínica & Espirometria/FeNO',
        legalName: 'BioMed Calibração Biomédica e Pneumológica Ltda.',
        cnpj: '23.701.445/0001-60',
        rbcAccreditationCode:
          'CAL-0589 (CGCRE / INMETRO — Fluxo, Volume Respiratório e Analisadores)',
        headquartersCityUf: 'Campinas/SP',
        coverageScope: 'SUL_SUDESTE',
        accreditedMagnitudes: [
          'FLUXO_ESPIROMETRIA_FENO',
          'ELETRICA_ECG_DESFIB',
          'PRESSAO_ARTERIAL',
        ],
        slaTurnaroundDays: 7,
        contactEmail: 'calibracao@biomedmetrologia.com.br',
        qualificationStatus: 'QUALIFIED_RBC',
        linkedAssetsCount: 1,
      },
    ],
    facilityAssets: [
      {
        id: 'eq-001',
        assetTag: 'PAT-CWB-0001',
        name: 'Ultra-Freezer Criogênico -86°C Thermo Scientific TSX',
        typeCode: 'FREEZER_MINUS80',
        manufacturerModel: 'Thermo Scientific / TSX60086V',
        serialNumber: 'SN-TSX-884920',
        siteCode: 'CWB-01',
        roomLocation: 'Sala de Criogenia & Processamento Biológico',
        criticality: 'CRITICAL',
        ownershipKind: 'GIYUS_TENANT',
        ownerEntityName: 'GIYUS Curitiba Research Center (CNPJ 69.584.441/0001-84)',
        linkedStudyCode: null,
        isDepreciableInGiyusBalance: true,
        acquisitionValueBrl: 84000,
        residualValueBrl: 8400,
        usefulLifeMonths: 120,
        elapsedMonths: 24,
        monthlyDepreciationBrl: 630,
        accumulatedDepreciationBrl: 15120,
        netBookValueBrl: 68880,
        hourlyBudgetCostBrl: 3.94,
        insuredCustodyValueBrl: 0,
        loanPolicyOrContractRef: 'Patrimônio Próprio NF-e #004812',
        endOfLifeDisposition: 'DESTROY_WITH_PGRSS_CERTIFICATE',
        dispositionStatus: 'ACTIVE_IN_USE',
        dispositionEvidenceRef: null,
        blocksStudyCloseOut: false,
        requiresCalibration: true,
        requiredMagnitude: 'TEMPERATURA_CADEIA_FRIA (-80°C)',
        baseCalibrationMonths: 12,
        studyOverrideCalibrationMonths: 6,
        effectiveCalibrationMonths: 6,
        assignedCalibrationSupplierId: 'cal-sup-002',
        assignedCalibrationSupplierName: 'CaliLab Metrologia Científica & Cadeia Fria',
        lastCalibratedDate: '2026-07-15',
        nextCalibrationDueDate: '2027-01-15',
        daysUntilCalibrationDue: 81,
        oneMonthWarningTriggerDate: '2026-12-15',
        isOneMonthWarningActive: false,
        oneMonthWarningMessage:
          'Aviso automático de 1 mês antes (T-30d) programado para 15/12/2026.',
        calibrationSemaphore: 'VALID',
        blockedForStudyUse: false,
        blockReason: null,
        lastCertificateNumber: 'CERT-RBC-CAL0312-2026-771',
        lastRecordedByEmail: 'eduarda.lazzarotto@giyus.com.br',
        lastVerifiedByEmail: 'syllas@giyus.com.br',
        equipmentInvoiceNumber: 'NF-e 000.004.812 (Série 1)',
        equipmentInvoiceAccessKey: '41241069584441000184550010000048121004812095',
        equipmentInvoiceIssuedDate: '2024-10-15',
        calibrationServiceInvoiceNumber: 'NFS-e 2026/001482',
        calibrationFeeBrl: 1450,
        compatibleCalibrationSuppliers: [
          {
            supplierId: 'cal-sup-002',
            tradeName: 'CaliLab Metrologia Científica & Cadeia Fria',
            rbcAccreditationCode: 'CAL-0312 (CGCRE / INMETRO — Ultra-Baixa Temperatura -80°C)',
            coverageScope: 'PR_SC_RS',
            slaTurnaroundDays: 3,
            estimatedCalibrationFeeBrl: 1450,
            compatibilityReason:
              'Acreditado RBC para Ultra-Baixa Temperatura -80°C · Atendimento local Curitiba/PR (SLA 3d)',
          },
          {
            supplierId: 'cal-sup-001',
            tradeName: 'Trescal Brasil Metrologia RBC',
            rbcAccreditationCode: 'CAL-0148 (CGCRE / INMETRO — ISO/IEC 17025)',
            coverageScope: 'NACIONAL_BRASIL',
            slaTurnaroundDays: 5,
            estimatedCalibrationFeeBrl: 1620,
            compatibilityReason:
              'Acreditado RBC Nacional para Cadeia Fria · Cobertura Nacional (SLA 5d)',
          },
        ],
        resourceCategory: 'EQUIPMENT',
        loanReturnDueDate: null,
        daysUntilLoanReturnDue: null,
        returnTriggerRule: null,
        disposalExecutionMode: 'PERFORMED_BY_SITE',
        disposalCertificateRequirement: 'WITH_DISPOSAL_CERTIFICATE',
        sponsorOrderedDestruction: false,
        canConvertDestructionToSiteUse: false,
        siteInterestStatus: 'NOT_APPLICABLE',
      },
      {
        id: 'eq-002',
        assetTag: 'COM-EPOCA-FENO-01',
        name: 'Analisador de Óxido Nítrico Exalado (FeNO) Circassia NIOX VERO',
        typeCode: 'FENO_ANALYZER',
        manufacturerModel: 'Circassia / NIOX VERO Clinical',
        serialNumber: 'SN-NIOX-551209',
        siteCode: 'CWB-01',
        roomLocation: 'Consultório de Função Pulmonar 02',
        criticality: 'CRITICAL',
        ownershipKind: 'SPONSOR_STUDY_LOAN',
        ownerEntityName: 'AstraZeneca Global / Estudo EPOCA (Comodato Internacional)',
        linkedStudyCode: 'EPOCA',
        isDepreciableInGiyusBalance: false,
        acquisitionValueBrl: 0,
        residualValueBrl: 0,
        usefulLifeMonths: 36,
        elapsedMonths: 14,
        monthlyDepreciationBrl: 0,
        accumulatedDepreciationBrl: 0,
        netBookValueBrl: 0,
        hourlyBudgetCostBrl: 0,
        insuredCustodyValueBrl: 38500,
        loanPolicyOrContractRef: 'CTA EPOCA Anexo IV — Apólice Chubb #99182-BR',
        endOfLifeDisposition: 'RETURN_TO_SPONSOR_OR_LAB',
        dispositionStatus: 'PENDING_CLOSEOUT_RETURN',
        dispositionEvidenceRef: null,
        blocksStudyCloseOut: true,
        requiresCalibration: true,
        requiredMagnitude: 'FLUXO_ESPIROMETRIA_FENO',
        baseCalibrationMonths: 12,
        studyOverrideCalibrationMonths: 6,
        effectiveCalibrationMonths: 6,
        assignedCalibrationSupplierId: 'cal-sup-003',
        assignedCalibrationSupplierName: 'BioMed Engenharia Clínica & Espirometria/FeNO',
        lastCalibratedDate: '2026-05-20',
        nextCalibrationDueDate: '2026-11-20',
        daysUntilCalibrationDue: 25,
        oneMonthWarningTriggerDate: '2026-10-20',
        isOneMonthWarningActive: true,
        oneMonthWarningMessage:
          'AVISO DE 1 MÊS ANTES ATIVO (Disparado em 20/10/2026): Calibração vence em 20/11/2026 (faltam 25 dias). Agende com BioMed RBC ou Trescal RBC.',
        calibrationSemaphore: 'DUE_SOON_T30',
        blockedForStudyUse: false,
        blockReason: null,
        lastCertificateNumber: 'CERT-RBC-CAL0589-2026-309',
        lastRecordedByEmail: 'mariana.linero@giyus.com.br',
        lastVerifiedByEmail: 'syllas@giyus.com.br',
        equipmentInvoiceNumber: 'NF-e Remessa Comodato 000.091.402',
        equipmentInvoiceAccessKey: '35250843854116000109550010000914021009140218',
        equipmentInvoiceIssuedDate: '2025-08-12',
        calibrationServiceInvoiceNumber: 'NFS-e 2026/001190',
        calibrationFeeBrl: 1180,
        compatibleCalibrationSuppliers: [
          {
            supplierId: 'cal-sup-003',
            tradeName: 'BioMed Engenharia Clínica & Espirometria/FeNO',
            rbcAccreditationCode:
              'CAL-0589 (CGCRE / INMETRO — Fluxo, Volume Respiratório e Analisadores)',
            coverageScope: 'SUL_SUDESTE',
            slaTurnaroundDays: 7,
            estimatedCalibrationFeeBrl: 1180,
            compatibilityReason:
              'Especialista RBC em Fluxo Respiratório e FeNO · Cobertura Sul/Sudeste (SLA 7d)',
          },
          {
            supplierId: 'cal-sup-001',
            tradeName: 'Trescal Brasil Metrologia RBC',
            rbcAccreditationCode: 'CAL-0148 (CGCRE / INMETRO — ISO/IEC 17025)',
            coverageScope: 'NACIONAL_BRASIL',
            slaTurnaroundDays: 5,
            estimatedCalibrationFeeBrl: 1390,
            compatibilityReason: 'Laboratório RBC Nacional Multigrandezas (SLA 5d)',
          },
        ],
        resourceCategory: 'EQUIPMENT',
        loanReturnDueDate: '2026-12-10',
        daysUntilLoanReturnDue: 45,
        returnTriggerRule:
          'Devolver em até 15 dias após Visita de Encerramento (COV) do estudo EPOCA via World Courier (Prazo: 10/12/2026)',
        disposalExecutionMode: 'SHIPPED_VIA_CARRIER',
        disposalCertificateRequirement: 'RETURN_AWB_ONLY',
        sponsorOrderedDestruction: false,
        canConvertDestructionToSiteUse: false,
        siteInterestStatus: 'NOT_APPLICABLE',
      },
      {
        id: 'eq-003',
        assetTag: 'COM-SOLAIR-ECG-02',
        name: 'Eletrocardiógrafo Digital 12 Derivações Clario / ERT ELI 280',
        typeCode: 'ECG',
        manufacturerModel: 'Mortara / Clario ELI 280 Touchscreen',
        serialNumber: 'SN-ERT-339104',
        siteCode: 'CWB-01',
        roomLocation: 'Sala de ECG & Sinais Vitais',
        criticality: 'CRITICAL',
        ownershipKind: 'CENTRAL_LAB_LOAN',
        ownerEntityName: 'Clario Cardiac Safety Central Lab / Estudo SOLAIR',
        linkedStudyCode: 'SOLAIR',
        isDepreciableInGiyusBalance: false,
        acquisitionValueBrl: 0,
        residualValueBrl: 0,
        usefulLifeMonths: 48,
        elapsedMonths: 18,
        monthlyDepreciationBrl: 0,
        accumulatedDepreciationBrl: 0,
        netBookValueBrl: 0,
        hourlyBudgetCostBrl: 0,
        insuredCustodyValueBrl: 29400,
        loanPolicyOrContractRef: 'Termo de Comodato Clario-SOLAIR-2025-BR',
        endOfLifeDisposition: 'RETURN_TO_SPONSOR_OR_LAB',
        dispositionStatus: 'PENDING_CLOSEOUT_RETURN',
        dispositionEvidenceRef: null,
        blocksStudyCloseOut: true,
        requiresCalibration: true,
        requiredMagnitude: 'ELETRICA_ECG_DESFIB',
        baseCalibrationMonths: 12,
        studyOverrideCalibrationMonths: 6,
        effectiveCalibrationMonths: 6,
        assignedCalibrationSupplierId: 'cal-sup-001',
        assignedCalibrationSupplierName: 'Trescal Brasil Metrologia RBC',
        lastCalibratedDate: '2026-03-10',
        nextCalibrationDueDate: '2026-09-10',
        daysUntilCalibrationDue: -46,
        oneMonthWarningTriggerDate: '2026-08-10',
        isOneMonthWarningActive: true,
        oneMonthWarningMessage:
          'VENCIDA HÁ 46 DIAS (Aviso de 1 mês enviado em 10/08/2026): Equipamento bloqueado para visitas clínicas até registro de novo certificado RBC.',
        calibrationSemaphore: 'EXPIRED_BLOCKED',
        blockedForStudyUse: true,
        blockReason:
          'TRAVA VERMELHA (REQ-FAC-006): Calibração RBC semestral (override protocolo SOLAIR 6m) vencida em 10/09/2026. Bloqueado para agendamento e uso clínico até novo certificado verificado (SoD).',
        lastCertificateNumber: 'CERT-RBC-CAL0148-2026-102',
        lastRecordedByEmail: 'elessandra@giyus.com.br',
        lastVerifiedByEmail: 'syllas@giyus.com.br',
        equipmentInvoiceNumber: 'NF-e Remessa Comodato 000.077.219',
        equipmentInvoiceAccessKey: '35250460840055000131550010000772191007721904',
        equipmentInvoiceIssuedDate: '2025-04-18',
        calibrationServiceInvoiceNumber: 'NFS-e 2026/000814',
        calibrationFeeBrl: 980,
        compatibleCalibrationSuppliers: [
          {
            supplierId: 'cal-sup-001',
            tradeName: 'Trescal Brasil Metrologia RBC',
            rbcAccreditationCode: 'CAL-0148 (CGCRE / INMETRO — ISO/IEC 17025)',
            coverageScope: 'NACIONAL_BRASIL',
            slaTurnaroundDays: 5,
            estimatedCalibrationFeeBrl: 980,
            compatibilityReason:
              'Acreditado RBC para Segurança Elétrica, ECG e Desfibriladores (SLA 5d)',
          },
          {
            supplierId: 'cal-sup-003',
            tradeName: 'BioMed Engenharia Clínica & Espirometria/FeNO',
            rbcAccreditationCode:
              'CAL-0589 (CGCRE / INMETRO — Fluxo, Volume Respiratório e Analisadores)',
            coverageScope: 'SUL_SUDESTE',
            slaTurnaroundDays: 7,
            estimatedCalibrationFeeBrl: 1090,
            compatibilityReason: 'Acreditado RBC em Engenharia Clínica Biomédica e ECG (SLA 7d)',
          },
        ],
        resourceCategory: 'EQUIPMENT',
        loanReturnDueDate: '2027-01-15',
        daysUntilLoanReturnDue: 81,
        returnTriggerRule:
          'Devolver ao Laboratório Central Clario via DHL Medical Express até 15/01/2027 (Fim da Coorte Cardíaca SOLAIR)',
        disposalExecutionMode: 'SHIPPED_VIA_CARRIER',
        disposalCertificateRequirement: 'RETURN_AWB_ONLY',
        sponsorOrderedDestruction: false,
        canConvertDestructionToSiteUse: false,
        siteInterestStatus: 'NOT_APPLICABLE',
      },
      {
        id: 'eq-004',
        assetTag: 'PAT-HLD-0019',
        name: 'Centrífuga Refrigerada Clínica Eppendorf 5702R (-9°C a +40°C)',
        typeCode: 'CENTRIFUGE',
        manufacturerModel: 'Eppendorf / 5702R Rotor Basculante',
        serialNumber: 'SN-EPP-774012',
        siteCode: 'CWB-01',
        roomLocation: 'Laboratório de Processamento PK / PBMC',
        criticality: 'CRITICAL',
        ownershipKind: 'GIYUS_HOLDING',
        ownerEntityName: 'GIYUS Holding Matriz (CNPJ 69.308.788/0001-02)',
        linkedStudyCode: null,
        isDepreciableInGiyusBalance: true,
        acquisitionValueBrl: 36000,
        residualValueBrl: 3600,
        usefulLifeMonths: 60,
        elapsedMonths: 30,
        monthlyDepreciationBrl: 540,
        accumulatedDepreciationBrl: 16200,
        netBookValueBrl: 19800,
        hourlyBudgetCostBrl: 3.38,
        insuredCustodyValueBrl: 0,
        loanPolicyOrContractRef: 'Ativo Imobilizado Holding #AI-2024-019',
        endOfLifeDisposition: 'REPLACE_OR_WRITE_OFF',
        dispositionStatus: 'ACTIVE_IN_USE',
        dispositionEvidenceRef: null,
        blocksStudyCloseOut: false,
        requiresCalibration: true,
        requiredMagnitude: 'ROTACAO_RPM + TEMPERATURA_CADEIA_FRIA',
        baseCalibrationMonths: 12,
        studyOverrideCalibrationMonths: null,
        effectiveCalibrationMonths: 12,
        assignedCalibrationSupplierId: 'cal-sup-002',
        assignedCalibrationSupplierName: 'CaliLab Metrologia Científica & Cadeia Fria',
        lastCalibratedDate: '2025-11-15',
        nextCalibrationDueDate: '2026-11-15',
        daysUntilCalibrationDue: 20,
        oneMonthWarningTriggerDate: '2026-10-15',
        isOneMonthWarningActive: true,
        oneMonthWarningMessage:
          'AVISO DE 1 MÊS ANTES ATIVO (Disparado em 15/10/2026): Calibração vence em 15/11/2026 (faltam 20 dias). Fornecedores recomendados: CaliLab RBC (SLA 3d) ou Trescal RBC (SLA 5d).',
        calibrationSemaphore: 'DUE_SOON_T30',
        blockedForStudyUse: false,
        blockReason: null,
        lastCertificateNumber: 'CERT-RBC-CAL0312-2025-889',
        lastRecordedByEmail: 'laura.ferreira@giyus.com.br',
        lastVerifiedByEmail: 'syllas@giyus.com.br',
        equipmentInvoiceNumber: 'NF-e 000.003.119 (Série 1)',
        equipmentInvoiceAccessKey: '35240469308788000102550010000031191003119081',
        equipmentInvoiceIssuedDate: '2024-04-20',
        calibrationServiceInvoiceNumber: 'NFS-e 2025/004412',
        calibrationFeeBrl: 1120,
        compatibleCalibrationSuppliers: [
          {
            supplierId: 'cal-sup-002',
            tradeName: 'CaliLab Metrologia Científica & Cadeia Fria',
            rbcAccreditationCode: 'CAL-0312 (CGCRE / INMETRO — Ultra-Baixa Temperatura -80°C)',
            coverageScope: 'PR_SC_RS',
            slaTurnaroundDays: 3,
            estimatedCalibrationFeeBrl: 1120,
            compatibilityReason:
              'Acreditado RBC para Rotação (RPM) + Temperatura · Curitiba/PR (SLA 3d)',
          },
          {
            supplierId: 'cal-sup-001',
            tradeName: 'Trescal Brasil Metrologia RBC',
            rbcAccreditationCode: 'CAL-0148 (CGCRE / INMETRO — ISO/IEC 17025)',
            coverageScope: 'NACIONAL_BRASIL',
            slaTurnaroundDays: 5,
            estimatedCalibrationFeeBrl: 1290,
            compatibilityReason:
              'Acreditado RBC Nacional para Tacometria (RPM) + Temperatura (SLA 5d)',
          },
        ],
        resourceCategory: 'EQUIPMENT',
        loanReturnDueDate: null,
        daysUntilLoanReturnDue: null,
        returnTriggerRule: null,
        disposalExecutionMode: 'PERFORMED_BY_SITE',
        disposalCertificateRequirement: 'WITH_DISPOSAL_CERTIFICATE',
        sponsorOrderedDestruction: false,
        canConvertDestructionToSiteUse: false,
        siteInterestStatus: 'NOT_APPLICABLE',
      },
      {
        id: 'eq-005',
        assetTag: 'PAT-CWB-0042',
        name: 'Carrinho de Emergência e Parada Cardiorrespiratória c/ Lacre Numerado',
        typeCode: 'EMERGENCY_CART',
        manufacturerModel: 'Fanem / Urgência Hospitalar Inox',
        serialNumber: 'SN-FAN-192004',
        siteCode: 'CWB-01',
        roomLocation: 'Sala de Infusão Biológica e Imunoterapia',
        criticality: 'HIGH',
        ownershipKind: 'GIYUS_TENANT',
        ownerEntityName: 'GIYUS Curitiba Research Center (CNPJ 69.584.441/0001-84)',
        linkedStudyCode: null,
        isDepreciableInGiyusBalance: true,
        acquisitionValueBrl: 14500,
        residualValueBrl: 1450,
        usefulLifeMonths: 120,
        elapsedMonths: 18,
        monthlyDepreciationBrl: 108.75,
        accumulatedDepreciationBrl: 1957.5,
        netBookValueBrl: 12542.5,
        hourlyBudgetCostBrl: 0.68,
        insuredCustodyValueBrl: 0,
        loanPolicyOrContractRef: 'Patrimônio Próprio NF-e #005190',
        endOfLifeDisposition: 'DONATE_INCORPORATE_TO_SITE',
        dispositionStatus: 'ACTIVE_IN_USE',
        dispositionEvidenceRef: null,
        blocksStudyCloseOut: false,
        requiresCalibration: false,
        requiredMagnitude: 'ISENTO_MANUTENCAO_PREVENTIVA (Checklist Mensal de Lacre/Validade)',
        baseCalibrationMonths: 0,
        studyOverrideCalibrationMonths: null,
        effectiveCalibrationMonths: 0,
        assignedCalibrationSupplierId: null,
        assignedCalibrationSupplierName: null,
        lastCalibratedDate: null,
        nextCalibrationDueDate: null,
        daysUntilCalibrationDue: null,
        oneMonthWarningTriggerDate: null,
        isOneMonthWarningActive: false,
        oneMonthWarningMessage: null,
        calibrationSemaphore: 'EXEMPT_PREVENTIVE_ONLY',
        blockedForStudyUse: false,
        blockReason: null,
        lastCertificateNumber: null,
        lastRecordedByEmail: null,
        lastVerifiedByEmail: null,
        equipmentInvoiceNumber: 'NF-e 000.005.190 (Série 1)',
        equipmentInvoiceAccessKey: '41250469584441000184550010000051901005190022',
        equipmentInvoiceIssuedDate: '2025-04-10',
        calibrationServiceInvoiceNumber: null,
        calibrationFeeBrl: 0,
        compatibleCalibrationSuppliers: [],
        resourceCategory: 'EQUIPMENT',
        loanReturnDueDate: null,
        daysUntilLoanReturnDue: null,
        returnTriggerRule: null,
        disposalExecutionMode: 'RETAINED_FOR_SITE_USE',
        disposalCertificateRequirement: 'WITHOUT_DISPOSAL_CERTIFICATE',
        sponsorOrderedDestruction: false,
        canConvertDestructionToSiteUse: false,
        siteInterestStatus: 'NOT_APPLICABLE',
      },
      {
        id: 'eq-006',
        assetTag: 'LAB-LABCORP-CENT-04',
        name: 'Centrífuga Refrigerada de Bancada Labcorp Central Lab (Hettich Rotanta 460R)',
        typeCode: 'CENTRIFUGE',
        manufacturerModel: 'Hettich / Rotanta 460R Labcorp Kit',
        serialNumber: 'SN-LBC-882014',
        siteCode: 'CWB-01',
        roomLocation: 'Sala de Processamento Central Lab',
        criticality: 'CRITICAL',
        ownershipKind: 'CENTRAL_LAB_LOAN',
        ownerEntityName: 'Labcorp Central Laboratory Services (Comodato Estudo ARGON)',
        linkedStudyCode: 'ARGON',
        isDepreciableInGiyusBalance: false,
        acquisitionValueBrl: 0,
        residualValueBrl: 0,
        usefulLifeMonths: 48,
        elapsedMonths: 22,
        monthlyDepreciationBrl: 0,
        accumulatedDepreciationBrl: 0,
        netBookValueBrl: 0,
        hourlyBudgetCostBrl: 0,
        insuredCustodyValueBrl: 34200,
        loanPolicyOrContractRef: 'Labcorp Equipment Loan Agreement #LBC-ARGON-2025-BR',
        endOfLifeDisposition: 'DISCARD_BY_CARRIER_WITH_CERTIFICATE',
        dispositionStatus: 'PENDING_CLOSEOUT_RETURN',
        dispositionEvidenceRef: null,
        blocksStudyCloseOut: true,
        requiresCalibration: true,
        requiredMagnitude: 'ROTACAO_RPM + TEMPERATURA_CADEIA_FRIA',
        baseCalibrationMonths: 12,
        studyOverrideCalibrationMonths: null,
        effectiveCalibrationMonths: 12,
        assignedCalibrationSupplierId: 'cal-sup-002',
        assignedCalibrationSupplierName: 'CaliLab Metrologia Científica & Cadeia Fria',
        lastCalibratedDate: '2026-04-10',
        nextCalibrationDueDate: '2027-04-10',
        daysUntilCalibrationDue: 166,
        oneMonthWarningTriggerDate: '2027-03-10',
        isOneMonthWarningActive: false,
        oneMonthWarningMessage:
          'Aviso automático de 1 mês antes (T-30d) programado para 10/03/2027.',
        calibrationSemaphore: 'VALID',
        blockedForStudyUse: false,
        blockReason: null,
        lastCertificateNumber: 'CERT-RBC-CAL0312-2026-412',
        lastRecordedByEmail: 'laura.ferreira@giyus.com.br',
        lastVerifiedByEmail: 'syllas@giyus.com.br',
        equipmentInvoiceNumber: 'NF-e Remessa Labcorp 000.118.904',
        equipmentInvoiceAccessKey: '35250260840055000131550010001189041011890419',
        equipmentInvoiceIssuedDate: '2025-02-14',
        calibrationServiceInvoiceNumber: 'NFS-e 2026/000991',
        calibrationFeeBrl: 1120,
        compatibleCalibrationSuppliers: [
          {
            supplierId: 'cal-sup-002',
            tradeName: 'CaliLab Metrologia Científica & Cadeia Fria',
            rbcAccreditationCode: 'CAL-0312 (CGCRE / INMETRO — Ultra-Baixa Temperatura -80°C)',
            coverageScope: 'PR_SC_RS',
            slaTurnaroundDays: 3,
            estimatedCalibrationFeeBrl: 1120,
            compatibilityReason: 'Acreditado RBC para Rotação (RPM) + Temperatura · SLA 3d',
          },
        ],
        resourceCategory: 'EQUIPMENT',
        loanReturnDueDate: '2026-11-28',
        daysUntilLoanReturnDue: 33,
        returnTriggerRule:
          'Devolver ou Descartar via Transportadora Credenciada em até 30 dias após LPLV do estudo ARGON (Prazo: 28/11/2026)',
        disposalExecutionMode: 'SHIPPED_VIA_CARRIER',
        disposalCertificateRequirement: 'WITH_DISPOSAL_CERTIFICATE',
        sponsorOrderedDestruction: false,
        canConvertDestructionToSiteUse: false,
        siteInterestStatus: 'NOT_APPLICABLE',
      },
      {
        id: 'eq-007',
        assetTag: 'LAB-ICON-THERM-08',
        name: 'Datalogger & Termohigrômetro Digital Calibrado ICON Central Lab (TempTale Ultra)',
        typeCode: 'THERMOHYGROMETER',
        manufacturerModel: 'Sensitech / TempTale Ultra BLE',
        serialNumber: 'SN-TTU-990412',
        siteCode: 'CWB-01',
        roomLocation: 'Farmácia de Pesquisa Clínica — Geladeira 2°C a 8°C',
        criticality: 'HIGH',
        ownershipKind: 'CENTRAL_LAB_LOAN',
        ownerEntityName: 'ICON Central Laboratories (Estudo ADEPT-4)',
        linkedStudyCode: 'ADEPT-4',
        isDepreciableInGiyusBalance: false,
        acquisitionValueBrl: 0,
        residualValueBrl: 0,
        usefulLifeMonths: 24,
        elapsedMonths: 19,
        monthlyDepreciationBrl: 0,
        accumulatedDepreciationBrl: 0,
        netBookValueBrl: 0,
        hourlyBudgetCostBrl: 0,
        insuredCustodyValueBrl: 4800,
        loanPolicyOrContractRef: 'ICON Lab Manual Section 9 — Descarte Local Autorizado s/ Laudo',
        endOfLifeDisposition: 'DISCARD_BY_SITE_WITHOUT_CERTIFICATE',
        dispositionStatus: 'PENDING_CLOSEOUT_RETURN',
        dispositionEvidenceRef: null,
        blocksStudyCloseOut: false,
        requiresCalibration: true,
        requiredMagnitude: 'TEMPERATURA_CADEIA_FRIA',
        baseCalibrationMonths: 12,
        studyOverrideCalibrationMonths: null,
        effectiveCalibrationMonths: 12,
        assignedCalibrationSupplierId: 'cal-sup-002',
        assignedCalibrationSupplierName: 'CaliLab Metrologia Científica & Cadeia Fria',
        lastCalibratedDate: '2026-06-01',
        nextCalibrationDueDate: '2027-06-01',
        daysUntilCalibrationDue: 218,
        oneMonthWarningTriggerDate: '2027-05-01',
        isOneMonthWarningActive: false,
        oneMonthWarningMessage:
          'Aviso automático de 1 mês antes (T-30d) programado para 01/05/2027.',
        calibrationSemaphore: 'VALID',
        blockedForStudyUse: false,
        blockReason: null,
        lastCertificateNumber: 'CERT-RBC-CAL0312-2026-580',
        lastRecordedByEmail: 'eduarda.lazzarotto@giyus.com.br',
        lastVerifiedByEmail: 'syllas@giyus.com.br',
        equipmentInvoiceNumber: 'NF-e Remessa ICON 000.044.118',
        equipmentInvoiceAccessKey: '35250560840055000131550010000441181004411807',
        equipmentInvoiceIssuedDate: '2025-05-19',
        calibrationServiceInvoiceNumber: 'NFS-e 2026/001210',
        calibrationFeeBrl: 490,
        compatibleCalibrationSuppliers: [
          {
            supplierId: 'cal-sup-002',
            tradeName: 'CaliLab Metrologia Científica & Cadeia Fria',
            rbcAccreditationCode: 'CAL-0312 (CGCRE / INMETRO — Ultra-Baixa Temperatura -80°C)',
            coverageScope: 'PR_SC_RS',
            slaTurnaroundDays: 3,
            estimatedCalibrationFeeBrl: 490,
            compatibilityReason: 'Acreditado RBC para Termohigrômetros e Dataloggers · SLA 3d',
          },
        ],
        resourceCategory: 'EQUIPMENT',
        loanReturnDueDate: '2026-11-12',
        daysUntilLoanReturnDue: 17,
        returnTriggerRule:
          'Laboratório Central autorizou descarte local pelo próprio Centro GIYUS SEM exigência de certificado externo até 12/11/2026',
        disposalExecutionMode: 'PERFORMED_BY_SITE',
        disposalCertificateRequirement: 'WITHOUT_DISPOSAL_CERTIFICATE',
        sponsorOrderedDestruction: false,
        canConvertDestructionToSiteUse: false,
        siteInterestStatus: 'NOT_APPLICABLE',
      },
      {
        id: 'eq-008',
        assetTag: 'COM-PERSIST-SPIRO-03',
        name: 'Espirômetro Clínico Portátil USB Vitalograph Pneumotrac c/ Seringa 3L',
        typeCode: 'SPIROMETER',
        manufacturerModel: 'Vitalograph / Pneumotrac Spirotrac 6',
        serialNumber: 'SN-VIT-663891',
        siteCode: 'CWB-01',
        roomLocation: 'Consultório de Função Pulmonar 01',
        criticality: 'CRITICAL',
        ownershipKind: 'SPONSOR_STUDY_LOAN',
        ownerEntityName:
          'GSK / IQVIA Estudo PERSIST (Patrocinador Mandou p/ Destruição → Elegível p/ Uso no Centro)',
        linkedStudyCode: 'PERSIST',
        isDepreciableInGiyusBalance: false,
        acquisitionValueBrl: 0,
        residualValueBrl: 0,
        usefulLifeMonths: 60,
        elapsedMonths: 20,
        monthlyDepreciationBrl: 0,
        accumulatedDepreciationBrl: 0,
        netBookValueBrl: 0,
        hourlyBudgetCostBrl: 0,
        insuredCustodyValueBrl: 22800,
        loanPolicyOrContractRef:
          'Carta Patrocinador GSK #DESTR-2026-09 (Autoriza Destruição Local ou Incorporação de Equipamento pelo Centro)',
        endOfLifeDisposition: 'SPONSOR_DESTRUCTION_CONVERTED_TO_SITE_USE',
        dispositionStatus: 'PENDING_CLOSEOUT_RETURN',
        dispositionEvidenceRef: null,
        blocksStudyCloseOut: true,
        requiresCalibration: true,
        requiredMagnitude: 'FLUXO_ESPIROMETRIA_FENO',
        baseCalibrationMonths: 12,
        studyOverrideCalibrationMonths: 6,
        effectiveCalibrationMonths: 6,
        assignedCalibrationSupplierId: 'cal-sup-003',
        assignedCalibrationSupplierName: 'BioMed Engenharia Clínica & Espirometria/FeNO',
        lastCalibratedDate: '2026-06-15',
        nextCalibrationDueDate: '2026-12-15',
        daysUntilCalibrationDue: 50,
        oneMonthWarningTriggerDate: '2026-11-15',
        isOneMonthWarningActive: false,
        oneMonthWarningMessage:
          'Aviso automático de 1 mês antes (T-30d) programado para 15/11/2026.',
        calibrationSemaphore: 'VALID',
        blockedForStudyUse: false,
        blockReason: null,
        lastCertificateNumber: 'CERT-RBC-CAL0589-2026-441',
        lastRecordedByEmail: 'mariana.linero@giyus.com.br',
        lastVerifiedByEmail: 'syllas@giyus.com.br',
        equipmentInvoiceNumber: 'NF-e Remessa Comodato 000.088.310',
        equipmentInvoiceAccessKey: '35250343854116000109550010000883101008831055',
        equipmentInvoiceIssuedDate: '2025-03-22',
        calibrationServiceInvoiceNumber: 'NFS-e 2026/001305',
        calibrationFeeBrl: 1090,
        compatibleCalibrationSuppliers: [
          {
            supplierId: 'cal-sup-003',
            tradeName: 'BioMed Engenharia Clínica & Espirometria/FeNO',
            rbcAccreditationCode:
              'CAL-0589 (CGCRE / INMETRO — Fluxo, Volume Respiratório e Analisadores)',
            coverageScope: 'SUL_SUDESTE',
            slaTurnaroundDays: 7,
            estimatedCalibrationFeeBrl: 1090,
            compatibilityReason: 'Especialista RBC em Espirômetros e Seringas 3L (SLA 7d)',
          },
        ],
        resourceCategory: 'EQUIPMENT',
        loanReturnDueDate: '2026-11-20',
        daysUntilLoanReturnDue: 25,
        returnTriggerRule:
          'Patrocinador mandou para destruição ao fim do estudo (20/11/2026) — Por ser EQUIPAMENTO e haver interesse do Centro GIYUS, pode ser aproveitado na rotina clínica!',
        disposalExecutionMode: 'RETAINED_FOR_SITE_USE',
        disposalCertificateRequirement: 'WITHOUT_DISPOSAL_CERTIFICATE',
        sponsorOrderedDestruction: true,
        canConvertDestructionToSiteUse: true,
        siteInterestStatus: 'ELIGIBLE_FOR_SITE_USE_EQUIPMENT_ONLY',
      },
    ],
    stockForecasts: [
      {
        id: 'stk-fc-001',
        itemCode: 'KIT-PK-EPOCA-V2',
        itemName: 'Kit Coleta Farmacocinética Seriada 8h + Criotubos -80°C (Protocolo EPOCA)',
        categoryCode: 'LAB_KITS',
        studyCode: 'EPOCA',
        lotNumber: 'LOT-LABCORP-2026-09A',
        supplierName: 'Labcorp Central Laboratory',
        currentUnits: 14,
        reservedUnitsNext30d: 11,
        projectedAvailableUnits: 3,
        minSafetyUnits: 12,
        expirationDate: '2026-11-18',
        daysUntilExpiration: 23,
        estimatedDaysUntilStockout: 9,
        advanceNoticeDays: 30,
        shortageReason: 'SHORTAGE_BOTH_CRITICAL',
        severity: 'CRITICAL',
        alertHeadline:
          'VAI FALTAR POR VENCIMENTO E FALTA DE UNIDADES (Restam 3 un. livres e lote vence em 23 dias — 18/11/2026)',
        recommendedAction:
          'Solicitar reposição imediata de +30 kits ao Labcorp Central Lab (SLA 5 dias) para cobrir as 11 visitas agendadas em novembro.',
        replenishmentStatus: 'ALERT_OPEN',
        lastReplenishmentOrderCode: null,
        unitCostBrl: 410,
      },
      {
        id: 'stk-fc-002',
        itemCode: 'INS-FENO-BOCAL-300',
        itemName: 'Kit Bocais Descartáveis com Filtro Bacteriano FeNO NIOX VERO',
        categoryCode: 'RESPIRATORY_CONSUMABLES',
        studyCode: 'PERSIST',
        lotNumber: 'LOT-CIRC-8841',
        supplierName: 'Circassia / BioMed Distribuidora',
        currentUnits: 18,
        reservedUnitsNext30d: 14,
        projectedAvailableUnits: 4,
        minSafetyUnits: 20,
        expirationDate: '2027-06-30',
        daysUntilExpiration: 247,
        estimatedDaysUntilStockout: 12,
        advanceNoticeDays: 30,
        shortageReason: 'SHORTAGE_BY_LOW_UNITS',
        severity: 'CRITICAL',
        alertHeadline:
          'VAI FALTAR POR FALTA DE UNIDADES EM 12 DIAS (Saldo projetado: 4 un. vs. Mínimo de Segurança: 20 un.)',
        recommendedAction:
          'Emitir pedido preventivo de +50 bocais FeNO com antecedência para não bloquear exames de espirometria/FeNO do estudo PERSIST.',
        replenishmentStatus: 'ALERT_OPEN',
        lastReplenishmentOrderCode: null,
        unitCostBrl: 185,
      },
      {
        id: 'stk-fc-003',
        itemCode: 'KIT-ECG-SOLAIR-EL',
        itemName: 'Eletrodos Pré-Gelados Ag/AgCl 12 Derivações Clario ELI 280 (Caixa c/ 100)',
        categoryCode: 'CARDIAC_CONSUMABLES',
        studyCode: 'SOLAIR',
        lotNumber: 'LOT-MORTARA-2026-11',
        supplierName: 'Clario ERT / MedLab Suprimentos',
        currentUnits: 32,
        reservedUnitsNext30d: 8,
        projectedAvailableUnits: 24,
        minSafetyUnits: 15,
        expirationDate: '2026-11-24',
        daysUntilExpiration: 29,
        estimatedDaysUntilStockout: 29,
        advanceNoticeDays: 30,
        shortageReason: 'SHORTAGE_BY_EXPIRATION',
        severity: 'WARNING_ADVANCE',
        alertHeadline:
          '⏰ VAI FALTAR POR VENCIMENTO DO LOTE EM 29 DIAS (Vence em 24/11/2026 — Aviso Antecipado T-30d)',
        recommendedAction:
          'Programar substituição do lote LOT-MORTARA-2026-11 antes de 24/11/2026 (regra FEFO bloqueia uso na data de vencimento).',
        replenishmentStatus: 'ALERT_OPEN',
        lastReplenishmentOrderCode: null,
        unitCostBrl: 290,
      },
      {
        id: 'stk-fc-004',
        itemCode: 'KIT-DERM-ADEPT4',
        itemName: 'Kit Biópsia Cutânea Punch 4mm & Tubos RNA-Later (Protocolo ADEPT-4)',
        categoryCode: 'LAB_KITS',
        studyCode: 'ADEPT-4',
        lotNumber: 'LOT-ICON-2027-04',
        supplierName: 'ICON Central Lab / PPD Thermo Fisher',
        currentUnits: 45,
        reservedUnitsNext30d: 6,
        projectedAvailableUnits: 39,
        minSafetyUnits: 15,
        expirationDate: '2027-08-15',
        daysUntilExpiration: 293,
        estimatedDaysUntilStockout: 95,
        advanceNoticeDays: 30,
        shortageReason: 'HEALTHY_STOCK',
        severity: 'OK',
        alertHeadline: 'Estoque Seguro (39 unidades livres após reservas · Validade 15/08/2027)',
        recommendedAction:
          'Nenhuma ação imediata. Monitoramento automático ativo com aviso 30 dias antes.',
        replenishmentStatus: 'STOCK_OK',
        lastReplenishmentOrderCode: 'PED-REP-2026-041',
        unitCostBrl: 520,
      },
      {
        id: 'stk-fc-005',
        itemCode: 'SKU-OPEX-PAPEL-HIG-CWB',
        itemName:
          'Papel Higiênico Folha Dupla Institucional & Papel Toalha Interfolha (Fardo 64 rolos — Uso Geral GIYUS Curitiba)',
        categoryCode: 'OPEX_HIGIENE_UNIDADE',
        studyCode: 'OPEX-CWB (Sem Contrato)',
        lotNumber: 'LOT-HIG-2026-10',
        supplierName: 'LimpSul Distribuidora Higiene Curitiba / Atacadão Empresas',
        currentUnits: 6,
        reservedUnitsNext30d: 5,
        projectedAvailableUnits: 1,
        minSafetyUnits: 8,
        expirationDate: '2029-12-31',
        daysUntilExpiration: 1161,
        estimatedDaysUntilStockout: 9,
        advanceNoticeDays: 30,
        shortageReason: 'SHORTAGE_BY_LOW_UNITS',
        severity: 'WARNING_ADVANCE',
        alertHeadline:
          'VAI FALTAR POR CONSUMO INTERNO EM 9 DIAS (Insumo NÃO atrelado a contrato — Custo Próprio de Operação GIYUS Curitiba)',
        recommendedAction:
          'Acionar fornecedor local LimpSul Curitiba (R$ 58,90/fardo CIF) ou Atacadão Empresas Curitiba. Despesa alocada 100% no Custo Próprio de Operação da GIYUS Curitiba (OPEX-CWB-001), sem vínculo com contrato de patrocinador.',
        replenishmentStatus: 'ALERT_OPEN',
        lastReplenishmentOrderCode: null,
        unitCostBrl: 58.9,
        costAllocationKind: 'CUSTO_OPERACIONAL_UNIDADE',
        costAllocationLabel:
          'Custo Próprio de Operação — GIYUS Curitiba Research Center (Não Atrelado a Contrato)',
      },
      {
        id: 'stk-fc-med-001',
        itemCode: 'MED-EMERG-ADREN-1MG',
        itemName:
          'Medicamento de Disponibilidade Obrigatória: Epinefrina (Adrenalina) 1mg/mL Injetável + Hidrocortisona 500mg + Difenidramina 50mg (Kit Emergência Anafilática / Carro de Parada)',
        categoryCode: 'MANDATORY_MEDICATION',
        studyCode: 'TODOS OS ESTUDOS & CARRO DE PARADA (GIYUS Curitiba)',
        lotNumber: 'LOT-CRIST-2026-11',
        supplierName: 'MedHosp Distribuidora Farmacêutica Curitiba / Cristália Hospitalar',
        currentUnits: 12,
        reservedUnitsNext30d: 4,
        projectedAvailableUnits: 8,
        minSafetyUnits: 10,
        expirationDate: '2026-11-18',
        daysUntilExpiration: 22,
        estimatedDaysUntilStockout: 22,
        advanceNoticeDays: 30,
        shortageReason: 'SHORTAGE_BY_EXPIRATION',
        severity: 'CRITICAL',
        alertHeadline:
          'MEDICAMENTO DE DISPONIBILIDADE OBRIGATÓRIA VENCE EM 22 DIAS (18/11/2026) — PROIBIDO VENCER SEM LOTE SUBSTITUTO EM ESTOQUE!',
        recommendedAction:
          'REGRA ZERO VENCIMENTO: Emitir IMEDIATAMENTE a compra antecipada do novo lote (validade >= 2028) com MedHosp Curitiba (entrega expressa 4h · R$ 148,00/kit) ANTES de 18/11/2026. Nunca podemos ficar sem este medicamento disponível no centro.',
        replenishmentStatus: 'ALERT_OPEN',
        lastReplenishmentOrderCode: null,
        unitCostBrl: 148,
        costAllocationKind: 'CUSTO_OPERACIONAL_UNIDADE',
        costAllocationLabel:
          'Custo Próprio de Operação — GIYUS Curitiba + Trava de Segurança Clínica de Todos os Estudos',
        isMandatoryAvailabilityMedication: true,
        preExpirationPurchaseAlertStage: 'ALERTA_COMPRA_T30_1MES',
        preExpirationPurchaseTriggerDate:
          '2026-10-19 (Alerta disparado 30 dias antes do vencimento p/ Compra Antecipada)',
        recommendedPurchaseSupplierLocality:
          'MedHosp Distribuidora Farmacêutica Curitiba/PR (Fornecedor Local · SLA 4h · R$ 148,00/kit) ou Mafra Hospitalar SP (Nacional · SLA 24h · R$ 134,50/kit)',
      },
      {
        id: 'stk-fc-med-002',
        itemCode: 'MED-RESG-SALBUT-100',
        itemName:
          'Medicamento de Resgate Obrigatório: Sulfato de Salbutamol 100mcg/dose Spray HFA + Brometo de Ipratrópio (Prova Broncodilatadora / FeNO / Resgate Asma)',
        categoryCode: 'MANDATORY_MEDICATION',
        studyCode: 'SOLAIR, PERSIST, EPOCA',
        lotNumber: 'LOT-GSK-2026-11B',
        supplierName: 'OncoProd / Mafra Hospitalar Sul & FarmaSul Local Curitiba',
        currentUnits: 15,
        reservedUnitsNext30d: 10,
        projectedAvailableUnits: 5,
        minSafetyUnits: 12,
        expirationDate: '2026-11-25',
        daysUntilExpiration: 29,
        estimatedDaysUntilStockout: 29,
        advanceNoticeDays: 30,
        shortageReason: 'SHORTAGE_BY_EXPIRATION',
        severity: 'CRITICAL',
        alertHeadline:
          '⏰ALERTA DE COMPRA ANTES DE VENCER (Faltam 29 dias · Vence em 25/11/2026) — Medicamento de Resgate de Disponibilidade Obrigatória',
        recommendedAction:
          'Disparar pedido de compra hoje (T-30d antes do vencimento) com OncoProd/Mafra Sul (R$ 34,80/frasco · SLA 24h) para que o novo lote entre na farmácia antes do vencimento em 25/11/2026.',
        replenishmentStatus: 'ALERT_OPEN',
        lastReplenishmentOrderCode: null,
        unitCostBrl: 34.8,
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationLabel:
          'Atrelado aos Contratos SOLAIR, PERSIST e EPOCA (100% Reembolsável Pass-Through)',
        isMandatoryAvailabilityMedication: true,
        preExpirationPurchaseAlertStage: 'ALERTA_COMPRA_T30_1MES',
        preExpirationPurchaseTriggerDate:
          '2026-10-26 (Alerta disparado 30 dias antes do vencimento p/ Compra Antecipada)',
        recommendedPurchaseSupplierLocality:
          'OncoProd / Mafra Hospitalar Curitiba/PR (Regional Sul · SLA 24h · R$ 34,80/frasco) ou FarmaSul Hospitalar Curitiba (Local · SLA 4h · R$ 37,50/frasco)',
      },
    ],
    calibrationTimeline: [
      {
        id: 'tl-001',
        assetId: 'eq-002',
        assetTag: 'COM-EPOCA-FENO-01',
        assetName: 'Analisador de Óxido Nítrico Exalado (FeNO) Circassia NIOX VERO',
        eventDate: '2026-10-20',
        eventKind: 'ONE_MONTH_WARNING_T30',
        statusBadge: 'ACTIVE_WARNING_30D',
        title: 'Aviso Automático 1 Mês Antes (T-30d) — Calibração Vence em 20/11/2026',
        description:
          'O motor de Gerenciamento de Recursos disparou o alerta com 30 dias de antecedência para agendar a calibração de Fluxo/FeNO com BioMed RBC (SLA 7d) ou Trescal RBC (SLA 5d).',
        supplierName: 'BioMed Engenharia Clínica & Espirometria/FeNO',
        rbcAccreditationCode: 'CAL-0589 (CGCRE / INMETRO)',
        certificateNumber: 'CERT-RBC-CAL0589-2026-309',
        equipmentInvoiceNumber: 'NF-e Remessa Comodato 000.091.402',
        calibrationServiceInvoiceNumber: 'NFS-e 2026/001190',
        documentValueBrl: 1180,
        recordedByEmail: 'mariana.linero@giyus.com.br',
        verifiedByEmail: 'syllas@giyus.com.br',
        maintenanceCategory: 'CALIBRATION_RBC',
        maintenanceWorkOrderCode: null,
        downtimeHours: 0,
        partsReplacedSummary: null,
        postMaintenanceRecalibrationRequired: false,
      },
      {
        id: 'tl-002',
        assetId: 'eq-004',
        assetTag: 'PAT-HLD-0019',
        assetName: 'Centrífuga Refrigerada Clínica Eppendorf 5702R (-9°C a +40°C)',
        eventDate: '2026-10-15',
        eventKind: 'ONE_MONTH_WARNING_T30',
        statusBadge: 'ACTIVE_WARNING_30D',
        title: 'Aviso Automático 1 Mês Antes (T-30d) — Calibração Vence em 15/11/2026',
        description:
          'Alerta emitido 1 mês antes do vencimento (15/11/2026). Fornecedores RBC habilitados para RPM + Temperatura: CaliLab RBC (Curitiba, SLA 3d) e Trescal Brasil RBC (Nacional, SLA 5d).',
        supplierName: 'CaliLab Metrologia Científica & Cadeia Fria',
        rbcAccreditationCode: 'CAL-0312 (CGCRE / INMETRO)',
        certificateNumber: 'CERT-RBC-CAL0312-2025-889',
        equipmentInvoiceNumber: 'NF-e 000.003.119 (Série 1)',
        calibrationServiceInvoiceNumber: 'NFS-e 2025/004412',
        documentValueBrl: 1120,
        recordedByEmail: 'laura.ferreira@giyus.com.br',
        verifiedByEmail: 'syllas@giyus.com.br',
        maintenanceCategory: 'CALIBRATION_RBC',
        maintenanceWorkOrderCode: null,
        downtimeHours: 0,
        partsReplacedSummary: null,
        postMaintenanceRecalibrationRequired: false,
      },
      {
        id: 'tl-maint-prev-001',
        assetId: 'eq-001',
        assetTag: 'PAT-CWB-0001',
        assetName: 'Ultra-Freezer Criogênico -86°C Thermo Scientific TSX',
        eventDate: '2026-09-28',
        eventKind: 'PREVENTIVE_MAINTENANCE',
        statusBadge: 'PREVENTIVE_OK',
        title: 'Manutenção Preventiva Programada Executada (OS-PREV-2026-412)',
        description:
          '[MANUTENÇÃO PREVENTIVA] Limpeza técnica do condensador de duplo estágio, substituição preventiva do filtro frontal antipoeira, inspeção de vedação das 4 portas internas e teste de bateria de backup da telemetria 24h.',
        supplierName: 'Thermo Scientific Assistência Técnica Autorizada & CaliLab',
        rbcAccreditationCode: 'Plano Preventivo NBR IEC 60601',
        certificateNumber: 'LAUDO-PREV-2026-412',
        equipmentInvoiceNumber: 'NF-e 000.004.812 (Série 1)',
        calibrationServiceInvoiceNumber: 'NFS-e Manutenção 2026/002104',
        documentValueBrl: 890,
        recordedByEmail: 'eduarda.lazzarotto@giyus.com.br',
        verifiedByEmail: 'syllas@giyus.com.br',
        maintenanceCategory: 'PREVENTIVE_MAINTENANCE',
        maintenanceWorkOrderCode: 'OS-PREV-2026-412',
        downtimeHours: 0,
        partsReplacedSummary:
          'Filtro antipoeira frontal Thermo TSX + Anel de vedação de silicone secundário + Limpeza de condensador (0h de parada)',
        postMaintenanceRecalibrationRequired: false,
      },
      {
        id: 'tl-003',
        assetId: 'eq-003',
        assetTag: 'COM-SOLAIR-ECG-02',
        assetName: 'Eletrocardiógrafo Digital 12 Derivações Clario / ERT ELI 280',
        eventDate: '2026-09-10',
        eventKind: 'NEXT_CALIBRATION_DUE',
        statusBadge: 'EXPIRED_BLOCKED',
        title: 'Calibração Semestral Vencida — Trava Clínica 423 Ativada',
        description:
          'Aviso de 1 mês enviado em 10/08/2026. Como o vencimento em 10/09/2026 foi ultrapassado, o equipamento entrou em BlockedForStudyUse = true até registro de novo certificado RBC.',
        supplierName: 'Trescal Brasil Metrologia RBC',
        rbcAccreditationCode: 'CAL-0148 (CGCRE / INMETRO)',
        certificateNumber: 'CERT-RBC-CAL0148-2026-102',
        equipmentInvoiceNumber: 'NF-e Remessa Comodato 000.077.219',
        calibrationServiceInvoiceNumber: 'NFS-e 2026/000814',
        documentValueBrl: 980,
        recordedByEmail: 'elessandra@giyus.com.br',
        verifiedByEmail: 'syllas@giyus.com.br',
        maintenanceCategory: 'CALIBRATION_RBC',
        maintenanceWorkOrderCode: null,
        downtimeHours: 0,
        partsReplacedSummary: null,
        postMaintenanceRecalibrationRequired: false,
      },
      {
        id: 'tl-maint-emerg-001',
        assetId: 'eq-004',
        assetTag: 'PAT-HLD-0019',
        assetName: 'Centrífuga Refrigerada Clínica Eppendorf 5702R (-9°C a +40°C)',
        eventDate: '2026-08-19',
        eventKind: 'EMERGENCY_MAINTENANCE',
        statusBadge: 'EMERGENCY_RESOLVED',
        title: 'Manutenção Emergencial / Corretiva Executada (OS-EMERG-2026-089)',
        description:
          '[MANUTENÇÃO EMERGENCIAL] Chamado corretivo urgente por alerta de vibração/erro de leitura de RPM no rotor A-4-38. Substituído sensor óptico Hall de rotação + amortecedores elastoméricos do motor, seguido de aferição RBC de confirmação.',
        supplierName: 'Eppendorf Service Brasil / CaliLab Engenharia Clínica',
        rbcAccreditationCode: 'Atendimento Emergencial SLA 4h + Aferição RBC',
        certificateNumber: 'LAUDO-EMERG-2026-089',
        equipmentInvoiceNumber: 'NF-e 000.003.119 (Série 1)',
        calibrationServiceInvoiceNumber: 'NFS-e Emergencial 2026/001745',
        documentValueBrl: 1840,
        recordedByEmail: 'laura.ferreira@giyus.com.br',
        verifiedByEmail: 'syllas@giyus.com.br',
        maintenanceCategory: 'EMERGENCY_MAINTENANCE',
        maintenanceWorkOrderCode: 'OS-EMERG-2026-089',
        downtimeHours: 4,
        partsReplacedSummary:
          'Sensor óptico Hall de tacômetro (P/N EPP-5702-HALL) + Kit 3 buchas amortecedoras do rotor + Aferição RBC pós-reparo',
        postMaintenanceRecalibrationRequired: true,
      },
      {
        id: 'tl-004',
        assetId: 'eq-001',
        assetTag: 'PAT-CWB-0001',
        assetName: 'Ultra-Freezer Criogênico -86°C Thermo Scientific TSX',
        eventDate: '2026-07-15',
        eventKind: 'CALIBRATION_CERTIFIED',
        statusBadge: 'EXECUTED_VERIFIED',
        title: 'Calibração RBC Executada & Verificada (CERT-RBC-CAL0312-2026-771)',
        description:
          'Calibração térmica em 5 pontos (-86°C a -70°C) com incerteza expandida ±0,4°C aprovada. Próximo aviso de 1 mês programado para 15/12/2026.',
        supplierName: 'CaliLab Metrologia Científica & Cadeia Fria',
        rbcAccreditationCode: 'CAL-0312 (CGCRE / INMETRO)',
        certificateNumber: 'CERT-RBC-CAL0312-2026-771',
        equipmentInvoiceNumber: 'NF-e 000.004.812 (Série 1)',
        calibrationServiceInvoiceNumber: 'NFS-e 2026/001482',
        documentValueBrl: 1450,
        recordedByEmail: 'eduarda.lazzarotto@giyus.com.br',
        verifiedByEmail: 'syllas@giyus.com.br',
        maintenanceCategory: 'CALIBRATION_RBC',
        maintenanceWorkOrderCode: null,
        downtimeHours: 0,
        partsReplacedSummary: null,
        postMaintenanceRecalibrationRequired: false,
      },
      {
        id: 'tl-maint-prev-002',
        assetId: 'eq-005',
        assetTag: 'PAT-HLD-0031',
        assetName: 'No-Break Online Dupla Conversão APC Smart-UPS 3000VA (Banco Cadeia Fria)',
        eventDate: '2026-06-10',
        eventKind: 'PREVENTIVE_MAINTENANCE',
        statusBadge: 'PREVENTIVE_OK',
        title: 'Manutenção Preventiva Trimestral de Autonomia & Baterias (OS-PREV-2026-298)',
        description:
          '[MANUTENÇÃO PREVENTIVA] Teste de descarga controlada para validar autonomia mínima de 4h do Ultra-Freezer -86°C, medição de condutância das baterias seladas VRLA e limpeza dos ventiladores inversores.',
        supplierName: 'Engenharia Clínica GIYUS / Schneider APC Partner',
        rbcAccreditationCode: 'Preventiva Trimestral Cadeia Fria',
        certificateNumber: 'LAUDO-PREV-2026-298',
        equipmentInvoiceNumber: 'NF-e 000.005.901 (Série 1)',
        calibrationServiceInvoiceNumber: 'NFS-e Manutenção 2026/001312',
        documentValueBrl: 620,
        recordedByEmail: 'mariana.linero@giyus.com.br',
        verifiedByEmail: 'syllas@giyus.com.br',
        maintenanceCategory: 'PREVENTIVE_MAINTENANCE',
        maintenanceWorkOrderCode: 'OS-PREV-2026-298',
        downtimeHours: 0,
        partsReplacedSummary:
          'Teste de autonomia de banco VRLA + Reaperto de conectores DC + Limpeza de coolers (0h de parada — Bypass ativo)',
        postMaintenanceRecalibrationRequired: false,
      },
      {
        id: 'tl-005',
        assetId: 'eq-001',
        assetTag: 'PAT-CWB-0001',
        assetName: 'Ultra-Freezer Criogênico -86°C Thermo Scientific TSX',
        eventDate: '2024-10-15',
        eventKind: 'EQUIPMENT_NFE_ENTRY',
        statusBadge: 'EXECUTED_VERIFIED',
        title: 'Aquisição do Equipamento & Nota Fiscal Eletrônica (NF-e 000.004.812)',
        description:
          'Incorporação patrimonial GIYUS Curitiba Research Center (R$ 84.000,00) com vida útil de 120 meses e certificado de fábrica anexado.',
        supplierName: 'Thermo Fisher Scientific Brasil',
        rbcAccreditationCode: 'ISO 9001 / Fábrica',
        certificateNumber: 'CERT-FAB-TSX-2024',
        equipmentInvoiceNumber: 'NF-e 000.004.812 (Série 1)',
        calibrationServiceInvoiceNumber: null,
        documentValueBrl: 84000,
        recordedByEmail: 'financeiro@giyus.com.br',
        verifiedByEmail: 'syllas@giyus.com.br',
        maintenanceCategory: 'ACQUISITION_NFE',
        maintenanceWorkOrderCode: null,
        downtimeHours: 0,
        partsReplacedSummary: null,
        postMaintenanceRecalibrationRequired: false,
      },
    ],
    resourceMailboxEvents: [
      {
        id: 'mbx-res-001',
        mailboxAddress: 'contratos@giyus.com.br',
        senderEmail: 'clinical.contracts@astrazeneca.com',
        subject: '[CTA-EPOCA-ANEXO-B] Mandatory Site Equipment List & SoA Resource Schedule',
        receivedDate: '2026-10-26 14:20',
        studyCode: 'EPOCA',
        resourceMovementKind: 'CONCILIACAO_NOMENCLATURA',
        contractOrEmailResourceName:
          'Fractional Exhaled Nitric Oxide (FeNO) Electrochemical Breath Analyzer',
        alreadyOwnedUnderDifferentName: true,
        matchedGiyusAssetOrSkuCode: 'COM-EPOCA-FENO-01',
        matchedGiyusResourceName: 'Analisador de Óxido Nítrico Exalado (FeNO) Circassia NIOX VERO',
        nomenclatureSimilarityPercent: 98,
        whatIsHappeningSummary:
          "CONCILIAÇÃO DE NOMENCLATURA: O contrato chama de 'Fractional Exhaled Nitric Oxide (FeNO) Electrochemical Breath Analyzer', mas JÁ TEMOS este recurso na GIYUS cadastrado como 'Analisador de Óxido Nítrico Exalado (FeNO) Circassia NIOX VERO' (COM-EPOCA-FENO-01). Evita compra ou comodato duplicado.",
        recommendedResourceIfMissing:
          "Usar 'Analisador FeNO Circassia NIOX VERO (COM-EPOCA-FENO-01)' já alocado na Sala de Espirometria + Bocais 'INS-FENO-BOCAL-300'.",
        reconciliationStatus: 'PENDING_RECONCILIATION',
        financialOrContractDocRef: 'CTA-EPOCA-2026-ANEXO-B (Cláusula 4.2)',
        reconciledByEmail: null,
      },
      {
        id: 'mbx-res-002',
        mailboxAddress: 'contratos@giyus.com.br',
        senderEmail: 'site.contracts@clario-ert.com',
        subject: '[SOLAIR Protocol Amend #3] Urgent V4 12-Lead ECG Requirement & Device Readiness',
        receivedDate: '2026-10-26 11:05',
        studyCode: 'SOLAIR',
        resourceMovementKind: 'FALTA_USAR_SUBSTITUTO',
        contractOrEmailResourceName:
          '12-Lead Digital Electrocardiograph Clario ELI 280 (Dedicated Unit)',
        alreadyOwnedUnderDifferentName: true,
        matchedGiyusAssetOrSkuCode: 'COM-SOLAIR-ECG-02',
        matchedGiyusResourceName: 'Eletrocardiógrafo Digital 12 Derivações Clario / ERT ELI 280',
        nomenclatureSimilarityPercent: 96,
        whatIsHappeningSummary:
          "FALTA / BLOQUEIO TÉCNICO (USAR SUBSTITUTO): O e-mail do contrato exige ECG 12 Derivações para a Visita V4 amanhã, porém a unidade dedicada 'COM-SOLAIR-ECG-02' está bloqueada por calibração vencida (Trava 423). O sistema aponta imediatamente qual recurso substituto do centro deve ser usado.",
        recommendedResourceIfMissing:
          "USAR IMEDIATAMENTE: Eletrocardiógrafo Reserva Próprio GIYUS 'PAT-CWB-0014 (Mortara ELI 250c 12 Derivações — Certificado RBC Vigente CERT-RBC-CAL0148-2026-512)' + Eletrodos 'KIT-ECG-SOLAIR-EL' enquanto Trescal RBC calibra o titular.",
        reconciliationStatus: 'FALLBACK_RESOURCE_ACTIVE',
        financialOrContractDocRef: 'Aditivo Operacional SOLAIR-AMD3 / Ordem RBC Trescal',
        reconciledByEmail: 'mariana.linero@giyus.com.br',
      },
      {
        id: 'mbx-res-003',
        mailboxAddress: 'financeiro@giyus.com.br',
        senderEmail: 'nfe.logistics@labcorp.com',
        subject:
          '[DANFE / NF-e 000.119.840] Remessa de Kits Laboratoriais PK/PD e Tubos Vacutainer — Estudo EPOCA',
        receivedDate: '2026-10-25 17:42',
        studyCode: 'EPOCA',
        resourceMovementKind: 'ENTRADA',
        contractOrEmailResourceName:
          'Labcorp Central Lab PK/PD Venipuncture Collection Kit Box (EDTA K2 + Gel SST)',
        alreadyOwnedUnderDifferentName: true,
        matchedGiyusAssetOrSkuCode: 'KIT-LAB-EPOCA-PK',
        matchedGiyusResourceName:
          'Kit Coleta Sanguínea PK/PD Tubos Vacutainer EDTA + Gel Separador',
        nomenclatureSimilarityPercent: 97,
        whatIsHappeningSummary:
          "ENTRADA DE RECURSO (NF-e no Financeiro): Recebida NF-e de Remessa 000.119.840 (+30 kits, Lote LOT-LC-2027-09) no e-mail financeiro@giyus.com.br. Conciliado automaticamente com a nomenclatura interna 'KIT-LAB-EPOCA-PK' para zerar o risco de falta em novembro.",
        recommendedResourceIfMissing:
          "Caso o desembaraço atrase >48h, usar lote de contingência autorizado pelo Labcorp: 'Tubos BD Vacutainer EDTA K2 4mL + SST II Advance (SKU: INS-BD-CONT-09)' do Almoxarifado GIYUS.",
        reconciliationStatus: 'RECONCILED_LINKED',
        financialOrContractDocRef: 'NF-e Remessa 000.119.840 / AWB World Courier 77182901',
        reconciledByEmail: 'financeiro@giyus.com.br',
      },
      {
        id: 'mbx-res-004',
        mailboxAddress: 'financeiro@giyus.com.br',
        senderEmail: 'reverse.logistics@labcorp.com',
        subject:
          '[AWB-DHL-992817410 & Termo Devolução] Coleta Reversa Centrífuga Refrigerada Beckman Allegra X-30R',
        receivedDate: '2026-10-25 09:15',
        studyCode: 'EPOCA',
        resourceMovementKind: 'SAIDA',
        contractOrEmailResourceName:
          'Beckman Coulter Allegra X-30R Refrigerated Benchtop Centrifuge (Central Lab Loan)',
        alreadyOwnedUnderDifferentName: true,
        matchedGiyusAssetOrSkuCode: 'LAB-EPOCA-CENT-05',
        matchedGiyusResourceName:
          'Centrífuga Refrigerada de Bancada Beckman Coulter Allegra X-30R (Laboratório Central)',
        nomenclatureSimilarityPercent: 99,
        whatIsHappeningSummary:
          'SAÍDA PROGRAMADA (Devolução ao Laboratório): O e-mail em financeiro@giyus.com.br agenda a saída/devolução em 12/11/2026 (em 17 dias) via transportadora especializada com certificado de descontaminação. Após a saída, as amostras de follow-up passarão para a centrífuga própria do centro.',
        recommendedResourceIfMissing:
          "Após a saída de LAB-EPOCA-CENT-05 em 12/11/2026, USAR: 'Centrífuga Refrigerada Clínica Eppendorf 5702R (PAT-HLD-0019)' própria do Centro GIYUS.",
        reconciliationStatus: 'RECONCILED_LINKED',
        financialOrContractDocRef: 'NF-e Devolução Comodato + AWB-DHL-992817410',
        reconciledByEmail: 'laura.ferreira@giyus.com.br',
      },
      {
        id: 'mbx-res-005',
        mailboxAddress: 'contratos@giyus.com.br',
        senderEmail: 'asset.disposition@sanofi-aventis.com',
        subject:
          '[Carta de Destruição / Doação ao Centro] CO2 Incubator Memmert INCO108 — Estudo SOLAIR',
        receivedDate: '2026-10-24 16:30',
        studyCode: 'SOLAIR',
        resourceMovementKind: 'SAIDA',
        contractOrEmailResourceName:
          'Memmert INCO108 Precision Cell Culture CO2 Incubator (S/N MM-INCO-7721)',
        alreadyOwnedUnderDifferentName: true,
        matchedGiyusAssetOrSkuCode: 'LAB-SOLAIR-INC-07',
        matchedGiyusResourceName:
          'Incubadora Microbiológica CO2 Memmert INCO108 (Enviada p/ Destruição pelo Patrocinador)',
        nomenclatureSimilarityPercent: 100,
        whatIsHappeningSummary:
          'SAÍDA P/ DESTRUIÇÃO OU USO PELO CENTRO: O patrocinador enviou carta em contratos@giyus.com.br mandando para destruição local (com certificado), mas autorizando conversão para uso próprio do Centro GIYUS (exclusivo por ser EQUIPAMENTO).',
        recommendedResourceIfMissing:
          'Interesse do Centro GIYUS confirmado: Converter destruição em Incorporação Patrimonial gratuita para uso em novos estudos de Dermatologia/Imunologia.',
        reconciliationStatus: 'PENDING_RECONCILIATION',
        financialOrContractDocRef: 'CARTA-PATROC-DESTR-SOLAIR-2026-09',
        reconciledByEmail: null,
      },
      {
        id: 'mbx-res-006',
        mailboxAddress: 'contratos@giyus.com.br',
        senderEmail: 'contracts.latam@abbvie.com',
        subject: '[CTA-ADEPT4-SEC-09] Biological Sample Cryo-Preservation Facility Requirements',
        receivedDate: '2026-10-23 10:12',
        studyCode: 'ADEPT-4',
        resourceMovementKind: 'CONCILIACAO_NOMENCLATURA',
        contractOrEmailResourceName:
          'Ultra-Low Temperature Mechanical Cryo-Freezer (-80°C to -86°C) with 24/7 Telemetry',
        alreadyOwnedUnderDifferentName: true,
        matchedGiyusAssetOrSkuCode: 'PAT-CWB-0001',
        matchedGiyusResourceName: 'Ultra-Freezer Criogênico -86°C Thermo Scientific TSX',
        nomenclatureSimilarityPercent: 99,
        whatIsHappeningSummary:
          "CONCILIAÇÃO DE NOMENCLATURA: O contrato ADEPT-4 pede 'Ultra-Low Temperature Mechanical Cryo-Freezer (-80°C to -86°C)', que JÁ TEMOS no Centro GIYUS com a nomenclatura 'Ultra-Freezer Criogênico -86°C Thermo Scientific TSX' (PAT-CWB-0001, calibrado RBC).",
        recommendedResourceIfMissing:
          "Em caso de manutenção preventiva do PAT-CWB-0001, usar Ultra-Freezer de Backup 'PAT-CWB-0009 (Haier DW-86L578J -86°C — Sala Crio 02)'.",
        reconciliationStatus: 'RECONCILED_LINKED',
        financialOrContractDocRef: 'CTA-ADEPT4-2026-SEC-09',
        reconciledByEmail: 'syllas@giyus.com.br',
      },
    ],
    externalPartnerUsers: [
      {
        id: 'ext-usr-001',
        displayName: 'Roberto Almeida (Portal B2B World Courier)',
        email: 'clinical.br@worldcourier.com',
        externalDomain: 'worldcourier.com',
        organizationName: 'World Courier do Brasil (IATA Cold Chain)',
        partnerRoleKind: 'EXTERNAL_SUPPLIER_B2B',
        rlsBoundResource: 'Supplier: SUP-WC-IATA (Somente suas Cotações, OS e NF-e)',
        allowedModules: [
          'Fornecedores B2B (Seus Dados)',
          'Comparar Cotações (Responder RFP)',
          'Financeiro (Status NF-e)',
        ],
        blockedCapabilitiesSummary:
          'Bloqueado: Cotações de concorrentes, Margem de Contratos GIYUS e Outros Centros',
        status: 'ACTIVE_B2B',
      },
      {
        id: 'ext-usr-002',
        displayName: 'Dra. Helena Prado (Portal B2B Grupo Fleury)',
        email: 'pesquisaclinica@grupofleury.com.br',
        externalDomain: 'grupofleury.com.br',
        organizationName: 'Fleury Medicina e Saúde (Imagem & Patologia)',
        partnerRoleKind: 'EXTERNAL_SUPPLIER_B2B',
        rlsBoundResource: 'Supplier: SUP-FLEURY-IMG (Exames RECIST 1.1 & Laudos)',
        allowedModules: [
          'Fornecedores B2B (Seus Dados)',
          'Comparar Cotações (Responder RFP)',
          'Ordens de Serviço Clínico',
        ],
        blockedCapabilitiesSummary:
          'Bloqueado: Outros fornecedores, Dossiês 360º e Caixas @giyus.com.br',
        status: 'ACTIVE_B2B',
      },
      {
        id: 'ext-usr-003',
        displayName: 'Amanda Contracts (IQVIA Site Activation)',
        email: 'amanda.contracts@iqvia.com',
        externalDomain: 'iqvia.com',
        organizationName: 'IQVIA RDS Brasil Ltda. (CRO)',
        partnerRoleKind: 'EXTERNAL_CRO_SPONSOR',
        rlsBoundResource: 'Estudos: EPOCA & PERSIST (Kits Consignados & Invoices)',
        allowedModules: [
          'Estoque Clínico & Kits (Lotes EPOCA/PERSIST)',
          'Financeiro (Faturas & Reembolso Pass-Through)',
        ],
        blockedCapabilitiesSummary:
          'Bloqueado: Simulação IA de Margem Interna, Outros Patrocinadores e Holding',
        status: 'ACTIVE_B2B',
      },
      {
        id: 'ext-usr-004',
        displayName: 'Prof. Dr. Nelson Augusto Rosário Filho (PI Externo UFPR)',
        email: 'nelson.rosario@ufpr.br',
        externalDomain: 'ufpr.br',
        organizationName: 'Hospital de Clínicas UFPR / Pesquisa Clínica',
        partnerRoleKind: 'EXTERNAL_PI_PARTNER',
        rlsBoundResource: 'Estudo: SOLAIR (Parecer Clínico SoA & Delegation Log DOA)',
        allowedModules: [
          'Estudos (Parecer Técnico SoA)',
          'Delegation Log (DOA)',
          'Estoque Clínico do Estudo',
        ],
        blockedCapabilitiesSummary:
          'Bloqueado: Administração da Holding GIYUS, Outros Estudos e Folha/Custos',
        status: 'ACTIVE_B2B',
      },
      {
        id: 'ext-usr-005',
        displayName: 'Carlos Fiscal (Bicount Contabilidade Externa)',
        email: 'fiscal01@bicount.com.br',
        externalDomain: 'bicount.com.br',
        organizationName: 'Bicount Contabilidade & BPO Fiscal',
        partnerRoleKind: 'EXTERNAL_ACCOUNTING_BPO',
        rlsBoundResource: 'Escopo Fiscal Somente-Leitura (NF-e, Retenções 14,25% e VCL)',
        allowedModules: [
          'Financeiro Integrado (Conciliação AP/AR & Gross-up)',
          'Patrimônio (Livro de Depreciação VCL)',
        ],
        blockedCapabilitiesSummary:
          'Bloqueado: Aprovação de Pedidos de Compra, Prontuários e Edição de Contratos',
        status: 'ACTIVE_B2B',
      },
    ],
    inventoryItems: [
      {
        id: 'inv-001',
        skuCode: 'KIT-PK-EPOCA-80C',
        itemName: 'Kit Criogênico Coleta PK Seriada 8h (Criotubos 2mL + Rack Código de Barras)',
        categoryCode: 'PHARMACY_KITS',
        ownershipKind: 'STUDY_CONSIGNED_KIT',
        linkedStudyCode: 'EPOCA',
        preferredSupplierName: 'Labcorp Central Laboratory',
        batchNumber: 'LOT-LC-2026-09A',
        expirationDate: '2027-04-30',
        storageCondition: 'AMBIENTE_15_25C',
        currentQty: 42,
        reorderPointQty: 15,
        unitCostBrl: 420,
        totalStockValueBrl: 17640,
        fefoStatus: 'OK_ESTOQUE',
      },
      {
        id: 'inv-002',
        skuCode: 'INS-FENO-NIOX-MOUTH',
        itemName: 'Bocal Descartável Filtrado para Analisador FeNO NIOX VERO (Pacote c/ 50 un)',
        categoryCode: 'PHARMACY_KITS',
        ownershipKind: 'GIYUS_OWNED_STOCK',
        linkedStudyCode: 'PERSIST',
        preferredSupplierName: 'Labcorp Central Laboratory',
        batchNumber: 'LOT-FENO-2026-11',
        expirationDate: '2026-12-15',
        storageCondition: 'AMBIENTE_15_25C',
        currentQty: 6,
        reorderPointQty: 10,
        unitCostBrl: 1850,
        totalStockValueBrl: 11100,
        fefoStatus: 'ALERTA_PONTO_PEDIDO',
      },
      {
        id: 'inv-003',
        skuCode: 'REAG-PBMC-SOLAIR-28C',
        itemName: 'Meio de Separação Celular Ficoll-Paque PLUS p/ Biomarcadores (Estudo SOLAIR)',
        categoryCode: 'PHARMACY_KITS',
        ownershipKind: 'GIYUS_OWNED_STOCK',
        linkedStudyCode: 'SOLAIR',
        preferredSupplierName: 'World Courier (IATA Cold Chain)',
        batchNumber: 'LOT-CYT-8821',
        expirationDate: '2026-11-20',
        storageCondition: 'REFRIGERADO_2_8C',
        currentQty: 14,
        reorderPointQty: 8,
        unitCostBrl: 980,
        totalStockValueBrl: 13720,
        fefoStatus: 'RISCO_VALIDADE_FEFO',
      },
      {
        id: 'inv-004',
        skuCode: 'SKU-OPEX-PAPEL-HIG-CWB',
        itemName:
          'Papel Higiênico Folha Dupla Institucional & Papel Toalha Interfolha (Fardo 64 rolos — Uso Geral GIYUS Curitiba)',
        categoryCode: 'OPEX_HIGIENE_UNIDADE',
        ownershipKind: 'GIYUS_OWNED_STOCK',
        linkedStudyCode: null,
        preferredSupplierName: 'LimpSul Distribuidora de Higiene & Limpeza Curitiba',
        batchNumber: 'LOT-HIG-2026-10',
        expirationDate: '2029-12-31',
        storageCondition: 'AMBIENTE_15_25C',
        currentQty: 6,
        reorderPointQty: 8,
        unitCostBrl: 58.9,
        totalStockValueBrl: 353.4,
        fefoStatus: 'ALERTA_PONTO_PEDIDO',
        costAllocationKind: 'CUSTO_OPERACIONAL_UNIDADE',
        costAllocationTargetLabel:
          'Custo Próprio de Operação — GIYUS Curitiba Research Center (OPEX-CWB · Não Atrelado a Contrato)',
      },
      {
        id: 'inv-005',
        skuCode: 'MED-EMERG-ADREN-1MG',
        itemName:
          'Medicamento de Disponibilidade Obrigatória: Epinefrina (Adrenalina) 1mg/mL + Hidrocortisona 500mg + Difenidramina 50mg (Kit Emergência Anafilática / Carro de Parada)',
        categoryCode: 'MANDATORY_MEDICATION',
        ownershipKind: 'GIYUS_OWNED_STOCK',
        linkedStudyCode: null,
        preferredSupplierName: 'MedHosp Distribuidora Farmacêutica Curitiba (SLA 4h)',
        batchNumber: 'LOT-CRIST-2026-11',
        expirationDate: '2026-11-18',
        storageCondition: 'AMBIENTE_15_25C',
        currentQty: 12,
        reorderPointQty: 10,
        unitCostBrl: 148,
        totalStockValueBrl: 1776,
        fefoStatus: 'RISCO_VALIDADE_FEFO',
        costAllocationKind: 'CUSTO_OPERACIONAL_UNIDADE',
        costAllocationTargetLabel:
          'Custo Próprio de Operação GIYUS Curitiba + Trava Obrigatória de Infusão (Sem Contrato)',
        isMandatoryAvailabilityMedication: true,
        daysUntilExpiration: 22,
        preExpirationPurchaseAlertMessage:
          'ALERTA DE COMPRA ANTES DE VENCER (Vence em 22 dias — 18/11/2026): Proibido deixar vencer sem substituto! Comprar novo lote c/ MedHosp Curitiba (R$ 148,00 · Entrega 4h).',
      },
      {
        id: 'inv-006',
        skuCode: 'MED-RESG-SALBUT-100',
        itemName:
          'Medicamento de Resgate Obrigatório: Sulfato de Salbutamol 100mcg/dose Spray HFA + Brometo de Ipratrópio (Prova Pulmonar / FeNO)',
        categoryCode: 'MANDATORY_MEDICATION',
        ownershipKind: 'GIYUS_OWNED_STOCK',
        linkedStudyCode: 'SOLAIR / PERSIST / EPOCA',
        preferredSupplierName: 'OncoProd / Mafra Hospitalar Sul (SLA 24h)',
        batchNumber: 'LOT-GSK-2026-11B',
        expirationDate: '2026-11-25',
        storageCondition: 'AMBIENTE_15_25C',
        currentQty: 15,
        reorderPointQty: 12,
        unitCostBrl: 34.8,
        totalStockValueBrl: 522,
        fefoStatus: 'RISCO_VALIDADE_FEFO',
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel:
          'Atrelado aos Contratos SOLAIR, PERSIST e EPOCA (100% Reembolsável Pass-Through)',
        isMandatoryAvailabilityMedication: true,
        daysUntilExpiration: 29,
        preExpirationPurchaseAlertMessage:
          '⏰ ALERTA DE COMPRA ANTES DE VENCER (T-30d · Vence em 29 dias — 25/11/2026): Emitir pedido de compra preventivo hoje c/ OncoProd Sul (R$ 34,80/frasco).',
      },
    ],
    quoteComparisons: [
      {
        id: 'rfq-001',
        rfqCode: 'MAPA-COT-2026-041',
        title:
          'Ressuprimento Automático FEFO: 12 Kits Bocais FeNO NIOX VERO + Sensores (Estudo PERSIST — Pass-Through)',
        categoryCode: 'PHARMACY_KITS',
        studyCode: 'PERSIST',
        quantityRequested: 12,
        contractCeilingTotalBrl: 26400,
        status: 'OPEN_COMPARING',
        awardedSupplierId: null,
        awardedSupplierName: null,
        generatedPoCode: null,
        savingsVsContractCeilingBrl: 4200,
        savingsPercent: 15.9,
        proposals: [
          {
            supplierId: '0199b900-0000-7000-8000-000000000003',
            supplierName: 'Labcorp Central Laboratory',
            submittedByExternalEmail: 'studysupport.latam@labcorp.com',
            unitPriceBrl: 1750,
            freightColdChainBrl: 1200,
            totalTcoBrl: 22200,
            leadTimeDays: 4,
            paymentTerms: '45 DDL (Conciliado c/ Reembolso IQVIA)',
            qaStatus: 'Qualified',
            isRecommendedBestOption: true,
          },
          {
            supplierId: '0199b900-0000-7000-8000-000000000001',
            supplierName: 'World Courier (IATA Cold Chain)',
            submittedByExternalEmail: 'clinical.br@worldcourier.com',
            unitPriceBrl: 1890,
            freightColdChainBrl: 1650,
            totalTcoBrl: 24330,
            leadTimeDays: 3,
            paymentTerms: '30 DDL',
            qaStatus: 'Qualified',
            isRecommendedBestOption: false,
          },
          {
            supplierId: '0199b900-0000-7000-8000-000000000002',
            supplierName: 'Fleury Medicina e Saúde (Suprimentos Diagnósticos)',
            submittedByExternalEmail: 'pesquisaclinica@grupofleury.com.br',
            unitPriceBrl: 1980,
            freightColdChainBrl: 1400,
            totalTcoBrl: 25160,
            leadTimeDays: 6,
            paymentTerms: '28 DDL',
            qaStatus: 'Qualified',
            isRecommendedBestOption: false,
          },
        ],
      },
      {
        id: 'rfq-002',
        rfqCode: 'MAPA-COT-2026-039',
        title:
          'Pacote de 12 Exames de Imagem Baseline (TC Tórax + RM RECIST 1.1) — Protocolo SOLAIR',
        categoryCode: 'IMAGING_LAB',
        studyCode: 'SOLAIR',
        quantityRequested: 12,
        contractCeilingTotalBrl: 39600,
        status: 'AWARDED_CONVERTED_TO_PO',
        awardedSupplierId: '0199b900-0000-7000-8000-000000000002',
        awardedSupplierName: 'Fleury Medicina e Saúde (Imagem & Patologia)',
        generatedPoCode: 'PC-2026-0102',
        savingsVsContractCeilingBrl: 5400,
        savingsPercent: 13.6,
        proposals: [
          {
            supplierId: '0199b900-0000-7000-8000-000000000002',
            supplierName: 'Fleury Medicina e Saúde (Imagem & Patologia)',
            submittedByExternalEmail: 'pesquisaclinica@grupofleury.com.br',
            unitPriceBrl: 2850,
            freightColdChainBrl: 0,
            totalTcoBrl: 34200,
            leadTimeDays: 2,
            paymentTerms: '45 DDL',
            qaStatus: 'Qualified',
            isRecommendedBestOption: true,
          },
          {
            supplierId: '0199b900-0000-7000-8000-000000000003',
            supplierName: 'Labcorp Partner Imaging Network',
            submittedByExternalEmail: 'studysupport.latam@labcorp.com',
            unitPriceBrl: 3150,
            freightColdChainBrl: 450,
            totalTcoBrl: 38250,
            leadTimeDays: 5,
            paymentTerms: '30 DDL',
            qaStatus: 'Qualified',
            isRecommendedBestOption: false,
          },
        ],
      },
    ],
    financialLedger: [
      {
        id: 'fin-001',
        documentCode: 'NFE-44910 / PC-2026-0101',
        entryKind: 'AP_SUPPLIER_PURCHASE',
        counterpartyName: 'Labcorp Central Laboratory',
        studyCode: 'EPOCA',
        linkedPoOrVisitRef: 'PC-2026-0101 · Lote LOT-LC-2026-09A',
        description:
          'Conta a Pagar (Fornecedor/Compra): Kits Criogênicos PK — Conferência 3-Way Match (Pedido ↔ Estoque ↔ NF-e)',
        amountBrl: 18450,
        isPassThroughReimbursable: true,
        threeWayMatchStatus: 'MATCHED_3_WAY',
        dueDate: '2026-11-15',
        settlementStatus: 'SCHEDULED',
      },
      {
        id: 'fin-002',
        documentCode: 'INV-EPOCA-2026-104',
        entryKind: 'AR_STUDY_INVOICE',
        counterpartyName: 'AstraZeneca / IQVIA RDS Brasil',
        studyCode: 'EPOCA',
        linkedPoOrVisitRef: 'Visitas V1-V4 + Reembolso Pass-Through PC-2026-0101',
        description:
          'Conta a Receber (Contrato/Estudo): Fatura Clínica + Reembolso Integral Pass-Through (Gross-up 14,25% Lucro Real)',
        amountBrl: 92400,
        isPassThroughReimbursable: true,
        threeWayMatchStatus: 'RECONCILED_PTAX',
        dueDate: '2026-11-10',
        settlementStatus: 'SETTLED_PAID',
      },
      {
        id: 'fin-003',
        documentCode: 'PROVISAO-PC-2026-0102',
        entryKind: 'AP_SUPPLIER_PURCHASE',
        counterpartyName: 'Fleury Medicina e Saúde (Imagem & Patologia)',
        studyCode: 'SOLAIR',
        linkedPoOrVisitRef: 'PC-2026-0102 · MAPA-COT-2026-039',
        description:
          'Conta a Pagar (Fornecedor/Compra): Pacote 12 Exames TC/RM RECIST 1.1 adjudicado no Mapa Comparativo',
        amountBrl: 34200,
        isPassThroughReimbursable: true,
        threeWayMatchStatus: 'PENDING_RECEIPT_OR_NFE',
        dueDate: '2026-12-05',
        settlementStatus: 'SCHEDULED',
      },
    ],
    harness2State: {
      surfaceName: 'giyus_hybrid (Google Research Harness² · c1..c8)',
      domain: 'clinical_trial_contracts_and_supply_chain',
      evolutionGeneration: 14,
      alwaysOnTokensUsed: 11420,
      alwaysOnTokensCap: 28000,
      activeSkills: [
        'soa_reconstruction',
        'bcb_macroeconomic_defense',
        'crio_rewk_and_kpis',
        'acesse_legal_clauses',
        'giyus_brand_voice',
        'counterparty_persona_profiling',
      ],
      activeSubAgents: [
        'clinical_budget_auditor',
        'legal_cta_redliner',
        'macroeconomic_fx_specialist',
      ],
      componentsC1ToC8: [
        {
          code: 'c1',
          fileOrDir: 'giyus_harness/systemprompt.md',
          title: 'Prompt Mestre GIYUS v2.0 & Auto-Trigger',
          roleInPlatform:
            'Define a Regra de Ouro max(Ofertado, Ref_GIYUS/PTAX) e aciona automaticamente as skills em Contratos, Cotações e Financeiro.',
          tokenOrItemMetric: '2.850 chars · Always-On',
          validationStatus: 'VALIDATED_ON_SURFACE',
        },
        {
          code: 'c2',
          fileOrDir: 'giyus_harness/tool_descriptions/tool_guidance.md',
          title: 'Guia de Ferramentas Determinísticas & OSINT',
          roleInPlatform:
            'Orienta quando invocar bcb_rates.py (PTAX/Selic/Capital de Giro), ctgov_osint.py e conferência 3-Way Match.',
          tokenOrItemMetric: '1.920 chars · Always-On',
          validationStatus: 'VALIDATED_ON_SURFACE',
        },
        {
          code: 'c3',
          fileOrDir: 'giyus_harness/LongTermMEMORY.md',
          title: 'Memória Evolutiva de Longo Prazo (LongTermMEMORY.md)',
          roleInPlatform:
            'Guarda permanentemente Dados Societários/Bancários (C6/Cayman/CEF), Parâmetros (STUP, SCLO, AR01, REWK, KPI), Perfis CRO e Regras [GLOBAL_ALL] de Contratos, Estoque FEFO, Cotações e Calibração.',
          tokenOrItemMetric: '10.654 chars · Always-On',
          validationStatus: 'VALIDATED_ON_SURFACE',
        },
        {
          code: 'c4',
          fileOrDir: 'giyus_harness/sub_agents/',
          title: 'Subagentes Especialistas c/ Modo Validado',
          roleInPlatform:
            'Executa auditoria isolada de Grade SoA, Redline Jurídico CTA, Defesa Cambial PTAX e Mapa Comparativo de 3 Cotações.',
          tokenOrItemMetric: '3 subagentes ativos',
          validationStatus: 'VALIDATED_ON_SURFACE',
        },
        {
          code: 'c5',
          fileOrDir: 'giyus_harness/scripts/',
          title: 'Scripts Determinísticos Compilados',
          roleInPlatform:
            'bcb_rates.py (Séries SGS BCB), ctgov_osint.py (ClinicalTrials.gov APIv2) e postmortem_check.py.',
          tokenOrItemMetric: '3 scripts Python validados',
          validationStatus: 'VALIDATED_ON_SURFACE',
        },
        {
          code: 'c6',
          fileOrDir: 'giyus_harness/skills/',
          title: 'Skills Modulares sob Demanda (must_match)',
          roleInPlatform:
            'soa_reconstruction, bcb_macroeconomic_defense, crio_rewk_and_kpis, acesse_legal_clauses, giyus_brand_voice, counterparty_persona_profiling.',
          tokenOrItemMetric: '6 skills indexadas',
          validationStatus: 'VALIDATED_ON_SURFACE',
        },
        {
          code: 'c7',
          fileOrDir: 'giyus_harness/guardrails.md',
          title: 'Os 8 Vetos Inegociáveis da Holding GIYUS',
          roleInPlatform:
            'Bloqueia redução de preço, prazo > Net30 sem recomposição BCB, Overhead < 30%, teto de Screen Failure, compra sem 3 cotações/SoD e equipamento c/ calibração vencida.',
          tokenOrItemMetric: '8 vetos ativos · Always-On',
          validationStatus: 'VALIDATED_ON_SURFACE',
        },
        {
          code: 'c8',
          fileOrDir: '.codex/hooks.json + library/EXPERIENCE.md',
          title: 'Hook Post-Mortem (JEV Belay Gate) & Biblioteca EXPERIENCE.md',
          roleInPlatform:
            'Valida todo patch via HarnessTree.validate e grava o histórico evolutivo imutável de lições aprendidas no longo prazo.',
          tokenOrItemMetric: '14 gerações auditadas',
          validationStatus: 'VALIDATED_ON_SURFACE',
        },
      ],
      longTermMemoryExcerpt: [
        '# MEMÓRIA DE LONGO PRAZO GIYUS (c3 — LongTermMEMORY.md)',
        '- [HOLDING GIYUS] CNPJ 69.308.788/0001-02 | C6 Bank Brasil (336) & C6 Cayman (SWIFT CSIXKYKY) | Fee CSC 10% | Overhead 30% | Gross-up Lucro Real 14,25%.',
        '- [PARÂMETROS OFICIAIS] STUP: R$ 30.000 (Net10) | SCLO: R$ 20.000 | AR01 Guarda 25a: R$ 54.189,38 | REWK CRIO->EDC: R$ 350/h | KPI K1..K5: R$ 30.000.',
        '- [GLOBAL_ALL · CONTRATOS & SOA] Em todo estudo, cruzar SoA e notas de rodapé com a grade: ECG triplicado, PK seriada, FeNO e REWK R$ 350/h.',
        '- [GLOBAL_ALL · FORNECEDORES, ESTOQUE FEFO & 3 COTAÇÕES] Toda requisição Pass-Through exige Mapa Comparativo de 3 fornecedores qualificados (QA SoD) respeitando o teto do contrato e validade FEFO.',
        '- [GLOBAL_ALL · EQUIPAMENTOS & CALIBRAÇÃO RBC] Avaliar cadeia fria -80°C, custo/hora depreciado GIYUS e calibração RBC min(Base, Estudo) antes de fechar o budget e bloquear Close-Out se comodato pendente.',
      ].join('\n'),
      experienceLogExcerpt: [
        '# EXPERIENCE.md — Biblioteca Evolutiva Google Harness² (library/clinical_trial_contracts/)',
        '- [Gen #12 · EPOCA/Hcor] Lição promovida p/ c3 LongTermMEMORY.md: proteger PK seriada 8h + cláusula EQ-BCB-01.',
        '- [Gen #13 · PERSIST/IQVIA] Lição promovida p/ c3 LongTermMEMORY.md + c6 counterparty_persona_profiling: reembolso integral de bocais FeNO NIOX VERO via Pass-Through.',
        '- [Gen #14 · ERP INTEGRADO] Lição promovida p/ c3 LongTermMEMORY.md: conciliação 3-Way Match (Pedido de Compra ↔ Estoque FEFO ↔ NF-e Fornecedor B2B) alimenta a Margem Realizada do Estudo.',
      ].join('\n'),
    },
    supplierGovernanceDossiers: [
      {
        id: 'SGD-001',
        supplierCode: 'SUP-CAL-01',
        tradeName: 'Metrologia Sul RBC',
        legalName: 'METROLOGIA SUL CALIBRACAO E ENSAIOS RBC LTDA',
        cnpj: '08.419.220/0001-74',
        supplierTypeCode: 'CALIBRATION_RBC',
        supplierTypeLabel: 'Calibração RBC/INMETRO & Qualificação Térmica',
        geographicScope: 'LOCAL_CIDADE_UF',
        coverageSummary:
          'Fornecedor Local — Atende Curitiba/PR e Região Metropolitana (Raio 60 km · In-Loco)',
        requestedByName: 'Eng. Marcos Viana (Engenharia Clínica)',
        requestedByEmail: 'marcos.viana@giyus.com.br',
        approvedByEmail: 'qualidade.qa@giyus.com.br',
        requestingCenterName: 'GIYUS Curitiba Research Center (Unidade Matriz)',
        linkedItemsSummary: [
          'EQP-ULT-001 · Ultrafreezer Thermo -80°C (Calibração Anual RBC)',
          'EQP-CEN-002 · Centrífuga Refrigerada Eppendorf 5810R (Tacometria + Térmica)',
          'EQP-ECG-003 · Eletrocardiógrafo Mortara ELI 280 (Simulador Cardíaco)',
          'Estudos vinculados: EPOCA, SOLAIR, PERSIST',
        ],
        bestPaymentTerms:
          'Net 45 DDL (PIX Corporativo ou Boleto) · Sem valor mínimo por chamado in-loco',
        earlyPaymentDiscountPolicy:
          'Desconto de 5% para antecipação em Net 10 DDL ou pacote trimestral >= 5 equipamentos',
        pendingPaymentAmountBrl: 1890,
        pendingInvoicesCount: 1,
        paymentPendencyStatus: 'BLOCKED_MISSING_CERTIFICATE',
        paymentPendencyReason:
          'NFS-e #2026-0884 (R$ 1.890,00) bloqueada preventivamente no Gate 3-Way Match: aguardando upload do Certificado RBC assinado com selo INMETRO referente à Centrífuga EQP-CEN-002.',
        observations:
          'Atendimento in-loco em Curitiba sem necessidade de retirar equipamento do centro. Plantão técnico 24x7 para cadeia fria.',
        contractNomenclatureAlias:
          "No Contrato EPOCA consta como 'Local RBC Calibration Vendor' -> Conciliado na Caixa financeiro@giyus.com.br com 'METROLOGIA SUL CALIBRACAO E ENSAIOS RBC LTDA'",
        backupHomologatedSupplierName:
          'Trescal Brasil Metrologia RBC (SUP-CAL-02 · Atendimento Nacional em até 24h)',
        regulatoryDocAlertT30Days:
          'OK · Acreditação CGCRE/INMETRO CAL-0418 e CND Federal válidas até Set/2027',
        isPassThroughRebillableToSponsor: true,
        passThroughSponsorNotes:
          '100% Reembolsável Pass-Through em estudos patrocinados quando vinculado a equipamento exclusivo do protocolo.',
        slaOnTimePercent: 98.4,
      },
      {
        id: 'SGD-002',
        supplierCode: 'SUP-ENG-01',
        tradeName: 'ClinicalEng Brasil',
        legalName: 'CLINICALENG ENGENHARIA HOSPITALAR E MANUTENCAO S/A',
        cnpj: '14.772.901/0001-19',
        supplierTypeCode: 'MAINTENANCE_CLINICAL_ENG',
        supplierTypeLabel: 'Manutenção Preventiva & Emergencial (Engenharia Clínica)',
        geographicScope: 'REGIONAL_SUL_SUDESTE',
        coverageSummary: 'Atende Sul e Sudeste (PR, SC, RS, SP, RJ, MG · SLA Emergencial 4h)',
        requestedByName: 'Dra. Helena Costa (Coordenadora Clínica)',
        requestedByEmail: 'helena.costa@giyus.com.br',
        approvedByEmail: 'diretoria.holding@giyus.com.br',
        requestingCenterName: 'Clínica Dr. Herberto Chong — Filial / Matriz (Curitiba/PR)',
        linkedItemsSummary: [
          'EQP-ULT-001 · Ultrafreezer -80°C (Preventiva Semestral + Emergencial Compressor)',
          'EQP-CEN-002 · Centrífuga Eppendorf 5810R (Troca de Escovas / Vedação)',
          'EQP-DEF-005 · Desfibrilador Bifásico Zoll M-Series (Teste de Carga 200J)',
          'Estudos vinculados: EPOCA, SOLAIR, ADEPT-4',
        ],
        bestPaymentTerms:
          'Net 30 DDL após aprovação do Relatório Técnico de Manutenção (OS) + Peças com 12 meses de garantia',
        earlyPaymentDiscountPolicy:
          'Isenção de taxa de deslocamento emergencial (R$ 650,00) para chamados abertos dentro do contrato guarda-chuva',
        pendingPaymentAmountBrl: 2650,
        pendingInvoicesCount: 1,
        paymentPendencyStatus: 'SCHEDULED_ON_TIME',
        paymentPendencyReason:
          'NFS-e #2026-3310 (R$ 2.650,00 · Manutenção Emergencial Relé Térmico Ultrafreezer OS-MAN-2026-041) auditada com Laudo de Recalibração Pós-Reparo anexado. Programada para 19/11/2026.',
        observations:
          'Obrigatoriedade de emitir Ordem de Serviço identificando se foi Preventiva ou Emergencial e se exige recalibração RBC após intervenção.',
        contractNomenclatureAlias:
          "Nas caixas contratos@giyus.com.br e financeiro@giyus.com.br consta como 'ClinicalEng / Thermo Authorized Service' -> Conciliado com CNPJ 14.772.901/0001-19",
        backupHomologatedSupplierName:
          'Arkmeds / EngClin Sul Homologada (SUP-ENG-02 · Backup SLA 6h)',
        regulatoryDocAlertT30Days:
          'ALERTA T-27 DIAS: ART CREA/PR do Responsável Técnico vence em 23/11/2026 — Notificação automática enviada ao fornecedor',
        isPassThroughRebillableToSponsor: false,
        passThroughSponsorNotes:
          'Preventiva de equipamentos próprios GIYUS é absorvida pelo Overhead do Centro; reparo de equipamento em comodato do patrocinador é 100% Pass-Through.',
        slaOnTimePercent: 96.8,
      },
      {
        id: 'SGD-003',
        supplierCode: 'SUP-LAB-01',
        tradeName: 'Fleury / Pardini Diagnósticos B2B',
        legalName: 'INSTITUTO HERMES PARDINI S/A (GRUPO FLEURY)',
        cnpj: '19.378.769/0001-76',
        supplierTypeCode: 'LAB_DIAGNOSTICS_IMAGING',
        supplierTypeLabel: 'Exames Laboratoriais, Anatomia Patológica & Imagem Diagnóstica',
        geographicScope: 'NACIONAL_BRASIL',
        coverageSummary:
          'Atende Brasil Todo (27 UFs · Cobertura Nacional Completa + Coleta Domiciliar/Hospitalar)',
        requestedByName: 'Dr. Herberto Chong (Investigador Principal - PI)',
        requestedByEmail: 'herberto.chong@giyus.com.br',
        approvedByEmail: 'qualidade.qa@giyus.com.br',
        requestingCenterName:
          'Holding GIYUS (Todos os Centros Brasil — Curitiba/PR e São Paulo/SP)',
        linkedItemsSummary: [
          'PROC-LAB-001 · Painel Bioquímico & Hematológico Completo (Triagem V0/V1)',
          'PROC-IMG-004 · Tomografia Computadorizada Torácica Alta Resolução (RECIST 1.1)',
          'PROC-ECO-002 · Ecocardiograma Transtorácico com Strain (Segurança Cardíaca)',
          'Estudos vinculados: EPOCA, SOLAIR, PERSIST, ADEPT-4, ARGON',
        ],
        bestPaymentTerms:
          'Net 60 DDL Consolidado Holding · Faturamento mensal único no dia 05 + Tabela Corporativa CBHPM -22%',
        earlyPaymentDiscountPolicy:
          'Desconto adicional de 3,5% para liquidação em Net 15 DDL via TED/PIX Corporativo',
        pendingPaymentAmountBrl: 4350,
        pendingInvoicesCount: 1,
        paymentPendencyStatus: 'BLOCKED_3WAY_MATCH_DIVERGENCE',
        paymentPendencyReason:
          'NF-e #000.892.114 (R$ 18.450,00) possui R$ 4.350,00 retidos para glosa: cobrança de 3 Tomografias sem código de visita eCRF validado no estudo SOLAIR.',
        observations:
          'Integra laudos HL7/DICOM direto no prontuário e possui certificação CAP + PALC vigente.',
        contractNomenclatureAlias:
          "No Contrato Patrocinador consta como 'Local Safety Lab / Diagnostic Imaging Partner' -> Conciliado com 'INSTITUTO HERMES PARDINI S/A'",
        backupHomologatedSupplierName:
          'DASA / Diagnósticos do Brasil (SUP-LAB-02 · Cobertura Nacional 27 UFs)',
        regulatoryDocAlertT30Days:
          'OK · Certificados CAP, PALC e Alvará Sanitário ANVISA válidos até Dez/2027',
        isPassThroughRebillableToSponsor: true,
        passThroughSponsorNotes:
          '100% Reembolsável Pass-Through pelo Patrocinador (Invoiceable Pass-Through conforme CTA Schedule B).',
        slaOnTimePercent: 99.1,
      },
      {
        id: 'SGD-004',
        supplierCode: 'SUP-KIT-01',
        tradeName: 'Thermo Fisher / Fisher Clinical Services',
        legalName: 'PATHEON / THERMO FISHER SCIENTIFIC BRASIL LTDA',
        cnpj: '03.816.477/0001-92',
        supplierTypeCode: 'CLINICAL_KITS_CONSUMABLES',
        supplierTypeLabel: 'Insumos Clínicos, Kits de Coleta Consignados & Reagentes FEFO',
        geographicScope: 'NACIONAL_BRASIL',
        coverageSummary:
          'Atende Brasil Todo (27 UFs · Distribuição Nacional Validada 2°C-8°C e -20°C)',
        requestedByName: 'Camila Rocha (Farmacêutica & Gestora de Suprimentos)',
        requestedByEmail: 'camila.rocha@giyus.com.br',
        approvedByEmail: 'diretoria.holding@giyus.com.br',
        requestingCenterName: 'GIYUS Curitiba Research Center (Unidade Matriz) & Centros Afiliados',
        linkedItemsSummary: [
          'SKU-KIT-EPOCA-V1 · Kit de Coleta PK/PD Estudo EPOCA (Consignado Patrocinador)',
          'SKU-CRIO-2ML · Criotubos Estéreis 2mL RNase/DNase Free (Sinônimo: Vials 2mL)',
          'SKU-AGULHA-VAC-21G · Agulha Coleta Múltipla à Vácuo 21G BD/Fisher',
          'Estudos vinculados: EPOCA, SOLAIR, PERSIST',
        ],
        bestPaymentTerms:
          'Net 45 DDL para insumos próprios GIYUS · Remessa Consignada Sem Faturamento (CFOP 5949) para Kits do Patrocinador',
        earlyPaymentDiscountPolicy:
          'Bonificação de 10% em criotubos e ponteiras estéreis para pedidos acima de R$ 10.000,00',
        pendingPaymentAmountBrl: 0,
        pendingInvoicesCount: 0,
        paymentPendencyStatus: 'NO_PENDENCY_PAID',
        paymentPendencyReason:
          '100% Quitado e Conciliado — Última NF-e #000.412.890 (R$ 8.940,00) liquidada com desconto de antecipação Net 10.',
        observations:
          "Monitorado pelo motor de equivalência de nomenclatura: concilia automaticamente 'Cryovials 2mL Sarstedt' do contrato com 'Criotubo 2mL Estéril' em estoque.",
        contractNomenclatureAlias:
          "Na caixa contratos@giyus.com.br consta como 'Central Lab Kit Depot / Fisher Clinical' -> Conciliado com 'PATHEON / THERMO FISHER SCIENTIFIC BRASIL LTDA'",
        backupHomologatedSupplierName:
          'BD Brasil / Sarstedt Científica (SUP-KIT-02 · Pronta Entrega Nacional)',
        regulatoryDocAlertT30Days:
          'OK · AFE ANVISA Insumos Diagnósticos e Boas Práticas de Distribuição (CBPF) vigentes até Mar/2028',
        isPassThroughRebillableToSponsor: true,
        passThroughSponsorNotes:
          'Kits do estudo vêm em remessa consignada gratuita; insumos ancilares locais são reembolsados via taxa de Supply Fee por visita.',
        slaOnTimePercent: 99.4,
      },
      {
        id: 'SGD-005',
        supplierCode: 'SUP-LOG-01',
        tradeName: 'World Courier Brasil (Cadeia Fria IATA)',
        legalName: 'WORLD COURIER DO BRASIL TRANSPORTE INTERNACIONAL LTDA',
        cnpj: '43.991.108/0001-55',
        supplierTypeCode: 'COLD_CHAIN_LOGISTICS',
        supplierTypeLabel:
          'Logística Fria IATA, Gelo Seco & Devolução/Exportação de Equipamentos e Amostras',
        geographicScope: 'NACIONAL_BRASIL',
        coverageSummary:
          'Atende Brasil Todo (27 UFs + Exportação Internacional IATA Classe 6.2 / UN3373)',
        requestedByName: 'Camila Rocha (Farmacêutica & Gestora de Suprimentos)',
        requestedByEmail: 'camila.rocha@giyus.com.br',
        approvedByEmail: 'qualidade.qa@giyus.com.br',
        requestingCenterName: 'Holding GIYUS (Todos os Centros Brasil)',
        linkedItemsSummary: [
          'EQP-CEN-004 · Centrífuga Refrigerada Beckman Allegra (Logística Reversa Close-Out ao Lab Central)',
          'EQP-ECG-003 · Eletrocardiógrafo Mortara ELI 280 (Transporte Segurado c/ Data-Logger)',
          'PROC-SHIP-IATA · Remessa Biológica Congelada -70°C em Gelo Seco c/ Reposição em Trânsito',
          'Estudos vinculados: EPOCA, SOLAIR, PERSIST, ADEPT-4',
        ],
        bestPaymentTerms:
          'Net 45 DDL para fretes nacionais · Pré-pago na Conta Global do Patrocinador (Account Billing) para envios ao Lab Central',
        earlyPaymentDiscountPolicy:
          'Tarifa flat nacional para reposição emergencial de Gelo Seco em trânsito (Dry Ice Top-Up)',
        pendingPaymentAmountBrl: 0,
        pendingInvoicesCount: 0,
        paymentPendencyStatus: 'NO_PENDENCY_PAID',
        paymentPendencyReason:
          'Sem pendências — Fretes de devolução de equipamentos e amostras faturados diretamente no Account Number do Patrocinador / CRO.',
        observations:
          'Emite AWB, Manifesto de Carga e Certificado de Coleta para devolução de equipamentos de laboratório no Close-Out.',
        contractNomenclatureAlias:
          "Nos e-mails de contratos@giyus.com.br consta como 'AmerisourceBergen / Cencora World Courier' -> Conciliado com 'WORLD COURIER DO BRASIL LTDA'",
        backupHomologatedSupplierName:
          'Marken / UPS Healthcare Cold Chain (SUP-LOG-02 · Cobertura Nacional e Internacional)',
        regulatoryDocAlertT30Days:
          'OK · Licença ANVISA Transporte Biológico e Certificação IATA CEIV Pharma válidas até Out/2027',
        isPassThroughRebillableToSponsor: true,
        passThroughSponsorNotes:
          '100% Faturado direto ao Patrocinador (Direct Sponsor Billing) ou 100% Pass-Through Reembolsável.',
        slaOnTimePercent: 99.7,
      },
      {
        id: 'SGD-006',
        supplierCode: 'SUP-PGRSS-01',
        tradeName: 'Stericycle / Ambipar PGRSS Ambiental',
        legalName: 'STERICYCLE GESTAO AMBIENTAL E RESIDUOS DE SAUDE LTDA',
        cnpj: '01.568.077/0001-81',
        supplierTypeCode: 'PGRSS_HAZARDOUS_DISPOSAL',
        supplierTypeLabel:
          'Descarte Ambiental PGRSS (RDC 222), Incineração & Destruição de Medicamento/Equipamento',
        geographicScope: 'LOCAL_CIDADE_UF',
        coverageSummary:
          'Fornecedor Local/Regional — Coleta Presencial em Curitiba/PR e Região Metropolitana (Frota Licenciada IAP/IBAMA)',
        requestedByName: 'Eng. Marcos Viana (Engenharia Clínica)',
        requestedByEmail: 'marcos.viana@giyus.com.br',
        approvedByEmail: 'qualidade.qa@giyus.com.br',
        requestingCenterName: 'GIYUS Curitiba Research Center (Unidade Matriz)',
        linkedItemsSummary: [
          'KIT-DESC-001 · Descarte de Kits Vencidos por Transportadora com Certificado CDR + MTR',
          'EQP-DESC-002 · Destruição / Descaracterização de Equipamento Obsoleto a Pedido do Patrocinador',
          'PGRSS-ROTINA · Coleta Quinzenal Grupo A (Infectante), Grupo B (Químico/IP) e Grupo E (Perfurocortante)',
          'Estudos vinculados: EPOCA, SOLAIR, PERSIST',
        ],
        bestPaymentTerms:
          'Net 30 DDL (Franquia Mensal PGRSS R$ 680,00) + Taxa sob demanda por Laudo de Incineração Especial c/ Testemunha QA',
        earlyPaymentDiscountPolicy:
          'Desconto de 12% em lotes de destruição de Kits/IP acima de 50 kg no encerramento de estudos',
        pendingPaymentAmountBrl: 1420,
        pendingInvoicesCount: 1,
        paymentPendencyStatus: 'OVERDUE_HIGH_PRIORITY',
        paymentPendencyReason:
          'NFS-e #2026-1109 (R$ 1.420,00 · Destruição Assistida de Kits Vencidos Lote #LOT-2025-09) venceu há 2 dias: Certificado CDR e MTR SINIR já validados — pronta para liberação imediata no PIX.',
        observations:
          'Atende descartes por transportadora com Certificado de Destruição (CDR) e Manifesto de Transporte de Resíduos (MTR), essencial para auditoria FDA/ANVISA.',
        contractNomenclatureAlias:
          "Na caixa financeiro@giyus.com.br consta como 'Ambipar Stericycle Resíduos' -> Conciliado com 'STERICYCLE GESTAO AMBIENTAL LTDA'",
        backupHomologatedSupplierName:
          'Serquip Tratamento de Resíduos PR (SUP-PGRSS-02 · Licenciado IAT/PR)',
        regulatoryDocAlertT30Days:
          'ALERTA T-29 DIAS: Licença de Operação Ambiental (LO IAT/IBAMA) vence em 25/11/2026 — Cobrar renovação antes do próximo descarte',
        isPassThroughRebillableToSponsor: true,
        passThroughSponsorNotes:
          'Taxa de destruição de kits/medicamento experimental com certificado CDR é 100% Pass-Through reembolsável pelo Patrocinador.',
        slaOnTimePercent: 97.2,
      },
    ],
    supplierTimelineEvents: [
      {
        id: 'STL-001',
        supplierCode: 'SUP-CAL-01',
        supplierName: 'Metrologia Sul RBC',
        eventDate: '2026-10-26',
        eventKind: 'PAYMENT_PENDENCY_BLOCK',
        title:
          'Nota Fiscal Recebida via financeiro@giyus.com.br — Retida no Gate 3-Way Match (Falta Certificado RBC)',
        description:
          'Recebida NFS-e #2026-0884 (R$ 1.890,00) referente à calibração da Centrífuga Refrigerada EQP-CEN-002. O sistema bloqueou preventivamente o pagamento pois o PDF do Certificado RBC assinado ainda não foi anexado pelo fornecedor.',
        invoiceOrDocumentCode: 'NFS-e #2026-0884',
        invoiceDownloadFileName: 'NFSe-2026-0884-MetrologiaSul-Centrifuga.pdf',
        certificateOrAttachmentFileName: null,
        amountBrl: 1890,
        requestedByName: 'Eng. Marcos Viana (Engenharia Clínica)',
        requestedByEmail: 'marcos.viana@giyus.com.br',
        centerName: 'GIYUS Curitiba Research Center (Unidade Matriz)',
        geographicScopeBadge: 'LOCAL_CIDADE_UF',
        linkedItemsSummary: 'EQP-CEN-002 (Centrífuga Eppendorf 5810R) · Estudo EPOCA',
        paymentTermsApplied: 'Net 45 DDL (Aguardando Certificado RBC para liberar)',
        pendencyStatus: 'BLOCKED_PENDENCY',
        observations:
          'Trava automática de governança: fornecedor de calibração só recebe após upload do Certificado RBC rastreável ao INMETRO.',
      },
      {
        id: 'STL-002',
        supplierCode: 'SUP-ENG-01',
        supplierName: 'ClinicalEng Brasil',
        eventDate: '2026-10-24',
        eventKind: 'MAINTENANCE_OR_CALIBRATION_EXEC',
        title: 'Manutenção Emergencial Executada + Nota Fiscal Programada (Ultrafreezer -80°C)',
        description:
          'Substituição emergencial do relé de partida do compressor secundário do Ultrafreezer EQP-ULT-001 (SLA 2,8h). Laudo técnico OS-MAN-2026-041 e Certificado de Verificação Térmica Pós-Reparo anexados.',
        invoiceOrDocumentCode: 'NFS-e #2026-3310 / OS-MAN-2026-041',
        invoiceDownloadFileName: 'NFSe-2026-3310-ClinicalEng-Emergencial.pdf',
        certificateOrAttachmentFileName: 'Laudo-Manutencao-Emergencial-OS-MAN-2026-041.pdf',
        amountBrl: 2650,
        requestedByName: 'Dra. Helena Costa (Coordenadora Clínica)',
        requestedByEmail: 'helena.costa@giyus.com.br',
        centerName: 'Clínica Dr. Herberto Chong — Filial / Matriz (Curitiba/PR)',
        geographicScopeBadge: 'REGIONAL_SUL_SUDESTE',
        linkedItemsSummary: 'EQP-ULT-001 (Ultrafreezer Thermo -80°C) · Estudos EPOCA & SOLAIR',
        paymentTermsApplied: 'Net 30 DDL · Vencimento programado 19/11/2026 (Sem taxa de urgência)',
        pendencyStatus: 'SCHEDULED_NET',
        observations:
          'Intervenção exigiu verificação térmica pós-reparo em 3 pontos (-80°C), aprovada pela Garantia da Qualidade.',
      },
      {
        id: 'STL-003',
        supplierCode: 'SUP-PGRSS-01',
        supplierName: 'Stericycle / Ambipar PGRSS Ambiental',
        eventDate: '2026-10-22',
        eventKind: 'INVOICE_NFE_NFSE',
        title:
          'Descarte por Transportadora com Certificado CDR + Nota Fiscal Vencendo (Prioridade Alta)',
        description:
          'Coleta e incineração assistida de 18 kits clínicos vencidos (Lote #LOT-2025-09). Certificado de Destruição (CDR #2026-991) e MTR SINIR emitidos e arquivados. NFS-e #2026-1109 aguarda liberação final no PIX.',
        invoiceOrDocumentCode: 'NFS-e #2026-1109 / CDR-2026-991',
        invoiceDownloadFileName: 'NFSe-2026-1109-Stericycle-DescarteKits.pdf',
        certificateOrAttachmentFileName: 'Certificado-Destruicao-CDR-2026-991-MTR.pdf',
        amountBrl: 1420,
        requestedByName: 'Eng. Marcos Viana (Engenharia Clínica)',
        requestedByEmail: 'marcos.viana@giyus.com.br',
        centerName: 'GIYUS Curitiba Research Center (Unidade Matriz)',
        geographicScopeBadge: 'LOCAL_CIDADE_UF',
        linkedItemsSummary: 'KIT-DESC-001 (18 Kits Vencidos Lote #LOT-2025-09) · Estudo SOLAIR',
        paymentTermsApplied: 'Net 30 DDL (Reembolsável Pass-Through pelo Patrocinador)',
        pendencyStatus: 'BLOCKED_PENDENCY',
        observations:
          "Documentação ambiental 100% completa (CDR + MTR). Basta clicar em 'Regularizar / Liquidar Pendência' na tabela 360°.",
      },
      {
        id: 'STL-004',
        supplierCode: 'SUP-LAB-01',
        supplierName: 'Fleury / Pardini Diagnósticos B2B',
        eventDate: '2026-10-19',
        eventKind: 'SHARED_MAILBOX_RECONCILIATION',
        title:
          'Conciliação Caixa contratos@giyus.com.br & Glosa Parcial 3-Way Match (Exames de Imagem)',
        description:
          "Fatura mensal NF-e #000.892.114 (R$ 18.450,00) conciliada entre 'INSTITUTO HERMES PARDINI S/A' e o item contratual 'Diagnostic Imaging RECIST 1.1'. Liberados R$ 14.100,00 e retidos R$ 4.350,00 (3 TCs aguardando vínculo de visita no eCRF).",
        invoiceOrDocumentCode: 'NF-e #000.892.114',
        invoiceDownloadFileName: 'NFe-000892114-FleuryPardini-LoteOutubro.pdf',
        certificateOrAttachmentFileName: 'Espelho-Auditoria-3WayMatch-Exames-Out2026.pdf',
        amountBrl: 4350,
        requestedByName: 'Dr. Herberto Chong (Investigador Principal - PI)',
        requestedByEmail: 'herberto.chong@giyus.com.br',
        centerName: 'Holding GIYUS (Todos os Centros Brasil — Curitiba/PR e São Paulo/SP)',
        geographicScopeBadge: 'NACIONAL_BRASIL',
        linkedItemsSummary:
          'PROC-IMG-004 (TC Torácica RECIST 1.1) & PROC-LAB-001 · Estudos EPOCA & SOLAIR',
        paymentTermsApplied: 'Net 60 DDL Consolidado Holding · Tabela CBHPM -22%',
        pendencyStatus: 'BLOCKED_PENDENCY',
        observations:
          'Fornecedor Nacional que atende todas as unidades da rede GIYUS no Brasil com tabela unificada.',
      },
      {
        id: 'STL-005',
        supplierCode: 'SUP-KIT-01',
        supplierName: 'Thermo Fisher / Fisher Clinical Services',
        eventDate: '2026-10-14',
        eventKind: 'PAYMENT_SETTLED',
        title: 'Pagamento Liquidado com Desconto Antecipado Net 10 + Conciliação de Sinônimo',
        description:
          "NF-e #000.412.890 (R$ 8.940,00) referente a 1.500 Criotubos 2mL Estéreis liquidada com 4,5% de desconto antecipado. Motor de conciliação mapeou automaticamente 'Cryovials 2mL Sarstedt' do protocolo para 'SKU-CRIO-2ML' fornecido pela Thermo Fisher.",
        invoiceOrDocumentCode: 'NF-e #000.412.890 / PO-2026-0038',
        invoiceDownloadFileName: 'NFe-000412890-ThermoFisher-Criotubos.pdf',
        certificateOrAttachmentFileName: 'Certificado-Qualidade-Lote-RNaseFree-Thermo.pdf',
        amountBrl: 8940,
        requestedByName: 'Camila Rocha (Farmacêutica & Gestora de Suprimentos)',
        requestedByEmail: 'camila.rocha@giyus.com.br',
        centerName: 'GIYUS Curitiba Research Center (Unidade Matriz) & Centros Afiliados',
        geographicScopeBadge: 'NACIONAL_BRASIL',
        linkedItemsSummary:
          'SKU-CRIO-2ML (Criotubos 2mL) & SKU-KIT-EPOCA-V1 · Estudos EPOCA & PERSIST',
        paymentTermsApplied: 'Net 10 DDL Antecipado (Economia de R$ 402,30 sobre Net 45)',
        pendencyStatus: 'PAID_OK',
        observations: 'Lote recebido na farmácia clínica com validade FEFO até Ago/2028.',
      },
      {
        id: 'STL-006',
        supplierCode: 'SUP-LOG-01',
        supplierName: 'World Courier Brasil (Cadeia Fria IATA)',
        eventDate: '2026-10-10',
        eventKind: 'PURCHASE_OR_SERVICE_ORDER',
        title:
          'Coleta Internacional IATA + Devolução de Equipamento em Comodato do Laboratório Central',
        description:
          'Coleta da Centrífuga Refrigerada EQP-CEN-004 (Encerramento do Estudo PERSIST) com emissão de AWB #WC-9948120 e Termo de Devolução Assinado (RMA). Custo 100% faturado direto na conta global do Patrocinador.',
        invoiceOrDocumentCode: 'AWB #WC-9948120 / NF-e Remessa #004.910',
        invoiceDownloadFileName: 'NFe-Remessa-Devolucao-004910-WorldCourier.pdf',
        certificateOrAttachmentFileName: 'AWB-WC-9948120-Termo-Devolucao-LabCentral.pdf',
        amountBrl: 0,
        requestedByName: 'Camila Rocha (Farmacêutica & Gestora de Suprimentos)',
        requestedByEmail: 'camila.rocha@giyus.com.br',
        centerName: 'Holding GIYUS (Todos os Centros Brasil)',
        geographicScopeBadge: 'NACIONAL_BRASIL',
        linkedItemsSummary:
          'EQP-CEN-004 (Devolução Lab Central) & Amostras PK -70°C · Estudo PERSIST',
        paymentTermsApplied: 'Direct Sponsor Billing (Sem desembolso de caixa GIYUS)',
        pendencyStatus: 'PAID_OK',
        observations: 'Rastreamento térmico contínuo validado sem excursão de temperatura.',
      },
    ],
    inventoryMultiSupplierItems: [
      {
        id: 'IMS-001',
        skuCode: 'SKU-CRIO-2ML',
        itemName: 'Criotubos Estéreis 2mL RNase/DNase Free (-80°C / LN2) · Sinônimo: Cryovials 2mL',
        categoryCode: 'PHARMACY_KITS',
        unitMeasure: 'Caixa c/ 500 un. (Preço expresso por Unidade)',
        contractBudgetCeilingUnitBrl: 7.2,
        linkedStudies: ['EPOCA', 'SOLAIR', 'PERSIST'],
        supplierOptionsByLocality: [
          {
            supplierCode: 'SUP-KIT-01',
            supplierName: 'Thermo Fisher / Fisher Clinical Services',
            geographicScope: 'NACIONAL_BRASIL',
            localityCityUf: 'Curitiba/PR',
            choiceOptionRole: 'MELHOR_PRECO_LOCALIDADE',
            choiceOptionLabel: 'Opção #1 em Curitiba/PR (Menor Custo Total TCO + Bonificação 10%)',
            unitPriceBrl: 5.65,
            freightPerUnitBrl: 0.31,
            landedUnitCostBrl: 5.96,
            leadTimeDays: 4,
            paymentTerms: 'Net 45 DDL (ou Net 10 c/ 4,5% desc.)',
            minOrderQty: 500,
            lastInvoiceCode: 'NF-e #000.412.890',
            lastPurchaseDate: '2026-10-14',
            isSelectedForLocality: true,
          },
          {
            supplierCode: 'SUP-LOC-CWB-01',
            supplierName: 'BioSul Suprimentos Laboratoriais Curitiba',
            geographicScope: 'LOCAL_CIDADE_UF',
            localityCityUf: 'Curitiba/PR',
            choiceOptionRole: 'ENTREGA_EXPRESSA_LOCAL_24H',
            choiceOptionLabel:
              'Opção #2 em Curitiba/PR (Entrega Local em 4h · Sem Frete · Urgência)',
            unitPriceBrl: 6.4,
            freightPerUnitBrl: 0,
            landedUnitCostBrl: 6.4,
            leadTimeDays: 1,
            paymentTerms: 'Net 30 DDL (PIX / Boleto Local)',
            minOrderQty: 100,
            lastInvoiceCode: 'NF-e #000.098.412',
            lastPurchaseDate: '2026-09-18',
            isSelectedForLocality: false,
          },
          {
            supplierCode: 'SUP-KIT-02',
            supplierName: 'BD Brasil / Sarstedt Científica',
            geographicScope: 'NACIONAL_BRASIL',
            localityCityUf: 'São Paulo/SP',
            choiceOptionRole: 'MELHOR_PRECO_LOCALIDADE',
            choiceOptionLabel:
              'Opção #1 em São Paulo/SP (CD Local Barueri/SP · Frete Zero + Net 60)',
            unitPriceBrl: 5.7,
            freightPerUnitBrl: 0.08,
            landedUnitCostBrl: 5.78,
            leadTimeDays: 1,
            paymentTerms: 'Net 60 DDL Consolidado Holding SP',
            minOrderQty: 500,
            lastInvoiceCode: 'NF-e #001.209.331',
            lastPurchaseDate: '2026-10-08',
            isSelectedForLocality: true,
          },
          {
            supplierCode: 'SUP-KIT-01',
            supplierName: 'Thermo Fisher / Fisher Clinical Services',
            geographicScope: 'NACIONAL_BRASIL',
            localityCityUf: 'São Paulo/SP',
            choiceOptionRole: 'BACKUP_CONTINGENCIA',
            choiceOptionLabel:
              'Opção #2 em São Paulo/SP (Backup Homologado · Lote Certificado FDA)',
            unitPriceBrl: 5.65,
            freightPerUnitBrl: 0.22,
            landedUnitCostBrl: 5.87,
            leadTimeDays: 2,
            paymentTerms: 'Net 45 DDL',
            minOrderQty: 500,
            lastInvoiceCode: 'NF-e #000.398.100',
            lastPurchaseDate: '2026-08-22',
            isSelectedForLocality: false,
          },
        ],
      },
      {
        id: 'IMS-002',
        skuCode: 'INS-FENO-BOCAL-300',
        itemName: 'Bocais Descartáveis c/ Filtro Antibacteriano p/ FeNO NIOX VERO',
        categoryCode: 'RESPIRATORY_CONSUMABLES',
        unitMeasure: 'Unidade Individual Certificada',
        contractBudgetCeilingUnitBrl: 48.0,
        linkedStudies: ['PERSIST', 'EPOCA'],
        supplierOptionsByLocality: [
          {
            supplierCode: 'SUP-RESP-01',
            supplierName: 'Circassia / BioMed Distribuidora Sul',
            geographicScope: 'REGIONAL_SUL_SUDESTE',
            localityCityUf: 'Curitiba/PR',
            choiceOptionRole: 'MELHOR_PRECO_LOCALIDADE',
            choiceOptionLabel: 'Opção #1 em Curitiba/PR (Distribuidor Oficial Sul · Menor TCO)',
            unitPriceBrl: 38.5,
            freightPerUnitBrl: 1.4,
            landedUnitCostBrl: 39.9,
            leadTimeDays: 2,
            paymentTerms: 'Net 45 DDL (Pass-Through IQVIA/GSK)',
            minOrderQty: 50,
            lastInvoiceCode: 'NF-e #000.088.910',
            lastPurchaseDate: '2026-10-11',
            isSelectedForLocality: true,
          },
          {
            supplierCode: 'SUP-HOSP-SP-02',
            supplierName: 'MedLab Diagnósticos & Respiratória Brasil',
            geographicScope: 'NACIONAL_BRASIL',
            localityCityUf: 'Curitiba/PR',
            choiceOptionRole: 'MELHOR_PRAZO_PAGAMENTO_NET60',
            choiceOptionLabel:
              'Opção #2 em Curitiba/PR (Maior Prazo Net 60 DDL · Estoque Nacional)',
            unitPriceBrl: 41.0,
            freightPerUnitBrl: 2.1,
            landedUnitCostBrl: 43.1,
            leadTimeDays: 4,
            paymentTerms: 'Net 60 DDL s/ Juros',
            minOrderQty: 50,
            lastInvoiceCode: 'NF-e #000.514.209',
            lastPurchaseDate: '2026-08-05',
            isSelectedForLocality: false,
          },
          {
            supplierCode: 'SUP-HOSP-SP-02',
            supplierName: 'MedLab Diagnósticos & Respiratória Brasil',
            geographicScope: 'NACIONAL_BRASIL',
            localityCityUf: 'São Paulo/SP',
            choiceOptionRole: 'MELHOR_PRECO_LOCALIDADE',
            choiceOptionLabel: 'Opção #1 em São Paulo/SP (Entrega Mesmo Dia em SP · Frete CIF)',
            unitPriceBrl: 39.2,
            freightPerUnitBrl: 0,
            landedUnitCostBrl: 39.2,
            leadTimeDays: 1,
            paymentTerms: 'Net 60 DDL Consolidado Holding',
            minOrderQty: 50,
            lastInvoiceCode: 'NF-e #000.551.800',
            lastPurchaseDate: '2026-10-19',
            isSelectedForLocality: true,
          },
        ],
      },
      {
        id: 'IMS-003',
        skuCode: 'SKU-DRYICE-PELLET-KG',
        itemName: 'Gelo Seco Grau Farmacêutico (Dry Ice Pellets -78,5°C p/ Remessa IATA UN3373)',
        categoryCode: 'COLD_CHAIN_CONSUMABLES',
        unitMeasure: 'Quilograma (kg) validado c/ Laudo de Pureza',
        contractBudgetCeilingUnitBrl: 26.0,
        linkedStudies: ['EPOCA', 'SOLAIR', 'PERSIST', 'ADEPT-4'],
        supplierOptionsByLocality: [
          {
            supplierCode: 'SUP-GAS-CWB-01',
            supplierName: 'CryoParaná Gases & Gelo Seco Local',
            geographicScope: 'LOCAL_CIDADE_UF',
            localityCityUf: 'Curitiba/PR',
            choiceOptionRole: 'MELHOR_PRECO_LOCALIDADE',
            choiceOptionLabel:
              'Opção #1 em Curitiba/PR (Fornecedor Local · Baixa Sublimação · Entrega 2h)',
            unitPriceBrl: 16.8,
            freightPerUnitBrl: 1.2,
            landedUnitCostBrl: 18.0,
            leadTimeDays: 1,
            paymentTerms: 'Net 30 DDL · Sem mínimo p/ reposição',
            minOrderQty: 10,
            lastInvoiceCode: 'NF-e #000.019.442',
            lastPurchaseDate: '2026-10-23',
            isSelectedForLocality: true,
          },
          {
            supplierCode: 'SUP-LOG-01',
            supplierName: 'World Courier Brasil (Cadeia Fria IATA)',
            geographicScope: 'NACIONAL_BRASIL',
            localityCityUf: 'Curitiba/PR',
            choiceOptionRole: 'ENTREGA_EXPRESSA_LOCAL_24H',
            choiceOptionLabel:
              'Opção #2 em Curitiba/PR (Incluso no Pacote de Coleta IATA c/ Caixa VIP)',
            unitPriceBrl: 22.5,
            freightPerUnitBrl: 0,
            landedUnitCostBrl: 22.5,
            leadTimeDays: 1,
            paymentTerms: 'Direct Sponsor Account Billing',
            minOrderQty: 5,
            lastInvoiceCode: 'AWB #WC-9948120',
            lastPurchaseDate: '2026-10-10',
            isSelectedForLocality: false,
          },
          {
            supplierCode: 'SUP-GAS-SP-01',
            supplierName: 'White Martins / Linde Gases Medicinal SP',
            geographicScope: 'NACIONAL_BRASIL',
            localityCityUf: 'São Paulo/SP',
            choiceOptionRole: 'MELHOR_PRECO_LOCALIDADE',
            choiceOptionLabel: 'Opção #1 em São Paulo/SP (Contrato Corporativo Holding SP)',
            unitPriceBrl: 15.4,
            freightPerUnitBrl: 0.9,
            landedUnitCostBrl: 16.3,
            leadTimeDays: 1,
            paymentTerms: 'Net 45 DDL Consolidado',
            minOrderQty: 15,
            lastInvoiceCode: 'NF-e #002.918.440',
            lastPurchaseDate: '2026-10-21',
            isSelectedForLocality: true,
          },
          {
            supplierCode: 'SUP-GAS-POA-01',
            supplierName: 'SulGases Criogenia Porto Alegre',
            geographicScope: 'LOCAL_CIDADE_UF',
            localityCityUf: 'Porto Alegre/RS',
            choiceOptionRole: 'MELHOR_PRECO_LOCALIDADE',
            choiceOptionLabel:
              'Opção #1 em Porto Alegre/RS (Fornecedor Local Centros Parceiros RS)',
            unitPriceBrl: 17.9,
            freightPerUnitBrl: 1.5,
            landedUnitCostBrl: 19.4,
            leadTimeDays: 1,
            paymentTerms: 'Net 30 DDL',
            minOrderQty: 10,
            lastInvoiceCode: 'NF-e #000.044.118',
            lastPurchaseDate: '2026-10-15',
            isSelectedForLocality: true,
          },
        ],
      },
      {
        id: 'IMS-004',
        skuCode: 'SKU-OPEX-PAPEL-HIG-CWB',
        itemName:
          'Papel Higiênico Folha Dupla Institucional & Papel Toalha Interfolha (Fardo 64 rolos — Uso Interno da Unidade)',
        categoryCode: 'OPEX_HIGIENE_UNIDADE',
        unitMeasure: 'Fardo Institucional c/ 64 rolos (100% Celulose Virgem)',
        contractBudgetCeilingUnitBrl: 68.0,
        linkedStudies: [],
        costAllocationKind: 'CUSTO_OPERACIONAL_UNIDADE',
        costAllocationTargetLabel:
          'Custo Próprio de Operação — GIYUS Curitiba Research Center (OPEX-CWB · Não Atrelado a Contrato)',
        supplierOptionsByLocality: [
          {
            supplierCode: 'SUP-HIG-CWB-01',
            supplierName: 'LimpSul Distribuidora de Higiene & Limpeza Curitiba',
            geographicScope: 'LOCAL_CIDADE_UF',
            localityCityUf: 'Curitiba/PR',
            choiceOptionRole: 'MELHOR_PRECO_LOCALIDADE',
            choiceOptionLabel:
              'Opção #1 em Curitiba/PR (Menor Preço Local + Frete CIF Grátis p/ GIYUS Curitiba)',
            unitPriceBrl: 58.9,
            freightPerUnitBrl: 0,
            landedUnitCostBrl: 58.9,
            leadTimeDays: 1,
            paymentTerms: 'Net 28 DDL (Boleto Operacional GIYUS Curitiba)',
            minOrderQty: 5,
            lastInvoiceCode: 'NF-e #000.031.209',
            lastPurchaseDate: '2026-10-16',
            isSelectedForLocality: true,
          },
          {
            supplierCode: 'SUP-ATAC-CWB-02',
            supplierName: 'Atacadão / Condor Empresas Curitiba',
            geographicScope: 'LOCAL_CIDADE_UF',
            localityCityUf: 'Curitiba/PR',
            choiceOptionRole: 'ENTREGA_EXPRESSA_LOCAL_24H',
            choiceOptionLabel:
              'Opção #2 em Curitiba/PR (Pronta-Entrega 2h no Mesmo Dia · Sem Pedido Mínimo)',
            unitPriceBrl: 61.5,
            freightPerUnitBrl: 2.0,
            landedUnitCostBrl: 63.5,
            leadTimeDays: 1,
            paymentTerms: 'PIX Corporativo à Vista (-3% desc.) ou Cartão C6 Empresas',
            minOrderQty: 1,
            lastInvoiceCode: 'NF-e #000.884.112',
            lastPurchaseDate: '2026-09-04',
            isSelectedForLocality: false,
          },
          {
            supplierCode: 'SUP-CORP-BR-03',
            supplierName: 'Gimba / Kalunga Suprimentos Corporativos Brasil',
            geographicScope: 'NACIONAL_BRASIL',
            localityCityUf: 'São Paulo/SP',
            choiceOptionRole: 'MELHOR_PRAZO_PAGAMENTO_NET60',
            choiceOptionLabel:
              'Opção #1 em São Paulo/SP & Brasil (Contrato Corporativo Net 60 DDL Holding)',
            unitPriceBrl: 57.5,
            freightPerUnitBrl: 4.9,
            landedUnitCostBrl: 62.4,
            leadTimeDays: 2,
            paymentTerms: 'Net 60 DDL Consolidado OPEX',
            minOrderQty: 10,
            lastInvoiceCode: 'NF-e #004.112.908',
            lastPurchaseDate: '2026-10-05',
            isSelectedForLocality: true,
          },
        ],
      },
      {
        id: 'IMS-005',
        skuCode: 'MED-EMERG-ADREN-1MG',
        itemName:
          'Medicamento de Disponibilidade Obrigatória (Proibido Vencer): Epinefrina 1mg/mL + Hidrocortisona 500mg + Difenidramina 50mg (Kit Emergência Anafilática / Carro de Parada)',
        categoryCode: 'MANDATORY_MEDICATION',
        unitMeasure:
          'Kit Hospitalar Lacrado c/ 5 Ampolas + Rastreabilidade ANVISA (Alerta Compra T-30d antes de vencer)',
        contractBudgetCeilingUnitBrl: 165.0,
        linkedStudies: [],
        costAllocationKind: 'CUSTO_OPERACIONAL_UNIDADE',
        costAllocationTargetLabel:
          'Custo Próprio de Operação GIYUS Curitiba + Disponibilidade Obrigatória ANVISA/GCP (Alerta Automático de Compra T-30d Antes de Vencer)',
        supplierOptionsByLocality: [
          {
            supplierCode: 'SUP-MED-CWB-01',
            supplierName: 'MedHosp Distribuidora Farmacêutica Curitiba',
            geographicScope: 'LOCAL_CIDADE_UF',
            localityCityUf: 'Curitiba/PR',
            choiceOptionRole: 'ENTREGA_EXPRESSA_LOCAL_24H',
            choiceOptionLabel:
              'Opção #1 p/ Evitar Vencimento em Curitiba/PR (Entrega Expressa 4h c/ Lote Validade 24 Meses)',
            unitPriceBrl: 148.0,
            freightPerUnitBrl: 0,
            landedUnitCostBrl: 148.0,
            leadTimeDays: 1,
            paymentTerms: 'Net 28 DDL (Prioridade Zero Ruptura Carro de Parada)',
            minOrderQty: 2,
            lastInvoiceCode: 'NF-e #000.071.904',
            lastPurchaseDate: '2026-10-19',
            isSelectedForLocality: true,
          },
          {
            supplierCode: 'SUP-MAFRA-BR-02',
            supplierName: 'CM Hospitalar / Mafra & Cristália Brasil',
            geographicScope: 'NACIONAL_BRASIL',
            localityCityUf: 'Curitiba/PR',
            choiceOptionRole: 'MELHOR_PRECO_LOCALIDADE',
            choiceOptionLabel:
              'Opção #2 em Curitiba/PR (Compra Programada T-60d/T-30d Antes de Vencer · Menor TCO)',
            unitPriceBrl: 131.5,
            freightPerUnitBrl: 3.0,
            landedUnitCostBrl: 134.5,
            leadTimeDays: 2,
            paymentTerms: 'Net 45 DDL Faturado Farmácia Hospitalar',
            minOrderQty: 5,
            lastInvoiceCode: 'NF-e #001.992.310',
            lastPurchaseDate: '2026-09-12',
            isSelectedForLocality: false,
          },
          {
            supplierCode: 'SUP-MAFRA-BR-02',
            supplierName: 'CM Hospitalar / Mafra & Cristália Brasil',
            geographicScope: 'NACIONAL_BRASIL',
            localityCityUf: 'São Paulo/SP',
            choiceOptionRole: 'MELHOR_PRECO_LOCALIDADE',
            choiceOptionLabel:
              'Opção #1 em São Paulo/SP (CD Ribeirão Preto/Barueri · Entrega 12h SP)',
            unitPriceBrl: 129.9,
            freightPerUnitBrl: 0,
            landedUnitCostBrl: 129.9,
            leadTimeDays: 1,
            paymentTerms: 'Net 60 DDL Consolidado Holding',
            minOrderQty: 5,
            lastInvoiceCode: 'NF-e #002.114.809',
            lastPurchaseDate: '2026-10-10',
            isSelectedForLocality: true,
          },
        ],
      },
      {
        id: 'IMS-006',
        skuCode: 'MED-RESG-SALBUT-100',
        itemName:
          'Medicamento de Resgate de Disponibilidade Obrigatória (Proibido Vencer): Sulfato de Salbutamol 100mcg/dose Spray HFA + Brometo de Ipratrópio',
        categoryCode: 'MANDATORY_MEDICATION',
        unitMeasure:
          'Frasco Inalador Dosimetrado 200 Doses (Alerta Automático de Compra T-30d Antes de Vencer)',
        contractBudgetCeilingUnitBrl: 44.0,
        linkedStudies: ['SOLAIR', 'PERSIST', 'EPOCA'],
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel:
          'Atrelado aos Contratos SOLAIR, PERSIST e EPOCA (Medicamento de Resgate Obrigatório · Compra Antecipada T-30d Antes do Vencimento)',
        supplierOptionsByLocality: [
          {
            supplierCode: 'SUP-ONCO-SUL-01',
            supplierName: 'OncoProd / Mafra Hospitalar Sul',
            geographicScope: 'REGIONAL_SUL_SUDESTE',
            localityCityUf: 'Curitiba/PR',
            choiceOptionRole: 'MELHOR_PRECO_LOCALIDADE',
            choiceOptionLabel:
              'Opção #1 em Curitiba/PR (Reposição Automática T-30d Antes de Vencer · Menor TCO)',
            unitPriceBrl: 33.8,
            freightPerUnitBrl: 1.0,
            landedUnitCostBrl: 34.8,
            leadTimeDays: 1,
            paymentTerms: 'Net 45 DDL (100% Pass-Through Contratos Respiratórios)',
            minOrderQty: 10,
            lastInvoiceCode: 'NF-e #000.612.400',
            lastPurchaseDate: '2026-10-20',
            isSelectedForLocality: true,
          },
          {
            supplierCode: 'SUP-FARMA-CWB-02',
            supplierName: 'FarmaSul Hospitalar & Emergência Curitiba',
            geographicScope: 'LOCAL_CIDADE_UF',
            localityCityUf: 'Curitiba/PR',
            choiceOptionRole: 'ENTREGA_EXPRESSA_LOCAL_24H',
            choiceOptionLabel:
              'Opção #2 em Curitiba/PR (Entrega Expressa 4h se faltar < 15 dias p/ vencer)',
            unitPriceBrl: 37.5,
            freightPerUnitBrl: 0,
            landedUnitCostBrl: 37.5,
            leadTimeDays: 1,
            paymentTerms: 'Net 28 DDL ou PIX Imediato',
            minOrderQty: 3,
            lastInvoiceCode: 'NF-e #000.091.332',
            lastPurchaseDate: '2026-09-25',
            isSelectedForLocality: false,
          },
        ],
      },
    ],
    consumablePriceTimelineEntries: [
      {
        id: 'CPT-001',
        eventDate: '2026-10-23',
        skuCode: 'SKU-DRYICE-PELLET-KG',
        itemName: 'Gelo Seco Grau Farmacêutico (-78,5°C)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        supplierCode: 'SUP-GAS-CWB-01',
        supplierName: 'CryoParaná Gases & Gelo Seco Local',
        geographicScope: 'LOCAL_CIDADE_UF',
        unitPriceBrl: 16.8,
        freightPerUnitBrl: 1.2,
        landedUnitCostBrl: 18.0,
        previousLandedUnitCostBrl: 19.5,
        deltaPercentVsPrevious: -7.69,
        contractCeilingUnitBrl: 26.0,
        savingsPerUnitVsCeilingBrl: 8.0,
        choiceReasonOrTrigger:
          'Escolha por Localidade + Menor Preço em Curitiba/PR: fornecedor local evita sublimação de transporte rodoviário longo e reduziu 7,69% na renovação trimestral.',
        invoiceOrQuoteRef: 'NF-e #000.019.442',
        recordedByEmail: 'camila.rocha@giyus.com.br',
      },
      {
        id: 'CPT-002',
        eventDate: '2026-10-21',
        skuCode: 'SKU-DRYICE-PELLET-KG',
        itemName: 'Gelo Seco Grau Farmacêutico (-78,5°C)',
        localityCityUf: 'São Paulo/SP',
        centerName: 'Holding GIYUS (São Paulo/SP)',
        supplierCode: 'SUP-GAS-SP-01',
        supplierName: 'White Martins / Linde Gases Medicinal SP',
        geographicScope: 'NACIONAL_BRASIL',
        unitPriceBrl: 15.4,
        freightPerUnitBrl: 0.9,
        landedUnitCostBrl: 16.3,
        previousLandedUnitCostBrl: 16.8,
        deltaPercentVsPrevious: -2.98,
        contractCeilingUnitBrl: 26.0,
        savingsPerUnitVsCeilingBrl: 9.7,
        choiceReasonOrTrigger:
          'Escolha por Escala na Localidade São Paulo/SP: usina local em SP permite custo efetivo R$ 1,70/kg menor que Curitiba/PR.',
        invoiceOrQuoteRef: 'NF-e #002.918.440',
        recordedByEmail: 'syllas@giyus.com.br',
      },
      {
        id: 'CPT-003',
        eventDate: '2026-10-19',
        skuCode: 'INS-FENO-BOCAL-300',
        itemName: 'Bocais Descartáveis c/ Filtro FeNO NIOX VERO',
        localityCityUf: 'São Paulo/SP',
        centerName: 'Holding GIYUS (São Paulo/SP)',
        supplierCode: 'SUP-HOSP-SP-02',
        supplierName: 'MedLab Diagnósticos & Respiratória Brasil',
        geographicScope: 'NACIONAL_BRASIL',
        unitPriceBrl: 39.2,
        freightPerUnitBrl: 0,
        landedUnitCostBrl: 39.2,
        previousLandedUnitCostBrl: 41.5,
        deltaPercentVsPrevious: -5.54,
        contractCeilingUnitBrl: 48.0,
        savingsPerUnitVsCeilingBrl: 8.8,
        choiceReasonOrTrigger:
          'Escolha por Frete CIF Zero em São Paulo/SP + Prazo Net 60 DDL via Mapa Comparativo de 3 Cotações.',
        invoiceOrQuoteRef: 'NF-e #000.551.800',
        recordedByEmail: 'camila.rocha@giyus.com.br',
      },
      {
        id: 'CPT-004',
        eventDate: '2026-10-14',
        skuCode: 'SKU-CRIO-2ML',
        itemName: 'Criotubos Estéreis 2mL RNase/DNase Free',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        supplierCode: 'SUP-KIT-01',
        supplierName: 'Thermo Fisher / Fisher Clinical Services',
        geographicScope: 'NACIONAL_BRASIL',
        unitPriceBrl: 5.65,
        freightPerUnitBrl: 0.31,
        landedUnitCostBrl: 5.96,
        previousLandedUnitCostBrl: 6.25,
        deltaPercentVsPrevious: -4.64,
        contractCeilingUnitBrl: 7.2,
        savingsPerUnitVsCeilingBrl: 1.24,
        choiceReasonOrTrigger:
          'Escolha por Preço e Desconto Antecipado Net 10 (-4,5%) em lote de 1.500 un. para Curitiba/PR (NF-e conciliada via financeiro@giyus.com.br).',
        invoiceOrQuoteRef: 'NF-e #000.412.890 / PO-2026-0038',
        recordedByEmail: 'camila.rocha@giyus.com.br',
      },
      {
        id: 'CPT-005',
        eventDate: '2026-10-11',
        skuCode: 'INS-FENO-BOCAL-300',
        itemName: 'Bocais Descartáveis c/ Filtro FeNO NIOX VERO',
        localityCityUf: 'Curitiba/PR',
        centerName: 'Clínica Dr. Herberto Chong (Curitiba/PR)',
        supplierCode: 'SUP-RESP-01',
        supplierName: 'Circassia / BioMed Distribuidora Sul',
        geographicScope: 'REGIONAL_SUL_SUDESTE',
        unitPriceBrl: 38.5,
        freightPerUnitBrl: 1.4,
        landedUnitCostBrl: 39.9,
        previousLandedUnitCostBrl: 42.0,
        deltaPercentVsPrevious: -5.0,
        contractCeilingUnitBrl: 48.0,
        savingsPerUnitVsCeilingBrl: 8.1,
        choiceReasonOrTrigger:
          'Escolha por Menor Preço em Curitiba/PR: distribuidor regional Sul entrega em 48h com economia de R$ 3,20/un. sobre o concorrente nacional em Curitiba.',
        invoiceOrQuoteRef: 'NF-e #000.088.910',
        recordedByEmail: 'helena.costa@giyus.com.br',
      },
      {
        id: 'CPT-006',
        eventDate: '2026-10-08',
        skuCode: 'SKU-CRIO-2ML',
        itemName: 'Criotubos Estéreis 2mL RNase/DNase Free',
        localityCityUf: 'São Paulo/SP',
        centerName: 'Holding GIYUS (São Paulo/SP)',
        supplierCode: 'SUP-KIT-02',
        supplierName: 'BD Brasil / Sarstedt Científica',
        geographicScope: 'NACIONAL_BRASIL',
        unitPriceBrl: 5.7,
        freightPerUnitBrl: 0.08,
        landedUnitCostBrl: 5.78,
        previousLandedUnitCostBrl: 5.95,
        deltaPercentVsPrevious: -2.86,
        contractCeilingUnitBrl: 7.2,
        savingsPerUnitVsCeilingBrl: 1.42,
        choiceReasonOrTrigger:
          'Escolha por Localidade São Paulo/SP: CD em Barueri/SP reduz o frete unitário para R$ 0,08 e oferece Net 60 DDL.',
        invoiceOrQuoteRef: 'NF-e #001.209.331',
        recordedByEmail: 'syllas@giyus.com.br',
      },
      {
        id: 'CPT-007',
        eventDate: '2026-09-18',
        skuCode: 'SKU-CRIO-2ML',
        itemName: 'Criotubos Estéreis 2mL RNase/DNase Free',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        supplierCode: 'SUP-LOC-CWB-01',
        supplierName: 'BioSul Suprimentos Laboratoriais Curitiba',
        geographicScope: 'LOCAL_CIDADE_UF',
        unitPriceBrl: 6.4,
        freightPerUnitBrl: 0,
        landedUnitCostBrl: 6.4,
        previousLandedUnitCostBrl: 6.35,
        deltaPercentVsPrevious: 0.79,
        contractCeilingUnitBrl: 7.2,
        savingsPerUnitVsCeilingBrl: 0.8,
        choiceReasonOrTrigger:
          'Escolha por Opção de Urgência em Curitiba/PR (Entrega Expressa Local em 4h sem pedido mínimo para cobrir pico de visitas V1 do estudo EPOCA).',
        invoiceOrQuoteRef: 'NF-e #000.098.412',
        recordedByEmail: 'helena.costa@giyus.com.br',
      },
      {
        id: 'CPT-008',
        eventDate: '2026-10-16',
        skuCode: 'SKU-OPEX-PAPEL-HIG-CWB',
        itemName: 'Papel Higiênico Folha Dupla Institucional & Papel Toalha (Fardo 64 rolos)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        supplierCode: 'SUP-HIG-CWB-01',
        supplierName: 'LimpSul Distribuidora de Higiene & Limpeza Curitiba',
        geographicScope: 'LOCAL_CIDADE_UF',
        unitPriceBrl: 58.9,
        freightPerUnitBrl: 0,
        landedUnitCostBrl: 58.9,
        previousLandedUnitCostBrl: 63.5,
        deltaPercentVsPrevious: -7.24,
        contractCeilingUnitBrl: 68.0,
        savingsPerUnitVsCeilingBrl: 9.1,
        choiceReasonOrTrigger:
          'Insumo NÃO atrelado a contrato (alocado 100% no Custo Próprio de Operação da GIYUS Curitiba — OPEX-CWB). Escolha da distribuidora local LimpSul gerou queda de 7,24% com frete CIF grátis em Curitiba.',
        invoiceOrQuoteRef: 'NF-e #000.031.209',
        recordedByEmail: 'camila.rocha@giyus.com.br',
        costAllocationKind: 'CUSTO_OPERACIONAL_UNIDADE',
        costAllocationTargetLabel:
          'Custo Próprio de Operação — GIYUS Curitiba Research Center (OPEX-CWB · Sem Contrato)',
      },
      {
        id: 'CPT-009',
        eventDate: '2026-09-04',
        skuCode: 'SKU-OPEX-PAPEL-HIG-CWB',
        itemName: 'Papel Higiênico Folha Dupla Institucional & Papel Toalha (Fardo 64 rolos)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        supplierCode: 'SUP-ATAC-CWB-02',
        supplierName: 'Atacadão / Condor Empresas Curitiba',
        geographicScope: 'LOCAL_CIDADE_UF',
        unitPriceBrl: 61.5,
        freightPerUnitBrl: 2.0,
        landedUnitCostBrl: 63.5,
        previousLandedUnitCostBrl: 64.0,
        deltaPercentVsPrevious: -0.78,
        contractCeilingUnitBrl: 68.0,
        savingsPerUnitVsCeilingBrl: 4.5,
        choiceReasonOrTrigger:
          'Compra de reposição rápida (entrega em 2h) para recepção e sanitários da GIYUS Curitiba. Despesa lançada diretamente no Custo Próprio de Operação da GIYUS Curitiba (não vinculada a contrato de estudo).',
        invoiceOrQuoteRef: 'NF-e #000.884.112',
        recordedByEmail: 'helena.costa@giyus.com.br',
        costAllocationKind: 'CUSTO_OPERACIONAL_UNIDADE',
        costAllocationTargetLabel:
          'Custo Próprio de Operação — GIYUS Curitiba Research Center (OPEX-CWB · Sem Contrato)',
      },
      {
        id: 'CPT-010',
        eventDate: '2026-10-20',
        skuCode: 'MED-RESG-SALBUT-100',
        itemName: 'Medicamento Obrigatório: Salbutamol 100mcg HFA Spray (Resgate / Prova Pulmonar)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        supplierCode: 'SUP-ONCO-SUL-01',
        supplierName: 'OncoProd / Mafra Hospitalar Sul',
        geographicScope: 'REGIONAL_SUL_SUDESTE',
        unitPriceBrl: 33.8,
        freightPerUnitBrl: 1.0,
        landedUnitCostBrl: 34.8,
        previousLandedUnitCostBrl: 37.5,
        deltaPercentVsPrevious: -7.2,
        contractCeilingUnitBrl: 44.0,
        savingsPerUnitVsCeilingBrl: 9.2,
        choiceReasonOrTrigger:
          'Compra preventiva disparada pelo Alerta de Antes de Vencer (T-30d) para nunca deixar vencer medicamento de disponibilidade obrigatória. Economia de 7,20% em relação à compra emergencial anterior em Curitiba/PR.',
        invoiceOrQuoteRef: 'NF-e #000.612.400',
        recordedByEmail: 'camila.rocha@giyus.com.br',
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel:
          'Atrelado aos Contratos SOLAIR, PERSIST e EPOCA (Alerta de Compra Antes de Vencer T-30d)',
      },
    ],
    procurementPolicy: {
      governancePhase: 'FASE_1_COM_APROVACAO_HUMANA',
      autoApproveExpiringMedications: true,
      autoApproveStudyRequiredItems: true,
      autoApproveEssentialSiteProducts: true,
      autoApprovalCeilingPerOrderBrl: 15000.0,
      requireQualifiedSupplierAndWithinCeiling: true,
      nonEssentialAlwaysRequiresHuman: true,
      lastUpdatedByEmail: 'syllas@giyus.com.br',
      lastUpdatedAtIso: '2026-10-27T06:00:00Z',
    },
    integratedProcurementOrders: [
      {
        id: 'IPO-001',
        orderCode: 'PED-INT-2026-001',
        createdAtIso: '2026-10-27T04:15:00Z',
        skuCode: 'MED-EMERG-ADREN-1MG',
        itemName:
          'Medicamento Obrigatório: Epinefrina (Adrenalina) 1mg/mL Ampola + Kit Anafilaxia (Carro de Parada)',
        categoryCode: 'EMERGENCY_RESCUE_MED',
        essentialityTriggerKind: 'PRODUTO_QUE_VENHA_A_VENCER',
        triggerHeadline:
          'ALERTA ANTES DE VENCER (Faltam 22 dias · Lote EPI-2025-11B vence em 2026-11-18): Compra obrigatória para NUNCA deixar vencer medicamento de disponibilidade contínua.',
        originModuleIntegration:
          'Estoque Clínico FEFO + Recursos (Trava 423 de Infusão Biológica) + Fornecedores (Melhor Landed Cost Curitiba/PR)',
        linkedStudyCode: null,
        costAllocationKind: 'CUSTO_OPERACIONAL_UNIDADE',
        costAllocationTargetLabel:
          'Custo Próprio de Operação & Segurança Clínica — GIYUS Curitiba Research Center (Carro de Parada Obrigatório)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        selectedSupplierCode: 'SUP-MEDHOSP-CWB-01',
        selectedSupplierName: 'MedHosp Distribuidora Hospitalar Curitiba (Entrega Expressa 4h)',
        supplierScope: 'LOCAL_CIDADE_UF',
        paymentTermsApplied: '28 DDL Boleto Faturado · Sem Pedido Mínimo Emergencial',
        quantityOrdered: 25,
        unitLandedPriceBrl: 20.0,
        contractOrBudgetCeilingUnitBrl: 26.0,
        totalOrderBrl: 500.0,
        savingsVsCeilingBrl: 150.0,
        daysUntilExpirationOrStockout: 22,
        isMandatoryMedication: true,
        approvalMode: 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE',
        approvalStatus: 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy:
          'Gatilho Preventivo T-30d (Aguardando Aprovação Humana na Fase 1 ou Auto-Compra na Fase 2)',
        approvedAtIso: null,
        erpIntegrationTrail:
          'Estoque FEFO (MED-EMERG-ADREN-1MG) → Mapa Localidade Curitiba/PR (R$ 20,00/amp) → Financeiro OPEX-CWB → E-mail financeiro@giyus.com.br',
      },
      {
        id: 'IPO-002',
        orderCode: 'PED-INT-2026-002',
        createdAtIso: '2026-10-27T04:20:00Z',
        skuCode: 'MED-RESG-SALBUT-100',
        itemName:
          'Medicamento Obrigatório: Salbutamol 100mcg HFA Spray (Resgate / Prova de Broncodilatação)',
        categoryCode: 'EMERGENCY_RESCUE_MED',
        essentialityTriggerKind: 'PRODUTO_QUE_VENHA_A_VENCER',
        triggerHeadline:
          'ALERTA ANTES DE VENCER (Faltam 29 dias · Lote SALB-2025-11X vence em 2026-11-25): Reposição preventiva antes do vencimento para não parar visitas de espirometria.',
        originModuleIntegration:
          'Estoque Clínico FEFO + Estudos SOLAIR/PERSIST/EPOCA + Fornecedores (Regional Sul)',
        linkedStudyCode: 'SOLAIR',
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel:
          'Atrelado aos Contratos SOLAIR, PERSIST e EPOCA (Reembolso Pass-Through Resgate Respiratório)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        selectedSupplierCode: 'SUP-ONCO-SUL-01',
        selectedSupplierName: 'OncoProd / Mafra Hospitalar Sul (CD Curitiba/PR)',
        supplierScope: 'REGIONAL_SUL_SUDESTE',
        paymentTermsApplied: '45 DDL Faturado Corporativo · Frete CIF acima de R$ 600',
        quantityOrdered: 30,
        unitLandedPriceBrl: 34.8,
        contractOrBudgetCeilingUnitBrl: 44.0,
        totalOrderBrl: 1044.0,
        savingsVsCeilingBrl: 276.0,
        daysUntilExpirationOrStockout: 29,
        isMandatoryMedication: true,
        approvalMode: 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE',
        approvalStatus: 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy:
          'Gatilho Preventivo T-30d (Aguardando Aprovação Humana na Fase 1 ou Auto-Compra na Fase 2)',
        approvedAtIso: null,
        erpIntegrationTrail:
          'Estoque FEFO (MED-RESG-SALBUT-100) → Dossiê SOLAIR/PERSIST → Financeiro Pass-Through → E-mail contratos@giyus.com.br',
      },
      {
        id: 'IPO-003',
        orderCode: 'PED-INT-2026-003',
        createdAtIso: '2026-10-27T04:30:00Z',
        skuCode: 'KIT-PK-EPOCA-V2',
        itemName:
          'Kit Coleta Farmacocinética Seriada V2 (Criotubos 2mL + Tubos EDTA K2) — Estudo EPOCA',
        categoryCode: 'LAB_KITS_CONSUMABLES',
        essentialityTriggerKind: 'NECESSARIO_PARA_UM_ESTUDO',
        triggerHeadline:
          'NECESSÁRIO PARA O ESTUDO EPOCA: 14 visitas V2 (PK seriada 8h) agendadas nos próximos 15 dias; estoque atual cobre apenas 9 visitas.',
        originModuleIntegration:
          'Estudos (Grade SoA Visita V2 EPOCA) + Caixa contratos@giyus.com.br + Estoque Clínico + Fornecedores',
        linkedStudyCode: 'EPOCA',
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel:
          'Atrelado à Minuta Local MINUTA-EPOCA-2026-TRIALS (Exclusivo TRIALS HUB LTDA · CNPJ 66.125.390/0001-34 · Patrocinadora Hcor — Sem outros centros e sem GIYUS)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'TRIALS HUB LTDA (Exclusivo na Minuta Local EPOCA — Sem GIYUS)',
        selectedSupplierCode: 'SUP-BD-DIAG-BR',
        selectedSupplierName: 'BD (Becton Dickinson) Diagnósticos Brasil',
        supplierScope: 'NACIONAL_BRASIL',
        paymentTermsApplied: '45 DDL Direto Fábrica · Lote único rastreável ANVISA',
        quantityOrdered: 500,
        unitLandedPriceBrl: 5.5,
        contractOrBudgetCeilingUnitBrl: 7.2,
        totalOrderBrl: 2750.0,
        savingsVsCeilingBrl: 850.0,
        daysUntilExpirationOrStockout: 9,
        isMandatoryMedication: false,
        approvalMode: 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE',
        approvalStatus: 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy:
          'Motor SoA Estudo EPOCA (Aguardando Aprovação Humana na Fase 1 ou Auto-Compra na Fase 2)',
        approvedAtIso: null,
        erpIntegrationTrail:
          'Agenda SoA EPOCA (14 visitas V2) → Estoque (SKU-CRIO-2ML) → Cotação Vencedora BD (R$ 5,50/un) → Contas a Receber Reembolso IQVIA',
      },
      {
        id: 'IPO-004',
        orderCode: 'PED-INT-2026-004',
        createdAtIso: '2026-10-27T04:40:00Z',
        skuCode: 'INS-FENO-BOCAL-300',
        itemName:
          'Kit Bocais Descartáveis com Filtro Antibacteriano FeNO (NIOX VERO) — Estudos PERSIST & SOLAIR',
        categoryCode: 'PULMONARY_DIAGNOSTICS',
        essentialityTriggerKind: 'NECESSARIO_PARA_UM_ESTUDO',
        triggerHeadline:
          'NECESSÁRIO PARA OS ESTUDOS PERSIST & SOLAIR: E-mail de regulatorio@giyus.com.br confirmou 1º Aditivo de reembolso integral de bocais FeNO para 22 participantes ativos.',
        originModuleIntegration:
          'Estudos (PERSIST & SOLAIR) + E-mail regulatorio@giyus.com.br + Equipamento PAT-CWB-0014 (NIOX VERO)',
        linkedStudyCode: 'PERSIST',
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel:
          'Atrelado aos Contratos PERSIST (GSK/IQVIA) e SOLAIR (AstraZeneca/Parexel)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        selectedSupplierCode: 'SUP-CIRCASSIA-BR',
        selectedSupplierName: 'Circassia / MedTech Respiratória Brasil (Importador Oficial NIOX)',
        supplierScope: 'NACIONAL_BRASIL',
        paymentTermsApplied: '30/60 DDL Boleto Faturado · Lote com Validade > 18 meses',
        quantityOrdered: 120,
        unitLandedPriceBrl: 41.0,
        contractOrBudgetCeilingUnitBrl: 48.0,
        totalOrderBrl: 4920.0,
        savingsVsCeilingBrl: 840.0,
        daysUntilExpirationOrStockout: 11,
        isMandatoryMedication: false,
        approvalMode: 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE',
        approvalStatus: 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy:
          'Motor SoA Estudos PERSIST/SOLAIR (Aguardando Aprovação Humana na Fase 1 ou Auto-Compra na Fase 2)',
        approvedAtIso: null,
        erpIntegrationTrail:
          'E-mail regulatorio@giyus.com.br (Aditivo PERSIST) + Ativo PAT-CWB-0014 → Pedido 120 bocais FeNO → Reembolso Pass-Through',
      },
      {
        id: 'IPO-005',
        orderCode: 'PED-INT-2026-005',
        createdAtIso: '2026-10-27T04:50:00Z',
        skuCode: 'SKU-DRYICE-PELLET-KG',
        itemName:
          'Produto Essencial: Gelo Seco Grau Clínico Pellet 16mm (IATA PI-954) — Remessas Biológicas -80°C',
        categoryCode: 'COLD_CHAIN_CONSUMABLES',
        essentialityTriggerKind: 'PRODUTO_ESSENCIAL_NECESSARIO',
        triggerHeadline:
          'PRODUTO ESSENCIAL NECESSÁRIO: 4 coletas World Courier agendadas na semana para envio de amostras PK (-80°C); estoque mínimo de segurança atingido.',
        originModuleIntegration:
          'Ordens de Serviço (OS-2026-001 World Courier) + Estoque Cadeia Fria + Fornecedor Local Curitiba/PR',
        linkedStudyCode: 'EPOCA',
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel:
          'Atrelado aos Contratos EPOCA e SOLAIR (Logística Biológica IATA Classe 6.2)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        selectedSupplierCode: 'SUP-CRIO-SUL-01',
        selectedSupplierName: 'CrioSul Gases & Gelo Seco Medicinal (CIC Curitiba/PR)',
        supplierScope: 'LOCAL_CIDADE_UF',
        paymentTermsApplied:
          '28 DDL Faturamento Quinzenal · Entrega Programada até 3h sem sublimação',
        quantityOrdered: 150,
        unitLandedPriceBrl: 13.3,
        contractOrBudgetCeilingUnitBrl: 18.5,
        totalOrderBrl: 1995.0,
        savingsVsCeilingBrl: 780.0,
        daysUntilExpirationOrStockout: 5,
        isMandatoryMedication: false,
        approvalMode: 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE',
        approvalStatus: 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy:
          'Ponto de Pedido Cadeia Fria (Aguardando Aprovação Humana na Fase 1 ou Auto-Compra na Fase 2)',
        approvedAtIso: null,
        erpIntegrationTrail:
          'OS-2026-001 (World Courier) → Timeline Preço Local Curitiba/PR (CrioSul R$ 13,30/kg) → Financeiro 3-Way Match',
      },
      {
        id: 'IPO-006',
        orderCode: 'PED-INT-2026-006',
        createdAtIso: '2026-10-25T10:15:00Z',
        skuCode: 'SKU-OPEX-PAPEL-HIG-CWB',
        itemName:
          'Produto Essencial da Unidade: Papel Higiênico Folha Dupla Institucional + Sabonete Antisséptico',
        categoryCode: 'FACILITY_HYGIENE_OPEX',
        essentialityTriggerKind: 'PRODUTO_ESSENCIAL_NECESSARIO',
        triggerHeadline:
          'PRODUTO ESSENCIAL DE OPERAÇÃO DA UNIDADE: Reposição mensal de higiene para recepção de participantes e consultórios da GIYUS Curitiba.',
        originModuleIntegration:
          'Estoque Operacional GIYUS Curitiba + Fornecedor Local + Financeiro OPEX-CWB (Sem Contrato de Estudo)',
        linkedStudyCode: null,
        costAllocationKind: 'CUSTO_OPERACIONAL_UNIDADE',
        costAllocationTargetLabel:
          'Custo Próprio de Operação — GIYUS Curitiba Research Center (OPEX-CWB · Sem Contrato)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        selectedSupplierCode: 'SUP-LIMP-CWB-01',
        selectedSupplierName: 'HigieSul Distribuidora Profissional Curitiba',
        supplierScope: 'LOCAL_CIDADE_UF',
        paymentTermsApplied: '28 DDL Boleto Faturado · Frete CIF Curitiba',
        quantityOrdered: 20,
        unitLandedPriceBrl: 58.9,
        contractOrBudgetCeilingUnitBrl: 68.0,
        totalOrderBrl: 1178.0,
        savingsVsCeilingBrl: 182.0,
        daysUntilExpirationOrStockout: 7,
        isMandatoryMedication: false,
        approvalMode: 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE',
        approvalStatus: 'APPROVED_BY_HUMAN_ORDERED',
        approvedOrTriggeredBy: 'Aprovado por Humano: Helena Costa (helena.costa@giyus.com.br)',
        approvedAtIso: '2026-10-25T11:00:00Z',
        erpIntegrationTrail:
          'Pedido aprovado na Fase 1 (Humano) → NF-e #000.914.205 conciliada no Razão Financeiro (OPEX-CWB)',
      },
      {
        id: 'IPO-007',
        orderCode: 'PED-INT-2026-007',
        createdAtIso: '2026-10-26T16:00:00Z',
        skuCode: 'EQP-CAPEX-ULT-BACKUP',
        itemName:
          'Capex Não-Essencial Imediato: Ultrafreezer Vertical -86°C 528L Adicional (Expansão Sala Criogenia)',
        categoryCode: 'EQUIPMENT_CAPEX',
        essentialityTriggerKind: 'NAO_ESSENCIAL_ACIMA_TETO',
        triggerHeadline:
          'CAPEX ACIMA DO TETO DE AUTO-COMPRA (R$ 64.500,00 > R$ 15.000,00): Sempre exige aprovação humana da Diretoria (2 chaves SoD), mesmo com Fase 2 ativa.',
        originModuleIntegration:
          'Recursos & Patrimônio (Expansão Capex) + Mapa Comparativo de 3 Cotações + Diretoria Financeira',
        linkedStudyCode: null,
        costAllocationKind: 'CUSTO_OPERACIONAL_UNIDADE',
        costAllocationTargetLabel:
          'Investimento CAPEX Próprio — GIYUS Curitiba Research Center (Depreciação Linear 120 meses)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        selectedSupplierCode: 'SUP-THERMO-CAPEX',
        selectedSupplierName: 'Thermo Fisher Scientific Brasil (Divisão Equipamentos Criogênicos)',
        supplierScope: 'NACIONAL_BRASIL',
        paymentTermsApplied:
          '30/60/90 DDL Faturado · Garantia 24 meses + Qualificação IQ/OQ/PQ inclusa',
        quantityOrdered: 1,
        unitLandedPriceBrl: 64500.0,
        contractOrBudgetCeilingUnitBrl: 68000.0,
        totalOrderBrl: 64500.0,
        savingsVsCeilingBrl: 3500.0,
        daysUntilExpirationOrStockout: null,
        isMandatoryMedication: false,
        approvalMode: 'REQUIRES_HUMAN_APPROVAL',
        approvalStatus: 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy:
          'Trava de Segurança Capex: Exige aprovação humana obrigatória (Syllas / Diretoria)',
        approvedAtIso: null,
        erpIntegrationTrail:
          'Bloqueado para auto-compra sem humano (Valor > Teto R$ 15.000,00 e categoria CAPEX) → Aguarda aprovação humana explícita',
      },
    ],
    pickupPolicy: {
      governancePhase: 'FASE_1_HUMANO_APROVA_FORNECEDOR_AGENDA',
      autoApproveBiologicalSamplePickups: true,
      autoApproveCalibrationAndReverseLogisticsPickups: true,
      autoApproveCertifiedDisposalPickups: true,
      autoTriggerSupplierSlotBookingWhenApiAvailable: true,
      enforceMonToWedWindowForBiologicalSamples: true,
      autoApprovalCeilingPerPickupBrl: 10000.0,
      lastUpdatedByEmail: 'syllas@giyus.com.br',
      lastUpdatedAtIso: '2026-10-27T06:20:00Z',
    },
    supplierPickups: [
      {
        id: 'COL-001',
        pickupCode: 'COL-INT-2026-001',
        createdAtIso: '2026-10-27T05:10:00Z',
        pickupCategory: 'AMOSTRAS_BIOLOGICAS_IATA',
        essentialityTriggerKind: 'COLETA_AMOSTRA_VISITA_ESTUDO',
        title:
          'Coleta Biológica IATA (-80°C em Gelo Seco PI-954): 18 Alíquotas PK Seriada 8h (Visita V2 — Estudo EPOCA)',
        triggerHeadline:
          'GATILHO DE VISITA CLÍNICA REALIZADA: Participantes SUBJ-004, SUBJ-007 e SUBJ-011 concluíram coleta PK de 8h. Amostras ultracongeladas (-80°C) prontas para embarque na janela obrigatória de Segunda a Quarta-feira.',
        originModuleIntegration:
          'Estudos Clínicos (Visita V2 EPOCA) + Estoque Cadeia Fria (Gelo Seco SKU-DRYICE-PELLET-KG) + Fornecedor IATA',
        linkedStudyCode: 'EPOCA',
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel:
          'Atrelado à Minuta Local MINUTA-EPOCA-2026-TRIALS (Exclusivo TRIALS HUB LTDA · CNPJ 66.125.390/0001-34 · Patrocinadora Hcor — Sem outros centros e sem GIYUS)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'TRIALS HUB LTDA (Exclusivo na Minuta Local EPOCA — Sem GIYUS)',
        pickupAddressSummary:
          'Sala de Coleta & Processamento — TRIALS HUB LTDA (Av. Iguaçu, 1150, Rebouças, Curitiba/PR)',
        destinationName: 'Labcorp Central Laboratory (Indianápolis/EUA via Guarulhos/GRU)',
        supplierCode: 'SUP-LOG-01',
        supplierName: 'World Courier Brasil (Cadeia Fria IATA)',
        supplierScope: 'NACIONAL_BRASIL',
        temperatureOrHandlingRequirement:
          'ULTRACONGELADO -80°C (Caixa VIP Validada c/ 25 kg Gelo Seco + DataLogger TempTale) · Embarque Seg–Qua',
        estimatedCostBrl: 2850.0,
        contractCeilingBrl: 3400.0,
        savingsVsCeilingBrl: 550.0,
        approvalMode: 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE',
        workflowStatus: 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy:
          '⏳ Etapa 1 (Fase 1 Inicial): Aguardando o Humano aprovar a coleta para liberar o agendamento ao fornecedor World Courier (ou automático na Fase 2)',
        approvedAtIso: null,
        scheduledPickupDate: null,
        scheduledPickupWindow: null,
        awbOrTrackingCode: null,
        courierAgentName: null,
        supplierScheduledByEmail: null,
        supplierScheduledAtIso: null,
        erpIntegrationTrail:
          'Visita V2 EPOCA Concluída → Amostras -80°C Conferidas → ⏳ Aguardando Aprovação Humana → Fornecedor World Courier Agenda Coleta',
      },
      {
        id: 'COL-002',
        pickupCode: 'COL-INT-2026-002',
        createdAtIso: '2026-10-26T14:20:00Z',
        pickupCategory: 'EQUIPAMENTO_CALIBRACAO_RBC',
        essentialityTriggerKind: 'COLETA_CALIBRACAO_OU_COMODATO_T30D',
        title:
          'Coleta Técnica para Calibração Acreditada RBC/INMETRO (Alerta 1 Mês Antes T-30d): Bomba de Infusão & Termohigrômetros Cadeia Fria',
        triggerHeadline:
          '⏰ GATILHO ALERTA 1 MÊS ANTES (T-30d): Certificado RBC vence em 26 dias. O humano já aprovou a retirada programada; agora o fornecedor CaliLab / Metrologia Sul deve agendar a data e janela da coleta com envio de equipamento backup.',
        originModuleIntegration:
          'Gerenciamento de Recursos (Menu Calibração T-30d) + Fornecedores RBC + Financeiro OPEX/Pass-Through',
        linkedStudyCode: 'EPOCA',
        costAllocationKind: 'CUSTO_OPERACIONAL_UNIDADE',
        costAllocationTargetLabel:
          'Custo Próprio de Operação & Qualidade — GIYUS Curitiba Research Center (OPEX Metrologia RBC)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        pickupAddressSummary: 'Engenharia Clínica & Farmácia — GIYUS Curitiba/PR',
        destinationName: 'Laboratório Acreditado CGCRE/INMETRO CAL-0418 (Curitiba/PR)',
        supplierCode: 'SUP-CAL-01',
        supplierName: 'Metrologia Sul RBC / CaliLab Científica',
        supplierScope: 'LOCAL_CIDADE_UF',
        temperatureOrHandlingRequirement:
          'TRANSPORTE METROLÓGICO ANTI-VIBRAÇÃO (Deixar Termohigrômetro Backup Calibrado no Ato da Coleta)',
        estimatedCostBrl: 980.0,
        contractCeilingBrl: 1250.0,
        savingsVsCeilingBrl: 270.0,
        approvalMode: 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE',
        workflowStatus: 'APPROVED_BY_HUMAN_AWAITING_SUPPLIER_SCHEDULE',
        approvedOrTriggeredBy:
          'Etapa 2: Aprovado pelo Humano (Eng. Marcos Viana) — Notificação enviada ao fornecedor; aguardando o fornecedor agendar a coleta',
        approvedAtIso: '2026-10-26T15:00:00Z',
        scheduledPickupDate: null,
        scheduledPickupWindow: null,
        awbOrTrackingCode: null,
        courierAgentName: null,
        supplierScheduledByEmail: null,
        supplierScheduledAtIso: null,
        erpIntegrationTrail:
          'Alerta Calibração T-30d → Humano Aprovou a Coleta → ⏳ Fornecedor Metrologia Sul Deve Agendar Data/Hora e Confirmar Backup',
      },
      {
        id: 'COL-003',
        pickupCode: 'COL-INT-2026-003',
        createdAtIso: '2026-10-27T05:25:00Z',
        pickupCategory: 'LOGISTICA_REVERSA_CLOSEOUT',
        essentialityTriggerKind: 'COLETA_CALIBRACAO_OU_COMODATO_T30D',
        title:
          'Coleta de Logística Reversa (Devolução de Equipamento em Comodato do Lab Central no Close-Out): Espirômetro & ECG ERT Clario',
        triggerHeadline:
          'GATILHO DESTRAVAR CLOSE-OUT (Trava 423): Estudo SOLAIR em fase de encerramento exige coleta reversa do equipamento em comodato do laboratório central para liberar o termo de Close-Out.',
        originModuleIntegration:
          'Gerenciamento de Recursos (Comodato Lab Central & Trava 423 Close-Out) + E-mail contratos@giyus.com.br + Fornecedor IATA',
        linkedStudyCode: 'SOLAIR',
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel:
          'Atrelado ao Contrato SOLAIR (AstraZeneca / Parexel · Logística Reversa 100% Reembolsável)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        pickupAddressSummary: 'Recepção Técnica / Almoxarifado de Estudos — GIYUS Curitiba/PR',
        destinationName: 'Clario / ERT Central Depot (São Paulo/SP)',
        supplierCode: 'SUP-LOG-01',
        supplierName: 'World Courier Brasil (Cadeia Fria IATA)',
        supplierScope: 'NACIONAL_BRASIL',
        temperatureOrHandlingRequirement:
          'CASE RÍGIDO ORIGINAL C/ LACRE DE INVENTÁRIO E TERMO RMA ASSINADO',
        estimatedCostBrl: 1450.0,
        contractCeilingBrl: 1800.0,
        savingsVsCeilingBrl: 350.0,
        approvalMode: 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE',
        workflowStatus: 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy:
          '⏳ Etapa 1 (Fase 1 Inicial): Aguardando aprovação humana para acionar a coleta reversa com a transportadora',
        approvedAtIso: null,
        scheduledPickupDate: null,
        scheduledPickupWindow: null,
        awbOrTrackingCode: null,
        courierAgentName: null,
        supplierScheduledByEmail: null,
        supplierScheduledAtIso: null,
        erpIntegrationTrail:
          'Trava 423 Close-Out SOLAIR → ⏳ Aguardando Humano Aprovar Coleta Reversa → Fornecedor Agenda Retirada e Emite AWB',
      },
      {
        id: 'COL-004',
        pickupCode: 'COL-INT-2026-004',
        createdAtIso: '2026-10-27T05:30:00Z',
        pickupCategory: 'RESIDUOS_DESCARTE_CERTIFICADO',
        essentialityTriggerKind: 'COLETA_ESSENCIAL_OPERACAO_DESCARTE',
        title:
          'Coleta por Transportadora Licenciada com Certificado de Destruição (CDR + MTR SINIR): Kits Vencidos & Resíduos PGRSS Classe A/B/E',
        triggerHeadline:
          'GATILHO DE DESCARTE COM CERTIFICADO: 14 kits expirados segregados em quarentena + bombonas PGRSS da unidade GIYUS Curitiba prontos para coleta e incineração assistida.',
        originModuleIntegration:
          'Gerenciamento de Recursos (Descarte por Transportadora c/ Certificado) + Fornecedor Local PGRSS + Financeiro',
        linkedStudyCode: 'PERSIST',
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel:
          'Misto: Destruição Kits PERSIST (Pass-Through) + Resíduos PGRSS Rotina (Custo Operacional GIYUS Curitiba)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        pickupAddressSummary: 'Abrigo Externo PGRSS / Quarentena Farmácia — GIYUS Curitiba/PR',
        destinationName:
          'Usina de Tratamento Térmico & Incineração Licenciada IAT/IBAMA (Região Metropolitana de Curitiba/PR)',
        supplierCode: 'SUP-PGRSS-01',
        supplierName: 'Stericycle / Ambipar PGRSS Ambiental',
        supplierScope: 'LOCAL_CIDADE_UF',
        temperatureOrHandlingRequirement:
          'VEÍCULO COLETOR LICENCIADO ANVISA RDC 222 + EMISSÃO OBRIGATÓRIA DE MTR E CERTIFICADO CDR',
        estimatedCostBrl: 1420.0,
        contractCeilingBrl: 1650.0,
        savingsVsCeilingBrl: 230.0,
        approvalMode: 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE',
        workflowStatus: 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy:
          '⏳ Etapa 1 (Fase 1 Inicial): Aguardando aprovação humana para liberar agendamento de coleta com a Stericycle',
        approvedAtIso: null,
        scheduledPickupDate: null,
        scheduledPickupWindow: null,
        awbOrTrackingCode: null,
        courierAgentName: null,
        supplierScheduledByEmail: null,
        supplierScheduledAtIso: null,
        erpIntegrationTrail:
          'Quarentena Farmácia + PGRSS → ⏳ Aguardando Aprovação Humana → Stericycle Agenda Coleta e Emite MTR/CDR',
      },
      {
        id: 'COL-005',
        pickupCode: 'COL-INT-2026-005',
        createdAtIso: '2026-10-25T11:00:00Z',
        pickupCategory: 'AMOSTRAS_BIOLOGICAS_IATA',
        essentialityTriggerKind: 'COLETA_AMOSTRA_VISITA_ESTUDO',
        title:
          '🩸Coleta de Amostras de Segurança & Lâminas Histopatológicas (2°C–8°C) — Estudo ADEPT-4',
        triggerHeadline:
          'FLUXO COMPLETO CONCLUÍDO: Humano aprovou a coleta e o fornecedor Fleury/Pardini já agendou a retirada com janela e código de rastreio confirmados.',
        originModuleIntegration:
          'Estudos Clínicos (ADEPT-4) + Portal B2B do Fornecedor + Timeline de Fornecedores',
        linkedStudyCode: 'ADEPT-4',
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel: 'Atrelado ao Contrato BMS CN0120056 ADEPT-4 (100% Pass-Through)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'Clínica Dr. Herberto Chong (Curitiba/PR)',
        pickupAddressSummary: 'Laboratório de Coleta — Clínica Dr. Herberto Chong (Curitiba/PR)',
        destinationName: 'Núcleo Técnico Operacional Fleury / Pardini (São Paulo/SP)',
        supplierCode: 'SUP-LAB-01',
        supplierName: 'Fleury / Pardini Diagnósticos B2B',
        supplierScope: 'NACIONAL_BRASIL',
        temperatureOrHandlingRequirement:
          'REFRIGERADO 2°C A 8°C (Maleta Térmica Validada c/ Gelox e Lacre Numerado)',
        estimatedCostBrl: 640.0,
        contractCeilingBrl: 780.0,
        savingsVsCeilingBrl: 140.0,
        approvalMode: 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE',
        workflowStatus: 'SUPPLIER_SCHEDULED_CONFIRMED',
        approvedOrTriggeredBy:
          'Aprovado por Humano: Dra. Helena Costa (helena.costa@giyus.com.br) + Agendado pelo Fornecedor',
        approvedAtIso: '2026-10-25T11:45:00Z',
        scheduledPickupDate: '2026-10-28 (Terça-feira)',
        scheduledPickupWindow: '09:30 às 11:30 (Janela Seg–Qua Confirmada)',
        awbOrTrackingCode: 'AWB-FLR-2026-8841',
        courierAgentName: 'Motorista B2B: Roberto Almeida (Frota Bio-Fria Placa BDF-4A19)',
        supplierScheduledByEmail: 'logistica.pesquisa@grupofleury.com.br',
        supplierScheduledAtIso: '2026-10-25T14:10:00Z',
        erpIntegrationTrail:
          'Visita ADEPT-4 → Humano Aprovou → Fornecedor Fleury Agendou Coleta (28/10 · 09:30-11:30 · AWB-FLR-2026-8841)',
      },
      {
        id: 'COL-006',
        pickupCode: 'COL-INT-2026-006',
        createdAtIso: '2026-10-27T05:40:00Z',
        pickupCategory: 'AMOSTRAS_BIOLOGICAS_IATA',
        essentialityTriggerKind: 'COLETA_ESPECIAL_EXIGE_HUMANO',
        title:
          'Coleta Especial Dedicada (Charter / Transferência Integral de Biorrepositório -80°C c/ Escolta Térmica — Acima do Teto)',
        triggerHeadline:
          'GUARDRAIL DE SEGURANÇA (R$ 24.800,00 > Teto R$ 10.000,00): Coleta extraordinária dedicada fora da rotina. Jamais é aprovada sem humano, mesmo com a Fase 2 Automática ativa.',
        originModuleIntegration:
          'Diretoria de Operações Clínicas + Garantia da Qualidade (QA) + Fornecedor IATA',
        linkedStudyCode: 'EPOCA',
        costAllocationKind: 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel:
          'Solicitação Extraordinária de Transferência de Biorrepositório (Sujeita a Aditivo Contratual)',
        localityCityUf: 'Curitiba/PR',
        centerName: 'GIYUS Curitiba Research Center (Matriz)',
        pickupAddressSummary: 'Doca Blindada de Criogenia — GIYUS Curitiba/PR',
        destinationName: 'Biorrepositório Central Global (Basileia / Suíça)',
        supplierCode: 'SUP-LOG-01',
        supplierName: 'World Courier Brasil (Cadeia Fria IATA)',
        supplierScope: 'NACIONAL_BRASIL',
        temperatureOrHandlingRequirement:
          'DRY SHIPPER LN2 (-196°C) + MONITORAMENTO SATELITAL GPS EM TEMPO REAL',
        estimatedCostBrl: 24800.0,
        contractCeilingBrl: 26000.0,
        savingsVsCeilingBrl: 1200.0,
        approvalMode: 'REQUIRES_HUMAN_APPROVAL',
        workflowStatus: 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy:
          'Trava de Segurança Ativa: Exige sempre aprovação humana da Diretoria antes de liberar o agendamento ao fornecedor',
        approvedAtIso: null,
        scheduledPickupDate: null,
        scheduledPickupWindow: null,
        awbOrTrackingCode: null,
        courierAgentName: null,
        supplierScheduledByEmail: null,
        supplierScheduledAtIso: null,
        erpIntegrationTrail:
          'Bloqueado para Auto-Coleta (Valor R$ 24.800,00 > Teto R$ 10.000,00) → Exige Aprovação Humana Explícita',
      },
    ],
    accountingContractBuckets: [
      {
        studyCode: 'EPOCA',
        contractCode: 'MINUTA-EPOCA-2026-TRIALS',
        studyTitle:
          'Ensaio clínico randomizado fatorial 2 x 2 (colchicina e polipílula em alto risco CV) — EPOCA (Exclusivo Trials Hub)',
        sponsorName: 'ASSOCIAÇÃO BENEFICENTE SÍRIA — Hcor (CNPJ 60.453.024/0003-90)',
        croIntermediaryName: 'Direto Hcor (fiscal@hcor.com.br · Sem CRO intermediária)',
        billingEntityName:
          'TRIALS HUB LTDA (Exclusivo na Minuta Local · PI Dr. Marco Stephan Lofrano Alves — Sem outros centros e sem GIYUS)',
        billingEntityCnpj: '66.125.390/0001-34',
        contractCurrency: 'BRL (Tabela Anexo I Minuta Local EPOCA Trials Hub)',
        exchangeRatePtaxBrl: 1.0,
        annualInflationAdjustmentRule:
          'Reajuste anual pelo IPCA/IBGE (Defesa Cláusula 4.1 Minuta Trials Hub)',
        paymentTerms:
          'Envio de NF até dia 20 p/ fiscal@hcor.com.br · Pagamento até dia 30 do mês subsequente',
        holdbackPercentage: 10.0,
        retroactiveBillingLimitDays: 90,
        taxRegimeProfile:
          'FATURAMENTO_EXCLUSIVO_TRIALSHUB_HCOR (CNPJ 66.125.390/0001-34 · Sem GIYUS)',
        taxWithholdingRatePct: 6.15,
        minBatchThresholdBrl: 15000.0,
        cutOffWindowLabel: 'Fechamento até dia 20 de cada mês (Envio p/ fiscal@hcor.com.br)',
        readyToBillBrl: 38420.0,
        expiringRiskBrl: 6800.0,
        conditionalEscrowBrl: 4200.0,
        holdbackRetainedCumulativeBrl: 12792.0,
        totalAlreadyInvoicedBrl: 48200.0,
      },
      {
        studyCode: 'SOLAIR',
        contractCode: 'CTA-SOLAIR-410-AZ',
        studyTitle: 'Estudo Multicêntrico DPOC Moderada a Grave (Protocolo SOLAIR-410)',
        sponsorName: 'AstraZeneca UK / Zurique',
        croIntermediaryName: 'Parexel International Brasil',
        billingEntityName: 'Clínica Dr. Herberto Chong — Filial GIYUS Curitiba',
        billingEntityCnpj: '51.785.454/0002-37',
        contractCurrency: 'USD Convertido na PTAX BCB do Dia do Faturamento',
        exchangeRatePtaxBrl: 5.68,
        annualInflationAdjustmentRule:
          'Proteção Cambial PTAX BCB na data de emissão da Invoice/NFS-e',
        paymentTerms: 'Net 45 DDL via Portal Ariba / Greenphire',
        holdbackPercentage: 10.0,
        retroactiveBillingLimitDays: 90,
        taxRegimeProfile:
          'EXPORTACAO_SERVICOS_ISS_ZERO (Remessa Direta Exterior Suíça · 0% Retenção Fonte)',
        taxWithholdingRatePct: 0.0,
        minBatchThresholdBrl: 12000.0,
        cutOffWindowLabel: 'Fechamento Mensal por Lote Mínimo de R$ 12.000 ou Visitas Concluídas',
        readyToBillBrl: 25890.0,
        expiringRiskBrl: 5120.0,
        conditionalEscrowBrl: 0.0,
        holdbackRetainedCumulativeBrl: 5537.0,
        totalAlreadyInvoicedBrl: 34600.0,
      },
      {
        studyCode: 'PERSIST',
        contractCode: 'CTA-PERSIST-208-GSK',
        studyTitle: 'Estudo Longo Prazo Imunobiológico Respiratório (Protocolo PERSIST-208)',
        sponsorName: 'GlaxoSmithKline (GSK Londres / Brasil)',
        croIntermediaryName: 'IQVIA RDS Brasil Ltda.',
        billingEntityName: 'GIYUS Curitiba Research Center Ltda. (Matriz)',
        billingEntityCnpj: '69.584.441/0001-84',
        contractCurrency: 'BRL (Tabela Congelada + 1º Aditivo FeNO Aprovado)',
        exchangeRatePtaxBrl: 1.0,
        annualInflationAdjustmentRule: 'Reajuste IPCA + Revisão de Aditivo de Insumos FeNO',
        paymentTerms: 'Net 30 DDL c/ Reembolso Pass-Through Integral sem Holdback',
        holdbackPercentage: 5.0,
        retroactiveBillingLimitDays: 120,
        taxRegimeProfile: 'CRO_NACIONAL_RETENCAO_6_15_PCT (ISS 2% + Retenção Federal 6,15%)',
        taxWithholdingRatePct: 6.15,
        minBatchThresholdBrl: 10000.0,
        cutOffWindowLabel: 'Fechamento Quinzenal (Visitas + Reembolsos Pass-Through de Compras)',
        readyToBillBrl: 21320.0,
        expiringRiskBrl: 0.0,
        conditionalEscrowBrl: 0.0,
        holdbackRetainedCumulativeBrl: 2140.0,
        totalAlreadyInvoicedBrl: 28400.0,
      },
      {
        studyCode: 'ESTUDO-423',
        contractCode: 'CTA-423-CLOSEOUT',
        studyTitle:
          'Estudo Clínico Fase III 423 (Em Encerramento / Close-Out & Liberação de Holdback)',
        sponsorName: 'Sanofi / Regeneron Global',
        croIntermediaryName: 'ICON Clinical Research Brasil',
        billingEntityName: 'Holding GIYUS Research Center Ltda. (São Paulo/SP)',
        billingEntityCnpj: '69.308.788/0001-02',
        contractCurrency: 'BRL',
        exchangeRatePtaxBrl: 1.0,
        annualInflationAdjustmentRule:
          'Quitação Final de Encerramento (Close-Out + Guarda de Arquivo 25 Anos)',
        paymentTerms: 'Net 15 DDL Especial Close-Out',
        holdbackPercentage: 0.0,
        retroactiveBillingLimitDays: 60,
        taxRegimeProfile: 'CRO_NACIONAL_RETENCAO_6_15_PCT (ISS 2% SP + Retenção 6,15%)',
        taxWithholdingRatePct: 6.15,
        minBatchThresholdBrl: 5000.0,
        cutOffWindowLabel: 'Gatilho Imediato de Close-Out (Termo de Desmobilização Assinado)',
        readyToBillBrl: 29400.0,
        expiringRiskBrl: 29400.0,
        conditionalEscrowBrl: 0.0,
        holdbackRetainedCumulativeBrl: 18900.0,
        totalAlreadyInvoicedBrl: 189000.0,
      },
    ],
    accountingAccumulatedCharges: [
      {
        id: 'ACC-CHG-001',
        chargeCode: 'COB-ACUM-2026-001',
        createdAtIso: '2026-10-25T14:20:00Z',
        performedDate: '2026-10-24',
        agingDays: 3,
        daysUntilRetroactiveForfeiture: 87,
        studyCode: 'EPOCA',
        contractCode: 'CTA-EPOCA-302-AZ',
        originSourceModule: 'Estudos Clínicos (Grade SoA / Medidata Rave) + Contrato Anexo I',
        chargeCategory: 'VISITA_CLINICA_SOA_ECRF',
        billingFlowKind: 'AUTO_SELF_BILLING_ECRF',
        title:
          'Lote Acumulado de 6 Visitas V2 (Randomização + Coleta PK Seriada 8h + Espirometria)',
        clinicalOrContractReference:
          'Cláusula 4.1 Anexo I Orçamento EPOCA · R$ 4.200,00/visita + IPCA R$ 190,00',
        participantOrBatchRef:
          'Participantes #BR-CWB-014 a #BR-CWB-019 (6 Visitas V2 Concluídas e Assinadas pelo PI)',
        quantity: 6,
        unitContractPriceBrl: 4390.0,
        inflationOrFxAdjustmentBrl: 1140.0,
        grossTotalBrl: 26340.0,
        holdbackRetainedBrl: 2634.0,
        estimatedTaxWithheldBrl: 1457.92,
        netReceivableBrl: 22248.08,
        status: 'ACCUMULATING_READY',
        proactiveGapWarning:
          '6 visitas V2 validadas no eCRF (SDV concluído pelo CRA); prontas para emissão de NFS-e do lote de Outubro.',
        linkedInvoiceNumber: null,
        supportingEvidenceNote:
          'Extrato Medidata Rave #OCT-2026-EPOCA + Assinatura ICP-Brasil Dr. Herberto Chong',
      },
      {
        id: 'ACC-CHG-002',
        chargeCode: 'COB-ACUM-2026-002',
        createdAtIso: '2026-08-12T10:00:00Z',
        performedDate: '2026-08-10',
        agingDays: 78,
        daysUntilRetroactiveForfeiture: 12,
        studyCode: 'EPOCA',
        contractCode: 'CTA-EPOCA-302-AZ',
        originSourceModule:
          'Contrato do Cadastro (Cláusula Visitas Não-Programadas / SAE) + Prontuário',
        chargeCategory: 'PROCEDIMENTO_CONDICIONAL_CONTRATO',
        billingFlowKind: 'INVOICE_REQUIRED_COM_COMPROVANTE',
        title:
          '⏰ 2 Visitas Não-Programadas por Broncoespasmo Agudo + Atendimento Médico SAE + Medicação de Resgate',
        clinicalOrContractReference:
          'Cláusula 6.3 (Unscheduled Visit & SAE Management — Exige Invoice Manual em até 90 dias)',
        participantOrBatchRef:
          'Participantes #BR-CWB-004 e #BR-CWB-009 (Atendimento Emergencial Hospital Dia)',
        quantity: 2,
        unitContractPriceBrl: 3400.0,
        inflationOrFxAdjustmentBrl: 0.0,
        grossTotalBrl: 6800.0,
        holdbackRetainedBrl: 680.0,
        estimatedTaxWithheldBrl: 376.38,
        netReceivableBrl: 5743.62,
        status: 'URGENT_RETROACTIVE_RISK',
        proactiveGapWarning:
          'GAP 1 DETECTADO (FALTAM 12 DIAS P/ PRESCREVER!): Contrato EPOCA bloqueia cobranças após 90 dias da visita (78/90 dias corridos). Emitir NFS-e imediatamente!',
        linkedInvoiceNumber: null,
        supportingEvidenceNote:
          'Relatório SAE submetido ao CEP/CONEP + Evolução Médica Assinada (Exige envio manual Invoice Required)',
      },
      {
        id: 'ACC-CHG-003',
        chargeCode: 'COB-ACUM-2026-003',
        createdAtIso: '2026-10-26T09:15:00Z',
        performedDate: '2026-10-26',
        agingDays: 1,
        daysUntilRetroactiveForfeiture: 89,
        studyCode: 'EPOCA',
        contractCode: 'CTA-EPOCA-302-AZ',
        originSourceModule: 'Cadastro do Contrato (Tabela Congelada e-TCLE por Página)',
        chargeCategory: 'TAXA_FIXA_CADASTRO_CONTRATO',
        billingFlowKind: 'INVOICE_REQUIRED_COM_COMPROVANTE',
        title:
          'Aplicação de e-TCLE por Página (Emenda #2 do Protocolo · 22 Páginas × 12 Participantes Ativos)',
        clinicalOrContractReference:
          'Anexo I Item 1.8 — Cobrança por Página de e-TCLE Aplicado (R$ 20,00/página · Valor Protegido no Contrato)',
        participantOrBatchRef:
          'Lote Re-consentimento Emenda #2 (12 Participantes × 22 páginas = 264 páginas)',
        quantity: 264,
        unitContractPriceBrl: 20.0,
        inflationOrFxAdjustmentBrl: 0.0,
        grossTotalBrl: 5280.0,
        holdbackRetainedBrl: 528.0,
        estimatedTaxWithheldBrl: 292.25,
        netReceivableBrl: 4459.75,
        status: 'ACCUMULATING_READY',
        proactiveGapWarning:
          'Calculado automaticamente pelo motor de páginas de TCLE do contrato; acumulado para compor a NFS-e mensal.',
        linkedInvoiceNumber: null,
        supportingEvidenceNote:
          'Log de Assinatura Eletrônica ICP-Brasil dos 12 termos e-TCLE (22 págs cada)',
      },
      {
        id: 'ACC-CHG-004',
        chargeCode: 'COB-ACUM-2026-004',
        createdAtIso: '2026-10-20T16:40:00Z',
        performedDate: '2026-10-20',
        agingDays: 7,
        daysUntilRetroactiveForfeiture: 83,
        studyCode: 'EPOCA',
        contractCode: 'CTA-EPOCA-302-AZ',
        originSourceModule: 'Estudos Clínicos (Triagem / Screen Failure Escrow)',
        chargeCategory: 'PROCEDIMENTO_CONDICIONAL_CONTRATO',
        billingFlowKind: 'INVOICE_REQUIRED_COM_COMPROVANTE',
        title:
          'Triagem Condicional (Screen Failure #SF-07 e #SF-08 — Aguardando Destrave da Proporção 1:3 Randomizados)',
        clinicalOrContractReference:
          'Cláusula 4.4 — Patrocinador paga até 1 Screen Failure para cada 3 pacientes randomizados',
        participantOrBatchRef:
          'Candidatos #SCR-CWB-041 e #SCR-CWB-042 (Falha de Critério FEV1 < 40%)',
        quantity: 2,
        unitContractPriceBrl: 2100.0,
        inflationOrFxAdjustmentBrl: 0.0,
        grossTotalBrl: 4200.0,
        holdbackRetainedBrl: 420.0,
        estimatedTaxWithheldBrl: 232.47,
        netReceivableBrl: 3547.53,
        status: 'CONDITIONAL_WAITING_UNLOCK',
        proactiveGapWarning:
          'GAP 6 (ESCROW DE SCREEN FAILURE): Aguardando randomização do 24º paciente (já randomizado hoje!) — Clique em Destravar para mover para a cesta de NFS-e.',
        linkedInvoiceNumber: null,
        supportingEvidenceNote:
          'Laudo de Espirometria de Triagem + Confirmação de Randomização #BR-CWB-024',
      },
      {
        id: 'ACC-CHG-005',
        chargeCode: 'COB-ACUM-2026-005',
        createdAtIso: '2026-10-22T11:30:00Z',
        performedDate: '2026-10-22',
        agingDays: 5,
        daysUntilRetroactiveForfeiture: 85,
        studyCode: 'SOLAIR',
        contractCode: 'CTA-SOLAIR-410-AZ',
        originSourceModule:
          'Estudos Clínicos (Visitas V4 SOLAIR) + Fornecedor Fleury/Pardini (Exames de Imagem)',
        chargeCategory: 'VISITA_CLINICA_SOA_ECRF',
        billingFlowKind: 'AUTO_SELF_BILLING_ECRF',
        title:
          '4 Visitas V4 de Seguimento DPOC + 4 Tomografias Torácicas de Alta Resolução (RECIST / Fleury)',
        clinicalOrContractReference:
          'Anexo I SOLAIR (USD 920/visita convertidos na PTAX R$ 5,68 = R$ 5.192,50/visita)',
        participantOrBatchRef:
          'Participantes #SOL-CWB-101 a #SOL-CWB-104 (Clínica Dr. Herberto Chong Filial)',
        quantity: 4,
        unitContractPriceBrl: 5192.5,
        inflationOrFxAdjustmentBrl: 780.0,
        grossTotalBrl: 20770.0,
        holdbackRetainedBrl: 2077.0,
        estimatedTaxWithheldBrl: 0.0,
        netReceivableBrl: 18693.0,
        status: 'ACCUMULATING_READY',
        proactiveGapWarning:
          'GAP 4 APLICADO: Contrato pago direto por Zurique/Suíça (Exportação de Serviços — 0% de retenção IRRF/CSRF).',
        linkedInvoiceNumber: null,
        supportingEvidenceNote: 'Laudos DICOM Fleury NF-e #000.892.114 + eCRF V4 validado',
      },
      {
        id: 'ACC-CHG-006',
        chargeCode: 'COB-ACUM-2026-006',
        createdAtIso: '2026-08-18T15:00:00Z',
        performedDate: '2026-08-18',
        agingDays: 70,
        daysUntilRetroactiveForfeiture: 20,
        studyCode: 'SOLAIR',
        contractCode: 'CTA-SOLAIR-410-AZ',
        originSourceModule:
          'Módulo de Fornecedores & Descarte PGRSS (Stericycle CDR #2026-991) + Compras',
        chargeCategory: 'PASS_THROUGH_COMPRAS_FORNECEDORES',
        billingFlowKind: 'INVOICE_REQUIRED_COM_COMPROVANTE',
        title:
          'Reembolso Pass-Through (100% Invoiceable): Descarte Certificado CDR Stericycle (R$ 1.420) + Taxa Renovação Anual CEP/CONEP (R$ 3.700)',
        clinicalOrContractReference:
          'Cláusula 8.2 Pass-Through Expense Reimbursement (Sem incidência de Holdback 10%)',
        participantOrBatchRef:
          'NFS-e Stericycle #2026-1109 + Certificado CDR-2026-991 + GRU/Comprovante Regulatório',
        quantity: 1,
        unitContractPriceBrl: 5120.0,
        inflationOrFxAdjustmentBrl: 0.0,
        grossTotalBrl: 5120.0,
        holdbackRetainedBrl: 0.0,
        estimatedTaxWithheldBrl: 0.0,
        netReceivableBrl: 5120.0,
        status: 'URGENT_RETROACTIVE_RISK',
        proactiveGapWarning:
          'GAP 1 + GAP 3: Despesa Pass-Through NÃO é paga automaticamente pelo eCRF e prescreve em 20 dias (70/90d)! Consolidar na Invoice imediatamente.',
        linkedInvoiceNumber: null,
        supportingEvidenceNote:
          'NFS-e #2026-1109 Stericycle + Certificado de Destruição CDR-2026-991 anexados',
      },
      {
        id: 'ACC-CHG-007',
        chargeCode: 'COB-ACUM-2026-007',
        createdAtIso: '2026-10-26T17:00:00Z',
        performedDate: '2026-10-26',
        agingDays: 1,
        daysUntilRetroactiveForfeiture: 119,
        studyCode: 'PERSIST',
        contractCode: 'CTA-PERSIST-208-GSK',
        originSourceModule:
          'Módulo de Compras Integrado (PED-INT-2026-004) + E-mail regulatorio@giyus.com.br + Visitas V3',
        chargeCategory: 'PASS_THROUGH_COMPRAS_FORNECEDORES',
        billingFlowKind: 'INVOICE_REQUIRED_COM_COMPROVANTE',
        title:
          'Reembolso Pass-Through (120 Bocais FeNO NIOX VERO — R$ 4.920,00) + 4 Visitas V3 Imunobiológico (R$ 16.400,00)',
        clinicalOrContractReference:
          '1º Aditivo Contratual PERSIST (Confirmado via regulatorio@giyus.com.br) + Grade SoA V3',
        participantOrBatchRef:
          'Pedido PED-INT-2026-004 (Circassia NIOX) + 4 Visitas V3 (#PER-011 a #PER-014)',
        quantity: 1,
        unitContractPriceBrl: 21320.0,
        inflationOrFxAdjustmentBrl: 0.0,
        grossTotalBrl: 21320.0,
        holdbackRetainedBrl: 820.0,
        estimatedTaxWithheldBrl: 1260.75,
        netReceivableBrl: 19239.25,
        status: 'ACCUMULATING_READY',
        proactiveGapWarning:
          'Integração Automática Compras → Contabilidade: O pedido PED-INT-2026-004 de bocais FeNO já caiu automaticamente na cesta de reembolso do estudo PERSIST!',
        linkedInvoiceNumber: null,
        supportingEvidenceNote:
          'NF-e Circassia Bocais FeNO + E-mail Aprovação 1º Aditivo GSK/IQVIA',
      },
      {
        id: 'ACC-CHG-008',
        chargeCode: 'COB-ACUM-2026-008',
        createdAtIso: '2026-10-24T12:00:00Z',
        performedDate: '2026-10-24',
        agingDays: 3,
        daysUntilRetroactiveForfeiture: 57,
        studyCode: 'ESTUDO-423',
        contractCode: 'CTA-423-CLOSEOUT',
        originSourceModule:
          'Contrato do Cadastro (Cláusula de Close-Out, Guarda 25 Anos & Resgate de Holdback 10%)',
        chargeCategory: 'TAXA_FIXA_CADASTRO_CONTRATO',
        billingFlowKind: 'INVOICE_REQUIRED_COM_COMPROVANTE',
        title:
          'Resgate Integral de Retenção Contratual Acumulada (10% Holdback = R$ 18.900,00) + Taxa Close-Out & Guarda 25 Anos (R$ 10.500,00)',
        clinicalOrContractReference:
          'Cláusula 11.4 Close-Out & Final Holdback Release (Destravado após coleta reversa AWB-WC-9948120-RMA)',
        participantOrBatchRef:
          'Encerramento Oficial Estudo 423 (18 Notas Fiscais anteriores com 10% retido)',
        quantity: 1,
        unitContractPriceBrl: 29400.0,
        inflationOrFxAdjustmentBrl: 0.0,
        grossTotalBrl: 29400.0,
        holdbackRetainedBrl: 0.0,
        estimatedTaxWithheldBrl: 1808.1,
        netReceivableBrl: 27591.9,
        status: 'URGENT_RETROACTIVE_RISK',
        proactiveGapWarning:
          'GAP 2 (RESGATE DE HOLDBACK 10% NO CLOSE-OUT): O Estudo 423 teve a coleta reversa agendada! Emitir a NFS-e final agora para resgatar os R$ 18.900,00 retidos ao longo do estudo.',
        linkedInvoiceNumber: null,
        supportingEvidenceNote:
          'Termo de Close-Out Assinado + Guia AWB-WC-9948120-RMA de Devolução da Centrífuga Beckman',
      },
    ],
    accountingIssuedInvoices: [
      {
        id: 'INV-ACC-001',
        invoiceNumber: 'NFS-e #20260938',
        verificationCodeSefaz: 'SEFAZ-CWB-20260938-44B',
        documentKind: 'NFSE_PREFEITURA_PESQUISA_CLINICA',
        issuedAtDate: '2026-10-05',
        dueDate: '2026-11-04',
        studyCode: 'EPOCA',
        contractCode: 'MINUTA-EPOCA-2026-TRIALS',
        billingEntityName:
          'TRIALS HUB LTDA (Exclusivo na Minuta Local — Sem outros centros e sem GIYUS)',
        billingEntityCnpj: '66.125.390/0001-34',
        payerLegalName: 'ASSOCIAÇÃO BENEFICENTE SÍRIA — Hcor (fiscal@hcor.com.br)',
        payerCnpjOrTaxId: '60.453.024/0003-90',
        taxRegimeApplied: 'FATURAMENTO_EXCLUSIVO_TRIALSHUB_HCOR (ISS Curitiba + Retenção 6,15%)',
        bundledChargesCount: 9,
        bundledItemsSummary:
          '8 Visitas V1/V2 Setembro/2026 no Trials Hub Ltda (PI Dr. Marco Stephan) + Reembolso Kits BD (PED-INT-2026-003)',
        grossAmountBrl: 48200.0,
        holdbackRetainedBrl: 4545.0,
        issAmountBrl: 873.1,
        irrfCsrfWithheldBrl: 2684.78,
        netReceivableAmountBrl: 40970.22,
        paymentStatus: 'ISSUED_AWAITING_PAYMENT',
        paidAtDate: null,
        bankReconciliationRef:
          'NFS-e emitida exclusivamente pelo TRIALS HUB LTDA (CNPJ 66.125.390/0001-34) para fiscal@hcor.com.br',
      },
      {
        id: 'INV-ACC-002',
        invoiceNumber: 'INV-INT-20260939',
        verificationCodeSefaz: 'SWIFT-CH-AZ-992014',
        documentKind: 'COMMERCIAL_INVOICE_EXPORTACAO_SERVICOS',
        issuedAtDate: '2026-09-28',
        dueDate: '2026-10-28',
        studyCode: 'SOLAIR',
        contractCode: 'CTA-SOLAIR-410-AZ',
        billingEntityName: 'Clínica Dr. Herberto Chong — Filial GIYUS Curitiba',
        billingEntityCnpj: '51.785.454/0002-37',
        payerLegalName: 'AstraZeneca AB (Zurique / Suíça — Direct Foreign Sponsor Payment)',
        payerCnpjOrTaxId: 'CHE-105.909.036',
        taxRegimeApplied:
          'EXPORTACAO_SERVICOS_ISS_ZERO (0% Retenção Fonte · Ingresso de Divisas Câmbio BCB)',
        bundledChargesCount: 6,
        bundledItemsSummary:
          '5 Visitas V3 SOLAIR + Start-Up Regulatório Emenda Global (Convertido PTAX R$ 5,68)',
        grossAmountBrl: 34600.0,
        holdbackRetainedBrl: 3460.0,
        issAmountBrl: 0.0,
        irrfCsrfWithheldBrl: 0.0,
        netReceivableAmountBrl: 31140.0,
        paymentStatus: 'PAID_RECONCILED',
        paidAtDate: '2026-10-24',
        bankReconciliationRef:
          'Liquidado via Contrato de Câmbio BCB #FX-2026-88412 (Banco Itaú Empresas · Conciliado por financeiro@giyus.com.br)',
      },
      {
        id: 'INV-ACC-003',
        invoiceNumber: 'NFS-e #20260940',
        verificationCodeSefaz: 'SEFAZ-CWB-20260940-19C',
        documentKind: 'NFSE_PREFEITURA_PESQUISA_CLINICA',
        issuedAtDate: '2026-10-12',
        dueDate: '2026-11-11',
        studyCode: 'PERSIST',
        contractCode: 'CTA-PERSIST-208-GSK',
        billingEntityName: 'GIYUS Curitiba Research Center Ltda. (Matriz)',
        billingEntityCnpj: '69.584.441/0001-84',
        payerLegalName: 'IQVIA RDS Brasil Ltda. (Patrocinador GSK)',
        payerCnpjOrTaxId: '04.891.220/0001-18',
        taxRegimeApplied: 'CRO_NACIONAL_RETENCAO_6_15_PCT (ISS 2% + IRRF/CSRF 6,15%)',
        bundledChargesCount: 5,
        bundledItemsSummary:
          'Taxa Start-Up Farmácia PERSIST + 4 Visitas V1/V2 + Pass-Through Gelo Seco CrioSul',
        grossAmountBrl: 28400.0,
        holdbackRetainedBrl: 1320.0,
        issAmountBrl: 541.6,
        irrfCsrfWithheldBrl: 1665.42,
        netReceivableAmountBrl: 25414.58,
        paymentStatus: 'ISSUED_AWAITING_PAYMENT',
        paidAtDate: null,
        bankReconciliationRef: 'Aguardando compensação programada Net 30 DDL em 11/11/2026',
      },
    ],
    accountingProactiveGaps: [
      {
        gapCode: 'GAP-CONT-01',
        title:
          '1. Trava Anti-Prescrição de Cobrança Retroativa (Janela Contratual de 90 Dias / Time-Barred)',
        riskPrevented:
          'Evita que o centro perca o direito contratual de cobrar visitas não-programadas (Unscheduled), SAEs ou reembolsos Pass-Through esquecidos há mais de 90 dias.',
        controlMechanism:
          'Contador regressivo automático de Aging por cláusula contratual (alerta vermelho aos 60 dias / T-30d antes da prescrição de 90 dias).',
        impactMetricLabel: 'R$ 31.320,00 protegidos contra prescrição neste mês',
        severity: 'CRITICAL',
      },
      {
        gapCode: 'GAP-CONT-02',
        title:
          '2. Conta Contábil de Retenção Contratual Acumulada (Holdback 5% a 10%) & Resgate no Close-Out',
        riskPrevented:
          'Impede que os 10% retidos pela CRO em cada Nota Fiscal ao longo de 2 a 4 anos de estudo caiam no esquecimento ao final do estudo.',
        controlMechanism:
          'Razão contábil auxiliar de Holdback a Receber por estudo, que dispara automaticamente a minuta de Nota Fiscal de Resgate quando o estudo entra em Close-Out (ex.: R$ 18.900,00 no Estudo 423).',
        impactMetricLabel: 'R$ 39.369,00 rastreados em Holdback Acumulado',
        severity: 'HIGH',
      },
      {
        gapCode: 'GAP-CONT-03',
        title:
          '3. Separação entre Auto-Pagamento eCRF (Self-Billing) vs. Itens "Invoice Required" c/ Comprovante',
        riskPrevented:
          'CROs pagam visitas normais automaticamente pelo eCRF, mas NÃO pagam Start-Up, e-TCLE por página, SAE, Visitas Não-Programadas e Pass-Through de Compras/Descarte se a contabilidade não enviar Invoice + Comprovante.',
        controlMechanism:
          'Classificação automática de cada item acumulado em "AUTO_SELF_BILLING_ECRF" vs. "INVOICE_REQUIRED_COM_COMPROVANTE", com dossiê de anexos (NF-e de compra, Certificado CDR, Log ICP-Brasil).',
        impactMetricLabel: '100% dos itens fora da grade padrão capturados',
        severity: 'CRITICAL',
      },
      {
        gapCode: 'GAP-CONT-04',
        title:
          '4. Enquadramento Tributário & Validação de CNPJ Emissor (Exportação ISS 0% vs. Retenção Nacional 6,15%)',
        riskPrevented:
          'Evita emissão de NFS-e pelo CNPJ errado (Matriz vs. Filial Chong vs. Holding) ou pagamento indevido de impostos em contratos pagos direto do exterior (Suíça/EUA/UK).',
        controlMechanism:
          'Motor fiscal vinculado ao cadastro do contrato: aplica 0% de retenção na exportação direta (SOLAIR/Zurique) e calcula automaticamente IRRF (1,5%) + PIS/COFINS/CSLL (4,65%) + ISS (2%) nas CROs nacionais.',
        impactMetricLabel: 'Economia fiscal e zero rejeição de NFS-e por CNPJ divergente',
        severity: 'HIGH',
      },
      {
        gapCode: 'GAP-CONT-05',
        title:
          '5. Gatilho de Reajuste Inflacionário Anual (IPCA) & Proteção Cambial PTAX BCB no Lote Acumulado',
        riskPrevented:
          'Impede que procedimentos realizados após 12 meses de contrato sejam faturados pelo valor antigo sem correção de IPCA ou com PTAX defasada.',
        controlMechanism:
          'Aplica automaticamente o índice de aniversário do contrato (IPCA +4,62% no EPOCA) e a PTAX BCB vigente (R$ 5,68 no SOLAIR) sobre cada item antes de emitir a Nota Fiscal.',
        impactMetricLabel: '+R$ 1.920,00 recuperados em correção IPCA/PTAX nos lotes atuais',
        severity: 'MEDIUM',
      },
      {
        gapCode: 'GAP-CONT-06',
        title:
          '6. Cesta Condicional de Screen Failures (Escrow Ratio 1:3) & Destrave Automático por Randomização',
        riskPrevented:
          'Quando o contrato limita o pagamento de falhas de triagem (ex.: 1 Screen Failure a cada 3 randomizados), triagens excedentes costumam ser descartadas pela equipe.',
        controlMechanism:
          'Mantém os Screen Failures excedentes em uma sub-cesta condicional ("Escrow") e avisa automaticamente quando novas randomizações destravam o direito de faturá-los.',
        impactMetricLabel: 'R$ 4.200,00 em Screen Failures prontos para destravar no EPOCA',
        severity: 'HIGH',
      },
    ],
    financeReimbursementItems: DEFAULT_FAKE_FINANCE_REIMBURSEMENT_ITEMS.map((item) => ({
      ...item,
    })),
    budgetEmailDrafts: [],
    threadDoubtResolutions: {},
    manualThreadMerges: {},
    isGreenfield: false,
    registeredPis: [...AVAILABLE_PI_CATALOG],
    registeredSubs: [...AVAILABLE_SUB_CATALOG],
    registeredCoordinators: [...AVAILABLE_COORDINATOR_CATALOG],
    registeredResearchCenters: [...AVAILABLE_RESEARCH_CENTERS_CATALOG],
    clinicalSpecialties: [...DEFAULT_CLINICAL_SPECIALTIES_CATALOG],
    clinicalHealthFunctions: [...DEFAULT_CLINICAL_HEALTH_FUNCTIONS_CATALOG],
    supplierAutoInvoices: [...DEFAULT_SUPPLIER_AUTO_INVOICES],
    supplierSubscriptions: [...DEFAULT_SUPPLIER_SUBSCRIPTIONS],
    autonomousRunsCount: 1,
    lastAutonomousRunAtIso: '2026-10-26T05:00:00Z',
  };
}

const DEFAULT_SUPPLIER_AUTO_INVOICES: readonly SupplierAutoInvoiceAndPaymentItemDto[] = [
  {
    id: 'sup-inv-auto-001',
    supplierId: '0199b900-0000-7000-8000-000000000001',
    supplierCode: 'SUP-WC-IATA',
    supplierTradeName: 'World Courier (IATA Cold Chain)',
    supplierCnpj: '43.854.116/0001-09',
    billingEmailMatched: 'faturamento.br@worldcourier.com',
    sharedMailboxSource: 'financeiro@giyus.com.br',
    emailSubject: 'NF-e #000.884.112 + Comprovante AWB Coleta Dry-Ice -80°C — Estudo EPOCA',
    receivedAtIso: '2026-10-29T10:15:00Z',
    invoiceNumber: 'NF-e 000.884.112',
    amountBrl: 4850,
    dueDate: '2026-11-15',
    studyCode: 'EPOCA',
    paymentStatus: 'PAID_AUTOMATIC',
    paidAtIso: '2026-10-29T16:40:00Z',
    paymentProofRef: 'PIX/TED Conciliado Automaticamente #WC-2026-884112',
    autoMatchedReason:
      'Relacionado automaticamente pelo e-mail financeiro do fornecedor (faturamento.br@worldcourier.com) e CNPJ 43.854.116/0001-09 na caixa compartilhada financeiro@giyus.com.br',
  },
  {
    id: 'sup-inv-auto-002',
    supplierId: '0199b900-0000-7000-8000-000000000002',
    supplierCode: 'SUP-FLEURY-IMG',
    supplierTradeName: 'Fleury Medicina e Saúde (Imagem & Patologia)',
    supplierCnpj: '60.840.055/0001-31',
    billingEmailMatched: 'faturamento.pesquisa@grupofleury.com.br',
    sharedMailboxSource: 'financeiro@giyus.com.br',
    emailSubject:
      'NFS-e #2026-4418 — Tomografia Tórax/Abdome RECIST 1.1 & Eco Strain — Estudos SOLAIR / EPOCA',
    receivedAtIso: '2026-10-29T14:20:00Z',
    invoiceNumber: 'NFS-e 2026-4418',
    amountBrl: 4300,
    dueDate: '2026-11-20',
    studyCode: 'SOLAIR',
    paymentStatus: 'PENDING_PAYMENT',
    paidAtIso: null,
    paymentProofRef: null,
    autoMatchedReason:
      'Relacionado automaticamente pelo e-mail de cobrança cadastrado no fornecedor (faturamento.pesquisa@grupofleury.com.br) na caixa compartilhada financeiro@giyus.com.br',
  },
  {
    id: 'sup-inv-auto-003',
    supplierId: '0199b900-0000-7000-8000-000000000003',
    supplierCode: 'SUP-LABCORP-CL',
    supplierTradeName: 'Labcorp Central Laboratory',
    supplierCnpj: '04.921.540/0001-88',
    billingEmailMatched: 'billing.brazil@labcorp.com',
    sharedMailboxSource: 'financeiro@giyus.com.br',
    emailSubject: 'NF-e #000.319.405 — Kits Criogênicos e Tubos PK — Estudo EPOCA (PC-2026-0101)',
    receivedAtIso: '2026-10-28T09:10:00Z',
    invoiceNumber: 'NF-e 000.319.405',
    amountBrl: 18450,
    dueDate: '2026-11-10',
    studyCode: 'EPOCA',
    paymentStatus: 'PAID_AUTOMATIC',
    paidAtIso: '2026-10-29T11:05:00Z',
    paymentProofRef: 'Liquidação Automática 3-Way Match #LC-2026-319405',
    autoMatchedReason:
      'Relacionado automaticamente pelo e-mail financeiro (billing.brazil@labcorp.com) e Ordem de Compra PC-2026-0101',
  },
];

const DEFAULT_SUPPLIER_SUBSCRIPTIONS: readonly SupplierSubscriptionItemDto[] = [
  {
    id: 'sub-wc-001',
    supplierId: '0199b900-0000-7000-8000-000000000001',
    supplierCode: 'SUP-WC-IATA',
    supplierTradeName: 'World Courier (IATA Cold Chain)',
    supplierCnpj: '43.854.116/0001-09',
    categoryCode: 'COURIER_IATA',
    productOrServiceName:
      'Assinatura Mensal de Monitoramento GPS/DataLogger Térmico Cloud 24/7 & Reserva de Caixas Credo Cube (-80°C)',
    monthlyFeeBrl: 2450,
    annualizedAmountBrl: 29400,
    billingDayOfMonth: 10,
    nextBillingDate: '2026-11-10',
    billingFinanceEmail: 'faturamento.br@worldcourier.com',
    linkedStudyCodes: ['EPOCA', 'SOLAIR', 'PERSIST'],
    status: 'ACTIVE',
    autoInvoiceReconciliation: true,
    lastPaidDate: '2026-10-10',
    lastInvoiceNumber: 'NFS-e SUB-WC-2026-10',
  },
  {
    id: 'sub-fl-002',
    supplierId: '0199b900-0000-7000-8000-000000000002',
    supplierCode: 'SUP-FLEURY-IMG',
    supplierTradeName: 'Fleury Medicina e Saúde (Imagem & Patologia)',
    supplierCnpj: '60.840.055/0001-31',
    categoryCode: 'IMAGING_LAB',
    productOrServiceName:
      'Portal PACS/DICOM Cloud Anonimizado p/ Monitores CRA & Armazenamento de Imagens de Estudos Clínicos',
    monthlyFeeBrl: 1690,
    annualizedAmountBrl: 20280,
    billingDayOfMonth: 5,
    nextBillingDate: '2026-11-05',
    billingFinanceEmail: 'faturamento.pesquisa@grupofleury.com.br',
    linkedStudyCodes: ['EPOCA', 'SOLAIR', 'ARGON'],
    status: 'ACTIVE',
    autoInvoiceReconciliation: true,
    lastPaidDate: '2026-10-05',
    lastInvoiceNumber: 'NFS-e SUB-FL-2026-10',
  },
];

const CLINICAL_CATEGORY_LABELS: Record<string, string> = {
  IMAGING_LAB: 'Laboratório de Imagem',
  CENTRAL_LAB: 'Laboratório Central',
  COURIER_IATA: 'Transportadora (Cadeia Fria IATA)',
  CALIBRATION: 'Calibração RBC/INMETRO',
  PHARMACY_KITS: 'Kits de Farmácia & Insumos',
};

function computeSupplierAutoLinkedStudies(
  state: FakeSuppliersAndStudiesState,
  clinicalCategories: readonly string[],
  relatedProcedures: readonly string[],
  imagingExamTypes: readonly string[],
  isCentralLab: boolean,
  centralLabServedExams: readonly string[],
): SupplierAutoLinkedStudyDto[] {
  const cats = new Set(clinicalCategories.map((c) => c.toUpperCase()));
  if (isCentralLab) {
    cats.add('CENTRAL_LAB');
  }
  const studiesCatalog =
    state.studies.length > 0
      ? state.studies
      : [
          {
            code: 'EPOCA',
            title: 'Ensaio Clínico Fatorial 2x2 Colchicina e Polipílula (EPOCA)',
          },
          {
            code: 'SOLAIR',
            title: 'Estudo Fase II/III Imunoterapia Combinada (SOLAIR)',
          },
          {
            code: 'PERSIST',
            title: 'Estudo Fase IIIB Asma Grave Eosinofílica (PERSIST)',
          },
          {
            code: 'ADEPT-4',
            title: 'Estudo Fase III Imunodermatologia (ADEPT-4)',
          },
        ];

  const linked: SupplierAutoLinkedStudyDto[] = [];
  const catArray = Array.from(cats);
  if (catArray.length === 0) return linked;

  const combinedLabel = catArray.map((c) => CLINICAL_CATEGORY_LABELS[c] ?? c).join(' + ');

  const scopeParts: string[] = [];
  if (relatedProcedures.length > 0) {
    scopeParts.push(`Procedimentos: ${relatedProcedures.slice(0, 2).join(', ')}`);
  }
  if (imagingExamTypes.length > 0) {
    scopeParts.push(`Exames de Imagem: ${imagingExamTypes.slice(0, 2).join(', ')}`);
  }
  if (isCentralLab && centralLabServedExams.length > 0) {
    scopeParts.push(`Lab Central: ${centralLabServedExams.slice(0, 2).join(', ')}`);
  }
  const fallbackScope =
    scopeParts.join(' · ') ||
    `Vinculado automaticamente pelas categorias clínicas (${combinedLabel})`;

  for (const st of studiesCatalog) {
    const code = st.code.toUpperCase();
    const primaryCat = catArray[0] ?? 'IMAGING_LAB';
    linked.push({
      studyCode: code,
      studyTitle: st.title,
      clinicalCategoryCode: primaryCat,
      clinicalCategoryLabel: combinedLabel,
      matchedScopeSummary: fallbackScope,
    });
  }
  return linked;
}

function buildOrderSharedMailboxDraft(params: {
  orderKind: 'SERVICE_ORDER' | 'PURCHASE_ORDER';
  orderCode: string;
  title: string;
  supplier: SupplierItem | undefined;
  isRecurringItem: boolean;
  lastHistoricalUnitPriceBrl: number | null;
  currentUnitPriceBrl: number;
  totalAmountBrl: number;
  studyCode: string | null;
}): {
  priceComparisonStatus:
    'SAME_PRICE_AUTO_APPROVED_ORDER' | 'PRICE_CHANGED_REQUIRES_NEGOTIATION' | 'NEW_QUOTE_REQUEST';
  sharedMailboxDraftSubject: string;
  sharedMailboxDraftTo: string;
  sharedMailboxDraftCc: string;
  sharedMailboxDraftBody: string;
  computedOrderStatus: string;
} {
  const {
    orderKind,
    orderCode,
    title,
    supplier,
    isRecurringItem,
    lastHistoricalUnitPriceBrl,
    currentUnitPriceBrl,
    totalAmountBrl,
    studyCode,
  } = params;

  const kindLabel =
    orderKind === 'SERVICE_ORDER' ? 'Ordem de Serviço (OS)' : 'Ordem de Compra (OC)';
  const supplierName = supplier?.tradeName ?? 'Fornecedor Qualificado';
  const contactName = supplier?.companyContactName ?? supplierName;
  const toEmails =
    [supplier?.contactEmail, ...(supplier?.contactPersons?.map((p) => p.email) ?? [])]
      .filter((e): e is string => Boolean(e?.trim()))
      .slice(0, 2)
      .join('; ') || 'comercial@fornecedor.com.br';

  const ccEmails = [
    supplier?.billingFinanceEmail,
    'contratos@giyus.com.br',
    'financeiro@giyus.com.br',
  ]
    .filter((e): e is string => Boolean(e?.trim()))
    .join('; ');

  const studyRef = studyCode ? ` (Estudo ${studyCode})` : '';

  if (
    isRecurringItem &&
    lastHistoricalUnitPriceBrl !== null &&
    lastHistoricalUnitPriceBrl > 0 &&
    Math.abs(currentUnitPriceBrl - lastHistoricalUnitPriceBrl) < 0.01
  ) {
    return {
      priceComparisonStatus: 'SAME_PRICE_AUTO_APPROVED_ORDER',
      computedOrderStatus: orderKind === 'SERVICE_ORDER' ? 'Confirmed' : 'Approved',
      sharedMailboxDraftSubject: `[PEDIDO JÁ APROVADO — PREÇO RECORRENTE MANTIDO] ${orderCode} · ${title}${studyRef} — ${supplierName}`,
      sharedMailboxDraftTo: toEmails,
      sharedMailboxDraftCc: ccEmails,
      sharedMailboxDraftBody: [
        `Prezado(a) ${contactName} (${supplierName}),`,
        '',
        `Referente à ${kindLabel} ${orderCode} — "${title}"${studyRef}:`,
        `Por tratar-se de item/serviço recorrente com o MESMO PREÇO praticado anteriormente (R$ ${currentUnitPriceBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} · Total R$ ${totalAmountBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}), formalizamos o pedido JÁ APROVANDO a compra/solicitação de serviço imediatamente.`,
        '',
        `Favor prosseguir com o atendimento e enviar a Nota Fiscal para financeiro@giyus.com.br.`,
        '',
        'Atenciosamente,',
        'Central de Suprimentos, Compras & Operações Clínicas GIYUS (contratos@giyus.com.br)',
      ].join('\n'),
    };
  }

  if (
    isRecurringItem &&
    lastHistoricalUnitPriceBrl !== null &&
    lastHistoricalUnitPriceBrl > 0 &&
    Math.abs(currentUnitPriceBrl - lastHistoricalUnitPriceBrl) >= 0.01
  ) {
    return {
      priceComparisonStatus: 'PRICE_CHANGED_REQUIRES_NEGOTIATION',
      computedOrderStatus:
        orderKind === 'SERVICE_ORDER' ? 'PendingNegotiationAndApproval' : 'PendingApproval',
      sharedMailboxDraftSubject: `[SOLICITAÇÃO DE ORÇAMENTO & NEGOCIAÇÃO — PREÇO DIFERENTE] ${orderCode} · ${title}${studyRef} — ${supplierName}`,
      sharedMailboxDraftTo: toEmails,
      sharedMailboxDraftCc: ccEmails,
      sharedMailboxDraftBody: [
        `Prezado(a) ${contactName} (${supplierName}),`,
        '',
        `Solicitamos orçamento e negociação comercial referente à ${kindLabel} ${orderCode} — "${title}"${studyRef}.`,
        `Identificamos que o valor unitário atual (R$ ${currentUnitPriceBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}) está diferente do valor histórico recorrente (R$ ${lastHistoricalUnitPriceBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}).`,
        '',
        `- Caso confirmem a manutenção do preço anterior (R$ ${lastHistoricalUnitPriceBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}), faremos o pedido já aprovando imediatamente a compra/solicitação de serviço.`,
        `- Caso o preço seja diferente, a proposta passará pelo fluxo de negociação e aprovação da Diretoria GIYUS.`,
        '',
        'Atenciosamente,',
        'Central de Suprimentos, Compras & Contratos GIYUS (contratos@giyus.com.br)',
      ].join('\n'),
    };
  }

  return {
    priceComparisonStatus: 'NEW_QUOTE_REQUEST',
    computedOrderStatus: orderKind === 'SERVICE_ORDER' ? 'DraftQuoteRequest' : 'PendingApproval',
    sharedMailboxDraftSubject: `[SOLICITAÇÃO DE ORÇAMENTO] ${orderCode} · ${title}${studyRef} — ${supplierName}`,
    sharedMailboxDraftTo: toEmails,
    sharedMailboxDraftCc: ccEmails,
    sharedMailboxDraftBody: [
      `Prezado(a) ${contactName} (${supplierName}),`,
      '',
      `Solicitamos orçamento comercial referente à ${kindLabel} ${orderCode} — "${title}"${studyRef} (Valor estimado: R$ ${totalAmountBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}).`,
      '',
      'Favor responder este e-mail com a proposta formal, prazo de atendimento e condições de faturamento para avaliação e aprovação.',
      '',
      'Atenciosamente,',
      'Central de Suprimentos & Compras GIYUS (contratos@giyus.com.br)',
    ].join('\n'),
  };
}

async function readBodyObj(request: Request): Promise<Record<string, unknown>> {
  return (await readJsonBody(request)) as Record<string, unknown>;
}

function strField(body: Record<string, unknown>, key: string, fallback = ''): string {
  const val = body[key];
  return typeof val === 'string' && val.length > 0 ? val : fallback;
}

function optStrField(body: Record<string, unknown>, key: string): string | null {
  const val = body[key];
  return typeof val === 'string' && val.length > 0 ? val : null;
}

function numField(body: Record<string, unknown>, key: string, fallback = 0): number {
  const val = body[key];
  return typeof val === 'number' && Number.isFinite(val) ? val : fallback;
}

export async function handleSuppliersAndStudiesRoute(
  request: Request,
  segments: readonly string[],
  state: FakeSuppliersAndStudiesState,
): Promise<Response | null> {
  const method = request.method.toUpperCase();

  // DOCUMENTS (/api/v1/network/documents/* and /api/v1/tenants/:tenantId/documents/*)
  const isNetworkDocs = segments[0] === 'network' && segments[1] === 'documents';
  const isTenantDocs = segments[0] === 'tenants' && segments[2] === 'documents';
  if (isNetworkDocs || isTenantDocs) {
    const subSegment = isNetworkDocs ? segments[2] : segments[3];
    if (subSegment === 'overview' && method === 'GET') {
      return jsonResponse({
        documents: [],
        signatures: [],
        templates: [],
        retentionClasses: [
          {
            code: 'RET-QA-5Y',
            namePtBr: 'Qualidade e Conformidade (5 Anos)',
            nameEnUs: 'Quality & Compliance (5 Years)',
            retentionYears: 5,
            regulatoryBasis: 'RDC ANVISA 09/2015',
          },
          {
            code: 'RET-CLIN-15Y',
            namePtBr: 'Dossiê Clínico TMF/ISF (15 Anos)',
            nameEnUs: 'Clinical Trial Master File (15 Years)',
            retentionYears: 15,
            regulatoryBasis: 'ICH-GCP E6(R3) & CONEP',
          },
        ],
      });
    }
    if (subSegment === 'items' && method === 'POST') {
      const body = await readBodyObj(request);
      return jsonResponse(
        {
          id: `doc-${Date.now()}`,
          code: strField(body, 'code', 'DOC-001'),
          title: strField(body, 'title', 'Documento'),
          categoryCode: strField(body, 'categoryCode', 'SOP'),
          studyCode: null,
          retentionClassCode: strField(body, 'retentionClassCode', 'RET-QA-5Y'),
          currentVersionNumber: 1,
          status: 'Draft',
          sharingScope: strField(body, 'sharingScope', 'NetworkOnly'),
          sha256Hash: '',
          isLegalHold: false,
          authorName: 'Sistema GIYUS',
          effectiveDateIso: new Date().toISOString(),
          signaturesCount: 0,
        },
        201,
      );
    }
  }

  if (segments[0] !== 'network') {
    return null;
  }

  // SUPPLIERS (/api/v1/network/suppliers*)
  if (segments[1] === 'suppliers') {
    if (segments.length === 2 && method === 'GET') {
      return jsonResponse(state.suppliers);
    }
    if (segments.length === 2 && method === 'POST') {
      const body = await readBodyObj(request);
      const nowIso = new Date().toISOString();
      const nextWeekIso = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      const categoryCode = strField(body, 'categoryCode', 'IMAGING_LAB');
      const clinicalCategories = Array.isArray(body['clinicalCategories'])
        ? (body['clinicalCategories'] as unknown[]).map((c) => String(c).trim()).filter(Boolean)
        : [categoryCode];
      const relatedProcedures = Array.isArray(body['relatedProcedures'])
        ? (body['relatedProcedures'] as unknown[]).map((p) => String(p).trim()).filter(Boolean)
        : [];
      const imagingExamTypes = Array.isArray(body['imagingExamTypes'])
        ? (body['imagingExamTypes'] as unknown[]).map((e) => String(e).trim()).filter(Boolean)
        : [];
      const isCentralLab =
        Boolean(body['isCentralLab']) || clinicalCategories.includes('CENTRAL_LAB');
      const centralLabServedExams = Array.isArray(body['centralLabServedExams'])
        ? (body['centralLabServedExams'] as unknown[]).map((e) => String(e).trim()).filter(Boolean)
        : [];
      const coversAllLocations = Boolean(body['coversAllLocations']);
      const rawCities = Array.isArray(body['servedCitiesStates'])
        ? (body['servedCitiesStates'] as unknown[]).map((c) => String(c).trim()).filter(Boolean)
        : [];
      const servedCitiesStates = coversAllLocations
        ? Array.from(new Set(['TODOS', ...rawCities]))
        : rawCities.length > 0
          ? rawCities
          : optStrField(body, 'cityUf')
            ? [strField(body, 'cityUf')]
            : [];
      const contactPersons: SupplierContactPersonDto[] = Array.isArray(body['contactPersons'])
        ? (body['contactPersons'] as Record<string, unknown>[]).map((cp, idx) => ({
            id: strField(cp, 'id', `cp-${Date.now()}-${idx}`),
            name: strField(cp, 'name', ''),
            phone: strField(cp, 'phone', ''),
            email: strField(cp, 'email', ''),
            responsibilityDescription: strField(cp, 'responsibilityDescription', ''),
          }))
        : [];
      const hasMonthlySubscription = Boolean(body['hasMonthlySubscription']);
      const subscriptionProductOrServiceName = strField(
        body,
        'subscriptionProductOrServiceName',
        '',
      );
      const subscriptionMonthlyFeeBrl = numField(body, 'subscriptionMonthlyFeeBrl', 0);
      const subscriptionBillingDay = numField(body, 'subscriptionBillingDay', 10);

      const autoLinkedStudies = computeSupplierAutoLinkedStudies(
        state,
        clinicalCategories,
        relatedProcedures,
        imagingExamTypes,
        isCentralLab,
        centralLabServedExams,
      );

      const created: SupplierItem = {
        id: `0199b900-0000-7000-8000-${String(state.suppliers.length + 10).padStart(12, '0')}`,
        code: strField(body, 'code', 'SUP-NEW').toUpperCase(),
        legalName: strField(body, 'legalName'),
        tradeName: strField(body, 'tradeName'),
        country: strField(body, 'country', 'BR'),
        registrationNumber: strField(body, 'registrationNumber'),
        categoryCode,
        clinicalCategories,
        mainCnae: strField(body, 'mainCnae', ''),
        secondaryCnaes: Array.isArray(body['secondaryCnaes'])
          ? (body['secondaryCnaes'] as unknown[]).map((s) => String(s))
          : [],
        cnesRecords: Array.isArray(body['cnesRecords'])
          ? (body['cnesRecords'] as SupplierItem['cnesRecords'] & unknown[])
          : [],
        qsaSummary: strField(body, 'qsaSummary', ''),
        taxIdStatus: strField(body, 'taxIdStatus'),
        taxRegime: strField(body, 'taxRegime'),
        companyPorte: strField(body, 'companyPorte'),
        cityUf: strField(body, 'cityUf'),
        address: strField(body, 'address', ''),
        relatedProcedures,
        imagingExamTypes,
        isCentralLab,
        centralLabServedExams,
        autoLinkedStudies,
        companyContactName: strField(body, 'companyContactName', ''),
        contactEmail: strField(body, 'contactEmail'),
        contactPhone: optStrField(body, 'contactPhone'),
        billingFinanceContactName: strField(body, 'billingFinanceContactName', ''),
        billingFinanceEmail: strField(body, 'billingFinanceEmail', strField(body, 'contactEmail')),
        contactPersons,
        coversAllLocations,
        servedCitiesStates,
        hasMonthlySubscription,
        subscriptionProductOrServiceName,
        subscriptionMonthlyFeeBrl,
        subscriptionBillingDay,
        lastWeeklySyncAtIso: nowIso,
        nextWeeklySyncAtIso: nextWeekIso,
        weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
        status: 'Active',
        qualificationStatus: 'Qualified',
        qualificationExpiresAt: '2028-10-30',
        notes: optStrField(body, 'notes'),
        coverageRulesCount: servedCitiesStates.length,
        activeOrdersCount: 0,
      };
      state.suppliers.unshift(created);

      // Cria regra de cobertura correspondente
      state.coverageRules.unshift({
        id: `cov-${Date.now()}`,
        supplierId: created.id,
        supplierName: created.tradeName,
        kind: coversAllLocations
          ? 'TODOS (Nacional / Todas as Localidades)'
          : 'Cidades/UF Selecionadas',
        effect: 'Include',
        stateCode: coversAllLocations ? 'TODOS' : (created.cityUf?.split('/')[1] ?? null),
        ibgeMunicipalityCode: null,
        centerLatitude: null,
        centerLongitude: null,
        radiusKm: null,
        categoryCode: created.categoryCode,
        effectiveFrom: nowIso.slice(0, 10),
        effectiveTo: null,
        notes: coversAllLocations
          ? 'Atende TODAS as Cidades e Estados (Cobertura Total)'
          : `Atende: ${servedCitiesStates.join(', ')}`,
      });

      // Se possui assinatura mensal, adiciona automaticamente ao Painel de Subscrição
      if (hasMonthlySubscription && subscriptionProductOrServiceName) {
        if (!state.supplierSubscriptions) {
          state.supplierSubscriptions = [];
        }
        state.supplierSubscriptions.unshift({
          id: `sub-${Date.now()}`,
          supplierId: created.id,
          supplierCode: created.code,
          supplierTradeName: created.tradeName,
          supplierCnpj: created.registrationNumber,
          categoryCode: created.categoryCode,
          productOrServiceName: subscriptionProductOrServiceName,
          monthlyFeeBrl: subscriptionMonthlyFeeBrl,
          annualizedAmountBrl: Number((subscriptionMonthlyFeeBrl * 12).toFixed(2)),
          billingDayOfMonth: subscriptionBillingDay,
          nextBillingDate: `2026-11-${String(Math.min(28, Math.max(1, subscriptionBillingDay))).padStart(2, '0')}`,
          billingFinanceEmail: created.billingFinanceEmail ?? created.contactEmail,
          linkedStudyCodes: autoLinkedStudies.map((s) => s.studyCode),
          status: 'ACTIVE',
          autoInvoiceReconciliation: true,
          lastPaidDate: nowIso.slice(0, 10),
          lastInvoiceNumber: `NFS-e SUB-${created.code}-01`,
        });
      }

      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(created, 201);
    }
    if (segments[2] === 'weekly-cnpj-sync' && segments.length === 3 && method === 'POST') {
      const syncedAtIso = new Date().toISOString();
      const nextScheduledSyncAtIso = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
      let totalCnesSyncedCount = 0;
      state.suppliers = state.suppliers.map((s) => {
        totalCnesSyncedCount += s.cnesRecords?.length ?? 1;
        return {
          ...s,
          lastWeeklySyncAtIso: syncedAtIso,
          nextWeeklySyncAtIso: nextScheduledSyncAtIso,
          weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
        };
      });
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse({
        ok: true,
        syncedSuppliersCount: state.suppliers.length,
        totalCnesSyncedCount,
        syncedAtIso,
        nextScheduledSyncAtIso,
        schedulePolicy: 'WEEKLY_EVERY_7_DAYS',
        suppliers: state.suppliers,
      });
    }
    if (segments[2] === 'auto-invoices' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.supplierAutoInvoices ?? DEFAULT_SUPPLIER_AUTO_INVOICES);
    }
    if (
      segments[2] === 'auto-sync-mailbox-invoices' &&
      segments.length === 3 &&
      method === 'POST'
    ) {
      if (!state.supplierAutoInvoices || state.supplierAutoInvoices.length === 0) {
        state.supplierAutoInvoices = [...DEFAULT_SUPPLIER_AUTO_INVOICES];
      }
      // Para cada fornecedor cadastrado que ainda não tenha NF conciliada, verifica e-mails/reembolsos
      for (const sup of state.suppliers) {
        const alreadyHas = state.supplierAutoInvoices.some((inv) => inv.supplierId === sup.id);
        if (!alreadyHas) {
          state.supplierAutoInvoices.unshift({
            id: `sup-inv-auto-${Date.now()}-${Math.floor(Math.random() * 100)}`,
            supplierId: sup.id,
            supplierCode: sup.code,
            supplierTradeName: sup.tradeName,
            supplierCnpj: sup.registrationNumber,
            billingEmailMatched: sup.billingFinanceEmail || sup.contactEmail,
            sharedMailboxSource: 'financeiro@giyus.com.br',
            emailSubject: `NF-e / Cobrança Automática — ${sup.tradeName}`,
            receivedAtIso: new Date().toISOString(),
            invoiceNumber: `NFS-e 2026-${Math.floor(1000 + Math.random() * 8999)}`,
            amountBrl:
              sup.subscriptionMonthlyFeeBrl && sup.subscriptionMonthlyFeeBrl > 0
                ? sup.subscriptionMonthlyFeeBrl
                : 2850,
            dueDate: '2026-11-20',
            studyCode: sup.autoLinkedStudies?.[0]?.studyCode ?? 'EPOCA',
            paymentStatus: 'PAID_AUTOMATIC',
            paidAtIso: new Date().toISOString(),
            paymentProofRef: `Conciliação Automática Caixa Compartilhada #${sup.code}`,
            autoMatchedReason: `Relacionado automaticamente pelo e-mail financeiro (${sup.billingFinanceEmail || sup.contactEmail}) e CNPJ ${sup.registrationNumber} na caixa compartilhada financeiro@giyus.com.br`,
          });
        }
      }
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(state.supplierAutoInvoices);
    }
    if (segments[2] === 'subscriptions' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.supplierSubscriptions ?? DEFAULT_SUPPLIER_SUBSCRIPTIONS);
    }
    if (segments[2] === 'subscriptions' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      if (!state.supplierSubscriptions) {
        state.supplierSubscriptions = [...DEFAULT_SUPPLIER_SUBSCRIPTIONS];
      }
      const supplierId = strField(body, 'supplierId', state.suppliers[0]?.id ?? '');
      const supplier = state.suppliers.find((s) => s.id === supplierId) ?? state.suppliers[0];
      const monthlyFeeBrl = numField(body, 'monthlyFeeBrl', 1500);
      const billingDayOfMonth = numField(body, 'billingDayOfMonth', 10);
      const productOrServiceName = strField(
        body,
        'productOrServiceName',
        'Assinatura Mensal de Serviço Clínico / Plataforma',
      );
      const linkedStudyCodes = Array.isArray(body['linkedStudyCodes'])
        ? (body['linkedStudyCodes'] as unknown[]).map((c) => String(c).trim()).filter(Boolean)
        : (supplier?.autoLinkedStudies?.map((s) => s.studyCode) ?? ['EPOCA']);

      const createdSub: SupplierSubscriptionItemDto = {
        id: `sub-${Date.now()}`,
        supplierId: supplier?.id ?? supplierId,
        supplierCode: supplier?.code ?? 'SUP-SUB',
        supplierTradeName: supplier?.tradeName ?? 'Fornecedor Subscrição',
        supplierCnpj: supplier?.registrationNumber ?? '',
        categoryCode: supplier?.categoryCode ?? 'IMAGING_LAB',
        productOrServiceName,
        monthlyFeeBrl,
        annualizedAmountBrl: Number((monthlyFeeBrl * 12).toFixed(2)),
        billingDayOfMonth,
        nextBillingDate: `2026-11-${String(Math.min(28, Math.max(1, billingDayOfMonth))).padStart(2, '0')}`,
        billingFinanceEmail:
          strField(body, 'billingFinanceEmail', '') ||
          supplier?.billingFinanceEmail ||
          supplier?.contactEmail ||
          'financeiro@fornecedor.com.br',
        linkedStudyCodes,
        status: 'ACTIVE',
        autoInvoiceReconciliation: true,
        lastPaidDate: new Date().toISOString().slice(0, 10),
        lastInvoiceNumber: `NFS-e SUB-${supplier?.code ?? 'NEW'}-2026`,
      };
      state.supplierSubscriptions.unshift(createdSub);
      if (supplier) {
        const sIdx = state.suppliers.findIndex((s) => s.id === supplier.id);
        if (sIdx >= 0) {
          state.suppliers[sIdx] = {
            ...state.suppliers[sIdx]!,
            hasMonthlySubscription: true,
            subscriptionProductOrServiceName: productOrServiceName,
            subscriptionMonthlyFeeBrl: monthlyFeeBrl,
            subscriptionBillingDay: billingDayOfMonth,
          };
        }
      }
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(createdSub, 201);
    }
    if (segments[2] === 'coverage-rules' && method === 'GET') {
      return jsonResponse(state.coverageRules);
    }
    if (segments[2] === 'qualifications' && method === 'GET') {
      return jsonResponse(state.qualifications);
    }
    if (segments[2] === 'who-serves-me' && method === 'GET') {
      const matches: WhoServesMeMatchItem[] = state.suppliers.map((s) => ({
        supplierId: s.id,
        code: s.code,
        tradeName: s.tradeName,
        categoryCode:
          s.clinicalCategories && s.clinicalCategories.length > 0
            ? s.clinicalCategories.join(' + ')
            : s.categoryCode,
        qualificationStatus: s.qualificationStatus,
        isCovered: true,
        matchedRuleSummary: s.coversAllLocations
          ? 'TODOS (Atende Todas as Cidades e Estados do Brasil)'
          : `Cidades/UF atendidas: ${s.servedCitiesStates && s.servedCitiesStates.length > 0 ? s.servedCitiesStates.join(', ') : s.cityUf || 'não informada'}`,
        distanceKm: s.coversAllLocations ? 0 : 4.2,
      }));
      return jsonResponse(matches);
    }
    if (segments[2] === 'service-orders' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.serviceOrders);
    }
    if (segments[2] === 'service-orders' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const supplierId = strField(body, 'supplierId', state.suppliers[0]?.id ?? '');
      const supplier = state.suppliers.find((s) => s.id === supplierId) ?? state.suppliers[0];
      const code = strField(
        body,
        'code',
        `OS-2026-${String(state.serviceOrders.length + 10).padStart(3, '0')}`,
      ).toUpperCase();
      const description = strField(body, 'description', 'Solicitação de Serviço Clínico');
      const requestedAmountBrl = numField(body, 'requestedAmountBrl', 3500);
      const isRecurringItem = body['isRecurringItem'] !== false;
      const lastHistoricalUnitPriceBrl =
        typeof body['lastHistoricalUnitPriceBrl'] === 'number'
          ? body['lastHistoricalUnitPriceBrl']
          : isRecurringItem
            ? requestedAmountBrl
            : null;
      const currentUnitPriceBrl = numField(body, 'currentUnitPriceBrl', requestedAmountBrl);
      const studyCode = optStrField(body, 'studyCode');

      const draftInfo = buildOrderSharedMailboxDraft({
        orderKind: 'SERVICE_ORDER',
        orderCode: code,
        title: description,
        supplier,
        isRecurringItem,
        lastHistoricalUnitPriceBrl,
        currentUnitPriceBrl,
        totalAmountBrl: requestedAmountBrl,
        studyCode,
      });

      const createdOs: ServiceOrderItem = {
        id: `0199b903-0000-7000-8000-${String(state.serviceOrders.length + 10).padStart(12, '0')}`,
        code,
        supplierId: supplier?.id ?? supplierId,
        supplierName: supplier?.tradeName ?? 'Fornecedor Qualificado',
        categoryCode: strField(body, 'categoryCode', supplier?.categoryCode ?? 'COURIER_IATA'),
        studyCode,
        description,
        requestedAmountBrl,
        scheduledDate: strField(body, 'scheduledDate', '2026-11-05'),
        status: draftInfo.computedOrderStatus,
        isRecurringItem,
        lastHistoricalUnitPriceBrl,
        currentUnitPriceBrl,
        priceComparisonStatus: draftInfo.priceComparisonStatus,
        autoGeneratedDraft: true,
        sharedMailboxDraftSubject: draftInfo.sharedMailboxDraftSubject,
        sharedMailboxDraftTo: draftInfo.sharedMailboxDraftTo,
        sharedMailboxDraftCc: draftInfo.sharedMailboxDraftCc,
        sharedMailboxDraftBody: draftInfo.sharedMailboxDraftBody,
        sharedMailboxDraftStatus: 'READY_FOR_1CLICK_SEND',
        sharedMailboxDraftSentAtIso: null,
      };
      state.serviceOrders.unshift(createdOs);
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(createdOs, 201);
    }
    if (segments[2] === 'purchase-orders' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.purchaseOrders);
    }
    if (segments[2] === 'purchase-orders' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const supplierId = strField(body, 'supplierId', state.suppliers[0]?.id ?? '');
      const supplier = state.suppliers.find((s) => s.id === supplierId) ?? state.suppliers[0];
      const code = strField(body, 'code', 'PC-NEW').toUpperCase();
      const title = strField(body, 'title', 'Pedido de Compra');
      const totalAmountBrl = numField(body, 'totalAmountBrl', 5000);
      const isRecurringItem = Boolean(body['isRecurringItem']);
      const lastHistoricalUnitPriceBrl =
        typeof body['lastHistoricalUnitPriceBrl'] === 'number'
          ? body['lastHistoricalUnitPriceBrl']
          : isRecurringItem
            ? totalAmountBrl
            : null;
      const currentUnitPriceBrl = numField(body, 'currentUnitPriceBrl', totalAmountBrl);
      const studyCode = optStrField(body, 'studyCode');

      const draftInfo = buildOrderSharedMailboxDraft({
        orderKind: 'PURCHASE_ORDER',
        orderCode: code,
        title,
        supplier,
        isRecurringItem,
        lastHistoricalUnitPriceBrl,
        currentUnitPriceBrl,
        totalAmountBrl,
        studyCode,
      });

      const created: PurchaseOrderItem = {
        id: `0199b904-0000-7000-8000-${String(state.purchaseOrders.length + 10).padStart(12, '0')}`,
        code,
        title,
        categoryCode: strField(body, 'categoryCode', supplier?.categoryCode ?? 'PHARMACY_KITS'),
        supplierId: supplier?.id ?? supplierId,
        supplierName: supplier?.tradeName ?? 'Fornecedor Qualificado',
        studyCode,
        status: draftInfo.computedOrderStatus,
        totalAmountBrl,
        quotesCount: numField(body, 'quotesCount', 3),
        justification: strField(body, 'justification'),
        requestedByName: strField(body, 'requestedByName'),
        approvedByName:
          draftInfo.priceComparisonStatus === 'SAME_PRICE_AUTO_APPROVED_ORDER'
            ? 'Aprovação Automática (Item Recorrente Mesmo Preço)'
            : null,
        itemsJson: '[]',
        createdAtIso: new Date().toISOString(),
        isRecurringItem,
        lastHistoricalUnitPriceBrl,
        currentUnitPriceBrl,
        priceComparisonStatus: draftInfo.priceComparisonStatus,
        autoGeneratedDraft: true,
        sharedMailboxDraftSubject: draftInfo.sharedMailboxDraftSubject,
        sharedMailboxDraftTo: draftInfo.sharedMailboxDraftTo,
        sharedMailboxDraftCc: draftInfo.sharedMailboxDraftCc,
        sharedMailboxDraftBody: draftInfo.sharedMailboxDraftBody,
        sharedMailboxDraftStatus: 'READY_FOR_1CLICK_SEND',
        sharedMailboxDraftSentAtIso: null,
      };
      state.purchaseOrders.unshift(created);
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(created, 201);
    }
    if (
      segments[2] === 'orders' &&
      segments[3] === 'send-shared-mailbox-draft' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const orderKind = strField(body, 'orderKind', 'PURCHASE_ORDER').toUpperCase();
      const orderId = strField(body, 'orderId', '');
      const mailboxAddress = strField(body, 'mailboxAddress', 'contratos@giyus.com.br');
      const nowIso = new Date().toISOString();

      let draftSubject = '';
      let draftTo = '';
      let draftCc = '';
      let draftBody = '';
      let studyCode = 'EPOCA';

      if (orderKind === 'SERVICE_ORDER') {
        const idx = state.serviceOrders.findIndex(
          (o) => o.id === orderId || o.code.toLowerCase() === orderId.toLowerCase(),
        );
        if (idx >= 0 && state.serviceOrders[idx]) {
          const prev = state.serviceOrders[idx];
          draftSubject = prev.sharedMailboxDraftSubject ?? `Orçamento OS ${prev.code}`;
          draftTo = prev.sharedMailboxDraftTo ?? 'comercial@fornecedor.com.br';
          draftCc = prev.sharedMailboxDraftCc ?? mailboxAddress;
          draftBody = prev.sharedMailboxDraftBody ?? prev.description;
          studyCode = prev.studyCode ?? 'EPOCA';
          state.serviceOrders[idx] = {
            ...prev,
            sharedMailboxDraftStatus: 'SENT_TO_SHARED_MAILBOX_DRAFT',
            sharedMailboxDraftSentAtIso: nowIso,
          };
        }
      } else {
        const idx = state.purchaseOrders.findIndex(
          (o) => o.id === orderId || o.code.toLowerCase() === orderId.toLowerCase(),
        );
        if (idx >= 0 && state.purchaseOrders[idx]) {
          const prev = state.purchaseOrders[idx];
          draftSubject = prev.sharedMailboxDraftSubject ?? `Orçamento OC ${prev.code}`;
          draftTo = prev.sharedMailboxDraftTo ?? 'comercial@fornecedor.com.br';
          draftCc = prev.sharedMailboxDraftCc ?? mailboxAddress;
          draftBody = prev.sharedMailboxDraftBody ?? prev.justification;
          studyCode = prev.studyCode ?? 'EPOCA';
          state.purchaseOrders[idx] = {
            ...prev,
            sharedMailboxDraftStatus: 'SENT_TO_SHARED_MAILBOX_DRAFT',
            sharedMailboxDraftSentAtIso: nowIso,
          };
        }
      }

      const sharedDraftId = `draft-order-${Date.now()}`;
      state.budgetEmailDrafts.unshift({
        id: sharedDraftId,
        graphDraftMessageId: `AAMkADg0YzE-ORDER-DRAFT-${Date.now()}`,
        studyCode,
        dossierId: `order-${orderId}`,
        mailboxAddress,
        status: 'ORIGINAL_DRAFT',
        createdAtIso: nowIso,
        createdByEmail: 'syllas@giyus.com.br',
        updatedAtIso: nowIso,
        updatedByEmail: 'syllas@giyus.com.br',
        originalTo: draftTo,
        originalCc: draftCc,
        originalSubject: draftSubject,
        originalBody: draftBody,
        currentTo: draftTo,
        currentCc: draftCc,
        currentSubject: draftSubject,
        currentBody: draftBody,
        isModifiedFromOriginal: false,
        latestChanges: [],
        revisions: [],
      });

      writePersistedGreenfieldMailboxState(state);
      return jsonResponse({
        ok: true,
        orderKind,
        orderId,
        mailboxAddress,
        draftId: sharedDraftId,
        sentAtIso: nowIso,
        subject: draftSubject,
        to: draftTo,
      });
    }
    if (segments[2] === 'purchase-orders' && segments[4] === 'status' && method === 'POST') {
      const id = segments[3];
      const body = await readBodyObj(request);
      const idx = state.purchaseOrders.findIndex((p) => p.id === id);
      const existing = state.purchaseOrders[idx];
      if (idx >= 0 && existing) {
        const updated: PurchaseOrderItem = {
          ...existing,
          status: strField(body, 'status', existing.status),
          approvedByName: optStrField(body, 'approvedByName') ?? existing.approvedByName,
        };
        state.purchaseOrders[idx] = updated;
        return jsonResponse(updated);
      }
    }
    if (segments[3] === 'qualifications' && method === 'POST') {
      const supplierId = segments[2] ?? '';
      const body = await readBodyObj(request);
      const supplier = state.suppliers.find((s) => s.id === supplierId);
      const qual: QualificationItem = {
        id: `0199b902-0000-7000-8000-${String(state.qualifications.length + 10).padStart(12, '0')}`,
        supplierId,
        supplierName: supplier?.tradeName ?? 'Fornecedor',
        status: strField(body, 'status', 'Qualified'),
        assessedByName: strField(body, 'assessedByName'),
        assessedAtIso: new Date().toISOString(),
        approvedByName: strField(body, 'approvedByName'),
        approvedAtIso: new Date().toISOString(),
        validUntil: '2028-10-25',
        evidenceSummary: strField(body, 'evidenceSummary', 'Evidência validada.'),
      };
      state.qualifications.unshift(qual);
      return jsonResponse(qual, 201);
    }
    // External Non-GIYUS Partner Users (B2B / CRO / PI / Accounting)
    if (segments[2] === 'external-users' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.externalPartnerUsers);
    }
    if (segments[2] === 'external-users' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const email = strField(body, 'email');
      const domain = email.includes('@') ? (email.split('@')[1] ?? 'externo.com') : 'externo.com';
      const roleKind = strField(
        body,
        'partnerRoleKind',
        'EXTERNAL_SUPPLIER_B2B',
      ) as ExternalPartnerUserItem['partnerRoleKind'];
      const rawModules = body['allowedModules'];
      const allowedModules = Array.isArray(rawModules)
        ? rawModules.filter((m): m is string => typeof m === 'string')
        : ['Fornecedores B2B (Seus Dados)', 'Comparar Cotações (Responder RFP)'];
      const created: ExternalPartnerUserItem = {
        id: `ext-usr-${String(state.externalPartnerUsers.length + 1).padStart(3, '0')}`,
        displayName: strField(body, 'displayName'),
        email,
        externalDomain: domain,
        organizationName: strField(body, 'organizationName'),
        partnerRoleKind: roleKind,
        rlsBoundResource: strField(
          body,
          'rlsBoundResource',
          'Supplier: Escopo Isolado RLS por Organização/Estudo',
        ),
        allowedModules,
        blockedCapabilitiesSummary:
          'Bloqueado: Cotações de concorrentes, Margem de Contratos GIYUS e Administração da Holding',
        status: 'ACTIVE_B2B',
      };
      state.externalPartnerUsers.unshift(created);
      return jsonResponse(created, 201);
    }
    // Clinical Inventory & Kits FEFO
    if (segments[2] === 'inventory' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.inventoryItems);
    }
    if (
      segments[2] === 'inventory' &&
      segments[4] === 'movement' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const itemId = segments[3] ?? '';
      const body = await readBodyObj(request);
      const idx = state.inventoryItems.findIndex((i) => i.id === itemId);
      const existing = state.inventoryItems[idx];
      if (idx >= 0 && existing) {
        const movementKind = strField(body, 'movementKind', 'INBOUND_RECEIPT');
        const qty = Math.max(1, numField(body, 'quantity', 10));
        const nextQty =
          movementKind === 'INBOUND_RECEIPT'
            ? existing.currentQty + qty
            : Math.max(0, existing.currentQty - qty);
        const updated: ClinicalInventoryItemDto = {
          ...existing,
          currentQty: nextQty,
          totalStockValueBrl: nextQty * existing.unitCostBrl,
          fefoStatus: nextQty <= existing.reorderPointQty ? 'ALERTA_PONTO_PEDIDO' : 'OK_ESTOQUE',
        };
        state.inventoryItems[idx] = updated;
        return jsonResponse(updated);
      }
    }
    // Quote Comparisons (3-Quote Comparative Map -> Award & Auto-create PO + AP Ledger)
    if (segments[2] === 'quote-comparisons' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.quoteComparisons);
    }
    if (
      segments[2] === 'quote-comparisons' &&
      segments[4] === 'award' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const comparisonId = segments[3] ?? '';
      const body = await readBodyObj(request);
      const idx = state.quoteComparisons.findIndex((q) => q.id === comparisonId);
      const existing = state.quoteComparisons[idx];
      if (idx >= 0 && existing) {
        const supplierId = strField(body, 'supplierId');
        const winningProposal =
          existing.proposals.find((p) => p.supplierId === supplierId) ?? existing.proposals[0];
        const generatedPoCode = `PC-2026-010${state.purchaseOrders.length + 3}`;
        const updated: QuoteComparisonMapDto = {
          ...existing,
          status: 'AWARDED_CONVERTED_TO_PO',
          awardedSupplierId: winningProposal?.supplierId ?? supplierId,
          awardedSupplierName: winningProposal?.supplierName ?? 'Fornecedor Vencedor',
          generatedPoCode,
          savingsVsContractCeilingBrl:
            existing.contractCeilingTotalBrl -
            (winningProposal?.totalTcoBrl ?? existing.contractCeilingTotalBrl),
        };
        state.quoteComparisons[idx] = updated;

        // Auto-create PurchaseOrder in Procurement
        const newPo: PurchaseOrderItem = {
          id: `0199b904-0000-7000-8000-${String(state.purchaseOrders.length + 20).padStart(12, '0')}`,
          code: generatedPoCode,
          title: existing.title,
          categoryCode: existing.categoryCode,
          supplierId: updated.awardedSupplierId ?? '',
          supplierName: updated.awardedSupplierName ?? 'Fornecedor Qualificado',
          studyCode: existing.studyCode,
          status: 'Approved',
          totalAmountBrl: winningProposal?.totalTcoBrl ?? 22200,
          quotesCount: existing.proposals.length,
          justification: `Adjudicado via Mapa Comparativo ${existing.rfqCode} (Economia de R$ ${updated.savingsVsContractCeilingBrl.toLocaleString('pt-BR')} vs. Teto do Contrato).`,
          requestedByName: 'Motor de Ressuprimento FEFO & Compras',
          approvedByName: strField(body, 'approvedByName'),
          itemsJson: '[]',
          createdAtIso: '2026-10-26T16:35:00Z',
        };
        state.purchaseOrders.unshift(newPo);

        // Auto-create AP entry in Integrated Financial Ledger
        state.financialLedger.unshift({
          id: `fin-00${state.financialLedger.length + 1}`,
          documentCode: `PROV-${generatedPoCode}`,
          entryKind: 'AP_SUPPLIER_PURCHASE',
          counterpartyName: newPo.supplierName,
          studyCode: existing.studyCode,
          linkedPoOrVisitRef: `${generatedPoCode} · ${existing.rfqCode}`,
          description: `Conta a Pagar gerada da Adjudicação ${existing.rfqCode}: ${existing.title}`,
          amountBrl: newPo.totalAmountBrl,
          isPassThroughReimbursable: true,
          threeWayMatchStatus: 'MATCHED_3_WAY',
          dueDate: '2026-12-10',
          settlementStatus: 'SCHEDULED',
        });

        return jsonResponse(updated);
      }
    }
    // Integrated Financial Ledger (AP + AR + Contract Margin Reconciliation)
    if (segments[2] === 'financial-ledger' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.financialLedger);
    }
    if (
      segments[2] === 'financial-ledger' &&
      segments[4] === 'settle' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const entryId = segments[3] ?? '';
      const idx = state.financialLedger.findIndex((f) => f.id === entryId);
      const existing = state.financialLedger[idx];
      if (idx >= 0 && existing) {
        const updated: IntegratedFinancialLedgerItemDto = {
          ...existing,
          threeWayMatchStatus:
            existing.entryKind === 'AP_SUPPLIER_PURCHASE' ? 'MATCHED_3_WAY' : 'RECONCILED_PTAX',
          settlementStatus: 'SETTLED_PAID',
        };
        state.financialLedger[idx] = updated;
        return jsonResponse(updated);
      }
    }
    if (segments[2] === 'financial-reconciliations' && segments.length === 3 && method === 'GET') {
      if (state.dossiers.length === 0 && state.financialLedger.length === 0) {
        return jsonResponse([]);
      }
      const reconciliations: StudyFinancialReconciliationDto[] = [
        {
          studyCode: 'EPOCA',
          studyTitle: 'Protocolo EPOCA — Cardiologia & Nefrologia Fase III (AstraZeneca / IQVIA)',
          contractedRevenueBrl: 462800,
          passThroughBudgetBrl: 74200,
          actualPurchasesAndSuppliersBrl: 23300,
          reimbursedPassThroughBrl: 23300,
          plannedNetMarginPercent: 29.4,
          realizedNetMarginPercent: 31.1,
        },
        {
          studyCode: 'SOLAIR',
          studyTitle: 'Protocolo SOLAIR — Oncologia Torácica Fase II/III (Novartis / ICON)',
          contractedRevenueBrl: 618400,
          passThroughBudgetBrl: 112500,
          actualPurchasesAndSuppliersBrl: 34200,
          reimbursedPassThroughBrl: 34200,
          plannedNetMarginPercent: 27.8,
          realizedNetMarginPercent: 29.2,
        },
        {
          studyCode: 'PERSIST',
          studyTitle: 'Protocolo PERSIST — Asma Grave Eosinofílica Fase IIIB (GSK / IQVIA)',
          contractedRevenueBrl: 348900,
          passThroughBudgetBrl: 52000,
          actualPurchasesAndSuppliersBrl: 22200,
          reimbursedPassThroughBrl: 22200,
          plannedNetMarginPercent: 28.5,
          realizedNetMarginPercent: 30.4,
        },
      ];
      return jsonResponse(reconciliations);
    }
    if (segments[2] === 'governance-dossiers' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.supplierGovernanceDossiers);
    }
    if (segments[2] === 'timeline' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.supplierTimelineEvents);
    }
    if (
      segments[2] === 'governance-dossiers' &&
      segments[4] === 'resolve-pendency' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const id = segments[3] ?? '';
      const body = await readBodyObj(request);
      const resolutionNotes = strField(
        body,
        'resolutionNotes',
        'Certificado/NF-e validado no 3-Way Match e liquidado.',
      );
      const resolvedByEmail = strField(body, 'resolvedByEmail');
      const idx = state.supplierGovernanceDossiers.findIndex(
        (d) => d.id.toLowerCase() === id.toLowerCase(),
      );
      const existing = state.supplierGovernanceDossiers[idx];
      if (idx >= 0 && existing) {
        const updated: SupplierGovernanceDossierDto = {
          ...existing,
          pendingPaymentAmountBrl: 0,
          pendingInvoicesCount: 0,
          paymentPendencyStatus: 'NO_PENDENCY_PAID',
          paymentPendencyReason: `Pendência regularizada e liquidada por ${resolvedByEmail}: ${resolutionNotes}`,
        };
        state.supplierGovernanceDossiers[idx] = updated;
        state.supplierTimelineEvents.unshift({
          id: `STL-RES-${String(state.supplierTimelineEvents.length + 10)}`,
          supplierCode: updated.supplierCode,
          supplierName: updated.tradeName,
          eventDate: '2026-10-27',
          eventKind: 'PAYMENT_SETTLED',
          title: `Liquidação / Desbloqueio de Pendência — ${updated.tradeName}`,
          description: `Pendência anterior de R$ ${existing.pendingPaymentAmountBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} regularizada (${resolutionNotes}). Conciliação 3-Way Match concluída.`,
          invoiceOrDocumentCode: 'LIQ-3WAY-2026',
          invoiceDownloadFileName: `Comprovante-Liquidacao-${updated.supplierCode}.pdf`,
          certificateOrAttachmentFileName: null,
          amountBrl: existing.pendingPaymentAmountBrl,
          requestedByName: updated.requestedByName,
          requestedByEmail: resolvedByEmail,
          centerName: updated.requestingCenterName,
          geographicScopeBadge: updated.geographicScope,
          linkedItemsSummary: updated.linkedItemsSummary.join(' · '),
          paymentTermsApplied: updated.bestPaymentTerms,
          pendencyStatus: 'PAID_OK',
          observations: resolutionNotes,
        });
        return jsonResponse(updated);
      }
    }
    if (segments[2] === 'inventory-multi-supplier' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.inventoryMultiSupplierItems);
    }
    if (segments[2] === 'consumable-price-timeline' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.consumablePriceTimelineEntries);
    }
    if (segments[2] === 'consumable-price-timeline' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const skuCode = strField(body, 'skuCode', 'SKU-CRIO-2ML');
      const itemName = strField(body, 'itemName', 'Criotubos Estéreis 2mL RNase/DNase Free');
      const localityCityUf = strField(body, 'localityCityUf');
      const centerName = strField(body, 'centerName', 'GIYUS Curitiba Research Center (Matriz)');
      const supplierCode = strField(body, 'supplierCode', 'SUP-KIT-01');
      const supplierName = strField(
        body,
        'supplierName',
        'Thermo Fisher / Fisher Clinical Services',
      );
      const geographicScope = strField(
        body,
        'geographicScope',
        'NACIONAL_BRASIL',
      ) as ConsumablePriceLocalityTimelineEntryDto['geographicScope'];
      const unitPriceBrl = numField(body, 'unitPriceBrl', 5.5);
      const freightPerUnitBrl = numField(body, 'freightPerUnitBrl', 0.25);
      const landedUnitCostBrl = Number((unitPriceBrl + freightPerUnitBrl).toFixed(2));
      const contractCeilingUnitBrl = numField(body, 'contractCeilingUnitBrl', 7.2);
      const prevEntry = state.consumablePriceTimelineEntries.find(
        (e) =>
          e.skuCode.toLowerCase() === skuCode.toLowerCase() &&
          e.localityCityUf.toLowerCase() === localityCityUf.toLowerCase(),
      );
      const prevLanded = prevEntry ? prevEntry.landedUnitCostBrl : null;
      const deltaPercent =
        prevLanded && prevLanded > 0
          ? Number((((landedUnitCostBrl - prevLanded) / prevLanded) * 100).toFixed(2))
          : 0;
      const created: ConsumablePriceLocalityTimelineEntryDto = {
        id: `CPT-NEW-${String(state.consumablePriceTimelineEntries.length + 10)}`,
        eventDate: '2026-10-27',
        skuCode,
        itemName,
        localityCityUf,
        centerName,
        supplierCode,
        supplierName,
        geographicScope,
        unitPriceBrl,
        freightPerUnitBrl,
        landedUnitCostBrl,
        previousLandedUnitCostBrl: prevLanded,
        deltaPercentVsPrevious: deltaPercent,
        contractCeilingUnitBrl,
        savingsPerUnitVsCeilingBrl: Number((contractCeilingUnitBrl - landedUnitCostBrl).toFixed(2)),
        choiceReasonOrTrigger: strField(
          body,
          'choiceReasonOrTrigger',
          `Cotação registrada para ${localityCityUf} por melhor relação Preço × Prazo.`,
        ),
        invoiceOrQuoteRef: strField(body, 'invoiceOrQuoteRef', 'COT-LOC-2026-10'),
        recordedByEmail: strField(body, 'recordedByEmail'),
        costAllocationKind:
          strField(body, 'costAllocationKind', 'ATRELADO_A_CONTRATO') ===
          'CUSTO_OPERACIONAL_UNIDADE'
            ? 'CUSTO_OPERACIONAL_UNIDADE'
            : 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel: strField(
          body,
          'costAllocationTargetLabel',
          strField(body, 'costAllocationKind', 'ATRELADO_A_CONTRATO') ===
            'CUSTO_OPERACIONAL_UNIDADE'
            ? `Custo Próprio de Operação — ${centerName} (Sem Contrato)`
            : 'Atrelado a Contrato de Estudo Clínico (Pass-Through / SoA)',
        ),
      };
      state.consumablePriceTimelineEntries.unshift(created);
      return jsonResponse(created, 201);
    }
    if (segments[2] === 'procurement-engine' && segments.length === 3 && method === 'GET') {
      return jsonResponse(buildAutonomousProcurementSnapshot(state));
    }
    if (
      segments[2] === 'procurement-engine' &&
      segments[3] === 'policy' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const nextPhase =
        strField(body, 'governancePhase', 'FASE_1_COM_APROVACAO_HUMANA') ===
        'FASE_2_SEM_APROVACAO_HUMANA_ESSENCIAIS'
          ? 'FASE_2_SEM_APROVACAO_HUMANA_ESSENCIAIS'
          : 'FASE_1_COM_APROVACAO_HUMANA';
      const autoExp = body['autoApproveExpiringMedications'] !== false;
      const autoStudy = body['autoApproveStudyRequiredItems'] !== false;
      const autoEss = body['autoApproveEssentialSiteProducts'] !== false;
      const ceiling = Math.max(500, numField(body, 'autoApprovalCeilingPerOrderBrl', 15000));
      const updatedBy = strField(body, 'updatedByEmail');
      state.procurementPolicy = {
        governancePhase: nextPhase,
        autoApproveExpiringMedications: autoExp,
        autoApproveStudyRequiredItems: autoStudy,
        autoApproveEssentialSiteProducts: autoEss,
        autoApprovalCeilingPerOrderBrl: ceiling,
        requireQualifiedSupplierAndWithinCeiling: true,
        nonEssentialAlwaysRequiresHuman: true,
        lastUpdatedByEmail: updatedBy,
        lastUpdatedAtIso: '2026-10-27T06:05:00Z',
      };
      if (nextPhase === 'FASE_2_SEM_APROVACAO_HUMANA_ESSENCIAIS') {
        runAutonomousProcurementCycleOnState(state);
      }
      return jsonResponse(buildAutonomousProcurementSnapshot(state));
    }
    if (
      segments[2] === 'procurement-engine' &&
      segments[3] === 'orders' &&
      segments[5] === 'approve-human' &&
      segments.length === 6 &&
      method === 'POST'
    ) {
      const id = segments[4] ?? '';
      const body = await readBodyObj(request);
      const approvedByName = strField(body, 'approvedByName');
      const approvedByEmail = strField(body, 'approvedByEmail');
      const approvalNotes = strField(
        body,
        'approvalNotes',
        'Aprovado manualmente pelo gestor humano após validação de preço por localidade e teto orçamentário.',
      );
      const idx = state.integratedProcurementOrders.findIndex(
        (o) =>
          o.id.toLowerCase() === id.toLowerCase() || o.orderCode.toLowerCase() === id.toLowerCase(),
      );
      const existing = state.integratedProcurementOrders[idx];
      if (idx >= 0 && existing) {
        const updated: IntegratedProcurementOrderDto = {
          ...existing,
          approvalStatus: 'APPROVED_BY_HUMAN_ORDERED',
          approvedOrTriggeredBy: `Aprovado por Humano: ${approvedByName} (${approvedByEmail})`,
          approvedAtIso: '2026-10-27T06:10:00Z',
          erpIntegrationTrail: `${existing.erpIntegrationTrail} → Pedido Emitido com Aprovação Humana (${approvalNotes})`,
        };
        state.integratedProcurementOrders[idx] = updated;
        return jsonResponse(updated);
      }
    }
    if (
      segments[2] === 'procurement-engine' &&
      segments[3] === 'run-autonomous-cycle' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      runAutonomousProcurementCycleOnState(state);
      return jsonResponse(buildAutonomousProcurementSnapshot(state));
    }
    if (
      segments[2] === 'procurement-engine' &&
      segments[3] === 'orders' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const triggerKindRaw = strField(body, 'essentialityTriggerKind', 'NECESSARIO_PARA_UM_ESTUDO');
      const triggerKind: IntegratedProcurementOrderDto['essentialityTriggerKind'] =
        triggerKindRaw === 'PRODUTO_QUE_VENHA_A_VENCER' ||
        triggerKindRaw === 'NECESSARIO_PARA_UM_ESTUDO' ||
        triggerKindRaw === 'PRODUTO_ESSENCIAL_NECESSARIO' ||
        triggerKindRaw === 'NAO_ESSENCIAL_ACIMA_TETO'
          ? triggerKindRaw
          : 'NECESSARIO_PARA_UM_ESTUDO';
      const qty = Math.max(1, numField(body, 'quantityOrdered', 20));
      const unitPrice = Math.max(0.01, numField(body, 'unitLandedPriceBrl', 35));
      const ceilingUnit = Math.max(
        unitPrice,
        numField(body, 'contractOrBudgetCeilingUnitBrl', unitPrice * 1.15),
      );
      const totalOrder = Number((qty * unitPrice).toFixed(2));
      const savings = Number(((ceilingUnit - unitPrice) * qty).toFixed(2));
      const isEssential =
        (triggerKind === 'PRODUTO_QUE_VENHA_A_VENCER' ||
          triggerKind === 'NECESSARIO_PARA_UM_ESTUDO' ||
          triggerKind === 'PRODUTO_ESSENCIAL_NECESSARIO') &&
        totalOrder <= state.procurementPolicy.autoApprovalCeilingPerOrderBrl &&
        unitPrice <= ceilingUnit;
      const autoApproveNow =
        state.procurementPolicy.governancePhase === 'FASE_2_SEM_APROVACAO_HUMANA_ESSENCIAIS' &&
        isEssential;
      const costKind: IntegratedProcurementOrderDto['costAllocationKind'] =
        strField(body, 'costAllocationKind', 'ATRELADO_A_CONTRATO') === 'CUSTO_OPERACIONAL_UNIDADE'
          ? 'CUSTO_OPERACIONAL_UNIDADE'
          : 'ATRELADO_A_CONTRATO';
      const linkedStudy = optStrField(body, 'linkedStudyCode');
      const centerName = strField(body, 'centerName', 'GIYUS Curitiba Research Center (Matriz)');
      const supplierScopeRaw = strField(body, 'supplierScope', 'LOCAL_CIDADE_UF');
      const supplierScope: IntegratedProcurementOrderDto['supplierScope'] =
        supplierScopeRaw === 'REGIONAL_SUL_SUDESTE' || supplierScopeRaw === 'NACIONAL_BRASIL'
          ? supplierScopeRaw
          : 'LOCAL_CIDADE_UF';
      const nextNum = state.integratedProcurementOrders.length + 1;
      const created: IntegratedProcurementOrderDto = {
        id: `IPO-${String(nextNum).padStart(3, '0')}`,
        orderCode: `PED-INT-2026-${String(nextNum).padStart(3, '0')}`,
        createdAtIso: '2026-10-27T06:15:00Z',
        skuCode: strField(body, 'skuCode', 'SKU-NOVO-INSUMO'),
        itemName: strField(body, 'itemName', 'Insumo Clínico Integrado'),
        categoryCode: strField(body, 'categoryCode', 'LAB_KITS_CONSUMABLES'),
        essentialityTriggerKind: triggerKind,
        triggerHeadline: strField(
          body,
          'triggerHeadline',
          'Pedido gerado no Módulo de Compras Integrado.',
        ),
        originModuleIntegration: strField(
          body,
          'originModuleIntegration',
          'Estoque Clínico FEFO + Estudos SoA + Fornecedores + Financeiro',
        ),
        linkedStudyCode: linkedStudy,
        costAllocationKind: costKind,
        costAllocationTargetLabel:
          costKind === 'CUSTO_OPERACIONAL_UNIDADE'
            ? `Custo Próprio de Operação — ${centerName} (OPEX · Sem Contrato)`
            : `Atrelado ao Contrato do Estudo ${linkedStudy ?? 'EPOCA'} (Pass-Through / SoA)`,
        localityCityUf: strField(body, 'localityCityUf'),
        centerName,
        selectedSupplierCode: strField(body, 'selectedSupplierCode', 'SUP-MEDHOSP-CWB-01'),
        selectedSupplierName: strField(
          body,
          'selectedSupplierName',
          'MedHosp Distribuidora Hospitalar Curitiba',
        ),
        supplierScope,
        paymentTermsApplied: strField(body, 'paymentTermsApplied', '28 DDL Boleto Faturado'),
        quantityOrdered: qty,
        unitLandedPriceBrl: unitPrice,
        contractOrBudgetCeilingUnitBrl: ceilingUnit,
        totalOrderBrl: totalOrder,
        savingsVsCeilingBrl: savings,
        daysUntilExpirationOrStockout:
          typeof body['daysUntilExpirationOrStockout'] === 'number'
            ? body['daysUntilExpirationOrStockout']
            : 15,
        isMandatoryMedication: Boolean(body['isMandatoryMedication']),
        approvalMode: isEssential ? 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE' : 'REQUIRES_HUMAN_APPROVAL',
        approvalStatus: autoApproveNow ? 'AUTO_ORDERED_WITHOUT_HUMAN' : 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy: autoApproveNow
          ? 'Emitido Automaticamente Sem Aprovação Humana (Fase 2 · Compra Essencial dentro do Teto)'
          : `Solicitado por ${strField(body, 'requestedByEmail') || 'solicitante não informado'} (Aguardando Aprovação Humana)`,
        approvedAtIso: autoApproveNow ? '2026-10-27T06:15:00Z' : null,
        erpIntegrationTrail: autoApproveNow
          ? 'Gatilho Essencial → Auto-Aprovado Zero-Touch (Fase 2) → PO Emitida ao Fornecedor + Lançamento 3-Way Match'
          : 'Gatilho Integrado → Aguardando Aprovação Humana Inicial (Fase 1)',
      };
      state.integratedProcurementOrders.unshift(created);
      return jsonResponse(created, 201);
    }
    if (segments[2] === 'pickup-engine' && segments.length === 3 && method === 'GET') {
      return jsonResponse(buildSupplierPickupSnapshot(state));
    }
    if (
      segments[2] === 'pickup-engine' &&
      segments[3] === 'policy' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const nextPhase =
        strField(body, 'governancePhase', 'FASE_1_HUMANO_APROVA_FORNECEDOR_AGENDA') ===
        'FASE_2_AUTOMATICO_SEM_HUMANO_FORNECEDOR_AGENDA'
          ? 'FASE_2_AUTOMATICO_SEM_HUMANO_FORNECEDOR_AGENDA'
          : 'FASE_1_HUMANO_APROVA_FORNECEDOR_AGENDA';
      state.pickupPolicy = {
        governancePhase: nextPhase,
        autoApproveBiologicalSamplePickups: body['autoApproveBiologicalSamplePickups'] !== false,
        autoApproveCalibrationAndReverseLogisticsPickups:
          body['autoApproveCalibrationAndReverseLogisticsPickups'] !== false,
        autoApproveCertifiedDisposalPickups: body['autoApproveCertifiedDisposalPickups'] !== false,
        autoTriggerSupplierSlotBookingWhenApiAvailable:
          body['autoTriggerSupplierSlotBookingWhenApiAvailable'] !== false,
        enforceMonToWedWindowForBiologicalSamples: true,
        autoApprovalCeilingPerPickupBrl: Math.max(
          500,
          numField(body, 'autoApprovalCeilingPerPickupBrl', 10000),
        ),
        lastUpdatedByEmail: strField(body, 'updatedByEmail'),
        lastUpdatedAtIso: '2026-10-27T06:28:00Z',
      };
      if (nextPhase === 'FASE_2_AUTOMATICO_SEM_HUMANO_FORNECEDOR_AGENDA') {
        runAutonomousPickupCycleOnState(state);
      }
      return jsonResponse(buildSupplierPickupSnapshot(state));
    }
    if (
      segments[2] === 'pickup-engine' &&
      segments[3] === 'pickups' &&
      segments[5] === 'approve-human' &&
      segments.length === 6 &&
      method === 'POST'
    ) {
      const id = segments[4] ?? '';
      const body = await readBodyObj(request);
      const approvedByName = strField(body, 'approvedByName');
      const approvedByEmail = strField(body, 'approvedByEmail');
      const approvalNotes = strField(
        body,
        'approvalNotes',
        'Coleta aprovada pelo humano; notificação disparada para o fornecedor agendar a janela de retirada.',
      );
      const idx = state.supplierPickups.findIndex(
        (p) =>
          p.id.toLowerCase() === id.toLowerCase() ||
          p.pickupCode.toLowerCase() === id.toLowerCase(),
      );
      const existing = state.supplierPickups[idx];
      if (idx >= 0 && existing) {
        const updated: SupplierPickupRequestDto = {
          ...existing,
          workflowStatus: 'APPROVED_BY_HUMAN_AWAITING_SUPPLIER_SCHEDULE',
          approvedOrTriggeredBy: `Aprovado pelo Humano: ${approvedByName} (${approvedByEmail}) — Notificação enviada para ${existing.supplierName} agendar a coleta`,
          approvedAtIso: '2026-10-27T06:28:00Z',
          erpIntegrationTrail: `${existing.erpIntegrationTrail} → Coleta Aprovada pelo Humano (${approvalNotes}) → ⏳ Aguardando o Fornecedor Agendar a Coleta`,
        };
        state.supplierPickups[idx] = updated;
        return jsonResponse(updated);
      }
    }
    if (
      segments[2] === 'pickup-engine' &&
      segments[3] === 'pickups' &&
      segments[5] === 'supplier-schedule' &&
      segments.length === 6 &&
      method === 'POST'
    ) {
      const id = segments[4] ?? '';
      const body = await readBodyObj(request);
      const idx = state.supplierPickups.findIndex(
        (p) =>
          p.id.toLowerCase() === id.toLowerCase() ||
          p.pickupCode.toLowerCase() === id.toLowerCase(),
      );
      const existing = state.supplierPickups[idx];
      if (idx >= 0 && existing) {
        const schedDate = strField(
          body,
          'scheduledPickupDate',
          '2026-10-28 (Terça-feira · Janela Seg–Qua Validada)',
        );
        const schedWindow = strField(body, 'scheduledPickupWindow', '10:00 às 12:00');
        const awb = strField(
          body,
          'awbOrTrackingCode',
          `AWB-${existing.supplierCode}-2026-${810 + idx}`,
        );
        const agent = strField(
          body,
          'courierAgentName',
          `Agente Credenciado ${existing.supplierName}`,
        );
        const schedEmail = strField(body, 'supplierScheduledByEmail');
        const notes = strField(
          body,
          'confirmationNotes',
          'Agendamento confirmado pelo fornecedor dentro do SLA e com embalagem/temperatura validadas.',
        );
        const updated: SupplierPickupRequestDto = {
          ...existing,
          workflowStatus: 'SUPPLIER_SCHEDULED_CONFIRMED',
          scheduledPickupDate: schedDate,
          scheduledPickupWindow: schedWindow,
          awbOrTrackingCode: awb,
          courierAgentName: agent,
          supplierScheduledByEmail: schedEmail,
          supplierScheduledAtIso: '2026-10-27T06:30:00Z',
          erpIntegrationTrail: `${existing.erpIntegrationTrail} → Coleta Agendada pelo Fornecedor (${schedDate} · ${schedWindow} · ${awb})`,
        };
        state.supplierPickups[idx] = updated;
        state.supplierTimelineEvents.unshift({
          id: `STL-COL-${String(state.supplierTimelineEvents.length + 10)}`,
          supplierCode: existing.supplierCode,
          supplierName: existing.supplierName,
          eventDate: '2026-10-27',
          eventKind: 'PURCHASE_OR_SERVICE_ORDER',
          title: `Coleta Agendada pelo Fornecedor (${existing.pickupCode}) — ${schedDate} (${schedWindow})`,
          description: `Fornecedor ${existing.supplierName} agendou a coleta '${existing.title}' em ${existing.centerName}. Rastreio/AWB: ${awb} (${agent}). ${notes}`,
          invoiceOrDocumentCode: awb,
          invoiceDownloadFileName: `Manifesto-Coleta-${existing.pickupCode}.pdf`,
          certificateOrAttachmentFileName: null,
          amountBrl: existing.estimatedCostBrl,
          requestedByName: schedEmail,
          requestedByEmail: schedEmail,
          centerName: existing.centerName,
          geographicScopeBadge: existing.supplierScope,
          linkedItemsSummary: existing.title,
          paymentTermsApplied: existing.costAllocationTargetLabel,
          pendencyStatus: 'SCHEDULED_NET',
          observations: notes,
        });
        return jsonResponse(updated);
      }
    }
    if (
      segments[2] === 'pickup-engine' &&
      segments[3] === 'run-autonomous-cycle' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      runAutonomousPickupCycleOnState(state);
      return jsonResponse(buildSupplierPickupSnapshot(state));
    }
    if (
      segments[2] === 'pickup-engine' &&
      segments[3] === 'pickups' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const triggerRaw = strField(body, 'essentialityTriggerKind', 'COLETA_AMOSTRA_VISITA_ESTUDO');
      const triggerKind: SupplierPickupRequestDto['essentialityTriggerKind'] =
        triggerRaw === 'COLETA_AMOSTRA_VISITA_ESTUDO' ||
        triggerRaw === 'COLETA_CALIBRACAO_OU_COMODATO_T30D' ||
        triggerRaw === 'COLETA_ESSENCIAL_OPERACAO_DESCARTE' ||
        triggerRaw === 'COLETA_ESPECIAL_EXIGE_HUMANO'
          ? triggerRaw
          : 'COLETA_AMOSTRA_VISITA_ESTUDO';
      const catRaw = strField(body, 'pickupCategory', 'AMOSTRAS_BIOLOGICAS_IATA');
      const pickupCategory: SupplierPickupRequestDto['pickupCategory'] =
        catRaw === 'AMOSTRAS_BIOLOGICAS_IATA' ||
        catRaw === 'EQUIPAMENTO_CALIBRACAO_RBC' ||
        catRaw === 'LOGISTICA_REVERSA_CLOSEOUT' ||
        catRaw === 'RESIDUOS_DESCARTE_CERTIFICADO'
          ? catRaw
          : 'AMOSTRAS_BIOLOGICAS_IATA';
      const estCost = Math.max(1, numField(body, 'estimatedCostBrl', 1450));
      const ceilingCost = Math.max(estCost, numField(body, 'contractCeilingBrl', estCost * 1.2));
      const isEssential = triggerKind !== 'COLETA_ESPECIAL_EXIGE_HUMANO';
      const withinCeiling =
        estCost <= state.pickupPolicy.autoApprovalCeilingPerPickupBrl && estCost <= ceilingCost;
      const eligibleZeroTouch = isEssential && withinCeiling;
      const isPhase2 =
        state.pickupPolicy.governancePhase === 'FASE_2_AUTOMATICO_SEM_HUMANO_FORNECEDOR_AGENDA';
      const autoApproveNow = isPhase2 && eligibleZeroTouch;
      const autoScheduleNow =
        autoApproveNow && state.pickupPolicy.autoTriggerSupplierSlotBookingWhenApiAvailable;
      const nextNum = state.supplierPickups.length + 1;
      const supplierCode = strField(body, 'supplierCode', 'SUP-LOG-01');
      const supplierName = strField(
        body,
        'supplierName',
        'World Courier Brasil (Cadeia Fria IATA)',
      );
      const centerName = strField(body, 'centerName', 'GIYUS Curitiba Research Center (Matriz)');
      const localityCityUf = strField(body, 'localityCityUf');
      const costKind: SupplierPickupRequestDto['costAllocationKind'] =
        strField(body, 'costAllocationKind', 'ATRELADO_A_CONTRATO') === 'CUSTO_OPERACIONAL_UNIDADE'
          ? 'CUSTO_OPERACIONAL_UNIDADE'
          : 'ATRELADO_A_CONTRATO';
      const linkedStudy = optStrField(body, 'linkedStudyCode');
      const autoAwb = autoScheduleNow ? `AWB-AUTO-${supplierCode}-${202700 + nextNum}` : null;

      const created: SupplierPickupRequestDto = {
        id: `COL-${String(nextNum).padStart(3, '0')}`,
        pickupCode: `COL-INT-2026-${String(nextNum).padStart(3, '0')}`,
        createdAtIso: '2026-10-27T06:30:00Z',
        pickupCategory,
        essentialityTriggerKind: triggerKind,
        title: strField(body, 'title', 'Nova Solicitação Integrada de Coleta com Fornecedor'),
        triggerHeadline: strField(
          body,
          'triggerHeadline',
          'Coleta gerada no Motor Integrado de Coletas com Fornecedores.',
        ),
        originModuleIntegration:
          'Estudos Clínicos (SoA) + Recursos & Calibração (T-30d) + Fornecedores B2B + Financeiro',
        linkedStudyCode: linkedStudy,
        costAllocationKind: costKind,
        costAllocationTargetLabel:
          costKind === 'CUSTO_OPERACIONAL_UNIDADE'
            ? `Custo Próprio de Operação — ${centerName} (OPEX · Sem Contrato)`
            : `Atrelado ao Contrato do Estudo ${linkedStudy ?? 'EPOCA'} (Pass-Through / Direct Billing)`,
        localityCityUf,
        centerName,
        pickupAddressSummary: `Doca Clínica / Farmácia — ${centerName} (${localityCityUf})`,
        destinationName: strField(body, 'destinationName', 'Laboratório Central / Acreditado RBC'),
        supplierCode,
        supplierName,
        supplierScope: 'NACIONAL_BRASIL',
        temperatureOrHandlingRequirement: strField(
          body,
          'temperatureOrHandlingRequirement',
          'ULTRACONGELADO -80°C (Gelo Seco IATA PI-954) · Janela Seg–Qua',
        ),
        estimatedCostBrl: estCost,
        contractCeilingBrl: ceilingCost,
        savingsVsCeilingBrl: Number((ceilingCost - estCost).toFixed(2)),
        approvalMode: eligibleZeroTouch
          ? 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE'
          : 'REQUIRES_HUMAN_APPROVAL',
        workflowStatus: autoScheduleNow
          ? 'SUPPLIER_SCHEDULED_CONFIRMED'
          : autoApproveNow
            ? 'AUTO_APPROVED_AWAITING_SUPPLIER_SCHEDULE'
            : 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy: autoScheduleNow
          ? `Aprovada Sem Humano + Agendada Automaticamente com ${supplierName} (Fase 2)`
          : autoApproveNow
            ? `Aprovada Sem Humano (Fase 2) — Aguardando ${supplierName} agendar horário`
            : `⏳ Solicitação gerada por ${strField(body, 'requestedByEmail') || 'solicitante não informado'} — Aguardando o Humano aprovar para o fornecedor agendar`,
        approvedAtIso: autoApproveNow ? '2026-10-27T06:30:00Z' : null,
        scheduledPickupDate: autoScheduleNow ? '2026-10-28 (Terça-feira · Janela Seg–Qua)' : null,
        scheduledPickupWindow: autoScheduleNow ? '10:00 às 12:00' : null,
        awbOrTrackingCode: autoAwb,
        courierAgentName: autoScheduleNow ? `Agente Credenciado ${supplierName}` : null,
        supplierScheduledByEmail: autoScheduleNow
          ? `api.b2b@${supplierCode.toLowerCase()}.com.br`
          : null,
        supplierScheduledAtIso: autoScheduleNow ? '2026-10-27T06:30:05Z' : null,
        erpIntegrationTrail: autoScheduleNow
          ? `Gatilho de Coleta → Auto-Aprovada Sem Humano (Fase 2) → Agendada pelo Fornecedor (${autoAwb})`
          : 'Gatilho de Coleta → ⏳ Aguardando Aprovação Humana (Fase 1) → Fornecedor Agenda Coleta',
      };
      state.supplierPickups.unshift(created);
      return jsonResponse(created, 201);
    }

    // ACCOUNTING & BILLING ACCUMULATOR (/api/v1/network/suppliers/accounting-billing*)
    if (segments[2] === 'accounting-billing' && segments.length === 3 && method === 'GET') {
      return jsonResponse(buildAccountingBillingSnapshot(state));
    }
    if (
      segments[2] === 'accounting-billing' &&
      segments[3] === 'charges' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const studyCode = strField(body, 'studyCode', 'EPOCA').toUpperCase();
      const bucket =
        state.accountingContractBuckets.find((b) => b.studyCode.toUpperCase() === studyCode) ??
        state.accountingContractBuckets[0]!;
      const qty = Math.max(1, numField(body, 'quantity', 1));
      const unitPrice = Math.max(0, numField(body, 'unitContractPriceBrl', 1500));
      const gross = Number((qty * unitPrice).toFixed(2));
      const cat = strField(body, 'chargeCategory', 'VISITA_CLINICA_SOA_ECRF');
      const isPassThrough = cat.toUpperCase().includes('PASS_THROUGH');
      const holdback = isPassThrough
        ? 0
        : Number((gross * (bucket.holdbackPercentage / 100)).toFixed(2));
      const tax = Number(((gross - holdback) * (bucket.taxWithholdingRatePct / 100)).toFixed(2));
      const net = Number((gross - holdback - tax).toFixed(2));
      const nextIdx = state.accountingAccumulatedCharges.length + 1;

      const created: AccumulatedBillingChargeDto = {
        id: `ACC-CHG-${String(nextIdx).padStart(3, '0')}`,
        chargeCode: `COB-ACUM-2026-${String(nextIdx).padStart(3, '0')}`,
        createdAtIso: '2026-10-27T06:50:00Z',
        performedDate: '2026-10-27',
        agingDays: 1,
        daysUntilRetroactiveForfeiture: bucket.retroactiveBillingLimitDays - 1,
        studyCode: bucket.studyCode,
        contractCode: bucket.contractCode,
        originSourceModule: 'Contabilidade & Estudos (Acúmulo Direto do Contrato / eCRF / NF-e)',
        chargeCategory: cat,
        billingFlowKind: strField(body, 'billingFlowKind', 'AUTO_SELF_BILLING_ECRF'),
        title: strField(body, 'title', 'Nova Cobrança Acumulada do Contrato / Estudo'),
        clinicalOrContractReference: strField(
          body,
          'clinicalOrContractReference',
          'Cláusula Orçamentária Anexo I do Contrato',
        ),
        participantOrBatchRef: strField(body, 'participantOrBatchRef', 'Lote Outubro/2026'),
        quantity: qty,
        unitContractPriceBrl: unitPrice,
        inflationOrFxAdjustmentBrl: 0,
        grossTotalBrl: gross,
        holdbackRetainedBrl: holdback,
        estimatedTaxWithheldBrl: tax,
        netReceivableBrl: net,
        status: 'ACCUMULATING_READY',
        proactiveGapWarning:
          'Acumulado dentro do prazo contratual; pronto para compor o próximo lote de Nota Fiscal (NFS-e / Invoice).',
        linkedInvoiceNumber: null,
        supportingEvidenceNote: strField(
          body,
          'supportingEvidenceNote',
          'Evidência clínica e contratual vinculada.',
        ),
      };
      state.accountingAccumulatedCharges.unshift(created);
      return jsonResponse(buildAccountingBillingSnapshot(state));
    }
    if (
      segments[2] === 'accounting-billing' &&
      segments[3] === 'charges' &&
      segments[5] === 'unlock' &&
      segments.length === 6 &&
      method === 'POST'
    ) {
      const id = segments[4] ?? '';
      const body = await readBodyObj(request);
      const actor = strField(body, 'unlockedByEmail');
      const just = strField(
        body,
        'evidenceOrRatioUnlockJustification',
        'Proporção contratual 1:3 de randomizados atingida / comprovante anexado.',
      );
      state.accountingAccumulatedCharges = state.accountingAccumulatedCharges.map((c) =>
        c.id === id || c.chargeCode === id
          ? {
              ...c,
              status: 'ACCUMULATING_READY',
              proactiveGapWarning: `Destravado por ${actor}: ${just}`,
              supportingEvidenceNote: `${c.supportingEvidenceNote} | Destravado p/ Faturamento: ${just}`,
            }
          : c,
      );
      return jsonResponse(buildAccountingBillingSnapshot(state));
    }
    if (
      segments[2] === 'accounting-billing' &&
      segments[3] === 'buckets' &&
      segments[5] === 'issue-invoice' &&
      segments.length === 6 &&
      method === 'POST'
    ) {
      const studyCode = (segments[4] ?? 'EPOCA').toUpperCase();
      const body = await readBodyObj(request);
      const bucket = state.accountingContractBuckets.find(
        (b) => b.studyCode.toUpperCase() === studyCode,
      );
      if (bucket) {
        const eligible = state.accountingAccumulatedCharges.filter(
          (c) =>
            c.studyCode.toUpperCase() === studyCode &&
            (c.status === 'ACCUMULATING_READY' || c.status === 'URGENT_RETROACTIVE_RISK'),
        );
        if (eligible.length > 0) {
          const seq = 20260940 + state.accountingIssuedInvoices.length + 1;
          const isExport = bucket.taxRegimeProfile.toUpperCase().includes('EXPORTACAO');
          const invNumber = isExport ? `INV-INT-${seq}` : `NFS-e #${seq}`;
          const sefaz = `SEFAZ-CWB-${seq}-99A`;
          let grossTotal = Number(eligible.reduce((acc, c) => acc + c.grossTotalBrl, 0).toFixed(2));
          let holdbackTotal = Number(
            eligible.reduce((acc, c) => acc + c.holdbackRetainedBrl, 0).toFixed(2),
          );
          if (body['includeHoldbackReleaseIfCloseOut']) {
            grossTotal = Number((grossTotal + bucket.holdbackRetainedCumulativeBrl).toFixed(2));
            holdbackTotal = 0;
          }
          const taxableBase = Math.max(0, grossTotal - holdbackTotal);
          const issAmount = isExport ? 0 : Number((taxableBase * 0.02).toFixed(2));
          const irrfCsrfAmount = isExport
            ? 0
            : Number((taxableBase * (bucket.taxWithholdingRatePct / 100)).toFixed(2));
          const netReceivable = Number((grossTotal - holdbackTotal - irrfCsrfAmount).toFixed(2));
          const periodRef = strField(body, 'billingPeriodReference', 'Competência Outubro/2026');

          state.accountingAccumulatedCharges = state.accountingAccumulatedCharges.map((c) =>
            eligible.some((e) => e.id === c.id)
              ? {
                  ...c,
                  status: 'BILLED_IN_INVOICE',
                  linkedInvoiceNumber: invNumber,
                  proactiveGapWarning: `Consolidado e faturado na Nota Fiscal ${invNumber} (${periodRef})`,
                }
              : c,
          );

          const newInv: AccountingIssuedInvoiceDto = {
            id: `INV-ACC-${String(state.accountingIssuedInvoices.length + 1).padStart(3, '0')}`,
            invoiceNumber: invNumber,
            verificationCodeSefaz: sefaz,
            documentKind: isExport
              ? 'COMMERCIAL_INVOICE_EXPORTACAO_SERVICOS'
              : 'NFSE_PREFEITURA_PESQUISA_CLINICA',
            issuedAtDate: '2026-10-27',
            dueDate: '2026-11-26',
            studyCode: bucket.studyCode,
            contractCode: bucket.contractCode,
            billingEntityName: bucket.billingEntityName,
            billingEntityCnpj: bucket.billingEntityCnpj,
            payerLegalName: `${bucket.sponsorName} / ${bucket.croIntermediaryName}`,
            payerCnpjOrTaxId: isExport
              ? 'CHE-105.909.036 (Suíça / Exterior)'
              : '04.891.220/0001-18 (CRO Brasil)',
            taxRegimeApplied: bucket.taxRegimeProfile,
            bundledChargesCount: eligible.length,
            bundledItemsSummary: eligible.map((e) => `${e.chargeCode} (${e.title})`).join(' + '),
            grossAmountBrl: grossTotal,
            holdbackRetainedBrl: holdbackTotal,
            issAmountBrl: issAmount,
            irrfCsrfWithheldBrl: irrfCsrfAmount,
            netReceivableAmountBrl: netReceivable,
            paymentStatus: 'ISSUED_AWAITING_PAYMENT',
            paidAtDate: null,
            bankReconciliationRef: strField(
              body,
              'fiscalObservationNotes',
              `Lote ${periodRef} emitido por ${strField(body, 'issuedByEmail')}`,
            ),
          };
          state.accountingIssuedInvoices.unshift(newInv);
        }
      }
      return jsonResponse(buildAccountingBillingSnapshot(state));
    }
    if (
      segments[2] === 'accounting-billing' &&
      segments[3] === 'invoices' &&
      segments[5] === 'mark-paid' &&
      segments.length === 6 &&
      method === 'POST'
    ) {
      const id = segments[4] ?? '';
      const body = await readBodyObj(request);
      const actor = strField(body, 'reconciledByEmail');
      const bankRef = strField(
        body,
        'bankStatementReference',
        'TED/PIX Conciliado Itaú Empresas #884920',
      );
      state.accountingIssuedInvoices = state.accountingIssuedInvoices.map((inv) =>
        inv.id === id || inv.invoiceNumber === id
          ? {
              ...inv,
              paymentStatus: 'PAID_RECONCILED',
              paidAtDate: '2026-10-27',
              bankReconciliationRef: `${bankRef} (Conciliado por ${actor})`,
            }
          : inv,
      );
      return jsonResponse(buildAccountingBillingSnapshot(state));
    }

    // CESTA DE SERVIÇOS E PRODUTOS ACUMULADOS NO ESTUDO & NOTAS FISCAIS EMITIDAS PELOS NOSSOS CENTROS (CONTROLE DO CONTADOR + AUTO-CLASSIFICAÇÃO IA COM FALLBACK HUMANO)
    if (
      segments[2] === 'center-issued-study-invoices' &&
      segments.length === 3 &&
      method === 'GET'
    ) {
      return jsonResponse(buildCenterIssuedStudyInvoicesSnapshot(state));
    }
    if (
      segments[2] === 'center-issued-study-invoices' &&
      segments[3] === 'receive' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      return jsonResponse(receiveCenterIssuedStudyInvoiceOnState(state, body));
    }
    if (
      segments[2] === 'center-issued-study-invoices' &&
      segments[3] === 'human-classify' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      return jsonResponse(classifyCenterIssuedStudyInvoiceByHumanOnState(state, body));
    }
  }

  // STUDIES (/api/v1/network/studies*)
  if (segments[1] === 'studies') {
    upgradeAndEnforceCanonV11OnState(state);
    if (segments[2] === 'canon-resync' && segments.length === 3 && method === 'POST') {
      upgradeAndEnforceCanonV11OnState(state);
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse({
        canonSchemaVersion: CANON_SCHEMA_VERSION,
        enforcedRulesCount: state.globalRules.filter((r) => r.status === 'APPROVED_GLOBAL').length,
        syncedEnginesCount: 6,
        upgradedDossiersCount: state.dossiers.length,
        upgradedDraftsCount: state.budgetEmailDrafts.length,
        harness2State: state.harness2State,
        globalRules: state.globalRules,
      });
    }
    if (segments.length === 2 && method === 'GET') {
      syncIncomingStudiesAndProceduresFromArrivedStudies(state);
      return jsonResponse(state.studies);
    }
    if (
      segments[2] === 'organizations' &&
      segments[3] === 'auto-detected' &&
      segments.length === 4 &&
      method === 'GET'
    ) {
      return jsonResponse(buildSponsorAndCroAutoDetectionDashboard(state));
    }
    if (
      segments[2] === 'organizations' &&
      segments[3] === 'register' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const updated = registerDetectedSponsorOrCroInSeparateRegistries(state, body);
      return jsonResponse(updated);
    }
    if (segments[2] === 'organizations' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.organizations);
    }
    if (segments[2] === 'clinical-people-directory' && segments.length === 3 && method === 'GET') {
      syncInMemoryStateFromPersistedVault(state);
      return jsonResponse({
        pis:
          state.isGreenfield !== false
            ? (state.registeredPis ?? [])
            : (state.registeredPis ?? AVAILABLE_PI_CATALOG),
        subs:
          state.isGreenfield !== false
            ? (state.registeredSubs ?? [])
            : (state.registeredSubs ?? AVAILABLE_SUB_CATALOG),
        coordinators:
          state.isGreenfield !== false
            ? (state.registeredCoordinators ?? [])
            : (state.registeredCoordinators ?? AVAILABLE_COORDINATOR_CATALOG),
        researchCenters:
          state.isGreenfield !== false
            ? (state.registeredResearchCenters ?? [])
            : (state.registeredResearchCenters ?? AVAILABLE_RESEARCH_CENTERS_CATALOG),
        specialties: state.clinicalSpecialties ?? DEFAULT_CLINICAL_SPECIALTIES_CATALOG,
        healthFunctions: state.clinicalHealthFunctions ?? DEFAULT_CLINICAL_HEALTH_FUNCTIONS_CATALOG,
      });
    }
    if (segments[2] === 'clinical-people-directory' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const updated = registerClinicalPersonOrCenterOnState(state, body);
      return jsonResponse(updated, 201);
    }
    if (segments[2] === 'clinical-specialties' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.clinicalSpecialties ?? DEFAULT_CLINICAL_SPECIALTIES_CATALOG);
    }
    if (segments[2] === 'clinical-specialties' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const result = manageClinicalSpecialtyOnState(state, body);
      return jsonResponse(result, 200);
    }
    if (segments[2] === 'clinical-health-functions' && segments.length === 3 && method === 'GET') {
      return jsonResponse(
        state.clinicalHealthFunctions ?? DEFAULT_CLINICAL_HEALTH_FUNCTIONS_CATALOG,
      );
    }
    if (segments[2] === 'clinical-health-functions' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const result = manageClinicalHealthFunctionOnState(state, body);
      return jsonResponse(result, 200);
    }
    if (segments[2] === 'bigdatacorp-cnpj-lookup' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const result = await lookupCnpjAndAutoRegisterBranchesOnState(state, body);
      return jsonResponse(result, result.ok ? 200 : 400);
    }
    if (
      segments[2] === 'research-centers' &&
      segments[3] === 'weekly-sync' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const result = await weeklySyncRegisteredResearchCentersOnState(state, body);
      return jsonResponse(result, 200);
    }
    if (segments[2] === 'procedures' && segments.length === 3 && method === 'GET') {
      enrichProceduresWithSupportedRegionsAndPrices(state);
      return jsonResponse(state.procedures);
    }
    if (
      segments[2] === 'procedures' &&
      segments[3] === 'normalization-queue' &&
      segments.length === 4 &&
      method === 'GET'
    ) {
      return jsonResponse(buildStudyProceduresNormalizationDashboard(state));
    }
    if (
      segments[2] === 'procedures' &&
      segments[3] === 'normalization-queue' &&
      segments[4] === 'resolve' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const updatedDashboard = resolveIncomingStudyProcedureDecision(state, body);
      return jsonResponse(updatedDashboard);
    }
    if (segments[2] === 'procedures' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const regionCityUf = strField(body, 'regionCityUf', 'Curitiba/PR');
      const attendingCenterName = strField(
        body,
        'attendingCenterName',
        `Centro de Pesquisa Atendente em ${regionCityUf}`,
      );
      const created: ProcedureDefinitionItem = {
        id: `0199ba02-0000-7000-8000-${String(state.procedures.length + 10).padStart(12, '0')}`,
        tenantId: null,
        code: strField(body, 'code', 'PROC-NEW').toUpperCase(),
        namePtBr: strField(body, 'namePtBr', 'Procedimento'),
        nameEnUs: strField(body, 'nameEnUs', 'Procedure'),
        category: strField(body, 'category', 'CLINICAL'),
        defaultDurationMinutes: numField(body, 'defaultDurationMinutes', 60),
        baseUnitCostBrl: numField(body, 'baseUnitCostBrl', 1000),
        currentVersion: 1,
        status: 'Published',
        regionCityUf,
        attendingCenterName,
        supportedRegionsCityUf: [regionCityUf],
      };
      state.procedures.push(created);
      ensureActiveRegionCityPriceOnlyIfUsed(
        state,
        created.code,
        regionCityUf,
        created.baseUnitCostBrl,
        'CATÁLOGO',
      );
      enrichProceduresWithSupportedRegionsAndPrices(state);
      syncIncomingStudiesAndProceduresFromArrivedStudies(state);
      writePersistedGreenfieldMailboxState(state);
      const enrichedCreated =
        state.procedures.find((p) => p.code.toUpperCase() === created.code.toUpperCase()) ??
        created;
      return jsonResponse(enrichedCreated, 201);
    }
    if (segments[2] === 'procedures' && segments[3] === 'city-prices' && method === 'GET') {
      return jsonResponse(state.cityPrices);
    }
    if (segments[2] === 'procedures' && segments[4] === 'city-prices' && method === 'POST') {
      const code = (segments[3] ?? '').toUpperCase();
      const body = await readBodyObj(request);
      const cityUf = strField(body, 'cityUf');
      const unitMeasure = strField(body, 'unitMeasure', 'PROCEDIMENTO_UNIDADE');
      const unitPriceBrl = numField(body, 'unitPriceBrl', 950);
      const updatedByEmail = strField(body, 'updatedByEmail');
      const changeReason = strField(
        body,
        'changeReason',
        'Ajuste de preço da tabela Holding GIYUS',
      );

      state.holdingPriceTableVersion += 1;
      state.studyDismissedAlerts = {};

      const existingIdx = state.cityPrices.findIndex(
        (c) =>
          c.procedureCode.toUpperCase() === code && c.cityUf.toLowerCase() === cityUf.toLowerCase(),
      );
      const existing = state.cityPrices[existingIdx];
      const nextVer = existing ? existing.version + 1 : 1;
      const item: ProcedureCityPriceItem = {
        id: existing ? existing.id : `cp-${String(state.cityPrices.length + 10).padStart(3, '0')}`,
        procedureCode: code,
        cityUf,
        unitMeasure,
        unitPriceBrl,
        version: nextVer,
        updatedAtIso: new Date().toISOString(),
        updatedByEmail,
        changeReason,
      };
      if (existingIdx >= 0) {
        state.cityPrices[existingIdx] = item;
      } else {
        state.cityPrices.push(item);
      }

      const procIdx = state.procedures.findIndex((p) => p.code.toUpperCase() === code);
      const proc = state.procedures[procIdx];
      if (procIdx >= 0 && proc) {
        state.procedures[procIdx] = {
          ...proc,
          baseUnitCostBrl:
            cityUf.toLowerCase() === 'curitiba/pr' ? unitPriceBrl : proc.baseUnitCostBrl,
          currentVersion: proc.currentVersion + 1,
        };
      }
      enrichProceduresWithSupportedRegionsAndPrices(state);
      writePersistedGreenfieldMailboxState(state);

      return jsonResponse(item);
    }
    if (segments[2] === 'dossiers' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.dossiers);
    }
    if (segments[2] === 'dossiers' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const code = strField(body, 'code', 'NEUROVANCE').toUpperCase();
      const candidate = CANDIDATE_PROJECTS_FROM_CONTRACT_EMAILS.find(
        (c) => c.code.toUpperCase() === code || c.studyCode.toUpperCase() === code,
      );
      const studyCode = strField(body, 'studyCode', candidate?.studyCode ?? code).toUpperCase();
      const title = strField(body, 'title', candidate?.title ?? code);

      let autoFitted = 0;
      state.messages = state.messages.map((m) => {
        if (
          m.classificationStatus === 'Unassigned' &&
          (m.subject.toUpperCase().includes(code) ||
            m.subject.toUpperCase().includes(studyCode) ||
            extractStudyMetadataFromRealEmail(m)?.code.toUpperCase() === code)
        ) {
          autoFitted++;
          return {
            ...m,
            classificationStatus: 'AutoAssigned',
            assignedProjectCode: code,
            confidenceScore: 95,
            matchedRulesJson: JSON.stringify([
              `Keyword:${code.toLowerCase()} (+70, Aprendido na criação do projeto)`,
            ]),
          };
        }
        return m;
      });

      const createdDossier: StudyContractDossierItem = {
        id: `0199ba03-0000-7000-8000-${String(state.dossiers.length + 10).padStart(12, '0')}`,
        code,
        studyCode,
        title,
        sponsorName: strField(
          body,
          'sponsorName',
          candidate?.sponsorName ?? 'Patrocinador (conforme e-mail)',
        ),
        croName: optStrField(body, 'croName') ?? candidate?.croName ?? null,
        currency: 'BRL',
        exchangeRateBrl: 1,
        taxRegime: 'LucroReal',
        taxRatePercent: code === 'EPOCA' ? 0 : 14.25,
        overheadPercent: candidate?.overheadPercent ?? 0,
        totalBaselineBrl: numField(body, 'totalBaselineBrl', candidate?.totalBaselineBrl ?? 0),
        totalNegotiatedBrl: numField(
          body,
          'totalNegotiatedBrl',
          candidate?.totalNegotiatedBrl ?? 0,
        ),
        passThroughTotalBrl: numField(
          body,
          'passThroughTotalBrl',
          candidate?.passThroughTotalBrl ?? 0,
        ),
        estimatedMarginPercent: candidate?.estimatedMarginPercent ?? 0,
        status: 'UnderAnalysis',
        riskScore: code === 'EPOCA' ? 35 : 0,
        clinicalAnalysisJson:
          code === 'EPOCA'
            ? '{"complexity":"Ensaio Clínico Randomizado Fatorial 2 x 2 (Colchicina + Polipílula · CAAE 59057422.4.1001.0060) · Anexo I: 21 Pagamentos (D0 a D1440 — 48 meses) + Taxa Única de Startup R$ 3.000,00 · Valores Unitários: R$ 5.900,00/participante (Overhead 25% incluso) · Recrutamento Competitivo (Cláusula 4.5 — até 10.797 no estudo geral) · Campos cadastrais CNPJ XX.XXX.XXX/XXXX-XX e PI em branco na minuta para preenchimento pelo Centro de Pesquisa TrialsHub"}'
            : '{"complexity":"Dossiê criado a partir de contratos@giyus.com.br — exibindo estritamente dados literais do e-mail/anexo"}',
        budgetGridJson:
          candidate && candidate.budgetRows.length > 0
            ? JSON.stringify(candidate.budgetRows)
            : '[]',
        contractClausesJson:
          candidate && candidate.contractClauses.length > 0
            ? JSON.stringify(candidate.contractClauses)
            : '[]',
        miroFishSimulationJson: '{"scenariosRun":0,"acceptanceProbabilityPercent":0}',
        jevScoreJson:
          '{"model":"Literal-Document-Extraction","decision":"UNDER_INITIAL_ANALYSIS","confidence":1}',
        linkedEmailsCount: autoFitted,
      };
      const existingDossierIdx = state.dossiers.findIndex((d) => d.code.toUpperCase() === code);
      if (existingDossierIdx >= 0) {
        state.dossiers[existingDossierIdx] = createdDossier;
      } else {
        state.dossiers.push(createdDossier);
      }

      if (!state.studies.some((s) => s.code.toUpperCase() === code)) {
        state.studies.push({
          id: `0199ba02-0000-7000-8000-${String(state.studies.length + 10).padStart(12, '0')}`,
          code,
          protocolCode: studyCode,
          title,
          phase: 'PhaseIII',
          therapeuticArea: 'Cardiologia / Pesquisa Clínica',
          sponsorName: createdDossier.sponsorName,
          croName: createdDossier.croName,
          status: 'Feasibility',
          targetEnrollment: candidate?.targetParticipants ?? 0,
          currentEnrollment: 0,
        });
      }

      state.rules.unshift({
        id: `0199ba05-0000-7000-8000-${String(state.rules.length + 10).padStart(12, '0')}`,
        projectCode: code,
        projectName: title,
        ruleKind: 'Keyword',
        pattern: code.toLowerCase(),
        weight: 65,
        learnedFromHumanFeedback: true,
        hitCount: autoFitted + 1,
        createdByRole: 'HUMAN_FEEDBACK',
      });
      syncIncomingStudiesAndProceduresFromArrivedStudies(state);
      writePersistedGreenfieldMailboxState(state);

      return jsonResponse(
        {
          dossier: createdDossier,
          autoFittedEmailsCount: autoFitted,
          learnedRulesCount: 2,
        },
        201,
      );
    }
    if (segments[2] === 'dossiers' && segments[4] === 'simulate' && method === 'POST') {
      if (isProductionRuntime()) {
        return jsonResponse(
          { error: 'Simulação desabilitada em Produção; permitida apenas em ambiente DEV.' },
          403,
        );
      }
      const code = (segments[3] ?? '').toUpperCase();
      const body = await readBodyObj(request);
      const idx = state.dossiers.findIndex((d) => d.code === code);
      const existing = state.dossiers[idx];
      if (idx >= 0 && existing) {
        const updated: StudyContractDossierItem = {
          ...existing,
          totalNegotiatedBrl: numField(body, 'totalNegotiatedBrl', existing.totalNegotiatedBrl),
          estimatedMarginPercent: Number((existing.estimatedMarginPercent + 1.2).toFixed(2)),
          status: 'ApprovedForExecution',
        };
        state.dossiers[idx] = updated;
        return jsonResponse(updated);
      }
    }
    if (
      segments[2] === 'mailboxes' &&
      segments[3] === 'messages' &&
      segments.length === 4 &&
      method === 'GET'
    ) {
      return jsonResponse(state.messages);
    }
    if (segments[2] === 'mailboxes' && segments[3] === 'rules' && method === 'GET') {
      return jsonResponse(state.rules);
    }
    if (
      segments[2] === 'mailboxes' &&
      segments[3] === 'drafts' &&
      segments.length === 4 &&
      method === 'GET'
    ) {
      return jsonResponse(state.budgetEmailDrafts);
    }
    if (
      segments[2] === 'dossiers' &&
      segments[4] === 'email-drafts' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const code = (segments[3] ?? 'EPOCA').toUpperCase();
      const body = await readBodyObj(request);
      const createdByEmail = strField(body, 'createdByEmail');
      const mailboxAddress = strField(body, 'mailboxAddress', 'contratos@giyus.com.br');
      const dossier = state.dossiers.find((d) => d.code.toUpperCase() === code);
      const existingDraftIdx = state.budgetEmailDrafts.findIndex(
        (d) => d.studyCode.toUpperCase() === code,
      );
      if (existingDraftIdx >= 0 && state.budgetEmailDrafts[existingDraftIdx]) {
        const existingDraft = state.budgetEmailDrafts[existingDraftIdx];
        if (
          !existingDraft.isModifiedFromOriginal ||
          !existingDraft.currentBody.includes('QUALIFICAÇÃO AUTOMÁTICA DO CENTRO DE PESQUISA')
        ) {
          const refreshedDraft = buildInitialBudgetEmailDraft(
            dossier,
            code,
            createdByEmail,
            mailboxAddress,
            existingDraftIdx + 1,
            state,
          );
          const mergedDraft: BudgetEmailDraftItemDto = {
            ...refreshedDraft,
            id: existingDraft.id,
            graphDraftMessageId: existingDraft.graphDraftMessageId,
            createdAtIso: existingDraft.createdAtIso,
          };
          state.budgetEmailDrafts[existingDraftIdx] = mergedDraft;
          writePersistedGreenfieldMailboxState(state);
          return jsonResponse(mergedDraft, 201);
        }
        return jsonResponse(existingDraft, 201);
      }
      const createdDraft = buildInitialBudgetEmailDraft(
        dossier,
        code,
        createdByEmail,
        mailboxAddress,
        state.budgetEmailDrafts.length + 1,
        state,
      );
      state.budgetEmailDrafts.unshift(createdDraft);
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(createdDraft, 201);
    }
    if (
      segments[2] === 'dossiers' &&
      segments[4] === 'team-docs-viability' &&
      segments.length === 5 &&
      method === 'GET'
    ) {
      const code = (segments[3] ?? 'EPOCA').toUpperCase();
      return jsonResponse(buildStudyTeamMultiDocAndLearningDashboard(state, code));
    }
    if (
      segments[2] === 'dossiers' &&
      segments[4] === 'team-docs-viability' &&
      segments[5] === 'bind' &&
      segments.length === 6 &&
      method === 'POST'
    ) {
      const code = (segments[3] ?? 'EPOCA').toUpperCase();
      const body = await readBodyObj(request);
      return jsonResponse(bindStudyTeamAndCenterAutoFillOnState(state, code, body));
    }
    if (
      segments[2] === 'dossiers' &&
      segments[4] === 'team-docs-viability' &&
      segments[5] === 'learn' &&
      segments.length === 6 &&
      method === 'POST'
    ) {
      const code = (segments[3] ?? 'EPOCA').toUpperCase();
      const body = await readBodyObj(request);
      return jsonResponse(learnCroSponsorAndGlobalNegotiationOnState(state, code, body));
    }
    if (
      segments[2] === 'dossiers' &&
      segments[4] === 'mirofish-optimize' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const code = (segments[3] ?? 'EPOCA').toUpperCase();
      const body = await readBodyObj(request);
      return jsonResponse(applyMiroFishProfitMaximizationOnState(state, code, body));
    }
    if (
      segments[2] === 'mailboxes' &&
      segments[3] === 'drafts' &&
      segments.length === 5 &&
      method === 'PUT'
    ) {
      const id = segments[4] ?? '';
      const body = await readBodyObj(request);
      const idx = state.budgetEmailDrafts.findIndex((d) => d.id === id);
      const existing = state.budgetEmailDrafts[idx];
      if (idx >= 0 && existing) {
        const action = strField(body, 'action', 'SAVE_CHANGES').toUpperCase();
        const updatedByEmail = strField(body, 'updatedByEmail');
        const nowIso = new Date().toISOString();

        if (action === 'RESTORE_ORIGINAL') {
          const restored: BudgetEmailDraftItemDto = {
            ...existing,
            status: 'ORIGINAL_DRAFT',
            updatedAtIso: nowIso,
            updatedByEmail,
            currentTo: existing.originalTo,
            currentCc: existing.originalCc,
            currentSubject: existing.originalSubject,
            currentBody: existing.originalBody,
            isModifiedFromOriginal: false,
            latestChanges: [],
          };
          state.budgetEmailDrafts[idx] = restored;
          writePersistedGreenfieldMailboxState(state);
          return jsonResponse(restored);
        }

        const currentTo = strField(body, 'currentTo', existing.currentTo);
        const currentCc = strField(body, 'currentCc', existing.currentCc);
        const currentSubject = strField(body, 'currentSubject', existing.currentSubject);
        const currentBody = strField(body, 'currentBody', existing.currentBody);

        const changes = computeBudgetEmailDraftDiff(
          {
            to: existing.originalTo,
            cc: existing.originalCc,
            subject: existing.originalSubject,
            body: existing.originalBody,
          },
          {
            to: currentTo,
            cc: currentCc,
            subject: currentSubject,
            body: currentBody,
          },
        );
        const isModified = changes.length > 0;
        const nextStatus: BudgetEmailDraftItemDto['status'] =
          action === 'SEND_EMAIL'
            ? isModified
              ? 'SENT_WITH_MODIFICATIONS'
              : 'SENT_ORIGINAL'
            : isModified
              ? 'MODIFIED_IN_MAILBOX'
              : 'ORIGINAL_DRAFT';

        const nextRevisions: BudgetEmailDraftRevisionDto[] = [...existing.revisions];
        if (isModified || action === 'SEND_EMAIL') {
          const revNum = nextRevisions.length + 1;
          const summaryParts: string[] = [];
          if (changes.some((c) => c.field === 'TO' || c.field === 'CC')) {
            summaryParts.push('destinatários alterados');
          }
          if (changes.some((c) => c.field === 'SUBJECT')) {
            summaryParts.push('assunto alterado');
          }
          const valCount = changes.filter((c) => c.field === 'BUDGET_VALUE').length;
          if (valCount > 0) {
            summaryParts.push(`${valCount} condição(ões)/valor(es) orçamentário(s) modificado(s)`);
          }
          const lineCount = changes.filter((c) => c.field === 'BODY_LINE').length;
          if (lineCount > 0) {
            summaryParts.push(`${lineCount} trecho(s) do corpo modificado(s)`);
          }

          nextRevisions.unshift({
            revisionId: `rev-${existing.studyCode.toLowerCase()}-${revNum}-${Date.now()}`,
            revisionNumber: revNum,
            modifiedAtIso: nowIso,
            modifiedByEmail: updatedByEmail,
            source: 'MAILBOX_UI',
            summary:
              summaryParts.length > 0
                ? `${action === 'SEND_EMAIL' ? 'E-mail enviado com alterações' : 'Rascunho modificado na Caixa Compartilhada'}: ${summaryParts.join(', ')}.`
                : 'E-mail enviado mantendo o texto do rascunho original.',
            changes,
            snapshotTo: currentTo,
            snapshotCc: currentCc,
            snapshotSubject: currentSubject,
            snapshotBody: currentBody,
          });
        }

        const updatedDraft: BudgetEmailDraftItemDto = {
          ...existing,
          status: nextStatus,
          updatedAtIso: nowIso,
          updatedByEmail,
          currentTo,
          currentCc,
          currentSubject,
          currentBody,
          isModifiedFromOriginal: isModified,
          latestChanges: changes,
          revisions: nextRevisions,
        };
        state.budgetEmailDrafts[idx] = updatedDraft;

        if (action === 'SEND_EMAIL') {
          const sentMsgId = `outbox-draft-${existing.id}`;
          const toList = currentTo
            .split(/[;,]/)
            .map((s) => s.trim())
            .filter(Boolean);
          const ccList = currentCc
            .split(/[;,]/)
            .map((s) => s.trim())
            .filter(Boolean);
          const sentMsg: SharedMailboxMessageItem = {
            id: sentMsgId,
            graphMessageId: `AAMkADg0YzE-OUTBOX-DRAFT-${existing.id}`,
            mailboxAddress: existing.mailboxAddress || 'contratos@giyus.com.br',
            mailboxFolder: 'OUTBOX',
            recipientEmailsJson: JSON.stringify(toList),
            ccEmailsJson: JSON.stringify(ccList),
            subject: currentSubject,
            senderName: `Giyus Contratos (${updatedByEmail})`,
            senderEmail: existing.mailboxAddress || 'contratos@giyus.com.br',
            senderDomain: 'giyus.com.br',
            receivedAtIso: nowIso,
            bodyPreview: currentBody,
            hasAttachments: false,
            attachmentNamesJson: '[]',
            classificationStatus: 'AutoAssigned',
            assignedProjectCode: existing.studyCode.toUpperCase(),
            confidenceScore: 100,
            matchedRulesJson: JSON.stringify([
              `Caixa de Saída (SentItems): E-mail de orçamento enviado para ${currentTo} (${existing.studyCode})`,
            ]),
            manuallyReviewedBy: updatedByEmail,
          };
          const existingSentIdx = state.messages.findIndex((m) => m.id === sentMsgId);
          if (existingSentIdx >= 0) {
            state.messages[existingSentIdx] = sentMsg;
          } else {
            state.messages.unshift(sentMsg);
          }
        }

        writePersistedGreenfieldMailboxState(state);
        return jsonResponse(updatedDraft);
      }
    }
    if (
      segments[2] === 'mailboxes' &&
      segments[3] === 'threads' &&
      segments.length === 4 &&
      method === 'GET'
    ) {
      return jsonResponse(buildSharedMailboxThreadsDashboard(state));
    }
    if (
      segments[2] === 'mailboxes' &&
      segments[3] === 'threads' &&
      segments[4] === 'resolve-doubt' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const doubtId = strField(body, 'doubtId');
      const decisionRaw = strField(body, 'decision', 'CONFIRM_SAME_THREAD').toUpperCase();
      const decision: 'CONFIRM_SAME_THREAD' | 'REJECT_SEPARATE_THREADS' =
        decisionRaw === 'REJECT_SEPARATE_THREADS'
          ? 'REJECT_SEPARATE_THREADS'
          : 'CONFIRM_SAME_THREAD';
      const reviewedBy = strField(body, 'reviewedBy');
      if (doubtId) {
        state.threadDoubtResolutions[doubtId] = {
          decision,
          reviewedBy,
          resolvedAtIso: new Date().toISOString(),
        };
        writePersistedGreenfieldMailboxState(state);
      }
      return jsonResponse(buildSharedMailboxThreadsDashboard(state));
    }
    if (
      segments[2] === 'mailboxes' &&
      segments[3] === 'threads' &&
      segments[4] === 'manual-merge' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const sourceThreadId = strField(body, 'sourceThreadId');
      const targetThreadId = strField(body, 'targetThreadId');
      if (sourceThreadId && targetThreadId && sourceThreadId !== targetThreadId) {
        state.manualThreadMerges[sourceThreadId] = targetThreadId;
        writePersistedGreenfieldMailboxState(state);
      }
      return jsonResponse(buildSharedMailboxThreadsDashboard(state));
    }
    if (segments[2] === 'mailboxes' && segments[3] === 'sync' && method === 'POST') {
      const fetchedMessages = await fetchLiveOrRealGraphMailboxMessages();
      const existingGraphIds = new Set(
        state.messages.map((m) => m.graphMessageId).filter((g) => Boolean(g)),
      );
      const existingIds = new Set(state.messages.map((m) => m.id));

      let newlyDownloadedCount = 0;
      for (const incoming of fetchedMessages) {
        if (!existingGraphIds.has(incoming.graphMessageId) && !existingIds.has(incoming.id)) {
          state.messages.push({ ...incoming });
          existingGraphIds.add(incoming.graphMessageId);
          existingIds.add(incoming.id);
          newlyDownloadedCount++;
        } else {
          // Ensure existing messages always refresh their attachments and full forwarded body text
          const existingIdx = state.messages.findIndex(
            (m) => m.id === incoming.id || m.graphMessageId === incoming.graphMessageId,
          );
          const currentMsg = state.messages[existingIdx];
          if (existingIdx >= 0 && currentMsg) {
            state.messages[existingIdx] = {
              ...currentMsg,
              mailboxFolder: incoming.mailboxFolder ?? currentMsg.mailboxFolder ?? 'INBOX',
              recipientEmailsJson: incoming.recipientEmailsJson ?? currentMsg.recipientEmailsJson,
              ccEmailsJson: incoming.ccEmailsJson ?? currentMsg.ccEmailsJson,
              conversationId: incoming.conversationId ?? currentMsg.conversationId,
              senderName: incoming.senderName,
              bodyPreview: incoming.bodyPreview,
              hasAttachments: incoming.hasAttachments,
              attachmentNamesJson: incoming.attachmentNamesJson,
            };
          }
        }
      }

      // Sort by receivedAtIso descending
      state.messages.sort((a, b) => b.receivedAtIso.localeCompare(a.receivedAtIso));

      // Re-evaluate Unassigned messages against active dossiers and learned rules
      let autoFittedCount = 0;
      state.messages = state.messages.map((m) => {
        if (m.classificationStatus !== 'Unassigned') {
          return m;
        }
        const upperSubj = m.subject.toUpperCase();
        const upperBody = m.bodyPreview.toUpperCase();

        // Check learned rules first
        for (const rule of state.rules) {
          const pat = rule.pattern.toUpperCase();
          if (
            pat &&
            (upperSubj.includes(pat) ||
              upperBody.includes(pat) ||
              m.senderEmail.toUpperCase().includes(pat) ||
              m.senderDomain.toUpperCase().includes(pat))
          ) {
            if (rule.projectCode === 'NON_PROJECT') {
              autoFittedCount++;
              return {
                ...m,
                classificationStatus: 'NotAProject',
                assignedProjectCode: null,
                confidenceScore: Math.min(99, rule.weight + 25),
                matchedRulesJson: JSON.stringify([
                  `${rule.ruleKind}:${rule.pattern} (+${rule.weight}, Regra ativa)`,
                ]),
              };
            }
            autoFittedCount++;
            return {
              ...m,
              classificationStatus: 'AutoAssigned',
              assignedProjectCode: rule.projectCode,
              confidenceScore: Math.min(99, rule.weight + 25),
              matchedRulesJson: JSON.stringify([
                `${rule.ruleKind}:${rule.pattern} (+${rule.weight}, Regra ativa)`,
              ]),
            };
          }
        }

        // Check active dossiers by code/studyCode
        for (const dossier of state.dossiers) {
          const code = dossier.code.toUpperCase();
          const studyCode = dossier.studyCode.toUpperCase();
          if (
            (code && (upperSubj.includes(code) || upperBody.includes(code))) ||
            (studyCode && (upperSubj.includes(studyCode) || upperBody.includes(studyCode)))
          ) {
            autoFittedCount++;
            return {
              ...m,
              classificationStatus: 'AutoAssigned',
              assignedProjectCode: dossier.code,
              confidenceScore: 95,
              matchedRulesJson: JSON.stringify([
                `Keyword:${dossier.code.toLowerCase()} (+70, Correspondência com Dossiê Ativo)`,
              ]),
            };
          }
        }

        return m;
      });

      syncIncomingStudiesAndProceduresFromArrivedStudies(state);
      writePersistedGreenfieldMailboxState(state);

      return jsonResponse({
        totalMessages: state.messages.length,
        autoAssignedCount: state.messages.filter((m) => m.classificationStatus === 'AutoAssigned')
          .length,
        manualAssignedCount: state.messages.filter(
          (m) => m.classificationStatus === 'ManualAssigned',
        ).length,
        unassignedCount: state.messages.filter((m) => m.classificationStatus === 'Unassigned')
          .length,
        notAProjectCount: state.messages.filter((m) => m.classificationStatus === 'NotAProject')
          .length,
        newlyDownloadedCount,
        autoFittedCount,
        syncedAtIso: new Date().toISOString(),
      });
    }
    if (
      segments[2] === 'mailboxes' &&
      segments[3] === 'messages' &&
      segments[5] === 'assign' &&
      method === 'POST'
    ) {
      const id = segments[4] ?? '';
      const body = await readBodyObj(request);
      const projectCode = strField(body, 'projectCode', 'SOLAIR').toUpperCase();
      const customRuleToken = optStrField(body, 'customRuleToken');
      const reviewedBy = strField(body, 'reviewedBy');
      const idx = state.messages.findIndex((m) => m.id === id);
      const existing = state.messages[idx];
      if (idx >= 0 && existing) {
        const isNotProject = projectCode === 'NON_PROJECT';
        const learnedPattern = (
          customRuleToken?.trim() || (!isNotProject ? projectCode : existing.senderEmail)
        ).toLowerCase();
        const updated: SharedMailboxMessageItem = {
          ...existing,
          classificationStatus: isNotProject ? 'NotAProject' : 'ManualAssigned',
          assignedProjectCode: isNotProject ? null : projectCode,
          confidenceScore: 100,
          matchedRulesJson: JSON.stringify([
            isNotProject
              ? `Confirmado pelo humano (${reviewedBy}): Fora de Escopo de Estudo · Filtro negativo aprendido: "${learnedPattern}"`
              : `Confirmado pelo humano (${reviewedBy}) → Estudo ${projectCode} · Regra aprendida: "${learnedPattern}" (+85 pts)`,
          ]),
          manuallyReviewedBy: reviewedBy,
        };
        state.messages[idx] = updated;

        let autoFittedPendingCount = 0;
        if (!isNotProject) {
          const upperLearned = learnedPattern.toUpperCase();
          state.messages = state.messages.map((m) => {
            if (m.id === id || m.classificationStatus !== 'Unassigned') {
              return m;
            }
            const detected = extractStudyMetadataFromRealEmail(m);
            const upperSubj = m.subject.toUpperCase();
            const upperBody = m.bodyPreview.toUpperCase();
            const upperSender = m.senderEmail.toUpperCase();
            if (
              detected?.code.toUpperCase() === projectCode ||
              upperSubj.includes(projectCode) ||
              (upperLearned.length >= 3 &&
                (upperSubj.includes(upperLearned) ||
                  upperBody.includes(upperLearned) ||
                  upperSender.includes(upperLearned)))
            ) {
              autoFittedPendingCount++;
              return {
                ...m,
                classificationStatus: 'AutoAssigned',
                assignedProjectCode: projectCode,
                confidenceScore: 95,
                matchedRulesJson: JSON.stringify([
                  `Keyword:${learnedPattern} (+85, Aprendido da confirmação humana em ${projectCode})`,
                ]),
              };
            }
            return m;
          });

          const candidate = CANDIDATE_PROJECTS_FROM_CONTRACT_EMAILS.find(
            (c) =>
              c.code.toUpperCase() === projectCode || c.studyCode.toUpperCase() === projectCode,
          );
          const linkedCount = state.messages.filter(
            (m) => m.assignedProjectCode?.toUpperCase() === projectCode,
          ).length;
          const existingDossierIdx = state.dossiers.findIndex(
            (d) => d.code.toUpperCase() === projectCode,
          );
          const dossierPayload: StudyContractDossierItem = {
            id:
              existingDossierIdx >= 0 && state.dossiers[existingDossierIdx]
                ? state.dossiers[existingDossierIdx].id
                : `0199ba03-0000-7000-8000-${String(state.dossiers.length + 10).padStart(12, '0')}`,
            code: projectCode,
            studyCode: candidate?.studyCode ?? projectCode,
            title: candidate?.title ?? `Estudo Clínico ${projectCode}`,
            sponsorName: candidate?.sponsorName ?? existing.senderName,
            croName: candidate?.croName ?? existing.senderDomain,
            currency: 'BRL',
            exchangeRateBrl: 1,
            taxRegime: 'LucroReal',
            taxRatePercent: projectCode === 'EPOCA' ? 0 : 14.25,
            overheadPercent: candidate?.overheadPercent ?? 0,
            totalBaselineBrl: candidate?.totalBaselineBrl ?? 0,
            totalNegotiatedBrl: candidate?.totalNegotiatedBrl ?? 0,
            passThroughTotalBrl: candidate?.passThroughTotalBrl ?? 0,
            estimatedMarginPercent: candidate?.estimatedMarginPercent ?? 0,
            status: 'UnderAnalysis',
            riskScore: projectCode === 'EPOCA' ? 35 : 0,
            clinicalAnalysisJson:
              projectCode === 'EPOCA'
                ? '{"complexity":"Ensaio Clínico Randomizado Fatorial 2 x 2 (Colchicina + Polipílula · CAAE 59057422.4.1001.0060) · Anexo I: 21 Pagamentos (D0 a D1440 — 48 meses) + Taxa Única de Startup R$ 3.000,00 · Valores Unitários: R$ 5.900,00/participante (Overhead 25% incluso) · Recrutamento Competitivo (Cláusula 4.5 — até 10.797 no estudo geral) · Campos cadastrais CNPJ XX.XXX.XXX/XXXX-XX e PI em branco na minuta para preenchimento pelo Centro de Pesquisa TrialsHub"}'
                : '{"complexity":"Dossiê criado a partir de contratos@giyus.com.br — exibindo estritamente dados literais do e-mail/anexo"}',
            budgetGridJson:
              candidate && candidate.budgetRows.length > 0
                ? JSON.stringify(candidate.budgetRows)
                : '[]',
            contractClausesJson:
              candidate && candidate.contractClauses.length > 0
                ? JSON.stringify(candidate.contractClauses)
                : '[]',
            miroFishSimulationJson: '{"scenariosRun":0,"acceptanceProbabilityPercent":0}',
            jevScoreJson:
              '{"model":"Literal-Document-Extraction","decision":"UNDER_INITIAL_ANALYSIS","confidence":1}',
            linkedEmailsCount: linkedCount,
          };

          if (existingDossierIdx >= 0) {
            state.dossiers[existingDossierIdx] = dossierPayload;
          } else {
            state.dossiers.push(dossierPayload);
          }

          if (!state.studies.some((s) => s.code.toUpperCase() === projectCode)) {
            state.studies.push({
              id: `0199ba02-0000-7000-8000-${String(state.studies.length + 10).padStart(12, '0')}`,
              code: projectCode,
              protocolCode: dossierPayload.studyCode,
              title: dossierPayload.title,
              phase: 'PhaseIII',
              therapeuticArea: 'Cardiologia / Pesquisa Clínica',
              sponsorName: dossierPayload.sponsorName,
              croName: dossierPayload.croName,
              status: 'Feasibility',
              targetEnrollment: candidate?.targetParticipants ?? 0,
              currentEnrollment: 0,
            });
          }
        }

        if (learnedPattern) {
          const existingRuleIdx = state.rules.findIndex(
            (r) =>
              r.projectCode.toUpperCase() === projectCode &&
              r.pattern.toLowerCase() === learnedPattern,
          );
          if (existingRuleIdx >= 0 && state.rules[existingRuleIdx]) {
            const currentRule = state.rules[existingRuleIdx];
            state.rules.splice(existingRuleIdx, 1);
            state.rules.unshift({
              ...currentRule,
              weight: Math.max(currentRule.weight, 85),
              learnedFromHumanFeedback: true,
              hitCount: currentRule.hitCount + autoFittedPendingCount + 1,
            });
          } else {
            state.rules.unshift({
              id: `0199ba05-0000-7000-8000-${String(state.rules.length + 100).padStart(12, '0')}`,
              projectCode,
              projectName: isNotProject ? 'Fora de Escopo de Estudo' : `Projeto ${projectCode}`,
              ruleKind: customRuleToken?.trim() || !isNotProject ? 'Keyword' : 'SenderEmail',
              pattern: learnedPattern,
              weight: 85,
              learnedFromHumanFeedback: true,
              hitCount: autoFittedPendingCount + 1,
              createdByRole: 'HUMAN_FEEDBACK',
            });
          }
        }

        syncIncomingStudiesAndProceduresFromArrivedStudies(state);
        writePersistedGreenfieldMailboxState(state);

        return jsonResponse({
          message: updated,
          autoFittedPendingCount,
          learnedRulesCount: 1,
          learnedRulePattern: learnedPattern,
          dossierCreatedOrUpdatedCode: isNotProject ? null : projectCode,
        });
      }
    }
    if (
      segments[2] === 'mailboxes' &&
      segments[3] === 'messages' &&
      segments[5] === 'unassign' &&
      method === 'POST'
    ) {
      const id = segments[4] ?? '';
      const idx = state.messages.findIndex((m) => m.id === id);
      const existing = state.messages[idx];
      if (idx >= 0 && existing) {
        const updated: SharedMailboxMessageItem = {
          ...existing,
          classificationStatus: 'Unassigned',
          assignedProjectCode: null,
          confidenceScore: 0,
          matchedRulesJson: '["Desvinculado manualmente pelo usuário"]',
        };
        state.messages[idx] = updated;
        writePersistedGreenfieldMailboxState(state);
        return jsonResponse({
          message: updated,
          autoFittedPendingCount: 0,
          learnedRulesCount: 0,
        });
      }
    }
    if (
      segments[2] === 'mailboxes' &&
      segments[3] === 'messages' &&
      segments[5] === 'mark-not-project' &&
      method === 'POST'
    ) {
      const id = segments[4] ?? '';
      const idx = state.messages.findIndex((m) => m.id === id);
      const existing = state.messages[idx];
      if (idx >= 0 && existing) {
        const updated: SharedMailboxMessageItem = {
          ...existing,
          classificationStatus: 'NotAProject',
          assignedProjectCode: null,
          confidenceScore: 100,
          matchedRulesJson: '["Marcado pelo humano: Não faz parte de nenhum projeto orçamentário"]',
        };
        state.messages[idx] = updated;
        writePersistedGreenfieldMailboxState(state);
        return jsonResponse({
          message: updated,
          autoFittedPendingCount: 0,
          learnedRulesCount: 2,
        });
      }
    }

    if (
      segments[2] === 'mailboxes' &&
      segments[3] === 'finance-reimbursements' &&
      segments.length === 4 &&
      method === 'GET'
    ) {
      return jsonResponse(buildFinanceMailboxReimbursementDashboard(state));
    }

    if (
      segments[2] === 'mailboxes' &&
      segments[3] === 'finance-reimbursements' &&
      segments[4] === 'simulate-incoming' &&
      method === 'POST'
    ) {
      if (isProductionRuntime()) {
        return jsonResponse(
          { error: 'Simulação desabilitada em Produção; permitida apenas em ambiente DEV.' },
          403,
        );
      }
      const body = await readBodyObj(request);
      const created = simulateFakeFinanceMailboxEmail(state, body);
      return jsonResponse(created, 201);
    }

    if (
      segments[2] === 'mailboxes' &&
      segments[3] === 'finance-reimbursements' &&
      segments[5] === 'reconcile' &&
      method === 'POST'
    ) {
      const id = segments[4] ?? '';
      const body = await readBodyObj(request);
      const updated = reconcileFakeFinanceMailboxInvoice(state, id, body);
      if (updated) {
        return jsonResponse(updated);
      }
    }

    if (segments[2] === 'dossiers' && segments[4] === 'workspace' && method === 'GET') {
      const code = (segments[3] ?? 'EPOCA').toUpperCase();
      const dossier = state.dossiers.find((d) => d.code === code) ?? state.dossiers[0];
      const baseTotal = dossier?.totalNegotiatedBrl ?? 462800;
      const status = dossier?.status ?? 'InNegotiation';
      return jsonResponse(buildFakeWorkspaceBundle(state, code, baseTotal, status));
    }

    if (segments[2] === 'dossiers' && segments[4] === 'sync-holding-prices' && method === 'POST') {
      const code = (segments[3] ?? 'EPOCA').toUpperCase();
      const body = await readBodyObj(request);
      const action = strField(body, 'action', 'APPLY_NEW_HOLDING_TABLE');
      const idx = state.dossiers.findIndex((d) => d.code === code);
      const existing = state.dossiers[idx];
      if (idx >= 0 && existing && existing.status !== 'ApprovedForExecution') {
        if (action === 'KEEP_PROPOSAL_VERSION') {
          state.studyDismissedAlerts[code] = true;
        } else {
          state.studyTableVersions[code] = state.holdingPriceTableVersion;
          state.studyDismissedAlerts[code] = false;
          const updatedTotal = Math.round(existing.totalNegotiatedBrl * 1.045);
          state.dossiers[idx] = {
            ...existing,
            totalNegotiatedBrl: updatedTotal,
            estimatedMarginPercent: Number((existing.estimatedMarginPercent + 0.8).toFixed(2)),
          };
        }
      }
      const currentDossier = state.dossiers[idx] ?? existing;
      return jsonResponse(
        buildFakeWorkspaceBundle(
          state,
          code,
          currentDossier?.totalNegotiatedBrl ?? 462800,
          currentDossier?.status ?? 'InNegotiation',
        ),
      );
    }

    if (
      segments[2] === 'dossiers' &&
      segments[4] === 'council' &&
      segments[5] === 'run' &&
      method === 'POST'
    ) {
      const code = (segments[3] ?? 'EPOCA').toUpperCase();
      const dossier = state.dossiers.find((d) => d.code === code);
      const baseTotal = dossier?.totalNegotiatedBrl ?? 462800;
      return jsonResponse(buildFakeCouncilRun(code, baseTotal));
    }

    if (segments[2] === 'dossiers' && segments[4] === 'critiques' && method === 'POST') {
      const code = (segments[3] ?? 'EPOCA').toUpperCase();
      const body = await readBodyObj(request);
      const title = strField(body, 'title', 'Nova Discrepância SoA vs. Orçamento');
      const distilledGlobalRule = strField(body, 'distilledGlobalRule', title);
      const promoteGlobal = Boolean(body['promoteGlobal']);
      const actorEmail = strField(body, 'actorEmail').toLowerCase();
      const isSingleApprover = actorEmail === 'syllas@giyus.com.br';
      let globalRuleStatus = 'LOCAL_STUDY_ONLY';

      if (promoteGlobal && distilledGlobalRule) {
        const nextStatus: GlobalAiRuleItemDto['status'] = isSingleApprover
          ? 'APPROVED_GLOBAL'
          : 'PENDING_GLOBAL_APPROVAL';
        globalRuleStatus = nextStatus;
        const createdRule: GlobalAiRuleItemDto = {
          id: `grule-${String(state.globalRules.length + 10).padStart(3, '0')}`,
          studyCode: code,
          ruleTitle: title,
          ruleText: distilledGlobalRule,
          originModule: 'PROTOCOL_CRITIQUE',
          proposedByEmail: actorEmail,
          status: nextStatus,
          approvedByEmail: isSingleApprover ? 'syllas@giyus.com.br' : null,
          updatedAtIso: new Date().toISOString(),
        };
        state.globalRules.unshift(createdRule);
        state.harness2State = {
          ...state.harness2State,
          evolutionGeneration: state.harness2State.evolutionGeneration + 1,
          alwaysOnTokensUsed: state.harness2State.alwaysOnTokensUsed + 95,
          longTermMemoryExcerpt: `${state.harness2State.longTermMemoryExcerpt}\n- [${createdRule.id.toUpperCase()} · ${createdRule.originModule}] ${createdRule.ruleTitle}: ${createdRule.ruleText}`,
          experienceLogExcerpt: `${state.harness2State.experienceLogExcerpt}\n- [Gen #${state.harness2State.evolutionGeneration + 1} · ${code}] ${createdRule.ruleTitle}: ${createdRule.ruleText}`,
        };
      }

      const list = state.critiquesByStudy[code] ?? buildDefaultCritiques(code);
      const created: ProtocolCritiqueItemDto = {
        id: `crit-${String(list.length + 10).padStart(3, '0')}`,
        studyCode: code,
        title,
        category: strField(body, 'category', 'PROTOCOL_BUDGET_DISCREPANCY'),
        severity: strField(body, 'severity', 'HIGH'),
        status: 'OPEN_ATTENTION',
        estimatedExposureBrl: numField(body, 'estimatedExposureBrl', 12000),
        protocolReference: strField(body, 'protocolReference', 'SoA Visitas V1-V6'),
        discrepancyDescription: strField(body, 'discrepancyDescription', title),
        recommendedAction: strField(body, 'recommendedAction', 'Incluir rubrica no orçamento.'),
        distilledGlobalRule,
        globalRuleStatus,
      };
      state.critiquesByStudy[code] = [created, ...list];
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(created, 201);
    }

    if (segments[2] === 'dossiers' && segments[4] === 'payments' && method === 'POST') {
      const code = (segments[3] ?? 'EPOCA').toUpperCase();
      const body = await readBodyObj(request);
      const originalAmount = numField(body, 'originalAmount', 5000);
      const currency = strField(body, 'currency', 'BRL').toUpperCase();
      const ptax = currency === 'BRL' ? 1 : numField(body, 'frozenPtaxRate', 5.72);
      const paidBrl = Number((originalAmount * ptax).toFixed(2));
      const dossier = state.dossiers.find((d) => d.code === code);
      const list =
        state.paymentsByStudy[code] ??
        buildDefaultPayments(
          code,
          dossier?.status ?? 'UnderAnalysis',
          state.suppliers.length === 0,
        );
      const created: StudyFinancialPaymentItemDto = {
        id: `pay-${String(list.length + 10).padStart(3, '0')}`,
        studyCode: code,
        paymentDate: strField(body, 'paymentDate', '2026-10-26'),
        category: strField(body, 'category', 'PROCEDIMENTO'),
        procedureOrVisitCode: strField(body, 'procedureOrVisitCode', 'V2-PK'),
        patientRef: strField(body, 'patientRef', 'SUBJ-001'),
        originalAmount,
        currency,
        frozenPtaxRate: ptax,
        paidAmountBrl: paidBrl,
        expectedBudgetBrl: numField(body, 'expectedBudgetBrl', paidBrl),
        invoiceNumber: strField(body, 'invoiceNumber', 'NF-2026-0199'),
        daysToReceive: numField(body, 'daysToReceive', 30),
        status: 'CONCILIADO',
      };
      state.paymentsByStudy[code] = [created, ...list];
      return jsonResponse(created, 201);
    }

    if (
      segments[2] === 'dossiers' &&
      segments[4] === 'cashflow' &&
      segments[5] === 'run' &&
      method === 'POST'
    ) {
      const code = (segments[3] ?? 'EPOCA').toUpperCase();
      const body = await readBodyObj(request);
      const dossier = state.dossiers.find((d) => d.code === code);
      const baseTotal = dossier?.totalNegotiatedBrl ?? 462800;
      const proj = buildFakeCashflow(
        code,
        baseTotal,
        numField(body, 'targetPatients', 12),
        numField(body, 'durationMonths', 12),
        numField(body, 'overheadRegularPercent', 28),
        numField(body, 'netReceiptDaysRegular', 42),
        numField(body, 'screenFailureRegularPercent', 25),
      );
      state.cashflowByStudy[code] = proj;
      return jsonResponse(proj);
    }

    if (segments[2] === 'global-rules' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.globalRules);
    }

    if (segments[2] === 'global-rules' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const actorEmail = strField(body, 'actorEmail').toLowerCase();
      const isSingleApprover = actorEmail === 'syllas@giyus.com.br';
      const created: GlobalAiRuleItemDto = {
        id: `grule-${String(state.globalRules.length + 10).padStart(3, '0')}`,
        studyCode: strField(body, 'studyCode', 'GLOBAL').toUpperCase(),
        ruleTitle: strField(body, 'ruleTitle', 'Regra Operacional & Orçamentária GIYUS'),
        ruleText: strField(body, 'ruleText', 'Regra aprendida e validada.'),
        originModule: strField(body, 'originModule', 'LLM_CHAT').toUpperCase(),
        proposedByEmail: actorEmail,
        status: isSingleApprover ? 'APPROVED_GLOBAL' : 'PENDING_GLOBAL_APPROVAL',
        approvedByEmail: isSingleApprover ? 'syllas@giyus.com.br' : null,
        updatedAtIso: new Date().toISOString(),
      };
      state.globalRules.unshift(created);
      state.harness2State = {
        ...state.harness2State,
        evolutionGeneration: state.harness2State.evolutionGeneration + 1,
        alwaysOnTokensUsed: state.harness2State.alwaysOnTokensUsed + 110,
        longTermMemoryExcerpt: `${state.harness2State.longTermMemoryExcerpt}\n- [${created.id.toUpperCase()} · ${created.originModule}] ${created.ruleTitle}: ${created.ruleText}`,
        experienceLogExcerpt: `${state.harness2State.experienceLogExcerpt}\n- [Gen #${state.harness2State.evolutionGeneration + 1} · Regra Global Travada em Todas as LLMs] ${created.ruleTitle}: ${created.ruleText}`,
      };
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(created, 201);
    }

    if (
      segments[2] === 'global-rules' &&
      segments[4] === 'approve' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const ruleId = segments[3] ?? '';
      const body = await readBodyObj(request);
      const approverEmail = strField(body, 'approverEmail').toLowerCase();
      if (approverEmail !== 'syllas@giyus.com.br') {
        return jsonResponse(
          {
            detail:
              'Apenas o Aprovador Único Global da Holding (syllas@giyus.com.br) pode aprovar regras globais.',
          },
          403,
        );
      }
      const idx = state.globalRules.findIndex((r) => r.id === ruleId);
      const existing = state.globalRules[idx];
      if (idx >= 0 && existing) {
        const updated: GlobalAiRuleItemDto = {
          ...existing,
          status: 'APPROVED_GLOBAL',
          approvedByEmail: 'syllas@giyus.com.br',
          updatedAtIso: new Date().toISOString(),
        };
        state.globalRules[idx] = updated;
        writePersistedGreenfieldMailboxState(state);
        return jsonResponse(updated);
      }
    }

    if (segments[2] === 'harness2' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.harness2State);
    }

    if (
      segments[2] === 'harness2' &&
      segments[3] === 'evolve' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const cue = strField(
        body,
        'cue',
        'Cruzamento Contrato × Mapa Comparativo 3 Cotações × Estoque FEFO × Financeiro AP/AR',
      );
      const lesson = strField(
        body,
        'lesson',
        'Toda economia obtida no Mapa Comparativo de 3 Cotações abaixo do Teto Pass-Through do Contrato atualiza automaticamente o benchmark unitário em c3 LongTermMEMORY.md.',
      );
      const originModule = strField(body, 'originModule', 'INTEGRATED_ERP_HARNESS2').toUpperCase();
      const nextGen = state.harness2State.evolutionGeneration + 1;
      state.harness2State = {
        ...state.harness2State,
        evolutionGeneration: nextGen,
        alwaysOnTokensUsed: state.harness2State.alwaysOnTokensUsed + 145,
        longTermMemoryExcerpt: `${state.harness2State.longTermMemoryExcerpt}\n- [GLOBAL_ALL · Gen #${nextGen} · ${originModule}] ${lesson}`,
        experienceLogExcerpt: `${state.harness2State.experienceLogExcerpt}\n- [Gen #${nextGen} · Cue: ${cue}] ${lesson} (Validado via HarnessTree.validate c1..c8)`,
      };
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(state.harness2State);
    }
  }

  // FACILITIES & ASSETS (/api/v1/network/facilities*)
  if (segments[1] === 'facilities') {
    const computeEqAuditHash = (seed: string): string => {
      let h1 = 0xdeadbeef ^ seed.length;
      let h2 = 0x41c6ce57 ^ seed.length;
      for (let i = 0; i < seed.length; i++) {
        const ch = seed.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
      }
      h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
      h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
      const hex1 = (h1 >>> 0).toString(16).padStart(8, '0');
      const hex2 = (h2 >>> 0).toString(16).padStart(8, '0');
      return `sha256:${hex1}${hex2}${hex1}${hex2}${hex1}${hex2}${hex1}${hex2}`;
    };

    if (segments[2] === 'assets' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.facilityAssets);
    }
    if (segments[2] === 'assets' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const ownership = strField(
        body,
        'ownershipKind',
        'GIYUS_TENANT',
      ) as EquipmentAssetItemDto['ownershipKind'];
      const isGiyusOwned = ownership === 'GIYUS_HOLDING' || ownership === 'GIYUS_TENANT';
      const usefulLife = Math.max(12, numField(body, 'usefulLifeMonths', 60));
      const elapsed = Math.min(usefulLife, Math.max(0, numField(body, 'elapsedMonths', 6)));
      const rawVal = numField(body, 'acquisitionOrCustodyValueBrl', 24000);
      const resVal = isGiyusOwned
        ? Math.min(numField(body, 'residualValueBrl', rawVal * 0.1), rawVal * 0.5)
        : 0;
      const monthlyDep = isGiyusOwned ? Number(((rawVal - resVal) / usefulLife).toFixed(2)) : 0;
      const accumDep = isGiyusOwned
        ? Math.min(rawVal - resVal, Number((monthlyDep * elapsed).toFixed(2)))
        : 0;
      const netBook = isGiyusOwned ? Math.max(resVal, Number((rawVal - accumDep).toFixed(2))) : 0;
      const hourlyCost = isGiyusOwned ? Number((monthlyDep / 160).toFixed(2)) : 0;
      const avgAnnualDepPct =
        typeof body['averageAnnualDepreciationPercent'] === 'number'
          ? Number(body['averageAnnualDepreciationPercent'])
          : Number(((12 / usefulLife) * 100).toFixed(2));
      const insuredCustody = isGiyusOwned ? 0 : rawVal;
      const requiresCal = body['requiresCalibration'] !== false;
      const baseCal = requiresCal ? Math.max(1, numField(body, 'baseCalibrationMonths', 12)) : 0;
      const studyOverrideRaw = body['studyOverrideCalibrationMonths'];
      const studyOverride =
        typeof studyOverrideRaw === 'number' && studyOverrideRaw > 0 ? studyOverrideRaw : null;
      const effectiveCal = requiresCal
        ? studyOverride !== null
          ? Math.min(baseCal, studyOverride)
          : baseCal
        : 0;
      const linkedStudyCode = optStrField(body, 'linkedStudyCode');
      const relatedStudyCodesRaw = Array.isArray(body['relatedStudyCodes'])
        ? (body['relatedStudyCodes'] as string[]).map((s) => String(s).trim()).filter(Boolean)
        : linkedStudyCode
          ? linkedStudyCode
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
      const blocksCloseOut = !isGiyusOwned && linkedStudyCode !== null;
      const supId = optStrField(body, 'assignedCalibrationSupplierId');
      const supplier = state.calibrationSuppliers.find((s) => s.id === supId);
      const typeCodeStr = strField(body, 'typeCode', 'OTHER');
      const isColdChainType =
        typeCodeStr.includes('FREEZER') ||
        typeCodeStr.includes('REFRIGERATOR') ||
        typeCodeStr.includes('THERMO');
      const requiresConstReadings =
        typeof body['requiresConstantReadings'] === 'boolean'
          ? Boolean(body['requiresConstantReadings'])
          : isColdChainType;
      const minAllowed =
        typeof body['readingMinAllowed'] === 'number'
          ? Number(body['readingMinAllowed'])
          : typeCodeStr.includes('FREEZER_MINUS80')
            ? -86
            : typeCodeStr.includes('FREEZER')
              ? -25
              : 2;
      const maxAllowed =
        typeof body['readingMaxAllowed'] === 'number'
          ? Number(body['readingMaxAllowed'])
          : typeCodeStr.includes('FREEZER_MINUS80')
            ? -70
            : typeCodeStr.includes('FREEZER')
              ? -15
              : 8;
      const readingUnit = strField(body, 'readingUnit', '°C');
      const readingRangeLabel = strField(
        body,
        'readingTargetRangeLabel',
        requiresConstReadings ? `${minAllowed}${readingUnit} a ${maxAllowed}${readingUnit}` : '',
      );
      const purchaseDateStr = strField(body, 'purchaseDate', '2026-10-26');
      const invoiceNumStr =
        optStrField(body, 'equipmentInvoiceNumber') ??
        `NF-e 000.00${state.facilityAssets.length + 6100}`;
      const researchCenterStr = strField(
        body,
        'researchCenterName',
        strField(body, 'ownerEntityName', 'GIYUS Curitiba Research Center'),
      );
      const nowIso = new Date().toISOString();
      const createdId = `eq-${String(state.facilityAssets.length + 10).padStart(3, '0')}`;
      const initialAuditEntry: EquipmentAuditLogEntryDto = {
        id: `eq-aud-${Date.now()}-1`,
        assetId: createdId,
        timestampIso: nowIso,
        actorEmail: strField(body, 'lastRecordedByEmail', 'syllas@giyus.com.br'),
        actionType: 'CREATED',
        summary: `Cadastro inicial do equipamento no centro ${researchCenterStr} (${invoiceNumStr} · Compra ${purchaseDateStr} · Vida útil ${usefulLife}m · Depreciação média ${avgAnnualDepPct}% a.a.).`,
        beforeAfterDiff: `Before: null -> After: { assetTag: "${strField(body, 'assetTag', `PAT-GIY-${state.facilityAssets.length + 100}`)}", center: "${researchCenterStr}", requiresCalibration: ${requiresCal}, periodicityMonths: ${effectiveCal}, invoice: "${invoiceNumStr}", studies: [${relatedStudyCodesRaw.join(', ')}] }`,
        sha256Hash: computeEqAuditHash(`${createdId}|CREATED|${nowIso}|${invoiceNumStr}`),
      };

      const created: EquipmentAssetItemDto = {
        id: createdId,
        assetTag: strField(body, 'assetTag', `PAT-GIY-${state.facilityAssets.length + 100}`),
        name: strField(body, 'name', 'Equipamento Clínico'),
        typeCode: typeCodeStr,
        manufacturerModel: strField(body, 'manufacturerModel', 'Modelo Clínico'),
        serialNumber: strField(body, 'serialNumber', `SN-${state.facilityAssets.length + 9000}`),
        siteCode: strField(body, 'siteCode', 'CWB-01'),
        researchCenterName: researchCenterStr,
        roomLocation: strField(body, 'roomLocation', 'Sala de Procedimentos'),
        criticality: strField(body, 'criticality', 'CRITICAL'),
        ownershipKind: ownership,
        ownerEntityName: strField(body, 'ownerEntityName', researchCenterStr),
        linkedStudyCode:
          linkedStudyCode ?? (relatedStudyCodesRaw.length > 0 ? relatedStudyCodesRaw.join(', ') : null),
        relatedStudyCodes: relatedStudyCodesRaw,
        purchaseDate: purchaseDateStr,
        purchaseSupplierName: strField(
          body,
          'purchaseSupplierName',
          supplier?.tradeName ?? 'Fornecedor Homologado GIYUS',
        ),
        averageAnnualDepreciationPercent: avgAnnualDepPct,
        requiresConstantReadings: requiresConstReadings,
        readingTargetRangeLabel: readingRangeLabel || null,
        readingMinAllowed: requiresConstReadings ? minAllowed : null,
        readingMaxAllowed: requiresConstReadings ? maxAllowed : null,
        readingUnit: requiresConstReadings ? readingUnit : null,
        constantReadings: [],
        equipmentAuditTrail: [initialAuditEntry],
        isDepreciableInGiyusBalance: isGiyusOwned,
        acquisitionValueBrl: isGiyusOwned ? rawVal : 0,
        residualValueBrl: resVal,
        usefulLifeMonths: usefulLife,
        elapsedMonths: elapsed,
        monthlyDepreciationBrl: monthlyDep,
        accumulatedDepreciationBrl: accumDep,
        netBookValueBrl: netBook,
        hourlyBudgetCostBrl: hourlyCost,
        insuredCustodyValueBrl: insuredCustody,
        loanPolicyOrContractRef: optStrField(body, 'loanPolicyOrContractRef'),
        endOfLifeDisposition: strField(
          body,
          'endOfLifeDisposition',
          isGiyusOwned ? 'DESTROY_WITH_PGRSS_CERTIFICATE' : 'RETURN_TO_SPONSOR_OR_LAB',
        ) as EquipmentAssetItemDto['endOfLifeDisposition'],
        dispositionStatus: blocksCloseOut ? 'PENDING_CLOSEOUT_RETURN' : 'ACTIVE_IN_USE',
        dispositionEvidenceRef: null,
        blocksStudyCloseOut: blocksCloseOut,
        requiresCalibration: requiresCal,
        requiredMagnitude: requiresCal
          ? strField(body, 'requiredMagnitude', 'TEMPERATURA_CADEIA_FRIA')
          : 'ISENTO_MANUTENCAO_PREVENTIVA',
        baseCalibrationMonths: baseCal,
        studyOverrideCalibrationMonths: studyOverride,
        effectiveCalibrationMonths: effectiveCal,
        assignedCalibrationSupplierId: supplier?.id ?? null,
        assignedCalibrationSupplierName: supplier?.tradeName ?? null,
        lastCalibratedDate: requiresCal ? purchaseDateStr : null,
        nextCalibrationDueDate: requiresCal ? '2027-04-26' : null,
        daysUntilCalibrationDue: requiresCal ? 182 : null,
        oneMonthWarningTriggerDate: requiresCal ? '2027-03-26' : null,
        isOneMonthWarningActive: false,
        oneMonthWarningMessage: requiresCal
          ? 'Aviso automático de 1 mês (T-30d) programado para 26/03/2027.'
          : null,
        calibrationSemaphore: requiresCal ? 'VALID' : 'EXEMPT_PREVENTIVE_ONLY',
        blockedForStudyUse: false,
        blockReason: null,
        lastCertificateNumber: requiresCal ? 'CERT-RBC-2026-NEW' : null,
        lastRecordedByEmail: requiresCal ? 'engenharia@giyus.com.br' : 'syllas@giyus.com.br',
        lastVerifiedByEmail: requiresCal ? 'syllas@giyus.com.br' : null,
        equipmentInvoiceNumber: invoiceNumStr,
        equipmentInvoiceAccessKey: `412610695844410001845500100000${state.facilityAssets.length + 6100}1009823410`,
        equipmentInvoiceIssuedDate: purchaseDateStr,
        calibrationServiceInvoiceNumber: requiresCal
          ? `NFS-e 2026/00${state.facilityAssets.length + 2210}`
          : null,
        calibrationFeeBrl: requiresCal ? 1280 : 0,
        compatibleCalibrationSuppliers: requiresCal
          ? state.calibrationSuppliers.map((s, index): CompatibleCalibrationSupplierItemDto => ({
              supplierId: s.id,
              tradeName: s.tradeName,
              rbcAccreditationCode: s.rbcAccreditationCode,
              coverageScope: s.coverageScope,
              slaTurnaroundDays: s.slaTurnaroundDays,
              estimatedCalibrationFeeBrl: 980 + index * 240,
              compatibilityReason: `Acreditado RBC/INMETRO · Cobertura ${s.coverageScope} · SLA ${s.slaTurnaroundDays}d`,
            }))
          : [],
        resourceCategory: strField(
          body,
          'resourceCategory',
          'EQUIPMENT',
        ) as EquipmentAssetItemDto['resourceCategory'],
        loanReturnDueDate:
          optStrField(body, 'loanReturnDueDate') ?? (!isGiyusOwned ? '2026-12-15' : null),
        daysUntilLoanReturnDue: !isGiyusOwned ? 50 : null,
        returnTriggerRule:
          optStrField(body, 'returnTriggerRule') ??
          (!isGiyusOwned
            ? 'Devolver ou Descartar conforme instrução do Laboratório Central / Patrocinador no Close-Out'
            : null),
        disposalExecutionMode: strField(
          body,
          'disposalExecutionMode',
          !isGiyusOwned ? 'SHIPPED_VIA_CARRIER' : 'PERFORMED_BY_SITE',
        ) as EquipmentAssetItemDto['disposalExecutionMode'],
        disposalCertificateRequirement: strField(
          body,
          'disposalCertificateRequirement',
          'WITH_DISPOSAL_CERTIFICATE',
        ) as EquipmentAssetItemDto['disposalCertificateRequirement'],
        sponsorOrderedDestruction: Boolean(body['sponsorOrderedDestruction']),
        canConvertDestructionToSiteUse:
          Boolean(body['sponsorOrderedDestruction']) &&
          strField(body, 'resourceCategory', 'EQUIPMENT') === 'EQUIPMENT',
        siteInterestStatus:
          Boolean(body['sponsorOrderedDestruction']) &&
          strField(body, 'resourceCategory', 'EQUIPMENT') === 'EQUIPMENT'
            ? 'ELIGIBLE_FOR_SITE_USE_EQUIPMENT_ONLY'
            : 'NOT_APPLICABLE',
      };
      state.facilityAssets.unshift(created);
      state.calibrationTimeline.unshift({
        id: `tl-new-${state.calibrationTimeline.length + 10}`,
        assetId: created.id,
        assetTag: created.assetTag,
        assetName: created.name,
        eventDate: purchaseDateStr,
        eventKind: 'EQUIPMENT_NFE_ENTRY',
        statusBadge: 'EXECUTED_VERIFIED',
        title: `Cadastro do Equipamento & Nota Fiscal (${created.equipmentInvoiceNumber ?? 'NF-e'})`,
        description: `Equipamento incorporado no centro ${researchCenterStr} sob regime ${created.ownershipKind}. Estudos vinculados: ${relatedStudyCodesRaw.length > 0 ? relatedStudyCodesRaw.join(', ') : 'Uso Geral'}. Aviso de calibração programado para 1 mês antes do vencimento (${created.oneMonthWarningTriggerDate ?? 'Isento'}).`,
        supplierName: created.purchaseSupplierName ?? created.assignedCalibrationSupplierName,
        rbcAccreditationCode: supplier?.rbcAccreditationCode ?? null,
        certificateNumber: created.lastCertificateNumber,
        equipmentInvoiceNumber: created.equipmentInvoiceNumber ?? null,
        calibrationServiceInvoiceNumber: created.calibrationServiceInvoiceNumber ?? null,
        documentValueBrl: rawVal,
        recordedByEmail: created.lastRecordedByEmail,
        verifiedByEmail: created.lastVerifiedByEmail,
        maintenanceCategory: 'ACQUISITION_NFE',
        maintenanceWorkOrderCode: null,
        downtimeHours: 0,
        partsReplacedSummary: null,
        postMaintenanceRecalibrationRequired: false,
      });
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(created, 201);
    }
    if (segments[2] === 'assets' && segments.length === 4 && method === 'PUT') {
      const id = segments[3] ?? '';
      const body = await readBodyObj(request);
      const idx = state.facilityAssets.findIndex((a) => a.id === id);
      const existing = state.facilityAssets[idx];
      if (idx < 0 || !existing) {
        return jsonResponse({ title: 'Equipamento não encontrado', status: 404 }, 404);
      }
      const actorEmail = strField(body, 'updatedByEmail', 'syllas@giyus.com.br');
      const usefulLife = Math.max(
        12,
        numField(body, 'usefulLifeMonths', existing.usefulLifeMonths),
      );
      const rawVal = numField(
        body,
        'acquisitionOrCustodyValueBrl',
        existing.acquisitionValueBrl || existing.insuredCustodyValueBrl || 24000,
      );
      const isGiyusOwned =
        existing.ownershipKind === 'GIYUS_HOLDING' || existing.ownershipKind === 'GIYUS_TENANT';
      const resVal = isGiyusOwned
        ? Math.min(
            numField(body, 'residualValueBrl', existing.residualValueBrl),
            rawVal * 0.5,
          )
        : 0;
      const monthlyDep = isGiyusOwned ? Number(((rawVal - resVal) / usefulLife).toFixed(2)) : 0;
      const avgAnnualDepPct =
        typeof body['averageAnnualDepreciationPercent'] === 'number'
          ? Number(body['averageAnnualDepreciationPercent'])
          : Number(((12 / usefulLife) * 100).toFixed(2));
      const requiresCal =
        typeof body['requiresCalibration'] === 'boolean'
          ? Boolean(body['requiresCalibration'])
          : existing.requiresCalibration;
      const baseCal = requiresCal
        ? Math.max(1, numField(body, 'baseCalibrationMonths', existing.baseCalibrationMonths || 12))
        : 0;
      const studyOverrideRaw = body['studyOverrideCalibrationMonths'];
      const studyOverride =
        typeof studyOverrideRaw === 'number' && studyOverrideRaw > 0
          ? studyOverrideRaw
          : studyOverrideRaw === null
            ? null
            : existing.studyOverrideCalibrationMonths;
      const effectiveCal = requiresCal
        ? studyOverride !== null
          ? Math.min(baseCal, studyOverride)
          : baseCal
        : 0;
      const relatedStudyCodes = Array.isArray(body['relatedStudyCodes'])
        ? (body['relatedStudyCodes'] as string[]).map((s) => String(s).trim()).filter(Boolean)
        : (existing.relatedStudyCodes ?? []);
      const linkedStudyCode =
        optStrField(body, 'linkedStudyCode') ??
        (relatedStudyCodes.length > 0 ? relatedStudyCodes.join(', ') : existing.linkedStudyCode);
      const requiresConstReadings =
        typeof body['requiresConstantReadings'] === 'boolean'
          ? Boolean(body['requiresConstantReadings'])
          : Boolean(existing.requiresConstantReadings);
      const nowIso = new Date().toISOString();
      const prevHash =
        existing.equipmentAuditTrail?.[0]?.sha256Hash ?? 'sha256:genesis00000000';
      const updateAuditEntry: EquipmentAuditLogEntryDto = {
        id: `eq-aud-${Date.now()}`,
        assetId: existing.id,
        timestampIso: nowIso,
        actorEmail,
        actionType: 'UPDATED',
        summary: `Atualização cadastral do equipamento (${strField(body, 'name', existing.name)}) no centro ${strField(body, 'researchCenterName', existing.researchCenterName ?? existing.ownerEntityName)}.`,
        beforeAfterDiff: `Before: { name: "${existing.name}", center: "${existing.researchCenterName ?? existing.ownerEntityName}", calMonths: ${existing.effectiveCalibrationMonths}, nf: "${existing.equipmentInvoiceNumber ?? ''}", usefulLife: ${existing.usefulLifeMonths}m } -> After: { name: "${strField(body, 'name', existing.name)}", center: "${strField(body, 'researchCenterName', existing.researchCenterName ?? existing.ownerEntityName)}", calMonths: ${effectiveCal}, nf: "${strField(body, 'equipmentInvoiceNumber', existing.equipmentInvoiceNumber ?? '')}", usefulLife: ${usefulLife}m }`,
        sha256Hash: computeEqAuditHash(`${prevHash}|UPDATED|${nowIso}|${actorEmail}`),
      };

      const updated: EquipmentAssetItemDto = {
        ...existing,
        assetTag: strField(body, 'assetTag', existing.assetTag),
        name: strField(body, 'name', existing.name),
        typeCode: strField(body, 'typeCode', existing.typeCode),
        manufacturerModel: strField(body, 'manufacturerModel', existing.manufacturerModel),
        serialNumber: strField(body, 'serialNumber', existing.serialNumber),
        siteCode: strField(body, 'siteCode', existing.siteCode),
        researchCenterName: strField(
          body,
          'researchCenterName',
          existing.researchCenterName ?? existing.ownerEntityName,
        ),
        ownerEntityName: strField(
          body,
          'researchCenterName',
          existing.researchCenterName ?? existing.ownerEntityName,
        ),
        roomLocation: strField(body, 'roomLocation', existing.roomLocation),
        criticality: strField(body, 'criticality', existing.criticality),
        linkedStudyCode,
        relatedStudyCodes,
        purchaseDate: strField(
          body,
          'purchaseDate',
          existing.purchaseDate ?? existing.equipmentInvoiceIssuedDate ?? '2026-10-26',
        ),
        purchaseSupplierName: strField(
          body,
          'purchaseSupplierName',
          existing.purchaseSupplierName ?? 'Fornecedor Homologado GIYUS',
        ),
        equipmentInvoiceNumber: strField(
          body,
          'equipmentInvoiceNumber',
          existing.equipmentInvoiceNumber ?? '',
        ),
        acquisitionValueBrl: isGiyusOwned ? rawVal : 0,
        residualValueBrl: resVal,
        usefulLifeMonths: usefulLife,
        monthlyDepreciationBrl: monthlyDep,
        averageAnnualDepreciationPercent: avgAnnualDepPct,
        requiresCalibration: requiresCal,
        requiredMagnitude: requiresCal
          ? strField(body, 'requiredMagnitude', existing.requiredMagnitude)
          : 'ISENTO_MANUTENCAO_PREVENTIVA',
        baseCalibrationMonths: baseCal,
        studyOverrideCalibrationMonths: studyOverride,
        effectiveCalibrationMonths: effectiveCal,
        calibrationSemaphore: requiresCal
          ? existing.calibrationSemaphore === 'EXEMPT_PREVENTIVE_ONLY'
            ? 'VALID'
            : existing.calibrationSemaphore
          : 'EXEMPT_PREVENTIVE_ONLY',
        requiresConstantReadings: requiresConstReadings,
        readingTargetRangeLabel: strField(
          body,
          'readingTargetRangeLabel',
          existing.readingTargetRangeLabel ?? '2°C a 8°C',
        ),
        readingMinAllowed:
          typeof body['readingMinAllowed'] === 'number'
            ? Number(body['readingMinAllowed'])
            : (existing.readingMinAllowed ?? 2),
        readingMaxAllowed:
          typeof body['readingMaxAllowed'] === 'number'
            ? Number(body['readingMaxAllowed'])
            : (existing.readingMaxAllowed ?? 8),
        readingUnit: strField(body, 'readingUnit', existing.readingUnit ?? '°C'),
        equipmentAuditTrail: [updateAuditEntry, ...(existing.equipmentAuditTrail ?? [])],
      };
      state.facilityAssets[idx] = updated;
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(updated);
    }
    if (segments[2] === 'assets' && segments.length === 4 && method === 'DELETE') {
      const id = segments[3] ?? '';
      const idx = state.facilityAssets.findIndex((a) => a.id === id);
      if (idx < 0) {
        return jsonResponse({ title: 'Equipamento não encontrado', status: 404 }, 404);
      }
      state.facilityAssets.splice(idx, 1);
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse({ deleted: true, id });
    }
    if (
      segments[2] === 'assets' &&
      segments[4] === 'readings' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const id = segments[3] ?? '';
      const body = await readBodyObj(request);
      const idx = state.facilityAssets.findIndex((a) => a.id === id);
      const existing = state.facilityAssets[idx];
      if (idx < 0 || !existing) {
        return jsonResponse({ title: 'Equipamento não encontrado', status: 404 }, 404);
      }
      const recordedByEmail = strField(body, 'recordedByEmail', 'coordenacao@giyus.com.br').trim();
      const measuredVal = numField(body, 'measuredValue', 4.5);
      const minVal =
        typeof body['minObserved'] === 'number' ? Number(body['minObserved']) : measuredVal - 0.5;
      const maxVal =
        typeof body['maxObserved'] === 'number' ? Number(body['maxObserved']) : measuredVal + 0.5;
      const unitStr = strField(body, 'unit', existing.readingUnit ?? '°C');
      const minAllowed = existing.readingMinAllowed ?? 2;
      const maxAllowed = existing.readingMaxAllowed ?? 8;
      const isExcursion =
        measuredVal < minAllowed ||
        measuredVal > maxAllowed ||
        minVal < minAllowed ||
        maxVal > maxAllowed;
      const nowIso = strField(body, 'recordedAtIso', new Date().toISOString());
      const prevHash =
        existing.equipmentAuditTrail?.[0]?.sha256Hash ?? 'sha256:genesis00000000';
      const readingHash = computeEqAuditHash(
        `${prevHash}|CONSTANT_READING|${nowIso}|${measuredVal}|${recordedByEmail}`,
      );
      const newReading: EquipmentConstantReadingDto = {
        id: `read-${Date.now()}`,
        assetId: existing.id,
        recordedAtIso: nowIso,
        parameterLabel: strField(body, 'parameterLabel', 'Temperatura Câmara Fria'),
        measuredValue: measuredVal,
        unit: unitStr,
        minObserved: minVal,
        maxObserved: maxVal,
        status: isExcursion ? 'THERMAL_EXCURSION' : 'WITHIN_RANGE',
        recordedByEmail,
        notes: optStrField(body, 'notes'),
        sha256AuditHash: readingHash,
      };
      const readingAudit: EquipmentAuditLogEntryDto = {
        id: `eq-aud-read-${Date.now()}`,
        assetId: existing.id,
        timestampIso: nowIso,
        actorEmail: recordedByEmail,
        actionType: 'CONSTANT_READING',
        summary: `Leitura constante registrada: ${measuredVal}${unitStr} (Mín ${minVal}${unitStr} / Máx ${maxVal}${unitStr}) — ${isExcursion ? 'EXCURSÃO TÉRMICA DETECTADA' : 'DENTRO DA FAIXA'}.`,
        beforeAfterDiff: `Faixa Alvo [${minAllowed}${unitStr} .. ${maxAllowed}${unitStr}] -> Leitura: ${measuredVal}${unitStr} (${newReading.status})`,
        sha256Hash: readingHash,
      };
      const updated: EquipmentAssetItemDto = {
        ...existing,
        requiresConstantReadings: true,
        constantReadings: [newReading, ...(existing.constantReadings ?? [])],
        equipmentAuditTrail: [readingAudit, ...(existing.equipmentAuditTrail ?? [])],
      };
      state.facilityAssets[idx] = updated;
      state.calibrationTimeline.unshift({
        id: `tl-read-${Date.now()}`,
        assetId: updated.id,
        assetTag: updated.assetTag,
        assetName: updated.name,
        eventDate: nowIso.slice(0, 10),
        eventKind: 'CALIBRATION_CERTIFIED',
        statusBadge: isExcursion ? 'OVERDUE_CRITICAL' : 'EXECUTED_VERIFIED',
        title: `Leitura Constante de Monitoramento (${measuredVal}${unitStr} · ${isExcursion ? 'Excursão Térmica' : 'Dentro da Faixa'})`,
        description: `Leitura constante (${newReading.parameterLabel}): Atual ${measuredVal}${unitStr} | Mín ${minVal}${unitStr} | Máx ${maxVal}${unitStr}. ${newReading.notes ?? ''} [Hash: ${readingHash.slice(0, 24)}...]`,
        supplierName: updated.researchCenterName ?? updated.ownerEntityName,
        rbcAccreditationCode: `Faixa: ${minAllowed}${unitStr} a ${maxAllowed}${unitStr}`,
        certificateNumber: readingHash.slice(0, 19),
        equipmentInvoiceNumber: updated.equipmentInvoiceNumber ?? null,
        calibrationServiceInvoiceNumber: null,
        documentValueBrl: null,
        recordedByEmail,
        verifiedByEmail: recordedByEmail,
        maintenanceCategory: 'CALIBRATION_RBC',
        maintenanceWorkOrderCode: null,
        downtimeHours: 0,
        partsReplacedSummary: null,
        postMaintenanceRecalibrationRequired: isExcursion,
      });
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(updated, 201);
    }
    if (
      segments[2] === 'assets' &&
      segments[4] === 'calibrate' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const id = segments[3] ?? '';
      const body = await readBodyObj(request);
      const recorder = strField(body, 'recordedByEmail').trim().toLowerCase();
      const verifier = strField(body, 'verifiedByEmail').trim().toLowerCase();
      if (!recorder || !verifier) {
        return jsonResponse(
          {
            title: 'Ator não identificado',
            detail:
              'ACTOR_REQUIRED: informe recordedByEmail e verifiedByEmail; o fake não assume um responsável.',
            errorCode: 'ACTOR_REQUIRED',
          },
          422,
        );
      }
      if (recorder === verifier) {
        return jsonResponse(
          {
            title: 'Violação de Segregação de Funções (SoD)',
            detail:
              'AUTHZ_SOD_VIOLATION: Segregação de Funções exige que o verificador seja pessoa distinta de quem registrou o certificado RBC.',
            errorCode: 'AUTHZ_SOD_VIOLATION',
          },
          422,
        );
      }
      const idx = state.facilityAssets.findIndex((a) => a.id === id);
      const existing = state.facilityAssets[idx];
      if (idx >= 0 && existing) {
        const supId = strField(body, 'supplierId', existing.assignedCalibrationSupplierId ?? '');
        const supplier = state.calibrationSuppliers.find((s) => s.id === supId);
        const nextDue = existing.effectiveCalibrationMonths <= 6 ? '2027-04-26' : '2027-10-26';
        const nextWarning = existing.effectiveCalibrationMonths <= 6 ? '2027-03-26' : '2027-09-26';
        const certNum = strField(body, 'certificateNumber', 'CERT-RBC-2026-9981');
        const nfseNum = `NFS-e 2026/00${state.calibrationTimeline.length + 2500}`;
        const nowIso = new Date().toISOString();
        const prevHash =
          existing.equipmentAuditTrail?.[0]?.sha256Hash ?? 'sha256:genesis00000000';
        const calAudit: EquipmentAuditLogEntryDto = {
          id: `eq-aud-cal-${Date.now()}`,
          assetId: existing.id,
          timestampIso: nowIso,
          actorEmail: `${recorder} (Verificado SoD: ${verifier})`,
          actionType: 'CALIBRATED',
          summary: `Calibração RBC registrada (${certNum}) com fornecedor ${supplier?.tradeName ?? existing.assignedCalibrationSupplierName ?? 'Laboratório RBC'}.`,
          beforeAfterDiff: `Before: { lastCert: "${existing.lastCertificateNumber ?? 'none'}" } -> After: { lastCert: "${certNum}", nextDue: "${nextDue}" }`,
          sha256Hash: computeEqAuditHash(`${prevHash}|CALIBRATED|${certNum}|${recorder}|${verifier}`),
        };
        const updated: EquipmentAssetItemDto = {
          ...existing,
          assignedCalibrationSupplierId: supplier?.id ?? existing.assignedCalibrationSupplierId,
          assignedCalibrationSupplierName:
            supplier?.tradeName ?? existing.assignedCalibrationSupplierName,
          lastCalibratedDate: strField(body, 'calibratedDate', '2026-10-26'),
          nextCalibrationDueDate: nextDue,
          daysUntilCalibrationDue: existing.effectiveCalibrationMonths <= 6 ? 182 : 365,
          oneMonthWarningTriggerDate: nextWarning,
          isOneMonthWarningActive: false,
          oneMonthWarningMessage: `Calibração renovada! Próximo aviso automático de 1 mês antes (T-30d) agendado para ${nextWarning}.`,
          calibrationSemaphore: 'VALID',
          blockedForStudyUse: false,
          blockReason: null,
          lastCertificateNumber: certNum,
          lastRecordedByEmail: recorder,
          lastVerifiedByEmail: verifier,
          calibrationServiceInvoiceNumber: nfseNum,
          equipmentAuditTrail: [calAudit, ...(existing.equipmentAuditTrail ?? [])],
        };
        state.facilityAssets[idx] = updated;
        state.calibrationTimeline.unshift({
          id: `tl-cal-${state.calibrationTimeline.length + 20}`,
          assetId: updated.id,
          assetTag: updated.assetTag,
          assetName: updated.name,
          eventDate: updated.lastCalibratedDate ?? '2026-10-26',
          eventKind: 'CALIBRATION_CERTIFIED',
          statusBadge: 'EXECUTED_VERIFIED',
          title: `Calibração Acreditada RBC Concluída (${certNum})`,
          description: `Certificado ${certNum} emitido por ${updated.assignedCalibrationSupplierName ?? 'Laboratório RBC'} e verificado em dupla checagem SoD (${recorder} → ${verifier}). Próximo alerta de 1 mês em ${nextWarning}.`,
          supplierName: updated.assignedCalibrationSupplierName,
          rbcAccreditationCode: supplier?.rbcAccreditationCode ?? null,
          certificateNumber: certNum,
          equipmentInvoiceNumber: updated.equipmentInvoiceNumber ?? null,
          calibrationServiceInvoiceNumber: nfseNum,
          documentValueBrl: updated.calibrationFeeBrl ?? 1180,
          recordedByEmail: recorder,
          verifiedByEmail: verifier,
          maintenanceCategory: 'CALIBRATION_RBC',
          maintenanceWorkOrderCode: null,
          downtimeHours: 0,
          partsReplacedSummary: null,
          postMaintenanceRecalibrationRequired: false,
        });
        writePersistedGreenfieldMailboxState(state);
        return jsonResponse(updated);
      }
    }
    if (
      segments[2] === 'assets' &&
      segments[4] === 'maintenance' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const id = segments[3] ?? '';
      const body = await readBodyObj(request);
      const maintRecorder = strField(body, 'recordedByEmail').trim().toLowerCase();
      const maintVerifier = strField(body, 'verifiedByEmail').trim().toLowerCase();
      if (!maintRecorder || !maintVerifier) {
        return jsonResponse(
          {
            title: 'Ator não identificado',
            detail:
              'ACTOR_REQUIRED: informe recordedByEmail e verifiedByEmail; o fake não assume um responsável.',
            errorCode: 'ACTOR_REQUIRED',
          },
          422,
        );
      }
      if (maintRecorder === maintVerifier) {
        return jsonResponse(
          {
            title: 'Violação de Segregação de Funções (SoD)',
            detail:
              'AUTHZ_SOD_VIOLATION: Segregação de Funções exige que o verificador seja pessoa distinta de quem registrou a manutenção.',
            errorCode: 'AUTHZ_SOD_VIOLATION',
          },
          422,
        );
      }
      const assetIdx = state.facilityAssets.findIndex((a) => a.id === id || a.assetTag === id);
      const asset = assetIdx >= 0 ? state.facilityAssets[assetIdx] : state.facilityAssets[0];
      if (asset) {
        const isEmerg = strField(body, 'maintenanceKind', 'PREVENTIVE_MAINTENANCE')
          .toUpperCase()
          .includes('EMERG');
        const workOrder = strField(
          body,
          'workOrderCode',
          isEmerg
            ? `OS-EMERG-2026-${state.calibrationTimeline.length + 10}`
            : `OS-PREV-2026-${state.calibrationTimeline.length + 400}`,
        );
        const parts = strField(
          body,
          'partsReplacedSummary',
          'Inspeção preventiva completa e aferição eletromecânica.',
        );
        const downtime = numField(body, 'downtimeHours', 0);
        const recalReq = Boolean(body['postMaintenanceRecalibrationRequired']);
        const supName = strField(body, 'supplierName', 'Engenharia Clínica Credenciada GIYUS');
        const created: CalibrationTimelineEntryDto = {
          id: `tl-maint-${state.calibrationTimeline.length + 1}`,
          assetId: asset.id,
          assetTag: asset.assetTag,
          assetName: asset.name,
          eventDate: strField(body, 'maintenanceDate', '2026-10-26'),
          eventKind: isEmerg ? 'EMERGENCY_MAINTENANCE' : 'PREVENTIVE_MAINTENANCE',
          statusBadge: isEmerg ? 'EMERGENCY_RESOLVED' : 'PREVENTIVE_OK',
          title: isEmerg
            ? `Manutenção Emergencial / Corretiva Executada (${workOrder})`
            : `Manutenção Preventiva Programada Executada (${workOrder})`,
          description: `${isEmerg ? '[MANUTENÇÃO EMERGENCIAL]' : '[MANUTENÇÃO PREVENTIVA]'} ${parts} (Fornecedor: ${supName} · Tempo de parada: ${downtime}h · ${recalReq ? 'Com recalibração RBC pós-manutenção' : 'Sem impacto metrológico'}).`,
          supplierName: supName,
          rbcAccreditationCode: isEmerg
            ? 'Engenharia Clínica · SLA Emergencial'
            : 'Plano Preventivo NBR IEC 60601',
          certificateNumber: workOrder,
          equipmentInvoiceNumber: asset.equipmentInvoiceNumber ?? null,
          calibrationServiceInvoiceNumber: strField(
            body,
            'serviceInvoiceNumber',
            'NFS-e Manutenção 2026/002490',
          ),
          documentValueBrl: numField(body, 'costBrl', isEmerg ? 1680 : 750),
          recordedByEmail: maintRecorder,
          verifiedByEmail: maintVerifier,
          maintenanceCategory: isEmerg ? 'EMERGENCY_MAINTENANCE' : 'PREVENTIVE_MAINTENANCE',
          maintenanceWorkOrderCode: workOrder,
          downtimeHours: downtime,
          partsReplacedSummary: parts,
          postMaintenanceRecalibrationRequired: recalReq,
        };
        state.calibrationTimeline.unshift(created);
        if (assetIdx >= 0) {
          const nowIso = new Date().toISOString();
          const prevHash =
            asset.equipmentAuditTrail?.[0]?.sha256Hash ?? 'sha256:genesis00000000';
          const maintAudit: EquipmentAuditLogEntryDto = {
            id: `eq-aud-maint-${Date.now()}`,
            assetId: asset.id,
            timestampIso: nowIso,
            actorEmail: `${maintRecorder} (Verificado SoD: ${maintVerifier})`,
            actionType: 'MAINTENANCE',
            summary: `Manutenção (${isEmerg ? 'Emergencial' : 'Preventiva'}) com fornecedor ${supName} — OS ${workOrder}.`,
            beforeAfterDiff: `OS: ${workOrder} | Fornecedor: ${supName} | Peças/Serviço: ${parts} | Parada: ${downtime}h`,
            sha256Hash: computeEqAuditHash(`${prevHash}|MAINTENANCE|${workOrder}|${supName}`),
          };
          state.facilityAssets[assetIdx] = {
            ...asset,
            equipmentAuditTrail: [maintAudit, ...(asset.equipmentAuditTrail ?? [])],
          };
        }
        writePersistedGreenfieldMailboxState(state);
        return jsonResponse(created, 201);
      }
    }
    if (
      segments[2] === 'assets' &&
      segments[4] === 'disposition' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const id = segments[3] ?? '';
      const body = await readBodyObj(request);
      const idx = state.facilityAssets.findIndex((a) => a.id === id);
      const existing = state.facilityAssets[idx];
      if (idx >= 0 && existing) {
        const convertSiteUse =
          Boolean(body['convertDestructionToSiteUse']) &&
          (existing.resourceCategory ?? 'EQUIPMENT') === 'EQUIPMENT';
        const effectiveAction = convertSiteUse
          ? 'SPONSOR_DESTRUCTION_CONVERTED_TO_SITE_USE'
          : (strField(
              body,
              'dispositionAction',
              existing.endOfLifeDisposition,
            ) as EquipmentAssetItemDto['endOfLifeDisposition']);
        const updated: EquipmentAssetItemDto = {
          ...existing,
          endOfLifeDisposition: effectiveAction,
          dispositionStatus: convertSiteUse ? 'CONVERTED_TO_SITE_ASSET' : 'COMPLETED_ARCHIVED',
          dispositionEvidenceRef: strField(
            body,
            'evidenceDocumentRef',
            convertSiteUse
              ? 'TERMO-INCORPORACAO-INTERESSE-CENTRO-GIYUS-2026'
              : 'GUIA-REMESSA-CLOSEOUT-2026',
          ),
          blocksStudyCloseOut: false,
          disposalExecutionMode: convertSiteUse
            ? 'RETAINED_FOR_SITE_USE'
            : (strField(
                body,
                'disposalExecutionMode',
                existing.disposalExecutionMode ?? 'SHIPPED_VIA_CARRIER',
              ) as EquipmentAssetItemDto['disposalExecutionMode']),
          disposalCertificateRequirement: strField(
            body,
            'disposalCertificateRequirement',
            existing.disposalCertificateRequirement ?? 'WITH_DISPOSAL_CERTIFICATE',
          ) as EquipmentAssetItemDto['disposalCertificateRequirement'],
          siteInterestStatus: convertSiteUse
            ? 'CONVERTED_TO_GIYUS_SITE_USE'
            : (existing.siteInterestStatus ?? 'NOT_APPLICABLE'),
          ownershipKind: convertSiteUse ? 'GIYUS_TENANT' : existing.ownershipKind,
          ownerEntityName: convertSiteUse
            ? 'GIYUS Curitiba Research Center (Incorporado de Ordem de Destruição do Patrocinador)'
            : existing.ownerEntityName,
        };
        state.facilityAssets[idx] = updated;
        writePersistedGreenfieldMailboxState(state);
        return jsonResponse(updated);
      }
    }
    if (segments[2] === 'calibration-suppliers' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.calibrationSuppliers);
    }
    if (segments[2] === 'calibration-suppliers' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const mags = Array.isArray(body['accreditedMagnitudes'])
        ? (body['accreditedMagnitudes'] as string[])
        : ['TEMPERATURA_CADEIA_FRIA', 'MASSA_VOLUME'];
      const created: NationalCalibrationSupplierDto = {
        id: `cal-sup-${String(state.calibrationSuppliers.length + 10).padStart(3, '0')}`,
        code: strField(body, 'code', `RBC-BR-${state.calibrationSuppliers.length + 10}`),
        tradeName: strField(body, 'tradeName'),
        legalName: strField(body, 'legalName'),
        cnpj: strField(body, 'cnpj'),
        rbcAccreditationCode: strField(body, 'rbcAccreditationCode'),
        headquartersCityUf: strField(body, 'headquartersCityUf'),
        coverageScope: strField(body, 'coverageScope', 'NACIONAL_BRASIL'),
        accreditedMagnitudes: mags,
        slaTurnaroundDays: numField(body, 'slaTurnaroundDays', 5),
        contactEmail: strField(body, 'contactEmail'),
        qualificationStatus: 'QUALIFIED_RBC',
        linkedAssetsCount: 0,
      };
      state.calibrationSuppliers.unshift(created);
      writePersistedGreenfieldMailboxState(state);
      return jsonResponse(created, 201);
    }
    if (segments[2] === 'closeout-locks' && segments.length === 3 && method === 'GET') {
      if (state.dossiers.length === 0 && state.facilityAssets.length === 0) {
        return jsonResponse([]);
      }
      const studyMeta = [
        {
          code: 'EPOCA',
          title: 'Protocolo EPOCA — Asma Grave Eosinofílica Fase III',
          sponsor: 'AstraZeneca / Labcorp Central Lab',
        },
        {
          code: 'SOLAIR',
          title: 'Protocolo SOLAIR — DPOC Moderada a Grave',
          sponsor: 'Novartis Biociências / Clario ERT',
        },
        {
          code: 'PERSIST',
          title: 'Protocolo PERSIST — Alergia Alimentar IgE-Mediada',
          sponsor: 'Sanofi / Eurofins Central Lab',
        },
        {
          code: 'ADEPT-4',
          title: 'Protocolo ADEPT-4 — Dermatite Atópica Pediátrica',
          sponsor: 'AbbVie / ICON Central Lab',
        },
        {
          code: 'ARGON',
          title: 'Protocolo ARGON — Urticária Crônica Espontânea',
          sponsor: 'Roche / Q2 Solutions',
        },
      ];
      const locks: StudyCloseOutLockDto[] = studyMeta.map((st) => {
        const linked = state.facilityAssets.filter((a) => a.linkedStudyCode === st.code);
        const blocking = linked.filter((a) => a.blocksStudyCloseOut);
        const isBlocked = blocking.length > 0;
        return {
          studyCode: st.code,
          studyTitle: st.title,
          sponsorOrLabName: st.sponsor,
          totalLoanedAssetsCount: linked.length,
          pendingReturnOrDispositionCount: blocking.length,
          isCloseOutBlocked: isBlocked,
          governanceRuleMessage: isBlocked
            ? `CLOSE-OUT BLOQUEADO (423): ${blocking.length} equipamento(s) em comodato de estudo/laboratório aguardam comprovante de devolução (logística reversa) ou termo de doação/descarte.`
            : linked.length > 0
              ? 'Close-Out Liberado: Todos os equipamentos em comodato possuem comprovação formal de destinação.'
              : 'Close-Out Liberado: Estudo opera exclusivamente com ativos próprios da rede GIYUS.',
          blockingAssetTags: blocking.map((b) => `${b.assetTag} (${b.name})`),
        };
      });
      return jsonResponse(locks);
    }
    if (segments[2] === 'stock-forecasts' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.stockForecasts);
    }
    if (
      segments[2] === 'stock-forecasts' &&
      segments[4] === 'replenish' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const id = segments[3] ?? '';
      const body = await readBodyObj(request);
      const idx = state.stockForecasts.findIndex((s) => s.id === id);
      const existing = state.stockForecasts[idx];
      if (idx >= 0 && existing) {
        const qty = Math.max(10, numField(body, 'quantityToOrder', 30));
        const reqEmail = strField(body, 'requestedByEmail');
        const nextUnits = existing.currentUnits + qty;
        const orderCode = `PED-REP-2026-${String(idx + 101).padStart(3, '0')}`;
        const updated: StockShortageForecastItemDto = {
          ...existing,
          currentUnits: nextUnits,
          projectedAvailableUnits: nextUnits - existing.reservedUnitsNext30d,
          estimatedDaysUntilStockout: Math.max(60, existing.estimatedDaysUntilStockout + 45),
          shortageReason: 'HEALTHY_STOCK',
          severity: 'OK',
          alertHeadline: `Reposição preventiva (${orderCode}) emitida (+${qty} un. com lote novo FEFO).`,
          recommendedAction: `Pedido ${orderCode} confirmado por ${reqEmail}. Estoque regularizado com antecedência.`,
          replenishmentStatus: 'REPLENISHMENT_ORDERED',
          lastReplenishmentOrderCode: orderCode,
        };
        state.stockForecasts[idx] = updated;
        return jsonResponse(updated);
      }
    }
    if (segments[2] === 'calibration-timeline' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.calibrationTimeline);
    }
    if (
      segments[2] === 'assets' &&
      segments[4] === 'documents' &&
      segments.length === 6 &&
      method === 'GET'
    ) {
      const assetId = segments[3] ?? 'ALL';
      const documentKind = (segments[5] ?? 'CERTIFICADO_RBC').toUpperCase();
      if (assetId === 'ALL' || documentKind.includes('PACOTE_COMPLETO')) {
        const lines = [
          '================================================================================',
          'GIYUS HOLDING & RESEARCH CENTERS — DOSSIÊ CONSOLIDADO DE EQUIPAMENTOS',
          'TODOS OS CERTIFICADOS DE CALIBRAÇÃO RBC/INMETRO & NOTAS FISCAIS (NF-e / NFS-e)',
          '================================================================================',
          ...state.facilityAssets.map(
            (a) =>
              `• [${a.assetTag}] ${a.name} | S/N: ${a.serialNumber}\n  - NF-e Equipamento: ${a.equipmentInvoiceNumber ?? 'NF-e'} (Chave: ${a.equipmentInvoiceAccessKey ?? 'N/A'})\n  - Certificado RBC: ${a.lastCertificateNumber ?? 'Isento RBC'} | Fornecedor: ${a.assignedCalibrationSupplierName ?? 'Interno'}\n  - NFS-e Calibração: ${a.calibrationServiceInvoiceNumber ?? 'N/A'} | Vencimento: ${a.nextCalibrationDueDate ?? 'N/A'}`,
          ),
          '================================================================================',
        ].join('\n');
        const bundleDto: EquipmentDocumentDownloadDto = {
          documentId: 'doc-bundle-all-assets',
          documentKind: 'PACOTE_COMPLETO_CERTIFICADOS_E_NOTAS_FISCAIS',
          assetTag: 'TODOS-EQUIPAMENTOS',
          fileName: 'GIYUS_Pacote_Completo_Certificados_RBC_e_Notas_Fiscais_Equipamentos.pdf',
          mimeType: 'application/pdf',
          sha256Hash: 'a89f4e21b704c9812398e4120c994a88f12c940129384a102938471029384710',
          generatedAtIso: '2026-10-26T22:50:00Z',
          contentBase64: btoa(unescape(encodeURIComponent(`%PDF-1.4\n${lines}\n%%EOF`))),
          previewText: lines,
        };
        return jsonResponse(bundleDto);
      }
      const asset =
        state.facilityAssets.find((a) => a.id === assetId || a.assetTag === assetId) ??
        state.facilityAssets[0];
      const tag = asset?.assetTag ?? 'PAT-CWB-0001';
      const isNfe = documentKind.includes('NFE') || documentKind.includes('EQUIPAMENTO');
      const isNfse = documentKind.includes('NFSE') || documentKind.includes('SERVICO');
      const isMaint = documentKind.includes('MANUTENCAO') || documentKind.includes('LAUDO');
      const preview = isNfe
        ? [
            '================================================================================',
            'DANFE — DOCUMENTO AUXILIAR DA NOTA FISCAL ELETRÔNICA DE EQUIPAMENTO (NF-e)',
            '================================================================================',
            `Nº NF-e: ${asset?.equipmentInvoiceNumber ?? 'NF-e 000.004.812'} | Emissão: ${asset?.equipmentInvoiceIssuedDate ?? '2025-04-10'}`,
            `Chave de Acesso (44 dígitos): ${asset?.equipmentInvoiceAccessKey ?? '41241069584441000184550010000048121004812095'}`,
            `Destinatário / Custodiante: ${asset?.ownerEntityName ?? 'GIYUS Curitiba Research Center'}`,
            `Patrimônio / Tag: ${tag} — ${asset?.name ?? 'Equipamento Clínico'} (S/N: ${asset?.serialNumber ?? 'SN-001'})`,
            `Valor Total da Nota Fiscal: R$ ${(asset?.isDepreciableInGiyusBalance ? (asset?.acquisitionValueBrl ?? 0) : (asset?.insuredCustodyValueBrl ?? 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
            '================================================================================',
          ].join('\n')
        : isNfse
          ? [
              '================================================================================',
              'NFS-e — NOTA FISCAL DE SERVIÇOS ELETRÔNICA (METROLOGIA & CALIBRAÇÃO RBC)',
              '================================================================================',
              `Nº NFS-e: ${asset?.calibrationServiceInvoiceNumber ?? 'NFS-e 2026/001482'} | Data: ${asset?.lastCalibratedDate ?? '2026-10-26'}`,
              `Prestador Acreditado RBC: ${asset?.assignedCalibrationSupplierName ?? 'Trescal Brasil Metrologia RBC'}`,
              `Equipamento Calibrado: ${tag} — ${asset?.name ?? 'Equipamento'} | Certificado: ${asset?.lastCertificateNumber ?? 'CERT-RBC'}`,
              `Valor Líquido do Serviço de Calibração: R$ ${(asset?.calibrationFeeBrl ?? 1180).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
              '================================================================================',
            ].join('\n')
          : isMaint
            ? [
                '================================================================================',
                'LAUDO / CERTIFICADO TÉCNICO DE MANUTENÇÃO (PREVENTIVA / EMERGENCIAL)',
                '================================================================================',
                `Equipamento: ${asset?.name ?? 'Equipamento'} | Tag: ${tag} | Série: ${asset?.serialNumber ?? 'SN-001'}`,
                'Procedimento Executado: Inspeção eletromecânica completa, limpeza técnica, substituição preventiva/corretiva e teste de segurança elétrica NBR IEC 60601.',
                'Status Pós-Manutenção: APROVADO E LIBERADO PARA OPERAÇÃO CLÍNICA.',
                '================================================================================',
              ].join('\n')
            : [
                '================================================================================',
                'CERTIFICADO DE CALIBRAÇÃO ACREDITADO RBC / CGCRE INMETRO (ABNT NBR ISO/IEC 17025)',
                '================================================================================',
                `Certificado Nº: ${asset?.lastCertificateNumber ?? 'CERT-RBC-CAL0312-2026-771'}`,
                `Laboratório Acreditado Emissor: ${asset?.assignedCalibrationSupplierName ?? 'CaliLab Metrologia Científica & Cadeia Fria'}`,
                `Equipamento: ${asset?.name ?? 'Equipamento'} | Tag: ${tag} | Série: ${asset?.serialNumber ?? 'SN-001'}`,
                `Grandeza Calibrada: ${asset?.requiredMagnitude ?? 'TEMPERATURA_CADEIA_FRIA'}`,
                `Data da Calibração: ${asset?.lastCalibratedDate ?? '2026-07-15'} | Próximo Vencimento: ${asset?.nextCalibrationDueDate ?? '2027-01-15'}`,
                `Aviso Antecipado de 1 Mês (T-30d): ${asset?.oneMonthWarningTriggerDate ?? '2026-12-15'}`,
                `Segregação SoD 2 Pessoas: Registrado por ${asset?.lastRecordedByEmail ?? 'engenharia@giyus.com.br'} | Verificado por ${asset?.lastVerifiedByEmail ?? 'syllas@giyus.com.br'}`,
                'Resultado: APROVADO SEM RESTRIÇÕES — Incerteza expandida (k=2,00) dentro do limite do protocolo clínico.',
                '================================================================================',
              ].join('\n');
      const docDto: EquipmentDocumentDownloadDto = {
        documentId: `doc-${tag}-${documentKind}`,
        documentKind: isNfe
          ? 'NOTA_FISCAL_EQUIPAMENTO_NFE'
          : isNfse
            ? 'NOTA_FISCAL_CALIBRACAO_NFSE'
            : isMaint
              ? 'LAUDO_MANUTENCAO_EQUIPAMENTO'
              : 'CERTIFICADO_RBC',
        assetTag: tag,
        fileName: isNfe
          ? `NFe_Equipamento_${tag}.pdf`
          : isNfse
            ? `NFSe_Servico_Calibracao_${tag}.pdf`
            : isMaint
              ? `Laudo_Manutencao_${tag}.pdf`
              : `Certificado_Calibracao_RBC_${tag}.pdf`,
        mimeType: 'application/pdf',
        sha256Hash: 'e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855',
        generatedAtIso: '2026-10-26T22:50:00Z',
        contentBase64: btoa(unescape(encodeURIComponent(`%PDF-1.4\n${preview}\n%%EOF`))),
        previewText: preview,
      };
      return jsonResponse(docDto);
    }
    if (segments[2] === 'mailbox-resource-monitor' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.resourceMailboxEvents);
    }
    if (
      segments[2] === 'mailbox-resource-monitor' &&
      segments[4] === 'reconcile' &&
      segments.length === 5 &&
      method === 'POST'
    ) {
      const id = segments[3] ?? '';
      const body = await readBodyObj(request);
      const idx = state.resourceMailboxEvents.findIndex((m) => m.id === id);
      const existing = state.resourceMailboxEvents[idx];
      if (idx >= 0 && existing) {
        const matchedCode = strField(
          body,
          'matchedGiyusAssetOrSkuCode',
          existing.matchedGiyusAssetOrSkuCode,
        );
        const matchedName = strField(
          body,
          'matchedGiyusResourceName',
          existing.matchedGiyusResourceName,
        );
        const fallback = strField(
          body,
          'recommendedResourceIfMissing',
          existing.recommendedResourceIfMissing,
        );
        const reconciledBy = strField(body, 'reconciledByEmail');
        const updated: ResourceMailboxMovementItemDto = {
          ...existing,
          matchedGiyusAssetOrSkuCode: matchedCode,
          matchedGiyusResourceName: matchedName,
          recommendedResourceIfMissing: fallback,
          nomenclatureSimilarityPercent: 100,
          reconciliationStatus: 'RECONCILED_LINKED',
          reconciledByEmail: reconciledBy,
          whatIsHappeningSummary: `Conciliado por ${reconciledBy}: Recurso do contrato/e-mail '${existing.contractOrEmailResourceName}' vinculado oficialmente a '${matchedName}' (${matchedCode}) na base GIYUS.`,
        };
        state.resourceMailboxEvents[idx] = updated;
        return jsonResponse(updated);
      }
    }
    if (segments[2] === 'inventory-multi-supplier' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.inventoryMultiSupplierItems);
    }
    if (segments[2] === 'consumable-price-timeline' && segments.length === 3 && method === 'GET') {
      return jsonResponse(state.consumablePriceTimelineEntries);
    }
    if (segments[2] === 'consumable-price-timeline' && segments.length === 3 && method === 'POST') {
      const body = await readBodyObj(request);
      const skuCode = strField(body, 'skuCode', 'SKU-CRIO-2ML');
      const itemName = strField(body, 'itemName', 'Criotubos Estéreis 2mL RNase/DNase Free');
      const localityCityUf = strField(body, 'localityCityUf');
      const centerName = strField(body, 'centerName', 'GIYUS Curitiba Research Center (Matriz)');
      const supplierCode = strField(body, 'supplierCode', 'SUP-KIT-01');
      const supplierName = strField(
        body,
        'supplierName',
        'Thermo Fisher / Fisher Clinical Services',
      );
      const geographicScope = strField(
        body,
        'geographicScope',
        'NACIONAL_BRASIL',
      ) as ConsumablePriceLocalityTimelineEntryDto['geographicScope'];
      const unitPriceBrl = numField(body, 'unitPriceBrl', 5.5);
      const freightPerUnitBrl = numField(body, 'freightPerUnitBrl', 0.25);
      const landedUnitCostBrl = Number((unitPriceBrl + freightPerUnitBrl).toFixed(2));
      const contractCeilingUnitBrl = numField(body, 'contractCeilingUnitBrl', 7.2);
      const prevEntry = state.consumablePriceTimelineEntries.find(
        (e) =>
          e.skuCode.toLowerCase() === skuCode.toLowerCase() &&
          e.localityCityUf.toLowerCase() === localityCityUf.toLowerCase(),
      );
      const prevLanded = prevEntry ? prevEntry.landedUnitCostBrl : null;
      const deltaPercent =
        prevLanded && prevLanded > 0
          ? Number((((landedUnitCostBrl - prevLanded) / prevLanded) * 100).toFixed(2))
          : 0;
      const created: ConsumablePriceLocalityTimelineEntryDto = {
        id: `CPT-NEW-${String(state.consumablePriceTimelineEntries.length + 10)}`,
        eventDate: '2026-10-27',
        skuCode,
        itemName,
        localityCityUf,
        centerName,
        supplierCode,
        supplierName,
        geographicScope,
        unitPriceBrl,
        freightPerUnitBrl,
        landedUnitCostBrl,
        previousLandedUnitCostBrl: prevLanded,
        deltaPercentVsPrevious: deltaPercent,
        contractCeilingUnitBrl,
        savingsPerUnitVsCeilingBrl: Number((contractCeilingUnitBrl - landedUnitCostBrl).toFixed(2)),
        choiceReasonOrTrigger: strField(
          body,
          'choiceReasonOrTrigger',
          `Cotação registrada para ${localityCityUf} por melhor relação Preço × Prazo.`,
        ),
        invoiceOrQuoteRef: strField(body, 'invoiceOrQuoteRef', 'COT-LOC-2026-10'),
        recordedByEmail: strField(body, 'recordedByEmail'),
        costAllocationKind:
          strField(body, 'costAllocationKind', 'ATRELADO_A_CONTRATO') ===
          'CUSTO_OPERACIONAL_UNIDADE'
            ? 'CUSTO_OPERACIONAL_UNIDADE'
            : 'ATRELADO_A_CONTRATO',
        costAllocationTargetLabel: strField(
          body,
          'costAllocationTargetLabel',
          strField(body, 'costAllocationKind', 'ATRELADO_A_CONTRATO') ===
            'CUSTO_OPERACIONAL_UNIDADE'
            ? `Custo Próprio de Operação — ${centerName} (Sem Contrato)`
            : 'Atrelado a Contrato de Estudo Clínico (Pass-Through / SoA)',
        ),
      };
      state.consumablePriceTimelineEntries.unshift(created);
      return jsonResponse(created, 201);
    }
    if (segments[2] === 'procurement-engine' && segments.length === 3 && method === 'GET') {
      return jsonResponse(buildAutonomousProcurementSnapshot(state));
    }
    if (
      segments[2] === 'procurement-engine' &&
      segments[3] === 'policy' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const nextPhase =
        strField(body, 'governancePhase', 'FASE_1_COM_APROVACAO_HUMANA') ===
        'FASE_2_SEM_APROVACAO_HUMANA_ESSENCIAIS'
          ? 'FASE_2_SEM_APROVACAO_HUMANA_ESSENCIAIS'
          : 'FASE_1_COM_APROVACAO_HUMANA';
      const autoExp = body['autoApproveExpiringMedications'] !== false;
      const autoStudy = body['autoApproveStudyRequiredItems'] !== false;
      const autoEss = body['autoApproveEssentialSiteProducts'] !== false;
      const ceiling = Math.max(500, numField(body, 'autoApprovalCeilingPerOrderBrl', 15000));
      const updatedBy = strField(body, 'updatedByEmail');
      state.procurementPolicy = {
        governancePhase: nextPhase,
        autoApproveExpiringMedications: autoExp,
        autoApproveStudyRequiredItems: autoStudy,
        autoApproveEssentialSiteProducts: autoEss,
        autoApprovalCeilingPerOrderBrl: ceiling,
        requireQualifiedSupplierAndWithinCeiling: true,
        nonEssentialAlwaysRequiresHuman: true,
        lastUpdatedByEmail: updatedBy,
        lastUpdatedAtIso: '2026-10-27T06:05:00Z',
      };
      if (nextPhase === 'FASE_2_SEM_APROVACAO_HUMANA_ESSENCIAIS') {
        runAutonomousProcurementCycleOnState(state);
      }
      return jsonResponse(buildAutonomousProcurementSnapshot(state));
    }
    if (
      segments[2] === 'procurement-engine' &&
      segments[3] === 'orders' &&
      segments[5] === 'approve-human' &&
      segments.length === 6 &&
      method === 'POST'
    ) {
      const id = segments[4] ?? '';
      const body = await readBodyObj(request);
      const approvedByName = strField(body, 'approvedByName');
      const approvedByEmail = strField(body, 'approvedByEmail');
      const approvalNotes = strField(
        body,
        'approvalNotes',
        'Aprovado manualmente pelo gestor humano após validação de preço por localidade e teto orçamentário.',
      );
      const idx = state.integratedProcurementOrders.findIndex(
        (o) =>
          o.id.toLowerCase() === id.toLowerCase() || o.orderCode.toLowerCase() === id.toLowerCase(),
      );
      const existing = state.integratedProcurementOrders[idx];
      if (idx >= 0 && existing) {
        const updated: IntegratedProcurementOrderDto = {
          ...existing,
          approvalStatus: 'APPROVED_BY_HUMAN_ORDERED',
          approvedOrTriggeredBy: `Aprovado por Humano: ${approvedByName} (${approvedByEmail})`,
          approvedAtIso: '2026-10-27T06:10:00Z',
          erpIntegrationTrail: `${existing.erpIntegrationTrail} → Pedido Emitido com Aprovação Humana (${approvalNotes})`,
        };
        state.integratedProcurementOrders[idx] = updated;
        return jsonResponse(updated);
      }
    }
    if (
      segments[2] === 'procurement-engine' &&
      segments[3] === 'run-autonomous-cycle' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      runAutonomousProcurementCycleOnState(state);
      return jsonResponse(buildAutonomousProcurementSnapshot(state));
    }
    if (
      segments[2] === 'procurement-engine' &&
      segments[3] === 'orders' &&
      segments.length === 4 &&
      method === 'POST'
    ) {
      const body = await readBodyObj(request);
      const triggerKindRaw = strField(body, 'essentialityTriggerKind', 'NECESSARIO_PARA_UM_ESTUDO');
      const triggerKind: IntegratedProcurementOrderDto['essentialityTriggerKind'] =
        triggerKindRaw === 'PRODUTO_QUE_VENHA_A_VENCER' ||
        triggerKindRaw === 'NECESSARIO_PARA_UM_ESTUDO' ||
        triggerKindRaw === 'PRODUTO_ESSENCIAL_NECESSARIO' ||
        triggerKindRaw === 'NAO_ESSENCIAL_ACIMA_TETO'
          ? triggerKindRaw
          : 'NECESSARIO_PARA_UM_ESTUDO';
      const qty = Math.max(1, numField(body, 'quantityOrdered', 20));
      const unitPrice = Math.max(0.01, numField(body, 'unitLandedPriceBrl', 35));
      const ceilingUnit = Math.max(
        unitPrice,
        numField(body, 'contractOrBudgetCeilingUnitBrl', unitPrice * 1.15),
      );
      const totalOrder = Number((qty * unitPrice).toFixed(2));
      const savings = Number(((ceilingUnit - unitPrice) * qty).toFixed(2));
      const isEssential =
        (triggerKind === 'PRODUTO_QUE_VENHA_A_VENCER' ||
          triggerKind === 'NECESSARIO_PARA_UM_ESTUDO' ||
          triggerKind === 'PRODUTO_ESSENCIAL_NECESSARIO') &&
        totalOrder <= state.procurementPolicy.autoApprovalCeilingPerOrderBrl &&
        unitPrice <= ceilingUnit;
      const autoApproveNow =
        state.procurementPolicy.governancePhase === 'FASE_2_SEM_APROVACAO_HUMANA_ESSENCIAIS' &&
        isEssential;
      const costKind: IntegratedProcurementOrderDto['costAllocationKind'] =
        strField(body, 'costAllocationKind', 'ATRELADO_A_CONTRATO') === 'CUSTO_OPERACIONAL_UNIDADE'
          ? 'CUSTO_OPERACIONAL_UNIDADE'
          : 'ATRELADO_A_CONTRATO';
      const linkedStudy = optStrField(body, 'linkedStudyCode');
      const centerName = strField(body, 'centerName', 'GIYUS Curitiba Research Center (Matriz)');
      const supplierScopeRaw = strField(body, 'supplierScope', 'LOCAL_CIDADE_UF');
      const supplierScope: IntegratedProcurementOrderDto['supplierScope'] =
        supplierScopeRaw === 'REGIONAL_SUL_SUDESTE' || supplierScopeRaw === 'NACIONAL_BRASIL'
          ? supplierScopeRaw
          : 'LOCAL_CIDADE_UF';
      const nextNum = state.integratedProcurementOrders.length + 1;
      const created: IntegratedProcurementOrderDto = {
        id: `IPO-${String(nextNum).padStart(3, '0')}`,
        orderCode: `PED-INT-2026-${String(nextNum).padStart(3, '0')}`,
        createdAtIso: '2026-10-27T06:15:00Z',
        skuCode: strField(body, 'skuCode', 'SKU-NOVO-INSUMO'),
        itemName: strField(body, 'itemName', 'Insumo Clínico Integrado'),
        categoryCode: strField(body, 'categoryCode', 'LAB_KITS_CONSUMABLES'),
        essentialityTriggerKind: triggerKind,
        triggerHeadline: strField(
          body,
          'triggerHeadline',
          'Pedido gerado no Módulo de Compras Integrado.',
        ),
        originModuleIntegration: strField(
          body,
          'originModuleIntegration',
          'Estoque Clínico FEFO + Estudos SoA + Fornecedores + Financeiro',
        ),
        linkedStudyCode: linkedStudy,
        costAllocationKind: costKind,
        costAllocationTargetLabel:
          costKind === 'CUSTO_OPERACIONAL_UNIDADE'
            ? `Custo Próprio de Operação — ${centerName} (OPEX · Sem Contrato)`
            : `Atrelado ao Contrato do Estudo ${linkedStudy ?? 'EPOCA'} (Pass-Through / SoA)`,
        localityCityUf: strField(body, 'localityCityUf'),
        centerName,
        selectedSupplierCode: strField(body, 'selectedSupplierCode', 'SUP-MEDHOSP-CWB-01'),
        selectedSupplierName: strField(
          body,
          'selectedSupplierName',
          'MedHosp Distribuidora Hospitalar Curitiba',
        ),
        supplierScope,
        paymentTermsApplied: strField(body, 'paymentTermsApplied', '28 DDL Boleto Faturado'),
        quantityOrdered: qty,
        unitLandedPriceBrl: unitPrice,
        contractOrBudgetCeilingUnitBrl: ceilingUnit,
        totalOrderBrl: totalOrder,
        savingsVsCeilingBrl: savings,
        daysUntilExpirationOrStockout:
          typeof body['daysUntilExpirationOrStockout'] === 'number'
            ? body['daysUntilExpirationOrStockout']
            : 15,
        isMandatoryMedication: Boolean(body['isMandatoryMedication']),
        approvalMode: isEssential ? 'AUTONOMOUS_ZERO_TOUCH_ELIGIBLE' : 'REQUIRES_HUMAN_APPROVAL',
        approvalStatus: autoApproveNow ? 'AUTO_ORDERED_WITHOUT_HUMAN' : 'PENDING_HUMAN_APPROVAL',
        approvedOrTriggeredBy: autoApproveNow
          ? 'Emitido Automaticamente Sem Aprovação Humana (Fase 2 · Compra Essencial dentro do Teto)'
          : `Solicitado por ${strField(body, 'requestedByEmail') || 'solicitante não informado'} (Aguardando Aprovação Humana)`,
        approvedAtIso: autoApproveNow ? '2026-10-27T06:15:00Z' : null,
        erpIntegrationTrail: autoApproveNow
          ? 'Gatilho Essencial → Auto-Aprovado Zero-Touch (Fase 2) → PO Emitida ao Fornecedor + Lançamento 3-Way Match'
          : 'Gatilho Integrado → Aguardando Aprovação Humana Inicial (Fase 1)',
      };
      state.integratedProcurementOrders.unshift(created);
      return jsonResponse(created, 201);
    }
  }

  // =========================================================================
  // REPORTING & AUTONOMOUS PROCESSING LAYER (/api/v1/network/reporting/*)
  // =========================================================================
  if (segments[1] === 'reporting') {
    if (
      (segments[2] === 'autonomous-stats' && segments.length === 3 && method === 'GET') ||
      (segments[2] === 'autonomous-run' && segments.length === 3 && method === 'POST')
    ) {
      if (method === 'POST') {
        state.autonomousRunsCount += 1;
        state.lastAutonomousRunAtIso = '2026-10-26T16:30:00Z';
      }
      const giyusOwned = state.facilityAssets.filter((a) => a.isDepreciableInGiyusBalance);
      const loanAssets = state.facilityAssets.filter((a) => !a.isDepreciableInGiyusBalance);
      const calibratable = state.facilityAssets.filter((a) => a.requiresCalibration);
      const expiredCal = calibratable.filter((a) => a.blockedForStudyUse);
      const weightedHourly =
        giyusOwned.length > 0
          ? Number(
              (
                giyusOwned.reduce((acc, a) => acc + a.hourlyBudgetCostBrl, 0) / giyusOwned.length
              ).toFixed(2),
            )
          : 0;
      const isGreenfieldEmpty =
        state.dossiers.length === 0 &&
        state.facilityAssets.length === 0 &&
        state.suppliers.length === 0;
      const calCompliance =
        calibratable.length > 0
          ? Number(
              (((calibratable.length - expiredCal.length) * 100) / calibratable.length).toFixed(1),
            )
          : isGreenfieldEmpty
            ? 0
            : 100;
      const totalCustody = loanAssets.reduce((acc, a) => acc + a.insuredCustodyValueBrl, 0);
      const weightedMargin =
        state.dossiers.length > 0
          ? Number(
              (
                state.dossiers.reduce((acc, d) => acc + d.estimatedMarginPercent, 0) /
                state.dossiers.length
              ).toFixed(1),
            )
          : isGreenfieldEmpty
            ? 0
            : 26.8;
      const autoMessages = state.messages.filter((m) => m.classificationStatus !== 'Unassigned');
      const autoClassRate =
        state.messages.length > 0
          ? Number(((autoMessages.length * 100) / state.messages.length).toFixed(1))
          : isGreenfieldEmpty
            ? 0
            : 100;
      const meanDaysToReceive = isGreenfieldEmpty ? 0 : 26;
      const slaComplianceRate = isGreenfieldEmpty ? 0 : 96.4;

      const snapshot: PlatformAutonomousSnapshotDto = {
        snapshotRunsCount: state.autonomousRunsCount,
        lastSnapshotGeneratedAtIso: state.lastAutonomousRunAtIso,
        autonomousFunctions: [
          {
            functionName: 'FacilitiesDepreciationAndCalibrationFunction',
            cronScheduleUtc: '0 0 4 * * * (Diário 04:00 UTC)',
            targetModule: 'Facilities',
            responsibilitySummary:
              'Avança depreciação linear mensal, recalcula VCL/custo-hora, avalia vencimento de certificados RBC e aplica trava 423 de uso clínico e Close-Out.',
            runsCount: state.autonomousRunsCount,
            lastRunAtIso: state.lastAutonomousRunAtIso,
            status: 'HEALTHY_ACTIVE',
          },
          {
            functionName: 'StudiesMailboxAndPriceDriftFunction',
            cronScheduleUtc: '0 */15 * * * * (A cada 15 min)',
            targetModule: 'Studies',
            responsibilitySummary:
              'Classifica e-mails das caixas compartilhadas @giyus.com.br, detecta deriva da tabela de preços por cidade da Holding e consolida pipeline e PTAX.',
            runsCount: state.autonomousRunsCount,
            lastRunAtIso: state.lastAutonomousRunAtIso,
            status: 'HEALTHY_ACTIVE',
          },
          {
            functionName: 'SuppliersQualificationAndSlaFunction',
            cronScheduleUtc: '0 30 4 * * * (Diário 04:30 UTC)',
            targetModule: 'Suppliers',
            responsibilitySummary:
              "Audita qualificações ANVISA/ISO 17025, cobertura geodésica 'Quem me atende' e SLA de ordens de serviço e compras.",
            runsCount: state.autonomousRunsCount,
            lastRunAtIso: state.lastAutonomousRunAtIso,
            status: 'HEALTHY_ACTIVE',
          },
          {
            functionName: 'PlatformAutonomousStatsSnapshotFunction',
            cronScheduleUtc: '0 0 5 * * * (Diário 05:00 UTC)',
            targetModule: 'Reporting',
            responsibilitySummary:
              'Agrega as ReadApis em N camadas e publica as variáveis operacionais e financeiras globais da plataforma GIYUS.',
            runsCount: state.autonomousRunsCount,
            lastRunAtIso: state.lastAutonomousRunAtIso,
            status: 'HEALTHY_ACTIVE',
          },
        ],
        operationalVariables: [
          {
            variableKey: 'facilities.depreciation.weightedHourlyCostBrl',
            moduleOrigin: 'FACILITIES',
            displayName: 'Custo/Hora Médio Depreciado de Equipamentos GIYUS',
            numericValue: weightedHourly,
            formattedValue: `R$ ${weightedHourly.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}/h`,
            unitCode: 'BRL_PER_HOUR',
            formulaDescription: '((Aquisição - Residual) / VidaÚtilMeses) / 160h clínicas',
          },
          {
            variableKey: 'facilities.calibration.rbcComplianceRatePercent',
            moduleOrigin: 'FACILITIES',
            displayName: 'Taxa de Conformidade de Calibração RBC / INMETRO',
            numericValue: calCompliance,
            formattedValue: `${calCompliance.toLocaleString('pt-BR', { minimumFractionDigits: 1 })}%`,
            unitCode: 'PERCENT',
            formulaDescription:
              'Ativos calibráveis vigentes / Total de ativos que exigem calibração RBC',
          },
          {
            variableKey: 'facilities.custody.insuredLoanValueBrl',
            moduleOrigin: 'FACILITIES',
            displayName: 'Valor Segurado sob Custódia (Comodato Estudos/Labs)',
            numericValue: totalCustody,
            formattedValue: `R$ ${totalCustody.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
            unitCode: 'BRL',
            formulaDescription:
              'Soma de InsuredCustodyValueBrl de ativos SPONSOR_STUDY_LOAN e CENTRAL_LAB_LOAN',
          },
          {
            variableKey: 'studies.pipeline.weightedNetMarginRegularPercent',
            moduleOrigin: 'STUDIES',
            displayName: 'Margem Líquida Ponderada (Cenário Regular)',
            numericValue: weightedMargin,
            formattedValue: `${weightedMargin.toLocaleString('pt-BR', { minimumFractionDigits: 1 })}%`,
            unitCode: 'PERCENT',
            formulaDescription:
              'Média ponderada da margem líquida estimada nos dossiês 360º ativos',
          },
          {
            variableKey: 'studies.financial.meanDaysToReceivePayment',
            moduleOrigin: 'STUDIES',
            displayName: 'Prazo Médio de Recebimento Financeiro (PTAX Congelada)',
            numericValue: meanDaysToReceive,
            formattedValue: `${meanDaysToReceive.toLocaleString('pt-BR', { minimumFractionDigits: 1 })} dias`,
            unitCode: 'DAYS',
            formulaDescription:
              'Média de dias entre emissão da NF/Invoice e liquidação com PTAX BCB congelada',
          },
          {
            variableKey: 'studies.mailbox.autoClassificationRatePercent',
            moduleOrigin: 'STUDIES',
            displayName: 'Taxa de Auto-Classificação & Aprendizado (@giyus.com.br)',
            numericValue: autoClassRate,
            formattedValue: `${autoClassRate.toLocaleString('pt-BR', { minimumFractionDigits: 1 })}%`,
            unitCode: 'PERCENT',
            formulaDescription:
              'E-mails encaixados em projetos / Total de e-mails sincronizados via Microsoft Graph',
          },
          {
            variableKey: 'suppliers.operations.slaComplianceRatePercent',
            moduleOrigin: 'SUPPLIERS',
            displayName: 'Índice de Cumprimento de SLA da Rede de Fornecedores',
            numericValue: slaComplianceRate,
            formattedValue: `${slaComplianceRate.toLocaleString('pt-BR', { minimumFractionDigits: 1 })}%`,
            unitCode: 'PERCENT',
            formulaDescription:
              'Ordens de serviço e calibrações entregues no prazo / Total de OS executadas',
          },
        ],
      };

      return jsonResponse(snapshot);
    }
  }

  return null;
}

function buildFakeCouncilRun(
  code: string,
  baseTotal: number,
  isGreenfieldEmpty = false,
): MultiLlmCouncilRunDto {
  if (code.toUpperCase() === 'EPOCA') {
    return {
      runId: 'council-epoca-trialshub-001',
      studyCode: code,
      executedAtIso: '2026-10-28T22:00:00Z',
      consensusDecision:
        'MINUTA_EM_ANALISE_INICIAL (Fidelidade Literal a MINUTA_EPOCA_2026_TrialsHub.docx · R$ 0,00 Recebido · Repactuação Anual Crítica em Contrato Longo 48m · Preencher Qualificação do Centro de Pesquisa TrialsHub em Controle de Alterações)',
      confidencePercent: 100,
      opinions: [
        {
          provider: 'OpenAI',
          modelName: 'GPT-5 / o3-pro',
          verdict:
            'CLÁUSULA 4.5 (RECRUTAMENTO COMPETITIVO), CLÁUSULA 4.7 (2ª ETAPA CONDICIONADA) & REGRA 03 (REPACTUAÇÃO ANUAL CRÍTICA EM CONTRATO LONGO 48 MESES)',
          recommendedTotalBrl: baseTotal,
          keyJustification:
            '[Obediência Obrigatória Regras 01, 02 e 03]: O Anexo I define valores estritamente unitários: Taxa Única de Startup de R$ 3.000,00 (R$ 2.400 + 25% overhead) + 1ª Etapa R$ 3.000,00/part. (Pagamentos 1 a 7, D0 a D360) + 2ª Etapa R$ 2.900,00/part. (Pagamentos 8 a 21, D450 a D1440) = R$ 5.900,00/participante. Por ser um contrato longo (> 12 meses — 48 meses de visitas e vigência até 30/06/2032), a omissão/vedação de repactuação e reequilíbrio financeiro durante os anos é CRÍTICA (diferente de contratos curtos <= 12m onde seria aceitável), exigindo Redline na Cláusula 6ª.',
        },
        {
          provider: 'Anthropic',
          modelName: 'Claude 4 Opus / Sonnet',
          verdict:
            'PREENCHER CAMPOS EM BRANCO (CNPJ XX.XXX.XXX/XXXX-XX, PI E TABELA 3), CORRIGIR ERRO MATERIAL PAGAMENTO 13 & REGRA 07 (ORIENTAÇÃO BIDIRECIONAL)',
          recommendedTotalBrl: baseTotal,
          keyJustification:
            '[Obediência Obrigatória Regras 01, 05 e 07]: No Preâmbulo e na Tabela 3 do Anexo I de MINUTA_EPOCA_2026_TrialsHub.docx, os campos da Instituição, Interveniente Administrativa, Investigador Principal e Dados Bancários estão em branco (RAZÃO SOCIAL DA EMPRESA, CNPJ XX.XXX.XXX/XXXX-XX, CPF XXX.XXX.XXX-XX, Banco/Agência/Conta XXXX) para preenchimento pelo Centro de Pesquisa TrialsHub em controle de alterações. No Pagamento 13 (Visita 7), corrigir a grafia "DIA 720" para "DIA 900". Patrocinador (Hcor) e CRO/Centro detectados em cadastros separados.',
        },
        {
          provider: 'Google DeepMind',
          modelName: 'Gemini 2.5 Pro',
          verdict:
            'PRAZO DE PAGAMENTO DE 30 DIAS APÓS NF (CLÁUSULA 6.5 · fiscal@hcor.com.br), GUARDA DE 5 ANOS & REGRA 04 (PROCEDIMENTOS REGIONAIS DE CURITIBA/PR DO CENTRO ATENDENTE)',
          recommendedTotalBrl: baseTotal,
          keyJustification:
            '[Obediência Obrigatória Regras 01, 04 e 06]: Nenhum valor foi faturado ou recebido (R$ 0,00). Conforme a Cláusula 6.5, o pagamento ocorre em 30 (trinta) dias após o recebimento da Nota Fiscal válida em fiscal@hcor.com.br, com prazo de 30 dias para contestar divergências (Cláusula 6.6), vigência até 30/06/2032 (Cláusula 7.1) e guarda documental por 05 (cinco) anos após a conclusão do estudo (Cláusulas 3.4 e 4.2.l). Embora a patrocinadora Hcor fique em São Paulo/SP, o centro de pesquisa que vai atender a pesquisa (Trials Hub Ltda) é de Curitiba/PR — logo os procedimentos do estudo são de Curitiba/PR (criados apenas em uso real).',
        },
      ],
    };
  }

  if (isGreenfieldEmpty) {
    return {
      runId: `council-${code.toLowerCase()}-empty`,
      studyCode: code,
      executedAtIso: new Date().toISOString(),
      consensusDecision:
        'AGUARDANDO_DOCUMENTO_LITERAL (Nenhum valor ou cláusula inventado — anexe a minuta/planilha orçamentária do estudo para análise literal)',
      confidencePercent: 100,
      opinions: [],
    };
  }

  const recommended = Math.round(baseTotal * 1.038);
  return {
    runId: `council-${code.toLowerCase()}-001`,
    studyCode: code,
    executedAtIso: '2026-10-26T12:00:00Z',
    consensusDecision: `PROCEED_WITH_COUNTEROFFER (Consenso 3/3 Modelos · Contraproposta R$ ${recommended.toLocaleString('pt-BR')})`,
    confidencePercent: 94,
    opinions: [
      {
        provider: 'OpenAI',
        modelName: 'GPT-5 / o3-pro',
        verdict: 'APROVAR COM AJUSTE DE PASS-THROUGH & CLÁUSULA EQ-BCB-01',
        recommendedTotalBrl: recommended,
        keyJustification:
          'Valida Gross-up de 14,25% (Lucro Real) e proteção cambial/inflacionária EQ-BCB-01 para prazos > 45 dias.',
      },
      {
        provider: 'Anthropic',
        modelName: 'Claude 4 Opus / Sonnet',
        verdict: 'EXIGIR COBERTURA DE SCREEN FAILURE 2:1 E START-UP NÃO-REEMBOLSÁVEL',
        recommendedTotalBrl: Math.round(baseTotal * 1.042),
        keyJustification:
          'Identificou densidade elevada de procedimentos na Visita V1/V2; recomenda blindar taxa de farmácia IWRS e arquivamento 25 anos.',
      },
      {
        provider: 'Google DeepMind',
        modelName: 'Gemini 2.5 Pro',
        verdict: 'ALINHAR TABELA DE MINUTOS CLÍNICOS POR CONSELHO (COREN/CRF/CRM)',
        recommendedTotalBrl: Math.round(baseTotal * 1.035),
        keyJustification:
          'Cruza a grade SoA com o Catálogo de Procedimentos da Holding em Curitiba/PR e confirma margem sustentável.',
      },
    ],
  };
}

function buildDefaultCritiques(code: string, isGreenfieldEmpty = false): ProtocolCritiqueItemDto[] {
  if (code.toUpperCase() === 'EPOCA') {
    return EPOCA_TRIALSHUB_PROTOCOL_CRITIQUES.map((c) => ({ ...c }));
  }
  if (isGreenfieldEmpty) {
    return [];
  }
  return [
    {
      id: `crit-${code}-1`,
      studyCode: code,
      title:
        'Nota de Rodapé SoA [c]: ECG Triplicado pré e pós-dose não listado em linha separada na planilha da CRO',
      category: 'PROTOCOL_BUDGET_DISCREPANCY',
      severity: 'CRITICAL',
      status: 'OPEN_ATTENTION',
      estimatedExposureBrl: 18400,
      protocolReference: 'Protocolo Seção 8.2 / Nota SoA [c] (Visitas V1 a V6)',
      discrepancyDescription:
        'O protocolo exige 3 traçados de ECG com intervalo de 5 min em cada visita de infusão, mas a planilha da CRO orçou apenas 1 ECG simples.',
      recommendedAction:
        'Incluir linha dedicada de ECG Triplicado (R$ 320,00 x 3 por visita) + Gross-up 14,25% antes da assinatura.',
      distilledGlobalRule:
        'Sempre que a nota de rodapé da SoA mencionar ECG triplicado ou sinais vitais seriados pós-dose, desmembrar a cobrança por repetição.',
      globalRuleStatus: 'APPROVED_GLOBAL',
    },
    {
      id: `crit-${code}-2`,
      studyCode: code,
      title: 'Teto de Screen Failure em 15% incompatível com critério de inclusão de biomarcador',
      category: 'FINANCIAL_HIDDEN_COST_RISK',
      severity: 'HIGH',
      status: 'OPEN_ATTENTION',
      estimatedExposureBrl: 24600,
      protocolReference: 'Minuta CTA Cláusula 4.3 vs. Critério de Inclusão #4',
      discrepancyDescription:
        'A taxa histórica de falha de seleção para este biomarcador é de 38%, gerando risco de exames de triagem não reembolsados.',
      recommendedAction:
        'Exigir reembolso de Screen Failure na razão 2:1 para todos os exames de imagem e laboratório local.',
      distilledGlobalRule:
        'Em protocolos com triagem por biomarcador específico, travar cláusula de Screen Failure mínima de 2:1.',
      globalRuleStatus: 'APPROVED_GLOBAL',
    },
  ];
}

function buildDefaultPayments(
  code: string,
  dossierStatus = 'ApprovedForExecution',
  isGreenfieldEmpty = false,
): StudyFinancialPaymentItemDto[] {
  // Contratos em análise inicial / negociação (como a Minuta EPOCA — TrialsHub ainda não assinada)
  // ou ambientes de produção/greenfield sem faturamento realizado possuem R$ 0,00 recebidos.
  if (
    isGreenfieldEmpty ||
    code.toUpperCase() === 'EPOCA' ||
    dossierStatus === 'UnderAnalysis' ||
    dossierStatus === 'InNegotiation'
  ) {
    return [];
  }

  return [
    {
      id: `pay-${code}-1`,
      studyCode: code,
      paymentDate: '2026-10-10',
      category: 'START_UP_REGULATORIO',
      procedureOrVisitCode: 'STARTUP-CTA',
      patientRef: 'SITE-CWB',
      originalAmount: 18500,
      currency: 'BRL',
      frozenPtaxRate: 1,
      paidAmountBrl: 18500,
      expectedBudgetBrl: 18500,
      invoiceNumber: 'NF-2026-0118',
      daysToReceive: 28,
      status: 'CONCILIADO',
    },
    {
      id: `pay-${code}-2`,
      studyCode: code,
      paymentDate: '2026-10-22',
      category: 'PROCEDIMENTO',
      procedureOrVisitCode: 'V1-V2-PK',
      patientRef: 'SUBJ-001',
      originalAmount: 3200,
      currency: 'USD',
      frozenPtaxRate: 5.72,
      paidAmountBrl: 18304,
      expectedBudgetBrl: 17900,
      invoiceNumber: 'INV-CAYMAN-2026-04',
      daysToReceive: 34,
      status: 'CONCILIADO',
    },
    {
      id: `pay-${code}-3`,
      studyCode: code,
      paymentDate: '2026-10-24',
      category: 'REEMBOLSO',
      procedureOrVisitCode: 'REEMB-TAXI-ALIM',
      patientRef: 'SUBJ-002',
      originalAmount: 1420,
      currency: 'BRL',
      frozenPtaxRate: 1,
      paidAmountBrl: 1420,
      expectedBudgetBrl: 1420,
      invoiceNumber: 'NF-2026-0141',
      daysToReceive: 21,
      status: 'CONCILIADO',
    },
  ];
}

function buildFakeCashflow(
  code: string,
  baseTotal: number,
  targetPatients: number,
  durationMonths: number,
  overheadRegularPercent: number,
  netReceiptDaysRegular: number,
  screenFailureRegularPercent: number,
): StudyCashflowProjectionDto {
  const isEpoca = code.toUpperCase() === 'EPOCA';
  const optimisticNet = Math.round(baseTotal * (isEpoca ? 0.25 : 0.345));
  const regularNet = Math.round(baseTotal * (overheadRegularPercent / 100));
  const pessimisticNet = Math.round(
    baseTotal * ((overheadRegularPercent - screenFailureRegularPercent * 0.42) / 100),
  );
  const spread = optimisticNet - pessimisticNet;
  const months = [1, 2, 3, 4, 5, 6].map((m) => {
    const r = m / 6;
    return {
      monthLabel: isEpoca ? `Semestre ${m}` : `Mês ${m}`,
      optimisticCumulativeBrl: Math.round(optimisticNet * r),
      regularCumulativeBrl: Math.round(regularNet * r),
      pessimisticCumulativeBrl: Math.round(pessimisticNet * r - (m === 1 ? 3000 : 0)),
    };
  });

  return {
    studyCode: code,
    targetPatients,
    durationMonths,
    overheadRegularPercent,
    netReceiptDaysRegular,
    screenFailureRegularPercent,
    optimisticNetBalanceBrl: optimisticNet,
    optimisticMarginPercent: isEpoca ? 25.0 : 34.5,
    regularNetBalanceBrl: regularNet,
    regularMarginPercent: overheadRegularPercent,
    pessimisticNetBalanceBrl: pessimisticNet,
    pessimisticMarginPercent: Number(((pessimisticNet / baseTotal) * 100).toFixed(1)),
    spreadRiskBrl: spread,
    monthlyPoints: months,
    criticalFactors: isEpoca
      ? [
          {
            factorName:
              'Ausência/Vedação de Repactuação e Reequilíbrio Financeiro em Contrato Longo (48 Meses D0–D1440 · Vigência até 30/06/2032)',
            impactLevel: 'CRÍTICO',
            watchingRule:
              'Em contratos curtos (<= 12 meses) a vedação de repactuação não é preocupante e pode ser aceita; porém, neste contrato longo (> 12 meses — 48 meses de visitas e vigência até 2032) é CRÍTICA e exige Redline na Cláusula 6ª para reajuste anual (IPCA) e reequilíbrio financeiro.',
          },
          {
            factorName: 'Condição Suspensiva da 2ª Etapa (Cláusula 4.7 — D450 a D1440)',
            impactLevel: 'CRÍTICO',
            watchingRule:
              'A 2ª Etapa (Pagamentos 8 a 21 = R$ 2.900,00/participante com 25% de overhead incluso) somente terá início mediante notificação formal por e-mail da Patrocinadora (Hcor).',
          },
          {
            factorName: 'Transporte e Alimentação Inclusos nos R$ 250,00/Visita (Anexo I)',
            impactLevel: 'ALTO',
            watchingRule:
              'O Anexo I estabelece literalmente que o valor de R$ 250,00 por visita presencial (R$ 200,00 base + R$ 50,00 overhead 25%) inclui procedimentos, consulta médica e ressarcimento de transporte e alimentação ao participante.',
          },
          {
            factorName:
              'Pagamento em 30 Dias após Nota Fiscal Válida (Cláusula 6.5 · fiscal@hcor.com.br)',
            impactLevel: 'MÉDIO',
            watchingRule: `O pagamento ocorre em ${netReceiptDaysRegular} (trinta) dias após o recebimento da Nota Fiscal válida enviada para fiscal@hcor.com.br, com prazo de 30 dias após o pagamento para contestar divergências (Cláusula 6.6).`,
          },
        ]
      : [
          {
            factorName: 'Taxa de Falha de Seleção (Screen Failure)',
            impactLevel: 'CRÍTICO',
            watchingRule: `Manter abaixo de ${screenFailureRegularPercent}% ou acionar aditivo de reembolso 2:1 a partir do 3º participante.`,
          },
          {
            factorName: 'Prazo Real de Liquidação de Invoice / NF',
            impactLevel: 'ALTO',
            watchingRule: `Acima de ${netReceiptDaysRegular} dias corridos, aplicar cláusula de correção financeira EQ-BCB-01.`,
          },
          {
            factorName: 'Consumo de Kits & Insumos Pass-Through (FeNO / Criogenia)',
            impactLevel: 'MÉDIO',
            watchingRule:
              'Faturar mensalmente junto à visita realizada para evitar capital de giro negativo no centro.',
          },
        ],
    coordinatorGuardrails: isEpoca
      ? [
          'Contrato ainda em Minuta em Análise Inicial (R$ 0,00 recebido): preencher em controle de alterações os campos em branco da minuta (Razão Social, CNPJ XX.XXX.XXX/XXXX-XX, CPF, PI e Dados Bancários na Tabela 3).',
          'Emitir Nota Fiscal somente após aprovação regulatória, assinatura do contrato, realização da Visita de Iniciação (SIV) e envio de relatório/validação da Patrocinadora, encaminhando a NF para fiscal@hcor.com.br (pagamento em 30 dias — Cláusula 6.5).',
          'Não iniciar a 2ª Etapa (Visita 5 / Dia 450 a Visita 12 / Dia 1440) sem prévia notificação formal por e-mail da Patrocinadora (Cláusula 4.7).',
        ]
      : [
          'Faturar 100% da Taxa de Start-up e Farmácia imediatamente na assinatura do CTA.',
          'Não iniciar visitas V1 sem validação prévia da tabela da Holding GIYUS para a cidade do centro.',
          'Conciliar todo pagamento internacional USD/EUR com a PTAX BCB congelada na data de liquidação.',
        ],
  };
}

function buildFakeWorkspaceBundle(
  state: FakeSuppliersAndStudiesState,
  code: string,
  currentNegotiatedBrl: number,
  dossierStatus: string,
): StudyWorkspaceBundleDto {
  const isEpoca = code.toUpperCase() === 'EPOCA';
  const isGreenfieldEmpty = state.suppliers.length === 0;
  const isSignedFrozen = dossierStatus === 'ApprovedForExecution';
  const appliedVersion = state.studyTableVersions[code] ?? 2;
  const holdingVer = state.holdingPriceTableVersion;
  const dismissed = state.studyDismissedAlerts[code] ?? false;
  const hasNewerTable = !isSignedFrozen && !isEpoca && appliedVersion < holdingVer && !dismissed;
  const recalculatedBrl = hasNewerTable
    ? Math.round(currentNegotiatedBrl * 1.045)
    : currentNegotiatedBrl;

  const alertMsg = isEpoca
    ? 'Minuta em Análise Inicial (Hcor · MINUTA_EPOCA_2026_TrialsHub.docx): Anexo I com valores unitários de R$ 3.000,00 (Taxa Única de Startup) + R$ 5.900,00 por participante (1ª Etapa R$ 3.000,00 + 2ª Etapa R$ 2.900,00, Overhead 25% incluso; soma unitária da tabela = R$ 8.900,00). Recrutamento competitivo (Cláusula 4.5). Contrato ainda não assinado e com R$ 0,00 recebido.'
    : isSignedFrozen
      ? `Contrato Fechado/Assinado: Preços congelados na versão v${appliedVersion} da Tabela da Holding (FrozenUnitPrice preservado).`
      : hasNewerTable
        ? `Nova tabela de preços da Holding disponível para Curitiba/PR (v${holdingVer} vs. v${appliedVersion} da proposta). Atualize com 1 clique para R$ ${recalculatedBrl.toLocaleString('pt-BR')} ou mantenha a versão enviada ao patrocinador.`
        : `Orçamento alinhado à versão v${appliedVersion} da Tabela de Procedimentos da Holding GIYUS.`;

  const critiques = state.critiquesByStudy[code] ?? buildDefaultCritiques(code, isGreenfieldEmpty);
  const payments =
    state.paymentsByStudy[code] ?? buildDefaultPayments(code, dossierStatus, isGreenfieldEmpty);
  const cashflow =
    state.cashflowByStudy[code] ??
    (isEpoca
      ? buildFakeCashflow(code, currentNegotiatedBrl, 1, 48, 25, 30, 0)
      : buildFakeCashflow(code, currentNegotiatedBrl, 12, 12, 28, 42, 25));

  return {
    studyCode: code,
    cityUf: 'Curitiba/PR',
    studyBudgetTableVersion: appliedVersion,
    holdingCurrentTableVersion: holdingVer,
    isContractSignedFrozen: isSignedFrozen,
    hasNewerHoldingPriceTable: hasNewerTable,
    dismissedKeepProposalVersion: dismissed,
    currentNegotiatedBrl,
    recalculatedWithNewHoldingTableBrl: recalculatedBrl,
    priceTableAlertMessage: alertMsg,
    latestCouncilRun: buildFakeCouncilRun(code, currentNegotiatedBrl, isGreenfieldEmpty),
    chatMessages:
      isGreenfieldEmpty || isEpoca
        ? []
        : [
            {
              id: `chat-${code}-1`,
              studyCode: code,
              authorEmail: 'syllas@giyus.com.br',
              authorRole: 'Super Administrador (Aprovador Único Global)',
              userMessage:
                'Verifique se o tempo de enfermagem (COREN) na coleta PK seriada de 8h e o bocal FeNO estão cobertos com Gross-up de 14,25%.',
              aiReply: `Confirmado: no estudo ${code}, a coleta PK de 8h exige 240 min de enfermagem dedicada + processamento criogênico -80°C (R$ 1.890,00 na tabela v3 de Curitiba/PR) acrescido de 14,25% de Gross-up Lucro Real.`,
              scope: 'GLOBAL',
              globalApprovalStatus: 'APPROVED_GLOBAL',
              createdAtIso: '2026-10-25T15:30:00Z',
            },
          ],
    protocolCritiques: critiques,
    financialPayments: payments,
    cashflowProjection: cashflow,
    budgetEmailDrafts: state.budgetEmailDrafts.filter(
      (d) => d.studyCode.toUpperCase() === code.toUpperCase(),
    ),
  };
}

export const DEFAULT_CLINICAL_SPECIALTIES_CATALOG: readonly ClinicalSpecialtyItemDto[] = [
  {
    id: 'spec-cardio',
    code: 'ESP-CARDIO',
    name: 'Cardiologia',
    category: 'Clínica Médica / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-alergia',
    code: 'ESP-ALERGIA',
    name: 'Alergia e Imunologia',
    category: 'Clínica Médica & Pediatria / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-pneumo',
    code: 'ESP-PNEUMO',
    name: 'Pneumologia',
    category: 'Clínica Médica / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-onco',
    code: 'ESP-ONCO',
    name: 'Oncologia Clínica',
    category: 'Clínica Médica / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-endo',
    code: 'ESP-ENDO',
    name: 'Endocrinologia e Metabologia',
    category: 'Clínica Médica / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-ped',
    code: 'ESP-PED',
    name: 'Pediatria',
    category: 'Pediatria / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-neuro',
    code: 'ESP-NEURO',
    name: 'Neurologia',
    category: 'Neurociências / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-dermato',
    code: 'ESP-DERMATO',
    name: 'Dermatologia',
    category: 'Clínica Médica / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-hemato',
    code: 'ESP-HEMATO',
    name: 'Hematologia e Hemoterapia',
    category: 'Clínica Médica / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-reumato',
    code: 'ESP-REUMATO',
    name: 'Reumatologia',
    category: 'Clínica Médica / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-infecto',
    code: 'ESP-INFECTO',
    name: 'Infectologia',
    category: 'Clínica Médica / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-gastro',
    code: 'ESP-GASTRO',
    name: 'Gastroenterologia',
    category: 'Clínica Médica / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-nefro',
    code: 'ESP-NEFRO',
    name: 'Nefrologia',
    category: 'Clínica Médica / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'spec-clinica',
    code: 'ESP-CLINICA',
    name: 'Clínica Médica',
    category: 'Clínica Médica / Especialidade AMB-CFM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
];

export const DEFAULT_CLINICAL_HEALTH_FUNCTIONS_CATALOG: readonly ClinicalHealthFunctionItemDto[] = [
  {
    id: 'hfunc-enfermagem',
    code: 'SAUDE-COREN',
    name: 'Enfermeira(o) (COREN)',
    councilAcronym: 'COREN',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'hfunc-farmacia',
    code: 'SAUDE-CRF',
    name: 'Farmacêutica(o) (CRF)',
    councilAcronym: 'CRF',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'hfunc-biomedicina',
    code: 'SAUDE-CRBM',
    name: 'Biomédica(o) (CRBM)',
    councilAcronym: 'CRBM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'hfunc-fisioterapia',
    code: 'SAUDE-CREFITO',
    name: 'Fisioterapeuta (CREFITO)',
    councilAcronym: 'CREFITO',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'hfunc-nutricao',
    code: 'SAUDE-CRN',
    name: 'Nutricionista (CRN)',
    councilAcronym: 'CRN',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'hfunc-biologia',
    code: 'SAUDE-CRBIO',
    name: 'Bióloga(o) (CRBio)',
    councilAcronym: 'CRBio',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'hfunc-psicologia',
    code: 'SAUDE-CRP',
    name: 'Psicóloga(o) (CRP)',
    councilAcronym: 'CRP',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'hfunc-tec-enfermagem',
    code: 'SAUDE-COREN-TEC',
    name: 'Técnica(o) de Enfermagem (COREN)',
    councilAcronym: 'COREN',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'hfunc-medicina',
    code: 'SAUDE-CRM',
    name: 'Médica(o) (CRM)',
    councilAcronym: 'CRM',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
  {
    id: 'hfunc-odontologia',
    code: 'SAUDE-CRO',
    name: 'Odontóloga(o) (CRO)',
    councilAcronym: 'CRO',
    active: true,
    updatedAtIso: '2026-10-30T12:00:00Z',
  },
];

const AVAILABLE_PI_CATALOG: StudyTeamMultiDocAndLearningDashboardDto['availablePiCatalog'] = [
  {
    name: 'Dr. Marco Stephan Lofrano Alves',
    crm: 'CRM-PR 32.845',
    cpf: '048.912.349-10',
    specialty: 'Cardiologia',
    secondarySpecialties: ['Clínica Médica', 'Endocrinologia e Metabologia'],
    email: 'marco.stephan@trialshub.com.br',
    commercialEmail: 'marco.stephan@trialshub.com.br',
    personalEmail: 'marcolofrano1@gmail.com',
    mobilePhone: '(41) 99918-4420',
    isPrincipalInvestigator: true,
    isSubInvestigator: true,
    piTherapeuticAreas: ['Cardiologia', 'Insuficiência Cardíaca', 'Clínica Médica'],
    subTherapeuticAreas: ['Cardiologia Intervencionista', 'Endocrinologia & Metabologia'],
    relatedCenters: [
      'Trials Hub Ltda (Centro Parceiro Rebouças) (CNPJ 66.125.390/0001-34 · CNES 2639540)',
    ],
  },
  {
    name: 'Prof. Dr. Nelson Augusto Rosário Filho',
    crm: 'CRM-PR 4.192',
    cpf: '192.844.519-00',
    specialty: 'Alergia e Imunologia',
    secondarySpecialties: ['Pneumologia', 'Pediatria'],
    email: 'nelson.rosario@ufpr.br',
    commercialEmail: 'nelson.rosario@ufpr.br',
    personalEmail: 'nelson.rosario.filho@gmail.com',
    mobilePhone: '(41) 99972-3330',
    isPrincipalInvestigator: true,
    isSubInvestigator: true,
    piTherapeuticAreas: ['Alergia e Imunologia', 'Pneumologia', 'Pediatria'],
    subTherapeuticAreas: ['Dermatologia Imunológica', 'Vacinas e Infectologia'],
    relatedCenters: [
      'Nelson Rosário — Médicos Associados Ltda (CNPJ 04.250.423/0001-10 · CNES 5322202)',
      'GIYUS Curitiba Research Center (Filial Operacional) (CNPJ 69.584.441/0001-84 · CNES 3128816)',
    ],
  },
  {
    name: 'Dr. Herberto José Chong Neto (Dr. Chong-Silva)',
    crm: 'CRM-PR 19.342',
    cpf: '024.718.939-44',
    specialty: 'Alergia e Imunologia',
    secondarySpecialties: ['Pediatria', 'Pneumologia'],
    email: 'chong.neto@giyus.com.br',
    commercialEmail: 'chong.neto@giyus.com.br',
    personalEmail: 'herberto.chong@gmail.com',
    mobilePhone: '(41) 99124-2636',
    isPrincipalInvestigator: true,
    isSubInvestigator: true,
    piTherapeuticAreas: ['Alergia e Imunologia Pediátrica', 'Asma Grave', 'Dermatite Atópica'],
    subTherapeuticAreas: ['Pneumologia', 'Imunobiológicos', 'Pediatria Geral'],
    relatedCenters: [
      'GIYUS Curitiba Research Center (Filial Operacional) (CNPJ 69.584.441/0001-84 · CNES 3128816)',
      'Clínica Dr. Herberto Chong (Filial Pesquisa & Matriz) (CNPJ 51.785.454/0001-56 · CNES 1159593)',
    ],
  },
  {
    name: 'Dra. Viviane Flumignan Zétola',
    crm: 'CRM-PR 11.898',
    cpf: '311.509.289-72',
    specialty: 'Neurologia',
    secondarySpecialties: ['Clínica Médica'],
    email: 'viviane.zetola@hc.ufpr.br',
    commercialEmail: 'viviane.zetola@hc.ufpr.br',
    personalEmail: 'viviane.zetola@gmail.com',
    mobilePhone: '(41) 99981-1189',
    isPrincipalInvestigator: true,
    isSubInvestigator: true,
    piTherapeuticAreas: ['Neurologia Vascular (AVC)', 'Doenças Neurodegenerativas'],
    subTherapeuticAreas: ['Clínica Médica', 'Cardiologia Preventiva'],
    relatedCenters: [
      'GIYUS Curitiba Research Center (Filial Operacional) (CNPJ 69.584.441/0001-84 · CNES 3128816)',
    ],
  },
  {
    name: 'Dra. Vaneuza Araújo Moreira Funke',
    crm: 'CRM-PR 15.104',
    cpf: '408.621.739-15',
    specialty: 'Hematologia e Hemoterapia',
    secondarySpecialties: ['Oncologia Clínica', 'Clínica Médica'],
    email: 'vaneuza.funke@hc.ufpr.br',
    commercialEmail: 'vaneuza.funke@hc.ufpr.br',
    personalEmail: 'vaneuza.funke@gmail.com',
    mobilePhone: '(41) 99964-1510',
    isPrincipalInvestigator: true,
    isSubInvestigator: true,
    piTherapeuticAreas: ['Hematologia', 'Onco-Hematologia', 'Transplante de Medula Óssea (TMO)'],
    subTherapeuticAreas: ['Oncologia Clínica', 'Terapia Celular CAR-T'],
    relatedCenters: [
      'GIYUS Curitiba Research Center (Filial Operacional) (CNPJ 69.584.441/0001-84 · CNES 3128816)',
    ],
  },
];

const AVAILABLE_SUB_CATALOG: StudyTeamMultiDocAndLearningDashboardDto['availableSubCatalog'] = [
  {
    name: 'Dra. Helena Magajevski',
    crm: 'CRM-PR 18.410',
    cpf: '077.419.829-31',
    specialty: 'Cardiologia',
    secondarySpecialties: ['Clínica Médica'],
    email: 'magajevski@yahoo.com.br',
    commercialEmail: 'helena.magajevski@trialshub.com.br',
    personalEmail: 'magajevski@yahoo.com.br',
    mobilePhone: '(41) 99812-1841',
    isPrincipalInvestigator: false,
    isSubInvestigator: true,
    piTherapeuticAreas: [],
    subTherapeuticAreas: ['Cardiologia Clínica', 'Insuficiência Cardíaca', 'Hipertensão Arterial'],
    relatedCenters: [
      'Trials Hub Ltda (Centro Parceiro Rebouças) (CNPJ 66.125.390/0001-34 · CNES 2639540)',
    ],
  },
  {
    name: 'Dr. Herberto José Chong Neto',
    crm: 'CRM-PR 19.342',
    cpf: '024.718.939-44',
    specialty: 'Alergia e Imunologia',
    secondarySpecialties: ['Pediatria', 'Pneumologia'],
    email: 'chong.neto@giyus.com.br',
    commercialEmail: 'chong.neto@giyus.com.br',
    personalEmail: 'herberto.chong@gmail.com',
    mobilePhone: '(41) 99124-2636',
    isPrincipalInvestigator: true,
    isSubInvestigator: true,
    piTherapeuticAreas: ['Alergia e Imunologia Pediátrica', 'Asma Grave'],
    subTherapeuticAreas: ['Pneumologia', 'Imunobiológicos'],
    relatedCenters: [
      'GIYUS Curitiba Research Center (Filial Operacional) (CNPJ 69.584.441/0001-84 · CNES 3128816)',
    ],
  },
  {
    name: 'Prof. Dr. Nelson Augusto Rosário Filho',
    crm: 'CRM-PR 4.192',
    cpf: '192.844.519-00',
    specialty: 'Alergia e Imunologia',
    secondarySpecialties: ['Pneumologia', 'Pediatria'],
    email: 'nelson.rosario@ufpr.br',
    commercialEmail: 'nelson.rosario@ufpr.br',
    personalEmail: 'nelson.rosario.filho@gmail.com',
    mobilePhone: '(41) 99972-3330',
    isPrincipalInvestigator: true,
    isSubInvestigator: true,
    piTherapeuticAreas: ['Alergia e Imunologia', 'Pneumologia'],
    subTherapeuticAreas: ['Dermatologia Imunológica', 'Vacinas e Infectologia'],
    relatedCenters: [
      'Nelson Rosário — Médicos Associados Ltda (CNPJ 04.250.423/0001-10 · CNES 5322202)',
    ],
  },
  {
    name: 'Dr. Marco Stephan Lofrano Alves',
    crm: 'CRM-PR 32.845',
    cpf: '048.912.349-10',
    specialty: 'Cardiologia',
    secondarySpecialties: ['Clínica Médica', 'Endocrinologia e Metabologia'],
    email: 'marcolofrano1@gmail.com',
    commercialEmail: 'marco.stephan@trialshub.com.br',
    personalEmail: 'marcolofrano1@gmail.com',
    mobilePhone: '(41) 99918-4420',
    isPrincipalInvestigator: true,
    isSubInvestigator: true,
    piTherapeuticAreas: ['Cardiologia', 'Insuficiência Cardíaca'],
    subTherapeuticAreas: ['Cardiologia Intervencionista', 'Clínica Médica'],
    relatedCenters: [
      'Trials Hub Ltda (Centro Parceiro Rebouças) (CNPJ 66.125.390/0001-34 · CNES 2639540)',
    ],
  },
  {
    name: 'Dra. Carmem Maria Sales Bonfim',
    crm: 'CRM-PR 12.680',
    cpf: '519.302.419-88',
    specialty: 'Hematologia e Hemoterapia',
    secondarySpecialties: ['Pediatria', 'Oncologia Clínica'],
    email: 'carmem.bonfim@hc.ufpr.br',
    commercialEmail: 'carmem.bonfim@hc.ufpr.br',
    personalEmail: 'carmem.bonfim@gmail.com',
    mobilePhone: '(41) 99945-1268',
    isPrincipalInvestigator: false,
    isSubInvestigator: true,
    piTherapeuticAreas: [],
    subTherapeuticAreas: ['Hematologia Pediátrica', 'Transplante de Medula Óssea (TMO)'],
    relatedCenters: [
      'GIYUS Curitiba Research Center (Filial Operacional) (CNPJ 69.584.441/0001-84 · CNES 3128816)',
    ],
  },
];

const AVAILABLE_COORDINATOR_CATALOG: StudyTeamMultiDocAndLearningDashboardDto['availableCoordinatorCatalog'] =
  [
    {
      name: 'Elessandra S. Bitencourt',
      cpf: '054.812.639-20',
      birthDate: '14/05/1988',
      level: 'Sênior',
      isHealthProfessional: true,
      healthProfessionalType: 'Enfermeira(o) (COREN)',
      councilRegistrationNumber: 'COREN/PR 244890',
      role: 'Study Coordinator (SC) — Enfermeira(o) (COREN/PR 244890)',
      email: 'elessandrabitencourt@gmail.com',
      corporateEmail: 'elessandra@giyus.com.br',
      personalEmail: 'elessandrabitencourt@gmail.com',
      mobilePhone: '(41) 99642-8810',
    },
    {
      name: 'Beatriz M. Linero',
      cpf: '089.314.729-55',
      birthDate: '22/09/1991',
      level: 'Sênior',
      isHealthProfessional: true,
      healthProfessionalType: 'Farmacêutica(o) (CRF)',
      councilRegistrationNumber: 'CRF/PR 21840',
      role: 'Coordenadora Sênior de Estudos Clínicos & Regulatório (CRF/PR 21840)',
      email: 'beatriz.coord@giyus.com.br',
      corporateEmail: 'beatriz.coord@giyus.com.br',
      personalEmail: 'beatriz.linero@gmail.com',
      mobilePhone: '(41) 99715-2209',
    },
    {
      name: 'Mariana Linero',
      cpf: '092.418.539-12',
      birthDate: '03/11/1993',
      level: 'Pleno',
      isHealthProfessional: true,
      healthProfessionalType: 'Farmacêutica(o) (CRF)',
      councilRegistrationNumber: 'CRF/PR 22910',
      role: 'Coordenadora Clínica & Qualidade BPC (CRF/PR 22910)',
      email: 'mariana.linero@giyus.com.br',
      corporateEmail: 'mariana.linero@giyus.com.br',
      personalEmail: 'mariana.linero.farm@gmail.com',
      mobilePhone: '(41) 99831-0311',
    },
  ];

const AVAILABLE_RESEARCH_CENTERS_CATALOG: StudyTeamMultiDocAndLearningDashboardDto['availableResearchCentersCatalog'] =
  [
    {
      name: 'Trials Hub Ltda — Curitiba/PR',
      legalName: 'TRIALS HUB PESQUISA CLÍNICA LTDA',
      cnpj: '66.125.390/0001-34',
      cnes: '9041825',
      cityUf: 'Curitiba/PR',
      address: 'Rua Padre Anchieta, 2050 — Conj. 804, Bigorrilho, Curitiba/PR — CEP 80730-000',
      bankSummary: 'Banco Itaú (341) · Ag. 0615 · C/C 44890-2 · PIX CNPJ 66.125.390/0001-34',
      classification: 'CENTRO_NAO_GIYUS',
      classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_NAO_GIYUS.label,
      paymentFlowRule: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_NAO_GIYUS.paymentAndGovernanceRule,
    },
    {
      name: 'GIYUS Curitiba Research Center (Matriz Site 0701)',
      legalName: 'GIYUS CURITIBA RESEARCH CENTER LTDA',
      cnpj: '69.584.441/0001-84',
      cnes: '9104218',
      cityUf: 'Curitiba/PR',
      address: 'Av. Sete de Setembro, 4698 — Batel, Curitiba/PR — CEP 80240-000',
      bankSummary: 'C6 Bank (336) / Cayman SWIFT CSIXKYKY · Ag. 0001 · C/C 991402-8',
      classification: 'CENTRO_GIYUS',
      classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_GIYUS.label,
      paymentFlowRule: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_GIYUS.paymentAndGovernanceRule,
    },
    {
      name: 'HC-UFPR / Centro de Pesquisa Clínica Site 297000',
      legalName: 'COMPLEXO HOSPITAL DE CLÍNICAS DA UFPR / FUNPAR',
      cnpj: '75.095.679/0002-20',
      cnes: '0015563',
      cityUf: 'Curitiba/PR',
      address: 'Rua General Carneiro, 181 — Alto da Glória, Curitiba/PR — CEP 80060-900',
      bankSummary: 'Caixa Econômica Federal (104) · Ag. 0372 · C/C 003.4920-1',
      classification: 'CENTRO_PARCEIRO_GIYUS',
      classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_PARCEIRO_GIYUS.label,
      paymentFlowRule:
        RESEARCH_CENTER_CLASSIFICATIONS.CENTRO_PARCEIRO_GIYUS.paymentAndGovernanceRule,
    },
    {
      name: 'GIYUS Holding Matriz (Gestão Central & CSC)',
      legalName: 'GIYUS HOLDING S.A. / GIYUS PESQUISA CLINICA',
      cnpj: '69.308.788/0001-02',
      cnes: 'Holding Corporativa',
      cityUf: 'Curitiba/PR',
      address: 'Rua Padre Anchieta, 2050 — Bigorrilho, Curitiba/PR — CEP 80730-000',
      bankSummary: 'Banco C6 S.A. (336) · Ag. 0001 · CC Corporativa Holding',
      classification: 'GIYUS_HOLDING',
      classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS.GIYUS_HOLDING.label,
      paymentFlowRule: RESEARCH_CENTER_CLASSIFICATIONS.GIYUS_HOLDING.paymentAndGovernanceRule,
    },
  ];

function resolveCenterClassification(
  rawClassification?: string,
  centerName?: string,
): ResearchCenterClassificationType {
  const upper = (rawClassification ?? '').trim().toUpperCase();
  if (
    upper === 'CENTRO_NAO_GIYUS' ||
    upper === 'CENTRO_GIYUS' ||
    upper === 'CENTRO_PARCEIRO_GIYUS' ||
    upper === 'GIYUS_HOLDING'
  ) {
    return upper;
  }
  const nameUpper = (centerName ?? '').toUpperCase();
  if (nameUpper.includes('HOLDING')) return 'GIYUS_HOLDING';
  if (nameUpper.includes('GIYUS') && !nameUpper.includes('PARCEIRO')) return 'CENTRO_GIYUS';
  if (nameUpper.includes('PARCEIRO GIYUS')) return 'CENTRO_PARCEIRO_GIYUS';
  return 'CENTRO_NAO_GIYUS';
}

function getEffectiveStudyTeamAndCenterBinding(
  state: FakeSuppliersAndStudiesState | undefined,
  studyCode: string,
): StudyTeamAndCenterBindingDto {
  const code = studyCode.toUpperCase();
  const isEpoca = code === 'EPOCA';
  const isSolair = code === 'SOLAIR';
  const isPersist = code === 'PERSIST';
  const isAdept = code === 'ADEPT-4';

  const toRecipients = isEpoca
    ? 'tabait@hcor.com.br; gvbatista@hcor.com.br; lilian.sabino@hcor.com.br; pesquisaclinica@trialshub.com.br'
    : isSolair
      ? 'Patricia.Stein@parexel.com'
      : isPersist
        ? 'amanda.contracts@iqvia.com; clinical.br@gsk.com'
        : isAdept
          ? 'Valeria.Santos2@thermofisher.com'
          : 'contratos@patrocinador.com.br';

  const persisted = state?.studyTeamBindings?.[code];
  const isGreenfield = state?.isGreenfield !== false;

  if (persisted && persisted.isAutoFilled && (persisted.explicitlyBoundByUser || !isGreenfield)) {
    const normalizedSubs =
      persisted.subInvestigators && persisted.subInvestigators.length > 0
        ? persisted.subInvestigators
        : persisted.subInvestigatorName
          ? [
              {
                name: persisted.subInvestigatorName,
                crm: persisted.subInvestigatorCrm,
                specialty: persisted.subInvestigatorSpecialty,
                email: persisted.subInvestigatorEmail,
              },
            ]
          : [];
    const normalizedCoords =
      persisted.coordinators && persisted.coordinators.length > 0
        ? persisted.coordinators
        : persisted.coordinatorName
          ? [
              {
                name: persisted.coordinatorName,
                role: persisted.coordinatorRole,
                email: persisted.coordinatorEmail,
              },
            ]
          : [];
    return {
      ...persisted,
      subInvestigators: normalizedSubs,
      coordinators: normalizedCoords,
    };
  }

  // [REGRA 01 — ZERO INVENÇÃO EM PRODUÇÃO]
  // Em produção / Greenfield (isGreenfield === true), se o usuário ainda não cadastrou
  // nem relacionou explicitamente o PI, Sub-Investigadores (1..N), Equipe de Coordenação (1..N)
  // e Centro de Pesquisa ao estudo, JAMAIS inventar pessoas ou dados bancários/CNPJ!
  if (isGreenfield) {
    return {
      studyCode: code,
      piName: '',
      piCrm: '',
      piSpecialty: '',
      piEmail: '',
      subInvestigatorName: '',
      subInvestigatorCrm: '',
      subInvestigatorSpecialty: '',
      subInvestigatorEmail: '',
      subInvestigators: [],
      coordinatorName: '',
      coordinatorRole: '',
      coordinatorEmail: '',
      coordinators: [],
      researchCenterName: '',
      researchCenterLegalName: '',
      researchCenterCnpj: '',
      researchCenterCnes: '',
      researchCenterCityUf: 'Curitiba/PR',
      researchCenterAddress: '',
      researchCenterBankSummary: '',
      isAutoFilled: false,
      explicitlyBoundByUser: false,
      autoFilledFieldsCount: 0,
      autoFilledContractPreambleText:
        'Aguardando cadastro em Gestão de Pessoas e vínculo explícito de PI, Sub-Investigadores (1..N), Equipe de Coordenação (1..N) e Centro de Pesquisa para preenchimento automático dos campos em branco da minuta (RAZÃO SOCIAL, CNPJ XX.XXX.XXX/XXXX-XX, CNES, Dados Bancários e Equipe).',
      autoFilledDraftRecipientsTo: toRecipients,
      autoFilledDraftRecipientsCc: 'contratos@giyus.com.br',
      lastLinkedAtIso: '',
      lastLinkedByEmail: '',
    };
  }

  const pi = isEpoca
    ? AVAILABLE_PI_CATALOG[0]!
    : isSolair
      ? AVAILABLE_PI_CATALOG[1]!
      : isPersist
        ? AVAILABLE_PI_CATALOG[2]!
        : isAdept
          ? AVAILABLE_PI_CATALOG[3]!
          : AVAILABLE_PI_CATALOG[4]!;

  const subPrimary = isEpoca
    ? AVAILABLE_SUB_CATALOG[0]!
    : isSolair
      ? AVAILABLE_SUB_CATALOG[1]!
      : isPersist
        ? AVAILABLE_SUB_CATALOG[2]!
        : isAdept
          ? AVAILABLE_SUB_CATALOG[3]!
          : AVAILABLE_SUB_CATALOG[4]!;

  const subSecondary = isEpoca
    ? AVAILABLE_SUB_CATALOG[1]!
    : isSolair
      ? AVAILABLE_SUB_CATALOG[0]!
      : isPersist
        ? AVAILABLE_SUB_CATALOG[0]!
        : isAdept
          ? AVAILABLE_SUB_CATALOG[0]!
          : AVAILABLE_SUB_CATALOG[0]!;

  const subInvestigators = persisted?.subInvestigators?.length
    ? persisted.subInvestigators
    : [subPrimary, subSecondary];

  const coordinators = persisted?.coordinators?.length
    ? persisted.coordinators
    : [AVAILABLE_COORDINATOR_CATALOG[0]!, AVAILABLE_COORDINATOR_CATALOG[1]!];

  const sub = subInvestigators[0] ?? subPrimary;
  const coord = coordinators[0] ?? AVAILABLE_COORDINATOR_CATALOG[0]!;

  const center = isEpoca
    ? AVAILABLE_RESEARCH_CENTERS_CATALOG[0]!
    : isSolair
      ? AVAILABLE_RESEARCH_CENTERS_CATALOG[1]!
      : isPersist
        ? AVAILABLE_RESEARCH_CENTERS_CATALOG[2]!
        : isAdept
          ? AVAILABLE_RESEARCH_CENTERS_CATALOG[3]!
          : AVAILABLE_RESEARCH_CENTERS_CATALOG[2]!;

  const ccRecipients = Array.from(
    new Set([
      pi.email,
      ...subInvestigators.map((s) => s.email),
      ...coordinators.map((c) => c.email),
      'regulatorio@trialshub.com.br',
      'contratos@giyus.com.br',
    ]),
  ).join('; ');

  const subsText = subInvestigators
    .map((s, idx) => `${idx + 1}) ${s.name} (${s.crm} · ${s.specialty} · <${s.email}>)`)
    .join('; ');
  const coordsText = coordinators
    .map((c, idx) => `${idx + 1}) ${c.name} (${c.role} · <${c.email}>)`)
    .join('; ');

  const preambleText = `INSTITUIÇÃO / CENTRO DE PESQUISA ATENDENTE: ${center.legalName} (${center.name}), inscrita no CNPJ sob nº ${center.cnpj}, CNES nº ${center.cnes}, sediada em ${center.address} (Regionalidade dos Procedimentos: ${center.cityUf}), Dados Bancários (Tabela 3): ${center.bankSummary}. INVESTIGADOR PRINCIPAL (PI): ${pi.name} (${pi.crm} · ${pi.specialty} · <${pi.email}>). SUB-INVESTIGADORES (${subInvestigators.length}): ${subsText}. EQUIPE DE COORDENAÇÃO DE PESQUISA (${coordinators.length}): ${coordsText}.`;

  return {
    studyCode: code,
    piName: pi.name,
    piCrm: pi.crm,
    piSpecialty: pi.specialty,
    piEmail: pi.email,
    subInvestigatorName: subInvestigators.map((s) => s.name).join(' & '),
    subInvestigatorCrm: subInvestigators.map((s) => s.crm).join(' / '),
    subInvestigatorSpecialty: sub.specialty,
    subInvestigatorEmail: subInvestigators.map((s) => s.email).join('; '),
    subInvestigators,
    coordinatorName: coordinators.map((c) => c.name).join(' & '),
    coordinatorRole: coord.role,
    coordinatorEmail: coordinators.map((c) => c.email).join('; '),
    coordinators,
    researchCenterName: center.name,
    researchCenterLegalName: center.legalName,
    researchCenterCnpj: center.cnpj,
    researchCenterCnes: center.cnes,
    researchCenterCityUf: center.cityUf,
    researchCenterAddress: center.address,
    researchCenterBankSummary: center.bankSummary,
    isAutoFilled: true,
    explicitlyBoundByUser: false,
    autoFilledFieldsCount: 16,
    autoFilledContractPreambleText: preambleText,
    autoFilledDraftRecipientsTo: toRecipients,
    autoFilledDraftRecipientsCc: ccRecipients,
    lastLinkedAtIso: '2026-10-29T06:45:00Z',
    lastLinkedByEmail: 'syllas@giyus.com.br',
  };
}

function buildMultiDocumentViabilityAnalysis(
  state: FakeSuppliersAndStudiesState | undefined,
  studyCode: string,
): {
  multiDocumentItems: readonly StudyIncomingDocumentAnalysisItemDto[];
  viabilitySummary: StudyMultiDocSponsorViabilityDto;
} {
  const code = studyCode.toUpperCase();
  const isEpoca = code === 'EPOCA';
  const isSolair = code === 'SOLAIR';
  const isPersist = code === 'PERSIST';
  const isAdept = code === 'ADEPT-4';

  const binding = getEffectiveStudyTeamAndCenterBinding(state, code);
  const isBound = binding.isAutoFilled;

  const sponsorName = isEpoca
    ? 'ASSOCIAÇÃO BENEFICENTE SÍRIA — Hcor'
    : isSolair
      ? 'AstraZeneca AB / AstraZeneca do Brasil'
      : isPersist
        ? 'GlaxoSmithKline (GSK)'
        : isAdept
          ? 'Bristol Myers Squibb (BMS)'
          : 'Sanofi-Aventis Farmacêutica Ltda';

  const croName = isEpoca
    ? 'Instituto de Pesquisa Hcor (PROADI-SUS) · Centro Trials Hub Ltda'
    : isSolair
      ? 'Parexel International Brasil (PXL 292941)'
      : isPersist
        ? 'IQVIA RDS Brasil Ltda (TORO Transfer of Regulatory Obligations)'
        : isAdept
          ? 'PPD / Thermo Fisher Scientific'
          : 'ICON Clinical Research / Sanofi CSU';

  const items: StudyIncomingDocumentAnalysisItemDto[] = [
    {
      id: `mdoc-${code.toLowerCase()}-protocolo`,
      studyCode: code,
      documentKind: 'PROTOCOLO',
      documentBadgeLabel: '📋 1. PROTOCOLO CLÍNICO & GRADE SoA',
      fileName: isEpoca
        ? 'Protocolo_Clinico_EPOCA_Fatorial_2x2_Colchicina_Polipilula_v3.pdf'
        : isSolair
          ? '_Protocol Main 2.0 11 May 2026 English D7820C00004.pdf'
          : isPersist
            ? '300742_PERSIST Asthma-1_Protocol_Initial_Final_15Jul2026_pt-BRA.pdf'
            : isAdept
              ? 'BMS-CN0120056_ADEPT-4_Clinical_Protocol_v2.pdf'
              : `Protocolo_Clinico_${code}_Emendado_02.pdf`,
      versionOrDateLabel: isEpoca
        ? 'Versão 3.0 · CAAE 59057422.4.1001.0060'
        : 'Versão 2.0 · Vigente 2026',
      arrivalSourceSummary: `Extraído do pacote documental do estudo ${code} recebido em contratos@giyus.com.br (${binding.researchCenterCityUf || 'cidade/UF não informada'})`,
      extractedObligationsSummary: isEpoca
        ? 'Ensaio fatorial 2x2 de 48 meses de seguimento por participante (D0 a D1440 — 21 marcos), 10 visitas presenciais c/ dispensação de medicação, 9 contatos telefônicos e 2 coletas de exames laboratoriais.'
        : 'Cronograma SoA com visitas de triagem, randomização, espirometria/FeNO ou exames de imagem seriados, coleta farmacocinética e acompanhamento de eventos adversos.',
      crossCheckWithContractAndBudget: isEpoca
        ? 'ALERTA DE VIABILIDADE: O Protocolo exige 48 meses de visitas (D0–D1440) e vigência até 30/06/2032, porém o Contrato omite repactuação anual (CRÍTICO conforme Regra 03 em contratos > 12m) e condiciona a 2ª Etapa (D450–D1440) a e-mail da Hcor (Cláusula 4.7).'
        : 'O Protocolo exige exames seriados e janela de triagem rigorosa; o orçamento deve cobrir 100% dos procedimentos na tabela de Curitiba/PR e taxa de Screen Failure 2:1.',
      viabilityImpactStatus: 'CRITICAL_GAP_DETECTED',
      estimatedFinancialImpactBrl: isEpoca ? 28400 : 34200,
      recommendedClauseOrBudgetAdjustment: isEpoca
        ? 'Exigir Redline na Cláusula 6ª para repactuação anual pelo IPCA/IBGE a cada 12 meses e garantia de pagamento integral dos participantes já randomizados caso a 2ª Etapa seja suspensa.'
        : 'Alinhar todos os procedimentos da SoA com a Tabela Regional de Curitiba/PR e incluir cláusula de reajuste anual.',
    },
    {
      id: `mdoc-${code.toLowerCase()}-tcle`,
      studyCode: code,
      documentKind: 'TCLE',
      documentBadgeLabel: '📝 2. TCLE (TERMO DE CONSENTIMENTO LIVRE E ESCLARECIDO)',
      fileName: isEpoca
        ? 'TCLE_EPOCA_Adulto_e_Representante_Legal_v3_CEP_CONEP.pdf'
        : `TCLE_${code}_Participante_Adulto_e_Cuidador_pt-BR.pdf`,
      versionOrDateLabel: 'Aprovado CEP/CONEP · Resolução CNS 466/2012 & Lei 14.874/2024',
      arrivalSourceSummary: isBound
        ? `Documento ético TCLE vinculado ao estudo ${code} (Aplicação pelo PI ${binding.piName} e Sub ${binding.subInvestigatorName})`
        : `Documento ético TCLE vinculado ao estudo ${code} (aguardando vínculo de PI e Sub-Investigadores)`,
      extractedObligationsSummary:
        'Garante ao participante e seu acompanhante ressarcimento integral de transporte e alimentação em todas as visitas presenciais, assistência médica imediata e gratuita a eventos adversos/complicações e direito a re-consentimento em emendas.',
      crossCheckWithContractAndBudget: isEpoca
        ? 'DISCREPÂNCIA TCLE × ANEXO I: O TCLE promete ressarcimento irrestrito de transporte e alimentação (inclusive acompanhante), mas o Anexo I embute transporte + alimentação + consulta + procedimentos dentro do teto fixo de R$ 250,00/visita presencial (R$ 200 base + R$ 50 overhead).'
        : 'O TCLE obriga reembolso de deslocamento/alimentação do participante + acompanhante e re-consentimento a cada emenda protocolar; exigir rubrica Pass-Through sem Holdback.',
      viabilityImpactStatus: 'NEEDS_REDLINE_ADJUSTMENT',
      estimatedFinancialImpactBrl: isEpoca ? 14800 : 18600,
      recommendedClauseOrBudgetAdjustment:
        'Incluir no Contrato/Anexo I cláusula de Reembolso Pass-Through suplementar para deslocamento/alimentação de participante + acompanhante quando exceder R$ 50,00/visita e rubrica de Re-Consentimento de TCLE por emenda (R$ 480,00).',
    },
    {
      id: `mdoc-${code.toLowerCase()}-tale`,
      studyCode: code,
      documentKind: 'TALE',
      documentBadgeLabel: '🧒 3. TALE (TERMO DE ASSENTIMENTO LIVRE E ESCLARECIDO)',
      fileName: isEpoca
        ? 'TALE_Matriz_Assentimento_e_Verificacao_Faixa_Etaria_EPOCA.pdf'
        : `TALE_${code}_Assentimento_Adolescentes_e_Tutores_pt-BR.pdf`,
      versionOrDateLabel: 'Conformidade Ética CONEP · Assentimento + Responsável Legal',
      arrivalSourceSummary: isBound
        ? `Análise de TALE do pacote regulatório/ético (${code}) pela Coordenação (${binding.coordinatorName})`
        : `Análise de TALE do pacote regulatório/ético (${code}) (aguardando vínculo da Equipe de Coordenação)`,
      extractedObligationsSummary: isEpoca
        ? 'No estudo EPOCA (adultos de alto risco cardiovascular), o TALE é verificado para dispensação formal por faixa etária adulta ou aplicação de Termo de Consentimento de Representante Legal / Cuidador em pacientes com vulnerabilidade funcional.'
        : `No estudo ${code}, participantes elegíveis à faixa de Assentimento exigem aplicação simultânea de TALE (assentiente) + TCLE (pais/responsável legal), demandando 90 minutos de equipe clínica e reembolso dobrado de transporte/refeição (participante + responsável).`,
      crossCheckWithContractAndBudget: isEpoca
        ? 'Validado: Quando houver cuidador/representante legal assinando junto ao participante vulnerável, garantir que o custo de alimentação/transporte do acompanhante não corroa os R$ 200,00 líquidos da visita clínica.'
        : 'GAP DETECTADO: A planilha da CRO orçou apenas 1 aplicação simples de consentimento, omitindo o tempo adicional de aplicação conjunta TCLE + TALE e a diária de alimentação/transporte obrigatória do responsável legal.',
      viabilityImpactStatus: isEpoca ? 'ALIGNED_VIABLE' : 'NEEDS_REDLINE_ADJUSTMENT',
      estimatedFinancialImpactBrl: isEpoca ? 4500 : 11200,
      recommendedClauseOrBudgetAdjustment:
        'Garantir cobertura de 100% da despesa de acompanhante/responsável legal exigida pelo TALE/TCLE via Pass-Through e remunerar a aplicação dupla TCLE + TALE na Visita V1.',
    },
    {
      id: `mdoc-${code.toLowerCase()}-contrato`,
      studyCode: code,
      documentKind: 'CONTRATO_CTA',
      documentBadgeLabel: '⚖️ 4. CONTRATO / MINUTA CTA (PREENCHIMENTO AUTOMÁTICO)',
      fileName: isEpoca
        ? 'MINUTA_EPOCA_2026_TrialsHub.docx'
        : isSolair
          ? 'GIYUS_SOLAIR_Contrato_Conciliado_R04.docx'
          : isPersist
            ? 'GSK_300742_BRA_CTA_297000_Dr. Chong-Silva_Draft 1_IQVIA to Site_28Sept2026.docx'
            : `Minuta_CTA_${code}_Draft_toSite.docx`,
      versionOrDateLabel: 'Minuta CTA em Negociação · Controle de Alterações',
      arrivalSourceSummary: isBound
        ? `Recebido em contratos@giyus.com.br · Auto-preenchido com ${binding.researchCenterName}, PI ${binding.piName}, Sub ${binding.subInvestigatorName} e Coord. ${binding.coordinatorName}`
        : 'Recebido em contratos@giyus.com.br · Aguardando cadastro e vínculo de Centro, PI, Sub-Investigadores e Coordenação para auto-preenchimento',
      extractedObligationsSummary: isBound
        ? isEpoca
          ? `Campos em branco na minuta original (RAZÃO SOCIAL, CNPJ XX.XXX.XXX/XXXX-XX, PI e Tabela 3) JÁ PREENCHIDOS AUTOMATICAMENTE com ${binding.researchCenterLegalName} (CNPJ ${binding.researchCenterCnpj}), PI ${binding.piName} (${binding.piCrm}), Sub ${binding.subInvestigatorName} (${binding.subInvestigatorCrm}) e Coordenação ${binding.coordinatorName}.`
          : `Qualificação do Centro ${binding.researchCenterLegalName} (CNPJ ${binding.researchCenterCnpj} · ${binding.researchCenterCityUf}), PI ${binding.piName}, Sub ${binding.subInvestigatorName} e Coordenação ${binding.coordinatorName} preenchida automaticamente.`
        : 'Campos cadastrais na minuta original (RAZÃO SOCIAL, CNPJ XX.XXX.XXX/XXXX-XX, Investigador Principal e Dados Bancários na Tabela 3) encontram-se em branco aguardando o cadastro e vínculo de PI, Sub-Investigadores (1..N), Equipe de Coordenação (1..N) e Centro de Pesquisa.',
      crossCheckWithContractAndBudget: isEpoca
        ? isBound
          ? '16 campos cadastrais/bancários auto-preenchidos + Redline da Cláusula 6ª (Repactuação Anual IPCA em contrato longo de 48m) + Correção material no Pagamento 13 (Visita 7: de DIA 720 para DIA 900).'
          : 'Pendente de auto-preenchimento cadastral (vincular Equipe e Centro) + Redline da Cláusula 6ª (Repactuação Anual IPCA em contrato longo de 48m) + Correção material no Pagamento 13 (Visita 7: de DIA 720 para DIA 900).'
        : isBound
          ? 'Qualificação jurídica/bancária 100% preenchida; manter prazo Net 30 DDL e proteção inflacionária/cambial.'
          : 'Aguardando vínculo da equipe e centro para qualificação jurídica/bancária; manter prazo Net 30 DDL e proteção inflacionária/cambial.',
      viabilityImpactStatus: 'NEEDS_REDLINE_ADJUSTMENT',
      estimatedFinancialImpactBrl: isEpoca ? 19200 : 24500,
      recommendedClauseOrBudgetAdjustment:
        'Enviar minuta devolvida com os campos do Centro, PI, Sub e Coordenação preenchidos automaticamente e Redline das cláusulas financeiras.',
    },
    {
      id: `mdoc-${code.toLowerCase()}-orcamento`,
      studyCode: code,
      documentKind: 'ORCAMENTO_ANEXO',
      documentBadgeLabel: '💰 5. ORÇAMENTO / ANEXO I (GRADE FINANCEIRA)',
      fileName: isEpoca
        ? 'Anexo_I_Orcamento_EPOCA_TrialsHub_21_Pagamentos.docx'
        : `Budget_Grid_${code}_Curitiba_PR.xlsx`,
      versionOrDateLabel: `Regionalidade do Centro Atendente: ${binding.researchCenterCityUf || 'cidade/UF não informada'}`,
      arrivalSourceSummary: isBound
        ? `Tabela Orçamentária vinculada ao Centro ${binding.researchCenterName} (${binding.researchCenterCityUf})`
        : `Tabela Orçamentária extraída do Anexo I (${binding.researchCenterCityUf || 'cidade/UF não informada'})`,
      extractedObligationsSummary: isEpoca
        ? 'Taxa Única de Startup R$ 3.000,00 + 1ª Etapa R$ 3.000,00/part. (D0–D360) + 2ª Etapa R$ 2.900,00/part. (D450–D1440) = R$ 5.900,00/participante (Overhead 25% incluso; soma unitária = R$ 8.900,00).'
        : `Grade orçamentária precificada para o Centro Atendente em ${binding.researchCenterCityUf || 'cidade/UF não informada'} com procedimentos normalizados e Pass-Through.`,
      crossCheckWithContractAndBudget:
        'Cruzado com Protocolo, TCLE e TALE: viabilidade financeira assegurada mediante inclusão de repactuação anual e proteção de custos de acompanhante/transporte exigidos no TCLE/TALE.',
      viabilityImpactStatus: 'ALIGNED_VIABLE',
      estimatedFinancialImpactBrl: isEpoca ? 8900 : 462800,
      recommendedClauseOrBudgetAdjustment:
        'Consolidar todos os itens no Draft de E-mail de Resposta para envio ao Patrocinador/CRO.',
    },
  ];

  const viabilitySummary: StudyMultiDocSponsorViabilityDto = {
    studyCode: code,
    sponsorName,
    croName,
    documentsAnalyzedCount: items.length,
    documentKindsPresent: ['PROTOCOLO', 'TCLE', 'TALE', 'CONTRATO_CTA', 'ORCAMENTO_ANEXO'],
    viabilityScorePercent: isEpoca ? 86 : 89,
    viabilityVerdict: 'VIABLE_WITH_REDLINES',
    viabilityHeadline: isBound
      ? `Viabilidade Multi-Documento (${code}): VIÁVEL COM REDLINES E AUTO-PREENCHIMENTO CONCLUÍDO (${items.length} documentos cruzados: Protocolo + TCLE + TALE + Contrato + Orçamento)`
      : `Viabilidade Multi-Documento (${code}): VIÁVEL COM REDLINES — AGUARDANDO VÍNCULO DE EQUIPE E CENTRO (${items.length} documentos cruzados: Protocolo + TCLE + TALE + Contrato + Orçamento)`,
    protocolVsBudgetSynthesis: isEpoca
      ? 'O Protocolo exige 48 meses de acompanhamento (D0–D1440) e vigência até 30/06/2032. Pela Regra 03, em contrato longo (> 12 meses) é obrigatória a cláusula de repactuação anual pelo IPCA e garantia de pagamento das visitas realizadas antes de qualquer suspensão da 2ª Etapa (Cláusula 4.7).'
      : `O Protocolo do estudo ${code} exige procedimentos de alta densidade clínica em ${binding.researchCenterCityUf || 'cidade/UF não informada'}; todos os procedimentos seguem a regionalidade de Curitiba/PR com suporte multi-localidade.`,
    tcleAndTaleEthicalFinancialSynthesis:
      'O cruzamento do TCLE e do TALE com o Contrato revelou que as obrigações éticas de ressarcimento integral de transporte/alimentação (participante + acompanhante/responsável legal) e re-consentimento em emendas precisam de proteção Pass-Through para não corroer a margem clínica da visita.',
    contractVsDurationSynthesis: isBound
      ? `Os campos cadastrais e bancários antes em branco na minuta foram 100% auto-preenchidos com o Centro ${binding.researchCenterLegalName} (CNPJ ${binding.researchCenterCnpj} · ${binding.researchCenterCityUf}), PI ${binding.piName} (${binding.piCrm}), Sub ${binding.subInvestigatorName} (${binding.subInvestigatorCrm}) e Coordenação ${binding.coordinatorName}.`
      : 'Os campos cadastrais e bancários da minuta (RAZÃO SOCIAL, CNPJ XX.XXX.XXX/XXXX-XX, PI, Sub-Investigadores, Coordenação e Tabela 3) estão prontos para auto-preenchimento assim que o usuário cadastrar e relacionar a equipe e o centro ao estudo.',
    actionableCounterproposalBullets: [
      isBound
        ? `1. Qualificação Completa Auto-Preenchida: ${binding.researchCenterLegalName} (CNPJ ${binding.researchCenterCnpj} · CNES ${binding.researchCenterCnes} · ${binding.researchCenterCityUf} · ${binding.researchCenterBankSummary}), PI ${binding.piName} (${binding.piCrm}), Sub ${binding.subInvestigatorName} (${binding.subInvestigatorCrm}) e Coordenação ${binding.coordinatorName} (<${binding.coordinatorEmail}>).`
        : '1. Qualificação Cadastral e Equipe (Regra 09): Cadastre e relacione o PI, Sub-Investigadores (1..N), Equipe de Coordenação (1..N) e Centro de Pesquisa para preencher automaticamente 100% da minuta e do Draft de E-mail.',
      '2. Viabilidade Protocolo × Contrato (Regra 03): Inclusão de Repactuação Anual pelo IPCA/IBGE a cada 12 meses (indispensável em contratos > 12 meses) e correção material no Pagamento 13 (Visita 7: de DIA 720 para DIA 900).',
      '3. Viabilidade TCLE & TALE × Orçamento: Reembolso Pass-Through suplementar para transporte/alimentação de participante e acompanhante/responsável legal exigidos no TCLE/TALE + rubrica para re-aplicação de TCLE/TALE em emendas.',
      '4. Aprendizado Aplicado (CRO + Patrocinador + Negociação Global): Argumentação técnica fundamentada na Resolução CNS 466/2012, Lei 14.874/2024 e equilíbrio econômico-financeiro plurianual.',
    ],
  };

  return { multiDocumentItems: items, viabilitySummary };
}

function getEffectiveCroSponsorGlobalLearnings(
  state: FakeSuppliersAndStudiesState | undefined,
  studyCode?: string,
): CroSponsorAndGlobalNegotiationLearningDto[] {
  const baseLearnings: CroSponsorAndGlobalNegotiationLearningDto[] = [
    {
      id: 'learn-epoca-hcor-trialshub',
      studyCode: 'EPOCA',
      croName: 'Instituto de Pesquisa Hcor (Coordenador Multicêntrico) / Trials Hub Ltda',
      croBehaviorPatternLearned:
        'Envia minuta padrão com campos cadastrais/bancários em branco para preenchimento em controle de alterações via Adobe Sign, recrutamento competitivo (Cláusula 4.5) e 2ª Etapa condicionada a notificação por e-mail (Cláusula 4.7).',
      croConcessionLeversLearned:
        'Aceita rapidamente o preenchimento cadastral completo em controle de alterações, a correção de erro material de cronograma (Pagamento 13: DIA 720 → DIA 900) e ressalva de proteção inflacionária/transporte quando fundamentada no TCLE e na duração de 48 meses (2032).',
      sponsorName: 'ASSOCIAÇÃO BENEFICENTE SÍRIA — Hcor',
      sponsorBudgetPolicyLearned:
        'Trabalha com valores unitários com Overhead de 25% já embutido (Startup R$ 3.000 + 1ª Etapa R$ 3.000 + 2ª Etapa R$ 2.900 = R$ 5.900/participante) e pagamento em 30 dias após NF enviada para fiscal@hcor.com.br (Cláusula 6.5).',
      sponsorProtocolTcleTaleFlexibilityLearned:
        'Quando demonstrado o cruzamento entre Protocolo (48 meses D0–D1440) + TCLE/TALE (obrigação ética de transporte/alimentação de participante e acompanhante), acolhe aditivo/ressalva de reequilíbrio financeiro e Pass-Through extraordinário.',
      globalContractNegotiationLesson:
        'PLAYBOOK GLOBAL #1 (Contratos Plurianuais > 12m c/ TCLE/TALE): Sempre cruzar Protocolo + TCLE + TALE + Contrato antes de responder; devolver a minuta já 100% preenchida com PI, Sub, Coordenação e Centro (CNPJ/Banco), exigir repactuação anual IPCA em contratos > 12 meses e blindar despesas de transporte/acompanhante exigidas no TCLE/TALE.',
      obeyedByLlms: [
        'OpenAI GPT-5 / o3-pro',
        'Anthropic Claude 4 Opus / Sonnet',
        'Google DeepMind Gemini 2.5 Pro',
        'Google Research Harness² (c1..c8)',
      ],
      promotedGlobalRuleId: 'grule-prod-010',
      updatedAtIso: '2026-10-29T06:50:00Z',
    },
    {
      id: 'learn-solair-parexel-az',
      studyCode: 'SOLAIR',
      croName: 'Parexel International Brasil (PXL 292941)',
      croBehaviorPatternLearned:
        'Tenta impor prazo padrão de pagamento de 45 dias (Net 45) e questiona edições contratuais sem justificativa clínica/operacional detalhada por item.',
      croConcessionLeversLearned:
        'Cede na tabela de procedimentos e em cláusulas de proteção quando cada alteração vem acompanhada da justificativa técnica cruzando o Protocolo (Espirometria Seriada + FeNO), o TCLE/TALE e o custo real da localidade (Curitiba/PR) avalizado pelo PI.',
      sponsorName: 'AstraZeneca AB / AstraZeneca do Brasil',
      sponsorBudgetPolicyLearned:
        'Aprova ajustes de taxa de procedimentos, condicionais e Pass-Through de exames de imagem/kits quando o centro comprova a exigência literal na nota de rodapé da SoA.',
      sponsorProtocolTcleTaleFlexibilityLearned:
        'Reconhece o custo adicional de aplicação conjunta TCLE + TALE e reembolso integral de deslocamento/alimentação sem retenção de Holdback.',
      globalContractNegotiationLesson:
        'PLAYBOOK GLOBAL #2 (Justificativa Item-a-Item p/ CROs Globais): Nunca enviar redline sem justificativa; vincular cada pedido de reajuste ou cláusula diretamente ao item correspondente do Protocolo, TCLE ou TALE e incluir o PI, Sub e Coordenação em cópia no draft.',
      obeyedByLlms: [
        'OpenAI GPT-5 / o3-pro',
        'Anthropic Claude 4 Opus / Sonnet',
        'Google DeepMind Gemini 2.5 Pro',
        'Google Research Harness² (c1..c8)',
      ],
      promotedGlobalRuleId: 'grule-prod-010',
      updatedAtIso: '2026-10-29T06:50:00Z',
    },
    {
      id: 'learn-persist-iqvia-gsk',
      studyCode: 'PERSIST',
      croName: 'IQVIA RDS Brasil Ltda (TORO)',
      croBehaviorPatternLearned:
        'Envia pacote completo (Protocolo PT/EN + TORO + CTA Draft 1 + Budget XLSM) com insumos de alto custo (bocais FeNO) inicialmente subdimensionados na grade principal.',
      croConcessionLeversLearned:
        'Aceita formalizar 1º Aditivo Contratual dedicado de Pass-Through de insumos (bocais FeNO NIOX VERO) e cobertura de TCLE/TALE quando apontado o cruzamento entre Protocolo e orçamento.',
      sponsorName: 'GlaxoSmithKline Brasil Ltda (GSK)',
      sponsorBudgetPolicyLearned:
        'Trabalha com orçamento em BRL e aceita recomposição de insumos respiratórios e Screen Failure 2:1 quando a meta do centro é restrita.',
      sponsorProtocolTcleTaleFlexibilityLearned:
        'Flexível para reembolsar 100% de re-consentimento de TCLE/TALE em emendas protocolares e despesas de acompanhante.',
      globalContractNegotiationLesson:
        'PLAYBOOK GLOBAL #3 (Insumos Especiais & TORO): Ao receber Protocolo + TORO + TCLE/TALE + Budget XLSM, isolar consumíveis exclusivos (ex.: FeNO, kits criogênicos) em cláusula Pass-Through 100% reembolsável sem Holdback.',
      obeyedByLlms: [
        'OpenAI GPT-5 / o3-pro',
        'Anthropic Claude 4 Opus / Sonnet',
        'Google DeepMind Gemini 2.5 Pro',
        'Google Research Harness² (c1..c8)',
      ],
      promotedGlobalRuleId: 'grule-prod-010',
      updatedAtIso: '2026-10-29T06:50:00Z',
    },
  ];

  const custom = state?.croSponsorGlobalLearnings ?? [];
  const merged = [...custom];
  for (const item of baseLearnings) {
    if (!merged.some((m) => m.id === item.id)) {
      merged.push(item);
    }
  }
  if (!studyCode) {
    return merged;
  }
  const code = studyCode.toUpperCase();
  const matching = merged.filter((m) => m.studyCode.toUpperCase() === code);
  return matching.length > 0 ? matching : merged;
}

function buildInitialBudgetEmailDraft(
  dossier: StudyContractDossierItem | undefined,
  studyCode: string,
  createdByEmail: string,
  mailboxAddress: string,
  seq: number,
  state?: FakeSuppliersAndStudiesState,
): BudgetEmailDraftItemDto {
  const code = studyCode.toUpperCase();
  const nowIso = new Date().toISOString();
  const isEpoca = code === 'EPOCA';
  const binding = getEffectiveStudyTeamAndCenterBinding(state, code);
  const isBound = binding.isAutoFilled;
  const { viabilitySummary } = buildMultiDocumentViabilityAnalysis(state, code);
  const learnings = getEffectiveCroSponsorGlobalLearnings(state, code);
  const primaryLearning = learnings[0];

  const originalTo = binding.autoFilledDraftRecipientsTo;
  const originalCc = binding.autoFilledDraftRecipientsCc || mailboxAddress;
  const originalSubject = isEpoca
    ? isBound
      ? `RE: Contrato Estudo EPOCA: ${binding.researchCenterName} — Qualificação Preenchida (PI ${binding.piName}), Viabilidade Multi-Documento (Protocolo/TCLE/TALE/CTA) e Devolutiva do Anexo I`
      : 'RE: Contrato Estudo EPOCA — Análise de Viabilidade Multi-Documento (Protocolo/TCLE/TALE/CTA) e Devolutiva do Anexo I'
    : isBound
      ? `RE: Proposta Orçamentária & Viabilidade Multi-Documento — Estudo ${code} (${binding.researchCenterName} · PI ${binding.piName})`
      : `RE: Proposta Orçamentária & Viabilidade Multi-Documento — Estudo ${code}`;

  const qualificationSection = isBound
    ? [
        '=== A. QUALIFICAÇÃO AUTOMÁTICA DO CENTRO DE PESQUISA, PI, SUB E COORDENAÇÃO (PREENCHIDA NA MINUTA E TABELA 3) ===',
        `• Centro de Pesquisa Atendente: ${binding.researchCenterLegalName} (${binding.researchCenterName})`,
        `• CNPJ: ${binding.researchCenterCnpj} | CNES: ${binding.researchCenterCnes} | Regionalidade: ${binding.researchCenterCityUf}`,
        `• Endereço: ${binding.researchCenterAddress}`,
        `• Dados Bancários (Tabela 3 Anexo I): ${binding.researchCenterBankSummary}`,
        `• Investigador Principal (PI): ${binding.piName} (${binding.piCrm} · ${binding.piSpecialty} · ${binding.piEmail})`,
        `• Sub-Investigadores (${binding.subInvestigators?.length ?? 1}): ${
          binding.subInvestigators && binding.subInvestigators.length > 0
            ? binding.subInvestigators
                .map((s, idx) => `${idx + 1}) ${s.name} (${s.crm} · ${s.specialty} · ${s.email})`)
                .join(' | ')
            : `${binding.subInvestigatorName} (${binding.subInvestigatorCrm} · ${binding.subInvestigatorSpecialty} · ${binding.subInvestigatorEmail})`
        }`,
        `• Equipe de Coordenação do Estudo (${binding.coordinators?.length ?? 1}): ${
          binding.coordinators && binding.coordinators.length > 0
            ? binding.coordinators
                .map((c, idx) => `${idx + 1}) ${c.name} (${c.role} · ${c.email})`)
                .join(' | ')
            : `${binding.coordinatorName} (${binding.coordinatorRole} · ${binding.coordinatorEmail})`
        }`,
      ]
    : [
        '=== A. QUALIFICAÇÃO DO CENTRO DE PESQUISA, PI, SUB-INVESTIGADORES E COORDENAÇÃO ===',
        '• [Aguardando Vínculo de Equipe e Centro]: Cadastre e relacione o Investigador Principal (PI), Sub-Investigadores (1..N), Equipe de Coordenação (1..N) e Centro de Pesquisa para preencher automaticamente Razão Social, CNPJ, CNES, Dados Bancários (Tabela 3) e equipe clínica.',
      ];

  const signatureSection = isBound
    ? [
        'Atenciosamente,',
        `${binding.coordinatorName} — ${binding.coordinatorRole} (${binding.coordinatorEmail})`,
        `${binding.piName} (${binding.piCrm}) & ${binding.subInvestigatorName} (${binding.subInvestigatorCrm})`,
        `${binding.researchCenterLegalName} (CNPJ ${binding.researchCenterCnpj} · ${binding.researchCenterCityUf})`,
        `Gestão de Contratos e Orçamentos Clínicos (${mailboxAddress})`,
      ]
    : ['Atenciosamente,', `Gestão de Contratos e Orçamentos Clínicos (${mailboxAddress})`];

  const originalBody = isEpoca
    ? [
        'Prezada Tamiris Abait Miranda (Instituto de Pesquisa Hcor) e Equipe Pesquisa Clínica TrialsHub,',
        '',
        'Referente ao estudo EPOCA (CAAE 59057422.4.1001.0060 — MINUTA_EPOCA_2026_TrialsHub.docx), encaminhamos a consolidação da análise orçamentária, de viabilidade multi-documento (Protocolo + TCLE + TALE + Contrato) e a devolutiva da minuta:',
        '',
        ...qualificationSection,
        '',
        '=== B. CONSOLIDAÇÃO FINANCEIRA DO ANEXO I E CONDIÇÕES DO CONTRATO ===',
        '1. Taxa Única de Startup: R$ 2.400,00 + R$ 600,00 (Overhead 25%) = R$ 3.000,00 (pós-aprovação regulatória, assinatura do contrato e realização da SIV).',
        '2. 1ª Etapa de Execução (Pagamentos 1 a 7 — D0 a D360): R$ 2.400,00 + R$ 600,00 (Overhead 25%) = R$ 3.000,00 por participante.',
        '3. 2ª Etapa de Execução (Pagamentos 8 a 21 — D450 a D1440, condicionada à notificação formal por e-mail da Patrocinadora — Cláusula 4.7): R$ 2.320,00 + R$ 580,00 (Overhead 25%) = R$ 2.900,00 por participante.',
        '4. Valor Total por Participante Completo (21 Pagamentos): R$ 4.720,00 + R$ 1.180,00 (Overhead 25%) = R$ 5.900,00 por participante (Recrutamento Competitivo — Cláusula 4.5).',
        '5. Condições de Faturamento (Cláusula 6.5): Pagamento em 30 (trinta) dias após recebimento da Nota Fiscal válida enviada para fiscal@hcor.com.br.',
        '6. Ajustes na Minuta em Controle de Alterações: Preenchimento dos campos cadastrais em branco do Centro de Pesquisa TrialsHub (Razão Social, CNPJ, Investigador Principal e Dados Bancários na Tabela 3) e correção material no Pagamento 13 (Visita 7: de DIA 720 para DIA 900).',
        '7. Cláusula de Repactuação e Reequilíbrio Econômico-Financeiro Anual (Crítico em Contrato Longo — 48 Meses D0–D1440 / Vigência até 30/06/2032): Inclusão na Cláusula 6ª de previsão de repactuação anual pelo IPCA/IBGE a cada 12 meses e garantia de reequilíbrio econômico-financeiro durante os anos de vigência (diretriz GIYUS: aceitável dispensar em contratos curtos <= 12 meses, porém indispensável em contratos longos).',
        '8. Viabilidade Multi-Documento (Cruzamento Protocolo + TCLE + TALE + Contrato): Ressalva de reembolso Pass-Through suplementar para despesas de transporte/alimentação de participante e acompanhante/responsável legal garantidas no TCLE/TALE quando excederem o valor base da visita, além de cobertura de re-consentimento em emendas.',
        ...(primaryLearning
          ? [
              `9. Diretriz Estratégica de Negociação (Aprendizado CRO/Patrocinador): ${primaryLearning.globalContractNegotiationLesson}`,
            ]
          : []),
        '',
        ...signatureSection,
      ].join('\n')
    : [
        `Prezados (${dossier?.sponsorName ?? viabilitySummary.sponsorName} / ${dossier?.croName ?? viabilitySummary.croName}),`,
        '',
        `Encaminhamos a proposta orçamentária, a qualificação cadastral e o parecer de viabilidade multi-documento (Protocolo + TCLE + TALE + Contrato) referentes ao estudo ${code} (${dossier?.title ?? code}):`,
        '',
        ...qualificationSection,
        '',
        '=== B. RESUMO ORÇAMENTÁRIO & VIABILIDADE MULTI-DOCUMENTO (PROTOCOLO, TCLE, TALE E CONTRATO) ===',
        `- Baseline: R$ ${(dossier?.totalBaselineBrl ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        `- Valor Negociado: R$ ${(dossier?.totalNegotiatedBrl ?? 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
        `- Overhead Administrativo: ${dossier?.overheadPercent ?? 30}%`,
        `- Parecer Multi-Documento (${viabilitySummary.documentsAnalyzedCount} documentos): ${viabilitySummary.tcleAndTaleEthicalFinancialSynthesis}`,
        ...(primaryLearning
          ? [
              `- Diretriz de Negociação Aprendida (CRO/Patrocinador): ${primaryLearning.globalContractNegotiationLesson}`,
            ]
          : []),
        '',
        ...signatureSection,
      ].join('\n');

  return {
    id: `draft-${code.toLowerCase()}-${String(seq).padStart(3, '0')}`,
    graphDraftMessageId: `AAMkADg0YzE-DRAFT-${code}-${seq}`,
    studyCode: code,
    dossierId: dossier?.id ?? `dossier-${code.toLowerCase()}`,
    mailboxAddress,
    status: 'ORIGINAL_DRAFT',
    createdAtIso: nowIso,
    createdByEmail,
    updatedAtIso: nowIso,
    updatedByEmail: createdByEmail,
    originalTo,
    originalCc,
    originalSubject,
    originalBody,
    currentTo: originalTo,
    currentCc: originalCc,
    currentSubject: originalSubject,
    currentBody: originalBody,
    isModifiedFromOriginal: false,
    latestChanges: [],
    revisions: [],
  };
}

function buildStudyTeamMultiDocAndLearningDashboard(
  state: FakeSuppliersAndStudiesState,
  studyCode: string,
): StudyTeamMultiDocAndLearningDashboardDto {
  const code = studyCode.toUpperCase();
  const binding = getEffectiveStudyTeamAndCenterBinding(state, code);
  const { multiDocumentItems, viabilitySummary } = buildMultiDocumentViabilityAnalysis(state, code);
  const learnings = getEffectiveCroSponsorGlobalLearnings(state, code);
  const dossier = state.dossiers.find((d) => d.code.toUpperCase() === code);
  const existingDraft = state.budgetEmailDrafts.find((d) => d.studyCode.toUpperCase() === code);
  const autoPopulatedDraftPreview =
    existingDraft ??
    buildInitialBudgetEmailDraft(
      dossier,
      code,
      'syllas@giyus.com.br',
      'contratos@giyus.com.br',
      state.budgetEmailDrafts.length + 1,
      state,
    );

  const isGreenfield = state.isGreenfield !== false;

  return {
    studyCode: code,
    teamAndCenterBinding: binding,
    availablePiCatalog: isGreenfield
      ? (state.registeredPis ?? [])
      : (state.registeredPis ?? AVAILABLE_PI_CATALOG),
    availableSubCatalog: isGreenfield
      ? (state.registeredSubs ?? [])
      : (state.registeredSubs ?? AVAILABLE_SUB_CATALOG),
    availableCoordinatorCatalog: isGreenfield
      ? (state.registeredCoordinators ?? [])
      : (state.registeredCoordinators ?? AVAILABLE_COORDINATOR_CATALOG),
    availableResearchCentersCatalog: isGreenfield
      ? (state.registeredResearchCenters ?? [])
      : (state.registeredResearchCenters ?? AVAILABLE_RESEARCH_CENTERS_CATALOG),
    availableSpecialtiesCatalog: state.clinicalSpecialties ?? DEFAULT_CLINICAL_SPECIALTIES_CATALOG,
    availableHealthFunctionsCatalog:
      state.clinicalHealthFunctions ?? DEFAULT_CLINICAL_HEALTH_FUNCTIONS_CATALOG,
    multiDocumentItems,
    viabilitySummary,
    croSponsorGlobalLearnings: learnings,
    autoPopulatedDraftPreview,
  };
}

function buildAutoFilledContractClausesJson(
  rawClausesJson: string,
  binding: StudyTeamAndCenterBindingDto,
): string {
  try {
    const parsed = JSON.parse(rawClausesJson || '[]') as {
      clause?: string;
      status?: string;
      riskLevel?: string;
      analysis?: string;
    }[];
    if (!Array.isArray(parsed) || parsed.length === 0) {
      return JSON.stringify([
        {
          clause: 'Preâmbulo & Qualificação das Partes (Campos Destacados na Minuta DOCX)',
          status: '100% Auto-Preenchido [REGRA 09]',
          riskLevel: 'Baixo',
          analysis: `[100% AUTO-PREENCHIDO — REGRA 09] ${binding.autoFilledContractPreambleText} — Todos os campos cadastrais do Centro de Pesquisa TrialsHub / ${binding.researchCenterName} (CNPJ ${binding.researchCenterCnpj}, CNES ${binding.researchCenterCnes}, PI ${binding.piName}, Sub ${binding.subInvestigatorName}, Coordenação ${binding.coordinatorName} e Dados Bancários ${binding.researchCenterBankSummary}) foram preenchidos automaticamente.`,
        },
      ]);
    }
    const updated = parsed.map((item, idx) => {
      const clauseTitle = item.clause ?? '';
      if (idx === 0 || /Preâmbulo|Qualificação das Partes/i.test(clauseTitle)) {
        return {
          ...item,
          clause:
            clauseTitle || 'Preâmbulo & Qualificação das Partes (Campos Destacados na Minuta DOCX)',
          status: '100% Auto-Preenchido [REGRA 09]',
          riskLevel: 'Baixo',
          analysis: `[100% AUTO-PREENCHIDO — REGRA 09] ${binding.autoFilledContractPreambleText} — Substituídos automaticamente os campos antes em branco na minuta MINUTA_EPOCA_2026_TrialsHub.docx pelo Centro de Pesquisa TrialsHub / ${binding.researchCenterName} (CNPJ ${binding.researchCenterCnpj}, CNES ${binding.researchCenterCnes}, Regionalidade ${binding.researchCenterCityUf}, PI ${binding.piName}, Sub ${binding.subInvestigatorName}, Coordenação ${binding.coordinatorName} e Dados Bancários Tabela 3: ${binding.researchCenterBankSummary}).`,
        };
      }
      return item;
    });
    return JSON.stringify(updated);
  } catch {
    return rawClausesJson;
  }
}

export function upgradeAndEnforceCanonV11OnState(state: FakeSuppliersAndStudiesState): void {
  state.globalRules = mergeCanonicalGlobalRules(state.globalRules);
  state.harness2State = mergeCanonicalHarness2State(state.harness2State, state.globalRules);

  if (!state.studyTeamBindings) {
    state.studyTeamBindings = {};
  }
  const isGreenfield = state.isGreenfield !== false;

  if (isGreenfield) {
    state.registeredPis = state.registeredPis ?? [];
    state.registeredSubs = state.registeredSubs ?? [];
    state.registeredCoordinators = state.registeredCoordinators ?? [];
    state.registeredResearchCenters = state.registeredResearchCenters ?? [];

    // Remove qualquer studyTeamBinding que tenha sido pré-preenchido sem ação explícita do usuário em produção
    for (const [code, existing] of Object.entries(state.studyTeamBindings)) {
      if (!existing.explicitlyBoundByUser) {
        delete state.studyTeamBindings[code];
      } else {
        if (
          existing.piName &&
          !state.registeredPis.some((p) => p.name.toLowerCase() === existing.piName.toLowerCase())
        ) {
          state.registeredPis.push({
            name: existing.piName,
            crm: existing.piCrm,
            specialty: existing.piSpecialty,
            email: existing.piEmail,
          });
        }
        for (const s of existing.subInvestigators ?? []) {
          if (
            s.name &&
            !state.registeredSubs.some((p) => p.name.toLowerCase() === s.name.toLowerCase())
          ) {
            state.registeredSubs.push(s);
          }
        }
        for (const c of existing.coordinators ?? []) {
          if (
            c.name &&
            !state.registeredCoordinators.some((p) => p.name.toLowerCase() === c.name.toLowerCase())
          ) {
            state.registeredCoordinators.push(c);
          }
        }
        if (
          existing.researchCenterName &&
          !state.registeredResearchCenters.some(
            (r) => r.name.toLowerCase() === existing.researchCenterName.toLowerCase(),
          )
        ) {
          const cls = resolveCenterClassification(
            existing.researchCenterClassification,
            existing.researchCenterName,
          );
          state.registeredResearchCenters.push({
            name: existing.researchCenterName,
            legalName: existing.researchCenterLegalName,
            cnpj: existing.researchCenterCnpj,
            cnes: existing.researchCenterCnes,
            cityUf: existing.researchCenterCityUf,
            address: existing.researchCenterAddress,
            bankSummary: existing.researchCenterBankSummary,
            classification: cls,
            classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS[cls].label,
            paymentFlowRule: RESEARCH_CENTER_CLASSIFICATIONS[cls].paymentAndGovernanceRule,
          });
        }
      }
    }

    // Normaliza as 4 classificações e garante unicidade da GIYUS Holding (só pode ser 1) [REGRA 12]
    let holdingSeen = false;
    state.registeredResearchCenters = state.registeredResearchCenters.map((r) => {
      let cls = resolveCenterClassification(r.classification, r.name);
      if (cls === 'GIYUS_HOLDING') {
        if (holdingSeen) {
          cls = 'CENTRO_PARCEIRO_GIYUS';
        } else {
          holdingSeen = true;
        }
      }
      return {
        ...r,
        classification: cls,
        classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS[cls].label,
        paymentFlowRule: RESEARCH_CENTER_CLASSIFICATIONS[cls].paymentAndGovernanceRule,
      };
    });
  } else {
    const codesToBind = new Set<string>(['EPOCA', 'SOLAIR', 'PERSIST']);
    for (const d of state.dossiers) {
      codesToBind.add(d.code.toUpperCase());
    }
    for (const code of codesToBind) {
      const existing = state.studyTeamBindings[code];
      if (!existing?.subInvestigators || !existing.coordinators) {
        state.studyTeamBindings[code] = getEffectiveStudyTeamAndCenterBinding(state, code);
      }
    }
  }

  state.dossiers = state.dossiers.map((d) => {
    const code = d.code.toUpperCase();
    const binding = getEffectiveStudyTeamAndCenterBinding(state, code);
    const isEpoca = code === 'EPOCA';

    if (binding.isAutoFilled) {
      return {
        ...d,
        croName:
          d.croName && d.croName.includes(binding.piName)
            ? d.croName
            : `${binding.researchCenterLegalName} (CNPJ ${binding.researchCenterCnpj} · ${binding.researchCenterCityUf} · PI ${binding.piName} · Sub ${binding.subInvestigatorName} · Coord. ${binding.coordinatorName})`,
        clinicalAnalysisJson: JSON.stringify({
          complexity: `${
            isEpoca
              ? 'Ensaio Clínico Randomizado Fatorial 2 x 2 (Colchicina + Polipílula · CAAE 59057422.4.1001.0060) · Anexo I: 21 Pagamentos (D0 a D1440 — 48 meses) + Taxa Única de Startup R$ 3.000,00 · Valores Unitários: R$ 5.900,00/participante (Overhead 25% incluso) · Recrutamento Competitivo (Cláusula 4.5 — até 10.797 no estudo geral) · '
              : ''
          }Auto-Preenchido [REGRA 09]: Centro de Pesquisa TrialsHub / ${binding.researchCenterName} (CNPJ ${binding.researchCenterCnpj} · CNES ${binding.researchCenterCnes} · ${binding.researchCenterCityUf}) | PI: ${binding.piName} (${binding.piCrm}) | Sub: ${binding.subInvestigatorName} (${binding.subInvestigatorCrm}) | Coordenação: ${binding.coordinatorName} (${binding.coordinatorEmail}) | Análise Multi-Documento (Protocolo + TCLE + TALE + Contrato) concluída.`,
        }),
        contractClausesJson: buildAutoFilledContractClausesJson(d.contractClausesJson, binding),
      };
    }

    const candidate = CANDIDATE_PROJECTS_FROM_CONTRACT_EMAILS.find(
      (c) => c.code.toUpperCase() === code || c.studyCode.toUpperCase() === code,
    );
    const hadUnboundAutoFill =
      (d.croName && d.croName.includes('PI Dr.')) ||
      d.clinicalAnalysisJson.includes('Auto-Preenchido [REGRA 09]') ||
      d.contractClausesJson.includes('100% AUTO-PREENCHIDO — REGRA 09');
    if (hadUnboundAutoFill) {
      return {
        ...d,
        croName: candidate?.croName ?? d.croName,
        clinicalAnalysisJson: isEpoca
          ? '{"complexity":"Ensaio Clínico Randomizado Fatorial 2 x 2 (Colchicina + Polipílula · CAAE 59057422.4.1001.0060) · Anexo I: 21 Pagamentos (D0 a D1440 — 48 meses) + Taxa Única de Startup R$ 3.000,00 · Valores Unitários: R$ 5.900,00/participante (Overhead 25% incluso) · Recrutamento Competitivo (Cláusula 4.5 — até 10.797 no estudo geral) · Campos cadastrais CNPJ XX.XXX.XXX/XXXX-XX e PI em branco na minuta para preenchimento pelo Centro de Pesquisa TrialsHub"}'
          : '{"complexity":"Dossiê criado a partir de contratos@giyus.com.br — exibindo estritamente dados literais do e-mail/anexo"}',
        contractClausesJson:
          candidate && candidate.contractClauses.length > 0
            ? JSON.stringify(candidate.contractClauses)
            : d.contractClausesJson,
      };
    }
    return d;
  });

  state.croSponsorGlobalLearnings = [...getEffectiveCroSponsorGlobalLearnings(state, '')];

  if (state.budgetEmailDrafts && state.budgetEmailDrafts.length > 0) {
    state.budgetEmailDrafts = state.budgetEmailDrafts.map((draft, idx) => {
      if (draft.isModifiedFromOriginal) {
        return draft;
      }
      const binding = getEffectiveStudyTeamAndCenterBinding(state, draft.studyCode);
      const hasBoundHeader = draft.currentBody.includes(
        'QUALIFICAÇÃO AUTOMÁTICA DO CENTRO DE PESQUISA',
      );
      const hasUnboundV11Header = draft.currentBody.includes(
        'QUALIFICAÇÃO DO CENTRO DE PESQUISA, PI, SUB-INVESTIGADORES E COORDENAÇÃO',
      );
      if (
        (binding.isAutoFilled && hasBoundHeader) ||
        (!binding.isAutoFilled && hasUnboundV11Header)
      ) {
        return draft;
      }
      const dossier = state.dossiers.find(
        (d) => d.code.toUpperCase() === draft.studyCode.toUpperCase(),
      );
      const upgraded = buildInitialBudgetEmailDraft(
        dossier,
        draft.studyCode,
        draft.createdByEmail || 'syllas@giyus.com.br',
        draft.mailboxAddress || 'contratos@giyus.com.br',
        idx + 1,
        state,
      );
      return {
        ...upgraded,
        id: draft.id,
        graphDraftMessageId: draft.graphDraftMessageId,
        createdAtIso: draft.createdAtIso,
      };
    });
  }
}

function normalizeCnpjDigitsOnly(raw: string): string {
  return String(raw ?? '').replace(/\D+/g, '');
}

function formatCnpjMask(digits: string): string {
  const clean = normalizeCnpjDigitsOnly(digits);
  if (clean.length !== 14) return digits;
  return `${clean.slice(0, 2)}.${clean.slice(2, 5)}.${clean.slice(5, 8)}/${clean.slice(8, 12)}-${clean.slice(12, 14)}`;
}

function computeCnpjDigitsForRootAndBranch(root8: string, branchOrder4: string): string {
  const base12 = `${normalizeCnpjDigitsOnly(root8).slice(0, 8)}${normalizeCnpjDigitsOnly(branchOrder4).padStart(4, '0').slice(0, 4)}`;
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum1 = 0;
  for (let i = 0; i < 12; i++) {
    sum1 += Number(base12[i]) * (weights1[i] ?? 0);
  }
  const rem1 = sum1 % 11;
  const d1 = rem1 < 2 ? 0 : 11 - rem1;

  const base13 = `${base12}${d1}`;
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum2 = 0;
  for (let i = 0; i < 13; i++) {
    sum2 += Number(base13[i]) * (weights2[i] ?? 0);
  }
  const rem2 = sum2 % 11;
  const d2 = rem2 < 2 ? 0 : 11 - rem2;
  return `${base12}${d1}${d2}`;
}

function buildTestFallbackBigDataCorpPayload(rawCnpj: string): BigDataCorpCnpjLookupResponseDto {
  const clean = normalizeCnpjDigitsOnly(rawCnpj);
  if (clean.length !== 14) {
    return {
      ok: false,
      found: false,
      queriedCnpj: rawCnpj,
      cnpjRoot: '',
      primaryEstablishment: null,
      headquarterEstablishment: null,
      branchEstablishments: [],
      allEstablishments: [],
      totalEstablishmentsFound: 0,
      autoRegisteredCount: 0,
      executionChannel: 'BACKEND_ONLY_BIGDATACORP_API (/api/bigdatacorp-cnpj)',
      error: 'Informe um CNPJ válido com 14 dígitos.',
    };
  }
  const root8 = clean.slice(0, 8);
  const queriedBranchOrder = clean.slice(8, 12);
  const matrizDigits = computeCnpjDigitsForRootAndBranch(root8, '0001');
  const filial2Digits = computeCnpjDigitsForRootAndBranch(root8, '0002');
  const isChong = root8 === '51785454';
  const isGiyusResearchCenter = root8 === '69308788';
  const isPequenoPrincipe = root8 === '76591569';
  const isDasa = root8 === '61486650';

  const qsaMembers: readonly BigDataCorpQsaMemberDto[] = isGiyusResearchCenter
    ? [
        {
          name: 'SANDRA CANDIDO',
          taxId: '058.868.549-67',
          role: 'SOCIO-ADMINISTRADOR',
          startDate: '23/09/2026',
        },
      ]
    : isPequenoPrincipe
      ? [
          {
            name: 'ETY GONCALVES FORTE',
            taxId: '000.000.000-00',
            role: 'PRESIDENTE',
            startDate: '09/11/2001',
          },
        ]
      : isChong
        ? [
            {
              name: 'HERBERTO JOSE CHONG NETO',
              taxId: '95413281991',
              role: 'SOCIO-ADMINISTRADOR',
              startDate: '11/08/2023',
            },
            {
              name: 'DULCINEIA DE FATIMA BORGES CHONG',
              taxId: '01815064907',
              role: 'SOCIO-ADMINISTRADOR',
              startDate: '11/08/2023',
            },
          ]
        : [
            {
              name: 'HERBERTO JOSE CHONG NETO',
              taxId: '95413281991',
              role: 'SOCIO-ADMINISTRADOR',
              startDate: '11/08/2023',
            },
          ];

  const qsaSummaryFormatted = qsaMembers
    .map((m) => `${m.name} (${m.role} · CPF/CNPJ ${m.taxId} · Entrada ${m.startDate})`)
    .join(' | ');

  const officialName = isGiyusResearchCenter
    ? 'GIYUS RESEARCH CENTER LTDA'
    : isPequenoPrincipe
      ? 'ASSOCIACAO HOSPITALAR DE PROT INFANCIA DR RAUL CARNEIRO'
      : isChong
        ? 'CLINICA DE ALERGIA E IMUNOLOGIA PROF.DR.HERBERTO CHONG LTDA'
        : isDasa
          ? 'DIAGNOSTICOS DA AMERICA S.A. - DASA / DELBONI / LABCORP'
          : `CENTRO DE PESQUISA CLINICA ${root8} LTDA`;
  const tradeName = isGiyusResearchCenter
    ? 'GIYUS RESEARCH CENTER'
    : isPequenoPrincipe
      ? 'HOSPITAL INFANTIL PEQUENO PRINCIPE'
      : isChong
        ? 'CLINICA DE ALERGIA E IMUNOLOGIA PROF.DR.HERBERTO CHONG'
        : isDasa
          ? 'Dasa / Delboni / Labcorp Diagnósticos & Laboratório Central'
          : `CENTRO DE PESQUISA ${root8}`;

  const matrizCnesRecords: readonly CnesRecordDto[] = isGiyusResearchCenter
    ? []
    : isPequenoPrincipe
      ? [
          {
            cnes: '0015563',
            unitName: 'HOSPITAL INFANTIL PEQUENO PRINCIPE',
            sanitaryLicenseTypeAndNumber:
              'Licença / Alvará Sanitário Municipal (SMS/VISA) nº 03.197/2025 (Exp.: 23/09/2025)',
            relatedToDescription:
              'HOSPITAL · HOSPITAL ESPECIALIZADO · PEDIATRIA | IGUACU, 1472 — AGUA VERDE — CURITIBA/PR',
            unitType: 'HOSPITAL · HOSPITAL ESPECIALIZADO · PEDIATRIA',
            sanitaryPermitNumber: '03.197/2025',
            sanitaryPermitDate: '23/09/2025',
            sanitaryPermitIssuer: 'Licença / Alvará Sanitário Municipal (SMS/VISA)',
            updatedAt: '14/10/2026',
          },
          {
            cnes: '2937255',
            unitName: 'FACULDADES PEQUENO PRINCIPE AMBULATORIO',
            sanitaryLicenseTypeAndNumber:
              'Licença / Alvará Sanitário Municipal (SMS/VISA) nº 2200892/2022 (Exp.: 30/03/2022)',
            relatedToDescription:
              'AMBULATORIO · CLINICA/CENTRO DE ESPECIALIDADE · OUTROS | RUA JOAO NEGRAO, 1103 — CENTRO — CURITIBA/PR',
            unitType: 'AMBULATORIO · CLINICA/CENTRO DE ESPECIALIDADE · OUTROS',
            sanitaryPermitNumber: '2200892/2022',
            sanitaryPermitDate: '30/03/2022',
            sanitaryPermitIssuer: 'Licença / Alvará Sanitário Municipal (SMS/VISA)',
            updatedAt: '18/11/2025',
          },
          {
            cnes: '7787081',
            unitName: 'HOSPITAL PEQUENO PRINCIPE',
            sanitaryLicenseTypeAndNumber:
              'Licença / Alvará Sanitário Municipal (SMS/VISA) nº 263/2024 (Exp.: 05/02/2024)',
            relatedToDescription:
              'AMBULATORIO · POLICLINICA | RUA JOSE FERNANDES FILHO, 200 — GUATUPE — SAO JOSE DOS PINHAIS/PR',
            unitType: 'AMBULATORIO · POLICLINICA',
            sanitaryPermitNumber: '263/2024',
            sanitaryPermitDate: '05/02/2024',
            sanitaryPermitIssuer: 'Licença / Alvará Sanitário Municipal (SMS/VISA)',
            updatedAt: '01/06/2026',
          },
        ]
      : [
          {
            cnes: '1159593',
            unitName: 'HERBERTO JOSE CHONG NETO CNPJ 51 785 454 0001 56',
            sanitaryLicenseTypeAndNumber:
              'Licença / Alvará Sanitário Municipal (SMS/VISA) nº 09/97/008230/2026 (Exp.: 30/03/2026)',
            relatedToDescription:
              'AMBULATORIO · CONSULTORIO ISOLADO | RUA COLOMBO, 1216 — AHU — CURITIBA/PR',
            unitType: 'AMBULATORIO · CONSULTORIO ISOLADO',
            sanitaryPermitNumber: '09/97/008230/2026',
            sanitaryPermitDate: '30/03/2026',
            sanitaryPermitIssuer: 'Licença / Alvará Sanitário Municipal (SMS/VISA)',
            updatedAt: '07/04/2026',
          },
        ];

  const matrizEst: BigDataCorpEstablishmentDto = {
    cnpj: formatCnpjMask(matrizDigits),
    cnpjDigits: matrizDigits,
    cnpjRoot: root8,
    branchOrder: '0001',
    isHeadquarter: true,
    establishmentTypeLabel: 'MATRIZ (0001)',
    officialName,
    tradeName,
    foundedDate: isGiyusResearchCenter ? '23/09/2026' : '11/08/2023',
    foundedDateIso: isGiyusResearchCenter ? '2026-09-23T00:00:00Z' : '2023-08-11T00:00:00Z',
    ageYears: isGiyusResearchCenter ? 0 : 3,
    taxIdStatus: 'ATIVA',
    taxIdStatusDate: isGiyusResearchCenter ? '10/10/2026' : '11/08/2023',
    taxRegime: isGiyusResearchCenter ? 'LTDA' : 'LUCRO PRESUMIDO',
    companyTypeReceita: 'DEMAIS',
    legalNatureCode: '2062',
    legalNatureDescription: 'SOCIEDADE EMPRESARIA LIMITADA',
    legalNatureFormatted: '2062 — SOCIEDADE EMPRESARIA LIMITADA',
    capitalSocialFormatted: isGiyusResearchCenter
      ? 'R$ 1.000.000,00 (UM MILHÃO DE REAIS)'
      : 'R$ 20.000,00',
    mainCnaeFormatted: isGiyusResearchCenter
      ? '64.62-0-00 (HOLDINGS DE INSTITUICOES NAO FINANCEIRAS)'
      : '8630503 — ATIVIDADE MEDICA AMBULATORIAL RESTRITA A CONSULTAS',
    secondaryCnaesFormatted: isGiyusResearchCenter
      ? [
          '64.63-8-00 (OUTRAS SOCIEDADES DE PARTICIPACAO, EXCETO HOLDINGS)',
          '70.20-4-00 (ATIVIDADES DE CONSULTORIA EM GESTAO EMPRESARIAL, EXCETO CONSULTORIA TECNICA ESPECIFICA)',
          '72.10-0-00 (PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS)',
        ]
      : [
          '7210000 — PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS',
          '8630502 — ATIVIDADE MEDICA AMBULATORIAL COM RECURSOS PARA REALIZACAO DE EXAMES COMPLEMENTARES',
          '8630504 — ATIVIDADE ODONTOLOGICA',
        ],
    allCnaesFormatted: isGiyusResearchCenter
      ? '[PRINCIPAL] 64.62-0-00 — HOLDINGS DE INSTITUICOES NAO FINANCEIRAS · [SECUNDÁRIO] 64.63-8-00 — OUTRAS SOCIEDADES DE PARTICIPACAO, EXCETO HOLDINGS · [SECUNDÁRIO] 70.20-4-00 — ATIVIDADES DE CONSULTORIA EM GESTAO EMPRESARIAL, EXCETO CONSULTORIA TECNICA ESPECIFICA · [SECUNDÁRIO] 72.10-0-00 — PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS'
      : '8630503 — ATIVIDADE MEDICA AMBULATORIAL RESTRITA A CONSULTAS (Principal) | 7210000 — PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS | 8630502 — ATIVIDADE MEDICA AMBULATORIAL COM RECURSOS PARA REALIZACAO DE EXAMES COMPLEMENTARES',
    cnaeActivities: [
      {
        code: isGiyusResearchCenter ? '6462000' : '8630503',
        description: isGiyusResearchCenter
          ? 'HOLDINGS DE INSTITUICOES NAO FINANCEIRAS'
          : 'ATIVIDADE MEDICA AMBULATORIAL RESTRITA A CONSULTAS',
        isMain: true,
      },
      {
        code: '7210000',
        description: 'PESQUISA E DESENVOLVIMENTO EXPERIMENTAL EM CIENCIAS FISICAS E NATURAIS',
        isMain: false,
      },
      {
        code: '8630502',
        description:
          'ATIVIDADE MEDICA AMBULATORIAL COM RECURSOS PARA REALIZACAO DE EXAMES COMPLEMENTARES',
        isMain: false,
      },
    ],
    qsaMembers,
    qsaSummaryFormatted,
    addressLine: isGiyusResearchCenter ? 'R CERRO CORA, 2175 — COND 517' : 'R COLOMBO, 1216',
    neighborhood: isGiyusResearchCenter ? 'VILA ROMANA' : 'AHU',
    zipCode: isGiyusResearchCenter ? '05061-450' : '80540-250',
    city: isGiyusResearchCenter ? 'São Paulo' : 'Curitiba',
    state: isGiyusResearchCenter ? 'SP' : 'PR',
    cityUf: isGiyusResearchCenter ? 'São Paulo/SP' : 'Curitiba/PR',
    fullAddressFormatted: isGiyusResearchCenter
      ? 'R CERRO CORA, 2175 — COND 517 — Bairro VILA ROMANA — São Paulo/SP — CEP 05061-450'
      : 'R COLOMBO, 1216 — AHU, Curitiba/PR · CEP 80540-250',
    latitude: -25.4017309,
    longitude: -49.2610665,
    phonesFormatted: isGiyusResearchCenter
      ? ['(11) 999999999 (MOBILE)']
      : ['+55 (41) 3015-2636 (COMERCIAL)'],
    emailsFormatted: isGiyusResearchCenter
      ? ['fiscal01@bicount.com.br']
      : ['chong.neto@giyus.com.br', 'fiscal@cpesq.com.br'],
    cnes: matrizCnesRecords.map((r) => r.cnes).join(' · '),
    cnesUnitType: matrizCnesRecords[0]?.unitType ?? '',
    cnesSanitaryPermit: matrizCnesRecords[0]?.sanitaryLicenseTypeAndNumber ?? '',
    cnesRecords: matrizCnesRecords,
    sourceProvider: 'Receita Federal & DATASUS CNES (Backend-Only API)',
    queriedAtIso: new Date().toISOString(),
  };

  const filial2Est: BigDataCorpEstablishmentDto = {
    ...matrizEst,
    cnpj: formatCnpjMask(filial2Digits),
    cnpjDigits: filial2Digits,
    branchOrder: '0002',
    isHeadquarter: false,
    establishmentTypeLabel: 'FILIAL 0002',
    foundedDate: '01/08/2024',
    foundedDateIso: '2024-08-01T00:00:00Z',
    ageYears: 2,
    taxIdStatusDate: '01/08/2024',
    addressLine: 'R PADRE CAMARGO, 530',
    neighborhood: 'ALTO DA GLORIA',
    zipCode: '80060-240',
    fullAddressFormatted: 'R PADRE CAMARGO, 530 — ALTO DA GLORIA, Curitiba/PR · CEP 80060-240',
    latitude: -25.42014,
    longitude: -49.26218,
    cnes: '',
    cnesUnitType: '',
    cnesSanitaryPermit: '',
    cnesRecords: [],
  };

  const allEstablishments =
    isGiyusResearchCenter || isPequenoPrincipe ? [matrizEst] : [matrizEst, filial2Est];
  const branchEstablishments = isGiyusResearchCenter || isPequenoPrincipe ? [] : [filial2Est];
  const primaryEstablishment =
    !isGiyusResearchCenter && queriedBranchOrder === '0002' ? filial2Est : matrizEst;

  return {
    ok: true,
    found: true,
    queriedCnpj: formatCnpjMask(clean),
    cnpjRoot: root8,
    primaryEstablishment,
    headquarterEstablishment: matrizEst,
    branchEstablishments,
    allEstablishments,
    totalEstablishmentsFound: allEstablishments.length,
    autoRegisteredCount: allEstablishments.length,
    executionChannel: 'BACKEND_ONLY_BIGDATACORP_API (/api/bigdatacorp-cnpj)',
  };
}

function normalizeBackendEstablishmentItem(
  raw: Record<string, unknown>,
): BigDataCorpEstablishmentDto {
  const cnpj = strField(raw, 'cnpj', '');
  const cnpjDigits = strField(raw, 'cnpjDigits', normalizeCnpjDigitsOnly(cnpj));
  const cnpjRoot = strField(raw, 'cnpjRoot', cnpjDigits.slice(0, 8));
  const branchOrder = strField(raw, 'branchOrder', cnpjDigits.slice(8, 12) || '0001');
  const isHeadquarter =
    typeof raw['isHeadquarter'] === 'boolean' ? raw['isHeadquarter'] : branchOrder === '0001';
  const officialName =
    strField(raw, 'officialName', '') ||
    strField(raw, 'legalName', '') ||
    strField(raw, 'name', '');
  const tradeName = strField(raw, 'tradeName', '') || officialName;
  const cityUf = strField(raw, 'cityUf');
  const [cityPart, statePart] = cityUf.split('/');
  const mainCnaeFormatted = strField(raw, 'mainCnaeFormatted', '') || strField(raw, 'mainCnae', '');
  const secondaryCnaesFormatted = Array.isArray(raw['secondaryCnaesFormatted'])
    ? (raw['secondaryCnaesFormatted'] as string[])
    : Array.isArray(raw['secondaryCnaes'])
      ? (raw['secondaryCnaes'] as string[])
      : [];
  const fullAddressFormatted =
    strField(raw, 'fullAddressFormatted', '') || strField(raw, 'address', '') || cityUf;
  const addressLine =
    strField(raw, 'addressLine', '') || strField(raw, 'street', '') || fullAddressFormatted;
  const phonesFormatted = Array.isArray(raw['phonesFormatted'])
    ? (raw['phonesFormatted'] as string[])
    : Array.isArray(raw['phones'])
      ? (raw['phones'] as string[])
      : [];
  const emailsFormatted = Array.isArray(raw['emailsFormatted'])
    ? (raw['emailsFormatted'] as string[])
    : Array.isArray(raw['emails'])
      ? (raw['emails'] as string[])
      : [];
  const qsaMembers = Array.isArray(raw['qsaMembers'])
    ? (raw['qsaMembers'] as BigDataCorpQsaMemberDto[])
    : [];
  const qsaSummaryFormatted =
    strField(raw, 'qsaSummaryFormatted', '') || strField(raw, 'qsaSummary', '');

  return {
    cnpj: cnpj || formatCnpjMask(cnpjDigits),
    cnpjDigits,
    cnpjRoot,
    branchOrder,
    isHeadquarter,
    establishmentTypeLabel:
      strField(raw, 'establishmentTypeLabel', '') ||
      (isHeadquarter ? `MATRIZ (${branchOrder})` : `FILIAL ${branchOrder}`),
    officialName,
    tradeName,
    foundedDate: strField(raw, 'foundedDate', ''),
    foundedDateIso: strField(raw, 'foundedDateIso', ''),
    ageYears: typeof raw['ageYears'] === 'number' ? raw['ageYears'] : 0,
    taxIdStatus: strField(raw, 'taxIdStatus', 'ATIVA'),
    taxIdStatusDate: strField(raw, 'taxIdStatusDate', ''),
    taxRegime: strField(raw, 'taxRegime', ''),
    companyTypeReceita:
      strField(raw, 'companyTypeReceita', '') || strField(raw, 'companyPorte', 'DEMAIS'),
    legalNatureCode: strField(raw, 'legalNatureCode', ''),
    legalNatureDescription: strField(raw, 'legalNatureDescription', ''),
    legalNatureFormatted:
      strField(raw, 'legalNatureFormatted', '') || strField(raw, 'legalNature', ''),
    capitalSocialFormatted:
      strField(raw, 'capitalSocialFormatted', '') || strField(raw, 'capitalSocial', ''),
    mainCnaeFormatted,
    secondaryCnaesFormatted,
    allCnaesFormatted: strField(raw, 'allCnaesFormatted', mainCnaeFormatted),
    cnaeActivities: Array.isArray(raw['cnaeActivities'])
      ? (raw['cnaeActivities'] as BigDataCorpEstablishmentDto['cnaeActivities'])
      : Array.isArray(raw['activities'])
        ? (raw['activities'] as BigDataCorpEstablishmentDto['cnaeActivities'])
        : [],
    qsaMembers,
    qsaSummaryFormatted,
    addressLine,
    neighborhood: strField(raw, 'neighborhood', ''),
    zipCode: strField(raw, 'zipCode', ''),
    city: strField(raw, 'city', cityPart || ''),
    state: strField(raw, 'state', statePart || ''),
    cityUf,
    fullAddressFormatted,
    latitude: typeof raw['latitude'] === 'number' ? raw['latitude'] : null,
    longitude: typeof raw['longitude'] === 'number' ? raw['longitude'] : null,
    phonesFormatted,
    emailsFormatted,
    cnes: strField(raw, 'cnes', ''),
    cnesUnitType: strField(raw, 'cnesUnitType', ''),
    cnesSanitaryPermit: strField(raw, 'cnesSanitaryPermit', ''),
    cnesRecords: Array.isArray(raw['cnesRecords']) ? (raw['cnesRecords'] as CnesRecordDto[]) : [],
    sourceProvider:
      strField(raw, 'sourceProvider', '') || 'Receita Federal & DATASUS CNES (Backend-Only API)',
    queriedAtIso: strField(raw, 'queriedAtIso', new Date().toISOString()),
  };
}

function normalizeRawBackendBigDataCorpResponse(
  rawJson: Record<string, unknown>,
  rawCnpj: string,
): BigDataCorpCnpjLookupResponseDto {
  const found = Boolean(rawJson['found']);
  const ok = typeof rawJson['ok'] === 'boolean' ? rawJson['ok'] : found;
  const cleanDigits = normalizeCnpjDigitsOnly(rawCnpj);
  const rawEstablishments = Array.isArray(rawJson['allEstablishments'])
    ? (rawJson['allEstablishments'] as Record<string, unknown>[])
    : Array.isArray(rawJson['establishments'])
      ? (rawJson['establishments'] as Record<string, unknown>[])
      : [];
  const allEstablishments = rawEstablishments.map((item) =>
    normalizeBackendEstablishmentItem(item),
  );
  const primaryRaw =
    rawJson['primaryEstablishment'] && typeof rawJson['primaryEstablishment'] === 'object'
      ? normalizeBackendEstablishmentItem(
          rawJson['primaryEstablishment'] as Record<string, unknown>,
        )
      : (allEstablishments[0] ?? null);
  const headquarterEstablishment = allEstablishments.find((e) => e.isHeadquarter) ?? primaryRaw;
  const branchEstablishments = allEstablishments.filter((e) => !e.isHeadquarter);
  const errText = strField(rawJson, 'error', '') || strField(rawJson, 'errorMessage', '');

  return {
    ok,
    found,
    queriedCnpj:
      strField(rawJson, 'queriedCnpj', '') ||
      (cleanDigits.length === 14 ? formatCnpjMask(cleanDigits) : rawCnpj),
    cnpjRoot: strField(rawJson, 'cnpjRoot', cleanDigits.slice(0, 8)),
    primaryEstablishment: primaryRaw,
    headquarterEstablishment,
    branchEstablishments,
    allEstablishments,
    totalEstablishmentsFound: allEstablishments.length,
    autoRegisteredCount: 0,
    executionChannel: 'BACKEND_ONLY_BIGDATACORP_API (/api/bigdatacorp-cnpj)',
    ...(errText ? { error: errText } : {}),
  };
}

export async function lookupCnpjAndAutoRegisterBranchesOnState(
  state: FakeSuppliersAndStudiesState,
  body: Record<string, unknown>,
): Promise<BigDataCorpCnpjLookupResponseDto> {
  const rawCnpj = strField(body, 'cnpj', '').trim();
  const cleanCnpjDigits = normalizeCnpjDigitsOnly(rawCnpj);
  const formattedCnpj = cleanCnpjDigits.length === 14 ? formatCnpjMask(cleanCnpjDigits) : rawCnpj;
  const includeBranches = body['includeBranches'] !== false;
  const autoRegister = body['autoRegisterEstablishments'] !== false;
  const defaultClassificationRaw = strField(body, 'defaultClassification', 'CENTRO_NAO_GIYUS');
  const defaultGiyusRelationship = strField(
    body,
    'defaultGiyusRelationship',
    'A definir na atualização complementar GIYUS (Classificação, CNES e Relação Operacional abaixo)',
  );
  const defaultCnes = strField(body, 'defaultCnes', '');

  let backendPayload: BigDataCorpCnpjLookupResponseDto | null = null;

  const preResolved = body['preResolvedPayload'];
  if (preResolved && typeof preResolved === 'object') {
    backendPayload = normalizeRawBackendBigDataCorpResponse(
      preResolved as Record<string, unknown>,
      formattedCnpj,
    );
  }

  // Em navegador (dev/prod), chama estritamente o endpoint de backend (/api/bigdatacorp-cnpj) para jamais expor chaves no browser!
  // O campo na tela permanece formatado (XX.XXX.XXX/XXXX-XX) e o backend recebe e consulta pelos 14 dígitos puros no doc{...}.
  if (!backendPayload && import.meta.env.MODE !== 'test' && typeof globalThis.fetch === 'function') {
    try {
      const resp = await globalThis.fetch('/api/bigdatacorp-cnpj', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          cnpj: cleanCnpjDigits || formattedCnpj,
          formattedCnpj,
          includeBranches,
          maxBranchesToProbe: 6,
        }),
      });
      const json = (await resp.json()) as Record<string, unknown>;
      if (json && typeof json === 'object' && ('ok' in json || 'found' in json)) {
        backendPayload = normalizeRawBackendBigDataCorpResponse(json, formattedCnpj);
      }
    } catch {
      // fallback if local server is unreachable
    }
  }

  if (!backendPayload) {
    backendPayload = buildTestFallbackBigDataCorpPayload(rawCnpj);
  }

  if (!backendPayload.ok || !backendPayload.found) {
    return backendPayload;
  }

  if (!state.registeredResearchCenters) {
    state.registeredResearchCenters = [];
  }

  let autoRegisteredCount = 0;
  if (autoRegister && backendPayload.allEstablishments.length > 0) {
    for (const est of backendPayload.allEstablishments) {
      const cleanEstCnpj = normalizeCnpjDigitsOnly(est.cnpj);
      const existingIdx = state.registeredResearchCenters.findIndex(
        (r) =>
          (cleanEstCnpj.length === 14 && normalizeCnpjDigitsOnly(r.cnpj) === cleanEstCnpj) ||
          r.name.toLowerCase() ===
            `${est.tradeName || est.officialName} — ${est.establishmentTypeLabel} (${est.cityUf})`.toLowerCase(),
      );
      const existingCenter =
        existingIdx >= 0 ? state.registeredResearchCenters[existingIdx] : undefined;

      // Determina a classificação respeitando a unicidade da GIYUS Holding [REGRA 12]
      let chosenClassification: ResearchCenterClassificationType = existingCenter?.classification
        ? resolveCenterClassification(existingCenter.classification, existingCenter.name)
        : resolveCenterClassification(defaultClassificationRaw, est.officialName);

      // Se for uma Filial (branchOrder !== '0001') e o default era GIYUS_HOLDING, classifica a filial como CENTRO_GIYUS para respeitar a unicidade da Holding
      if (!est.isHeadquarter && chosenClassification === 'GIYUS_HOLDING') {
        chosenClassification = 'CENTRO_GIYUS';
      }

      const existingHoldingOther = state.registeredResearchCenters.find(
        (r) =>
          resolveCenterClassification(r.classification, r.name) === 'GIYUS_HOLDING' &&
          normalizeCnpjDigitsOnly(r.cnpj) !== cleanEstCnpj,
      );
      if (chosenClassification === 'GIYUS_HOLDING' && existingHoldingOther) {
        chosenClassification = 'CENTRO_GIYUS';
      }

      const displayCenterName =
        existingCenter?.name ||
        `${est.tradeName || est.officialName} — ${est.establishmentTypeLabel} (${est.cityUf})`;

      const resolvedCnesRecords: readonly CnesRecordDto[] =
        est.cnesRecords && est.cnesRecords.length > 0
          ? est.cnesRecords
          : existingCenter?.cnesRecords && existingCenter.cnesRecords.length > 0
            ? existingCenter.cnesRecords
            : [];

      const resolvedUnitCnes =
        resolvedCnesRecords.length > 0
          ? resolvedCnesRecords.map((r) => r.cnes).join(' · ')
          : est.cnes?.trim() ||
            (existingCenter?.cnes &&
            existingCenter.cnes !== 'Pendente atualização CNES' &&
            !existingCenter.cnes.startsWith('A atualizar')
              ? existingCenter.cnes
              : '') ||
            defaultCnes ||
            'Pendente atualização CNES';

      const nowSyncIso = new Date().toISOString();
      const nextWeeklyIso = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

      const enrichedItem: ClinicalResearchCenterDirectoryItemDto = {
        ...(existingCenter ?? {}),
        name: displayCenterName,
        legalName: est.officialName,
        cnpj: est.cnpj,
        cnes: resolvedUnitCnes,
        cnesRecords: resolvedCnesRecords,
        cityUf: est.cityUf || '',
        address: est.fullAddressFormatted || est.addressLine || est.cityUf || '',
        bankSummary:
          existingCenter?.bankSummary ||
          'A informar na atualização complementar GIYUS (Tabela 3 CTA)',
        bankIntl:
          existingCenter?.bankIntl || 'Liquidação Nacional BRL / Internacional conforme CTA',
        ...(existingCenter?.bankAccounts ? { bankAccounts: existingCenter.bankAccounts } : {}),
        classification: chosenClassification,
        classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS[chosenClassification].label,
        paymentFlowRule:
          RESEARCH_CENTER_CLASSIFICATIONS[chosenClassification].paymentAndGovernanceRule,
        giyusRelationship:
          existingCenter?.giyusRelationship ||
          (est.isHeadquarter
            ? defaultGiyusRelationship
            : `Filial (${est.establishmentTypeLabel}) vinculada automaticamente à Matriz CNPJ raiz ${est.cnpjRoot}`),
        tradeName: est.tradeName,
        foundedDate: est.foundedDate,
        taxIdStatus: `${est.taxIdStatus}${est.taxIdStatusDate ? ` (desde ${est.taxIdStatusDate})` : ''}`,
        taxRegime: est.taxRegime,
        companyPorte: est.companyTypeReceita,
        legalNature: est.legalNatureFormatted,
        capitalSocial: est.capitalSocialFormatted,
        mainCnae: est.mainCnaeFormatted,
        secondaryCnaes: est.secondaryCnaesFormatted,
        allCnaesFormatted: est.allCnaesFormatted,
        qsaSummary: est.qsaSummaryFormatted,
        qsaMembers: est.qsaMembers,
        neighborhood: est.neighborhood,
        zipCode: est.zipCode,
        phones: est.phonesFormatted,
        emails: est.emailsFormatted,
        isHeadquarter: est.isHeadquarter,
        branchOrder: est.branchOrder,
        establishmentTypeLabel: est.establishmentTypeLabel,
        enrichedFromBigDataCorp: true,
        lastWeeklySyncAtIso: nowSyncIso,
        nextWeeklySyncAtIso: nextWeeklyIso,
        weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
      };

      if (existingIdx >= 0) {
        state.registeredResearchCenters[existingIdx] = enrichedItem;
      } else {
        state.registeredResearchCenters.push(enrichedItem);
      }
      autoRegisteredCount++;
    }
    writePersistedGreenfieldMailboxState(state);
  }

  const isGreenfield = state.isGreenfield !== false;
  const updatedDirectory = {
    pis: isGreenfield ? (state.registeredPis ?? []) : (state.registeredPis ?? AVAILABLE_PI_CATALOG),
    subs: isGreenfield
      ? (state.registeredSubs ?? [])
      : (state.registeredSubs ?? AVAILABLE_SUB_CATALOG),
    coordinators: isGreenfield
      ? (state.registeredCoordinators ?? [])
      : (state.registeredCoordinators ?? AVAILABLE_COORDINATOR_CATALOG),
    researchCenters: isGreenfield
      ? (state.registeredResearchCenters ?? [])
      : (state.registeredResearchCenters ?? AVAILABLE_RESEARCH_CENTERS_CATALOG),
  };

  return {
    ...backendPayload,
    autoRegisteredCount,
    updatedDirectory,
  };
}

export function registerClinicalPersonOrCenterOnState(
  state: FakeSuppliersAndStudiesState,
  body: Record<string, unknown>,
) {
  const kind = strField(body, 'kind', 'COORDINATOR').toUpperCase();
  const rawName = strField(body, 'name', '').trim();
  const incomingCnpjForFallback = strField(body, 'cnpj', '').trim();
  const name =
    rawName ||
    (kind === 'RESEARCH_CENTER'
      ? strField(body, 'tradeName', '').trim() ||
        strField(body, 'legalName', '').trim() ||
        (incomingCnpjForFallback ? `Matriz CNPJ ${incomingCnpjForFallback}` : '')
      : '');

  if (!state.registeredPis) state.registeredPis = [];
  if (!state.registeredSubs) state.registeredSubs = [];
  if (!state.registeredCoordinators) state.registeredCoordinators = [];
  if (!state.registeredResearchCenters) state.registeredResearchCenters = [];

  let holdingSingletonError: string | undefined;

  if (name) {
    if (kind === 'PI' || kind === 'SUB') {
      const piIdx = state.registeredPis.findIndex(
        (p) => p.name.toLowerCase() === name.toLowerCase(),
      );
      const subIdx = state.registeredSubs.findIndex(
        (p) => p.name.toLowerCase() === name.toLowerCase(),
      );
      const prevInv =
        (piIdx >= 0 ? state.registeredPis[piIdx] : undefined) ??
        (subIdx >= 0 ? state.registeredSubs[subIdx] : undefined);

      const commercialEmail = strField(
        body,
        'commercialEmail',
        strField(body, 'email', prevInv?.commercialEmail ?? prevInv?.email ?? ''),
      ).trim();
      const personalEmail = strField(body, 'personalEmail', prevInv?.personalEmail ?? '').trim();
      const mobilePhone = strField(body, 'mobilePhone', prevInv?.mobilePhone ?? '').trim();
      const isPrincipalInvestigator =
        body['isPrincipalInvestigator'] !== undefined
          ? Boolean(body['isPrincipalInvestigator'])
          : kind === 'PI'
            ? true
            : Boolean(prevInv?.isPrincipalInvestigator);
      const isSubInvestigator =
        body['isSubInvestigator'] !== undefined
          ? Boolean(body['isSubInvestigator'])
          : body['canActAsSubInvestigator'] !== undefined
            ? Boolean(body['canActAsSubInvestigator'])
            : kind === 'SUB'
              ? true
              : Boolean(prevInv?.isSubInvestigator);

      const piTherapeuticAreas = Array.isArray(body['piTherapeuticAreas'])
        ? (body['piTherapeuticAreas'] as unknown[]).map((a) => String(a).trim()).filter(Boolean)
        : (prevInv?.piTherapeuticAreas ?? []);
      const subTherapeuticAreas = Array.isArray(body['subTherapeuticAreas'])
        ? (body['subTherapeuticAreas'] as unknown[]).map((a) => String(a).trim()).filter(Boolean)
        : (prevInv?.subTherapeuticAreas ?? []);
      const secondarySpecialties = Array.isArray(body['secondarySpecialties'])
        ? (body['secondarySpecialties'] as unknown[]).map((s) => String(s).trim()).filter(Boolean)
        : (prevInv?.secondarySpecialties ?? []);
      const relatedCenters = Array.isArray(body['relatedCenters'])
        ? (body['relatedCenters'] as unknown[]).map((c) => String(c).trim()).filter(Boolean)
        : (prevInv?.relatedCenters ?? []);

      const fallbackSpecialty =
        piTherapeuticAreas[0] || subTherapeuticAreas[0] || prevInv?.specialty || 'Cardiologia';

      const item = {
        name,
        crm: strField(body, 'crm', prevInv?.crm ?? 'CRM-PR'),
        cpf: strField(body, 'cpf', prevInv?.cpf ?? ''),
        specialty: strField(body, 'specialty', fallbackSpecialty),
        secondarySpecialties,
        email: commercialEmail || personalEmail,
        commercialEmail,
        personalEmail,
        mobilePhone,
        isPrincipalInvestigator,
        isSubInvestigator,
        piTherapeuticAreas,
        subTherapeuticAreas,
        relatedCenters,
      };

      if (isPrincipalInvestigator || kind === 'PI') {
        if (piIdx >= 0) state.registeredPis[piIdx] = item;
        else state.registeredPis.push(item);
      } else if (piIdx >= 0 && body['isPrincipalInvestigator'] === false) {
        state.registeredPis.splice(piIdx, 1);
      }

      if (isSubInvestigator || kind === 'SUB') {
        if (subIdx >= 0) state.registeredSubs[subIdx] = item;
        else state.registeredSubs.push(item);
      } else if (subIdx >= 0 && body['isSubInvestigator'] === false) {
        state.registeredSubs.splice(subIdx, 1);
      }
    } else if (kind === 'COORDINATOR') {
      const idx = state.registeredCoordinators.findIndex(
        (p) => p.name.toLowerCase() === name.toLowerCase(),
      );
      const prevCoord = idx >= 0 ? state.registeredCoordinators[idx] : undefined;
      const corporateEmail = strField(
        body,
        'corporateEmail',
        strField(body, 'email', prevCoord?.corporateEmail ?? prevCoord?.email ?? ''),
      ).trim();
      const personalEmail = strField(body, 'personalEmail', prevCoord?.personalEmail ?? '').trim();
      const mobilePhone = strField(body, 'mobilePhone', prevCoord?.mobilePhone ?? '').trim();
      const cpf = strField(body, 'cpf', prevCoord?.cpf ?? '').trim();
      const birthDate = strField(body, 'birthDate', prevCoord?.birthDate ?? '').trim();
      const rawLevel = strField(body, 'level', prevCoord?.level ?? 'Pleno');
      const level: 'Júnior' | 'Pleno' | 'Sênior' =
        rawLevel === 'Sênior' || rawLevel === 'Júnior' ? rawLevel : 'Pleno';
      const isHealthProfessional =
        body['isHealthProfessional'] !== undefined
          ? Boolean(body['isHealthProfessional'])
          : (prevCoord?.isHealthProfessional ?? true);
      const healthProfessionalType = isHealthProfessional
        ? strField(
            body,
            'healthProfessionalType',
            prevCoord?.healthProfessionalType ?? 'Enfermeira(o) (COREN)',
          ).trim()
        : 'Fora da Área da Saúde (Atuação Administrativa / Regulatória)';
      const councilRegistrationNumber = isHealthProfessional
        ? strField(
            body,
            'councilRegistrationNumber',
            prevCoord?.councilRegistrationNumber ?? '',
          ).trim()
        : '';
      const computedRole = strField(
        body,
        'role',
        isHealthProfessional
          ? `Coordenadora ${level} — ${healthProfessionalType}${councilRegistrationNumber ? ` (${councilRegistrationNumber})` : ''}`
          : `Coordenadora ${level} — Fora da Área da Saúde`,
      );
      const item = {
        name,
        cpf,
        birthDate,
        level,
        isHealthProfessional,
        healthProfessionalType,
        councilRegistrationNumber,
        role: computedRole,
        email: corporateEmail || personalEmail,
        corporateEmail,
        personalEmail,
        mobilePhone,
      };
      if (idx >= 0) state.registeredCoordinators[idx] = item;
      else state.registeredCoordinators.push(item);
    } else if (kind === 'RESEARCH_CENTER') {
      const incomingCnpj = strField(body, 'cnpj', '').trim();
      const incomingCnpjDigits = normalizeCnpjDigitsOnly(incomingCnpj);
      const fallbackLookup =
        incomingCnpjDigits.length === 14
          ? buildTestFallbackBigDataCorpPayload(incomingCnpjDigits)
          : null;
      const fallbackEst =
        fallbackLookup?.primaryEstablishment ?? fallbackLookup?.headquarterEstablishment ?? null;
      const idx = state.registeredResearchCenters.findIndex(
        (r) =>
          (incomingCnpjDigits.length === 14 &&
            normalizeCnpjDigitsOnly(r.cnpj) === incomingCnpjDigits) ||
          r.name.toLowerCase() === name.toLowerCase(),
      );
      const prevCenter = idx >= 0 ? state.registeredResearchCenters[idx] : undefined;

      const classification = resolveCenterClassification(
        strField(body, 'classification', prevCenter?.classification ?? ''),
        name,
      );
      const existingHolding = state.registeredResearchCenters.find(
        (r, i) =>
          i !== idx &&
          resolveCenterClassification(r.classification, r.name) === 'GIYUS_HOLDING' &&
          r.name.toLowerCase() !== name.toLowerCase(),
      );
      if (classification === 'GIYUS_HOLDING' && existingHolding) {
        holdingSingletonError = `Regra de Governança GIYUS [REGRA 12]: A classificação "GIYUS Holding" só pode ser 1 (única — já cadastrada como "${existingHolding.name}"). Os outros tipos (Centro Não-GIYUS, Centro GIYUS e Centro Parceiro GIYUS) podem ser múltiplos.`;
      } else {
        const secondaryCnaes = Array.isArray(body['secondaryCnaes'])
          ? (body['secondaryCnaes'] as string[])
          : (prevCenter?.secondaryCnaes ?? fallbackEst?.secondaryCnaesFormatted);
        const secondaryCnaeItems = Array.isArray(body['secondaryCnaeItems'])
          ? (body['secondaryCnaeItems'] as { readonly code: string; readonly description: string }[])
          : prevCenter?.secondaryCnaeItems;
        const qsaMembers = Array.isArray(body['qsaMembers'])
          ? (body['qsaMembers'] as BigDataCorpQsaMemberDto[])
          : (prevCenter?.qsaMembers ?? fallbackEst?.qsaMembers);
        const cnesRecords = Array.isArray(body['cnesRecords'])
          ? (body['cnesRecords'] as CnesRecordDto[])
          : (prevCenter?.cnesRecords ?? fallbackEst?.cnesRecords);
        const phones = Array.isArray(body['phones'])
          ? (body['phones'] as string[])
          : (prevCenter?.phones ?? fallbackEst?.phonesFormatted);
        const emails = Array.isArray(body['emails'])
          ? (body['emails'] as string[])
          : (prevCenter?.emails ?? fallbackEst?.emailsFormatted);
        const bankAccounts = Array.isArray(body['bankAccounts'])
          ? (body['bankAccounts'] as ResearchCenterBankAccountDto[])
          : prevCenter?.bankAccounts;
        const computedBankSummaries =
          bankAccounts && bankAccounts.length > 0
            ? formatResearchCenterBankAccountsSummary(bankAccounts)
            : null;
        const nowSyncIso = new Date().toISOString();
        const nextWeeklyIso = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
        const resolvedBranchOrder =
          strField(
            body,
            'branchOrder',
            prevCenter?.branchOrder ||
              fallbackEst?.branchOrder ||
              (incomingCnpjDigits.length === 14 ? incomingCnpjDigits.slice(8, 12) : '0001'),
          ).trim() || '0001';
        const resolvedIsHeadquarter =
          typeof body['isHeadquarter'] === 'boolean'
            ? body['isHeadquarter']
            : prevCenter?.isHeadquarter !== undefined
              ? prevCenter.isHeadquarter
              : resolvedBranchOrder === '0001';
        const resolvedEstablishmentTypeLabel =
          strField(
            body,
            'establishmentTypeLabel',
            prevCenter?.establishmentTypeLabel ||
              fallbackEst?.establishmentTypeLabel ||
              (resolvedIsHeadquarter ? 'MATRIZ (0001)' : `FILIAL (${resolvedBranchOrder})`),
          ).trim() || (resolvedIsHeadquarter ? 'MATRIZ (0001)' : `FILIAL (${resolvedBranchOrder})`);
        const resolvedCnpjRoot = strField(
          body,
          'cnpjRoot',
          prevCenter?.cnpjRoot ||
            fallbackEst?.cnpjRoot ||
            (incomingCnpjDigits.length === 14 ? incomingCnpjDigits.slice(0, 8) : ''),
        ).trim();

        const item: ClinicalResearchCenterDirectoryItemDto = {
          ...(prevCenter ?? {}),
          name,
          legalName: strField(
            body,
            'legalName',
            prevCenter?.legalName || fallbackEst?.officialName || name.toUpperCase(),
          ),
          cnpj: incomingCnpj || prevCenter?.cnpj || fallbackEst?.cnpj || '',
          ...(resolvedCnpjRoot ? { cnpjRoot: resolvedCnpjRoot } : {}),
          cnes: strField(
            body,
            'cnes',
            cnesRecords && cnesRecords.length > 0
              ? cnesRecords.map((r) => r.cnes).join(' · ')
              : (prevCenter?.cnes ?? fallbackEst?.cnes ?? ''),
          ),
          ...(cnesRecords ? { cnesRecords } : {}),
          cityUf: strField(body, 'cityUf', prevCenter?.cityUf ?? fallbackEst?.cityUf ?? ''),
          address: strField(
            body,
            'address',
            prevCenter?.address ?? fallbackEst?.fullAddressFormatted ?? '',
          ),
          street: strField(body, 'street', prevCenter?.street ?? fallbackEst?.street ?? ''),
          number: strField(body, 'number', prevCenter?.number ?? fallbackEst?.number ?? ''),
          complement: strField(
            body,
            'complement',
            prevCenter?.complement ?? fallbackEst?.complement ?? '',
          ),
          city: strField(body, 'city', prevCenter?.city ?? fallbackEst?.city ?? ''),
          state: strField(body, 'state', prevCenter?.state ?? fallbackEst?.state ?? ''),
          bankSummary: strField(
            body,
            'bankSummary',
            computedBankSummaries?.fullCombinedSummary || prevCenter?.bankSummary || '',
          ),
          bankIntl: strField(
            body,
            'bankIntl',
            computedBankSummaries?.bankIntlSummary ||
              prevCenter?.bankIntl ||
              'Liquidação Nacional BRL / Internacional conforme CTA',
          ),
          ...(bankAccounts ? { bankAccounts } : {}),
          municipalTaxRegistration: strField(
            body,
            'municipalTaxRegistration',
            prevCenter?.municipalTaxRegistration ?? '',
          ),
          stateTaxRegistration: strField(
            body,
            'stateTaxRegistration',
            prevCenter?.stateTaxRegistration ?? '',
          ),
          technicalDirectorName: strField(
            body,
            'technicalDirectorName',
            prevCenter?.technicalDirectorName ?? '',
          ),
          technicalDirectorCrm: strField(
            body,
            'technicalDirectorCrm',
            prevCenter?.technicalDirectorCrm ?? '',
          ),
          legalSignatoryName: strField(
            body,
            'legalSignatoryName',
            prevCenter?.legalSignatoryName ?? '',
          ),
          legalSignatoryCpf: strField(
            body,
            'legalSignatoryCpf',
            prevCenter?.legalSignatoryCpf ?? '',
          ),
          legalSignatoryRole: strField(
            body,
            'legalSignatoryRole',
            prevCenter?.legalSignatoryRole ?? '',
          ),
          legalSignatoryEmail: strField(
            body,
            'legalSignatoryEmail',
            prevCenter?.legalSignatoryEmail ?? '',
          ),
          billingContactName: strField(
            body,
            'billingContactName',
            prevCenter?.billingContactName ?? '',
          ),
          billingEmail: strField(body, 'billingEmail', prevCenter?.billingEmail ?? ''),
          billingPhone: strField(body, 'billingPhone', prevCenter?.billingPhone ?? ''),
          cepName: strField(body, 'cepName', prevCenter?.cepName ?? ''),
          conepNumber: strField(body, 'conepNumber', prevCenter?.conepNumber ?? ''),
          fwaNumber: strField(body, 'fwaNumber', prevCenter?.fwaNumber ?? ''),
          classification,
          classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS[classification].label,
          paymentFlowRule: RESEARCH_CENTER_CLASSIFICATIONS[classification].paymentAndGovernanceRule,
          giyusRelationship: strField(
            body,
            'giyusRelationship',
            prevCenter?.giyusRelationship ??
              RESEARCH_CENTER_CLASSIFICATIONS[classification].paymentAndGovernanceRule,
          ),
          tradeName: strField(
            body,
            'tradeName',
            prevCenter?.tradeName ?? fallbackEst?.tradeName ?? name,
          ),
          foundedDate: strField(
            body,
            'foundedDate',
            prevCenter?.foundedDate ?? fallbackEst?.foundedDate ?? '',
          ),
          taxIdStatus: strField(
            body,
            'taxIdStatus',
            prevCenter?.taxIdStatus ?? fallbackEst?.taxIdStatus ?? 'ATIVA',
          ),
          taxRegime: strField(
            body,
            'taxRegime',
            prevCenter?.taxRegime ?? fallbackEst?.taxRegime ?? '',
          ),
          companyPorte: strField(
            body,
            'companyPorte',
            prevCenter?.companyPorte ?? fallbackEst?.companyTypeReceita ?? '',
          ),
          legalNature: strField(
            body,
            'legalNature',
            prevCenter?.legalNature ?? fallbackEst?.legalNatureFormatted ?? '',
          ),
          capitalSocial: strField(
            body,
            'capitalSocial',
            prevCenter?.capitalSocial ?? fallbackEst?.capitalSocialFormatted ?? '',
          ),
          mainCnae: strField(
            body,
            'mainCnae',
            prevCenter?.mainCnae ?? fallbackEst?.mainCnaeFormatted ?? '',
          ),
          ...(secondaryCnaes ? { secondaryCnaes } : {}),
          ...(secondaryCnaeItems ? { secondaryCnaeItems } : {}),
          allCnaesFormatted: strField(
            body,
            'allCnaesFormatted',
            prevCenter?.allCnaesFormatted ?? fallbackEst?.allCnaesFormatted ?? '',
          ),
          qsaSummary: strField(
            body,
            'qsaSummary',
            prevCenter?.qsaSummary ?? fallbackEst?.qsaSummaryFormatted ?? '',
          ),
          ...(qsaMembers ? { qsaMembers } : {}),
          neighborhood: strField(
            body,
            'neighborhood',
            prevCenter?.neighborhood ?? fallbackEst?.neighborhood ?? '',
          ),
          zipCode: strField(body, 'zipCode', prevCenter?.zipCode ?? fallbackEst?.zipCode ?? ''),
          ...(phones ? { phones } : {}),
          ...(emails ? { emails } : {}),
          isHeadquarter: resolvedIsHeadquarter,
          branchOrder: resolvedBranchOrder,
          establishmentTypeLabel: resolvedEstablishmentTypeLabel,
          enrichedFromBigDataCorp: Boolean(
            body['enrichedFromBigDataCorp'] ??
              prevCenter?.enrichedFromBigDataCorp ??
              incomingCnpjDigits.length === 14,
          ),
          lastWeeklySyncAtIso: strField(
            body,
            'lastWeeklySyncAtIso',
            prevCenter?.lastWeeklySyncAtIso ?? nowSyncIso,
          ),
          nextWeeklySyncAtIso: strField(
            body,
            'nextWeeklySyncAtIso',
            prevCenter?.nextWeeklySyncAtIso ?? nextWeeklyIso,
          ),
          weeklySyncSchedulePolicy: strField(
            body,
            'weeklySyncSchedulePolicy',
            prevCenter?.weeklySyncSchedulePolicy ?? 'WEEKLY_EVERY_7_DAYS',
          ),
        };
        if (idx >= 0) state.registeredResearchCenters[idx] = item;
        else state.registeredResearchCenters.push(item);

        // Garante que qualquer CNPJ Matriz (0001) ou filiais vinculadas em additionalEstablishments ou fallbackLookup.headquarterEstablishment sejam salvas da mesma forma no banco de dados
        const extraEstsRaw: BigDataCorpEstablishmentDto[] = [];
        if (Array.isArray(body['additionalEstablishments'])) {
          for (const candidate of body['additionalEstablishments'] as BigDataCorpEstablishmentDto[]) {
            if (candidate && candidate.cnpj) extraEstsRaw.push(candidate);
          }
        }
        if (
          fallbackLookup?.headquarterEstablishment &&
          normalizeCnpjDigitsOnly(fallbackLookup.headquarterEstablishment.cnpj) !== incomingCnpjDigits &&
          !extraEstsRaw.some(
            (e) =>
              normalizeCnpjDigitsOnly(e.cnpj) ===
              normalizeCnpjDigitsOnly(fallbackLookup.headquarterEstablishment!.cnpj),
          )
        ) {
          extraEstsRaw.push(fallbackLookup.headquarterEstablishment);
        }
        for (const est of extraEstsRaw) {
          const cleanExtraCnpj = normalizeCnpjDigitsOnly(est.cnpj);
          if (!cleanExtraCnpj || cleanExtraCnpj === incomingCnpjDigits) continue;
          const existingExtraIdx = state.registeredResearchCenters.findIndex(
            (r) => normalizeCnpjDigitsOnly(r.cnpj) === cleanExtraCnpj,
          );
          const prevExtra =
            existingExtraIdx >= 0 ? state.registeredResearchCenters[existingExtraIdx] : undefined;
          const extraClass: ResearchCenterClassificationType =
            prevExtra?.classification ??
            (classification === 'GIYUS_HOLDING' ? 'CENTRO_GIYUS' : classification);
          const extraItem: ClinicalResearchCenterDirectoryItemDto = {
            ...(prevExtra ?? {}),
            name:
              prevExtra?.name ||
              `${est.tradeName || est.officialName} — ${est.establishmentTypeLabel || (est.isHeadquarter ? 'MATRIZ (0001)' : `FILIAL (${est.branchOrder || '0002'})`)}${est.cityUf ? ` (${est.cityUf})` : ''}`,
            legalName: est.officialName || prevExtra?.legalName || name.toUpperCase(),
            tradeName: est.tradeName || prevExtra?.tradeName || est.officialName || name,
            cnpj: est.cnpj,
            cnpjRoot: est.cnpjRoot || cleanExtraCnpj.slice(0, 8),
            cnes:
              est.cnesRecords && est.cnesRecords.length > 0
                ? est.cnesRecords.map((r) => r.cnes).join(' · ')
                : est.cnes || prevExtra?.cnes || '',
            ...(est.cnesRecords ? { cnesRecords: est.cnesRecords } : {}),
            cityUf: est.cityUf || prevExtra?.cityUf || '',
            address: est.fullAddressFormatted || est.addressLine || prevExtra?.address || '',
            street: est.street || prevExtra?.street || '',
            number: est.number || prevExtra?.number || '',
            complement: est.complement || prevExtra?.complement || '',
            neighborhood: est.neighborhood || prevExtra?.neighborhood || '',
            zipCode: est.zipCode || prevExtra?.zipCode || '',
            city: est.city || prevExtra?.city || '',
            state: est.state || prevExtra?.state || '',
            phones: est.phonesFormatted ?? prevExtra?.phones ?? [],
            emails: est.emailsFormatted ?? prevExtra?.emails ?? [],
            foundedDate: est.foundedDate || prevExtra?.foundedDate || '',
            taxIdStatus: est.taxIdStatus || prevExtra?.taxIdStatus || 'ATIVA',
            taxRegime: est.taxRegime || prevExtra?.taxRegime || '',
            companyPorte: est.companyTypeReceita || prevExtra?.companyPorte || '',
            legalNature: est.legalNatureFormatted || prevExtra?.legalNature || '',
            capitalSocial: est.capitalSocialFormatted || prevExtra?.capitalSocial || '',
            mainCnae: est.mainCnaeFormatted || prevExtra?.mainCnae || '',
            secondaryCnaes: est.secondaryCnaesFormatted ?? prevExtra?.secondaryCnaes ?? [],
            allCnaesFormatted: est.allCnaesFormatted || prevExtra?.allCnaesFormatted || '',
            qsaSummary: est.qsaSummaryFormatted || prevExtra?.qsaSummary || '',
            qsaMembers: est.qsaMembers ?? prevExtra?.qsaMembers ?? [],
            bankSummary: prevExtra?.bankSummary || item.bankSummary || '',
            bankIntl:
              prevExtra?.bankIntl ||
              item.bankIntl ||
              'Liquidação Nacional BRL / Internacional conforme CTA',
            ...(prevExtra?.bankAccounts
              ? { bankAccounts: prevExtra.bankAccounts }
              : item.bankAccounts
                ? { bankAccounts: item.bankAccounts }
                : {}),
            classification: extraClass,
            classificationLabel: RESEARCH_CENTER_CLASSIFICATIONS[extraClass].label,
            paymentFlowRule: RESEARCH_CENTER_CLASSIFICATIONS[extraClass].paymentAndGovernanceRule,
            giyusRelationship:
              prevExtra?.giyusRelationship ||
              RESEARCH_CENTER_CLASSIFICATIONS[extraClass].paymentAndGovernanceRule,
            isHeadquarter: Boolean(est.isHeadquarter ?? (est.branchOrder || '0001') === '0001'),
            branchOrder: est.branchOrder || cleanExtraCnpj.slice(8, 12) || '0001',
            establishmentTypeLabel:
              est.establishmentTypeLabel ||
              ((est.branchOrder || cleanExtraCnpj.slice(8, 12) || '0001') === '0001'
                ? 'MATRIZ (0001)'
                : `FILIAL (${est.branchOrder || cleanExtraCnpj.slice(8, 12)})`),
            enrichedFromBigDataCorp: true,
            lastWeeklySyncAtIso: nowSyncIso,
            nextWeeklySyncAtIso: nextWeeklyIso,
            weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
          };
          if (existingExtraIdx >= 0) {
            state.registeredResearchCenters[existingExtraIdx] = extraItem;
          } else {
            state.registeredResearchCenters.push(extraItem);
          }
        }

        // Sincroniza também na tabela state.organizations do banco de dados
        for (const rc of state.registeredResearchCenters) {
          const rcDigits = normalizeCnpjDigitsOnly(rc.cnpj);
          const orgCode = rcDigits
            ? `SITE-${rcDigits.slice(0, 8)}-${rc.branchOrder || rcDigits.slice(8, 12) || '0001'}`
            : `SITE-${rc.name.replace(/[^A-Za-z0-9]+/g, '-').slice(0, 18).toUpperCase()}`;
          const orgIdx = state.organizations.findIndex(
            (o) =>
              o.code.toLowerCase() === orgCode.toLowerCase() ||
              o.name.toLowerCase() === rc.name.toLowerCase(),
          );
          const orgItem: OrganizationItem = {
            id: orgIdx >= 0 ? state.organizations[orgIdx]!.id : `org-rc-${orgCode.toLowerCase()}`,
            code: orgCode,
            name: rc.name,
            kind: 'Site',
            country: 'BR',
            contactEmail: rc.emails?.[0] || rc.billingEmail || 'contratos@giyus.com.br',
            status: 'Active',
          };
          if (orgIdx >= 0) state.organizations[orgIdx] = orgItem;
          else state.organizations.push(orgItem);
        }
      }
    }
  }

  writePersistedGreenfieldMailboxState(state);

  const isGreenfield = state.isGreenfield !== false;
  return {
    pis: isGreenfield ? state.registeredPis : (state.registeredPis ?? AVAILABLE_PI_CATALOG),
    subs: isGreenfield ? state.registeredSubs : (state.registeredSubs ?? AVAILABLE_SUB_CATALOG),
    coordinators: isGreenfield
      ? state.registeredCoordinators
      : (state.registeredCoordinators ?? AVAILABLE_COORDINATOR_CATALOG),
    researchCenters: isGreenfield
      ? state.registeredResearchCenters
      : (state.registeredResearchCenters ?? AVAILABLE_RESEARCH_CENTERS_CATALOG),
    specialties: state.clinicalSpecialties ?? DEFAULT_CLINICAL_SPECIALTIES_CATALOG,
    healthFunctions: state.clinicalHealthFunctions ?? DEFAULT_CLINICAL_HEALTH_FUNCTIONS_CATALOG,
    ...(holdingSingletonError ? { holdingSingletonError } : {}),
  };
}

function manageClinicalSpecialtyOnState(
  state: FakeSuppliersAndStudiesState,
  body: Record<string, unknown>,
): { ok: boolean; specialties: readonly ClinicalSpecialtyItemDto[] } {
  if (!state.clinicalSpecialties) {
    state.clinicalSpecialties = [...DEFAULT_CLINICAL_SPECIALTIES_CATALOG];
  }
  const action = strField(body, 'action', 'CREATE').toUpperCase();
  const id = strField(body, 'id', '').trim();
  const name = strField(body, 'name', '').trim();
  const rawCode = strField(body, 'code', '').trim();
  const category = strField(body, 'category', 'Clínica Médica / Especialidade AMB-CFM').trim();
  const nowIso = new Date().toISOString();

  if (action === 'CREATE') {
    if (name) {
      const existingIdx = state.clinicalSpecialties.findIndex(
        (s) => s.name.toLowerCase() === name.toLowerCase(),
      );
      const generatedCode =
        rawCode ||
        `ESP-${name
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-zA-Z0-9]+/g, '')
          .slice(0, 8)
          .toUpperCase()}`;
      const newItem: ClinicalSpecialtyItemDto = {
        id: id || `spec-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        code: generatedCode,
        name,
        category,
        active: body['active'] !== undefined ? Boolean(body['active']) : true,
        updatedAtIso: nowIso,
      };
      if (existingIdx >= 0) {
        state.clinicalSpecialties[existingIdx] = newItem;
      } else {
        state.clinicalSpecialties.unshift(newItem);
      }
    }
  } else if (action === 'UPDATE') {
    const idx = state.clinicalSpecialties.findIndex(
      (s) => s.id === id || (name && s.name.toLowerCase() === name.toLowerCase()),
    );
    if (idx >= 0) {
      const prev = state.clinicalSpecialties[idx]!;
      state.clinicalSpecialties[idx] = {
        ...prev,
        ...(name ? { name } : {}),
        ...(rawCode ? { code: rawCode } : {}),
        ...(body['category'] !== undefined ? { category } : {}),
        ...(body['active'] !== undefined ? { active: Boolean(body['active']) } : {}),
        updatedAtIso: nowIso,
      };
    }
  } else if (action === 'DELETE') {
    state.clinicalSpecialties = state.clinicalSpecialties.filter(
      (s) => s.id !== id && (!name || s.name.toLowerCase() !== name.toLowerCase()),
    );
  }

  writePersistedGreenfieldMailboxState(state);
  return {
    ok: true,
    specialties: [...state.clinicalSpecialties],
  };
}

function manageClinicalHealthFunctionOnState(
  state: FakeSuppliersAndStudiesState,
  body: Record<string, unknown>,
): { ok: boolean; healthFunctions: readonly ClinicalHealthFunctionItemDto[] } {
  if (!state.clinicalHealthFunctions) {
    state.clinicalHealthFunctions = [...DEFAULT_CLINICAL_HEALTH_FUNCTIONS_CATALOG];
  }
  const action = strField(body, 'action', 'CREATE').toUpperCase();
  const id = strField(body, 'id', '').trim();
  const name = strField(body, 'name', '').trim();
  const rawCode = strField(body, 'code', '').trim();
  const councilAcronym = strField(body, 'councilAcronym', 'CONSELHO DE CLASSE').trim();
  const nowIso = new Date().toISOString();

  if (action === 'CREATE') {
    if (name) {
      const existingIdx = state.clinicalHealthFunctions.findIndex(
        (h) => h.name.toLowerCase() === name.toLowerCase(),
      );
      const generatedCode =
        rawCode ||
        `SAUDE-${name
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-zA-Z0-9]+/g, '')
          .slice(0, 8)
          .toUpperCase()}`;
      const newItem: ClinicalHealthFunctionItemDto = {
        id: id || `hf-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
        code: generatedCode,
        name,
        councilAcronym,
        active: body['active'] !== undefined ? Boolean(body['active']) : true,
        updatedAtIso: nowIso,
      };
      if (existingIdx >= 0) {
        state.clinicalHealthFunctions[existingIdx] = newItem;
      } else {
        state.clinicalHealthFunctions.unshift(newItem);
      }
    }
  } else if (action === 'UPDATE') {
    const idx = state.clinicalHealthFunctions.findIndex(
      (h) => h.id === id || (name && h.name.toLowerCase() === name.toLowerCase()),
    );
    if (idx >= 0) {
      const prev = state.clinicalHealthFunctions[idx]!;
      state.clinicalHealthFunctions[idx] = {
        ...prev,
        ...(name ? { name } : {}),
        ...(rawCode ? { code: rawCode } : {}),
        ...(body['councilAcronym'] !== undefined ? { councilAcronym } : {}),
        ...(body['active'] !== undefined ? { active: Boolean(body['active']) } : {}),
        updatedAtIso: nowIso,
      };
    }
  } else if (action === 'DELETE') {
    state.clinicalHealthFunctions = state.clinicalHealthFunctions.filter(
      (h) => h.id !== id && (!name || h.name.toLowerCase() !== name.toLowerCase()),
    );
  }

  writePersistedGreenfieldMailboxState(state);
  return {
    ok: true,
    healthFunctions: [...state.clinicalHealthFunctions],
  };
}

const WEEKLY_SYNC_INTERVAL_MS = 7 * 24 * 60 * 60 * 1000;

async function weeklySyncRegisteredResearchCentersOnState(
  state: FakeSuppliersAndStudiesState,
  body: Record<string, unknown>,
): Promise<WeeklySyncResearchCentersResponseDto> {
  if (!state.registeredResearchCenters) {
    state.registeredResearchCenters = [];
  }
  const forceAll = body['forceAll'] !== false;
  const targetCnpjs = Array.isArray(body['targetCnpjs'])
    ? (body['targetCnpjs'] as string[]).map((c) => normalizeCnpjDigitsOnly(String(c)))
    : [];

  let syncedCentersCount = 0;
  let totalCnesSyncedCount = 0;
  const nowMs = Date.now();
  const syncedAtIso = new Date(nowMs).toISOString();
  const nextScheduledSyncAtIso = new Date(nowMs + WEEKLY_SYNC_INTERVAL_MS).toISOString();

  // Faz cópia dos centros atuais para reconsultar
  const snapshotCenters = [...state.registeredResearchCenters];
  const seenRoots = new Set<string>();

  for (const center of snapshotCenters) {
    const cleanDigits = normalizeCnpjDigitsOnly(center.cnpj);
    if (cleanDigits.length < 14) continue;
    if (targetCnpjs.length > 0 && !targetCnpjs.includes(cleanDigits)) continue;

    const lastSyncMs = center.lastWeeklySyncAtIso ? Date.parse(center.lastWeeklySyncAtIso) : 0;
    const isDue =
      !lastSyncMs || Number.isNaN(lastSyncMs) || nowMs - lastSyncMs >= WEEKLY_SYNC_INTERVAL_MS;
    if (!forceAll && !isDue) continue;

    const root8 = cleanDigits.slice(0, 8);
    if (seenRoots.has(root8)) continue;
    seenRoots.add(root8);

    const res = await lookupCnpjAndAutoRegisterBranchesOnState(state, {
      cnpj: cleanDigits,
      includeBranches: true,
      autoRegisterEstablishments: true,
      defaultClassification: center.classification ?? 'CENTRO_NAO_GIYUS',
      defaultGiyusRelationship: center.giyusRelationship ?? center.paymentFlowRule ?? '',
      defaultCnes: center.cnes,
    });
    if (res.ok && res.found) {
      syncedCentersCount += res.allEstablishments.length;
      for (const est of res.allEstablishments) {
        totalCnesSyncedCount += est.cnesRecords?.length ?? (est.cnes ? 1 : 0);
      }
    }
  }

  // Garante atualização dos carimbos semanais em todos os centros sincronizados
  state.registeredResearchCenters = state.registeredResearchCenters.map((c) => ({
    ...c,
    lastWeeklySyncAtIso: syncedAtIso,
    nextWeeklySyncAtIso: nextScheduledSyncAtIso,
    weeklySyncSchedulePolicy: 'WEEKLY_EVERY_7_DAYS',
  }));
  writePersistedGreenfieldMailboxState(state);

  const isGreenfield = state.isGreenfield !== false;
  const updatedDirectory = {
    pis: isGreenfield ? (state.registeredPis ?? []) : (state.registeredPis ?? AVAILABLE_PI_CATALOG),
    subs: isGreenfield
      ? (state.registeredSubs ?? [])
      : (state.registeredSubs ?? AVAILABLE_SUB_CATALOG),
    coordinators: isGreenfield
      ? (state.registeredCoordinators ?? [])
      : (state.registeredCoordinators ?? AVAILABLE_COORDINATOR_CATALOG),
    researchCenters: isGreenfield
      ? (state.registeredResearchCenters ?? [])
      : (state.registeredResearchCenters ?? AVAILABLE_RESEARCH_CENTERS_CATALOG),
  };

  return {
    ok: true,
    syncedCentersCount,
    totalCnesSyncedCount,
    syncedAtIso,
    nextScheduledSyncAtIso,
    schedulePolicy: 'WEEKLY_EVERY_7_DAYS',
    updatedDirectory,
  };
}

function bindStudyTeamAndCenterAutoFillOnState(
  state: FakeSuppliersAndStudiesState,
  studyCode: string,
  body: Record<string, unknown>,
): StudyTeamMultiDocAndLearningDashboardDto {
  const code = studyCode.toUpperCase();
  const prev = getEffectiveStudyTeamAndCenterBinding(state, code);
  const nowIso = new Date().toISOString();
  const actorEmail = strField(body, 'actorEmail');

  const piName = strField(body, 'piName', prev.piName).trim();
  const piCrm = strField(body, 'piCrm', prev.piCrm).trim();
  const piSpecialty = strField(body, 'piSpecialty', prev.piSpecialty || 'Pesquisa Clínica').trim();
  const piEmail = strField(body, 'piEmail', prev.piEmail).trim();

  const rawSubs = body['subInvestigators'];
  const parsedSubs = Array.isArray(rawSubs)
    ? rawSubs
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const rec = item as Record<string, unknown>;
          const name = strField(rec, 'name', '').trim();
          if (!name) return null;
          return {
            name,
            crm: strField(rec, 'crm', 'CRM-PR'),
            specialty: strField(rec, 'specialty', 'Pesquisa Clínica'),
            email: strField(rec, 'email', ''),
          };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null)
    : [];

  const fallbackSubName = strField(body, 'subInvestigatorName', prev.subInvestigatorName).trim();
  const subInvestigators =
    parsedSubs.length > 0
      ? parsedSubs
      : fallbackSubName
        ? [
            {
              name: fallbackSubName,
              crm: strField(body, 'subInvestigatorCrm', prev.subInvestigatorCrm || 'CRM-PR'),
              specialty: strField(
                body,
                'subInvestigatorSpecialty',
                prev.subInvestigatorSpecialty || 'Pesquisa Clínica',
              ),
              email: strField(body, 'subInvestigatorEmail', prev.subInvestigatorEmail),
            },
          ]
        : [];

  const rawCoords = body['coordinators'];
  const parsedCoords = Array.isArray(rawCoords)
    ? rawCoords
        .map((item) => {
          if (!item || typeof item !== 'object') return null;
          const rec = item as Record<string, unknown>;
          const name = strField(rec, 'name', '').trim();
          if (!name) return null;
          return {
            name,
            role: strField(rec, 'role', 'Study Coordinator (SC)'),
            email: strField(rec, 'email', ''),
          };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null)
    : [];

  const fallbackCoordName = strField(body, 'coordinatorName', prev.coordinatorName).trim();
  const coordinators =
    parsedCoords.length > 0
      ? parsedCoords
      : fallbackCoordName
        ? [
            {
              name: fallbackCoordName,
              role: strField(
                body,
                'coordinatorRole',
                prev.coordinatorRole || 'Study Coordinator (SC)',
              ),
              email: strField(body, 'coordinatorEmail', prev.coordinatorEmail),
            },
          ]
        : [];

  const subInvestigatorName = subInvestigators.map((s) => s.name).join(' & ');
  const subInvestigatorCrm = subInvestigators.map((s) => s.crm).join(' / ');
  const subInvestigatorSpecialty = subInvestigators[0]?.specialty ?? prev.subInvestigatorSpecialty;
  const subInvestigatorEmail = subInvestigators
    .map((s) => s.email)
    .filter(Boolean)
    .join('; ');

  const coordinatorName = coordinators.map((c) => c.name).join(' & ');
  const coordinatorRole = coordinators[0]?.role ?? prev.coordinatorRole;
  const coordinatorEmail = coordinators
    .map((c) => c.email)
    .filter(Boolean)
    .join('; ');

  const researchCenterName = strField(body, 'researchCenterName', prev.researchCenterName).trim();
  const researchCenterLegalName = strField(
    body,
    'researchCenterLegalName',
    prev.researchCenterLegalName || researchCenterName.toUpperCase(),
  ).trim();
  const researchCenterCnpj = strField(body, 'researchCenterCnpj', prev.researchCenterCnpj).trim();
  const researchCenterCnes = strField(body, 'researchCenterCnes', prev.researchCenterCnes).trim();
  const researchCenterCityUf = strField(
    body,
    'researchCenterCityUf',
    prev.researchCenterCityUf || '',
  ).trim();
  const researchCenterAddress = strField(
    body,
    'researchCenterAddress',
    prev.researchCenterAddress || researchCenterCityUf,
  ).trim();
  const researchCenterBankSummary = strField(
    body,
    'researchCenterBankSummary',
    prev.researchCenterBankSummary,
  ).trim();
  const matchedRegisteredCenter = (state.registeredResearchCenters ?? []).find(
    (r) => r.name.toLowerCase() === researchCenterName.toLowerCase(),
  );
  let researchCenterClassification = resolveCenterClassification(
    strField(
      body,
      'researchCenterClassification',
      matchedRegisteredCenter?.classification ?? prev.researchCenterClassification ?? '',
    ),
    researchCenterName,
  );

  // Sincroniza automaticamente as pessoas/centro vinculados no diretório global de Gestão de Pessoas
  if (!state.registeredPis) state.registeredPis = [];
  if (!state.registeredSubs) state.registeredSubs = [];
  if (!state.registeredCoordinators) state.registeredCoordinators = [];
  if (!state.registeredResearchCenters) state.registeredResearchCenters = [];

  const existingHoldingInCatalog = state.registeredResearchCenters.find(
    (r) =>
      resolveCenterClassification(r.classification, r.name) === 'GIYUS_HOLDING' &&
      r.name.toLowerCase() !== researchCenterName.toLowerCase(),
  );
  if (researchCenterClassification === 'GIYUS_HOLDING' && existingHoldingInCatalog) {
    researchCenterClassification = 'CENTRO_PARCEIRO_GIYUS';
  }
  const researchCenterClassificationLabel =
    RESEARCH_CENTER_CLASSIFICATIONS[researchCenterClassification].label;
  const researchCenterPaymentFlowRule =
    RESEARCH_CENTER_CLASSIFICATIONS[researchCenterClassification].paymentAndGovernanceRule;

  if (piName && !state.registeredPis.some((p) => p.name.toLowerCase() === piName.toLowerCase())) {
    state.registeredPis.push({
      name: piName,
      crm: piCrm,
      specialty: piSpecialty,
      email: piEmail,
    });
  }
  for (const s of subInvestigators) {
    if (
      s.name &&
      !state.registeredSubs.some((p) => p.name.toLowerCase() === s.name.toLowerCase())
    ) {
      state.registeredSubs.push(s);
    }
  }
  for (const c of coordinators) {
    if (
      c.name &&
      !state.registeredCoordinators.some((p) => p.name.toLowerCase() === c.name.toLowerCase())
    ) {
      state.registeredCoordinators.push(c);
    }
  }
  if (
    researchCenterName &&
    !state.registeredResearchCenters.some(
      (r) => r.name.toLowerCase() === researchCenterName.toLowerCase(),
    )
  ) {
    state.registeredResearchCenters.push({
      name: researchCenterName,
      legalName: researchCenterLegalName,
      cnpj: researchCenterCnpj,
      cnes: researchCenterCnes,
      cityUf: researchCenterCityUf,
      address: researchCenterAddress,
      bankSummary: researchCenterBankSummary,
      classification: researchCenterClassification,
      classificationLabel: researchCenterClassificationLabel,
      paymentFlowRule: researchCenterPaymentFlowRule,
    });
  }

  const ccList = Array.from(
    new Set(
      [
        piEmail,
        ...subInvestigators.map((s) => s.email),
        ...coordinators.map((c) => c.email),
        'regulatorio@trialshub.com.br',
        'contratos@giyus.com.br',
      ].filter((e) => e.trim().length > 0),
    ),
  ).join('; ');

  const subsPreamble = subInvestigators
    .map((s, idx) => `${idx + 1}) ${s.name} (${s.crm} · ${s.specialty} · <${s.email}>)`)
    .join('; ');
  const coordsPreamble = coordinators
    .map((c, idx) => `${idx + 1}) ${c.name} (${c.role} · <${c.email}>)`)
    .join('; ');

  const preambleText = `INSTITUIÇÃO / CENTRO DE PESQUISA ATENDENTE: ${researchCenterLegalName} (${researchCenterName} · Classificação GIYUS: ${researchCenterClassificationLabel} — ${researchCenterPaymentFlowRule}), inscrita no CNPJ sob nº ${researchCenterCnpj}, CNES nº ${researchCenterCnes}, sediada em ${researchCenterAddress} (Regionalidade dos Procedimentos: ${researchCenterCityUf}), Dados Bancários (Tabela 3): ${researchCenterBankSummary}. INVESTIGADOR PRINCIPAL (PI): ${piName} (${piCrm} · ${piSpecialty} · <${piEmail}>). SUB-INVESTIGADORES (${subInvestigators.length}): ${subsPreamble}. EQUIPE DE COORDENAÇÃO DE PESQUISA (${coordinators.length}): ${coordsPreamble}.`;

  const updatedBinding: StudyTeamAndCenterBindingDto = {
    studyCode: code,
    piName,
    piCrm,
    piSpecialty,
    piEmail,
    subInvestigatorName,
    subInvestigatorCrm,
    subInvestigatorSpecialty,
    subInvestigatorEmail,
    subInvestigators,
    coordinatorName,
    coordinatorRole,
    coordinatorEmail,
    coordinators,
    researchCenterName,
    researchCenterLegalName,
    researchCenterCnpj,
    researchCenterCnes,
    researchCenterCityUf,
    researchCenterAddress,
    researchCenterBankSummary,
    researchCenterClassification,
    researchCenterClassificationLabel,
    researchCenterPaymentFlowRule,
    isAutoFilled: true,
    explicitlyBoundByUser: true,
    autoFilledFieldsCount: 16,
    autoFilledContractPreambleText: preambleText,
    autoFilledDraftRecipientsTo: prev.autoFilledDraftRecipientsTo,
    autoFilledDraftRecipientsCc: ccList,
    lastLinkedAtIso: nowIso,
    lastLinkedByEmail: actorEmail,
  };

  if (!state.studyTeamBindings) {
    state.studyTeamBindings = {};
  }
  state.studyTeamBindings[code] = updatedBinding;

  // Auto-update the study dossier qualification & clauses so nothing stays blank
  const dossierIdx = state.dossiers.findIndex((d) => d.code.toUpperCase() === code);
  if (dossierIdx >= 0 && state.dossiers[dossierIdx]) {
    const existingDossier = state.dossiers[dossierIdx];
    state.dossiers[dossierIdx] = {
      ...existingDossier,
      croName: `${researchCenterLegalName} (CNPJ ${researchCenterCnpj} · ${researchCenterCityUf} · PI ${piName} · Sub ${subInvestigatorName} · Coord. ${coordinatorName})`,
      clinicalAnalysisJson: JSON.stringify({
        complexity: `Auto-Preenchido [REGRA 09]: Centro ${researchCenterName} (CNPJ ${researchCenterCnpj} · CNES ${researchCenterCnes} · ${researchCenterCityUf}) | PI: ${piName} (${piCrm}) | Sub: ${subInvestigatorName} (${subInvestigatorCrm}) | Coordenação: ${coordinatorName} (${coordinatorEmail}) | Análise Multi-Documento (Protocolo + TCLE + TALE + Contrato) concluída.`,
      }),
      contractClausesJson: buildAutoFilledContractClausesJson(
        existingDossier.contractClausesJson,
        updatedBinding,
      ),
    };
  }

  // Automatically create or refresh the Budget Email Draft so that when the user opens/creates the draft, 100% is already filled
  const updatedDossier = state.dossiers.find((d) => d.code.toUpperCase() === code);
  const freshDraft = buildInitialBudgetEmailDraft(
    updatedDossier,
    code,
    actorEmail,
    'contratos@giyus.com.br',
    state.budgetEmailDrafts.length + 1,
    state,
  );
  const existingDraftIdx = state.budgetEmailDrafts.findIndex(
    (d) => d.studyCode.toUpperCase() === code,
  );
  if (existingDraftIdx >= 0 && state.budgetEmailDrafts[existingDraftIdx]) {
    const prevDraft = state.budgetEmailDrafts[existingDraftIdx];
    state.budgetEmailDrafts[existingDraftIdx] = {
      ...freshDraft,
      id: prevDraft.id,
      graphDraftMessageId: prevDraft.graphDraftMessageId,
      createdAtIso: prevDraft.createdAtIso,
      updatedAtIso: nowIso,
      updatedByEmail: actorEmail,
    };
  } else {
    state.budgetEmailDrafts.unshift(freshDraft);
  }

  writePersistedGreenfieldMailboxState(state);
  return buildStudyTeamMultiDocAndLearningDashboard(state, code);
}

function applyMiroFishProfitMaximizationOnState(
  state: FakeSuppliersAndStudiesState,
  studyCode: string,
  body: Record<string, unknown>,
) {
  const code = studyCode.toUpperCase();
  const nowIso = new Date().toISOString();
  const croName = strField(body, 'croName', 'Instituto de Pesquisa Hcor / Trials Hub');
  const sponsorName = strField(body, 'sponsorName', 'ASSOCIAÇÃO BENEFICENTE SÍRIA — Hcor');
  const monitorProfileLabel = strField(
    body,
    'monitorProfileLabel',
    'CRA Sênior Presencial + Remoto (SDV 100% + Visitas a cada 6 semanas)',
  );
  const senderPersonLabel = strField(
    body,
    'senderPersonLabel',
    'Tamiris Abait Miranda / Gabriela Batista <tabait@hcor.com.br> (Coordenação Multicêntrica)',
  );
  const studySizeLabel = strField(
    body,
    'studySizeLabel',
    'Megatrial Multicêntrico Competitivo (3.000+ participantes)',
  );
  const sponsorSizeLabel = strField(
    body,
    'sponsorSizeLabel',
    'Hospital de Excelência / PROADI-SUS Grande Porte',
  );
  const studyDurationMonths = numField(body, 'studyDurationMonths', 48);
  const controllablePatientsCount = numField(body, 'controllablePatientsCount', 28);
  const controllableRetentionPct = numField(body, 'controllableRetentionPct', 95);
  const controllableCounterproposalPct = numField(body, 'controllableCounterproposalPct', 10);
  const controllableScreenFailurePct = numField(body, 'controllableScreenFailurePct', 12);
  const controllablePassThroughShieldPct = numField(body, 'controllablePassThroughShieldPct', 100);
  const controllableBatchingEfficiencyPct = numField(body, 'controllableBatchingEfficiencyPct', 22);
  const controllableAnnualIpcaRebalance = Boolean(
    body['controllableAnnualIpcaRebalance'] ?? studyDurationMonths > 12,
  );
  const baseNetProfitBrl = numField(body, 'baseNetProfitBrl', 26850);
  const simulatedGrossRevenueBrl = numField(body, 'simulatedGrossRevenueBrl', 214800);
  const simulatedNetProfitBrl = numField(body, 'simulatedNetProfitBrl', 94620);
  const simulatedNetMarginPct = numField(body, 'simulatedNetMarginPct', 44.05);
  const acceptanceProbabilityPct = numField(body, 'acceptanceProbabilityPct', 88);
  const profitGainOverBaseBrl = numField(
    body,
    'profitGainOverBaseBrl',
    simulatedNetProfitBrl - baseNetProfitBrl,
  );
  const tacticalDraftSummary = strField(
    body,
    'tacticalDraftSummary',
    `Estratégia de Lucro Máximo MiroFish (${code}): Lucro Líquido otimizado de R$ ${baseNetProfitBrl.toLocaleString('pt-BR')} para R$ ${simulatedNetProfitBrl.toLocaleString('pt-BR')} (+R$ ${profitGainOverBaseBrl.toLocaleString('pt-BR')} · Margem ${simulatedNetMarginPct.toFixed(1)}%) considerando CRO (${croName}), Patrocinador (${sponsorName}), Monitor (${monitorProfileLabel}), Remetente (${senderPersonLabel}), Tamanho do Estudo (${studySizeLabel}), Tamanho do Patrocinador (${sponsorSizeLabel}) e Tempo de Estudo (${studyDurationMonths} meses).`,
  );

  const updatedMiroFishJson = JSON.stringify(
    {
      scenariosRun: 1000,
      optimizationMode: 'MAX_NET_PROFIT_MULTIVARIABLE_MIROFISH',
      studyCode: code,
      contextConsidered: {
        cro: croName,
        sponsor: sponsorName,
        monitor: monitorProfileLabel,
        emailSenderPerson: senderPersonLabel,
        studySize: studySizeLabel,
        sponsorSize: sponsorSizeLabel,
        studyDurationMonths,
      },
      controllableClinicalLevers: {
        competitiveRecruitmentPatients: controllablePatientsCount,
        patientRetentionUntilFinalVisitPct: controllableRetentionPct,
        counterproposalAdjustmentPct: controllableCounterproposalPct,
        preScreeningFailureRatePct: controllableScreenFailurePct,
        passThroughShieldCoveragePct: controllablePassThroughShieldPct,
        visitBatchingEfficiencyPct: controllableBatchingEfficiencyPct,
        annualIpcaRebalanceClause: controllableAnnualIpcaRebalance,
      },
      financialOutcome: {
        baseNetProfitBrl,
        simulatedGrossRevenueBrl,
        maximizedNetProfitBrl: simulatedNetProfitBrl,
        profitGainOverBaseBrl,
        maximizedNetMarginPct: simulatedNetMarginPct,
        acceptanceProbabilityPercent: acceptanceProbabilityPct,
      },
    },
    null,
    2,
  );

  const dossierIdx = state.dossiers.findIndex((d) => d.code.toUpperCase() === code);
  if (dossierIdx >= 0 && state.dossiers[dossierIdx]) {
    state.dossiers[dossierIdx] = {
      ...state.dossiers[dossierIdx],
      miroFishSimulationJson: updatedMiroFishJson,
    };
  }

  const dossier = state.dossiers.find((d) => d.code.toUpperCase() === code);
  const baseDraft = buildInitialBudgetEmailDraft(
    dossier,
    code,
    'syllas@giyus.com.br',
    'contratos@giyus.com.br',
    state.budgetEmailDrafts.length + 1,
    state,
  );

  const mirofishDraftSection = [
    '',
    '=== C. SIMULAÇÃO MIROFISH DE LUCRO MÁXIMO & ALAVANCAS CONTROLÁVEIS DA PESQUISA CLÍNICA ===',
    `• Contexto Calibrado (7 Dimensões): CRO (${croName}) | Patrocinador (${sponsorName} · ${sponsorSizeLabel}) | Monitor (${monitorProfileLabel}) | Remetente (${senderPersonLabel}) | Tamanho do Estudo (${studySizeLabel}) | Tempo de Estudo (${studyDurationMonths} meses).`,
    `• Alavancas Controláveis Ativadas no Centro: Meta de Recrutamento Competitivo de ${controllablePatientsCount} participantes, Retenção até a última visita de ${controllableRetentionPct}%, Pré-Triagem Clínica c/ Screen Failure de ${controllableScreenFailurePct}%, Reajuste de +${controllableCounterproposalPct}%, Blindagem Pass-Through de ${controllablePassThroughShieldPct}% (TCLE/TALE + Monitoria Extra) e Repactuação Anual IPCA (${controllableAnnualIpcaRebalance ? 'ATIVA' : 'Dispensada'}).`,
    `• Resultado Projetado p/ Tomada de Decisão: Receita Bruta R$ ${simulatedGrossRevenueBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} | Lucro Líquido Máximo R$ ${simulatedNetProfitBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} (Ganho de +R$ ${profitGainOverBaseBrl.toLocaleString('pt-BR', { minimumFractionDigits: 2 })} sobre o cenário base · Margem Líquida ${simulatedNetMarginPct.toFixed(1)}% · Probabilidade de Aceite ${acceptanceProbabilityPct}%).`,
    `• Diretriz Tática: ${tacticalDraftSummary}`,
  ].join('\n');

  const finalBody = `${baseDraft.currentBody}\n${mirofishDraftSection}`;
  const updatedDraft: BudgetEmailDraftItemDto = {
    ...baseDraft,
    currentBody: finalBody,
    originalBody: finalBody,
    updatedAtIso: nowIso,
  };

  const existingDraftIdx = state.budgetEmailDrafts.findIndex(
    (d) => d.studyCode.toUpperCase() === code,
  );
  if (existingDraftIdx >= 0 && state.budgetEmailDrafts[existingDraftIdx]) {
    const prev = state.budgetEmailDrafts[existingDraftIdx];
    state.budgetEmailDrafts[existingDraftIdx] = {
      ...updatedDraft,
      id: prev.id,
      graphDraftMessageId: prev.graphDraftMessageId,
      createdAtIso: prev.createdAtIso,
    };
  } else {
    state.budgetEmailDrafts.unshift(updatedDraft);
  }

  writePersistedGreenfieldMailboxState(state);
  return {
    studyCode: code,
    updatedMiroFishSimulationJson: updatedMiroFishJson,
    updatedDraft:
      state.budgetEmailDrafts.find((d) => d.studyCode.toUpperCase() === code) ?? updatedDraft,
  };
}

function learnCroSponsorAndGlobalNegotiationOnState(
  state: FakeSuppliersAndStudiesState,
  studyCode: string,
  body: Record<string, unknown>,
): StudyTeamMultiDocAndLearningDashboardDto {
  const code = studyCode.toUpperCase();
  const nowIso = new Date().toISOString();
  const actorEmail = strField(body, 'actorEmail');

  const croName = strField(body, 'croName', `CRO (${code})`);
  const croBehaviorPatternLearned = strField(
    body,
    'croBehaviorPatternLearned',
    'Padrão contratual da CRO mapeado a partir de Protocolo, TCLE, TALE e Minuta CTA.',
  );
  const croConcessionLeversLearned = strField(
    body,
    'croConcessionLeversLearned',
    'Alavancas de concessão validadas com justificativa clínica e ética.',
  );
  const sponsorName = strField(body, 'sponsorName', `Patrocinador (${code})`);
  const sponsorBudgetPolicyLearned = strField(
    body,
    'sponsorBudgetPolicyLearned',
    'Política orçamentária do Patrocinador mapeada.',
  );
  const sponsorProtocolTcleTaleFlexibilityLearned = strField(
    body,
    'sponsorProtocolTcleTaleFlexibilityLearned',
    'Acolhe ajustes de reembolso Pass-Through quando vinculados às exigências do Protocolo, TCLE e TALE.',
  );
  const globalContractNegotiationLesson = strField(
    body,
    'globalContractNegotiationLesson',
    'Cruzar sempre Protocolo + TCLE + TALE + Contrato para fundamentar repactuação anual e cobertura integral de custos éticos e clínicos.',
  );

  const ruleId = `grule-learn-${code.toLowerCase()}-${String(state.globalRules.length + 11).padStart(3, '0')}`;
  const createdRule: GlobalAiRuleItemDto = {
    id: ruleId,
    studyCode: code,
    ruleTitle: `Aprendizado CRO (${croName}) + Patrocinador (${sponsorName}) & Negociação Global [${code}]`,
    ruleText: `[CRO: ${croBehaviorPatternLearned} | Alavanca: ${croConcessionLeversLearned}] [Patrocinador: ${sponsorBudgetPolicyLearned} | TCLE/TALE: ${sponsorProtocolTcleTaleFlexibilityLearned}] [Lição Global de Negociação: ${globalContractNegotiationLesson}]`,
    originModule: 'MULTI_DOC_CRO_SPONSOR_LEARNING',
    proposedByEmail: actorEmail,
    status: 'APPROVED_GLOBAL',
    approvedByEmail: 'syllas@giyus.com.br',
    updatedAtIso: nowIso,
  };
  state.globalRules.unshift(createdRule);

  const nextGen = state.harness2State.evolutionGeneration + 1;
  state.harness2State = {
    ...state.harness2State,
    evolutionGeneration: nextGen,
    alwaysOnTokensUsed: state.harness2State.alwaysOnTokensUsed + 165,
    longTermMemoryExcerpt: `${state.harness2State.longTermMemoryExcerpt}\n- [${ruleId.toUpperCase()} · MULTI_DOC_CRO_SPONSOR_LEARNING] ${createdRule.ruleTitle}: ${createdRule.ruleText}`,
    experienceLogExcerpt: `${state.harness2State.experienceLogExcerpt}\n- [Gen #${nextGen} · ${code} · Aprendizado CRO/Patrocinador/Negociação Global] ${globalContractNegotiationLesson}`,
  };

  if (!state.croSponsorGlobalLearnings) {
    state.croSponsorGlobalLearnings = [];
  }
  const newLearning: CroSponsorAndGlobalNegotiationLearningDto = {
    id: `learn-${code.toLowerCase()}-${Date.now()}`,
    studyCode: code,
    croName,
    croBehaviorPatternLearned,
    croConcessionLeversLearned,
    sponsorName,
    sponsorBudgetPolicyLearned,
    sponsorProtocolTcleTaleFlexibilityLearned,
    globalContractNegotiationLesson,
    obeyedByLlms: [
      'OpenAI GPT-5 / o3-pro',
      'Anthropic Claude 4 Opus / Sonnet',
      'Google DeepMind Gemini 2.5 Pro',
      'Google Research Harness² (c1..c8)',
    ],
    promotedGlobalRuleId: ruleId,
    updatedAtIso: nowIso,
  };
  state.croSponsorGlobalLearnings.unshift(newLearning);

  // Also inject the newly learned negotiation lesson into the study's email draft
  const dossier = state.dossiers.find((d) => d.code.toUpperCase() === code);
  const updatedDraft = buildInitialBudgetEmailDraft(
    dossier,
    code,
    actorEmail,
    'contratos@giyus.com.br',
    state.budgetEmailDrafts.length + 1,
    state,
  );
  const existingDraftIdx = state.budgetEmailDrafts.findIndex(
    (d) => d.studyCode.toUpperCase() === code,
  );
  if (existingDraftIdx >= 0 && state.budgetEmailDrafts[existingDraftIdx]) {
    const prevDraft = state.budgetEmailDrafts[existingDraftIdx];
    state.budgetEmailDrafts[existingDraftIdx] = {
      ...updatedDraft,
      id: prevDraft.id,
      graphDraftMessageId: prevDraft.graphDraftMessageId,
      createdAtIso: prevDraft.createdAtIso,
      updatedAtIso: nowIso,
      updatedByEmail: actorEmail,
    };
  } else {
    state.budgetEmailDrafts.unshift(updatedDraft);
  }

  writePersistedGreenfieldMailboxState(state);
  return buildStudyTeamMultiDocAndLearningDashboard(state, code);
}

function computeBudgetEmailDraftDiff(
  original: { to: string; cc: string; subject: string; body: string },
  current: { to: string; cc: string; subject: string; body: string },
): BudgetEmailDraftChangeItemDto[] {
  const changes: BudgetEmailDraftChangeItemDto[] = [];

  if (original.to.trim() !== current.to.trim()) {
    changes.push({
      field: 'TO',
      description: 'Destinatário (Para) alterado na Caixa Compartilhada',
      originalValue: original.to.trim(),
      newValue: current.to.trim(),
    });
  }

  if (original.cc.trim() !== current.cc.trim()) {
    changes.push({
      field: 'CC',
      description: 'Cópia (CC) alterada na Caixa Compartilhada',
      originalValue: original.cc.trim(),
      newValue: current.cc.trim(),
    });
  }

  if (original.subject.trim() !== current.subject.trim()) {
    changes.push({
      field: 'SUBJECT',
      description: 'Assunto do e-mail alterado na Caixa Compartilhada',
      originalValue: original.subject.trim(),
      newValue: current.subject.trim(),
    });
  }

  const origLines = original.body
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const currLines = current.body
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  const financialPattern = /R\$\s?[\d.,]+|\d+(?:[.,]\d+)?%|\b\d+\s*dias\b/i;

  const matchedOrigIndices = new Set<number>();
  const matchedCurrIndices = new Set<number>();

  // First pass: exact matches
  for (let i = 0; i < origLines.length; i++) {
    const oLine = origLines[i] ?? '';
    const exactIdx = currLines.findIndex(
      (cLine, cIdx) => !matchedCurrIndices.has(cIdx) && cLine === oLine,
    );
    if (exactIdx >= 0) {
      matchedOrigIndices.add(i);
      matchedCurrIndices.add(exactIdx);
    }
  }

  // Second pass: pair remaining lines by numbered prefix (e.g. "1.", "2.") or position
  for (let i = 0; i < origLines.length; i++) {
    if (matchedOrigIndices.has(i)) {
      continue;
    }
    const oLine = origLines[i] ?? '';
    const prefixMatch = /^(\d+\.|[-•])\s*\S+/.exec(oLine);
    const prefix = prefixMatch ? prefixMatch[0] : oLine.slice(0, 18);

    let pairedCurrIdx = currLines.findIndex(
      (cLine, cIdx) =>
        !matchedCurrIndices.has(cIdx) && prefix.length >= 3 && cLine.startsWith(prefix),
    );
    if (pairedCurrIdx < 0 && i < currLines.length && !matchedCurrIndices.has(i)) {
      pairedCurrIdx = i;
    }

    if (pairedCurrIdx >= 0) {
      const cLine = currLines[pairedCurrIdx] ?? '';
      matchedOrigIndices.add(i);
      matchedCurrIndices.add(pairedCurrIdx);
      const isBudgetChange = financialPattern.test(oLine) || financialPattern.test(cLine);
      changes.push({
        field: isBudgetChange ? 'BUDGET_VALUE' : 'BODY_LINE',
        description: isBudgetChange
          ? 'Valor ou condição orçamentária alterada em relação ao rascunho original'
          : 'Trecho do rascunho original modificado na Caixa Compartilhada',
        originalValue: oLine,
        newValue: cLine,
      });
    } else {
      matchedOrigIndices.add(i);
      changes.push({
        field: 'BODY_LINE',
        description: 'Trecho do rascunho original removido na Caixa Compartilhada',
        originalValue: oLine,
        newValue: '(Removido pelo usuário na caixa)',
      });
    }
  }

  // Third pass: lines added in currentBody
  for (let j = 0; j < currLines.length; j++) {
    if (matchedCurrIndices.has(j)) {
      continue;
    }
    const cLine = currLines[j] ?? '';
    const isBudgetAddition = financialPattern.test(cLine);
    changes.push({
      field: isBudgetAddition ? 'BUDGET_VALUE' : 'BODY_LINE',
      description: isBudgetAddition
        ? 'Nova condição/valor orçamentário adicionado no e-mail da Caixa Compartilhada'
        : 'Novo parágrafo/observação adicionado no e-mail da Caixa Compartilhada',
      originalValue: '(Não constava no rascunho original)',
      newValue: cLine,
    });
  }

  return changes;
}

function runAutonomousProcurementCycleOnState(state: FakeSuppliersAndStudiesState): void {
  const pol = state.procurementPolicy;
  state.integratedProcurementOrders = state.integratedProcurementOrders.map((order) => {
    if (order.approvalStatus !== 'PENDING_HUMAN_APPROVAL') {
      return order;
    }
    const eligibleByTrigger =
      (order.essentialityTriggerKind === 'PRODUTO_QUE_VENHA_A_VENCER' &&
        pol.autoApproveExpiringMedications) ||
      (order.essentialityTriggerKind === 'NECESSARIO_PARA_UM_ESTUDO' &&
        pol.autoApproveStudyRequiredItems) ||
      (order.essentialityTriggerKind === 'PRODUTO_ESSENCIAL_NECESSARIO' &&
        pol.autoApproveEssentialSiteProducts);
    const withinCeiling =
      order.totalOrderBrl <= pol.autoApprovalCeilingPerOrderBrl &&
      order.unitLandedPriceBrl <= order.contractOrBudgetCeilingUnitBrl;
    const isNotNonEssential = order.essentialityTriggerKind !== 'NAO_ESSENCIAL_ACIMA_TETO';

    if (eligibleByTrigger && withinCeiling && isNotNonEssential) {
      return {
        ...order,
        approvalStatus: 'AUTO_ORDERED_WITHOUT_HUMAN',
        approvedOrTriggeredBy:
          'Motor Autônomo de Compras GIYUS (Fase 2 Sem Aprovação Humana · Compra Essencial dentro do Teto)',
        approvedAtIso: '2026-10-27T06:10:00Z',
        erpIntegrationTrail: `${order.erpIntegrationTrail} → Auto-Compra Emitida Sem Intervenção Humana (PO enviada ao fornecedor + 3-Way Match agendado)`,
      };
    }
    return order;
  });
}

function buildAutonomousProcurementSnapshot(
  state: FakeSuppliersAndStudiesState,
): AutonomousProcurementSnapshotDto {
  const orders = state.integratedProcurementOrders;
  const pendingHuman = orders.filter((o) => o.approvalStatus === 'PENDING_HUMAN_APPROVAL').length;
  const humanApproved = orders.filter(
    (o) => o.approvalStatus === 'APPROVED_BY_HUMAN_ORDERED',
  ).length;
  const autoZeroTouch = orders.filter(
    (o) => o.approvalStatus === 'AUTO_ORDERED_WITHOUT_HUMAN',
  ).length;
  const totalCommitted = Number(orders.reduce((acc, o) => acc + o.totalOrderBrl, 0).toFixed(2));
  const totalSavings = Number(orders.reduce((acc, o) => acc + o.savingsVsCeilingBrl, 0).toFixed(2));

  return {
    policy: state.procurementPolicy,
    totalOrdersCount: orders.length,
    pendingHumanApprovalCount: pendingHuman,
    humanApprovedCount: humanApproved,
    autonomousZeroTouchCount: autoZeroTouch,
    totalCommittedBrl: totalCommitted,
    totalSavingsVsCeilingBrl: totalSavings,
    orders,
  };
}

function runAutonomousPickupCycleOnState(state: FakeSuppliersAndStudiesState): void {
  const pol = state.pickupPolicy;
  state.supplierPickups = state.supplierPickups.map((p, idx) => {
    if (
      p.essentialityTriggerKind === 'COLETA_ESPECIAL_EXIGE_HUMANO' ||
      p.estimatedCostBrl > pol.autoApprovalCeilingPerPickupBrl ||
      p.estimatedCostBrl > p.contractCeilingBrl
    ) {
      return p;
    }

    const triggerAllowed =
      (p.essentialityTriggerKind === 'COLETA_AMOSTRA_VISITA_ESTUDO' &&
        pol.autoApproveBiologicalSamplePickups) ||
      (p.essentialityTriggerKind === 'COLETA_CALIBRACAO_OU_COMODATO_T30D' &&
        pol.autoApproveCalibrationAndReverseLogisticsPickups) ||
      (p.essentialityTriggerKind === 'COLETA_ESSENCIAL_OPERACAO_DESCARTE' &&
        pol.autoApproveCertifiedDisposalPickups);

    if (!triggerAllowed) {
      return p;
    }

    if (p.workflowStatus === 'PENDING_HUMAN_APPROVAL') {
      if (pol.autoTriggerSupplierSlotBookingWhenApiAvailable) {
        const autoAwb = `AWB-AUTO-${p.supplierCode}-${202680 + idx}`;
        return {
          ...p,
          workflowStatus: 'SUPPLIER_SCHEDULED_CONFIRMED',
          approvedOrTriggeredBy:
            'Fase 2 Automática Sem Humano: Coleta aprovada automaticamente + Slot de coleta agendado via integração B2B do fornecedor',
          approvedAtIso: '2026-10-27T06:28:00Z',
          scheduledPickupDate: '2026-10-28 (Terça-feira · Janela Seg–Qua Validada)',
          scheduledPickupWindow: '10:00 às 12:00 (Slot SLA Automático B2B)',
          awbOrTrackingCode: autoAwb,
          courierAgentName: `Frota Dedicada ${p.supplierName} (DataLogger Validado)`,
          supplierScheduledByEmail: `api.b2b@${p.supplierCode.toLowerCase()}.com.br`,
          supplierScheduledAtIso: '2026-10-27T06:28:05Z',
          erpIntegrationTrail: `${p.erpIntegrationTrail} → Aprovada Sem Humano (Fase 2) → Agendada Automaticamente com o Fornecedor (${autoAwb})`,
        };
      }
      return {
        ...p,
        workflowStatus: 'AUTO_APPROVED_AWAITING_SUPPLIER_SCHEDULE',
        approvedOrTriggeredBy:
          'Fase 2 Automática Sem Humano: Coleta aprovada sem intervenção humana e disparada para o fornecedor agendar',
        approvedAtIso: '2026-10-27T06:28:00Z',
        erpIntegrationTrail: `${p.erpIntegrationTrail} → Aprovada Sem Humano (Fase 2) → ⏳ Fornecedor Notificado para Agendar Coleta`,
      };
    }

    if (
      (p.workflowStatus === 'APPROVED_BY_HUMAN_AWAITING_SUPPLIER_SCHEDULE' ||
        p.workflowStatus === 'AUTO_APPROVED_AWAITING_SUPPLIER_SCHEDULE') &&
      pol.autoTriggerSupplierSlotBookingWhenApiAvailable
    ) {
      const autoAwb = `AWB-AUTO-${p.supplierCode}-${202690 + idx}`;
      return {
        ...p,
        workflowStatus: 'SUPPLIER_SCHEDULED_CONFIRMED',
        scheduledPickupDate: '2026-10-28 (Terça-feira · Janela Seg–Qua Validada)',
        scheduledPickupWindow: '13:30 às 15:30 (Slot SLA Confirmado pelo Fornecedor)',
        awbOrTrackingCode: autoAwb,
        courierAgentName: `Agente Credenciado ${p.supplierName}`,
        supplierScheduledByEmail: `agendamento@${p.supplierCode.toLowerCase()}.com.br`,
        supplierScheduledAtIso: '2026-10-27T06:28:10Z',
        erpIntegrationTrail: `${p.erpIntegrationTrail} → Slot de Coleta Confirmado Automaticamente pelo Fornecedor (${autoAwb})`,
      };
    }

    return p;
  });
}

function buildSupplierPickupSnapshot(
  state: FakeSuppliersAndStudiesState,
): SupplierPickupSnapshotDto {
  const pickups = state.supplierPickups;
  const pendingHuman = pickups.filter((p) => p.workflowStatus === 'PENDING_HUMAN_APPROVAL').length;
  const awaitingSchedule = pickups.filter(
    (p) =>
      p.workflowStatus === 'APPROVED_BY_HUMAN_AWAITING_SUPPLIER_SCHEDULE' ||
      p.workflowStatus === 'AUTO_APPROVED_AWAITING_SUPPLIER_SCHEDULE',
  ).length;
  const scheduledConfirmed = pickups.filter(
    (p) =>
      p.workflowStatus === 'SUPPLIER_SCHEDULED_CONFIRMED' ||
      p.workflowStatus === 'PICKUP_COMPLETED',
  ).length;
  const autoZeroTouch = pickups.filter(
    (p) =>
      p.workflowStatus === 'AUTO_APPROVED_AWAITING_SUPPLIER_SCHEDULE' ||
      p.approvedOrTriggeredBy.includes(''),
  ).length;
  const totalCost = Number(pickups.reduce((acc, p) => acc + p.estimatedCostBrl, 0).toFixed(2));
  const totalSavings = Number(
    pickups.reduce((acc, p) => acc + p.savingsVsCeilingBrl, 0).toFixed(2),
  );

  return {
    policy: state.pickupPolicy,
    totalPickupsCount: pickups.length,
    pendingHumanApprovalCount: pendingHuman,
    awaitingSupplierScheduleCount: awaitingSchedule,
    supplierScheduledConfirmedCount: scheduledConfirmed,
    autoProcessedWithoutHumanCount: autoZeroTouch,
    totalEstimatedCostBrl: totalCost,
    totalSavingsBrl: totalSavings,
    pickups,
  };
}

function buildAccountingBillingSnapshot(
  state: FakeSuppliersAndStudiesState,
): AccountingBillingSnapshotDto {
  state.accountingContractBuckets = state.accountingContractBuckets.map((b) => {
    const studyCharges = state.accountingAccumulatedCharges.filter(
      (c) => c.studyCode.toUpperCase() === b.studyCode.toUpperCase(),
    );
    const studyInvoices = state.accountingIssuedInvoices.filter(
      (inv) => inv.studyCode.toUpperCase() === b.studyCode.toUpperCase(),
    );
    const readyBrl = Number(
      studyCharges
        .filter((c) => c.status === 'ACCUMULATING_READY' || c.status === 'URGENT_RETROACTIVE_RISK')
        .reduce((acc, c) => acc + c.grossTotalBrl, 0)
        .toFixed(2),
    );
    const expiringBrl = Number(
      studyCharges
        .filter((c) => c.status === 'URGENT_RETROACTIVE_RISK')
        .reduce((acc, c) => acc + c.grossTotalBrl, 0)
        .toFixed(2),
    );
    const conditionalBrl = Number(
      studyCharges
        .filter((c) => c.status === 'CONDITIONAL_WAITING_UNLOCK')
        .reduce((acc, c) => acc + c.grossTotalBrl, 0)
        .toFixed(2),
    );
    const invoicedBrl = Number(
      studyInvoices.reduce((acc, inv) => acc + inv.grossAmountBrl, 0).toFixed(2),
    );
    const holdbackBrl = Number(
      (
        studyInvoices.reduce((acc, inv) => acc + inv.holdbackRetainedBrl, 0) +
        studyCharges
          .filter(
            (c) => c.status === 'ACCUMULATING_READY' || c.status === 'URGENT_RETROACTIVE_RISK',
          )
          .reduce((acc, c) => acc + c.holdbackRetainedBrl, 0)
      ).toFixed(2),
    );
    return {
      ...b,
      readyToBillBrl: readyBrl,
      expiringRiskBrl: expiringBrl,
      conditionalEscrowBrl: conditionalBrl,
      totalAlreadyInvoicedBrl: invoicedBrl,
      holdbackRetainedCumulativeBrl: holdbackBrl,
    };
  });

  const readyCharges = state.accountingAccumulatedCharges.filter(
    (c) => c.status === 'ACCUMULATING_READY' || c.status === 'URGENT_RETROACTIVE_RISK',
  );
  const expiringCharges = state.accountingAccumulatedCharges.filter(
    (c) => c.status === 'URGENT_RETROACTIVE_RISK',
  );
  const conditionalCharges = state.accountingAccumulatedCharges.filter(
    (c) => c.status === 'CONDITIONAL_WAITING_UNLOCK',
  );

  return {
    totalReadyToBillAccumulatedBrl: Number(
      readyCharges.reduce((acc, c) => acc + c.grossTotalBrl, 0).toFixed(2),
    ),
    totalExpiringRetroactiveRiskBrl: Number(
      expiringCharges.reduce((acc, c) => acc + c.grossTotalBrl, 0).toFixed(2),
    ),
    totalHoldbackRetainedReceivableBrl: Number(
      state.accountingContractBuckets
        .reduce((acc, b) => acc + b.holdbackRetainedCumulativeBrl, 0)
        .toFixed(2),
    ),
    totalConditionalEscrowBrl: Number(
      conditionalCharges.reduce((acc, c) => acc + c.grossTotalBrl, 0).toFixed(2),
    ),
    totalGrossInvoicedBrl: Number(
      state.accountingIssuedInvoices.reduce((acc, i) => acc + i.grossAmountBrl, 0).toFixed(2),
    ),
    totalNetInvoicedBrl: Number(
      state.accountingIssuedInvoices
        .reduce((acc, i) => acc + i.netReceivableAmountBrl, 0)
        .toFixed(2),
    ),
    readyChargesCount: readyCharges.length,
    expiringRiskChargesCount: expiringCharges.length,
    conditionalChargesCount: conditionalCharges.length,
    issuedInvoicesCount: state.accountingIssuedInvoices.length,
    contractBuckets: state.accountingContractBuckets,
    accumulatedCharges: state.accountingAccumulatedCharges,
    issuedInvoices: state.accountingIssuedInvoices,
    proactiveGaps: state.accountingProactiveGaps,
  };
}

const DEFAULT_FAKE_FINANCE_REIMBURSEMENT_ITEMS: readonly FinanceMailboxReimbursementItemDto[] = [
  {
    id: 'FIN-REIMB-2026-001',
    graphMessageId: 'AAMkADg0YzE-FIN-REIMB-001',
    mailboxAddress: 'financeiro@giyus.com.br',
    receivedAtIso: '2026-10-27T08:15:00Z',
    senderName: 'Faturamento Diagnósticos Cardiovasculares Sul',
    senderEmail: 'faturamento.diagnostico@cardiosul-exames.com.br',
    subject:
      'NF-e #000.148.920 + Laudo Ecocardiograma Strain e Coleta Extra V2 — Reembolso Pass-Through Estudo EPOCA',
    bodyPreview:
      'Prezado Financeiro GIYUS / Trials Hub, encaminhamos a NF-e #000.148.920 referente ao Ecocardiograma Transtorácico com Strain Longitudinal e kit de coleta extra do participante SUBJ-EPOCA-04 na Visita V2 para reembolso contratual Pass-Through.',
    attachments: [
      'NFe_000148920_EcoStrain_EPOCA.xml',
      'DANFE_000148920_Reembolso_EPOCA.pdf',
      'Comprovante_Execucao_Clinica_SUBJ_EPOCA_04.pdf',
    ],
    invoiceNumber: 'NF-e 000.148.920',
    documentKind: 'NF_E_DANFE_XML',
    issuerCnpj: '14.892.310/0001-44',
    invoiceAmountBrl: 2900.0,
    isReimbursementCandidate: true,
    aiDetectionConfidencePercent: 97,
    reimbursementCategoryCode: 'PASS_THROUGH_EXAME_DIAGNOSTICO',
    reimbursementCategoryLabel:
      'Reembolso Pass-Through de Exame Diagnóstico & Procedimento Protocolar',
    studyCode: 'EPOCA',
    sponsorOrCroName: 'ASSOCIAÇÃO BENEFICENTE SÍRIA — Hcor · Exclusivo Trials Hub Ltda',
    contractClauseReference:
      'Anexo Financeiro I EPOCA — Reembolso Integral Pass-Through s/ Holdback (Exames de Imagem & Visitas Extras)',
    studyPassThroughCeilingBrl: 74200.0,
    studyPassThroughRemainingBrl: 58400.0,
    billingTaxTreatment: 'NOTA_DEBITO_PURA_SEM_HOLD_0PCT',
    grossUpTaxPercent: 0.0,
    holdbackPercent: 0.0,
    rebillableToSponsorAmountBrl: 2900.0,
    clinicalOriginReference:
      'Participante SUBJ-EPOCA-04 · Visita V2 (Dia 1) · Validado por PI Dr. Marco Stephan Lofrano Alves',
    reconciliationStatus: 'AGUARDANDO_CONCILIACAO_FINANCEIRA',
    accountingAccumulatorCode: null,
    reconciledByEmail: null,
    reconciledAtIso: null,
    auditSha256Hash: '9f4a81c2e7104b93',
    reconciliationNotes:
      'Detectado automaticamente na caixa financeiro@giyus.com.br: NF-e com XML SEFAZ anexo e referência explícita ao estudo EPOCA e reembolso Pass-Through.',
  },
  {
    id: 'FIN-REIMB-2026-002',
    graphMessageId: 'AAMkADg0YzE-FIN-REIMB-002',
    mailboxAddress: 'financeiro@giyus.com.br',
    receivedAtIso: '2026-10-27T07:50:00Z',
    senderName: 'Grupo Fleury — Faturamento Pesquisa Clínica',
    senderEmail: 'faturamento.pesquisa@grupofleury.com.br',
    subject:
      'NFS-e #2026-4418 — Tomografia Tórax/Abdome RECIST 1.1 (Reembolso Screen Failure) — Protocolo ONC-SOLAIR-204',
    bodyPreview:
      'Encaminhamos ao financeiro@giyus.com.br a Nota Fiscal de Serviço Eletrônica #2026-4418 referente a 2 Tomografias Computadorizadas com laudo RECIST 1.1 elegíveis a reembolso Pass-Through conforme Cláusula 11.3 do contrato SOLAIR.',
    attachments: [
      'NFSe_2026_4418_Fleury_TC_RECIST.pdf',
      'NFSe_2026_4418_PadraoNacional.xml',
      'Termo_Elegibilidade_PassThrough_SOLAIR.pdf',
    ],
    invoiceNumber: 'NFS-e 2026-4418',
    documentKind: 'NFS_E_SERVICO_DIAGNOSTICO',
    issuerCnpj: '60.840.055/0001-31',
    invoiceAmountBrl: 4300.0,
    isReimbursementCandidate: true,
    aiDetectionConfidencePercent: 98,
    reimbursementCategoryCode: 'PASS_THROUGH_EXAME_DIAGNOSTICO',
    reimbursementCategoryLabel:
      'Reembolso Pass-Through de Exame de Imagem em Screen Failure (Cláusula 11.3)',
    studyCode: 'SOLAIR',
    sponsorOrCroName: 'Novartis Biociências S.A. / ICON Clinical Research',
    contractClauseReference:
      'Cláusula 11.3 CTA SOLAIR — Reembolso 100% Pass-Through de TC RECIST 1.1 e Biópsia em Falha de Triagem',
    studyPassThroughCeilingBrl: 112500.0,
    studyPassThroughRemainingBrl: 89200.0,
    billingTaxTreatment: 'NFSE_REEMBOLSO_COM_GROSS_UP_14_25PCT',
    grossUpTaxPercent: 14.25,
    holdbackPercent: 0.0,
    rebillableToSponsorAmountBrl: 5014.58,
    clinicalOriginReference:
      'Participantes SUBJ-SOL-09 & SUBJ-SOL-11 · Screening Oncologia · Laudo RECIST 1.1 arquivado no eISF',
    reconciliationStatus: 'AGUARDANDO_CONCILIACAO_FINANCEIRA',
    accountingAccumulatorCode: null,
    reconciledByEmail: null,
    reconciledAtIso: null,
    auditSha256Hash: '3c19e08a552d71b4',
    reconciliationNotes:
      'Patrocinador exige refaturamento via NFS-e: aplicado Gross-Up de 14,25% (R$ 5.014,58) e Holdback 0% para preservar margem líquida.',
  },
  {
    id: 'FIN-REIMB-2026-003',
    graphMessageId: 'AAMkADg0YzE-UNASSIGNED-006',
    mailboxAddress: 'financeiro@giyus.com.br',
    receivedAtIso: '2026-10-25T15:12:00Z',
    senderName: 'Roberto Tavares (Consultoria Contábil Externa)',
    senderEmail: 'roberto.tavares@tavarescontabil.com.br',
    subject:
      'Dúvida sobre Alíquota de ISS e Retenção na Fonte — Aditivo de Visita Extra (Dra. Helena)',
    bodyPreview:
      'Prezados, precisamos confirmar em qual dossiê orçamentário devemos alocar a nota fiscal de reembolso das coletas extras realizadas na última quarta-feira.',
    attachments: ['Espelho_NFSe_Reembolso_Coletas_Extras_DraHelena.pdf'],
    invoiceNumber: 'NFS-e 2026-3990 (Prévia)',
    documentKind: 'NFS_E_REEMBOLSO_COLETA_EXTRA',
    issuerCnpj: '28.411.902/0001-19',
    invoiceAmountBrl: 1890.0,
    isReimbursementCandidate: true,
    aiDetectionConfidencePercent: 86,
    reimbursementCategoryCode: 'COLETA_EXTRA_VISITA_NAO_PROGRAMADA',
    reimbursementCategoryLabel: 'Reembolso de Visita Não Programada / Coleta Extra de Segurança',
    studyCode: 'EPOCA',
    sponsorOrCroName: 'ASSOCIAÇÃO BENEFICENTE SÍRIA — Hcor · Exclusivo Trials Hub Ltda',
    contractClauseReference:
      'Seção 4.4 CTA — Unscheduled Visits (100% Reembolsável por Procedimento Executado)',
    studyPassThroughCeilingBrl: 74200.0,
    studyPassThroughRemainingBrl: 58400.0,
    billingTaxTreatment: 'NFSE_REEMBOLSO_COM_GROSS_UP_14_25PCT',
    grossUpTaxPercent: 14.25,
    holdbackPercent: 0.0,
    rebillableToSponsorAmountBrl: 2204.08,
    clinicalOriginReference:
      'Coleta PK Extra de Segurança (Quarta-feira) · Equipe Dra. Helena / Enfermagem de Pesquisa',
    reconciliationStatus: 'AGUARDANDO_CONCILIACAO_FINANCEIRA',
    accountingAccumulatorCode: null,
    reconciledByEmail: null,
    reconciledAtIso: null,
    auditSha256Hash: '7b82d44e19a033f1',
    reconciliationNotes:
      'E-mail recebido em financeiro@giyus.com.br solicitando confirmação do estudo destino para a nota fiscal de reembolso das coletas extras.',
  },
  {
    id: 'FIN-REIMB-2026-004',
    graphMessageId: 'AAMkADg0YzE-UNASSIGNED-005',
    mailboxAddress: 'financeiro@giyus.com.br',
    receivedAtIso: '2026-10-25T16:45:00Z',
    senderName: 'Clara Mendes (Biogen LatAm Budgets)',
    senderEmail: 'clara.mendes@biogen-trials.com',
    subject:
      'Complemento da Grade de Ressonância Magnética Funcional — Protocolo NEUROVANCE (NV-301)',
    bodyPreview:
      'Complementando o e-mail anterior sobre o estudo NEUROVANCE NV-301, enviamos a tabela de reembolso Pass-through para exames de RM 3 Tesla e a nota de qualificação do Phantom.',
    attachments: [
      'Anexo_PassThrough_RM3T_NEUROVANCE.xlsx',
      'NFe_000088412_Phantom_RM3T_Startup.pdf',
    ],
    invoiceNumber: 'NF-e 000.088.412',
    documentKind: 'NF_E_PASS_THROUGH_STARTUP',
    issuerCnpj: '41.209.774/0001-80',
    invoiceAmountBrl: 3650.0,
    isReimbursementCandidate: true,
    aiDetectionConfidencePercent: 92,
    reimbursementCategoryCode: 'PASS_THROUGH_EXAME_DIAGNOSTICO',
    reimbursementCategoryLabel: 'Reembolso Pass-Through de Qualificação RM 3 Tesla (Start-Up)',
    studyCode: 'ARGON',
    sponsorOrCroName: 'Biogen Global / Dossiê em Triagem (NEUROVANCE / ARGON)',
    contractClauseReference:
      'Anexo Pass-Through Start-Up — Reembolso Integral de Qualificação de Imagem 3T',
    studyPassThroughCeilingBrl: 48900.0,
    studyPassThroughRemainingBrl: 45250.0,
    billingTaxTreatment: 'NOTA_DEBITO_PURA_SEM_HOLD_0PCT',
    grossUpTaxPercent: 0.0,
    holdbackPercent: 0.0,
    rebillableToSponsorAmountBrl: 3650.0,
    clinicalOriginReference:
      'Qualificação Radiológica RM 3 Tesla · Engenharia Clínica & Neuroimagem',
    reconciliationStatus: 'AGUARDANDO_CONCILIACAO_FINANCEIRA',
    accountingAccumulatorCode: null,
    reconciledByEmail: null,
    reconciledAtIso: null,
    auditSha256Hash: '5e91c770a8321b0c',
    reconciliationNotes:
      'Tabela e NF-e de reembolso Pass-Through para RM 3 Tesla recebidas na caixa compartilhada do financeiro.',
  },
  {
    id: 'FIN-REIMB-2026-005',
    graphMessageId: 'AAMkADg0YzE-FIN-STERICYCLE-005',
    mailboxAddress: 'financeiro@giyus.com.br',
    receivedAtIso: '2026-10-27T06:17:00Z',
    senderName: 'Stericycle / Ambipar Gestão Ambiental',
    senderEmail: 'faturamento@stericycle.com.br',
    subject:
      'NFS-e #2026-1109 + Certificado de Destruição CDR-2026-991 (Reembolso Pass-Through Descarte IMP)',
    bodyPreview:
      'Segue NFS-e #2026-1109 acompanhada do Certificado de Destruição Térmica CDR-2026-991 para reembolso Pass-Through junto ao patrocinador do estudo SOLAIR.',
    attachments: ['NFSe_2026_1109_Stericycle.pdf', 'CDR_2026_991_Incineracao.pdf'],
    invoiceNumber: 'NFS-e 2026-1109',
    documentKind: 'NFS_E_COM_CERTIFICADO_CDR',
    issuerCnpj: '01.568.077/0001-95',
    invoiceAmountBrl: 1420.0,
    isReimbursementCandidate: true,
    aiDetectionConfidencePercent: 100,
    reimbursementCategoryCode: 'PASS_THROUGH_INSUMO_EQUIPAMENTO',
    reimbursementCategoryLabel: 'Reembolso Pass-Through de Incineração Regulatória (CDR/ANVISA)',
    studyCode: 'SOLAIR',
    sponsorOrCroName: 'Novartis Biociências S.A. / ICON Clinical Research',
    contractClauseReference:
      'Anexo Farmácia Clínica — Destruição de IMP c/ Certificado CDR (100% Pass-Through s/ Holdback)',
    studyPassThroughCeilingBrl: 112500.0,
    studyPassThroughRemainingBrl: 87780.0,
    billingTaxTreatment: 'NOTA_DEBITO_PURA_SEM_HOLD_0PCT',
    grossUpTaxPercent: 0.0,
    holdbackPercent: 0.0,
    rebillableToSponsorAmountBrl: 1420.0,
    clinicalOriginReference:
      'Farmácia Clínica · Lote IMP Expirado · Certificado CDR-2026-991 Validado',
    reconciliationStatus: 'CONCILIADA_REEMBOLSO_PASS_THROUGH',
    accountingAccumulatorCode: 'COB-ACUM-REIMB-2026-1109',
    reconciledByEmail: 'syllas@giyus.com.br',
    reconciledAtIso: '2026-10-27T06:22:00Z',
    auditSha256Hash: 'a44f12b90c883d17',
    reconciliationNotes:
      'Conciliada como Nota Fiscal de Reembolso Pass-Through e já injetada no Acumulador Contábil sem retenção de Holdback.',
  },
  {
    id: 'FIN-REIMB-2026-006',
    graphMessageId: 'AAMkADg0YzE-FIN-ORDINARY-006',
    mailboxAddress: 'financeiro@giyus.com.br',
    receivedAtIso: '2026-10-26T18:10:00Z',
    senderName: 'LimpClin Serviços Gerais & Facilities',
    senderEmail: 'faturamento@limpclin-sp.com.br',
    subject: 'NFS-e #2026-8801 — Conservação e Limpeza Predial Mensal da Unidade',
    bodyPreview:
      'Segue nota fiscal referente ao contrato mensal de limpeza predial das áreas administrativas e recepção geral.',
    attachments: ['NFSe_2026_8801_Limpeza_Predial.pdf'],
    invoiceNumber: 'NFS-e 2026-8801',
    documentKind: 'NFS_E_SERVICO_PREDIAL',
    issuerCnpj: '33.190.442/0001-10',
    invoiceAmountBrl: 2150.0,
    isReimbursementCandidate: false,
    aiDetectionConfidencePercent: 96,
    reimbursementCategoryCode: 'FATURA_ORDINARIA_NAO_REEMBOLSO',
    reimbursementCategoryLabel:
      'Despesa Operacional Ordinária da Unidade (Não Reembolsável por Patrocinador)',
    studyCode: 'CUSTO_UNIDADE',
    sponsorOrCroName: 'Custo Interno do Centro (Overhead Operacional)',
    contractClauseReference:
      'Coberto pela Taxa de Overhead (30%) — Não constitui reembolso Pass-Through',
    studyPassThroughCeilingBrl: 0.0,
    studyPassThroughRemainingBrl: 0.0,
    billingTaxTreatment: 'AP_DESPESA_ORDINARIA_CENTRO',
    grossUpTaxPercent: 0.0,
    holdbackPercent: 0.0,
    rebillableToSponsorAmountBrl: 0.0,
    clinicalOriginReference: 'Facilities & Manutenção Predial Mensal',
    reconciliationStatus: 'CLASSIFICADA_DESPESA_ORDINARIA_UNIDADE',
    accountingAccumulatorCode: null,
    reconciledByEmail: 'syllas@giyus.com.br',
    reconciledAtIso: '2026-10-26T18:30:00Z',
    auditSha256Hash: 'c02b994e17d5a820',
    reconciliationNotes:
      'Triado na caixa financeiro@giyus.com.br: NÃO é Nota Fiscal de Reembolso. Alocado diretamente em Contas a Pagar da unidade.',
  },
];

function buildFinanceMailboxReimbursementDashboard(
  state: FakeSuppliersAndStudiesState,
): FinanceMailboxReimbursementDashboardDto {
  const items = state.financeReimbursementItems;
  const pending = items.filter(
    (i) => i.reconciliationStatus === 'AGUARDANDO_CONCILIACAO_FINANCEIRA',
  );
  const reconciled = items.filter(
    (i) => i.reconciliationStatus === 'CONCILIADA_REEMBOLSO_PASS_THROUGH',
  );
  const ordinary = items.filter(
    (i) => i.reconciliationStatus === 'CLASSIFICADA_DESPESA_ORDINARIA_UNIDADE',
  );

  return {
    // REGRA 01/segurança: o fake não conhece (nem expõe) usuário, host ou nome de banco reais.
    topology: {
      activeProfile: 'não configurado',
      localEnvironmentMode: 'não configurado',
      localMongoConnectionUri: 'não configurado',
      localDatabaseName: 'não configurado',
      localBootstrapStrategy: 'não configurado',
      remoteEnvironmentMode: 'não configurado',
      remoteMongoAtlasConnectionMasked: 'não configurado',
      remoteDatabaseName: 'não configurado',
      remoteBootstrapStrategy: 'não configurado',
      sharedMailboxAddress: 'financeiro@giyus.com.br',
      runtimeFramework: 'não configurado',
    },
    totalFinanceEmailsCount: items.length,
    pendingReimbursementConciliationCount: pending.length,
    reconciledAsReimbursementCount: reconciled.length,
    reclassifiedAsOrdinaryExpenseCount: ordinary.length,
    totalPendingReimbursementAmountBrl: Number(
      pending.reduce((acc, i) => acc + i.invoiceAmountBrl, 0).toFixed(2),
    ),
    totalReconciledReimbursementAmountBrl: Number(
      reconciled.reduce((acc, i) => acc + i.rebillableToSponsorAmountBrl, 0).toFixed(2),
    ),
    items,
  };
}

function reconcileFakeFinanceMailboxInvoice(
  state: FakeSuppliersAndStudiesState,
  id: string,
  body: Record<string, unknown>,
): FinanceMailboxReimbursementItemDto | null {
  const idx = state.financeReimbursementItems.findIndex(
    (i) => i.id.toUpperCase() === id.toUpperCase(),
  );
  const current = state.financeReimbursementItems[idx];
  if (idx < 0 || !current) {
    return null;
  }

  const decision = strField(
    body,
    'decision',
    'CONCILIAR_COMO_REEMBOLSO_PASS_THROUGH',
  ).toUpperCase();
  const studyCode = strField(body, 'studyCode', current.studyCode).toUpperCase();
  const taxTreatment = strField(body, 'billingTaxTreatment', current.billingTaxTreatment);
  const actor = strField(body, 'reconciledByEmail');
  const notes = strField(body, 'notes', '');
  const nowIso = new Date().toISOString();

  if (decision === 'CLASSIFICAR_DESPESA_ORDINARIA_NAO_REEMBOLSO') {
    const updatedOrdinary: FinanceMailboxReimbursementItemDto = {
      ...current,
      isReimbursementCandidate: false,
      studyCode: 'CUSTO_UNIDADE',
      billingTaxTreatment: 'AP_DESPESA_ORDINARIA_CENTRO',
      grossUpTaxPercent: 0,
      holdbackPercent: 0,
      rebillableToSponsorAmountBrl: 0,
      reconciliationStatus: 'CLASSIFICADA_DESPESA_ORDINARIA_UNIDADE',
      accountingAccumulatorCode: null,
      reconciledByEmail: actor,
      reconciledAtIso: nowIso,
      reconciliationNotes:
        notes ||
        `Reclassificado por ${actor}: NÃO é Nota Fiscal de Reembolso. Alocado como despesa operacional ordinária da unidade.`,
    };
    state.financeReimbursementItems[idx] = updatedOrdinary;
    return updatedOrdinary;
  }

  if (decision === 'SOLICITAR_CARTA_CORRECAO_FISCAL') {
    const updatedCorrection: FinanceMailboxReimbursementItemDto = {
      ...current,
      studyCode,
      reconciliationStatus: 'PENDENTE_CARTA_CORRECAO_OU_XML',
      reconciledByEmail: actor,
      reconciledAtIso: nowIso,
      reconciliationNotes:
        notes ||
        `Retido em auditoria fiscal por ${actor}: solicitada Carta de Correção Eletrônica (CC-e) ou envio do XML SEFAZ para comprovar vínculo com o estudo ${studyCode}.`,
    };
    state.financeReimbursementItems[idx] = updatedCorrection;
    return updatedCorrection;
  }

  const applyGrossUp = taxTreatment.toUpperCase().includes('GROSS_UP');
  const grossUpPct = applyGrossUp ? 14.25 : 0;
  const rebillable = applyGrossUp
    ? Number((current.invoiceAmountBrl / (1 - grossUpPct / 100)).toFixed(2))
    : current.invoiceAmountBrl;
  const digits = current.invoiceNumber.replace(/\D/g, '').slice(-4) || '0999';
  const accCode = `COB-ACUM-REIMB-${studyCode}-${digits}`;

  const updatedReimb: FinanceMailboxReimbursementItemDto = {
    ...current,
    isReimbursementCandidate: true,
    studyCode,
    billingTaxTreatment: taxTreatment,
    grossUpTaxPercent: grossUpPct,
    holdbackPercent: 0,
    rebillableToSponsorAmountBrl: rebillable,
    studyPassThroughRemainingBrl: Math.max(
      0,
      Number((current.studyPassThroughRemainingBrl - current.invoiceAmountBrl).toFixed(2)),
    ),
    reconciliationStatus: 'CONCILIADA_REEMBOLSO_PASS_THROUGH',
    accountingAccumulatorCode: accCode,
    reconciledByEmail: actor,
    reconciledAtIso: nowIso,
    reconciliationNotes:
      notes ||
      `Conciliada como Nota Fiscal de Reembolso Pass-Through do estudo ${studyCode} por ${actor}. Gerado item ${accCode} no Acumulador Contábil (Holdback 0%).`,
  };
  state.financeReimbursementItems[idx] = updatedReimb;

  // Injeta no Acumulador de Cobranças da Contabilidade
  if (!state.accountingAccumulatedCharges.some((c) => c.chargeCode === accCode)) {
    state.accountingAccumulatedCharges.unshift({
      id: `ACC-REIMB-${Date.now()}`,
      chargeCode: accCode,
      createdAtIso: nowIso,
      performedDate: nowIso.slice(0, 10),
      agingDays: 1,
      daysUntilRetroactiveForfeiture: 89,
      studyCode,
      contractCode: `CTA-${studyCode}-2026`,
      originSourceModule: `Caixa Compartilhada (financeiro@giyus.com.br) · Conciliação Reembolso (${current.id})`,
      chargeCategory: 'PASS_THROUGH_COMPRAS_FORNECEDORES',
      billingFlowKind: 'INVOICE_REQUIRED_COM_COMPROVANTE',
      title: `Reembolso Conciliado de financeiro@giyus.com.br: ${current.invoiceNumber} — ${current.subject}`,
      clinicalOrContractReference: current.contractClauseReference,
      participantOrBatchRef: current.clinicalOriginReference,
      quantity: 1,
      unitContractPriceBrl: rebillable,
      inflationOrFxAdjustmentBrl: 0,
      grossTotalBrl: rebillable,
      holdbackRetainedBrl: 0,
      estimatedTaxWithheldBrl: 0,
      netReceivableBrl: rebillable,
      status: 'ACCUMULATING_READY',
      proactiveGapWarning: 'Conciliado em financeiro@giyus.com.br — Isento de Holdback (0%)',
      linkedInvoiceNumber: null,
      supportingEvidenceNote: `NF de Reembolso: ${current.invoiceNumber} (${current.attachments.join(', ')})`,
    });
  }

  return updatedReimb;
}

function simulateFakeFinanceMailboxEmail(
  state: FakeSuppliersAndStudiesState,
  body: Record<string, unknown>,
): FinanceMailboxReimbursementItemDto {
  const nextNum = state.financeReimbursementItems.length + 1;
  const id = `FIN-REIMB-2026-00${nextNum}`;
  const nowIso = new Date().toISOString();
  const subject =
    strField(
      body,
      'subject',
      'NF-e #000.219.500 — Reembolso Pass-Through de Exames / Procedimentos',
    ).trim() || 'NF-e #000.219.500 — Reembolso Pass-Through de Exames / Procedimentos';
  const bodyPreview =
    strField(
      body,
      'bodyPreview',
      'Encaminhamos para financeiro@giyus.com.br a nota fiscal de reembolso.',
    ).trim() || 'Encaminhamos para financeiro@giyus.com.br a nota fiscal de reembolso.';
  const invoiceNumber =
    strField(body, 'invoiceNumber', 'NF-e 000.219.500').trim() || 'NF-e 000.219.500';
  const amount = numField(body, 'invoiceAmountBrl', 1680.0) || 1680.0;
  const studyCode = (strField(body, 'suggestedStudyCode', 'EPOCA').trim() || 'EPOCA').toUpperCase();
  const combined = `${subject} ${bodyPreview} ${invoiceNumber}`.toLowerCase();
  const isReimb =
    combined.includes('reembolso') ||
    combined.includes('pass-through') ||
    combined.includes('exame') ||
    combined.includes('transporte') ||
    combined.includes('visita extra');

  const item: FinanceMailboxReimbursementItemDto = {
    id,
    graphMessageId: `AAMkADg0YzE-SIM-00${nextNum}`,
    mailboxAddress: 'financeiro@giyus.com.br',
    receivedAtIso: nowIso,
    senderName: strField(body, 'senderName'),
    senderEmail: strField(body, 'senderEmail'),
    subject,
    bodyPreview,
    attachments: [
      `${invoiceNumber.replace(/\s+/g, '_')}.xml`,
      `${invoiceNumber.replace(/\s+/g, '_')}_DANFE.pdf`,
    ],
    invoiceNumber,
    documentKind: strField(body, 'documentKind', 'NF_E_DANFE_XML'),
    issuerCnpj: strField(body, 'issuerCnpj'),
    invoiceAmountBrl: amount,
    isReimbursementCandidate: isReimb,
    aiDetectionConfidencePercent: isReimb ? 96 : 85,
    reimbursementCategoryCode: combined.includes('transporte')
      ? 'REEMBOLSO_DESPESA_PACIENTE'
      : 'PASS_THROUGH_EXAME_DIAGNOSTICO',
    reimbursementCategoryLabel: combined.includes('transporte')
      ? 'Reembolso de Despesa de Participante (Transporte / Alimentação / Stipend)'
      : 'Reembolso Pass-Through de Exame Diagnóstico / Procedimento do Estudo',
    studyCode,
    sponsorOrCroName: `Patrocinador / CRO (${studyCode})`,
    contractClauseReference: `Anexo Pass-Through ${studyCode} — Reembolso Integral sem Holdback`,
    studyPassThroughCeilingBrl: 74200.0,
    studyPassThroughRemainingBrl: 58400.0,
    billingTaxTreatment: 'NOTA_DEBITO_PURA_SEM_HOLD_0PCT',
    grossUpTaxPercent: 0,
    holdbackPercent: 0,
    rebillableToSponsorAmountBrl: isReimb ? amount : 0,
    clinicalOriginReference: `Triagem Automática na Caixa financeiro@giyus.com.br · Estudo ${studyCode}`,
    reconciliationStatus: 'AGUARDANDO_CONCILIACAO_FINANCEIRA',
    accountingAccumulatorCode: null,
    reconciledByEmail: null,
    reconciledAtIso: null,
    auditSha256Hash: `f81d4fae7dec00${nextNum}`,
    reconciliationNotes: isReimb
      ? `Detectada chegada de Nota Fiscal candidata a Reembolso (${studyCode}) em financeiro@giyus.com.br. Aguardando conciliação.`
      : 'Detectado e-mail sem gatilho explícito de reembolso em financeiro@giyus.com.br.',
  };

  state.financeReimbursementItems = [item, ...state.financeReimbursementItems];
  return item;
}

function getEffectiveMailboxFolder(msg: SharedMailboxMessageItem): 'INBOX' | 'OUTBOX' {
  if (msg.mailboxFolder === 'OUTBOX' || msg.mailboxFolder === 'INBOX') {
    return msg.mailboxFolder;
  }
  const senderLower = msg.senderEmail.toLowerCase();
  const subjLower = msg.subject.trim().toLowerCase();
  if (
    senderLower.endsWith('@giyus.com.br') &&
    (subjLower.startsWith('re:') || subjLower.startsWith('res:'))
  ) {
    return 'OUTBOX';
  }
  return 'INBOX';
}

function stripEmailSubjectPrefixes(subject: string): string {
  let clean = subject.trim();
  const prefixRegex = /^(?:re|res|fw|fwd|enc|aw|wg|tr)\s*:\s*/i;
  let safety = 0;
  while (prefixRegex.test(clean) && safety < 10) {
    clean = clean.replace(prefixRegex, '').trim();
    safety++;
  }
  return clean.replace(/\s+/g, ' ').trim();
}

function normalizeSubjectKey(subject: string): string {
  const clean = stripEmailSubjectPrefixes(subject)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[–—_]/g, '-')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return clean || 'sem-assunto';
}

function parseJsonStringArray(raw?: string | null): string[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is string => typeof item === 'string' && item.length > 0);
    }
    return [];
  } catch {
    return [];
  }
}

function detectThreadProtocolHint(messages: readonly SharedMailboxMessageItem[]): string | null {
  for (const m of messages) {
    if (m.assignedProjectCode) {
      return m.assignedProjectCode.toUpperCase();
    }
  }
  for (const m of messages) {
    const meta = extractStudyMetadataFromRealEmail(m);
    if (meta?.code) {
      return meta.code.toUpperCase();
    }
    const upper = `${m.subject} ${m.bodyPreview}`.toUpperCase();
    if (upper.includes('NEUROVANCE') || upper.includes('NV-301') || upper.includes('NV301')) {
      return 'NEUROVANCE';
    }
    if (upper.includes('SOLAIR') || upper.includes('D7820C00004')) {
      return 'SOLAIR';
    }
    if (upper.includes('PERSIST') || upper.includes('222503')) {
      return 'PERSIST';
    }
    if (upper.includes('ROCK FORWARD') || upper.includes('R9457-ONC-2409')) {
      return 'ROCK-FORWARD';
    }
    if (
      upper.includes('ADEPT-4') ||
      upper.includes('ADEPT 4') ||
      upper.includes('CN012-0056') ||
      upper.includes('CN0120056')
    ) {
      return 'ADEPT-4';
    }
    if (upper.includes('EPOCA')) {
      return 'EPOCA';
    }
    if (upper.includes('7064521')) {
      return 'ESTUDO-7064521';
    }
    if (upper.includes('SOLICITACAO DE PAF') || upper.includes('SOLICITAÇÃO DE PAF')) {
      return 'PAF-CPESQ';
    }
  }
  return null;
}

function formatTimeDeltaLabel(prevIso: string, currentIso: string): string {
  const prevMs = Date.parse(prevIso);
  const currMs = Date.parse(currentIso);
  if (!Number.isFinite(prevMs) || !Number.isFinite(currMs)) {
    return 'Mesmo dia';
  }
  const diffMinutes = Math.max(0, Math.round((currMs - prevMs) / 60000));
  if (diffMinutes < 1) {
    return '< 1 min depois';
  }
  if (diffMinutes < 60) {
    return `+${diffMinutes} min`;
  }
  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return `+${diffHours}h`;
  }
  const diffDays = Math.round(diffHours / 24);
  return diffDays === 1 ? '+1 dia' : `+${diffDays} dias`;
}

function buildSharedMailboxThreadsDashboard(
  state: FakeSuppliersAndStudiesState,
): SharedMailboxThreadsDashboardDto {
  interface RawThreadBucket {
    threadId: string;
    canonicalSubject: string;
    subjectVariants: Set<string>;
    messages: SharedMailboxMessageItem[];
    protocolHint: string | null;
    groupingOrigin: SharedMailboxThreadItemDto['groupingOrigin'];
  }

  // Step 1: Group messages by exact normalized subject
  const bucketMap = new Map<string, RawThreadBucket>();
  for (const msg of state.messages) {
    const cleanSubj = stripEmailSubjectPrefixes(msg.subject);
    const key = `thread-${normalizeSubjectKey(msg.subject).slice(0, 68)}`;
    const existing = bucketMap.get(key);
    if (existing) {
      existing.messages.push(msg);
      existing.subjectVariants.add(msg.subject);
    } else {
      bucketMap.set(key, {
        threadId: key,
        canonicalSubject: cleanSubj,
        subjectVariants: new Set([msg.subject]),
        messages: [msg],
        protocolHint: null,
        groupingOrigin: 'AUTO_EXACT_SUBJECT',
      });
    }
  }

  for (const bucket of bucketMap.values()) {
    bucket.protocolHint = detectThreadProtocolHint(bucket.messages);
  }

  // Step 2: Detect doubtful pairs among base buckets BEFORE applying confirmed doubt merges
  const baseBuckets = Array.from(bucketMap.values());
  const allDoubts: SharedMailboxThreadDoubtItemDto[] = [];
  const mergeParent = new Map<
    string,
    { targetId: string; origin: SharedMailboxThreadItemDto['groupingOrigin'] }
  >();

  const findRoot = (id: string): string => {
    let curr = id;
    const visited = new Set<string>();
    while (mergeParent.has(curr) && !visited.has(curr)) {
      visited.add(curr);
      curr = mergeParent.get(curr)!.targetId;
    }
    return curr;
  };

  for (let i = 0; i < baseBuckets.length; i++) {
    const a = baseBuckets[i]!;
    for (let j = i + 1; j < baseBuckets.length; j++) {
      const b = baseBuckets[j]!;
      const sameProtocol = Boolean(a.protocolHint) && a.protocolHint === b.protocolHint;

      const subjALower = a.canonicalSubject.toLowerCase();
      const subjBLower = b.canonicalSubject.toLowerCase();
      const isPrefixOrSubstring =
        (subjALower.length >= 12 && subjBLower.includes(subjALower)) ||
        (subjBLower.length >= 12 && subjALower.includes(subjBLower));

      const sendersA = new Set(
        a.messages
          .map((m) => m.senderEmail.toLowerCase())
          .filter((e) => !e.endsWith('@giyus.com.br')),
      );
      const sendersB = new Set(
        b.messages
          .map((m) => m.senderEmail.toLowerCase())
          .filter((e) => !e.endsWith('@giyus.com.br')),
      );
      const sharedExternalSenders = Array.from(sendersA).filter((s) => sendersB.has(s));

      const domainsA = new Set(
        a.messages
          .map((m) => m.senderDomain.toLowerCase())
          .filter((d) => d !== 'giyus.com.br' && d !== 'gmail.com'),
      );
      const domainsB = new Set(
        b.messages
          .map((m) => m.senderDomain.toLowerCase())
          .filter((d) => d !== 'giyus.com.br' && d !== 'gmail.com'),
      );
      const sharedExternalDomains = Array.from(domainsA).filter((d) => domainsB.has(d));

      if (!sameProtocol && !isPrefixOrSubstring && sharedExternalSenders.length === 0) {
        continue;
      }

      const sortedIds = [a.threadId, b.threadId].sort();
      const doubtId = `doubt-${sortedIds[0]}--${sortedIds[1]}`;
      const sharedSignals: string[] = [];
      let similarity = 68;
      let reason = '';

      if (sameProtocol && a.protocolHint) {
        sharedSignals.push(`Mesmo Estudo/Protocolo: ${a.protocolHint}`);
        similarity += 16;
      }
      if (isPrefixOrSubstring) {
        sharedSignals.push('Assunto com sufixo/prefixo derivado do mesmo tema');
        similarity += 12;
      }
      if (sharedExternalSenders.length > 0) {
        sharedSignals.push(`Mesmo Remetente Externo: ${sharedExternalSenders.join(', ')}`);
        similarity += 8;
      } else if (sharedExternalDomains.length > 0) {
        sharedSignals.push(`Mesmo Domínio/CRO: @${sharedExternalDomains.join(', @')}`);
        similarity += 5;
      }

      const hasAttachmentForward =
        Array.from(a.subjectVariants).some((s) => /^(fwd|fw|enc):/i.test(s.trim())) ||
        Array.from(b.subjectVariants).some((s) => /^(fwd|fw|enc):/i.test(s.trim()));
      if (hasAttachmentForward) {
        sharedSignals.push('Contém encaminhamento (Fwd:/ENC:) com variação na linha de assunto');
      }

      similarity = Math.min(96, similarity);

      if (sameProtocol && a.protocolHint && a.protocolHint !== 'PAF-CPESQ') {
        reason = `Os e-mails referem-se ao mesmo protocolo (${a.protocolHint}), porém possuem linhas de assunto diferentes ("${a.canonicalSubject}" vs. "${b.canonicalSubject}"). Deseja confirmar que fazem parte da mesma thread para unificar na Linha do Tempo?`;
      } else if (isPrefixOrSubstring) {
        reason = `O assunto "${b.canonicalSubject}" parece ser uma variação/resposta direta de "${a.canonicalSubject}" com texto adicional no assunto. Confirmar que pertencem à mesma thread?`;
      } else {
        reason = `Mensagens do mesmo remetente (${sharedExternalSenders.join(', ')}) recebidas próximas na caixa compartilhada com assuntos distintos ("${a.canonicalSubject}" vs. "${b.canonicalSubject}"). Confirmar se pertencem à mesma thread?`;
      }

      const resolution = state.threadDoubtResolutions[doubtId];
      const status: SharedMailboxThreadDoubtItemDto['status'] = resolution
        ? resolution.decision === 'CONFIRM_SAME_THREAD'
          ? 'CONFIRMED_MERGED'
          : 'REJECTED_SEPARATED'
        : 'PENDING_USER_CONFIRMATION';

      if (status === 'CONFIRMED_MERGED') {
        const rootA = findRoot(a.threadId);
        const rootB = findRoot(b.threadId);
        if (rootA !== rootB) {
          mergeParent.set(rootB, {
            targetId: rootA,
            origin: 'USER_CONFIRMED_DOUBT',
          });
        }
      }

      const latestMsgA = [...a.messages].sort((x, y) =>
        y.receivedAtIso.localeCompare(x.receivedAtIso),
      )[0]!;
      const latestMsgB = [...b.messages].sort((x, y) =>
        y.receivedAtIso.localeCompare(x.receivedAtIso),
      )[0]!;

      allDoubts.push({
        id: doubtId,
        candidateThreadAId: a.threadId,
        candidateThreadASubject: a.canonicalSubject,
        candidateThreadBId: b.threadId,
        candidateThreadBSubject: b.canonicalSubject,
        messageIdsA: a.messages.map((m) => m.id),
        messageIdsB: b.messages.map((m) => m.id),
        senderSummaryA: `${latestMsgA.senderName} <${latestMsgA.senderEmail}> (${a.messages.length} msg)`,
        senderSummaryB: `${latestMsgB.senderName} <${latestMsgB.senderEmail}> (${b.messages.length} msg)`,
        receivedAtA: latestMsgA.receivedAtIso,
        receivedAtB: latestMsgB.receivedAtIso,
        similarityScorePercent: similarity,
        doubtReason: reason,
        sharedSignals,
        suggestedStudyCode:
          a.protocolHint && a.protocolHint !== 'PAF-CPESQ' ? a.protocolHint : null,
        status,
        resolvedByEmail: resolution?.reviewedBy ?? null,
        resolvedAtIso: resolution?.resolvedAtIso ?? null,
      });
    }
  }

  // Step 3: Apply manual merges requested by the user
  for (const [sourceId, targetId] of Object.entries(state.manualThreadMerges)) {
    if (bucketMap.has(sourceId) && bucketMap.has(targetId)) {
      const rootSource = findRoot(sourceId);
      const rootTarget = findRoot(targetId);
      if (rootSource !== rootTarget) {
        mergeParent.set(rootSource, {
          targetId: rootTarget,
          origin: 'USER_MANUAL_MERGE',
        });
      }
    }
  }

  // Step 4: Consolidate merged buckets
  const mergedBucketsMap = new Map<string, RawThreadBucket>();
  for (const bucket of baseBuckets) {
    const rootId = findRoot(bucket.threadId);
    const mergeInfo = mergeParent.get(bucket.threadId);
    const existingRoot = mergedBucketsMap.get(rootId);
    if (existingRoot) {
      existingRoot.messages.push(...bucket.messages);
      for (const v of bucket.subjectVariants) {
        existingRoot.subjectVariants.add(v);
      }
      if (!existingRoot.protocolHint && bucket.protocolHint) {
        existingRoot.protocolHint = bucket.protocolHint;
      }
      existingRoot.groupingOrigin = mergeInfo?.origin ?? 'USER_CONFIRMED_DOUBT';
    } else {
      const rootTemplate = bucketMap.get(rootId) ?? bucket;
      mergedBucketsMap.set(rootId, {
        threadId: rootId,
        canonicalSubject: rootTemplate.canonicalSubject,
        subjectVariants: new Set([
          ...Array.from(rootTemplate.subjectVariants),
          ...Array.from(bucket.subjectVariants),
        ]),
        messages: [...bucket.messages],
        protocolHint: rootTemplate.protocolHint ?? bucket.protocolHint,
        groupingOrigin:
          rootId !== bucket.threadId
            ? (mergeInfo?.origin ?? 'USER_CONFIRMED_DOUBT')
            : bucket.groupingOrigin,
      });
    }
  }

  // Deduplicate messages inside each merged bucket and build chronological timeline
  const pendingDoubts = allDoubts.filter((d) => d.status === 'PENDING_USER_CONFIRMATION');
  const resolvedDoubts = allDoubts.filter((d) => d.status !== 'PENDING_USER_CONFIRMATION');
  const pendingDoubtThreadIds = new Set<string>();
  for (const d of pendingDoubts) {
    pendingDoubtThreadIds.add(findRoot(d.candidateThreadAId));
    pendingDoubtThreadIds.add(findRoot(d.candidateThreadBId));
  }

  const threads: SharedMailboxThreadItemDto[] = [];
  for (const bucket of mergedBucketsMap.values()) {
    const uniqueById = new Map<string, SharedMailboxMessageItem>();
    for (const m of bucket.messages) {
      uniqueById.set(m.id, m);
    }
    // Sort chronologically ascending (oldest first -> newest last) for the Timeline!
    const sortedChronological = Array.from(uniqueById.values()).sort((a, b) =>
      a.receivedAtIso.localeCompare(b.receivedAtIso),
    );

    const participantsSet = new Set<string>();
    const mailboxSet = new Set<string>();
    let inboxCount = 0;
    let outboxCount = 0;
    let assignedCode: string | null = null;
    const seenAttachmentsInThread = new Set<string>();
    const threadAttachmentEmergenceMap = new Map<string, ThreadAttachmentEmergenceEventDto>();

    const timeline: SharedMailboxThreadTimelineEntryDto[] = sortedChronological.map(
      (msg, index) => {
        const folder = getEffectiveMailboxFolder(msg);
        if (folder === 'OUTBOX') {
          outboxCount++;
        } else {
          inboxCount++;
        }
        mailboxSet.add(msg.mailboxAddress);
        participantsSet.add(`${msg.senderName} <${msg.senderEmail}>`);
        if (msg.assignedProjectCode && !assignedCode) {
          assignedCode = msg.assignedProjectCode;
        }

        const parsedRecipients = parseJsonStringArray(msg.recipientEmailsJson);
        const recipients = parsedRecipients.length > 0 ? parsedRecipients : [msg.mailboxAddress];
        for (const r of recipients) {
          participantsSet.add(r);
        }
        const ccList = parseJsonStringArray(msg.ccEmailsJson);
        const attachments = parseJsonStringArray(msg.attachmentNamesJson);
        const prevMsg = index > 0 ? sortedChronological[index - 1] : null;
        const relayTimeline = extractEmailRelayAndAttachmentTimeline(msg);

        const newlyEmergedInStep: string[] = [];
        const repassedInStep: string[] = [];

        for (const att of attachments) {
          const normAtt = att.trim().toLowerCase();
          if (!seenAttachmentsInThread.has(normAtt)) {
            seenAttachmentsInThread.add(normAtt);
            newlyEmergedInStep.push(att);
            const specificEvent = relayTimeline.attachmentEmergenceEvents.find(
              (ev) => ev.fileName.toLowerCase() === normAtt,
            );
            threadAttachmentEmergenceMap.set(normAtt, {
              fileName: att,
              emergedAtHopOrStepLabel:
                relayTimeline.overallTransmissionKind === 'FORWARDED_CHAIN' && specificEvent
                  ? `Passo #${index + 1} da Thread (${specificEvent.emergedAtHopOrStepLabel})`
                  : `Passo #${index + 1} da Thread (${folder === 'OUTBOX' ? 'Envio Caixa de Saída' : 'Recebimento Caixa de Entrada'})`,
              emergedAtTimestampLabel:
                specificEvent?.emergedAtTimestampLabel ??
                new Date(msg.receivedAtIso).toLocaleString('pt-BR'),
              introducedByActor:
                specificEvent?.introducedByActor ?? `${msg.senderName} <${msg.senderEmail}>`,
              relayHistorySummary:
                specificEvent?.relayHistorySummary ??
                `Surgiu no Passo #${index + 1} (${new Date(msg.receivedAtIso).toLocaleString('pt-BR')}) enviado por ${msg.senderName} <${msg.senderEmail}> para ${recipients.join(', ')}`,
            });
          } else {
            repassedInStep.push(att);
            const prevEvent = threadAttachmentEmergenceMap.get(normAtt);
            if (prevEvent) {
              threadAttachmentEmergenceMap.set(normAtt, {
                ...prevEvent,
                relayHistorySummary: `${prevEvent.relayHistorySummary} → Reenviado/Repassado no Passo #${index + 1} (${new Date(msg.receivedAtIso).toLocaleString('pt-BR')}) por ${msg.senderName}`,
              });
            }
          }
        }

        const upperSubj = msg.subject.trim().toUpperCase();
        const isFwd =
          upperSubj.startsWith('FWD:') ||
          upperSubj.startsWith('FW:') ||
          upperSubj.startsWith('ENC:');
        const isRe = upperSubj.startsWith('RE:') || upperSubj.startsWith('RES:');

        const transmissionKind: SharedMailboxThreadTimelineEntryDto['transmissionKind'] =
          folder === 'OUTBOX'
            ? 'SENT_OUTBOX'
            : isFwd
              ? 'FORWARDED_RELAY'
              : isRe || index > 0
                ? 'REPLY_INBOX'
                : 'ORIGINAL_SENT';

        const transmissionBadgeLabel =
          transmissionKind === 'SENT_OUTBOX'
            ? '📤 Enviado pela Caixa de Saída (SentItems)'
            : transmissionKind === 'FORWARDED_RELAY'
              ? '↪️ Repassado / Encaminhado (Fwd:)'
              : transmissionKind === 'REPLY_INBOX'
                ? '↩️ Resposta Recebida (Re:)'
                : '📨 Envio Original na Thread';

        return {
          messageId: msg.id,
          graphMessageId: msg.graphMessageId,
          stepNumber: index + 1,
          mailboxAddress: msg.mailboxAddress,
          mailboxFolder: folder,
          directionLabel:
            folder === 'OUTBOX' ? '📤 Caixa de Saída (Enviado)' : '📥 Caixa de Entrada (Recebido)',
          transmissionKind,
          transmissionBadgeLabel,
          subject: msg.subject,
          senderName: msg.senderName,
          senderEmail: msg.senderEmail,
          recipientEmails: recipients,
          ccEmails: ccList,
          timestampIso: msg.receivedAtIso,
          timeDeltaFromPreviousLabel: prevMsg
            ? formatTimeDeltaLabel(prevMsg.receivedAtIso, msg.receivedAtIso)
            : 'Início da Thread',
          bodyPreview: msg.bodyPreview,
          hasAttachments: msg.hasAttachments,
          attachmentNames: attachments,
          newlyEmergedAttachments: newlyEmergedInStep,
          repassedAttachments: repassedInStep,
          internalRelayHops: relayTimeline.hops,
          classificationStatus: msg.classificationStatus,
          assignedProjectCode: msg.assignedProjectCode,
          confidenceScore: msg.confidenceScore,
        };
      },
    );

    const firstMsg = sortedChronological[0];
    const lastMsg = sortedChronological[sortedChronological.length - 1];
    const detectedMeta = firstMsg ? extractStudyMetadataFromRealEmail(firstMsg) : null;

    threads.push({
      threadId: bucket.threadId,
      canonicalSubject: bucket.canonicalSubject,
      subjectVariants: Array.from(bucket.subjectVariants),
      studyCode:
        assignedCode ??
        (bucket.protocolHint && bucket.protocolHint !== 'PAF-CPESQ'
          ? bucket.protocolHint
          : (detectedMeta?.code ?? null)),
      mailboxAddresses: Array.from(mailboxSet),
      participants: Array.from(participantsSet),
      totalMessagesCount: timeline.length,
      inboxMessagesCount: inboxCount,
      outboxMessagesCount: outboxCount,
      startedAtIso: firstMsg?.receivedAtIso ?? new Date().toISOString(),
      lastActivityAtIso: lastMsg?.receivedAtIso ?? new Date().toISOString(),
      groupingOrigin: bucket.groupingOrigin,
      hasPendingDoubt: pendingDoubtThreadIds.has(bucket.threadId),
      attachmentEmergenceEvents: Array.from(threadAttachmentEmergenceMap.values()),
      timeline,
    });
  }

  // Sort threads by multi-message threads / latest activity first
  threads.sort((a, b) => {
    if (a.totalMessagesCount > 1 && b.totalMessagesCount === 1) return -1;
    if (a.totalMessagesCount === 1 && b.totalMessagesCount > 1) return 1;
    return b.lastActivityAtIso.localeCompare(a.lastActivityAtIso);
  });

  const totalInbox = state.messages.filter((m) => getEffectiveMailboxFolder(m) === 'INBOX').length;
  const totalOutbox = state.messages.filter(
    (m) => getEffectiveMailboxFolder(m) === 'OUTBOX',
  ).length;

  return {
    totalMessagesCount: state.messages.length,
    inboxMessagesCount: totalInbox,
    outboxMessagesCount: totalOutbox,
    totalThreadsCount: threads.length,
    pendingDoubtsCount: pendingDoubts.length,
    resolvedDoubtsCount: resolvedDoubts.length,
    threads,
    pendingDoubts,
    resolvedDoubts,
  };
}

function normalizeProcedureToken(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function enrichProceduresWithSupportedRegionsAndPrices(state: FakeSuppliersAndStudiesState): void {
  state.procedures = state.procedures.map((p) => {
    const upperCode = p.code.trim().toUpperCase();
    const matchingCityPrices = state.cityPrices.filter(
      (cp) => cp.procedureCode.trim().toUpperCase() === upperCode,
    );
    const primaryRegion = p.regionCityUf ?? matchingCityPrices[0]?.cityUf ?? '';
    const regionalPrices =
      matchingCityPrices.length > 0
        ? matchingCityPrices.map((cp) => ({
            cityUf: cp.cityUf,
            unitPriceBrl: cp.unitPriceBrl,
            unitMeasure: cp.unitMeasure,
            version: cp.version,
          }))
        : [
            {
              cityUf: primaryRegion,
              unitPriceBrl: p.baseUnitCostBrl,
              unitMeasure: 'PROCEDIMENTO_UNIDADE',
              version: p.currentVersion,
            },
          ];
    const supportedRegionsCityUf = Array.from(new Set(regionalPrices.map((rp) => rp.cityUf)));
    return {
      ...p,
      regionCityUf: primaryRegion,
      supportedRegionsCityUf,
      regionalPrices,
    };
  });
}

function findExactGlobalProcedureMatch(
  procedures: readonly ProcedureDefinitionItem[],
  proposedCode: string,
  proposedNamePtBr: string,
  studyRawName: string,
  _activeUsedRegionCityUf = 'Curitiba/PR',
): ProcedureDefinitionItem | undefined {
  const upperCode = proposedCode.trim().toUpperCase();
  const normProposedName = normalizeProcedureToken(proposedNamePtBr);
  const normRawName = normalizeProcedureToken(studyRawName);
  return procedures.find((p) => {
    if (p.code.trim().toUpperCase() === upperCode) {
      return true;
    }
    const normExisting = normalizeProcedureToken(p.namePtBr);
    return (
      normExisting.length > 0 && (normExisting === normProposedName || normExisting === normRawName)
    );
  });
}

function ensureActiveRegionCityPriceOnlyIfUsed(
  state: FakeSuppliersAndStudiesState,
  procedureCode: string,
  activeUsedRegionCityUf: string,
  unitPriceBrl: number,
  studyCode: string,
  actorEmail = 'syllas@giyus.com.br',
): void {
  const cleanRegion = activeUsedRegionCityUf.trim();
  if (!cleanRegion) {
    return;
  }
  const upperCode = procedureCode.trim().toUpperCase();
  const existingIdx = state.cityPrices.findIndex(
    (cp) =>
      cp.procedureCode.toUpperCase() === upperCode &&
      cp.cityUf.toLowerCase() === cleanRegion.toLowerCase(),
  );
  if (existingIdx < 0) {
    state.cityPrices.push({
      id: `cp-${String(state.cityPrices.length + 10).padStart(3, '0')}`,
      procedureCode: upperCode,
      cityUf: cleanRegion,
      unitMeasure: 'PROCEDIMENTO_UNIDADE',
      unitPriceBrl,
      version: 1,
      updatedAtIso: new Date().toISOString(),
      updatedByEmail: actorEmail,
      changeReason: `Uso real ativo no estudo ${studyCode} na regionalidade ${cleanRegion} (preço por localidade — sem criar regionalidades não usadas)`,
    });
  }
  enrichProceduresWithSupportedRegionsAndPrices(state);
}

function syncIncomingStudiesAndProceduresFromArrivedStudies(
  state: FakeSuppliersAndStudiesState,
): void {
  const arrivedStudyCodes = new Set<string>();
  for (const d of state.dossiers) {
    arrivedStudyCodes.add(d.code.toUpperCase());
  }
  for (const m of state.messages) {
    if (m.assignedProjectCode) {
      arrivedStudyCodes.add(m.assignedProjectCode.toUpperCase());
    }
    const detected = extractStudyMetadataFromRealEmail(m);
    if (detected) {
      arrivedStudyCodes.add(detected.code.toUpperCase());
    }
  }

  for (const cand of CANDIDATE_PROJECTS_FROM_CONTRACT_EMAILS) {
    const upperCode = cand.code.toUpperCase();
    if (!arrivedStudyCodes.has(upperCode)) {
      continue;
    }

    // 1. Register Clinical Protocol (StudyItem) in state.studies if not yet present
    if (
      !state.studies.some(
        (s) =>
          s.code.toUpperCase() === upperCode ||
          s.protocolCode.toUpperCase() === cand.studyCode.toUpperCase(),
      )
    ) {
      state.studies.push({
        id: `0199ba02-0000-7000-8000-${String(state.studies.length + 10).padStart(12, '0')}`,
        code: cand.code,
        protocolCode: cand.studyCode,
        title: cand.title,
        phase: 'Phase III',
        therapeuticArea: cand.therapeuticArea,
        sponsorName: cand.sponsorName,
        croName: cand.croName,
        status: state.dossiers.some((d) => d.code.toUpperCase() === upperCode)
          ? 'UnderAnalysis'
          : 'Feasibility',
        targetEnrollment: cand.targetParticipants,
        currentEnrollment: 0,
      });
    }

    // 2. Extract study procedure list and attempt automatic normalization with global catalog
    for (const tpl of cand.extractedProcedures) {
      const existingIdx = state.incomingStudyProcedures.findIndex((item) => item.id === tpl.id);
      const exactGlobal = findExactGlobalProcedureMatch(
        state.procedures,
        tpl.proposedGlobalCode,
        tpl.proposedGlobalNamePtBr,
        tpl.studyRawName,
        tpl.activeUsedRegionCityUf,
      );

      if (existingIdx < 0) {
        if (exactGlobal) {
          ensureActiveRegionCityPriceOnlyIfUsed(
            state,
            exactGlobal.code,
            tpl.activeUsedRegionCityUf,
            tpl.studyUnitNegotiatedBrl,
            tpl.studyCode,
          );
          state.incomingStudyProcedures.push({
            ...tpl,
            normalizationStatus: 'AUTO_MATCHED_GLOBAL',
            matchedGlobalProcedureCode: exactGlobal.code,
            matchedGlobalProcedureName: exactGlobal.namePtBr,
            resolvedByEmail: 'SYSTEM_AUTO_NORMALIZER',
            resolvedAtIso: new Date().toISOString(),
            resolutionNotes: `Normalizado automaticamente com o procedimento ${exactGlobal.code} (${exactGlobal.namePtBr}) na regionalidade ${tpl.activeUsedRegionCityUf} do centro de pesquisa que vai atender a pesquisa (${tpl.attendingResearchCenterName}).`,
          });
        } else {
          state.incomingStudyProcedures.push({
            ...tpl,
            normalizationStatus: 'PENDING_HUMAN_DECISION',
            matchedGlobalProcedureCode: null,
            matchedGlobalProcedureName: null,
            resolvedByEmail: null,
            resolvedAtIso: null,
            resolutionNotes: null,
          });
        }
      } else {
        const current = state.incomingStudyProcedures[existingIdx]!;
        if (current.normalizationStatus === 'PENDING_HUMAN_DECISION' && exactGlobal) {
          ensureActiveRegionCityPriceOnlyIfUsed(
            state,
            exactGlobal.code,
            tpl.activeUsedRegionCityUf,
            tpl.studyUnitNegotiatedBrl,
            tpl.studyCode,
          );
          state.incomingStudyProcedures[existingIdx] = {
            ...current,
            attendingResearchCenterName: tpl.attendingResearchCenterName,
            normalizationStatus: 'AUTO_MATCHED_GLOBAL',
            matchedGlobalProcedureCode: exactGlobal.code,
            matchedGlobalProcedureName: exactGlobal.namePtBr,
            resolvedByEmail: 'SYSTEM_AUTO_NORMALIZER',
            resolvedAtIso: new Date().toISOString(),
            resolutionNotes: `Normalizado automaticamente com o procedimento ${exactGlobal.code} (${exactGlobal.namePtBr}) na regionalidade ${tpl.activeUsedRegionCityUf} do centro de pesquisa que vai atender a pesquisa (${tpl.attendingResearchCenterName}).`,
          };
        }
      }
    }
  }
}

function buildStudyProceduresNormalizationDashboard(
  state: FakeSuppliersAndStudiesState,
): StudyProceduresNormalizationDashboardDto {
  syncIncomingStudiesAndProceduresFromArrivedStudies(state);
  const items = state.incomingStudyProcedures;
  const activeRegions = Array.from(
    new Set([
      ...state.cityPrices.map((cp) => cp.cityUf),
      ...items
        .filter((i) => i.normalizationStatus !== 'PENDING_HUMAN_DECISION')
        .map((i) => i.activeUsedRegionCityUf),
    ]),
  ).filter(Boolean);

  return {
    incomingStudiesCount: state.studies.length,
    totalStudyProceduresCount: items.length,
    autoNormalizedCount: items.filter((i) => i.normalizationStatus === 'AUTO_MATCHED_GLOBAL')
      .length,
    pendingHumanDecisionCount: items.filter(
      (i) => i.normalizationStatus === 'PENDING_HUMAN_DECISION',
    ).length,
    humanApprovedCreatedGlobalCount: items.filter(
      (i) => i.normalizationStatus === 'HUMAN_APPROVED_CREATED_GLOBAL',
    ).length,
    humanAlignedExistingGlobalCount: items.filter(
      (i) => i.normalizationStatus === 'HUMAN_ALIGNED_EXISTING_GLOBAL',
    ).length,
    activeUsedRegions: activeRegions,
    items,
  };
}

function resolveIncomingStudyProcedureDecision(
  state: FakeSuppliersAndStudiesState,
  body: Record<string, unknown>,
): StudyProceduresNormalizationDashboardDto {
  syncIncomingStudiesAndProceduresFromArrivedStudies(state);
  const itemId = strField(body, 'itemId');
  const decisionRaw = strField(body, 'decision', 'APPROVE_CREATE_GLOBAL').toUpperCase();
  const actorEmail = strField(body, 'actorEmail');
  const nowIso = new Date().toISOString();

  const idx = state.incomingStudyProcedures.findIndex((item) => item.id === itemId);
  const targetItem = state.incomingStudyProcedures[idx];
  if (idx < 0 || !targetItem) {
    return buildStudyProceduresNormalizationDashboard(state);
  }

  if (decisionRaw === 'ALIGN_WITH_EXISTING_GLOBAL') {
    const requestedGlobalCode = strField(
      body,
      'targetGlobalProcedureCode',
      targetItem.suggestedExistingGlobalCode ?? state.procedures[0]?.code ?? '',
    ).toUpperCase();
    const existingProc =
      state.procedures.find((p) => p.code.toUpperCase() === requestedGlobalCode) ??
      state.procedures[0];

    if (existingProc) {
      ensureActiveRegionCityPriceOnlyIfUsed(
        state,
        existingProc.code,
        targetItem.activeUsedRegionCityUf,
        existingProc.baseUnitCostBrl,
        targetItem.studyCode,
        actorEmail,
      );
      state.incomingStudyProcedures[idx] = {
        ...targetItem,
        normalizationStatus: 'HUMAN_ALIGNED_EXISTING_GLOBAL',
        matchedGlobalProcedureCode: existingProc.code,
        matchedGlobalProcedureName: existingProc.namePtBr,
        resolvedByEmail: actorEmail,
        resolvedAtIso: nowIso,
        resolutionNotes: `Alinhado pelo humano (${actorEmail}) com o procedimento já existente ${existingProc.code} (${existingProc.namePtBr}) na regionalidade ${targetItem.activeUsedRegionCityUf} do centro de pesquisa que vai atender a pesquisa (${targetItem.attendingResearchCenterName ?? 'Curitiba/PR'}).`,
      };
    }
  } else {
    // APPROVE_CREATE_GLOBAL: Create in the regionality of the Research Center attending the study (e.g. Curitiba/PR) ONLY what we are actually using
    const customCode = strField(body, 'customGlobalCode', targetItem.proposedGlobalCode)
      .trim()
      .toUpperCase();
    const customNamePt = strField(
      body,
      'customGlobalNamePtBr',
      targetItem.proposedGlobalNamePtBr,
    ).trim();
    const customCostBrl = numField(
      body,
      'customBaseUnitCostBrl',
      targetItem.studyUnitNegotiatedBrl,
    );

    let globalProc = state.procedures.find((p) => p.code.toUpperCase() === customCode);
    if (!globalProc) {
      globalProc = {
        id: `0199ba02-0000-7000-8000-${String(state.procedures.length + 10).padStart(12, '0')}`,
        tenantId: null,
        code: customCode,
        namePtBr: customNamePt,
        nameEnUs: targetItem.proposedGlobalNameEnUs,
        category: targetItem.category,
        defaultDurationMinutes: targetItem.defaultDurationMinutes,
        baseUnitCostBrl: customCostBrl,
        currentVersion: 1,
        status: 'Published',
        regionCityUf: targetItem.activeUsedRegionCityUf,
        attendingCenterName:
          targetItem.attendingResearchCenterName ??
          `Centro de Pesquisa Atendente em ${targetItem.activeUsedRegionCityUf}`,
        supportedRegionsCityUf: [targetItem.activeUsedRegionCityUf],
      };
      state.procedures.push(globalProc);
    }

    ensureActiveRegionCityPriceOnlyIfUsed(
      state,
      globalProc.code,
      targetItem.activeUsedRegionCityUf,
      customCostBrl,
      targetItem.studyCode,
      actorEmail,
    );

    state.incomingStudyProcedures[idx] = {
      ...targetItem,
      normalizationStatus: 'HUMAN_APPROVED_CREATED_GLOBAL',
      matchedGlobalProcedureCode: globalProc.code,
      matchedGlobalProcedureName: globalProc.namePtBr,
      resolvedByEmail: actorEmail,
      resolvedAtIso: nowIso,
      resolutionNotes: `Aprovado pelo humano (${actorEmail}) e criado para ${targetItem.activeUsedRegionCityUf} (${globalProc.code}) — pois o centro de pesquisa que vai atender o estudo ${targetItem.studyCode} (${targetItem.attendingResearchCenterName ?? 'Curitiba/PR'}) é de ${targetItem.activeUsedRegionCityUf} (só criamos procedimentos que estamos usando na regionalidade que usamos).`,
    };
  }

  syncIncomingStudiesAndProceduresFromArrivedStudies(state);
  writePersistedGreenfieldMailboxState(state);
  return buildStudyProceduresNormalizationDashboard(state);
}

function findRegisteredPartnerInSeparateRegistry(
  registryList: readonly OrganizationItem[],
  candidateCode: string,
  candidateName: string,
): OrganizationItem | undefined {
  const upperCode = candidateCode.trim().toUpperCase();
  const normCandidateName = normalizeProcedureToken(candidateName);
  return registryList.find((org) => {
    const orgCode = org.code.trim().toUpperCase();
    if (orgCode === upperCode) {
      return true;
    }
    // Also match if ORG- prefix vs SPON-/CRO- prefix shares the core code (e.g. ORG-HCOR vs SPON-HCOR, ORG-PAREXEL vs CRO-PAREXEL)
    const cleanOrgSuffix = orgCode.replace(/^(ORG|SPON|CRO)-/, '');
    const cleanCandSuffix = upperCode.replace(/^(ORG|SPON|CRO)-/, '');
    if (cleanOrgSuffix.length >= 3 && cleanOrgSuffix === cleanCandSuffix) {
      return true;
    }
    const normOrgName = normalizeProcedureToken(org.name);
    return (
      normOrgName.length > 0 &&
      (normOrgName === normCandidateName ||
        normOrgName.includes(normCandidateName) ||
        normCandidateName.includes(normOrgName))
    );
  });
}

function buildSponsorAndCroAutoDetectionDashboard(
  state: FakeSuppliersAndStudiesState,
): SponsorAndCroAutoDetectionDashboardDto {
  syncIncomingStudiesAndProceduresFromArrivedStudies(state);

  const sponsorsRegistry = state.organizations.filter((o) => o.kind === 'Sponsor');
  const crosRegistry = state.organizations.filter((o) => o.kind === 'Cro');

  const pairsByStudyCode = new Map<
    string,
    {
      studyCode: string;
      protocolCode: string;
      studyTitle: string;
      sourceMessagesCount: number;
      latestMessageSubject: string;
      detectedSponsorRaw: {
        kind: 'Sponsor';
        code: string;
        name: string;
        country: string;
        contactEmail: string;
        cnpjOrTaxId: string;
        detectionSource: string;
      };
      detectedCroRaw: {
        kind: 'Cro';
        code: string;
        name: string;
        country: string;
        contactEmail: string;
        cnpjOrTaxId: string;
        detectionSource: string;
      };
    }
  >();

  // 1. Inspect all arrived messages in shared mailbox
  for (const msg of state.messages) {
    const extracted = extractSponsorAndCroFromEmail(msg);
    if (!extracted) {
      continue;
    }
    const key = extracted.studyCode.toUpperCase();
    const existing = pairsByStudyCode.get(key);
    if (existing) {
      existing.sourceMessagesCount += 1;
    } else {
      pairsByStudyCode.set(key, {
        studyCode: extracted.studyCode,
        protocolCode: extracted.protocolCode,
        studyTitle: extracted.studyTitle,
        sourceMessagesCount: 1,
        latestMessageSubject: msg.subject,
        detectedSponsorRaw: {
          ...extracted.detectedSponsor,
          kind: 'Sponsor',
        },
        detectedCroRaw: {
          ...extracted.detectedCro,
          kind: 'Cro',
        },
      });
    }
  }

  // 2. Also include any active dossier or study in state
  for (const cand of CANDIDATE_PROJECTS_FROM_CONTRACT_EMAILS) {
    const key = cand.code.toUpperCase();
    const hasDossierOrStudy =
      state.dossiers.some((d) => d.code.toUpperCase() === key) ||
      state.studies.some((s) => s.code.toUpperCase() === key);
    if (hasDossierOrStudy && !pairsByStudyCode.has(key)) {
      pairsByStudyCode.set(key, {
        studyCode: cand.code,
        protocolCode: cand.studyCode,
        studyTitle: cand.title,
        sourceMessagesCount: 1,
        latestMessageSubject: cand.title,
        detectedSponsorRaw: {
          ...cand.detectedSponsor,
          kind: 'Sponsor',
        },
        detectedCroRaw: {
          ...cand.detectedCro,
          kind: 'Cro',
        },
      });
    }
  }

  const detectedPairs: DetectedSponsorAndCroPairDto[] = Array.from(pairsByStudyCode.values()).map(
    (entry) => {
      const matchedSponsor = findRegisteredPartnerInSeparateRegistry(
        sponsorsRegistry,
        entry.detectedSponsorRaw.code,
        entry.detectedSponsorRaw.name,
      );
      const matchedCro = findRegisteredPartnerInSeparateRegistry(
        crosRegistry,
        entry.detectedCroRaw.code,
        entry.detectedCroRaw.name,
      );

      const detectedSponsor: DetectedEntityCandidateDto = {
        ...entry.detectedSponsorRaw,
        isRegisteredInSeparateRegistry: Boolean(matchedSponsor),
        registeredOrganizationId: matchedSponsor?.id ?? null,
      };
      const detectedCro: DetectedEntityCandidateDto = {
        ...entry.detectedCroRaw,
        isRegisteredInSeparateRegistry: Boolean(matchedCro),
        registeredOrganizationId: matchedCro?.id ?? null,
      };

      return {
        studyCode: entry.studyCode,
        protocolCode: entry.protocolCode,
        studyTitle: entry.studyTitle,
        sourceMessagesCount: entry.sourceMessagesCount,
        latestMessageSubject: entry.latestMessageSubject,
        detectedSponsor,
        detectedCro,
      };
    },
  );

  const pendingSponsors = new Set<string>();
  const pendingCros = new Set<string>();
  for (const pair of detectedPairs) {
    if (!pair.detectedSponsor.isRegisteredInSeparateRegistry) {
      pendingSponsors.add(pair.detectedSponsor.code.toUpperCase());
    }
    if (!pair.detectedCro.isRegisteredInSeparateRegistry) {
      pendingCros.add(pair.detectedCro.code.toUpperCase());
    }
  }

  return {
    sponsorsRegistry,
    crosRegistry,
    detectedPairs,
    totalDetectedStudiesCount: detectedPairs.length,
    pendingSponsorsToRegisterCount: pendingSponsors.size,
    pendingCrosToRegisterCount: pendingCros.size,
  };
}

function ensureOrganizationInSpecificRegistry(
  state: FakeSuppliersAndStudiesState,
  kind: 'Sponsor' | 'Cro',
  code: string,
  name: string,
  country: string,
  contactEmail: string,
): void {
  const registryOfSameKind = state.organizations.filter((o) => o.kind === kind);
  const existing = findRegisteredPartnerInSeparateRegistry(registryOfSameKind, code, name);
  if (existing) {
    return;
  }
  state.organizations.push({
    id: `0199ba01-0000-7000-8000-${String(state.organizations.length + 10).padStart(12, '0')}`,
    code: code.trim().toUpperCase(),
    name: name.trim(),
    kind,
    country: country.trim().toUpperCase() || 'BR',
    contactEmail: contactEmail.trim(),
    status: 'Active',
  });
}

function registerDetectedSponsorOrCroInSeparateRegistries(
  state: FakeSuppliersAndStudiesState,
  body: Record<string, unknown>,
): SponsorAndCroAutoDetectionDashboardDto {
  const currentDashboard = buildSponsorAndCroAutoDetectionDashboard(state);
  const target = strField(body, 'target', 'BOTH_SEPARATE_REGISTRIES').toUpperCase();
  const studyCode = strField(body, 'studyCode', '').toUpperCase();

  if (target === 'MANUAL_SEPARATE_ENTRY') {
    const manualKindRaw = strField(body, 'manualKind', 'Sponsor');
    const kind: 'Sponsor' | 'Cro' = manualKindRaw === 'Cro' ? 'Cro' : 'Sponsor';
    const customCode = strField(body, 'customCode', kind === 'Sponsor' ? 'SPON-NOVO' : 'CRO-NOVA');
    const customName = strField(
      body,
      'customName',
      kind === 'Sponsor' ? 'Novo Patrocinador' : 'Nova CRO',
    );
    const customCountry = strField(body, 'customCountry', 'BR');
    const customContactEmail = strField(body, 'customContactEmail');
    ensureOrganizationInSpecificRegistry(
      state,
      kind,
      customCode,
      customName,
      customCountry,
      customContactEmail,
    );
    writePersistedGreenfieldMailboxState(state);
    return buildSponsorAndCroAutoDetectionDashboard(state);
  }

  if (target === 'ALL_DETECTED_SEPARATE_REGISTRIES') {
    for (const pair of currentDashboard.detectedPairs) {
      ensureOrganizationInSpecificRegistry(
        state,
        'Sponsor',
        pair.detectedSponsor.code,
        pair.detectedSponsor.name,
        pair.detectedSponsor.country,
        pair.detectedSponsor.contactEmail,
      );
      ensureOrganizationInSpecificRegistry(
        state,
        'Cro',
        pair.detectedCro.code,
        pair.detectedCro.name,
        pair.detectedCro.country,
        pair.detectedCro.contactEmail,
      );
    }
    writePersistedGreenfieldMailboxState(state);
    return buildSponsorAndCroAutoDetectionDashboard(state);
  }

  const pair =
    currentDashboard.detectedPairs.find(
      (p) => p.studyCode.toUpperCase() === studyCode || p.protocolCode.toUpperCase() === studyCode,
    ) ?? currentDashboard.detectedPairs[0];

  if (pair) {
    if (target === 'SPONSOR_ONLY' || target === 'BOTH_SEPARATE_REGISTRIES') {
      ensureOrganizationInSpecificRegistry(
        state,
        'Sponsor',
        strField(body, 'customCode', pair.detectedSponsor.code),
        strField(body, 'customName', pair.detectedSponsor.name),
        strField(body, 'customCountry', pair.detectedSponsor.country),
        strField(body, 'customContactEmail', pair.detectedSponsor.contactEmail),
      );
    }
    if (target === 'CRO_ONLY' || target === 'BOTH_SEPARATE_REGISTRIES') {
      ensureOrganizationInSpecificRegistry(
        state,
        'Cro',
        target === 'CRO_ONLY'
          ? strField(body, 'customCode', pair.detectedCro.code)
          : pair.detectedCro.code,
        target === 'CRO_ONLY'
          ? strField(body, 'customName', pair.detectedCro.name)
          : pair.detectedCro.name,
        target === 'CRO_ONLY'
          ? strField(body, 'customCountry', pair.detectedCro.country)
          : pair.detectedCro.country,
        target === 'CRO_ONLY'
          ? strField(body, 'customContactEmail', pair.detectedCro.contactEmail)
          : pair.detectedCro.contactEmail,
      );
    }
  }

  writePersistedGreenfieldMailboxState(state);
  return buildSponsorAndCroAutoDetectionDashboard(state);
}

const DEFAULT_STUDY_SERVICE_BASKET_ITEMS: readonly StudyServiceBasketItemDto[] = [
  {
    id: 'bsk-epoca-001',
    studyCode: 'EPOCA',
    itemType: 'SERVICO_VISITA_CLINICA',
    title:
      'Visitas Clínicas D0 Randomização + D30 Follow-up (4 participantes · Equipe PI/Coordenação)',
    competenceSuggested: '10/2026',
    quantity: 4,
    unitValueBrl: 2450,
    totalValueBrl: 9800,
    invoicedStatus: 'INCLUIDO_EM_NF',
    linkedInvoiceNumber: 'NFS-e CWB-2026/001480',
  },
  {
    id: 'bsk-epoca-002',
    studyCode: 'EPOCA',
    itemType: 'PROCEDIMENTO_EXAME',
    title:
      'Ecocardiograma Transtorácico + ECG 12 Derivações + Coleta e Processamento Local (Curitiba/PR)',
    competenceSuggested: '10/2026',
    quantity: 4,
    unitValueBrl: 1150,
    totalValueBrl: 4600,
    invoicedStatus: 'INCLUIDO_EM_NF',
    linkedInvoiceNumber: 'NFS-e CWB-2026/001480',
  },
  {
    id: 'bsk-epoca-003',
    studyCode: 'EPOCA',
    itemType: 'PASS_THROUGH_REEMBOLSO',
    title:
      'Ressarcimento de Transporte e Alimentação de Participantes e Acompanhantes (Previsto no TCLE)',
    competenceSuggested: '10/2026',
    quantity: 8,
    unitValueBrl: 220,
    totalValueBrl: 1760,
    invoicedStatus: 'ACUMULADO_NA_CESTA',
    linkedInvoiceNumber: null,
  },
  {
    id: 'bsk-epoca-004',
    studyCode: 'EPOCA',
    itemType: 'PRODUTO_INSUMO_KIT',
    title: 'Insumos de Coleta Seriada, Tubos Criogênicos e Armazenamento Farmácia -80°C',
    competenceSuggested: '10/2026',
    quantity: 1,
    unitValueBrl: 1940,
    totalValueBrl: 1940,
    invoicedStatus: 'ACUMULADO_NA_CESTA',
    linkedInvoiceNumber: null,
  },
  {
    id: 'bsk-solair-001',
    studyCode: 'SOLAIR',
    itemType: 'SERVICO_VISITA_CLINICA',
    title: 'Visitas V1 Screening + V2 Randomização + Aplicação TCLE/TALE Pediátrico (SOLAIR)',
    competenceSuggested: '10/2026',
    quantity: 5,
    unitValueBrl: 2800,
    totalValueBrl: 14000,
    invoicedStatus: 'ACUMULADO_NA_CESTA',
    linkedInvoiceNumber: null,
  },
  {
    id: 'bsk-solair-002',
    studyCode: 'SOLAIR',
    itemType: 'PROCEDIMENTO_EXAME',
    title:
      'Exame de Óxido Nítrico Exalado (FeNO) + Espirometria c/ Prova Broncodilatadora (Curitiba/PR)',
    competenceSuggested: '10/2026',
    quantity: 5,
    unitValueBrl: 920,
    totalValueBrl: 4600,
    invoicedStatus: 'ACUMULADO_NA_CESTA',
    linkedInvoiceNumber: null,
  },
  {
    id: 'bsk-solair-003',
    studyCode: 'SOLAIR',
    itemType: 'PRODUTO_INSUMO_KIT',
    title: 'Kit Sensor FeNO NIOX VERO + Bocais Descartáveis + Medicação de Resgate Obrigatória',
    competenceSuggested: '10/2026',
    quantity: 5,
    unitValueBrl: 480,
    totalValueBrl: 2400,
    invoicedStatus: 'ACUMULADO_NA_CESTA',
    linkedInvoiceNumber: null,
  },
];

const DEFAULT_CENTER_ISSUED_STUDY_INVOICES: readonly CenterIssuedStudyInvoiceDto[] = [
  {
    id: 'cinf-001',
    invoiceNumber: 'NFS-e CWB-2026/001480',
    issuedAtDate: '2026-10-25',
    issuingCenterName: 'TRIALS HUB LTDA (Curitiba/PR)',
    issuingCenterCnpj: '66.125.390/0001-34',
    issuingCenterClassification: 'CENTRO_PARCEIRO_GIYUS',
    studyCode: 'EPOCA',
    studyTitle: 'Estudo EPOCA — Ensaio Clínico Plurianual Cardiovascular (D0–D1440)',
    sponsorOrCroPayerName: 'Associação Beneficente Síria — Hcor (PROADI-SUS)',
    sponsorOrCroPayerCnpj: '60.453.024/0001-22',
    accountantResponsibleName: 'Contabilidade Exata / Contador Responsável GIYUS & Centros',
    accountantResponsibleEmail: 'contabilidade@giyus.com.br',
    grossAmountBrl: 14400,
    rawInvoiceDescription:
      'Prestação de serviços de pesquisa clínica Protocolo EPOCA — Competência 10/2026: 4 Visitas Clínicas D0/D30 (R$ 9.800,00) + 4 Ecocardiogramas/ECG/Coleta Local (R$ 4.600,00).',
    classificationStatus: 'AUTO_CLASSIFIED',
    aiConfidenceScorePct: 98,
    aiClassificationNote:
      '✅ IA classificou sozinha (98% confiança): identificou Competência 10/2026 e conciliou exatamente com os 2 itens da cesta de serviços do estudo EPOCA (R$ 9.800,00 + R$ 4.600,00 = R$ 14.400,00).',
    competenceMonthYear: '10/2026',
    includedBasketItemIds: ['bsk-epoca-001', 'bsk-epoca-002'],
    includedBasketItemsSummary: [
      'Visitas Clínicas D0 Randomização + D30 Follow-up (4 participantes · R$ 9.800,00)',
      'Ecocardiograma Transtorácico + ECG 12 Derivações + Coleta Local (4 exames · R$ 4.600,00)',
    ],
    classifiedByHumanEmail: null,
    classifiedAtIso: '2026-10-25T14:20:00.000Z',
  },
  {
    id: 'cinf-002',
    invoiceNumber: 'NFS-e CWB-2026/001512',
    issuedAtDate: '2026-10-29',
    issuingCenterName: 'GIYUS Curitiba Research Center (Filial Operacional)',
    issuingCenterCnpj: '69.584.441/0001-84',
    issuingCenterClassification: 'CENTRO_GIYUS',
    studyCode: 'SOLAIR',
    studyTitle: 'Estudo SOLAIR (D7820C00004) — Asma Grave & Imunologia',
    sponsorOrCroPayerName: 'Parexel International / AstraZeneca AB',
    sponsorOrCroPayerCnpj: '04.891.220/0001-18',
    accountantResponsibleName: 'Contabilidade Exata / Contador Responsável GIYUS & Centros',
    accountantResponsibleEmail: 'contabilidade@giyus.com.br',
    grossAmountBrl: 18600,
    rawInvoiceDescription:
      'NFS-e emitida pelo contador ref. serviços clínicos e procedimentos realizados no protocolo SOLAIR conforme medição parcial autorizada.',
    classificationStatus: 'NEEDS_HUMAN_CLASSIFICATION',
    aiConfidenceScorePct: 41,
    aiClassificationNote:
      '⚠️ IA NÃO CONSEGUIU CLASSIFICAR SOZINHA (41% confiança): A nota fiscal emitida pelo contador não especifica no corpo o mês/ano exato de competência nem discrimina quais itens da cesta acumulada do estudo SOLAIR compõem os R$ 18.600,00. Solicita-se ajuda humana para categorizar a Competência e selecionar o que está incluído nesta nota.',
    competenceMonthYear: '',
    includedBasketItemIds: [],
    includedBasketItemsSummary: [],
    classifiedByHumanEmail: null,
    classifiedAtIso: null,
  },
  {
    id: 'cinf-003',
    invoiceNumber: 'NFS-e CWB-2026/001519',
    issuedAtDate: '2026-10-30',
    issuingCenterName: 'TRIALS HUB LTDA (Curitiba/PR)',
    issuingCenterCnpj: '66.125.390/0001-34',
    issuingCenterClassification: 'CENTRO_PARCEIRO_GIYUS',
    studyCode: 'EPOCA',
    studyTitle: 'Estudo EPOCA — Ensaio Clínico Plurianual Cardiovascular (D0–D1440)',
    sponsorOrCroPayerName: 'Associação Beneficente Síria — Hcor (PROADI-SUS)',
    sponsorOrCroPayerCnpj: '60.453.024/0001-22',
    accountantResponsibleName: 'Contabilidade Exata / Contador Responsável GIYUS & Centros',
    accountantResponsibleEmail: 'contabilidade@giyus.com.br',
    grossAmountBrl: 3700,
    rawInvoiceDescription:
      'Segunda Nota Fiscal do estudo EPOCA — Repasse de despesas operacionais, insumos e ressarcimentos vinculados ao protocolo.',
    classificationStatus: 'NEEDS_HUMAN_CLASSIFICATION',
    aiConfidenceScorePct: 48,
    aiClassificationNote:
      '⚠️ IA SOLICITA AJUDA HUMANA (48% confiança): Esta é a 2ª nota fiscal emitida contra o estudo EPOCA pelo contador. O texto é genérico ("despesas operacionais, insumos e ressarcimentos"). Por favor, confirme a Competência (MM/AAAA) e marque quais itens da cesta acumulada (ex.: Pass-Through TCLE R$ 1.760 + Insumos R$ 1.940) estão incluídos nesta nota.',
    competenceMonthYear: '',
    includedBasketItemIds: [],
    includedBasketItemsSummary: [],
    classifiedByHumanEmail: null,
    classifiedAtIso: null,
  },
];

function ensureCenterIssuedInvoicesState(state: FakeSuppliersAndStudiesState): void {
  if (!state.studyServiceBasketItems) {
    state.studyServiceBasketItems = [...DEFAULT_STUDY_SERVICE_BASKET_ITEMS];
  }
  if (!state.centerIssuedStudyInvoices) {
    state.centerIssuedStudyInvoices = [...DEFAULT_CENTER_ISSUED_STUDY_INVOICES];
  }
}

function buildCenterIssuedStudyInvoicesSnapshot(
  state: FakeSuppliersAndStudiesState,
): CenterIssuedStudyInvoicesResponseDto {
  ensureCenterIssuedInvoicesState(state);
  return {
    invoices: state.centerIssuedStudyInvoices ?? [],
    basketItems: state.studyServiceBasketItems ?? [],
  };
}

function receiveCenterIssuedStudyInvoiceOnState(
  state: FakeSuppliersAndStudiesState,
  body: Record<string, unknown>,
): CenterIssuedStudyInvoicesResponseDto {
  ensureCenterIssuedInvoicesState(state);
  const invoiceNumber = strField(
    body,
    'invoiceNumber',
    `NFS-e CWB-2026/${1520 + (state.centerIssuedStudyInvoices?.length ?? 0)}`,
  );
  const issuingCenterName = strField(
    body,
    'issuingCenterName',
    'GIYUS Curitiba Research Center (Filial Operacional)',
  );
  const issuingCenterCnpj = strField(body, 'issuingCenterCnpj');
  const studyCode = strField(body, 'studyCode', 'SOLAIR').toUpperCase();
  const grossAmountBrl = Math.max(1, numField(body, 'grossAmountBrl', 4600));
  const rawDesc = strField(
    body,
    'rawInvoiceDescription',
    'Prestação de serviços de pesquisa clínica conforme medição do contador.',
  );
  const accountantName = strField(
    body,
    'accountantResponsibleName',
    'Contabilidade Exata / Contador Responsável GIYUS',
  );

  // Tenta classificar sozinho: verifica se a descrição traz competência explícita (MM/202X) E se bate exatamente com algum item disponível na cesta do estudo
  const competenceMatch = /\b(0[1-9]|1[0-2])\/202\d\b/.exec(rawDesc);
  const candidateBasketItems = (state.studyServiceBasketItems ?? []).filter(
    (b) => b.studyCode.toUpperCase() === studyCode && b.invoicedStatus === 'ACUMULADO_NA_CESTA',
  );
  const exactItemMatch = candidateBasketItems.find(
    (b) =>
      Math.abs(b.totalValueBrl - grossAmountBrl) < 1 ||
      rawDesc.toLowerCase().includes(b.title.slice(0, 18).toLowerCase()),
  );

  const canAutoClassify = Boolean(competenceMatch && exactItemMatch);
  const detectedCompetence = competenceMatch ? competenceMatch[0] : '';

  if (canAutoClassify && exactItemMatch) {
    state.studyServiceBasketItems = (state.studyServiceBasketItems ?? []).map((b) =>
      b.id === exactItemMatch.id
        ? {
            ...b,
            invoicedStatus: 'INCLUIDO_EM_NF',
            linkedInvoiceNumber: invoiceNumber,
          }
        : b,
    );
  }

  const newInvoice: CenterIssuedStudyInvoiceDto = {
    id: `cinf-${Date.now()}`,
    invoiceNumber,
    issuedAtDate: '2026-10-30',
    issuingCenterName,
    issuingCenterCnpj,
    issuingCenterClassification: issuingCenterName.toUpperCase().includes('HOLDING')
      ? 'GIYUS_HOLDING'
      : issuingCenterName.toUpperCase().includes('GIYUS')
        ? 'CENTRO_GIYUS'
        : 'CENTRO_PARCEIRO_GIYUS',
    studyCode,
    studyTitle:
      studyCode === 'EPOCA'
        ? 'Estudo EPOCA — Ensaio Clínico Plurianual Cardiovascular (D0–D1440)'
        : `Protocolo Clínico ${studyCode}`,
    sponsorOrCroPayerName:
      studyCode === 'EPOCA'
        ? 'Associação Beneficente Síria — Hcor (PROADI-SUS)'
        : 'Parexel International / Patrocinador do Estudo',
    sponsorOrCroPayerCnpj: studyCode === 'EPOCA' ? '60.453.024/0001-22' : '04.891.220/0001-18',
    accountantResponsibleName: accountantName,
    accountantResponsibleEmail: 'contabilidade@giyus.com.br',
    grossAmountBrl,
    rawInvoiceDescription: rawDesc,
    classificationStatus: canAutoClassify ? 'AUTO_CLASSIFIED' : 'NEEDS_HUMAN_CLASSIFICATION',
    aiConfidenceScorePct: canAutoClassify ? 96 : 38,
    aiClassificationNote: canAutoClassify
      ? `✅ IA classificou sozinha (96% confiança): Competência ${detectedCompetence} e item da cesta "${exactItemMatch?.title}" identificados automaticamente.`
      : '⚠️ IA NÃO CONSEGUIU CLASSIFICAR SOZINHA (38% confiança): Descrição da NF emitida pelo contador não permite cravar a competência exata e/ou quais produtos e serviços acumulados na cesta do estudo compõem esta nota. Solicita-se a ajuda de um humano para categorizar a Competência e selecionar o que está incluído nesta nota.',
    competenceMonthYear: canAutoClassify ? detectedCompetence : '',
    includedBasketItemIds: canAutoClassify && exactItemMatch ? [exactItemMatch.id] : [],
    includedBasketItemsSummary:
      canAutoClassify && exactItemMatch
        ? [`${exactItemMatch.title} (R$ ${exactItemMatch.totalValueBrl.toFixed(2)})`]
        : [],
    classifiedByHumanEmail: null,
    classifiedAtIso: canAutoClassify ? new Date().toISOString() : null,
  };

  state.centerIssuedStudyInvoices = [newInvoice, ...(state.centerIssuedStudyInvoices ?? [])];
  return buildCenterIssuedStudyInvoicesSnapshot(state);
}

function classifyCenterIssuedStudyInvoiceByHumanOnState(
  state: FakeSuppliersAndStudiesState,
  body: Record<string, unknown>,
): CenterIssuedStudyInvoicesResponseDto {
  ensureCenterIssuedInvoicesState(state);
  const invoiceId = strField(body, 'invoiceId', '');
  const competenceMonthYear = strField(body, 'competenceMonthYear', '10/2026');
  const rawIds = Array.isArray(body['includedBasketItemIds'])
    ? (body['includedBasketItemIds'] as unknown[]).map((x) => String(x))
    : [];
  const customSummary = strField(body, 'customIncludedSummary', '').trim();
  const humanEmail = strField(body, 'humanEmail');

  const targetInv = (state.centerIssuedStudyInvoices ?? []).find((inv) => inv.id === invoiceId);
  if (!targetInv) {
    return buildCenterIssuedStudyInvoicesSnapshot(state);
  }

  const matchedItems = (state.studyServiceBasketItems ?? []).filter((b) => rawIds.includes(b.id));
  const summaryLines = [
    ...matchedItems.map((m) => `${m.title} (R$ ${m.totalValueBrl.toFixed(2)})`),
    ...(customSummary ? [customSummary] : []),
  ];

  state.studyServiceBasketItems = (state.studyServiceBasketItems ?? []).map((b) =>
    rawIds.includes(b.id)
      ? {
          ...b,
          invoicedStatus: 'INCLUIDO_EM_NF',
          linkedInvoiceNumber: targetInv.invoiceNumber,
        }
      : b,
  );

  state.centerIssuedStudyInvoices = (state.centerIssuedStudyInvoices ?? []).map((inv) =>
    inv.id === invoiceId
      ? {
          ...inv,
          classificationStatus: 'HUMAN_CLASSIFIED',
          competenceMonthYear,
          includedBasketItemIds: rawIds,
          includedBasketItemsSummary:
            summaryLines.length > 0
              ? summaryLines
              : [`Classificado manualmente na competência ${competenceMonthYear}`],
          aiClassificationNote: `✅ Classificado com ajuda humana por ${humanEmail}: Competência ${competenceMonthYear} · Itens incluídos na nota vinculados à cesta de serviços e produtos do estudo ${inv.studyCode}.`,
          classifiedByHumanEmail: humanEmail,
          classifiedAtIso: new Date().toISOString(),
        }
      : inv,
  );

  return buildCenterIssuedStudyInvoicesSnapshot(state);
}
